// =============================================================================
// Rolled — unified site app. FOH flows poured in from the live Trafford app
// (Phase 1). Site-specific values now come from config (src/config/sites.js);
// everything else is migrated AS-IS. React, ReactDOM and the scanner/EXIF
// libraries are bundled here — no unpkg, no in-browser Babel.
// =============================================================================
import React from "react";
import * as ReactDOM from "react-dom";
import { Html5Qrcode } from "html5-qrcode";
import { startInvertedQrFallback } from "./lib/invertedQr.js"; // reads a customer QR their phone flipped to cream-on-dark (dark mode)
import exifr from "exifr";
import QRCode from "qrcode";
import { getActiveSite } from "./config/sites.js";
import { MeadowhallForecast } from "./lib/meadowhallForecast.jsx";
import { ProductionForecast } from "./lib/productionForecast.jsx";
import { InboundDeliveries } from "./lib/inboundDeliveries.jsx"; // Kitchen → Trafford week 1: the kitchen's Inbound Deliveries tile in own-premises mode (storeroom on site) // Trafford: the Manchester V2 forecast tile, lifted from the retired kitchen app (kitchen on site since 24 Sep 2026)
import { confirmedWrite, notifyWriteFailed, localMarkIsFresh } from "./lib/confirmedWrite.js";
import { fetchJsonWithRetry, confirmByReRead } from "./lib/backendJson.js"; // shared retry-until-JSON read + re-poll confirm for write verifies
import { eodSubmitted, skuSoldOut, sellOutCycleCount, stockHasOpenRequest } from "./lib/writeVerify.js"; // goal-state checks for the FOH write verifies
import * as stockSync from "./lib/stockSync.js"; // Phase 2 dual-run shadow (best-effort)
import * as selloutSync from "./lib/selloutSync.js"; // Phase 3 module 1: sell-outs + EOD shadow (best-effort)
import * as ingredientWastage from "./lib/ingredientWastageSync.js"; // Meadowhall-only: daily In/Used/Wasted grams -> ingredient_wastage_daily (Supabase is source of truth; result surfaced, not swallowed)
import * as incidents from "./lib/incidentsSync.js"; // Incident Report: file to Supabase (record_incident door + private photo bucket); result surfaced, not swallowed
import * as notificationsSync from "./lib/notificationsSync.js"; // reorder alerts (a FOH running-low/out-of-stock tap raises the SAME alert the kitchen does) + urgent incidents (real-time Command Centre page) — queues only, the dispatcher sends
import * as stockReader from "./lib/stockReader.js"; // Phase 3 module 2: stock read-flip (gated OFF until STOCK_READ_HOSTS lists a host)
import * as readFreshness from "./lib/readFreshness.js"; // "is the tablet's cached copy fresh enough to skip a backend read?" (login-time reads, 20 Sep 2026)
import * as stockCatalogSrc from "./lib/stockCatalog.js"; // BJ: catalogue read-flip (gated OFF; CATALOG_READ_HOSTS empty). A flipped FOH tablet reads its OWN site's catalogue from Supabase, sheet fallback on any problem. Meadowhall HELD (the historical site=tc hard-code means it shows Trafford's list today — correcting that is Adam's daylight call).
import { openStockSnapshotFromFeed } from "./lib/eodStockSnapshot.js"; // Lane BE Wave 3: freeze column-12 open-requests snapshot from the Supabase read
import * as eodWriteDrop from "./lib/eodWriteDrop.js"; // Lane CD: the End of Day write-drop gate (ships with an EMPTY shop list — off everywhere)
import { fetchEmergencyContacts } from "./lib/emergencyContacts.js"; // Phase 3: emergency contacts read from Supabase (was the Apps Script feed)
import { fetchTileOverrides } from "./lib/tileOverrides.js"; // Phase 3: tile config read from Supabase (was the Apps Script feed)
import { fetchAllergenCatalog, fetchAllergenCategories } from "./lib/allergens.js"; // Phase 3: allergen matrix read from Supabase (was the Apps Script feeds)
import * as checklistSrc from "./lib/checklist.js"; // Phase 3: FOH 67 checklist read from Supabase (gated OFF until CHECKLIST_READ_HOSTS lists this host)
import * as checklistSync from "./lib/checklistSync.js"; // Phase 3: FOH 67 checklist tick + RAK dual-write shadow (best-effort; WRITE only, read stays on the sheet)
import * as presence from "./lib/presence.js"; // S22: shadow-record every PIN tap so we know who was in the shop (write-only, nothing reads it)
import * as deliveryOrdersSync from "./lib/deliveryOrdersSync.js"; // S31: outbound courier hand-off proof -> Supabase + private photo bucket (best-effort, after the sheet write)
import * as deliveryWriteDrop from "./lib/deliveryWriteDrop.js"; // Lane BM: FOH courier hand-off WRITE-DROP gate (DELIVERY_WRITE_DROP_HOSTS ships EMPTY) + the strip's Supabase today feed. OFF path = the original tile code, untouched.
import * as eodInsights from "./lib/eodInsights.js"; // B8: Previous-Week EOD tile read from Supabase, OFF everywhere
import * as pinAuth from "./lib/auth.js"; // A1: server-side PIN verify — ON at Trafford + Meadowhall since 1 Oct 2026 (the Command Centre's list decides every sign-in)
import * as deliveryConfigReader from "./lib/deliveryConfigReader.js"; // Lane BL: delivery-config read-flip (getdeliveryplatforms + getdeliveryorderguidance) from Supabase, gated OFF until DELIVERY_CONFIG_READ_HOSTS lists this host
import * as complianceReader from "./lib/complianceReader.js"; // Lane BK: compliance CONFIG read-flip (gated OFF until COMPLIANCE_CONFIG_READ_HOSTS lists this host)
import { ComplianceModuleFull } from "./lib/complianceFull.jsx"; // Kitchen → Trafford week 1: the full BOH compliance module for sites.js compliance:"full" (Trafford); "subset" sites keep the FOH screens below
import * as rosterFree from "./lib/rosterFree.js"; // BF: pure helpers so a gated kiosk never DECIDES from the downloaded roster (initials/role/clock-out compare)
import { KioskBindPanel } from "./lib/kioskBindPanel.jsx"; // CB: admin-only "which shop is this tablet?" (gated OFF — KIOSK_BIND_HOSTS is empty, so this renders nothing anywhere)
import * as clockPinCheck from "./lib/clockPinCheck.js"; // BT: pure decisions for the gated clock-scrim PIN check ("Checking…", stale-answer guard, 8 s timeout) — the OFF path never runs a check
import * as sessionLock from "./lib/sessionLock.js"; // JJ 28 Sep 2026: no remembered sign-ins on a shop host + the 10-minute idle lock
import * as siteAccess from "./lib/siteAccess.js"; // JJ 29 Sep 2026: device + network + location on every sign-in and action; server-side block/sign-out (fails open). Trafford + Meadowhall hosts only (or ?access=on)
import * as offlinePin from "./lib/offlinePin.js"; // JJ 13 Oct 2026: the Club Card scanner's PIN check with no connection — this tablet's stretched book of server-checked PINs, else the staffer types their name

// Active site for this deployment (was a hard-coded const per photocopy).
const SITE = getActiveSite();
const BACKEND = SITE.backend;
// This shop's roll list for Sell-Out Times + End-of-Day: the shared list with
// the shop's own swaps applied (sites.js `skuSwaps` — Trafford sells SC Chicken
// where Meadowhall sells Katsu Prawn).
const SKU_LIST = SITE.skuList;
// The roll each swapped-in label took the row of (Trafford: SC Chicken took
// Katsu Prawn's). A count saved under the old name the same day — before the
// swap reached this tablet — was counted on that row, so it belongs to the new
// one: savedCount() reads it through. If a tablet saw both builds today, both
// names can hold a count; the old build counted both in its total, so they are
// added and the total stays the same across the refresh.
const SKU_REPLACED = Object.fromEntries(Object.entries(SITE.skuSwaps || {}).map(([from, to]) => [to, from]));
const savedCount = (saved, sku) => {
  const from = SKU_REPLACED[sku], a = saved[sku], b = from ? saved[from] : undefined;
  if (a === undefined && b === undefined) return undefined;
  return (Number(a) || 0) + (Number(b) || 0);
};

// How long the checklist keeps an unconfirmed optimistic tick before the poll is
// allowed to drop it as a phantom. Must comfortably outlast a tick's own
// confirmedWrite verify (a few seconds) plus one 15s poll cycle, so a genuinely
// in-flight write is never dropped, while a tick that a clean server read keeps
// saying isn't there can't stay green forever.
const CHECKLIST_OPTIMISTIC_GRACE_MS = 30000;
// Login-time backend reads (20 Sep 2026). The checklist day read is the slowest
// thing a tablet fires at login (12 s alone, 24–83 s when the backend is busy),
// and three screens used to fire their own copy of it. See fetchChecklistDayShared
// in lib/checklist.js and readFreshness.js.
//   CHECKLIST_POLL_MS            the tile re-polls this long AFTER the previous
//                                read settles (was a fixed 15 s timer that stacked
//                                copies of a 20-second read).
//   CHECKLIST_PREWARM_MAX_AGE_MS the login pre-warm is skipped when the tablet's
//                                cached copy of today is younger than this — the
//                                tile refreshes on open anyway. 30 min (was 10)
//                                since the session lock (28 Sep 2026): the idle lock
//                                is also 10 min, so a 10-min skip window fired the
//                                slow read on nearly every sign-in after a lock.
//   REVIEWS_BANNER_MIN_REFRESH_MS the home banner's four reads (three of them
//                                20–35 s) are not re-fired on every trip back to
//                                the home screen; the cached numbers are shown and
//                                refreshed once they turn this old (its own poll
//                                cadence), exactly as when the tablet sits on home.
const CHECKLIST_POLL_MS = 15000;
const CHECKLIST_PREWARM_MAX_AGE_MS = 30 * 60 * 1000;
const REVIEWS_BANNER_MIN_REFRESH_MS = 5 * 60 * 1000;

// Dev surface + keeps the save-safety helper in the bundle until task #6 wires
// it into the FOH writes. Harmless in production.
if (typeof window !== "undefined") window.__rolled = { site: SITE, confirmedWrite };

const {useState,useEffect,useCallback,useRef,useMemo} = React;

// Local QR renderer — draws the QR as an inline SVG in-browser via the bundled
// `qrcode` package, so claim/recruiter tokens NEVER leave our origin. (These
// used to be built as third-party image URLs, leaking the token to a
// third-party CDN + its logs.) Offline-safe: no network request at render time.
function LocalQr({value, size=280, alt="QR code"}){
  const [svg,setSvg]=useState("");
  useEffect(()=>{
    let live=true;
    QRCode.toString(value,{type:"svg",errorCorrectionLevel:"M",margin:2,width:size},(err,str)=>{ if(live&&!err) setSvg(str); });
    return ()=>{live=false;};
  },[value,size]);
  return <div role="img" aria-label={alt} style={{width:size,height:size,display:"block",margin:"0 auto"}} dangerouslySetInnerHTML={{__html:svg}}/>;
}

// The scanner's customer QR screens (migration, Mates keychain, sign-up, Centre,
// Day 1) stay up until staff tap "Done — next customer" (JJ, 11 Oct 2026). Each
// renders <CCQrHold/> while its QR is showing; the scanner's hardware-back handler
// swallows a back press while any hold is up, so a stray swipe can't clear a code
// mid-scan either.
let ccQrHolds = 0;
function CCQrHold(){
  useEffect(()=>{ ccQrHolds++; return ()=>{ ccQrHolds--; }; },[]);
  return null;
}
// Same idea for a Square code on screen (a redeemed reward or Special Card): the
// redemption has already happened, so leaving the screen would lose the code for
// good. Hardware back is swallowed while one is up, and the Site App's own Back /
// side menu ask first (round-1 audit, agreed 13 Oct 2026). Done — Next Customer
// closes it as before.
let ccCodeHolds = 0;
function CCCodeHold(){
  useEffect(()=>{ ccCodeHolds++; return ()=>{ ccCodeHolds--; }; },[]);
  return null;
}

// Build version — bump this on every deploy. The auto-updater below polls the
// live HTML every ~2 min, extracts the latest BUILD_VERSION, and surfaces a
// "New version available — Refresh" banner if the running tab is stale. Fixes
// the classic SPA problem where a tablet keeps executing the originally-loaded
// JS for hours, unaware that the server has newer code.
// Pre-built pipeline: the version now lives in index.html as window.BUILD_VERSION
// (single source of truth) and is read off window here — matches Meadowhall.
const BUILD_VERSION = (typeof window !== "undefined" && window.BUILD_VERSION) || "unknown";

// SC-STAFF-1 — optional server PIN fall-through (flag-gated, DEFAULT OFF). The
// login keypad validates a typed PIN against the locally-cached Bizimply roster
// (staffPins) and rejects a miss. A staffer added in Command Centre 'Add Staff'
// lives in the backend `staff` table (and POST /staff/pin-login accepts them)
// but NOT in the local roster, so today the scanner rejects them. When this flag
// is ON and the local check MISSES, the login falls through to the backend
// pin-login and, on success, establishes the session from the server response.
// This is strictly ADDITIVE — a PIN the local path already accepts is handled
// exactly as before, so it can only let MORE valid PINs in, never lock existing
// staff out. Default ON (JJ, 5 Aug) ⇒ a staffer added in Command Centre → Add Staff
// can log into the scanner via the backend /staff/pin-login when the local Bizimply
// roster misses. Opt a tablet OUT with ?serverpinlogin=0 (persisted); ?serverpinlogin=1
// clears the opt-out. Existing local-roster logins are untouched — the fall-through only
// runs on a local MISS, so it can only admit more valid PINs, never lock anyone out.
const SERVER_PIN_LOGIN_FALLTHROUGH = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get("serverpinlogin");
    if (q === "0") { try { localStorage.setItem("rolled.serverPinLoginOff", "1"); } catch (e) {} return false; }
    if (q === "1") { try { localStorage.removeItem("rolled.serverPinLoginOff"); } catch (e) {} return true; }
    return localStorage.getItem("rolled.serverPinLoginOff") !== "1";
  } catch (e) { return true; }
})();

// Session lock (JJ, 28 Sep 2026) — see lib/sessionLock.js. On a shop host a sign-in
// never survives a page load: the copy older builds saved is wiped here, once, BEFORE
// the app renders, so every open of the app starts at the PIN pad. Preview hosts keep
// the old refresh-survives behaviour (QA harness) and may shorten the idle window with
// ?idlelock=<seconds>; a shop host always gets the full ten minutes.
const SESSION_RESTORE_OK = (() => {
  try { return sessionLock.sessionRestoreAllowed(window.location.hostname); } catch (e) { return false; }
})();
if (!SESSION_RESTORE_OK) { try { sessionLock.wipeSavedSession(window.sessionStorage); } catch (e) {} }
const IDLE_LOCK_MS_HERE = (() => {
  try {
    return sessionLock.idleLockMsFor({ hostname: window.location.hostname, query: new URLSearchParams(window.location.search).get("idlelock") });
  } catch (e) { return sessionLock.IDLE_LOCK_MS; }
})();

// Site access (JJ, 29 Sep 2026) — see lib/siteAccess.js and SITE_ACCESS_SPEC.md §4. On
// trafford/meadowhall.eatrolled.com (or any host with ?access=on) every sign-in is checked
// with the shop's /api/access before it completes, a heartbeat runs while signed in, and
// every tap's activity line carries this device's id. Everywhere else: inert. Fails open —
// only an explicit server "block" (login) or "signout" (heartbeat) changes anything.
const SITE_ACCESS_ON = (() => {
  try { return siteAccess.siteAccessEnabled(); } catch (e) { return false; }
})();

// While someone is signed in, watch for `idleMs` without a tap and then call `onLock`
// (sessionLock.startIdleWatch — timer + wake checks, see there). The latest onLock is
// read through a ref so the watcher never has to restart when the app re-renders.
function useIdleLock({active, idleMs, onLock}){
  const onLockRef = useRef(onLock);
  onLockRef.current = onLock;
  useEffect(() => {
    if (!active) return;
    return sessionLock.startIdleWatch({win: window, doc: document, idleMs, onLock: () => onLockRef.current()});
  }, [active, idleMs]);
}

function useAutoUpdater(){
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (cancelled) return;
      try {
        const url = window.location.pathname + "?_v=" + Date.now();
        const r = await fetch(url, {cache: "no-store"});
        if (!r.ok || cancelled) return;
        const html = await r.text();
        const m = html.match(/window\.BUILD_VERSION\s*=\s*"([^"]+)"/);
        if (m && m[1] !== BUILD_VERSION && !cancelled) setUpdateAvailable(true);
      } catch (e) {}
    };
    const firstCheck = setTimeout(check, 15000);
    const interval = setInterval(check, 120000);
    return () => { cancelled = true; clearTimeout(firstCheck); clearInterval(interval); };
  }, []);
  return updateAvailable;
}

function useVersionPillDOM(updateAvailable){
  // Renders outside the React tree so it's visible on every view without
  // having to thread it through login/home/tool-shell returns.
  useEffect(() => {
    const pill = document.createElement("div");
    pill.id = "__rolled-version-pill";
    pill.style.cssText = "position:fixed;bottom:6px;right:10px;z-index:9998;font-family:monospace;font-size:10px;color:rgba(254,250,240,0.3);letter-spacing:0.02em;pointer-events:none;user-select:none";
    pill.textContent = "v" + BUILD_VERSION;
    document.body.appendChild(pill);
    return () => { try { document.body.removeChild(pill); } catch(e) {} };
  }, []);
  useEffect(() => {
    if (!updateAvailable) return;
    // Full-screen non-dismissible modal (2026-04-21): the old corner banner
    // let staff ignore new builds indefinitely on the same tablet session.
    // This version blocks all page interaction and exposes only one action:
    // Refresh. Escape is swallowed so the OS can't close it.
    const backdrop = document.createElement("div");
    backdrop.id = "__rolled-update-modal";
    backdrop.style.cssText = "position:fixed;inset:0;background:rgba(0,20,15,0.85);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:24px;z-index:99999;animation:__rolledFade 0.25s ease-out";
    backdrop.innerHTML = '<style>@keyframes __rolledFade{from{opacity:0}to{opacity:1}}</style><div role="alertdialog" aria-modal="true" aria-labelledby="__rolled-update-title" style="background:#00392C;color:#FEFAF0;padding:40px 36px;border-radius:20px;max-width:420px;width:100%;text-align:center;box-shadow:0 24px 64px rgba(0,0,0,0.45);border:1px solid rgba(212,251,111,0.15)"><div style="font-size:48px;margin-bottom:8px" aria-hidden="true">\u2728</div><div id="__rolled-update-title" style="font-family:gelica,Georgia,serif;font-size:26px;font-weight:700;color:#D4FB6F;letter-spacing:-0.02em;margin-bottom:10px">New version ready</div><div style="font-size:15px;color:rgba(254,250,240,0.75);line-height:1.5;margin-bottom:28px">A newer build of this app is live. Tap refresh to load it.</div><button id="__rolled-update-refresh" style="background:#D4FB6F;color:#00392C;border:none;padding:18px 28px;border-radius:12px;font-family:Manrope,sans-serif;font-weight:800;font-size:17px;letter-spacing:0.02em;cursor:pointer;width:100%;box-shadow:0 4px 16px rgba(212,251,111,0.25);min-height:56px">\u21BB Refresh now</button></div>';
    document.body.appendChild(backdrop);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const btn = backdrop.querySelector("#__rolled-update-refresh");
    const reload = () => window.location.reload();
    btn.addEventListener("click", reload);
    const swallowKey = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener("keydown", swallowKey, true);
    setTimeout(() => { try { btn.focus(); } catch(e) {} }, 50);
    return () => {
      btn.removeEventListener("click", reload);
      window.removeEventListener("keydown", swallowKey, true);
      document.body.style.overflow = prevOverflow;
      try { document.body.removeChild(backdrop); } catch(e) {}
    };
  }, [updateAvailable]);
}

// Orientation hook — detects portrait vs landscape for responsive layout
function useOrientation(){
  const [isPortrait,setIsPortrait]=useState(window.innerHeight>window.innerWidth);
  useEffect(()=>{
    const h=()=>setIsPortrait(window.innerHeight>window.innerWidth);
    window.addEventListener('resize',h);window.addEventListener('orientationchange',h);
    return()=>{window.removeEventListener('resize',h);window.removeEventListener('orientationchange',h)};
  },[]);
  return isPortrait;
}

// ===========================================
// CONFIG - TRAFFORD CENTRE
// ===========================================
// [poured in: now sourced from config/sites.js]
// [poured in: now sourced from config/sites.js]

// FOH Clock-In rollout gate (2026-04-29). The full Clock-In flow + roster
// banner ships to all 4 sites simultaneously, but stays hidden behind this
// per-site flag so JJ can pilot on Manchester Kitchen first. Flip to `true`
// here to enable on Trafford. While `false`: banner hidden, no
// getStaffOnShift poller, no scrim modal — code is inert.
// FOH Clock-In gate. Per JJ 2026-04-29: feature is scoped to Manchester
// Kitchen only — all other sites stay disabled. Even though Trafford's
// SITE.key is "tc" (so this evaluates false here), keeping the gate
// site-aware means the same source can be lifted into another worktree
// without re-introducing the original contamination on TC/Arndale/Meadowhall.
const CLOCK_IN_ENABLED = (SITE.key === "mcr" || SITE.key === "kitchen");
// Backend siteKey for clock-in actions. For FOH apps this matches SITE.key
// directly. The Manchester Kitchen frontend overrides — its SITE.key is
// "mcr" (legacy) but the backend's SITE_TO_BIZIMPLY_STORE_ + StaffShifts
// schema use "kitchen". Keeping this constant per-site avoids touching
// SITE.key across the rest of the kitchen frontend.
const CLOCK_IN_SITE_KEY = "tc";

// Coarse device classifier (forensic only) — used on PIN attempts so the
// admin Activity Log can show where a failed PIN came from. tablet / mobile /
// desktop. iPad Safari on iPadOS 13+ reports as Mac; we treat anything with
// maxTouchPoints>1 + Mac as a tablet too.
function getDeviceInfo(){
  if (typeof navigator === "undefined") return {device:"unknown", ua:""};
  const ua = String(navigator.userAgent || "");
  const w = (typeof window !== "undefined" && window.innerWidth) || 0;
  const isiPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
  const isAndroidTablet = /Android/.test(ua) && !/Mobile/.test(ua);
  const isPhone = /iPhone|iPod|Android.*Mobile|webOS|BlackBerry|IEMobile|Opera Mini/.test(ua);
  let device = "desktop";
  if (isiPad || isAndroidTablet) device = "tablet";
  else if (isPhone || (w && w < 600)) device = "mobile";
  else if (w && w >= 600 && w < 1100 && /Mobi|Tablet|Touch/i.test(ua)) device = "tablet";
  return {device, ua};
}
// [poured in: now sourced from config/sites.js]
// No hard-coded PINs (removed 2026-06-21, Phase 0 security). Staff roster is fetched from the
// Admin Staff Directory on mount (see useEffect in App), then cached in localStorage. If the
// backend is unreachable AND there's no cache, login fails closed (empty roster) rather than
// exposing emergency PINs in the public bundle. A proper offline emergency login is a Phase 2 task.
const STAFF_PINS_FALLBACK = {};
const STAFF_BY_PIN_FALLBACK = {};
// Tile gating is intentionally minimal — any tile that's `on:true` opens to
// every logged-in staffer. Per-action authorisation is enforced server-side
// via canPerformAction → Function Permissions matrix when a staff member
// actually tries to commit an action (e.g. submit an EOD report). Coming-soon
// tiles (`on:false`) stay visible-but-disabled with a Soon badge.
// 2026-04-22 1700: reverted matrix-driven tile gating (it was hiding tiles
// from JJ as Admin on Meadowhall). Matrix now only governs actions, not tiles.
// Tile IDs with a wired view component (see `toolContent` below). Tiles
// outside this set have no route, so if the admin Tile Editor flips a
// placeholder to `live` we defensively downgrade to `coming_soon` rather than
// render a dead tappable card. Keep this in sync with `toolContent` keys.
const ROUTED_TILE_IDS = {report:1, sellout:1, stock:1, scanner:1, checklist:1, compliance:1, insights:1, allergens:1, allergensQR:1, deliveryOrders:1, contacts:1, forecast:1, production:1, inbound:1, ingredientwaste:1, incident:1};

// Derives the effective visibility state of a tile from the merged seed +
// admin override. Returns one of: 'live' | 'coming_soon' | 'hidden'. The
// v2 tile-override schema (2026-04-24) uses an explicit `state` field;
// legacy rows without a state fall back to the `on` boolean.
function tileEffectiveState(tool){
  // Site-scoped tiles (Meadowhall's Daily Forecast) are hidden on every other
  // site — the only per-site tile difference in the unified app.
  if (tool.siteOnly && tool.siteOnly !== SITE.key) return "hidden";
  // Kitchen tiles (Production Forecast, Ingredient Wastage, …) only render on a
  // full-service site — one with the kitchen on the premises (sites.js
  // `hasKitchen`: Meadowhall, and Trafford since the 24 Sep 2026 reopen).
  if (tool.kitchen && !SITE.hasKitchen) return "hidden";
  // Local-preview escape hatch: unlock the Club Card Scanner tile on localhost
  // so Phase 5 work is testable without hitting the admin dashboard to flip
  // the override. Production hosts still respect the admin-set state.
  const isLocalPreview = typeof window!=="undefined" && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  if(isLocalPreview && tool.id==="scanner") return "live";
  // Allergen Matrix preview (2026-05-07) — same dev-only escape so QA can see
  // the tile without flipping it via admin Tile Editor. Production hosts still
  // respect state:"hidden" until JJ pilots Meadowhall via the Tile Editor.
  if(isLocalPreview && tool.id==="allergens") return "live";

  let state = tool.state;
  if (state !== "live" && state !== "coming_soon" && state !== "hidden") {
    state = tool.on ? "live" : "coming_soon";
  }
  // Placeholder-fn safety: a live tile without a wired view falls back to
  // coming_soon so staff never tap a tile that renders nothing.
  if (state === "live" && !ROUTED_TILE_IDS[tool.id]) state = "coming_soon";
  return state;
}

function toolAccessFor(tool){
  const state = tileEffectiveState(tool);
  if (state === "hidden")      return {state, enabled:false, badge:null};
  if (state === "coming_soon") return {state, enabled:false, badge: tool.tag || "Soon"};
  return {state, enabled:true, badge:null};
}
const B = {nori:"#00392C",wasabi:"#D4FB6F",kewpie:"#FEFAF0",rice:"#FFFFFF",coral:"#FF7A5C",sunshine:"#F1C544",eucalyptus:"#6FAF8F"};

// Reviews — group-target preview (2026-05-27). Lets JJ eyeball the team-target
// chrome on staging WITHOUT shipping the backend handler changes. Reads URL
// params and synthesises a monthlyConfig payload that the Banner + Leaderboard
// tile consume identically to a real backend response.
//   ?previewMode=group                       — turns on team mode
//   &previewTarget=100                       — fallback teamTarget
//   &previewCount=87                         — fallback monthCount
//   &previewPrize=£20 each                   — fallback teamPrize
// Per-site overrides (so the leaderboard tile's site toggle changes the bar):
//   &previewTarget_trafford=100              — per-site teamTarget
//   &previewCount_trafford=87                — per-site monthCount
//   &previewTarget_arndale=100
//   &previewCount_arndale=42
// Manchester combined view sums Trafford + Arndale automatically.
// Once the real backend ships getReviewMonthlyConfig with teamTarget/teamPrize/
// monthCount fields populated, these params become an override-for-testing
// rather than the sole way to see the UI. Safe to leave permanently — only
// fires when the URL params are present.
function getPreviewTeamConfig(siteKey){
  try {
    const p = new URLSearchParams(window.location.search);
    const mode = p.get("previewMode");
    if (mode !== "group") return null;
    const defT = parseInt(p.get("previewTarget") || "0", 10) || 0;
    const defC = parseInt(p.get("previewCount")  || "0", 10) || 0;
    const prize = p.get("previewPrize") || "";
    const readSite = (s) => ({
      target: parseInt(p.get("previewTarget_" + s) || "0", 10) || defT,
      count:  parseInt(p.get("previewCount_"  + s) || "0", 10) || defC,
    });
    // Build the per-site teamTargets map in the SAME shape the backend returns
    // (getReviewMonthlyConfig). The AND-gate logic in the banner + leaderboard
    // reads monthlyConfig.teamTargets, so preview must populate it or the
    // per-site "hit" test has nothing to work with. (2026-07-28)
    const mkRow = (s) => { const r = readSite(s); return { target: r.target, monthCount: r.count, reward: prize, start: "", end: "", isActive: r.target > 0 }; };
    const teamTargets = {};
    let target = defT, count = defC;
    if (siteKey === "manchester") {
      teamTargets.trafford = mkRow("trafford");
      teamTargets.arndale  = mkRow("arndale");
      target = teamTargets.trafford.target + teamTargets.arndale.target;
      count  = teamTargets.trafford.monthCount + teamTargets.arndale.monthCount;
    } else if (siteKey) {
      teamTargets[siteKey] = mkRow(siteKey);
      target = teamTargets[siteKey].target;
      count  = teamTargets[siteKey].monthCount;
    }
    return {
      mostReviewsPrize: "",
      bestRatePrize: "",
      beatChampActive: false,
      groupTargetActive: true,
      teamTargets: teamTargets,
      teamTarget: target,
      teamPrize: prize,
      monthCount: count,
    };
  } catch(e) { return null; }
}

// Stock catalog is fetched from the Admin Stock Catalog on mount (see App).
// Items + categories + images + default quantities are all admin-managed.

// `fn` maps each tile to its backend function name in the Function Permissions
// matrix. Admin dashboard edits to that matrix gate the tile here end-to-end.
const TOOLS = [
  {id:"report",fn:"dailyReport",label:"End of Day Report",desc:"Wastage, TGTG & running totals",icon:"📋",on:true,role:"FOH"},
  {id:"sellout",fn:"sellOutTimes",label:"Roll Sell Out Times",desc:"Mark rolls as out of stock",icon:"⏱️",on:true,role:"FOH"},
  // Daily Forecast — Meadowhall ONLY (its own Sheffield forecast backend). Hidden
  // on every other site via `siteOnly` (see tileEffectiveState). Trafford/Arndale
  // read their forecast on the kitchen app, not here.
  {id:"forecast",fn:"forecastConfirm",label:"Daily Forecast",desc:"Today's production target — confirm the day's forecast",icon:"📊",on:true,role:"FOH",siteOnly:"meadowhall"},
  // Production Forecast — Trafford ONLY. Adam's Manchester V2 engine, confirmed
  // by the chef on site now the kitchen lives in the unit (24 Sep 2026). Lifted
  // from the retired kitchen app (src/lib/productionForecast.jsx): every read,
  // confirm and undo already carried site "tc", so nothing moved in the data.
  // `kitchen:true` = only on a full-service site; `siteOnly` ties it to the one
  // site the Manchester engine forecasts for (Meadowhall has its own tile above).
  // Server gate unchanged: forecastConfirm / forecastUndo are BOH + Admin. NO `fn`
  // on purpose: `fn` only feeds applyFunctionMeta (CC label/desc/icon overrides),
  // and Meadowhall's Daily Forecast already owns the forecastConfirm key — sharing
  // it would let one CC rename hit both tiles. Tile wording is per-id via the
  // Tile Editor (tile_overrides tc/production) instead.
  {id:"production",label:"Production Forecast",desc:"Per-roll targets for today — chef confirms what the kitchen makes",icon:"📊",on:true,role:"BOH",kitchen:true,siteOnly:"tc"},
  // Inbound Deliveries (week 1 of the kitchen → Trafford merge): log + photo-
  // confirm a supplier drop at the on-site storeroom. FOH or BOH can receive
  // (decision 4, 17 Sep 2026). No `fn` for the same reason as `production`.
  // `siteOnly:"tc"` for now: Meadowhall never had this tile and widening it there
  // is a separate decision (drop siteOnly and `kitchen:true` does the rest).
  {id:"inbound",label:"Inbound Deliveries",desc:"Log + photo-confirm what just arrived at the storeroom",icon:"🚛",on:true,role:"BOTH",kitchen:true,siteOnly:"tc"},
  // Ingredient Wastage — full-service sites (2026-08-23 Meadowhall; Trafford from
  // 24 Sep 2026). Daily In/Used/Wasted grams per protein (the "Fish Tracker").
  // Gated by `kitchen:true` (sites.js hasKitchen), not a hardcoded site key.
  // NO `fn`: it writes to Supabase (ingredient_wastage_daily), not an Apps Script
  // action, so it deliberately skips the Function Permissions matrix gate.
  {id:"ingredientwaste",label:"Ingredient Wastage",desc:"Log fish & prep waste in grams",icon:"🐟",on:true,role:"FOH",kitchen:true},
  // Previous Week Insights (Phase A, 2026-05-06). Read-only EOD view —
  // yesterday + last 7 days. Hidden by default; flip live via CC Tile Editor
  // when ready. No per-action gate (view-only tile).
  {id:"insights",fn:"previousWeekInsights",label:"Previous Week Insights",desc:"Yesterday + last 7 days · wastage, TGTG, sell-outs",icon:"📊",state:"hidden",role:"BOTH"},
  {id:"stock",fn:"stockRequest",label:"Stock Requests",desc:"Request items needed at site",icon:"🚚",on:false,role:"FOH"},
  // Delivery Orders (2026-05-12) — outbound handoff photo proof for Uber
  // Eats / Deliveroo / Just Eat orders. Mandatory photo of order+ticket
  // + order #. Surfaces on admin Delivery Order Proof tile.
  {id:"deliveryOrders",fn:"deliveryOrderLogged",label:"Delivery Orders",desc:"Photo proof before handing off",icon:"🛵",on:true,role:"FOH"},
  {id:"scanner",fn:"scanner",label:"Club Card Scanner",desc:"Scan loyalty QR codes",icon:"💳",on:true,role:"FOH"},
  // Food Allergens QR (2026-05-26) — customer-facing FOH tile. Tapping
  // opens a full-bleed branded screen with a QR linking to eatrolled.com/menu
  // so customers scan it on their phone for full menu + allergen info.
  {id:"allergensQR",fn:"allergensQR",label:"Food Allergens",desc:"Scan QR for the full menu",icon:"⚠️",on:true,role:"FOH"},
  // Allergens Legacy (kept hidden 2026-05-26) — the in-house allergen lookup
  // matrix. Superseded by the Food Allergens QR tile above. Code kept intact;
  // admin Tile Editor can flip it back on temporarily if ever needed.
  {id:"allergens",fn:"allergens",label:"Allergens Legacy",desc:"Allergen info for every item",icon:"⚠️",state:"hidden",role:"FOH"},
  {id:"guides",fn:"guides",label:"Guides / Handbook",desc:"Staff guides and training",icon:"📖",on:false,role:"BOTH"},
  // HACCP Sheets retired as a standalone tile (2026-04-29) — HACCP Daily
  // now lives inside the Compliance module alongside Cleaning/Pest/Probe/
  // Fish/Emergency/Declaration/Corrective. Kept the comment so any
  // historical admin Tile Editor entry has context.
  {id:"temps",fn:"tempRecording",label:"Temp Recording",desc:"Temperature logging",icon:"🌡️",on:false,role:"BOTH"},
  // FOH 67 Checklist (Phase C, 2026-04-22). Default off — turned on at runtime
  // when getChecklistConfig returns visibleToStaff=true (admin-controlled per
  // site). Kept in TOOLS so the App-level fetch can override `on` post-mount.
  {id:"checklist",fn:"checklistTick",label:"FOH 67 Checklist",desc:"4-sweep daily checklist · squad effort",icon:"🧠",on:false,role:"FOH"},
  {id:"checklists",label:"FOH Checklists",desc:"Opening and closing checklists",icon:"✅",on:false,role:"FOH"},
  // Compliance Module (Phase 1 foundation, 2026-04-29). Module code is lifted
  // and ready, but per JJ this site stays Coming Soon at the FOH grid until
  // the kitchen rollout proves the flow. Flip `on:true` (or push admin tile
  // override → state:'live') when ready to enable on Trafford. Tile gating
  // downgrades to coming_soon while on:false; module code remains inert.
  // Compliance — sites.js `compliance` picks the module ("full" = the kitchen's
  // HACCP set in complianceFull.jsx, "subset" = the cleaning · pest · corrective
  // screens below) and switches the tile on; a site without it keeps the Soon
  // badge. An admin tile_overrides row still wins, as for every tile.
  {id:"compliance",fn:"complianceLog",label:"Compliance",desc:SITE.compliance==="full"?"HACCP · Cleaning · Pest · Probe · Fish · Emergency · Declaration · Corrective":"Cleaning · Pest · Corrective actions",icon:"🛡️",on:!!SITE.compliance,role:"BOTH"},
  // Emergency Contacts (2026-05-18) — site-specific phone book. Admin manages
  // entries in the Command Centre "Emergency Contacts" config tile; FOH fetches
  // per-site on mount and caches in localStorage for 5 min.
  {id:"contacts",fn:"contacts",label:"Emergency Contacts",desc:"Key numbers · tap to call",icon:"📞",on:true,role:"BOTH"},
  {id:"incident",label:"Incident Report",desc:"Log issues, accidents & equipment",icon:"🚨",on:true,role:"BOTH"},
  {id:"complaint",label:"Complaint Form QR",desc:"Customer complaint logging",icon:"💬",on:false,role:"FOH"},
  {id:"bizimply",label:"Bizimply Clock In",desc:"Staff clock in/out",icon:"⏰",on:false,role:"BOTH"},
  {id:"stocktake",label:"Sunday Stocktake",desc:"Weekly stock count",icon:"📦",on:false,role:"FOH"},
  {id:"helpbot",label:"Help Bot",desc:"Ask questions, get answers",icon:"🤖",on:false,role:"BOTH"},
];

// Logo as base64 image
const LOGO_B64 = "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0OTkuNDggMTI5Ljc4Ij4KICA8ZGVmcz4KICAgIDxzdHlsZT4KICAgICAgLmNscy0xIHsKICAgICAgICBmaWxsOiAjZDNmYTZmOwogICAgICB9CiAgICA8L3N0eWxlPgogIDwvZGVmcz4KICA8ZyBpZD0iUk9MTEVEIj4KICAgIDxnPgogICAgICA8Zz4KICAgICAgICA8Zz4KICAgICAgICAgIDxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTU5LjY3LDgzLjMyYy40NC40Ni4yOSwxLjE1LS4yMiwxLjk1LTEyLjY1LDE2LjMyLTIzLjM5LDM3LjQyLTQ0LjkzLDQyLjg0LTMuMzEuNzgtMTIuMTgsMy4xMy0xNC40MS4zMi0xLjItMy4xNyw3LjE4LTYuNzUsOS40LTguMzQsMS41LS45MywzLjAxLTEuOTEsNC40OS0yLjkxLDEwLjk1LTcuMjksMTcuMDUtMTYuMDQsMTguNi0yOC45Mi4xOC0uOTcuNS0xLjgzLDEuMDctMi40NSwzLjcxLTMuNDIsMjEuNTctNC45MiwyNS45NS0yLjUzbC4wNC4wNFoiLz4KICAgICAgICAgIDxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTY2Ljc5LDY2Ljg5YzUuODIuOTYsMTEuNjQsMi41OCwxNi44Niw1LjM2LDMuNzQsMi4xNi0xLjgzLDIuNzgtMy40NiwzLjI0LTMuODguOTQtNy42NywyLjQ1LTExLjQzLDMuNzMtLjg1LjI5LTEuNzQuNS0yLjY0LjU0LTEuMDUuMDYtMi4xMi0uMTQtMy4xNi0uMzgtOS4wOS0yLjIxLTE4LjU2LTEuNTktMjcuNjEuNjktMi4yNi4zOC0xLjc0LTEuNzktMS4yNi0zLjE3LDEuMjMtMy4xNSwzLjI4LTkuNDUsNi42NC0xMC42NCwzLjUxLS45MSw3LjI1LS43NiwxMC44Ni0uODUsNS4wOCwwLDEwLjEzLjU1LDE1LjEsMS40NmwuMDkuMDJaIi8+CiAgICAgICAgICA8cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik0xMDguNzYsNzIuMzFjLjE1LjkxLTQuMjcuOC04LjM3LjczLTMuMTItLjA1LTUuOTEuMDctNy4yMy0uMjUtMS4zLS4zMy0yLjUxLTEuMDgtMy42NS0xLjgtMTAuNjMtNi44MS0yMy42MS05LjIyLTM2LjE2LTkuNzEtMi4zMy0uMTMtNS4xNC0uMDMtNy4zMy4wOS0xLjIxLjA0LTItLjAyLTIuMDYtLjU2LS4wNi0uNDIuMzMtMS4xMS45MS0xLjg3Ljg3LTEuMTQsMi4xOS0yLjUyLDMuMjYtMy43MSwyLjEyLTIuMzIsNC41OC01LjI3LDYuODktNS43NCwyLjU2LS42OCw1Ljg2LS4wNSw4LjU5LjI1LDE2Ljg4LDEuNzQsMzQuNzksOC42NSw0NS4xNCwyMi41MnYuMDVaIi8+CiAgICAgICAgICA8cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik0xMjIuMDYsNjguOTdjLjkzLDEuODksNC4xNiw3LjEsMi44Myw4LjM5LS44NC44MS0yLjk4LS42LTQuMTctMS4xOXMtMi4yNy0xLjAzLTMuNTgtMS44NWMtLjc3LS40OS0xLjcxLTEuMDQtMi4zMi0xLjgtMTIuMDItMTYuMTMtMzEuMzctMjUuMjItNTEuMTgtMjcuMDgtLjktLjExLTIuNzQtLjIyLTIuOTUtLjkzLS4xNC0uOTgsMi40My0yLjEzLDMuNDctMi41OCwyNS40LTEwLjUxLDQ1Ljc2LDQuNTUsNTcuODUsMjYuOTZsLjA1LjA4WiIvPgogICAgICAgIDwvZz4KICAgICAgICA8cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik0xMTEuMTIsODYuMWMtLjMtNS42OC02LjU3LTYuODEtMTEuODMtNi41OS04LjI0LjI3LTE2LjA1LDIuMjEtMjMuNjMsNC45N2wtLjA4LjAzYy02LjkyLDIuMDktMTMuMyw5LjQ2LTE2LjU0LDE2LjI3LTE0Ljk3LDMzLjYyLDUzLjk4LDguNzQsNTIuMDgtMTQuNjhaTTk0LjQ2LDk1LjE2Yy0zLjkzLDMuMzQtOS40MSw1Ljc2LTE0LjM1LDcuNjEtMy40OCwxLjMzLTExLjM3LDQuMDQtNy44Ny0yLjksMS4zNS0yLjg5LDQuMTctNi44NCw3Ljk4LTguODcsNC4zNy0yLjMsMTAuMTMtMi42LDE0LjgzLTIsMS4xMS4xNSwyLjgzLjU3LDIuOTksMS4xNnYuMDNjLjMyLDEuMzYtMS4yNiwyLjk3LTMuNTgsNC45N1oiLz4KICAgICAgICA8cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik0xMDUsMTguOTZjLjUyLjgzLjY1LjkyLjc0LDEuNS42Nyw0LjE2LTEuMTYsNi42NC0uMzksMTEuMDcuOTcsNS42NCwyLjg0LDEzLjAxLDguNDcsMTYuMTEsMy43OCwyLjA4LDUuMzQtMy43Myw3LjI2LTUuMjMsMi42My0yLjA0LDYuNzguMjMsOS4zOS42Myw0Ljk0Ljc2LDE0LjQ5LDMuMzIsMTcuMzYtMS4xMyw0LjQ3LTYuOTUtMi4yLTguMTMtNi40My0xMS40LTUtMy4wMy04LjQ1LTcuMTctMTMuMDctMTAuNjQtMi43OC0yLjA4LTcuNTctMS4yNS0xMC4yLTMuNi0yLjYzLTIuMzQtMi40OC02LjkyLTQuMS05LjYtMS40LTIuMy0zLjU5LTQuMTItNS40My01LjE1LTIuMy0xLjI5LTQuNzktMi4zLTYuNjEtLjc4LTMuNjUsMy4wNS0xLjI2LDguMjUtLjM0LDExLjcuNzYsMi44NSwyLjA4LDQuNDcsMy4zNSw2LjVaIi8+CiAgICAgIDwvZz4KICAgICAgPGc+CiAgICAgICAgPHBhdGggY2xhc3M9ImNscy0xIiBkPSJNMjAzLjAzLDExMC4xVjQ1LjU1aDEyLjU2djguNTFsLjAzLDEuOTQsMS4yNy0yLjA3YzEuMDYtMS41LDIuNTItMy4wMyw0LjM0LTQuNTMsMS44MS0xLjUxLDQuMDQtMi43Niw2LjYyLTMuNzQsMi41Ni0uOTgsNS4zLTEuNDcsOC4xNS0xLjQ3aDIuODZ2MTMuMWMtLjU5LS4xMi0xLjIzLS4yMy0xLjg5LS4zMy0uOTgtLjE0LTEuOTktLjIxLTMuMDItLjIxLTMuMjgsMC02LjM4Ljg4LTkuMTksMi42Mi0yLjgxLDEuNzQtNS4wNyw0LjItNi43LDcuMzMtMS42MywzLjEyLTIuNDYsNi42NC0yLjQ2LDEwLjQ1djMyLjk2aC0xMi41NloiLz4KICAgICAgICA8cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik0zMDQuODMsNjAuOGMtMi45NS01LjEzLTcuMDQtOS4yMy0xMi4xNy0xMi4xNy01LjEzLTIuOTUtMTAuODYtNC40NC0xNy4wMy00LjQ0cy0xMS44OSwxLjQ5LTE3LjAzLDQuNDRjLTUuMTMsMi45NS05LjIzLDcuMDQtMTIuMTcsMTIuMTctMi45NSw1LjE0LTQuNDQsMTAuODYtNC40NCwxNy4wM3MxLjQ5LDExLjg5LDQuNDQsMTcuMDNjMi45NCw1LjE0LDcuMDQsOS4yMywxMi4xNywxMi4xNyw1LjEzLDIuOTUsMTAuODYsNC40NSwxNy4wMyw0LjQ1czExLjktMS41LDE3LjAzLTQuNDVjNS4xNC0yLjk1LDkuMjMtNy4wNCwxMi4xNy0xMi4xNywyLjk1LTUuMTMsNC40NC0xMC44Niw0LjQ0LTE3LjAzcy0xLjQ5LTExLjg5LTQuNDQtMTcuMDNaTTI5My45OSw4OC45N2MtMS44MiwzLjMyLTQuMzgsNS45NC03LjYsNy44MS0zLjIxLDEuODctNi44MywyLjgxLTEwLjc1LDIuODFzLTcuNTQtLjk0LTEwLjc1LTIuODFjLTMuMjItMS44Ny01Ljc4LTQuNDktNy42LTcuODEtMS44Mi0zLjMtMi43NC03LjA1LTIuNzQtMTEuMTRzLjkzLTcuODQsMi43NC0xMS4xNWMxLjgyLTMuMyw0LjM3LTUuOTMsNy42LTcuODEsMy4yMi0xLjg2LDYuODMtMi44MSwxMC43NS0yLjgxaDBjMy45MSwwLDcuNTMuOTUsMTAuNzUsMi44MiwzLjIzLDEuODcsNS43OCw0LjUsNy42LDcuODEsMS44MSwzLjMxLDIuNzMsNy4wNSwyLjczLDExLjE0cy0uOTMsNy44NC0yLjczLDExLjE0WiIvPgogICAgICAgIDxyZWN0IGNsYXNzPSJjbHMtMSIgeD0iMzE3Ljg1IiB5PSIxOC42OCIgd2lkdGg9IjEyLjU2IiBoZWlnaHQ9IjkxLjQyIi8+CiAgICAgICAgPHJlY3QgY2xhc3M9ImNscy0xIiB4PSIzMzkuNTYiIHk9IjE4LjY4IiB3aWR0aD0iMTIuNTYiIGhlaWdodD0iOTEuNDIiLz4KICAgICAgICA8cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik00MTYuODcsODQuNjFjLTEuNTYsMi44OC0zLjUxLDUuNDgtNS44LDcuNzItMi40NCwyLjM5LTUuNTQsNC4yNC05LjI0LDUuNDctMi40OS44My00Ljk3LDEuMjYtNy40LDEuMjYtLjk0LDAtMS44OS0uMDYtMi44My0uMTktMy4zNS0uNDUtNi4zOC0xLjYtOS4wMS0zLjQzLTIuNjMtMS44Mi00LjY5LTQuMTYtNi4xMi02Ljk0bC0uMy0uNTgsNDguMTgtMTYuMTljLS4wOC0uNjYtLjIyLTEuNDgtLjQyLTIuNDQtLjI2LTEuMi0uNjEtMi40NC0xLjA0LTMuNy0xLjktNS42Ny00Ljk1LTEwLjQtOS4wNS0xNC4wNi00LjA5LTMuNjUtOC44OS02LjAzLTE0LjI4LTcuMS0xLjkzLS4zOC0zLjktLjU3LTUuODYtLjU3LTMuNTQsMC03LjE3LjYyLTEwLjgxLDEuODQtNS42NywxLjktMTAuNDYsNS4xMS0xNC4yNSw5LjU0LTMuNzksNC40My02LjMsOS42LTcuNDcsMTUuMzYtMS4xOCw1Ljc1LS43NywxMS42NCwxLjE5LDE3LjQ4LDEuOTYsNS44NCw1LjIsMTAuOCw5LjYzLDE0LjczLDQuNDMsMy45Myw5LjYxLDYuNSwxNS40MSw3LjY2LDIuMTYuNDMsNC4zNC42NSw2LjUuNjUsMy42NiwwLDcuMzgtLjYzLDExLjA2LTEuODcsNS45NC0xLjk5LDEwLjg2LTQuOTMsMTQuNjItOC43NCwzLjYxLTMuNjUsNi4xNS03LjMyLDcuNTYtMTAuOTFsLTEwLjI3LTUuMDFaTTM3Mi42Niw3Ny45OGMtLjE1LTMuMzUuMjEtNi40OCwxLjA5LTkuMjkuODgtMi44MSwyLjQyLTUuMzMsNC41Ni03LjQ5LDIuMTQtMi4xNCw0Ljg4LTMuNzksOC4xNS00Ljg4LDIuMTEtLjcxLDQuMi0xLjA4LDYuMi0xLjA4LjkzLDAsMS44NS4wOCwyLjc0LjIzLDIuODMuNDgsNS40NCwxLjYxLDcuNzYsMy4zNSwyLjMxLDEuNzQsNC4zMSw0LjA3LDUuOTcsNi45NWwuMzMuNTktMzYuNzUsMTIuMzUtLjAzLS43NVoiLz4KICAgICAgICA8cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik00ODYuOTIsMTguNjh2MzYuMDdsLS4wMywxLjMtMS4xNC0xLjQ2Yy0xLjA2LTEuNjctMi42NS0zLjI5LTQuNzItNC44NC0yLjA5LTEuNTUtNC42Ni0yLjg5LTcuNjYtMy45Ni0yLjk5LTEuMDctNi4yMi0xLjYxLTkuNTktMS42MS01LjM0LDAtMTAuMzYsMS40Ny0xNC45Niw0LjM2LTQuNTksMi45LTguMzMsNi45NS0xMS4wOSwxMi4wNC0yLjc3LDUuMDktNC4xOCwxMC45LTQuMTgsMTcuMjVzMS40MSwxMi4xNSw0LjE4LDE3LjI1YzIuNzcsNS4wOSw2LjUsOS4xNCwxMS4wOSwxMi4wNCw0LjU4LDIuODksOS42Miw0LjM2LDE0Ljk2LDQuMzZoMGMzLjM5LDAsNi42MS0uNTQsOS41OS0xLjYxLDMtMS4wOCw1LjU4LTIuNDEsNy42NS0zLjk2LDIuMDktMS41NSwzLjY3LTMuMTgsNC43Mi00Ljg1bDEuMTctMS44NnYxMC45aDEyLjU2VjE4LjY4aC0xMi41NlpNNDg0LjE4LDg4Ljk3Yy0xLjgyLDMuMzItNC4zLDUuOTQtNy4zOCw3LjgxLTMuMDgsMS44Ny02LjU0LDIuODItMTAuMjksMi44MnMtNy4yLS45NS0xMC4yOC0yLjgyLTUuNTYtNC40OS03LjM4LTcuODFjLTEuODEtMy4zLTIuNzMtNy4wNS0yLjczLTExLjE0cy45Mi03LjgzLDIuNzMtMTEuMTRjMS44Mi0zLjMxLDQuMy01LjkzLDcuMzgtNy44MSwzLjA5LTEuODcsNi41NS0yLjgyLDEwLjI4LTIuODJoMGMzLjczLDAsNy4xOS45NiwxMC4yOSwyLjgzLDMuMDgsMS44OCw1LjU3LDQuNSw3LjM4LDcuOCwxLjgyLDMuMzEsMi43NCw3LjA2LDIuNzQsMTEuMTVzLS45Myw3LjgzLTIuNzQsMTEuMTRaIi8+CiAgICAgIDwvZz4KICAgIDwvZz4KICA8L2c+Cjwvc3ZnPg==";
function RolledLogo({height}) {
  height = height || 48;
  return React.createElement("img", {src: LOGO_B64, style: {height: height, width: "auto", display: "block"}, alt: "Rolled"});
}

// ===========================================
// APP - persists data across logins
// ===========================================
// App-level state for data that persists across logins
// sellOutTimes = flat {sku: latestSoldOutTime or null} (used by EOD discrepancy check + backend sync)
// sellOutCycles = {sku: [{soldOut, restock}, ...]} — full history of sell-out / restock events per SKU per day
// stockRequests = [{id, item, time, dateStr, custom:bool}] — single running list, no Next Drop/Next Day split
// tgtgSold / tgtgNC default null (not 0) — EOD requires explicit entry, even
// if the true value is zero. Submit button is gated on both being non-null.
const appData = {waste:{},tgtgSold:null,tgtgNC:null,sellOutTimes:{},sellOutCycles:{},stockRequests:[],reportSubmitted:false};
SKU_LIST.forEach(s => appData.waste[s]=0);

// Hydrate from localStorage on first load — survives tab close / accidental refresh mid-service.
// Stamped with today's date; yesterday's leftovers are auto-cleared so a new day starts fresh.
const LS_KEY = "rolled.appData";
// UK-local day key so the rollover boundary lines up with shop closing (00:00
// London time), not 00:00 UTC (which is 01:00 BST and falls during early-AM
// closing routines). Intl.DateTimeFormat with timeZone:Europe/London handles
// BST/GMT automatically. en-CA gives YYYY-MM-DD natively.
function todayKey(){
  try{ return new Intl.DateTimeFormat("en-CA",{timeZone:"Europe/London"}).format(new Date()); }
  catch(e){ return new Date().toISOString().slice(0,10); }
}
(function hydrateAppData(){
  try{
    const raw = localStorage.getItem(LS_KEY);
    if(!raw) return;
    const parsed = JSON.parse(raw);
    // A 2026-05-15 one-shot migration used to sit here. It dropped anything
    // written by a build that didn't carry a "day-rollover-watchdog" marker in
    // BUILD_VERSION — except NO BUILD_VERSION HAS EVER CONTAINED THAT STRING.
    // All 89 values ever set were checked; not one matches. persistAppData
    // writes `build: BUILD_VERSION`, so the test was false on every load and
    // this tablet has been deleting its own saved work every time the page
    // opened, for three months.
    //
    // What that cost: a refresh, a crash or a tab restore part-way through the
    // end-of-day count wiped the per-SKU waste, the TGTG numbers and the
    // sell-out times, and the screen came back to zeros. Staff re-typed them.
    // The one-shot has therefore already run, on every device, hundreds of
    // times — there cannot be a pre-2026-05-15 payload left anywhere — so it is
    // removed rather than repaired.
    //
    // The day-rollover case it was written for is still covered by the date
    // check on the next line, which is the check that actually works.
    if(!parsed || parsed.date !== todayKey()){ localStorage.removeItem(LS_KEY); return; }
    const d = parsed.data || {};
    // Only this shop's current rolls come back, so nothing rides along off the
    // screen (in the total and the submitted report but never shown). A count
    // saved under a roll that has since been swapped off the list (a promote
    // landing mid-count) moves to the roll that took its row — see savedCount.
    if(d.waste) SKU_LIST.forEach(s => { const v = savedCount(d.waste, s); if(v !== undefined) appData.waste[s] = v; });
    SKU_LIST.forEach(s => { if(appData.waste[s] === undefined) appData.waste[s] = 0; });
    if(typeof d.tgtgSold === "number") appData.tgtgSold = d.tgtgSold;
    if(typeof d.tgtgNC === "number") appData.tgtgNC = d.tgtgNC;
    if(d.sellOutTimes) appData.sellOutTimes = d.sellOutTimes;
    if(d.sellOutCycles) appData.sellOutCycles = d.sellOutCycles;
    if(Array.isArray(d.stockRequests)) appData.stockRequests = d.stockRequests;
    if(typeof d.reportSubmitted === "boolean") appData.reportSubmitted = d.reportSubmitted;
  }catch(e){/* corrupt localStorage — ignore and start fresh */}
})();
function persistAppData(){
  try{
    localStorage.setItem(LS_KEY, JSON.stringify({date: todayKey(), build: BUILD_VERSION, data: {
      waste: appData.waste,
      tgtgSold: appData.tgtgSold,
      tgtgNC: appData.tgtgNC,
      sellOutTimes: appData.sellOutTimes,
      sellOutCycles: appData.sellOutCycles,
      stockRequests: appData.stockRequests,
      reportSubmitted: appData.reportSubmitted,
    }}));
  }catch(e){/* quota / private mode — ignore */}
}

// Applies admin-dashboard metadata overrides on top of a TOOLS entry. Only
// non-empty overrides replace the hardcoded defaults — blank fields mean
// "keep the default from this file". Keyed by the tool's `fn` (skipped for
// placeholder tiles that have no backend function yet — they can't be edited
// from the admin editor until they get a `fn` assigned).
function applyFunctionMeta(tool, metadata) {
  if (!tool || !tool.fn || !metadata) return tool;
  const m = metadata[tool.fn];
  if (!m) return tool;
  const out = {...tool};
  if (m.label && String(m.label).trim()) out.label = String(m.label);
  if (m.desc  && String(m.desc).trim())  out.desc  = String(m.desc);
  if (m.icon  && String(m.icon).trim())  out.icon  = String(m.icon);
  return out;
}

// Tile-level overrides (keyed by tool.id). Wins over functionMetadata for
// label/desc/icon so admin renames + state flips flow to tablets without
// a deploy. Unlike the fn-keyed version, this also reaches tiles that have no
// backend fn (Coming Soon placeholders). Composed after applyFunctionMeta.
// v2 (2026-04-24): explicit `state` takes precedence over the legacy
// `comingSoon` boolean. iconKind/thumbnail/banner/manages are carried through
// for future FOH rendering — not yet consumed in the grid.
function applyTileOverride(tool, tileOverrides) {
  if (!tool || !tileOverrides) return tool;
  const o = tileOverrides[tool.id];
  if (!o) return tool;
  const out = {...tool};
  if (o.label && String(o.label).trim()) out.label = String(o.label);
  if (o.desc  && String(o.desc).trim())  out.desc  = String(o.desc);
  if (o.icon  && String(o.icon).trim())  out.icon  = String(o.icon);
  if (o.state === "live" || o.state === "coming_soon" || o.state === "hidden") {
    out.state = o.state;
    out.on = (o.state === "live"); // keep legacy consumers sane
  } else if (o.comingSoon === true) {
    out.state = "coming_soon";
    out.on = false;
  }
  if (o.iconKind) out.iconKind = o.iconKind;
  if (o.thumbnailUrl) out.thumbnailUrl = o.thumbnailUrl;
  if (o.bannerUrl) out.bannerUrl = o.bannerUrl;
  if (o.manages) out.manages = o.manages;
  return out;
}

// ===========================================
// FOH CLOCK-IN — replaces Bizimply Timestation as the kiosk source-of-truth.
// Roster banner on home polls getStaffOnShift every 30s. Tablet-only — phones
// + desktops never see the banner or its modals (you can't be on shift but
// pretending to be on a phone). Backend = staffShiftStart / staffShiftEnd /
// getStaffOnShift on Apps Script @147+. Photo upload to Drive is v2 — v1
// captures the JPEG client-side and ships base64 with the POST so the row
// records the bytes, but PhotoUrl stays empty server-side until the Drive
// helper lands.
// ===========================================

// Helper: is `staffName` in the current on-shift roster? Used by the
// per-action PIN modal to gate every action behind a clock-in. Backend
// backstops the same rule with a NOT_CLOCKED_IN response if the cache
// is stale.
function staffIsOnShift(staffName, staffOnShift) {
  if (!staffName || !Array.isArray(staffOnShift)) return false;
  const target = String(staffName).trim().toLowerCase();
  return staffOnShift.some(function(s){
    return s && String(s.name || "").trim().toLowerCase() === target;
  });
}

// UUID v4 — minted on clock-in, reused server-side on clock-out (Bizimply V1
// rejects fresh UUIDs on clock_out per backend handler comment, so we keep
// the original UUID in the row's RawJson).
function uuid4() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c){
    var r = Math.random()*16|0, v = c==="x" ? r : (r&0x3|0x8);
    return v.toString(16);
  });
}

// Tablet-only gate for the Clock-In flow. iPads + Android tablets pass.
// Phones + desktops fail. Local preview hosts (localhost / LAN / 127.x) also
// pass so QA + dev work without a real iPad — production hostnames respect
// getDeviceInfo() strictly.
function isTabletKioskHost() {
  try {
    var host = (typeof window !== "undefined" && window.location && window.location.hostname) || "";
    if (/^(localhost|127\.0\.0\.1)$/.test(host)) return true;
    if (/^(192\.168\.|10\.0\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/.test(host)) return true;
    return getDeviceInfo().device === "tablet";
  } catch (e) { return false; }
}

// Camera hook — wraps getUserMedia + cleanup. Stops every track when active
// flips false or component unmounts (memory-leak fix). Capture returns a
// JPEG dataURL at q=0.7, target width 720. If the device blocks camera or
// the page is on plain HTTP (Safari blocks getUserMedia outside HTTPS +
// localhost), error stays populated and capture returns null — caller
// should fall back to a Skip button.
function useClockInCamera(active) {
  var videoRef = React.useRef(null);
  var streamRef = React.useRef(null);
  var [error, setError] = React.useState("");

  React.useEffect(function(){
    if (!active) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(function(t){ t.stop(); });
        streamRef.current = null;
      }
      setError("");
      return;
    }
    var cancelled = false;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Camera not available on this device");
      return function(){ cancelled = true; };
    }
    navigator.mediaDevices.getUserMedia({video: {facingMode: "user"}, audio: false})
      .then(function(stream){
        if (cancelled) { stream.getTracks().forEach(function(t){ t.stop(); }); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          var p = videoRef.current.play();
          if (p && p.catch) p.catch(function(){});
        }
      })
      .catch(function(e){
        if (cancelled) return;
        // Surface a brief reason so the Skip-photo button has context.
        var msg = (e && e.name === "NotAllowedError") ? "Camera blocked — tap Skip" :
                  (e && e.name === "NotFoundError")   ? "No camera on this tablet" :
                  (e && e.message) || "Camera unavailable";
        setError(msg);
      });
    return function(){
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(function(t){ t.stop(); });
        streamRef.current = null;
      }
    };
  }, [active]);

  var capture = React.useCallback(function(){
    var v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    var targetW = 720;
    var w = Math.min(targetW, v.videoWidth);
    var h = Math.round(w * (v.videoHeight / v.videoWidth));
    var c = document.createElement("canvas");
    c.width = w; c.height = h;
    var ctx = c.getContext("2d");
    // Mirror the canvas to match the mirrored <video> preview (`transform:
    // scaleX(-1)` is preview-only — without this, the captured JPEG would
    // be the un-mirrored front-camera frame, so the audit photo wouldn't
    // match what the staffer saw on screen mid-capture). Code-review fix.
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, w, h);
    return c.toDataURL("image/jpeg", 0.7);
  }, []);

  return {videoRef: videoRef, error: error, capture: capture};
}

// Roster banner — empty ("No one is clocked in" + Clock in CTA) vs live
// ("3 on shift: Sharon · Muhammad · Jo" + Clock out CTA). Sits at the top
// of the home view, above the tile grid. Tablet-only — caller gates the
// device-class check.
function ClockInRosterBanner({ staffOnShift, onClockIn, onClockOut }) {
  const live = staffOnShift && staffOnShift.length > 0;
  const names = (staffOnShift || []).map(function(s){ return s.name; }).filter(Boolean);
  const baseStyle = {
    display:"flex", alignItems:"center", justifyContent:"space-between", gap:14,
    width:"100%", maxWidth:960, margin:"0 auto 18px", padding:"18px 22px",
    borderRadius:18, minHeight:108,
    fontFamily:"Manrope,sans-serif", textAlign:"left", boxSizing:"border-box",
    flexWrap:"wrap"
  };
  const liveStyle = {
    ...baseStyle,
    background:"linear-gradient(135deg,#0f6849 0%,#185a44 100%)",
    color:B.rice, border:"none", boxShadow:"0 16px 32px -16px rgba(0,57,44,0.5)"
  };
  // Empty state matches the signed-off mockup: white Rice card on the
  // dark Nori home, Nori dashed border, Nori CTA pill with Wasabi text.
  const emptyStyle = {
    ...baseStyle,
    background:B.rice,
    border:"1px dashed rgba(0,57,44,0.25)",
    color:B.nori,
    cursor:"pointer"
  };
  const eyebrow = (col)=>({fontSize:"0.6rem", letterSpacing:"0.2em", textTransform:"uppercase", fontWeight:700, color:col, display:"flex", alignItems:"center", gap:6});
  const title = {fontFamily:"gelica,serif", fontSize:"1.4rem", fontWeight:700, lineHeight:1.1, marginTop:6};
  const rosterLine = (col)=>({fontSize:"0.82rem", fontWeight:500, lineHeight:1.35, color:col, marginTop:6, overflow:"hidden", textOverflow:"ellipsis"});
  // Buttons share padding/shape but differ in fill so a quick glance can't
  // mistake one for the other. Wasabi=Clock in (primary positive action),
  // Rice=Clock out (secondary, distinct fill so taps can't go wrong on a
  // tablet kiosk).
  const ctaBase = {padding:"14px 22px", borderRadius:999, fontWeight:700, fontSize:"0.85rem", minWidth:130, textAlign:"center", border:"none", fontFamily:"Manrope,sans-serif", cursor:"pointer", flexShrink:0};
  const ctaIn   = {...ctaBase, background:B.wasabi, color:B.nori};
  const ctaOut  = {...ctaBase, background:B.rice,   color:B.nori};
  const ctaEmpty= {...ctaBase, background:B.nori,   color:B.wasabi};

  if (live) {
    // Whole card is NOT a button (would nest buttons illegally). Each pill is
    // its own real button; the eyebrow+title+roster on the left is purely
    // informational.
    return (
      <div style={liveStyle}>
        <div style={{flex:"1 1 200px", minWidth:0}}>
          <div style={eyebrow("rgba(212,251,111,0.85)")}>
            <span style={{display:"inline-block", width:7, height:7, borderRadius:"50%", background:B.wasabi}}></span>
            On shift
          </div>
          <div style={title}>{names.length} on shift</div>
          <div style={rosterLine("rgba(255,255,255,0.78)")}>{names.join(" · ")}</div>
        </div>
        <div style={{display:"flex", gap:10, flexShrink:0, flexWrap:"wrap", justifyContent:"flex-end"}}>
          <button onClick={onClockIn}  style={ctaIn}  aria-label="Start clock-in flow">Clock in</button>
          <button onClick={onClockOut} style={ctaOut} aria-label="Open clock-out picker">Clock out</button>
        </div>
      </div>
    );
  }
  return (
    <button style={emptyStyle} onClick={onClockIn} aria-label="Start clock-in flow">
      <div style={{flex:1, minWidth:0}}>
        <div style={eyebrow("rgba(0,57,44,0.55)")}>Clock in</div>
        <div style={title}>No one is clocked in</div>
        <div style={rosterLine("rgba(0,57,44,0.55)")}>Tap below when you start your shift</div>
      </div>
      <span style={ctaEmpty}>Clock in</span>
    </button>
  );
}

// Numpad block reused inside the dark scrim — visual match for the mockup's
// landscape PIN step. Auto-fires onComplete when the 4th digit lands so the
// caller can advance to the next step without a Submit button.
function ScrimNumpad({ pin, setPin, busy, onComplete }) {
  const handle = (d)=>{
    if (busy) return;
    setPin(p => {
      if (p.length >= 4) return p;
      const next = p + d;
      if (next.length === 4 && onComplete) setTimeout(()=>onComplete(next), 60);
      return next;
    });
  };
  const clear = ()=>{ if (!busy) setPin(""); };
  const back = ()=>{ if (!busy) setPin(p => p.slice(0,-1)); };
  const numStyle = {
    height:62, borderRadius:14,
    background:"rgba(255,255,255,0.08)",
    border:"1px solid rgba(255,255,255,0.12)",
    color: B.rice, fontFamily:"gelica,serif", fontSize:"1.5rem", fontWeight:700,
    cursor: busy ? "default" : "pointer"
  };
  const muted = {...numStyle, background:"transparent", border:"1px solid transparent", color:"rgba(255,255,255,0.6)", fontSize:"0.82rem", fontFamily:"Manrope,sans-serif", fontWeight:600};
  return (
    <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, width:"100%", maxWidth:340}}>
      {["1","2","3","4","5","6","7","8","9"].map(d=>(
        <button key={d} style={numStyle} onClick={()=>handle(d)} disabled={busy}>{d}</button>
      ))}
      <button style={muted} onClick={clear} disabled={busy}>Clear</button>
      <button style={numStyle} onClick={()=>handle("0")} disabled={busy}>0</button>
      <button style={muted} onClick={back} disabled={busy}>←</button>
    </div>
  );
}

// PIN dots row — mirrors the existing login-screen visual but tuned for the
// dark scrim background.
function ScrimPinDots({ count }) {
  return (
    <div style={{display:"flex", gap:14, marginBottom:12}}>
      {[0,1,2,3].map(i=>(
        <div key={i} style={{
          width:14, height:14, borderRadius:"50%",
          background: count>i ? B.wasabi : "rgba(255,255,255,0.18)",
          boxShadow: count>i ? "0 0 0 3px rgba(212,251,111,0.18)" : "none",
          transition:"all 0.15s ease"
        }}/>
      ))}
    </div>
  );
}

// Full-screen scrim used for both clock-in (PIN → Camera → Success) and
// clock-out (Picker → PIN → Success). Dark Nori background, Wasabi accents,
// matches the v3 mockup. Closes via the ✕ in the top-right (calls onClose).
function ClockScrim({ children, headerLabel, onClose }) {
  // Portal to document.body so the fixed-position scrim escapes the home
  // view's transformed ancestor (S.homeRoot has `animation: viewIn` which
  // applies a transform — that creates a new containing block, causing the
  // scrim to size to the home content (~1845px) instead of the viewport.
  // Portaling ensures the scrim sits at the body level where `position:fixed`
  // is correctly bound to the actual viewport.
  return ReactDOM.createPortal(
    <div style={{
      position:"fixed", inset:0, background:B.nori, color:B.rice, zIndex:600,
      display:"flex", flexDirection:"column",
      fontFamily:"Manrope,sans-serif"
    }}>
      <div style={{
        flexShrink:0,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.08)"
      }}>
        <div style={{fontSize:"0.6rem", letterSpacing:"0.2em", textTransform:"uppercase", color:B.wasabi, fontWeight:700}}>
          {headerLabel}
        </div>
        <button onClick={onClose} aria-label="Close" style={{
          background:"transparent", border:"none", color:B.rice, fontSize:"1.4rem", cursor:"pointer", lineHeight:1, padding:"4px 8px"
        }}>✕</button>
      </div>
      <div style={{flex:1, minHeight:0, display:"flex", flexDirection:"column"}}>
        {children}
      </div>
    </div>,
    document.body
  );
}

// Camera step — mounts the useClockInCamera hook so getUserMedia starts /
// stops with the step's lifecycle (avoids leaving the camera light on if
// the user backs out of the flow). Shows a live <video> with a Wasabi-edge
// guide and a shutter button. Skip-photo button kicks in if the camera
// errored — keeps the flow shippable on plain HTTP local previews where
// Safari blocks getUserMedia.
function ClockCameraStep({ staffName, busy, onCapture, onSkip }) {
  const cam = useClockInCamera(true);
  const [flash, setFlash] = useState(false);
  const tryCapture = ()=>{
    if (busy) return;
    // Visual feedback: white flash overlay so the staffer sees a clear
    // "shutter clicked" cue. Yield two animation frames so React mounts
    // the flash overlay AND the browser paints it before we run the
    // synchronous canvas grab — without the RAFs the flash either doesn't
    // render at all or paints for a single frame.
    setFlash(true);
    requestAnimationFrame(()=>requestAnimationFrame(()=>{
      const dataUrl = cam.capture();
      setTimeout(()=>setFlash(false), 220);
      if (!dataUrl) { onCapture(""); return; }
      // Strip the "data:image/jpeg;base64," prefix — backend expects the raw
      // base64 only (and wants a tight payload anyway).
      const idx = dataUrl.indexOf("base64,");
      const base64 = idx >= 0 ? dataUrl.slice(idx + 7) : dataUrl;
      onCapture(base64);
    }));
  };
  return (
    <>
      <style>{`
        @keyframes clockin-flash {
          0%   { opacity: 0; }
          12%  { opacity: 0.95; }
          100% { opacity: 0; }
        }
      `}</style>
      <div style={{flex:1, padding:"14px 18px 8px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", overflow:"hidden", minHeight:0, gap:14}}>
        <div style={{position:"relative", width:"100%", maxWidth:360, aspectRatio:"1/1", borderRadius:18, overflow:"hidden", background:"#0e1d18", flexShrink:0}}>
          <video ref={cam.videoRef} playsInline muted autoPlay style={{
            position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover",
            transform:"scaleX(-1)" /* mirror so it feels like a selfie */
          }}/>
          <div style={{
            position:"absolute", inset:"8%",
            border:"2px dashed rgba(212,251,111,0.5)", borderRadius:24, pointerEvents:"none"
          }}/>
          <div style={{
            position:"absolute", top:18, left:0, right:0, textAlign:"center",
            fontSize:"0.78rem", fontWeight:600, color:B.wasabi, letterSpacing:"0.05em",
            textShadow:"0 1px 2px rgba(0,0,0,0.4)"
          }}>
            {cam.error ? cam.error : "Frame your face inside the box"}
          </div>
          <div style={{
            position:"absolute", left:14, bottom:14,
            background:"rgba(0,57,44,0.6)", border:"1px solid rgba(212,251,111,0.4)",
            color:B.rice, padding:"6px 12px", borderRadius:999, fontSize:"0.75rem", fontWeight:600
          }}>
            Capturing for <b style={{color:B.wasabi}}>{staffName}</b>
          </div>
          {/* Capture flash — white overlay, fades 220ms */}
          {flash && (
            <div style={{
              position:"absolute", inset:0, background:B.rice, pointerEvents:"none",
              animation:"clockin-flash 220ms ease-out forwards"
            }}/>
          )}
        </div>
        <div style={{textAlign:"center", fontSize:"0.78rem", color:"rgba(255,255,255,0.78)", maxWidth:360}}>
          {busy ? "Sending..." : "Look at the camera, then tap the shutter."}
        </div>
      </div>
      <div style={{padding:"10px 18px 22px", display:"flex", flexDirection:"column", gap:10, alignItems:"center", flexShrink:0}}>
        <button onClick={tryCapture} disabled={busy} aria-label="Capture photo and clock in" style={{
          width:80, height:80, borderRadius:"50%",
          border:"4px solid "+B.wasabi, background:"rgba(255,255,255,0.08)",
          display:"grid", placeItems:"center", cursor: busy?"default":"pointer", padding:0,
          transition:"transform 80ms ease",
          ...(busy ? {opacity:0.5} : {})
        }}
        onTouchStart={e=>{ e.currentTarget.style.transform="scale(0.94)"; }}
        onTouchEnd={e=>{ e.currentTarget.style.transform="scale(1)"; }}>
          <span style={{width:54, height:54, borderRadius:"50%", background:B.wasabi, display:"block"}}></span>
        </button>
        {cam.error && (
          <button onClick={onSkip} disabled={busy} style={{
            background:"transparent", border:"1px solid rgba(255,255,255,0.18)",
            color:"rgba(255,255,255,0.7)", padding:"8px 16px", borderRadius:999,
            fontSize:"0.78rem", fontFamily:"Manrope,sans-serif", cursor: busy?"default":"pointer"
          }}>Skip photo &amp; clock in</button>
        )}
      </div>
    </>
  );
}

// Clock-out picker — list of staff currently on shift. Tap a row to nominate
// who's clocking out; the next step asks for that staff's PIN. Mirrors the
// mockup's ClockOutPicker layout (avatar + name + on-since chip).
function ClockOutPickerStep({ staffOnShift, onPick }) {
  const initials = (n)=>{
    const parts = String(n||"").trim().split(/\s+/);
    return ((parts[0]||"")[0] + ((parts[1]||"")[0]||"")).toUpperCase();
  };
  const fmt = (iso)=>{
    if (!iso) return "—";
    try { return new Date(iso).toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"}); } catch(e) { return "—"; }
  };
  return (
    <div style={{flex:1, overflowY:"auto", padding:"14px 18px", display:"flex", flexDirection:"column", alignItems:"center", gap:14}}>
      <div style={{fontFamily:"gelica,serif", fontSize:"1.5rem", fontWeight:700, color:B.rice, textAlign:"center"}}>Who&apos;s clocking out?</div>
      <div style={{fontSize:"0.82rem", color:"rgba(255,255,255,0.7)", textAlign:"center", maxWidth:380, marginBottom:6}}>
        Tap your name. We&apos;ll ask for your PIN to confirm.
      </div>
      <div style={{display:"flex", flexDirection:"column", gap:10, width:"100%", maxWidth:420}}>
        {(staffOnShift || []).map((s, i) => (
          <button key={(s.name||"")+i} onClick={()=>onPick(s)} style={{
            display:"flex", alignItems:"center", gap:14,
            background:"rgba(255,255,255,0.06)",
            border:"1px solid rgba(255,255,255,0.12)",
            borderRadius:14, padding:"14px 16px", color:B.rice, textAlign:"left",
            cursor:"pointer", fontFamily:"Manrope,sans-serif", width:"100%"
          }}>
            <div style={{width:42, height:42, borderRadius:"50%", background:B.wasabi, color:B.nori, display:"grid", placeItems:"center", fontFamily:"gelica,serif", fontWeight:700, fontSize:"0.95rem", flexShrink:0}}>
              {initials(s.name)}
            </div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontWeight:600, fontSize:"0.95rem", lineHeight:1.2}}>{s.name || "Unknown"}</div>
              <div style={{fontSize:"0.7rem", color:"rgba(255,255,255,0.6)", marginTop:2}}>On since {fmt(s.clockedInAt)}</div>
            </div>
            <div style={{fontSize:"0.82rem", fontWeight:700, color:B.wasabi, flexShrink:0}}>Clock out →</div>
          </button>
        ))}
        {(!staffOnShift || staffOnShift.length === 0) && (
          <div style={{textAlign:"center", color:"rgba(255,255,255,0.55)", fontSize:"0.82rem", padding:24}}>
            No one&apos;s currently on shift here.
          </div>
        )}
      </div>
    </div>
  );
}

function ClockSuccessStep({ name, time, mode, onDone }) {
  const greeting = mode === "out" ? "See you next time" : "G'day";
  const farewell = mode === "out" ? "Thanks for the shift." : "Have a ripper shift.";
  // Stays on screen until the staffer taps Done (or the ✕ in the header) —
  // no auto-close. Earlier versions auto-dismissed after 2.5s, which was too
  // fast on a tablet held at arm's length and left no clear "I'm done"
  // affordance.
  return (
    <>
      <style>{`
        @keyframes clockin-tick-pop {
          0%   { transform: scale(0.4); opacity: 0; }
          55%  { transform: scale(1.1); opacity: 1; }
          80%  { transform: scale(0.96); }
          100% { transform: scale(1); }
        }
        @keyframes clockin-tick-stroke {
          0%   { stroke-dashoffset: 60; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes clockin-tick-glow {
          0%, 100% { box-shadow: 0 0 0 12px rgba(212,251,111,0.18), 0 12px 24px -8px rgba(212,251,111,0.4); }
          50%      { box-shadow: 0 0 0 18px rgba(212,251,111,0.28), 0 14px 28px -6px rgba(212,251,111,0.55); }
        }
        @keyframes clockin-success-fade {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{
        flex:1, display:"flex", flexDirection:"column", alignItems:"center",
        justifyContent:"center", textAlign:"center", padding:"24px 24px 8px", gap:18,
        animation:"clockin-success-fade 0.45s ease-out both"
      }}>
        <div style={{
          width:104, height:104, borderRadius:"50%", background:B.wasabi,
          display:"grid", placeItems:"center",
          animation:"clockin-tick-pop 0.55s cubic-bezier(.34,1.56,.64,1) both, clockin-tick-glow 2.4s ease-in-out 0.6s infinite"
        }}>
          <svg width="60" height="60" viewBox="0 0 64 64" aria-hidden="true">
            <path d="M14 33 L27 46 L50 21" style={{
              fill:"none", stroke:B.nori, strokeWidth:6, strokeLinecap:"round", strokeLinejoin:"round",
              strokeDasharray:60, strokeDashoffset:60,
              animation:"clockin-tick-stroke 0.4s cubic-bezier(.5,0,.5,1) 0.35s forwards"
            }}/>
          </svg>
        </div>
        <div>
          <h2 style={{fontFamily:"gelica,serif", fontSize:"2.2rem", margin:"6px 0 0", lineHeight:1.05, color:B.rice}}>{greeting}, {name}</h2>
          <div style={{fontFamily:"gelica,serif", fontSize:"3rem", fontWeight:700, color:B.wasabi, letterSpacing:"-0.02em", lineHeight:1, marginTop:8}}>{time}</div>
          <div style={{fontSize:"0.95rem", color:"rgba(255,255,255,0.78)", marginTop:10}}>{farewell}</div>
        </div>
      </div>
      <div style={{padding:"8px 24px 24px", display:"flex", justifyContent:"center", flexShrink:0}}>
        <button onClick={onDone} style={{
          padding:"14px 32px", borderRadius:999, border:"none",
          background:B.wasabi, color:B.nori, fontWeight:700, fontSize:"0.95rem",
          fontFamily:"Manrope,sans-serif", cursor:"pointer", letterSpacing:"0.02em",
          minWidth:160
        }}>Done</button>
      </div>
    </>
  );
}

// Per-action prompt-to-clock-in (2026-04-29). Mounted at the App level when
// a staffer enters their PIN for any per-action gate but isn't currently
// on shift. The CTA closes the prompt and opens the existing clock-in scrim
// — the staffer just retries their action after clocking in. Portaled to
// document.body so the home view's transformed ancestor doesn't squish it.
function NotClockedInPrompt({ staffName, onClockIn, onDismiss }) {
  return ReactDOM.createPortal(
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,57,44,0.72)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:24, zIndex:650, fontFamily:"Manrope,sans-serif"
    }} onClick={onDismiss}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:B.rice, color:B.nori, borderRadius:18, padding:"28px 24px 22px",
        maxWidth:420, width:"100%", boxShadow:"0 24px 48px -12px rgba(0,57,44,0.6)",
        textAlign:"center"
      }}>
        <div style={{fontSize:"2.5rem", marginBottom:6}}>⏰</div>
        <h2 style={{fontFamily:"gelica,serif", fontSize:"1.5rem", margin:"0 0 8px", color:B.nori, lineHeight:1.15}}>
          {staffName ? "G'day, " + staffName + " — clock in first?" : "Clock in first?"}
        </h2>
        <p style={{fontSize:"0.9rem", color:"rgba(0,57,44,0.65)", margin:"0 0 20px", lineHeight:1.45}}>
          You need to be clocked in to use this. It only takes 5 seconds — tap below to start your shift.
        </p>
        <div style={{display:"flex", gap:10, flexDirection:"column"}}>
          <button onClick={onClockIn} style={{
            background:B.wasabi, color:B.nori, border:"none", borderRadius:999,
            padding:"14px 22px", fontWeight:700, fontSize:"0.95rem",
            fontFamily:"Manrope,sans-serif", cursor:"pointer"
          }}>Clock in now</button>
          <button onClick={onDismiss} style={{
            background:"transparent", color:"rgba(0,57,44,0.55)", border:"none",
            borderRadius:999, padding:"10px 22px", fontWeight:600, fontSize:"0.85rem",
            fontFamily:"Manrope,sans-serif", cursor:"pointer"
          }}>Not now</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Step-router for the Clock-In scrim. Mounted only while a flow is active —
// the camera hook (inside ClockCameraStep) starts and stops with this
// component's lifecycle, so backing out of the flow always frees the camera.
// All flow state lives in clockFlow on the App; this component just reads
// + dispatches.
function ClockFlowScrimRenderer({ clockFlow, setClockFlow, submitClockIn, submitClockOut, closeClock, staffOnShift, staffPins }) {
  const headerLabel =
    clockFlow.mode === "in"  && clockFlow.step === "pin"      ? "Step 1 of 2 · PIN" :
    clockFlow.mode === "in"  && clockFlow.step === "camera"   ? "Step 2 of 2 · Photo" :
    clockFlow.mode === "in"  && clockFlow.step === "success"  ? "Done" :
    clockFlow.mode === "out" && clockFlow.step === "picker"   ? "Clock out" :
    clockFlow.mode === "out" && clockFlow.step === "pin"      ? "Step 1 of 2 · Confirm PIN" :
    clockFlow.mode === "out" && clockFlow.step === "camera"   ? "Step 2 of 2 · Photo" :
    clockFlow.mode === "out" && clockFlow.step === "success"  ? "Done" : "";

  // A1 leak-close: resolve the PIN's owner from the SERVER (verify-only, so it
  // never unlocks) when server PIN verify is on, instead of the downloaded
  // roster. Same opaque messages either way. Inert until a shop is flipped.
  //
  // Lane BT: on a gated host the server is asked ONCE, up front in onPinComplete
  // (checkPinOnServer), with the three guards the login numpad already has — a
  // neutral "Checking…" state, an attempt counter so an answer that lands after
  // the person retyped or closed the scrim is dropped, and an 8 s timeout that
  // hands the numpad back with the existing "couldn't reach" message. The OFF
  // path (every shop today) is this roster lookup, byte for byte as before.
  const resolvePinName = async (pin) => (staffPins && staffPins[pin]) || null;
  const pinAttemptRef = useRef(0);
  useEffect(() => () => { pinAttemptRef.current++; }, []);   // scrim closed (unmount): any in-flight check is now stale
  const checkPinOnServer = async (pin) => {
    const attempt = ++pinAttemptRef.current;
    setClockFlow(f => ({...f, checking: true, err: ""}));
    let srv = null;
    try { srv = await pinAuth.verifyPin(pin, { timeoutMs: clockPinCheck.CLOCK_PIN_VERIFY_TIMEOUT_MS }); } catch (e) { srv = null; }
    const out = clockPinCheck.clockPinOutcome(srv, attempt, pinAttemptRef.current);
    if (!out.stale) setClockFlow(f => ({...f, checking: false}));
    return out;
  };
  const onPinComplete = async (pin) => {
    const srv = pinAuth.serverPinVerifyEnabled() ? await checkPinOnServer(pin) : null;
    if (srv && srv.stale) return;                                   // a newer attempt (or a closed scrim) owns the screen — touch nothing
    if (srv && srv.unreachable) {                                   // offline / timed out: say so and hand the numpad back
      setClockFlow(f => ({...f, pin: "", err: srv.error}));
      return;
    }
    const whoIs = async (p) => (srv ? srv.name : resolvePinName(p));   // gated: the server's answer · OFF: the roster, as before
    if (clockFlow.mode === "in") {
      // Reject unknown PINs at the boundary so the camera step never opens
      // with an empty staffName / a PIN that would only fail on submit.
      const name = await whoIs(pin);
      if (!name) {
        setClockFlow(f => ({...f, pin: "", err: "PIN not recognised"}));
        return;
      }
      // Thread the verified name into the flow so the camera step + submit never
      // re-read the roster for it (row C). OFF path stores staffPins[pin] here,
      // and the camera step ignores it (reads the roster), so behaviour is unchanged.
      setClockFlow(f => ({...f, pin: pin, step: "camera", err: "", pinName: name}));
    } else if (clockFlow.mode === "out") {
      // Clock-out PIN privacy (memory pin_privacy.md). BOTH error paths
      // surface the SAME opaque "Wrong PIN" message:
      //   - PIN not in directory at all
      //   - PIN belongs to a different staffer than the tapped target
      // Distinct messages would let a probing staffer distinguish
      // "this PIN doesn't exist" from "this PIN exists but for someone
      // else" — that's still a directory-membership leak. Identical
      // message either way; the user just tries again.
      const localName = await whoIs(pin);
      const target = clockFlow.target;
      if (rosterFree.clockOutRejected(localName, target && target.name)) {
        setClockFlow(f => ({...f, pin: "", err: rosterFree.WRONG_PIN_MESSAGE}));
        return;
      }
      // PIN matches the nominated target. Advance to camera step (clock-out
      // photo for audit symmetry with clock-in). Thread the verified name (row C).
      setClockFlow(f => ({...f, pin: pin, step: "camera", err: "", pinName: localName}));
    }
  };
  // Camera step routes to clock-in OR clock-out submit based on flow mode.
  const onCapture = (photoBase64) => {
    if (clockFlow.mode === "in") submitClockIn(clockFlow.pin, photoBase64, clockFlow.pinName);
    else submitClockOut(clockFlow.pin, clockFlow.target && clockFlow.target.name, photoBase64, clockFlow.pinName);
  };
  const onSkip = () => {
    if (clockFlow.mode === "in") submitClockIn(clockFlow.pin, "", clockFlow.pinName);
    else submitClockOut(clockFlow.pin, clockFlow.target && clockFlow.target.name, "", clockFlow.pinName);
  };
  const onPickerPick = (s) => setClockFlow(f => ({...f, step:"pin", target: s, pin:"", err:""}));
  const setScrimPin = (updater) => setClockFlow(f => ({
    ...f,
    pin: typeof updater === "function" ? updater(f.pin) : updater,
    err: ""
  }));

  return (
    <ClockScrim headerLabel={headerLabel} onClose={closeClock}>
      {clockFlow.step === "pin" && (
        <div style={{flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:18, padding:"14px 18px"}}>
          {clockFlow.mode === "out" && clockFlow.target && (
            <div style={{textAlign:"center", marginBottom:6}}>
              <div style={{fontSize:"0.65rem", letterSpacing:"0.2em", textTransform:"uppercase", color:"rgba(212,251,111,0.6)", fontWeight:700}}>Clocking out</div>
              <div style={{fontFamily:"gelica,serif", fontSize:"1.4rem", fontWeight:700, color:B.rice, marginTop:4}}>{clockFlow.target.name}</div>
            </div>
          )}
          <ScrimPinDots count={clockFlow.pin.length}/>
          {clockPinCheck.showChecking(clockFlow) && (
            <div style={{color:B.rice, opacity:0.6, fontSize:"0.78rem", fontWeight:600, textAlign:"center", maxWidth:340}}>
              Checking…
            </div>
          )}
          {clockFlow.err && (
            <div style={{color:B.coral, fontSize:"0.78rem", fontWeight:600, textAlign:"center", maxWidth:340}}>
              {clockFlow.err}
            </div>
          )}
          <ScrimNumpad
            pin={clockFlow.pin}
            setPin={setScrimPin}
            busy={clockPinCheck.numpadBusy(clockFlow)}
            onComplete={onPinComplete}
          />
        </div>
      )}
      {clockFlow.step === "camera" && (
        <ClockCameraStep
          staffName={
            clockFlow.mode === "in"
              ? (pinAuth.serverPinVerifyEnabled() ? (clockFlow.pinName || "") : ((staffPins && staffPins[clockFlow.pin]) || ""))
              : (clockFlow.target && clockFlow.target.name) || ""
          }
          busy={clockFlow.busy}
          onCapture={onCapture}
          onSkip={onSkip}
        />
      )}
      {clockFlow.mode === "out" && clockFlow.step === "picker" && (
        <ClockOutPickerStep staffOnShift={staffOnShift} onPick={onPickerPick}/>
      )}
      {clockFlow.step === "success" && (
        <ClockSuccessStep
          name={clockFlow.successName}
          time={clockFlow.successTime}
          mode={clockFlow.mode}
          onDone={closeClock}
        />
      )}
    </ClockScrim>
  );
}

// Reviews banner v4.7 — sits above the FOH tile grid on the home screen.
// Fetches getSiteSummary (today's count) + getLeaderboard×2 (month leader
// by total credits, top rate) on mount. Silent-fail on network errors so
// the home screen still renders. siteKey scopes the data:
//   "trafford" / "arndale" / "meadowhall" — single-site
//   "manchester" — combined Trafford + Arndale (Manchester tablets only)
//
// Layout (v4.7, signed off via mockup _mockup-foh-reviews-banner-v4):
//   - Always: 3-col stats row (Today's 5★ · Month leader · Top review rate)
//   - Conditional: team-target progress strip when admin sets a teamTarget
//   - Conditional: prize strip when admin sets a monthly prize
//   - Conditional: golden takeover (celebrate-a) when teamTarget hit
//
// monthlyConfig is the new piece (gated until task 2 — getReviewMonthlyConfig
// endpoint — ships). Until that endpoint exists, the fetch is intentionally
// skipped and monthlyConfig stays null. Banner renders without the new chrome.
// One-line wire-up later: add post({action:"getReviewMonthlyConfig", site:siteKey})
// to the Promise.all and pull config off the response.
//
// Caching: last successful payload is mirrored to localStorage so repeat
// loads render instantly with last-known numbers, then refresh in background.
function ReviewsBanner({ siteKey, siteName, onOpen }) {
  const cacheKey = "reviewsBannerCache::" + siteKey;
  const cached = (() => {
    try { const c = localStorage.getItem(cacheKey); if (c) return JSON.parse(c); } catch(e) {}
    return null;
  })();
  const [loading, setLoading] = useState(!cached);
  const [errored, setErrored] = useState(false);
  const [today, setToday] = useState(cached ? cached.today : 0);
  const [leader, setLeader] = useState(cached ? cached.leader : null);
  const [rateChamp, setRateChamp] = useState(cached ? cached.rateChamp : null);
  const [target, setTarget] = useState(cached ? cached.target : 2.0);
  const [blackout, setBlackout] = useState(cached ? cached.blackout : false);
  // monthlyConfig: shape { mostReviewsPrize, bestRatePrize, teamTarget,
  // teamPrize, monthCount }. Stays null until getReviewMonthlyConfig ships,
  // unless ?previewMode=group URL params are present (mockup-mode).
  const previewCfg = getPreviewTeamConfig(siteKey);
  const [monthlyConfig, setMonthlyConfig] = useState(previewCfg || (cached ? cached.monthlyConfig : null));

  useEffect(() => {
    let cancelled = false;
    const post = (body) => fetch(BACKEND, {
      method: "POST",
      headers: {"Content-Type":"text/plain;charset=utf-8"},
      body: JSON.stringify(body)
    }).then(r => r.ok ? r.json() : null);

    // Refactored 2026-05-12: fetch wrapped in a named helper so we can poll
    // every 5 min. Banner used to fetch only on mount — meaning new reviews
    // sat invisible on home until staff navigated away and back. Aligns with
    // the EOD time-gate poll pattern (60s) but at a much slower cadence
    // since reviews come in at most a handful of times a day.
    const fetchAll = () => {
      Promise.all([
        post({action:"getSiteSummary"}),
        post({action:"getLeaderboard", site:siteKey, period:"month", sort:"total", limit:1}),
        post({action:"getLeaderboard", site:siteKey, period:"month", sort:"rate",  limit:1}),
        // Phase 2 (2026-05-27): fetch monthly config so the team-target chrome
        // can react to admin toggles. Skip in preview mode (URL params already
        // injected the synthesised config at component init).
        previewCfg ? Promise.resolve(null) : post({action:"getReviewMonthlyConfig", site:siteKey}),
      ]).then(([summary, totalLb, rateLb, cfgRes]) => {
        if (cancelled) return;
        let nextToday = 0;
        if (summary && summary.sites) {
          if (siteKey === "manchester") {
            nextToday = ((summary.sites.trafford && summary.sites.trafford.today) || 0)
                      + ((summary.sites.arndale  && summary.sites.arndale.today)  || 0);
          } else if (summary.sites[siteKey]) {
            nextToday = summary.sites[siteKey].today || 0;
          }
        }
        const nextLeader = (totalLb && totalLb.rows && totalLb.rows.length > 0)
          ? { name: totalLb.rows[0].displayName || totalLb.rows[0].name, credits: totalLb.rows[0].credits, bySite: totalLb.rows[0].bySite }
          : null;
        const nextTarget = (totalLb && totalLb.targetPerShift) ? totalLb.targetPerShift : 2.0;
        const nextBlackout = !!(totalLb && totalLb.blackoutActive);
        const nextRate = (rateLb && rateLb.rows && rateLb.rows.length > 0 && rateLb.rows[0].shifts > 0)
          ? { name: rateLb.rows[0].displayName || rateLb.rows[0].name, perShift: rateLb.rows[0].perShift }
          : null;
        // Preview takes precedence over backend (mockup never gets overwritten
        // by the live config). Otherwise build a fresh monthlyConfig payload
        // from the backend response — top-level teamTarget/teamPrize/monthCount
        // are scoped to siteKey so the banner reads three flat fields.
        const nextConfig = previewCfg ? previewCfg : (cfgRes ? {
          mostReviewsPrize: cfgRes.mostReviewsPrize || "",
          bestRatePrize: cfgRes.bestRatePrize || "",
          beatChampActive: !!cfgRes.beatChampActive,
          groupTargetActive: !!cfgRes.groupTargetActive,
          teamTargets: cfgRes.teamTargets || {},
          teamTarget: typeof cfgRes.teamTarget === "number" ? cfgRes.teamTarget : 0,
          teamPrize: cfgRes.teamPrize || "",
          monthCount: typeof cfgRes.monthCount === "number" ? cfgRes.monthCount : 0,
        } : monthlyConfig);
        setToday(nextToday);
        setLeader(nextLeader);
        setTarget(nextTarget);
        setBlackout(nextBlackout);
        setRateChamp(nextRate);
        setMonthlyConfig(nextConfig);
        setLoading(false);
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            today: nextToday, leader: nextLeader, rateChamp: nextRate,
            target: nextTarget, blackout: nextBlackout,
            monthlyConfig: nextConfig, ts: Date.now()
          }));
        } catch(e) {}
      }).catch(() => {
        if (cancelled) return;
        if (!cached) setErrored(true);
        setLoading(false);
      });
    };

    // 20 Sep 2026 (login-time reads): this banner unmounts on every trip into a
    // tile and re-mounts on the way back, and each mount re-fired these four reads
    // — three of them 20–35 s on the backend (the leaderboard is computed on the
    // spot). That was the bulk of the backend load a tablet generated all day, and
    // every other read (checklist day, stock, EOD) queued behind it. The cached copy
    // carries `ts`, so a re-mount within the poll cadence paints the cached numbers
    // and refreshes when they turn REVIEWS_BANNER_MIN_REFRESH_MS old — exactly what
    // a tablet sitting on the home screen already does. No cache (first load, or
    // a cleared tablet) still fetches straight away, as before.
    let first = null, id = null;
    const startPolling = () => { id = setInterval(fetchAll, 5 * 60 * 1000); };
    const cachedAge = (cached && Number(cached.ts) > 0) ? (Date.now() - Number(cached.ts)) : Infinity;
    if (readFreshness.readIsFresh(cached, { maxAgeMs: REVIEWS_BANNER_MIN_REFRESH_MS })) {
      first = setTimeout(() => { fetchAll(); startPolling(); }, Math.max(1000, REVIEWS_BANNER_MIN_REFRESH_MS - cachedAge));
    } else {
      fetchAll();
      startPolling();
    }

    return () => { cancelled = true; if (first) clearTimeout(first); if (id) clearInterval(id); };
  }, [siteKey]);

  if (errored) return null;

  const teamTarget = (monthlyConfig && typeof monthlyConfig.teamTarget === "number") ? monthlyConfig.teamTarget : null;
  const monthCount = (monthlyConfig && typeof monthlyConfig.monthCount === "number") ? monthlyConfig.monthCount : null;
  const teamMode  = teamTarget !== null && teamTarget > 0;
  // Per-site rows for the AND-gate. teamTarget/monthCount above stay POOLED
  // (combined progress); the "hit" decision below uses each site individually.
  const _bTeam = (monthlyConfig && monthlyConfig.teamTargets) || {};
  const _bRows = (siteKey === "manchester")
    ? ["trafford", "arndale"].map(s => (_bTeam[s] && _bTeam[s].isActive) ? { site: s, ..._bTeam[s] } : null).filter(Boolean)
    : ((_bTeam[siteKey] && _bTeam[siteKey].isActive) ? [{ site: siteKey, ..._bTeam[siteKey] }] : []);
  const teamCombined = siteKey === "manchester" && _bRows.length > 1;
  // AND-gate (fix 2026-07-28): celebrate only when EVERY active site clears its
  // OWN target. The old pooled test (monthCount >= teamTarget) fired when the
  // summed count crossed the summed target, so one site's overshoot masked
  // another's shortfall (Arndale 105/75 hiding Trafford 45/75, both net 150/150).
  const goalHit   = teamMode && _bRows.length > 0 && _bRows.every(r => (r.monthCount || 0) >= (r.target || 0));
  const teamPrize = monthlyConfig && monthlyConfig.teamPrize;
  const siteShort = { trafford: "TC", arndale: "ARN", meadowhall: "MDH" };

  const indivMostPrize = monthlyConfig && monthlyConfig.mostReviewsPrize;
  const indivRatePrize = monthlyConfig && monthlyConfig.bestRatePrize;
  // ONE scheme, not two per-winner prizes: when the Command Centre has the same
  // string in both slots the prize is not something a single leader WINS — it is
  // the rule everyone is on (e.g. "£1 Per Review, Attributed by Name"). Saying
  // "WIN … top reviewer + top rate" there reads as "only the leader gets paid",
  // which is the opposite of the scheme. Same string both sides => EARN, and no
  // top-of-the-board qualifier. Two DIFFERENT strings are still two real prizes
  // with real winners, so those keep WIN and their qualifiers untouched.
  const oneSchemeForAll = !!(indivMostPrize && indivRatePrize && indivMostPrize === indivRatePrize);
  const indivPrize = (() => {
    if (!indivMostPrize && !indivRatePrize) return null;
    if (oneSchemeForAll) {
      return { name: indivMostPrize, qual: "everyone, every review", verb: "EARN" };
    }
    if (indivMostPrize && indivRatePrize) {
      return { name: indivMostPrize + " + " + indivRatePrize, qual: "top reviewer · top rate", verb: "WIN" };
    }
    if (indivMostPrize) return { name: indivMostPrize, qual: "top reviewer this month", verb: "WIN" };
    return { name: indivRatePrize, qual: "top review rate this month", verb: "WIN" };
  })();

  const activePrize = teamMode
    ? (teamPrize ? { name: teamPrize, qual: "", verb: "EARN" } : null)
    : indivPrize;

  const fillPct = (teamMode && monthCount !== null) ? Math.min(100, Math.round((monthCount / teamTarget) * 100)) : 0;
  const pctLabel = (teamMode && monthCount !== null) ? Math.round((monthCount / teamTarget) * 100) : null;

  const wrap = {
    background: B.nori, color: B.rice, borderRadius: 16, padding: "14px 18px",
    cursor: onOpen ? "pointer" : "default", transition: "transform 0.1s",
    position: "relative", overflow: "hidden", margin: "0 0 14px 0",
    border: "1px solid rgba(212, 251, 111, 0.18)",
  };
  const glow = { position: "absolute", top: "-40%", right: "-8%", width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(212, 251, 111, 0.12) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 };
  const top = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, position: "relative", zIndex: 1 };
  const label = { fontSize: 10, fontWeight: 800, color: B.wasabi, letterSpacing: "0.12em", textTransform: "uppercase" };
  const more = { fontSize: 11, fontWeight: 700, color: B.wasabi };
  const cols = { display: "grid", gridTemplateColumns: "1fr 1.2fr 1.2fr", gap: 14, position: "relative", zIndex: 1 };
  const col = { paddingRight: 14, borderRight: "1px solid rgba(255, 255, 255, 0.12)" };
  const colLast = { paddingRight: 0, borderRight: 0 };
  const l = { fontSize: 9.5, fontWeight: 800, color: "rgba(255, 255, 255, 0.5)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 };
  const v = { fontFamily: "gelica, Manrope, serif", fontSize: 22, fontWeight: 800, lineHeight: 1, color: B.rice };
  const num = { color: B.wasabi };
  const h = { marginTop: 4, fontSize: 11.5, fontWeight: 600, color: "rgba(255, 255, 255, 0.7)" };

  const progStrip = { marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.12)", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", position: "relative", zIndex: 1 };
  const progLabel = { fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.55)", letterSpacing: "0.1em", textTransform: "uppercase" };
  const progValue = { fontFamily: "gelica, Manrope, serif", fontWeight: 800, color: B.rice, fontSize: 14 };
  const progBar = { flex: 1, minWidth: 180, height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden", position: "relative" };
  const progFill = { height: "100%", background: B.wasabi, borderRadius: 3, transition: "width 0.6s cubic-bezier(.45,.05,.55,.95)", width: fillPct + "%" };
  const progPct = { fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)", minWidth: 38, textAlign: "right" };

  const prizeStrip = { marginTop: 10, padding: "8px 12px", background: "linear-gradient(135deg, rgba(251,191,36,0.12), rgba(212,251,111,0.10))", border: "1px solid rgba(251,191,36,0.35)", borderRadius: 10, display: "flex", alignItems: "center", gap: 10, position: "relative", zIndex: 1, flexWrap: "wrap" };
  const prizeIcon = { fontSize: 18, lineHeight: 1 };
  const prizeLbl  = { fontSize: 9.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#fbbf24" };
  const prizeName = { fontFamily: "gelica, Manrope, serif", fontWeight: 800, fontSize: 14, color: B.rice, flex: 1 };
  const prizeQual = { fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.55)" };

  if (loading) {
    return (
      <div style={{...wrap, opacity: 0.55}}>
        <div style={top}><span style={label}>⭐ Reviews · {siteName}</span></div>
        <div style={{fontSize: 12, color:"rgba(255,255,255,0.55)"}}>Loading review stats…</div>
      </div>
    );
  }

  return (
    <div style={wrap} className={goalHit ? "rolled-banner-celebrate-a" : ""} onClick={onOpen || undefined}>
      <div style={glow}/>
      <div style={top}>
        <span style={label}>⭐ Reviews · {siteName}</span>
        <span style={more}>Open Reviews →</span>
      </div>

      {goalHit && (
        <div className="rolled-banner-celebration-banner">
          <span className="rolled-banner-trophy-icon">🏆</span>
          {teamPrize ? "TEAM GOAL HIT · EARN " + teamPrize.toUpperCase() : "TEAM GOAL HIT · " + monthCount + " of " + teamTarget + " reviews"}
          <span className="rolled-banner-trophy-icon">🏆</span>
        </div>
      )}

      <div style={cols}>
        <div style={col}>
          <div style={l}>Today's 5★</div>
          <div style={v}><span style={num}>{today}</span></div>
          {/* Was: manchester -> "across Trafford + Arndale". Arndale closed 16 Aug
              2026 and the region is Trafford alone, so the combined wording named a
              shut shop on every till. One line for every scope now. */}
          <div style={h}>new reviews this site</div>
        </div>
        <div style={col}>
          <div style={l}>🥇 Month leader</div>
          <div style={v}>
            {blackout
              ? <span style={{letterSpacing:"0.15em"}}>🤫 Hidden</span>
              : (leader ? leader.name : "—")}
          </div>
          <div style={h}>
            {leader ? (
              <>
                <span style={{...num, fontWeight: 800}}>{leader.credits}</span>
                {/* Per-site split, only worth showing when the scope really spans more
                    than one trading site. Was hardcoded "N TC · N ARN", which kept
                    printing "· 0 ARN" after Arndale closed. Derived from the data now,
                    so it simply disappears for a single-site region. */}
                {!blackout && leader.bySite && _bySiteSplit(leader.bySite) && (
                  <span style={{opacity: 0.55, fontSize: 10.5, marginLeft: 4}}>
                    ({_bySiteSplit(leader.bySite)})
                  </span>
                )}
                {blackout && <span style={{opacity: 0.6}}> · revealed soon</span>}
              </>
            ) : (
              <span style={{opacity: 0.6}}>No reviews yet this month</span>
            )}
          </div>
        </div>
        <div style={{...col, ...colLast}}>
          <div style={l}>⚡ Top review rate</div>
          <div style={v}>{rateChamp ? rateChamp.name : "—"}</div>
          <div style={h}>
            {rateChamp ? (
              <>
                <span style={{...num, fontWeight: 800}}>{rateChamp.perShift}</span>
                <span style={{opacity: 0.55}}>/{target}</span> per shift
              </>
            ) : (
              <span style={{opacity: 0.6}}>No shift data yet</span>
            )}
          </div>
        </div>
      </div>

      {teamMode && !goalHit && (teamCombined ? (
        <div style={progStrip}>
          <span style={progLabel}>🏆 Team target</span>
          {_bRows.map(r => {
            const done = (r.monthCount || 0) >= (r.target || 0);
            return (
              <span key={r.site} style={{...progValue, display: "inline-flex", alignItems: "baseline", gap: 4}}>
                <span style={{fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "rgba(255,255,255,0.5)"}}>{siteShort[r.site] || r.site}</span>
                <span style={{...num, color: done ? "#fbbf24" : B.wasabi}}>{r.monthCount || 0}</span>
                <span style={{opacity: 0.6, fontWeight: 700}}>/ {r.target || 0}</span>
                {done && <span style={{fontSize: 11, color: "#fbbf24"}}>✓</span>}
              </span>
            );
          })}
        </div>
      ) : (
        <div style={progStrip}>
          <span style={progLabel}>🏆 Team target</span>
          <span style={progValue}>
            <span style={num}>{monthCount !== null ? monthCount : "—"}</span>
            <span style={{opacity: 0.6, fontWeight: 700}}> / {teamTarget}</span>
          </span>
          <div style={progBar}>
            <div className={goalHit ? "rolled-banner-progress-fill-gold" : ""} style={progFill} />
          </div>
          {pctLabel !== null && <span style={progPct}>{pctLabel}%</span>}
        </div>
      ))}

      {activePrize && (
        <div style={prizeStrip}>
          <span style={prizeIcon}>🎁</span>
          <span style={prizeLbl}>{activePrize.verb} this month</span>
          <span style={prizeName}>{activePrize.name}</span>
          {activePrize.qual && <span style={prizeQual}>· {activePrize.qual}</span>}
        </div>
      )}
    </div>
  );
}

// Under an Unearned discount that a reward given offline explains (JJ, 14 Oct 2026), from the
// server's offline_redemption on the row: refused when the till synced (and why), the wrong
// reward picked, or checked OK but synced too late to be matched to the sale. No customer
// details here: this is the shop's shared tablet.
function OfflineRefusalNote({ r }) {
  if (!r) return null;
  let t = "";
  try { const d = new Date(r.served_at); t = isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }); } catch (e) { t = ""; }
  const who = r.staff_pin_check === "matched" ? (r.staff_name || "")
    : r.staff_pin_check === "switched_off" ? "a switched-off PIN"
    : r.staff_pin_check === "no_match" ? "a PIN that matched nobody"
    : r.staff_pin_check === "unverified" ? "an unchecked PIN"
    : (r.staff_name || "");
  const ok = r.status === "ok";
  const head = "Given offline" + (t ? " at " + t : "") + (ok ? ", checked OK when the till synced" : r.status === "wrong_reward" ? ", wrong reward picked" : r.status === "error" ? ", couldn't be checked when the till synced" : ", refused when the till synced");
  const body = ok ? "It just synced too late to be matched to this sale." : (r.status === "error" ? "" : r.reason);
  return (
    <div style={{ padding: "0 0 10px", borderBottom: "1px dashed rgba(255,255,255,0.09)", fontSize: 11.5, lineHeight: 1.45, color: "rgba(254,250,240,0.75)" }}>
      <span style={{ color: ok ? B.wasabi : B.coral, fontWeight: 800 }}>{"📴"} {head}{body ? ":" : ""}</span>{body ? " " + body : null}{who ? <span style={{ color: "rgba(254,250,240,0.5)" }}>{" · by " + who}</span> : null}
    </div>
  );
}

// Digital Club Card Stats — the FOH tablet's daily loyalty scorecard, directly
// under the reviews banner on the home screen. Two read-only cards for THIS
// tablet's OWN site + today, from the Club Card backend (site scoping is baked
// into the staff token, so no site is passed):
//   1. Sign-up rate vs the 12.5% target — genuine new members / regular till sales,
//      with Too Good To Go, delivery, paper migrations and already-member sales
//      stripped out (a migration converts an existing paper-card holder to digital,
//      not a fresh sign-up won at the till).
//   2. Discounts applied without a scan — every Special Card / Club Card reward
//      discount rung with no matching scan, with the pounds given away. This card
//      only appears once at least one is registered (hidden on a clean day).
// Silent-fail like ReviewsBanner: any error renders nothing so the home screen is
// never blocked. Refreshes every 5 minutes.
function ClubCardStats({ token }) {
  const [data, setData] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = () => {
      // Square-backed, so it can run long on a busy day, and nobody waits on it:
      // the longer bound, not the scanner's 8 s.
      ccApiCall("/staff/site-day-summary", token, { timeoutMs: CC_SLOW_TIMEOUT_MS })
        .then((d) => { if (!cancelled) { setData(d); setErrored(false); } })
        .catch(() => { if (!cancelled) setErrored((e) => (data ? e : true)); });
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  if (!token || errored || !data) return null;

  const su = data.signups || {};
  const un = data.unearned || {};
  const target = typeof su.target_pct === "number" ? su.target_pct : 12.5;
  const pct = typeof su.conversion_pct === "number" ? su.conversion_pct : null;
  // Headline is clamped at 100% — the rate is counter joins ÷ eligible food sales,
  // and a heavy sticker day can still tip just over. The raw breakdown below always
  // shows the true counts, so nothing is hidden (JJ, 24 Sep 2026).
  const pctShown = pct != null ? Math.min(100, pct) : null;
  // Join breakdown: counter-won (the rate numerator) vs off-counter promo/QR vs the
  // day's total across every channel. Backend fields with graceful fallbacks.
  const counterJoins = su.counter_new_members ?? su.new_members ?? 0;
  const promoJoins = su.promo_qr_joins ?? 0;
  const totalJoins = su.total_new_members ?? (counterJoins + promoJoins);
  const over = pct != null && pct >= target;
  const accent = over ? B.wasabi : B.sunshine;
  const gbp = (p) => "£" + ((Number(p) || 0) / 100).toFixed(2);
  const hhmm = (iso) => { try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }); } catch (e) { return ""; } };
  const itemsLabel = (items) => (Array.isArray(items) ? items : []).map((it) => (it.quantity ? it.quantity + "× " : "") + it.name).join(" · ");
  const SCALE = 20; // gauge runs 0–20% so the 12.5% target sits right of centre
  const fillPct = pct != null ? Math.max(0, Math.min(100, (pct / SCALE) * 100)) : 0;
  const targetLeft = Math.min(100, (target / SCALE) * 100);
  const diff = pct != null ? Math.round(Math.abs(pct - target) * 10) / 10 : null;
  const rows = Array.isArray(un.rows) ? un.rows : [];
  const showLeak = rows.length > 0;
  const cols = "60px minmax(110px,1fr) minmax(120px,1.2fr) 84px 74px";

  return (
    <div style={{ margin: "0 0 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, margin: "2px 2px 10px" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: B.wasabi }} />
        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: B.wasabi }}>Digital Club Card Stats · Today</span>
      </div>

      {/* 1 — Sign-up rate */}
      <div style={{ background: B.nori, border: "1px solid rgba(212,251,111,0.22)", borderRadius: 16, padding: "16px 18px", marginBottom: 12 }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: B.wasabi, marginBottom: 12 }}>📈 Sign-up rate · Today</div>
        <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 220px", minWidth: 200 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
              <span style={{ fontFamily: "gelica,serif", fontWeight: 800, fontSize: 46, lineHeight: 0.9, color: accent }}>{pctShown != null ? pctShown + "%" : "—"}</span>
              <span style={{ fontSize: 13, color: "rgba(254,250,240,0.72)", lineHeight: 1.5 }}>of <b style={{ color: B.kewpie, fontWeight: 800, fontSize: 15 }}>{su.transactions || 0}</b> till sales,<br />
                <b style={{ color: B.kewpie, fontWeight: 800, fontSize: 15 }}>{counterJoins}</b> joined at the counter</span>
            </div>
            {pct != null && (
              <div style={{ marginTop: 12 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 800, padding: "5px 11px", borderRadius: 999, background: over ? "rgba(212,251,111,0.14)" : "rgba(241,197,68,0.14)", color: accent }}>
                  {over ? "▲ " + diff + " pts over target" : "▼ " + diff + " pts under target"}
                </span>
              </div>
            )}
          </div>
          <div style={{ flex: "1 1 240px", minWidth: 200 }}>
            <div style={{ position: "relative", height: 11, background: "rgba(255,255,255,0.08)", borderRadius: 999 }}>
              <div style={{ height: "100%", width: fillPct + "%", borderRadius: 999, background: over ? "linear-gradient(90deg,#6FAF8F,#D4FB6F)" : "linear-gradient(90deg,#c9822f,#F1C544)", transition: "width .5s" }} />
              <div style={{ position: "absolute", top: -4, bottom: -4, left: targetLeft + "%", width: 2, background: B.kewpie, borderRadius: 2 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 9.5, fontWeight: 700, color: "rgba(254,250,240,0.4)" }}><span>0%</span><span>target {target}%</span><span>{SCALE}%</span></div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 5, fontSize: 12, fontWeight: 700, color: "rgba(254,250,240,0.7)", background: "rgba(212,251,111,0.12)", border: "1px solid rgba(212,251,111,0.28)", padding: "6px 11px", borderRadius: 999 }}>
            <b style={{ color: B.wasabi, fontWeight: 800, fontSize: 13.5 }}>{counterJoins}</b> counter
          </span>
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 5, fontSize: 12, fontWeight: 700, color: "rgba(254,250,240,0.6)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", padding: "6px 11px", borderRadius: 999 }}>
            <b style={{ color: B.kewpie, fontWeight: 800, fontSize: 13.5 }}>{promoJoins}</b> promo / QR
          </span>
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 5, fontSize: 12, fontWeight: 700, color: "rgba(254,250,240,0.7)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", padding: "6px 11px", borderRadius: 999 }}>
            <b style={{ color: B.kewpie, fontWeight: 800, fontSize: 13.5 }}>{totalJoins}</b> joined today
          </span>
        </div>
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: 11, color: "rgba(254,250,240,0.45)", lineHeight: 1.45 }}>
          The rate counts <b style={{ color: "rgba(254,250,240,0.7)", fontWeight: 800 }}>counter</b> sign-ups only (counter sticker, packaging sticker, starter card, Free Roll card, staff QR) against till sales — promo &amp; QR joins are shown separately, not lost. Sales exclude Too Good To Go, delivery, paper migrations, drink/snack-only sales, and any sale linked to a stamp or Special Card.
        </div>
      </div>

      {/* 2 — Discounts applied without a scan (hidden until at least one) */}
      {showLeak && (
        <div style={{ background: B.nori, border: "1px solid rgba(255,122,92,0.32)", borderRadius: 16, padding: "18px" }}>
          <div style={{ fontFamily: "gelica,serif", fontSize: 17, fontWeight: 700, color: B.kewpie, marginBottom: 4 }}><span style={{ color: B.coral }}>⚠</span> Discounts Applied without correctly scanning rewards</div>
          <div style={{ fontSize: 12, color: "rgba(254,250,240,0.5)", lineHeight: 1.45 }}>These discounts were applied in Square with no corresponding Special Card or Club Card Reward scanned.</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, margin: "14px 0 10px" }}>
            <span style={{ fontFamily: "gelica,serif", fontWeight: 800, fontSize: 32, color: B.coral }}>{gbp(un.total_lost_pence)}</span>
            <span style={{ fontSize: 12, color: "rgba(254,250,240,0.6)", fontWeight: 700 }}><b style={{ color: B.kewpie, fontWeight: 800 }}>{un.count}</b> discount{un.count === 1 ? "" : "s"} today · revenue given away</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: cols, gap: 10, alignItems: "center", padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.14)", fontSize: 9, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(254,250,240,0.45)" }}>
            <span>Time</span><span>Discount applied</span><span>Order</span><span style={{ textAlign: "right" }}>Order value</span><span style={{ textAlign: "right" }}>Lost</span>
          </div>
          {rows.map((r, i) => (
            <React.Fragment key={i}>
            <div style={{ display: "grid", gridTemplateColumns: cols, gap: 10, alignItems: "center", padding: "10px 0", borderBottom: r.offline_redemption ? "none" : "1px dashed rgba(255,255,255,0.09)" }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: B.kewpie }}>{hhmm(r.time)}</span>
              <span>{(Array.isArray(r.discount_names) ? r.discount_names : []).map((dn, j) => (
                <span key={j} style={{ display: "inline-block", fontSize: 11, fontWeight: 800, color: B.coral, background: "rgba(255,122,92,0.1)", border: "1px solid rgba(255,122,92,0.24)", padding: "3px 8px", borderRadius: 999, marginRight: 4, marginBottom: 2 }}>{dn}</span>
              ))}</span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: B.kewpie }}>{itemsLabel(r.items) || "—"}</span>
              <span style={{ textAlign: "right", fontSize: 12.5, color: "rgba(254,250,240,0.62)" }}>{gbp(r.order_pence)}</span>
              <span style={{ textAlign: "right", fontSize: 13, fontWeight: 800, color: B.coral }}>{"−" + gbp(r.discount_pence)}</span>
            </div>
            {r.offline_redemption && <OfflineRefusalNote r={r.offline_redemption} />}
            </React.Fragment>
          ))}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 0 2px" }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(254,250,240,0.6)" }}>Lost revenue today</span>
            <span style={{ fontFamily: "gelica,serif", fontSize: 22, fontWeight: 800, color: B.coral }}>{gbp(un.total_lost_pence)}</span>
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: "rgba(254,250,240,0.5)", lineHeight: 1.5 }}>Discounts can <b style={{ color: B.kewpie, fontWeight: 800 }}>ONLY</b> be applied if the correct reward is scanned on the Digital Club Card or Special Cards. <span style={{ color: "rgba(254,250,240,0.4)", fontStyle: "italic" }}>*excluding NHS, Staff Lunch, Old Card 8th Stamp</span></div>
        </div>
      )}
    </div>
  );
}

// Reviews leaderboard tile v2 — the "Open Reviews →" landing.
// Mockup signed off at _mockup-reviews-leaderboard-v2 (port 8110).
//
// Layout (top → bottom):
//   1. Header bar — Back · "⭐ Reviews leaderboard" · site pill
//   2. Filter row — site toggle (Manchester tablets) + period toggle (Month/All time)
//   3. Reigning champ card — REGION-scoped (not site-scoped). Manchester staff
//      cross-cover so TC↔Arndale↔Manchester combined all show same Manchester
//      reigning. Crown reserved for reigning. 🔥 N-month streak when ≥ 2.
//      Beat-the-Champ challenge sub-section (when admin-active).
//   4. Current leaders — two side-by-side cards (Most Reviews + Best Rate).
//      🥇 / ⚡ icons. Prize chip when admin set monthly prizes.
//   5. Sort tabs — flips the ranking below between Most Reviews / Best Rate.
//   6. Full ranking — name + byMethod sub-line + count + per-shift pill.
//      Tiebreaker: total tied → rate breaks; rate tied → total breaks.
//   7. How attribution works — plain-English explainer footer.
//
// Backend wiring:
//   - getLeaderboard(site, period, sort) — already live. Two parallel calls
//     (total + rate) so we can show both leaders simultaneously.
//   - getReigningChamps(region) — TODO. Until backend ships, reigning state
//     stays null and the reigning card + Beat-the-Champ stay hidden. Same
//     pattern as the v4.7 banner's monthlyConfig.
//   - getReviewMonthlyConfig({site}) — TODO. Until then, prize chips stay
//     hidden on the current-leader cards.
//
// localStorage cache per (site, period) so flipping the sort tab re-renders
// instantly without re-fetching (sort is a frontend re-shuffle of cached rows).
// Builds the "12 TC · 4 MDH" per-site split shown next to a leader's credit count.
// Returns null when fewer than two sites actually contributed, so a single-site
// region shows nothing rather than a misleading "· 0 ARN" for a closed shop.
// Sites are read from the data, never hardcoded — that hardcoding is exactly what
// left Arndale on the Trafford till after it closed (17 Aug 2026).
const _BY_SITE_SHORT = { trafford: "TC", tc: "TC", arndale: "ARN", meadowhall: "MDH", kitchen: "KIT" };
function _bySiteSplit(bySite) {
  if (!bySite) return null;
  const parts = Object.keys(bySite)
    .filter(k => (bySite[k] || 0) > 0)
    .map(k => (bySite[k] || 0) + " " + (_BY_SITE_SHORT[k] || String(k).toUpperCase()));
  return parts.length > 1 ? parts.join(" · ") : null;
}

function ReviewsTile({ defaultSiteKey, allowedSites, siteName, region, requestPin, onBack }) {
  const [site, setSite] = useState(defaultSiteKey);
  const [period, setPeriod] = useState("month");
  const [sort, setSort] = useState("total");
  // Collapsible state for the two longer sections — both default expanded
  // (locked 2026-05-11). Tap the chevron in the section header to collapse.
  const [leaderboardOpen, setLeaderboardOpen] = useState(true);
  const [feedOpen, setFeedOpen] = useState(true);

  const cacheKey = "reviewsTileV2Cache::" + site + "::" + period;
  const cached = (() => {
    try { const c = localStorage.getItem(cacheKey); if (c) return JSON.parse(c); } catch(e) {}
    return null;
  })();

  const [totalRows, setTotalRows] = useState((cached && cached.totalRows) || []);
  const [rateRows, setRateRows] = useState((cached && cached.rateRows) || []);
  const [feedRows, setFeedRows] = useState((cached && cached.feedRows) || []);
  const [target, setTarget] = useState((cached && cached.target) || 2.0);
  const [blackout, setBlackout] = useState(!!(cached && cached.blackout));
  const [loading, setLoading] = useState(!cached);
  const [errored, setErrored] = useState(false);

  // Reigning champ + monthly prize config — region-scoped, period-agnostic.
  // Fetched together on mount; re-fetched if region changes. Cached to
  // localStorage so reloads paint the gold reigning chrome + prize chips
  // instantly with last-known values, then refresh in the background.
  // Backend wired 2026-05-11: getReigningChamps + getReviewMonthlyConfig.
  const cfgCacheKey = "reviewsTileV2CfgCache::" + region;
  const cachedCfg = (() => {
    try { const c = localStorage.getItem(cfgCacheKey); if (c) return JSON.parse(c); } catch(e) {}
    return null;
  })();
  const [reigning, setReigning] = useState((cachedCfg && cachedCfg.reigning) || null);
  // Preview-mode injection (mockup): ?previewMode=group URL params populate
  // monthlyConfig with team-target fields without needing the backend handler
  // to be deployed. Site-aware so the leaderboard's site toggle (TC/Arndale/
  // Manchester combined) swaps the bar to that site's preview values. See
  // getPreviewTeamConfig at the top of the file.
  const previewCfg = getPreviewTeamConfig(defaultSiteKey);
  const previewActive = !!previewCfg;
  const [monthlyConfig, setMonthlyConfig] = useState(previewCfg || (cachedCfg && cachedCfg.monthlyConfig) || null);

  // B4 — personal stats by PIN (2026-05-12). Opens a modal over the leaderboard
  // with the staff member's own credits / per-shift rate / target attainment /
  // rank-in-scope, plus vs-last-month deltas. Backend handler is the same one
  // the admin drill modal uses (handleGetStaffPersonalStats). PIN-gated via
  // requestPin so identity is verified per-tap (no implicit-login trust).
  const [myStats, setMyStats] = useState(null);
  const [myStatsErr, setMyStatsErr] = useState(null);
  const [myStatsBusy, setMyStatsBusy] = useState(false);
  const openMyStats = () => {
    if (!requestPin) { window.alert("Hang on — PIN entry isn't ready, refresh and try again."); return; }
    setMyStatsErr(null);
    requestPin((_staffName, staffPin) => {
      setMyStatsBusy(true);
      fetch(BACKEND, {
        method: "POST",
        headers: {"Content-Type":"text/plain;charset=utf-8"},
        body: JSON.stringify({action:"getStaffPersonalStats", pin: staffPin})
      }).then(r => r.ok ? r.json() : null).then(r => {
        setMyStatsBusy(false);
        if (r && r.status === "ok") setMyStats(r);
        else setMyStatsErr((r && r.message) || "Couldn't load your stats — try again in a sec.");
      }).catch(() => {
        setMyStatsBusy(false);
        setMyStatsErr("Connection error — check signal and retry.");
      });
    });
  };

  useEffect(() => {
    let cancelled = false;
    // Retry-once helper — Apps Script cold-start sometimes returns 503 or
    // empty body on the first hit. One retry with a small delay catches it
    // without surfacing the failure to the user.
    const postWithRetry = async (body) => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const r = await fetch(BACKEND, {
            method: "POST",
            headers: {"Content-Type":"text/plain;charset=utf-8"},
            body: JSON.stringify(body)
          });
          if (r.ok) {
            const j = await r.json().catch(() => null);
            if (j && j.status === "ok") return j;
          }
        } catch(e) { /* fall through to retry */ }
        if (attempt === 0) await new Promise(res => setTimeout(res, 1500));
      }
      return null;
    };

    // In preview-mode, skip the live config fetch so URL-param values aren't
    // overwritten by an empty/legacy backend response. Reigning still loads
    // (it's hidden anyway when teamMode is on, but the call is cheap).
    //
    // Phase 2 (2026-05-27): config fetch now sends `site:site` (not :region)
    // so the backend response's top-level teamTarget/teamPrize/monthCount are
    // scoped to the currently-toggled site. Effect dep updated to [region,
    // site] so a site toggle re-fetches the config — reigning re-fetches too
    // but that's region-scoped so result is identical, cheap.
    Promise.all([
      postWithRetry({action:"getReigningChamps", region:region}),
      previewCfg ? Promise.resolve(null) : postWithRetry({action:"getReviewMonthlyConfig", site:region}),
    ]).then(([reignRes, cfgRes]) => {
      if (cancelled) return;
      const nextReigning = reignRes
        ? { visible: reignRes.visible !== false, total: reignRes.total || null, rate: reignRes.rate || null }
        : null;
      const nextConfig = previewCfg ? previewCfg : (cfgRes ? {
        mostReviewsPrize: cfgRes.mostReviewsPrize || "",
        bestRatePrize: cfgRes.bestRatePrize || "",
        beatChampActive: !!cfgRes.beatChampActive,
        groupTargetActive: !!cfgRes.groupTargetActive,
        teamTargets: cfgRes.teamTargets || {},
        teamTarget: typeof cfgRes.teamTarget === "number" ? cfgRes.teamTarget : 0,
        teamPrize: cfgRes.teamPrize || "",
        monthCount: typeof cfgRes.monthCount === "number" ? cfgRes.monthCount : 0,
      } : null);
      setReigning(nextReigning);
      setMonthlyConfig(nextConfig);
      try { localStorage.setItem(cfgCacheKey, JSON.stringify({ reigning: nextReigning, monthlyConfig: nextConfig })); } catch(e) {}
    }).catch(() => { /* keep prior state; UI gracefully renders without it */ });

    return () => { cancelled = true; };
  }, [region]);

  useEffect(() => {
    let cancelled = false;
    setErrored(false);
    // Restore (or clear) rows for the new site/period combo so a tab switch
    // never lingers on stale data. If cache exists → instant paint of last
    // known, then refresh in background. If not → empty + Refreshing pill.
    const fresh = (() => {
      try { const v = localStorage.getItem(cacheKey); if (v) return JSON.parse(v); } catch(e) {}
      return null;
    })();
    setTotalRows((fresh && fresh.totalRows) || []);
    setRateRows((fresh && fresh.rateRows) || []);
    setFeedRows((fresh && fresh.feedRows) || []);
    setLoading(true);

    const post = (body) => fetch(BACKEND, {
      method: "POST",
      headers: {"Content-Type":"text/plain;charset=utf-8"},
      body: JSON.stringify(body)
    }).then(r => r.ok ? r.json() : null);

    // Feed capped at 10 most recent (locked 2026-05-11). Staff side stays
    // scannable; full history lives in the Staff Performance admin tile.
    const monthStart = new Date();
    monthStart.setDate(1);
    const fromDate = period === "month" ? monthStart.toISOString().slice(0,10) : null;
    const feedLimit = 10;

    // 2026-05-12: wrapped in named fetcher so we can poll every 5 min.
    // Previously fetched only on mount, so a staffer who opened the tile
    // mid-shift wouldn't see reviews that landed afterwards until they
    // closed and re-opened. Cadence matches the home banner.
    const fetchAll = () => {
      Promise.all([
        post({action:"getLeaderboard", site:site, period:period, sort:"total", limit:100}),
        post({action:"getLeaderboard", site:site, period:period, sort:"rate",  limit:100}),
        post({action:"getReviewFeed",  site:site, fromDate:fromDate, limit:feedLimit, fiveStarOnly:true}),
      ]).then(([totalLb, rateLb, feedRes]) => {
        if (cancelled) return;
        const next = {
          totalRows: (totalLb && totalLb.status === "ok" && totalLb.rows) || [],
          rateRows:  (rateLb  && rateLb.status  === "ok" && rateLb.rows)  || [],
          feedRows:  (feedRes && feedRes.status === "ok" && feedRes.reviews) || [],
          target:    (totalLb && totalLb.targetPerShift) || 2.0,
          blackout:  !!(totalLb && totalLb.blackoutActive),
        };
        setTotalRows(next.totalRows);
        setRateRows(next.rateRows);
        setFeedRows(next.feedRows);
        setTarget(next.target);
        setBlackout(next.blackout);
        setLoading(false);
        try { localStorage.setItem(cacheKey, JSON.stringify(next)); } catch(e) {}
      }).catch(() => {
        if (cancelled) return;
        if (!cached) setErrored(true);
        setLoading(false);
      });
    };

    fetchAll();
    const id = setInterval(fetchAll, 5 * 60 * 1000);

    return () => { cancelled = true; clearInterval(id); };
  }, [site, period]);

  // Preview-mode site swap (mockup). When ?previewMode=group is set and the
  // user toggles the site (TC / Arndale / Manchester combined), re-derive the
  // monthlyConfig from per-site URL params so the bar swaps to that site's
  // values. No-op in production (previewActive = false).
  useEffect(() => {
    if (!previewActive) return;
    const fresh = getPreviewTeamConfig(site);
    if (fresh) setMonthlyConfig(fresh);
  }, [site, previewActive]);

  // Tiebreaker rule (locked 2026-05-10):
  //   Most Reviews tab → total credits DESC, then per-shift rate DESC
  //   Best Rate tab    → per-shift rate DESC, then total credits DESC
  const sorted = useMemo(() => {
    const rows = (sort === "rate" ? rateRows : totalRows).slice();
    if (sort === "rate") {
      rows.sort((a, b) => (b.perShift - a.perShift) || (b.credits - a.credits));
    } else {
      rows.sort((a, b) => (b.credits - a.credits) || (b.perShift - a.perShift));
    }
    return rows.map((r, i) => ({ ...r, rank: i + 1 }));
  }, [totalRows, rateRows, sort]);

  // Current-month leaders — independent of the sort tab. Tiebreaker matches.
  const totalLeader = useMemo(() => {
    const c = totalRows.slice().sort((a, b) => (b.credits - a.credits) || (b.perShift - a.perShift));
    return c[0] || null;
  }, [totalRows]);
  const totalSecond = useMemo(() => {
    const c = totalRows.slice().sort((a, b) => (b.credits - a.credits) || (b.perShift - a.perShift));
    return c[1] || null;
  }, [totalRows]);
  const rateLeader = useMemo(() => {
    const c = rateRows.slice().filter(r => r.shifts > 0).sort((a, b) => (b.perShift - a.perShift) || (b.credits - a.credits));
    return c[0] || null;
  }, [rateRows]);

  const showSiteToggle = allowedSites && allowedSites.length > 1;
  // Only spotlight a "current leader" once someone has actually done
  // something this month. Without this gate the section paints from the
  // first day of the period showing whoever sorts first alphabetically
  // as the leader with 0 reviews / 0.0 per shift — feels broken to staff.
  const showCurrentLeaders = period === "month" && totalLeader && rateLeader && ((totalLeader.credits > 0) || (rateLeader.perShift > 0));

  const indivMostPrize = monthlyConfig && monthlyConfig.mostReviewsPrize;
  const indivRatePrize = monthlyConfig && monthlyConfig.bestRatePrize;
  // Same string in both Command Centre slots = ONE scheme everyone is on (e.g.
  // "£1 Per Review, Attributed by Name"), not two prizes with two winners. The
  // chip on each leader card then reads EARN, not WIN — matching the home
  // banner's oneSchemeForAll branch. Two different strings stay WIN.
  const prizeVerb = (indivMostPrize && indivRatePrize && indivMostPrize === indivRatePrize) ? "EARN" : "WIN";
  // beatChampActive lives on the monthly config (admin-set per region) — not
  // on the reigning record itself. Reigning supplies the target number; the
  // config toggles whether the challenge section renders.
  const beatChampOn = !!(monthlyConfig && monthlyConfig.beatChampActive && reigning && reigning.total);

  // Team-target mode (2026-05-27). When admin sets monthlyConfig.teamTarget > 0,
  // this month's individual comps (reigning / leaders / leaderboard) are
  // hidden and replaced by a site-scoped team progress card. Goal: every 5★
  // review counts toward the site target; if the team hits it by month-end,
  // the whole team earns the configured reward (manual payout — qualification
  // logic deliberately out of scope this month).
  // Per-site team-target values DERIVED FROM THE REGION-SCOPED teamTargets
  // MAP (not the flat top-level fields). Why: the config fetch is per-region
  // and returns BOTH sites' data in `teamTargets`. Deriving per-site here
  // means the site toggle re-renders instantly with the correct numbers —
  // no re-fetch latency that previously caused the card to flash the old
  // site's numbers under the new site's label. "manchester" combined view
  // sums both sites' active rows.
  const _teamMap = (monthlyConfig && monthlyConfig.teamTargets) || {};
  const _teamRow = (function(){
    if (site === "manchester") {
      let tgt = 0, count = 0, prize = "";
      const rows = [];
      ["trafford", "arndale"].forEach(s => {
        const r = _teamMap[s];
        if (r && r.isActive) {
          tgt += (r.target||0); count += (r.monthCount||0);
          if (!prize) prize = r.reward || "";
          rows.push({ site: s, target: r.target||0, monthCount: r.monthCount||0 });
        }
      });
      return { target: tgt, monthCount: count, reward: prize, isActive: tgt > 0, sites: rows };
    }
    const row = _teamMap[site];
    if (row && row.isActive) {
      return { target: row.target||0, monthCount: row.monthCount||0, reward: row.reward||"", isActive: true,
               sites: [{ site: site, target: row.target||0, monthCount: row.monthCount||0 }] };
    }
    return { target: 0, monthCount: 0, reward: "", isActive: false, sites: [] };
  })();
  const teamTarget = _teamRow.target || 0;
  const teamPrize  = _teamRow.reward || "";
  const monthCount = _teamRow.monthCount || 0;
  const teamSites  = _teamRow.sites || [];
  const teamCombined = site === "manchester" && teamSites.length > 1;
  const teamMode   = teamTarget > 0;
  const teamPct    = teamMode ? Math.min(100, Math.round((monthCount / teamTarget) * 100)) : 0;
  // AND-gate (fix 2026-07-28): the group reward unlocks ONLY when EVERY active
  // site clears its OWN target. The old test was pooled (sum count >= sum
  // target), which let one site's overshoot mask another's shortfall — e.g.
  // Arndale 105/75 (+30) cancelling Trafford 45/75 (-30) both net 150/150, so
  // the card wrongly declared the staff event "SMASHED" while Trafford never
  // hit its 75. Require each site individually.
  const teamHit    = teamMode && teamSites.length > 0 && teamSites.every(r => r.monthCount >= r.target);
  const teamRemaining = teamMode ? Math.max(0, teamTarget - monthCount) : 0;
  const monthName  = new Date().toLocaleString("en-GB", { month: "long" });

  const siteLabels = { trafford: "Trafford", arndale: "Arndale", manchester: "Manchester", meadowhall: "Meadowhall" };

  const st = {
    root: { background: B.nori, minHeight: "100vh", color: B.rice, paddingBottom: 80 },
    bar: { display: "flex", alignItems: "center", gap: 12, padding: "10px 18px", background: B.nori, borderBottom: "1px solid rgba(212,251,111,0.18)", position: "sticky", top: 0, zIndex: 10 },
    backBtn: { background: "transparent", border: "1px solid rgba(212,251,111,0.3)", color: B.wasabi, padding: "6px 12px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" },
    title: { fontFamily: "gelica,serif", fontSize: 16, fontWeight: 700, color: B.wasabi, flex: 1 },
    sitePill: { background: B.kewpie, color: B.nori, padding: "4px 10px", borderRadius: 999, fontWeight: 800, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" },
    body: { padding: "18px", maxWidth: 880, margin: "0 auto" },
    filterRow: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18, alignItems: "center" },
    filterLabel: { fontSize: 9.5, fontWeight: 800, color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", textTransform: "uppercase", paddingRight: 4 },
    filterGroup: { display: "inline-flex", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,251,111,0.18)", borderRadius: 999, padding: 3 },
    filterBtn: { background: "transparent", border: 0, color: "rgba(255,255,255,0.65)", padding: "7px 14px", borderRadius: 999, fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },
    filterBtnOn: { background: B.wasabi, color: B.nori },

    reignLbl: { fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#fbbf24", display: "inline-flex", alignItems: "center", gap: 6, position: "relative", zIndex: 1 },
    reignWinners: { marginTop: 12, display: "flex", flexDirection: "column", gap: 8, position: "relative", zIndex: 1 },
    reignItem: { display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(251,191,36,0.22)", borderRadius: 10 },
    reignName: { fontFamily: "gelica,serif", fontWeight: 800, color: B.rice, fontSize: 22, letterSpacing: "-0.01em", lineHeight: 1.1 },
    reignStat: { fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 600 },
    reignNum: { color: B.wasabi, fontWeight: 800 },
    reignCat: { fontSize: 9.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginRight: 4 },
    streak: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#fbbf24", fontWeight: 800, background: "rgba(251,191,36,0.18)", padding: "3px 9px", borderRadius: 999, border: "1px solid rgba(251,191,36,0.4)" },
    secondaryLine: { marginTop: 4, fontSize: 12.5, color: "rgba(255,255,255,0.7)", fontWeight: 600 },

    beatChamp: { marginTop: 14, paddingTop: 14, borderTop: "1px dashed rgba(251,191,36,0.4)", position: "relative", zIndex: 1 },
    beatHead: { display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8, flexWrap: "wrap" },
    beatTtl: { fontFamily: "gelica,serif", fontSize: 14, fontWeight: 800, color: B.wasabi, letterSpacing: "0.05em", textTransform: "uppercase" },
    beatCopy: { fontSize: 12, color: "rgba(255,255,255,0.7)", fontWeight: 600 },
    beatProgress: { display: "flex", alignItems: "center", gap: 12, marginTop: 6 },
    beatLeader: { fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 700 },
    beatBar: { flex: 1, height: 8, background: "rgba(255,255,255,0.08)", borderRadius: 999, overflow: "hidden", position: "relative", minWidth: 80 },
    beatBarFill: { height: "100%", background: "linear-gradient(90deg," + B.wasabi + ",#fbbf24)", borderRadius: 999, transition: "width 0.6s cubic-bezier(.45,.05,.55,.95)" },
    beatPct: { fontFamily: "gelica,serif", fontWeight: 800, color: B.wasabi, fontSize: 16, minWidth: 44, textAlign: "right" },

    sectionHead: { fontSize: 12, fontWeight: 800, color: B.wasabi, letterSpacing: "0.14em", textTransform: "uppercase", margin: "4px 4px 12px 4px" },
    sectionHeadSub: { color: "rgba(255,255,255,0.45)", fontWeight: 700, letterSpacing: "0.08em", marginLeft: 6 },

    champs: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 },
    champ: { background: "rgba(255,255,255,0.04)", border: "1.5px solid rgba(212,251,111,0.25)", borderRadius: 16, padding: "16px 18px", position: "relative", overflow: "hidden" },
    champYou: { borderColor: B.wasabi, boxShadow: "0 0 0 3px rgba(212,251,111,0.15)" },
    champHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10, position: "relative", zIndex: 1 },
    champTtl: { fontFamily: "gelica,serif", fontWeight: 800, color: B.wasabi, fontSize: 12.5, letterSpacing: "0.08em", textTransform: "uppercase" },
    champBody: { position: "relative", zIndex: 1 },
    champName: { fontFamily: "gelica,serif", fontSize: 22, fontWeight: 800, color: B.rice, lineHeight: 1.05 },
    champCtx: { marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.65)", fontWeight: 600 },
    champStatRow: { marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "baseline", gap: 8, position: "relative", zIndex: 1 },
    champBig: { fontFamily: "gelica,serif", fontSize: 36, fontWeight: 800, color: B.wasabi, lineHeight: 1, letterSpacing: "-0.02em" },
    champBigSub: { fontSize: 12, color: "rgba(255,255,255,0.55)", fontWeight: 700 },
    youTag: { background: B.wasabi, color: B.nori, padding: "3px 9px", borderRadius: 999, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", position: "absolute", top: -10, right: 14, zIndex: 2 },

    prize: { marginTop: 12, padding: "7px 11px", background: "linear-gradient(135deg, rgba(251,191,36,0.15), rgba(212,251,111,0.10))", border: "1px solid rgba(251,191,36,0.35)", borderRadius: 10, display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, fontWeight: 700, position: "relative", zIndex: 1 },
    prizeLbl: { color: "#fbbf24", fontWeight: 800, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" },
    prizeName: { fontFamily: "gelica,serif", fontWeight: 800, color: B.rice, fontSize: 12.5 },

    sortBand: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, flexWrap: "wrap" },
    sortTabs: { display: "inline-flex", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,251,111,0.18)", borderRadius: 999, padding: 3 },
    sortTab: { background: "transparent", border: 0, color: "rgba(255,255,255,0.65)", padding: "8px 16px", borderRadius: 999, fontWeight: 700, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 },
    sortTabOn: { background: B.wasabi, color: B.nori },
    sortSummary: { fontSize: 11.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 },

    list: { display: "flex", flexDirection: "column", gap: 6 },
    row: { display: "grid", gridTemplateColumns: "44px 1fr 72px 120px", gap: 14, alignItems: "center", padding: "12px 16px 14px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,251,111,0.08)", borderRadius: 12, position: "relative", overflow: "hidden" },
    rowGold: { background: "rgba(251,191,36,0.08)", border: "1.5px solid rgba(251,191,36,0.45)", boxShadow: "0 0 24px rgba(251,191,36,0.12)" },
    rowYou: { borderColor: B.wasabi, boxShadow: "0 0 0 1px " + B.wasabi },
    rowProgress: { position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "rgba(255,255,255,0.05)" },
    rowProgressFill: { height: "100%", background: B.wasabi, transition: "width 0.5s cubic-bezier(.45,.05,.55,.95)" },
    rank: { fontFamily: "gelica,serif", fontSize: 18, fontWeight: 800, color: "rgba(255,255,255,0.5)", textAlign: "center" },
    rankGold: { color: "#fbbf24", fontSize: 22 },
    nm: { display: "flex", flexDirection: "column", minWidth: 0 },
    nmWho: { fontWeight: 700, fontSize: 15, color: B.rice },
    nmSub: { fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 3 },
    creditsBig: { textAlign: "right", fontFamily: "gelica,serif", fontSize: 24, fontWeight: 800, color: B.rice, lineHeight: 1, minWidth: 36 },
    creditsSmall: { fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.55)", whiteSpace: "nowrap" },
    ratePill: { fontSize: 12, fontWeight: 800, padding: "5px 12px", borderRadius: 999, whiteSpace: "nowrap", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
    rateOk: { background: "rgba(212,251,111,0.18)", color: B.wasabi },
    rateLow: { background: "rgba(255,122,92,0.18)", color: "#FFB39E" },
    rateNone: { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.45)" },
    rateBig: { textAlign: "right", fontFamily: "gelica,serif", fontSize: 24, fontWeight: 800, lineHeight: 1, minWidth: 56, whiteSpace: "nowrap" },
    rateBigOk: { color: B.wasabi },
    rateBigLow: { color: "#FF7A5C" },
    rateBigNone: { color: "rgba(255,255,255,0.45)" },

    howto: { marginTop: 18, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "14px 18px" },
    howtoH: { color: B.wasabi, fontSize: 14, marginBottom: 8, letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 800 },
    howtoUl: { margin: 0, paddingLeft: 18, fontSize: 12.5, color: "rgba(255,255,255,0.75)", lineHeight: 1.7 },
    howtoSmall: { marginTop: 8, fontSize: 11, color: "rgba(255,255,255,0.45)", fontStyle: "italic" },

    collapsibleHead: { display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,251,111,0.22)", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", margin: "10px 0 12px", textAlign: "left" },
    collapsibleTitle: { fontSize: 12.5, fontWeight: 800, color: B.wasabi, letterSpacing: "0.14em", textTransform: "uppercase" },
    collapsibleSummary: { fontSize: 11.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 },
    collapsibleAction: { marginLeft: "auto", fontSize: 11, fontWeight: 800, color: B.wasabi, letterSpacing: "0.06em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 4 },

    feedSection: { marginTop: 14 },
    feedDayHead: { fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.55)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "16px 4px 8px" },
    feedRow: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px", marginBottom: 6 },
    feedTop: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 },
    feedPill: { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" },
    feedTime: { fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 700 },
    feedReviewer: { fontWeight: 700, fontSize: 13, color: B.rice },
    feedStars: { color: "#fbbf24", letterSpacing: 1, fontSize: 11 },
    feedComment: { fontSize: 13, color: "rgba(255,255,255,0.85)", lineHeight: 1.45, padding: "8px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 8, margin: "4px 0" },
    feedCommentEmpty: { color: "rgba(255,255,255,0.45)", fontStyle: "italic", fontSize: 12 },
    feedCredit: { display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12, flexWrap: "wrap" },
    feedCreditLbl: { fontSize: 9.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)" },
    feedCreditWho: { fontFamily: "gelica,serif", fontWeight: 800, color: B.rice, fontSize: 13 },
    feedCreditUnv: { color: "rgba(255,255,255,0.6)", fontStyle: "italic", fontFamily: "Manrope,sans-serif", fontWeight: 700, fontSize: 12 },
    looksWrong: { marginLeft: "auto", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.65)", padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },

    empty: { padding: 30, textAlign: "center", fontSize: 13, color: "rgba(255,255,255,0.6)" },
  };

  const ATTR = {
    CONFIRMED_NAME:        { label: "Confirmed",   pillBg: "rgba(212,251,111,0.18)", pillFg: B.wasabi,  border: B.wasabi  },
    CONFIRMED_SHIFT_SOLO:  { label: "Solo shift",  pillBg: "rgba(96,165,250,0.18)",  pillFg: "#93c5fd", border: "#60a5fa" },
    CONFIRMED_SHIFT_MULTI: { label: "Multi-staff", pillBg: "rgba(192,132,252,0.18)", pillFg: "#d8b4fe", border: "#c084fc" },
    OVERLAP_SHIFTS_TEAM_POOL: { label: "Team pool", pillBg: "rgba(192,132,252,0.18)", pillFg: "#d8b4fe", border: "#c084fc" },
    NO_NAME_IN_TEXT:       { label: "Not attributed", pillBg: "rgba(255,255,255,0.06)", pillFg: "rgba(255,255,255,0.55)", border: "rgba(255,255,255,0.2)" },
    NO_SHIFT_DATA:         { label: "Not attributed", pillBg: "rgba(255,255,255,0.06)", pillFg: "rgba(255,255,255,0.55)", border: "rgba(255,255,255,0.2)" },
    FLAGGED:               { label: "Flagged",     pillBg: "rgba(251,191,36,0.18)",  pillFg: "#fbbf24", border: "#fbbf24" },
  };

  const groupFeedByDay = (rows) => {
    const m = new Map();
    rows.forEach(r => {
      const key = (r.createTime || "").slice(0, 10);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    });
    return Array.from(m.entries()).sort(([a],[b]) => b.localeCompare(a));
  };

  const formatDayHead = (isoDate) => {
    if (!isoDate) return "";
    const d = new Date(isoDate);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    const isToday = d.toDateString() === today.toDateString();
    const isYest  = d.toDateString() === yest.toDateString();
    const dayName  = d.toLocaleDateString("en-GB", { weekday: "short" });
    const dayNum   = d.getDate();
    const monthName = d.toLocaleDateString("en-GB", { month: "short" });
    const dateLabel = dayName + " " + dayNum + " " + monthName;
    if (isToday) return "Today · " + dateLabel;
    if (isYest)  return "Yesterday · " + dateLabel;
    return dateLabel;
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  };

  const onLookWrong = (r) => {
    const ok = window.confirm(
      'Flag this attribution for your manager?\n\n' +
      r.reviewer + ' · ' + (r.comment || '5★ rating only') + '\n' +
      'Currently goes to: ' + (r.attributedName || 'no-one') + '\n\n' +
      '(Submission goes to admin dispute queue — coming with the Staff Performance admin tile.)'
    );
    if (ok) window.alert("Sent to manager for review.\n\n(Stub — submission wires up when the Staff Performance admin tile ships.)");
  };

  const renderFeedRow = (r) => {
    const meta = ATTR[r.attributionMethod] || ATTR.NO_NAME_IN_TEXT;
    const time = formatTime(r.createTime);
    const isAttributed = !!r.attributedName;
    const multiNames = isAttributed && (r.attributionMethod === "CONFIRMED_SHIFT_MULTI" || r.attributionMethod === "OVERLAP_SHIFTS_TEAM_POOL")
      ? r.attributedName.split(",").map(s => s.trim()).filter(Boolean).join(" · ")
      : null;

    return (
      <div key={r.reviewId} style={st.feedRow}>
        <div style={st.feedTop}>
          <span style={st.feedTime}>{time}</span>
          <span style={st.feedReviewer}>{r.reviewer || "Anonymous"}</span>
          <span style={st.feedStars}>★★★★★</span>
        </div>
        {r.comment ? (
          <div style={st.feedComment}>{r.comment}</div>
        ) : (
          <div style={{...st.feedComment, ...st.feedCommentEmpty}}>5★ rating, no review text</div>
        )}
        <div style={st.feedCredit}>
          <span style={st.feedCreditLbl}>
            {r.attributionMethod === "FLAGGED" ? "Flagged" : "Goes to"}
          </span>
          <span style={isAttributed ? st.feedCreditWho : st.feedCreditUnv}>
            {isAttributed
              ? (multiNames || r.attributedName)
              : (r.attributionMethod === "FLAGGED" ? "Manager review needed" : "Not attributed yet")}
          </span>
          {isAttributed && r.attributionMethod !== "FLAGGED" && (
            <button style={st.looksWrong} onClick={() => onLookWrong(r)}>looks wrong?</button>
          )}
        </div>
      </div>
    );
  };

  const feedDays = groupFeedByDay(feedRows);

  // Per-row progress bar target. Priority: Beat the Champ → individual
  // target → month target. v1: only Beat the Champ. Returns null if no
  // target is active (in which case rows render without a progress bar).
  const rowTarget = beatChampOn ? (reigning.total.credits + 1) : null;

  const renderRow = (r) => {
    // Gold treatment (background glow + 🥇 icon) is earned, not automatic:
    // - Most Reviews tab → #1 is always gold (real winner)
    // - Best Rate tab    → only gild #1 when their rate actually meets target;
    //   below-target rate-leaders show as "#1" with regular styling so the
    //   visual reward matches the achievement (JJ 2026-05-12).
    // Gold 🥇 only fires when the rank actually means something. Total sort:
    // requires credits > 0 (so day-1-of-month doesn't crown whoever sorts
    // first alphabetically with 0 reviews). Rate sort: existing requirement
    // — must have shifts AND meet the per-shift target.
    const qualifiesForGold = r.rank === 1 && (sort === "total" ? r.credits > 0 : (r.shifts > 0 && r.perShift >= target));
    const cls = qualifiesForGold ? st.rowGold : null;
    const youCls = r.you ? st.rowYou : null;
    const rateClass = (!r.shifts || r.shifts === 0)
      ? st.rateNone
      : (r.perShift >= target ? st.rateOk : st.rateLow);

    // Sub-line keeps only the bySite breakdown for Manchester combined view
    // (helps staff see which site they're racking credits at). Attribution
    // method breakdown + shift count dropped per JJ 2026-05-10 — admin-grade
    // detail, lives in Staff Performance admin tile only.
    const bySiteHint = r.bySite ? _bySiteSplit(r.bySite) : null;

    const rowPct = rowTarget ? Math.min(100, Math.round((r.credits / rowTarget) * 100)) : 0;
    const rowSmashed = rowTarget ? r.credits >= rowTarget : false;

    // Hero metric flips with the sort tab (locked 2026-05-11):
    //   Most Reviews tab → credits is BIG, rate is small badge
    //   Best Rate tab    → rate is BIG (target-coloured), credits is small muted
    const rateBigClass = (!r.shifts || r.shifts === 0) ? st.rateBigNone
      : (r.perShift >= target ? st.rateBigOk : st.rateBigLow);

    return (
      <div key={r.pin || ("row-" + r.rank)} style={{...st.row, ...cls, ...youCls}}>
        <div style={{...st.rank, ...(qualifiesForGold ? st.rankGold : {})}}>{qualifiesForGold ? "🥇" : "#" + r.rank}</div>
        <div style={st.nm}>
          <div style={st.nmWho}>{r.displayName || r.name}</div>
          {bySiteHint && <div style={st.nmSub}>{bySiteHint}</div>}
        </div>
        {sort === "total" ? (
          <>
            <div style={st.creditsBig}>{r.credits}</div>
            <div style={{...st.ratePill, ...rateClass}}>
              {r.shifts > 0 ? (<><span>Rate =</span><span>{r.perShift}/{target}</span></>) : <span style={{margin:"0 auto"}}>—</span>}
            </div>
          </>
        ) : (
          <>
            <div style={{...st.rateBig, ...rateBigClass}}>
              {r.shifts > 0 ? (Number(r.perShift).toFixed(1) + "/" + target) : "—"}
            </div>
            <div style={{...st.ratePill, ...rateClass}}>
              <span>Total =</span><span>{r.credits}</span>
            </div>
          </>
        )}
        {rowTarget !== null && (
          <div style={st.rowProgress}>
            <div className={rowSmashed ? "rolled-beat-bar-fill-smashed" : ""} style={{...st.rowProgressFill, width: rowPct + "%"}} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={st.root}>
      <div style={st.bar}>
        <button style={st.backBtn} onClick={onBack}>← Back</button>
        <span style={st.title}>⭐ Reviews leaderboard</span>
        <button onClick={openMyStats} disabled={myStatsBusy} aria-label="View your personal review stats" style={{background:"rgba(212,251,111,0.16)", color:B.wasabi, border:"1px solid rgba(212,251,111,0.3)", padding:"6px 11px", borderRadius:8, fontWeight:700, fontSize:12.5, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:5, marginRight:6, opacity: myStatsBusy ? 0.6 : 1, whiteSpace:"nowrap"}}>📊 Your stats</button>
        <span style={st.sitePill}>{siteName}</span>
      </div>
      <div style={st.body}>
        <div style={st.filterRow}>
          {showSiteToggle && (
            <>
              <span style={st.filterLabel}>Site</span>
              <div style={st.filterGroup} role="tablist" aria-label="Site scope">
                {allowedSites.map(s => (
                  <button key={s} style={{...st.filterBtn, ...(site === s ? st.filterBtnOn : {})}} onClick={() => setSite(s)} aria-pressed={site === s}>
                    {siteLabels[s] || s}
                  </button>
                ))}
              </div>
            </>
          )}
          <span style={{...st.filterLabel, marginLeft: showSiteToggle ? 6 : 0}}>Period</span>
          <div style={st.filterGroup} role="tablist" aria-label="Period">
            {[{k:"month", label:"This month"}].map(p => (
              <button key={p.k} style={{...st.filterBtn, ...(period === p.k ? st.filterBtnOn : {})}} onClick={() => setPeriod(p.k)} aria-pressed={period === p.k}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Team target card (2026-05-27) — sits alongside reigning + leaders +
            full leaderboard when admin sets monthlyConfig.teamTarget > 0 for
            the region. Per-site target: flipping the site toggle re-renders
            against that site's target/count. When monthCount >= teamTarget the
            card gets the gold .rolled-team-card-hit shimmer + the prize copy
            flips from "Prize this month" to "🎉 TARGET HIT — EARN". */}
        {teamMode && period === "month" && (
          <div className={"rolled-team-card" + (teamHit ? " rolled-team-card-hit" : "")}>
            <div style={{position:"relative", zIndex:1}}>
              <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.14em", textTransform:"uppercase", color: teamHit ? "#fbbf24" : B.wasabi, display:"inline-flex", alignItems:"center", gap:6, marginBottom:6}}>
                <span style={{fontSize:16}}>{teamHit ? "🎉" : "🏆"}</span>
                Team target · {monthName} · {siteLabels[site] || site}
                {teamHit && <span style={{marginLeft:6, fontSize:10.5, background:"rgba(251,191,36,0.22)", color:"#fbbf24", padding:"2px 8px", borderRadius:999, border:"1px solid rgba(251,191,36,0.45)", letterSpacing:"0.1em"}}>SMASHED</span>}
              </div>
              <div style={{fontSize:13, color:"rgba(255,255,255,0.72)", fontWeight:600, marginBottom:18, lineHeight:1.4, maxWidth:520}}>
                {teamHit
                  ? "We did it. Every 5★ review counted — the whole team earns the reward."
                  : (teamCombined
                      ? "Every 5★ review counts. Every site must hit its own target by month-end for the whole team to earn."
                      : "Every 5★ review counts. Hit the site target by month-end and the whole team earns.")}
              </div>

              {teamCombined ? (
                <div style={{display:"flex", flexDirection:"column", gap:14}}>
                  {teamSites.map(rw => {
                    const pct = rw.target > 0 ? Math.min(100, Math.round((rw.monthCount / rw.target) * 100)) : 0;
                    const done = rw.monthCount >= rw.target;
                    const remain = Math.max(0, rw.target - rw.monthCount);
                    return (
                      <div key={rw.site}>
                        <div style={{display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:6, flexWrap:"wrap", gap:8}}>
                          <span style={{fontFamily:"gelica,serif", fontSize:16, fontWeight:800, color:B.rice, display:"inline-flex", alignItems:"center", gap:6}}>
                            {siteLabels[rw.site] || rw.site}
                          </span>
                          <span style={{fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.7)"}}>
                            <span style={{fontFamily:"gelica,serif", fontSize:22, fontWeight:800, color: done ? "#fbbf24" : B.wasabi}}>{rw.monthCount}</span>
                            <span style={{opacity:0.6}}> / {rw.target}</span>
                          </span>
                        </div>
                        <div className="rolled-team-bar-track">
                          <div className={"rolled-team-bar-fill" + (done ? " rolled-team-bar-fill-hit" : "")} style={{width: pct + "%"}} />
                        </div>
                        <div style={{marginTop:6, fontSize:11.5, fontWeight:700, color: done ? "#fbbf24" : "rgba(255,255,255,0.6)"}}>
                          {done ? "Target hit" : (remain + " more 5★ to go")}
                        </div>
                      </div>
                    );
                  })}
                  <div style={{fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.6)", lineHeight:1.4}}>
                    {teamHit
                      ? "Both sites hit their target — the reward is earned."
                      : "Both sites must reach their own target for the team to earn the reward."}
                  </div>
                </div>
              ) : (
                <>
                  <div style={{display:"flex", alignItems:"baseline", gap:8, marginBottom:10, flexWrap:"wrap"}}>
                    <span style={{fontFamily:"gelica,serif", fontSize:46, fontWeight:800, color: teamHit ? "#fbbf24" : B.wasabi, lineHeight:1, letterSpacing:"-0.02em"}}>{monthCount}</span>
                    <span style={{fontSize:18, fontWeight:600, color:"rgba(255,255,255,0.55)"}}>of</span>
                    <span style={{fontFamily:"gelica,serif", fontSize:28, fontWeight:800, color:B.rice, lineHeight:1}}>{teamTarget}</span>
                    <span style={{fontSize:13, color:"rgba(255,255,255,0.55)", fontWeight:600, marginLeft:4}}>5★ reviews this month</span>
                  </div>

                  <div className="rolled-team-bar-track">
                    <div className={"rolled-team-bar-fill" + (teamHit ? " rolled-team-bar-fill-hit" : "")} style={{width: teamPct + "%"}} />
                  </div>
                  <div style={{display:"flex", justifyContent:"space-between", marginTop:8, fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.7)"}}>
                    <span>{teamPct}% there</span>
                    <span>{teamHit ? "Target smashed" : (teamRemaining + " more to go")}</span>
                  </div>
                </>
              )}

              {teamPrize && (
                <div style={{marginTop:18, padding:"12px 14px", background: teamHit ? "linear-gradient(135deg, rgba(251,191,36,0.22), rgba(212,251,111,0.16))" : "linear-gradient(135deg, rgba(212,251,111,0.14), rgba(111,175,143,0.08))", border: teamHit ? "1px solid rgba(251,191,36,0.5)" : "1px solid rgba(212,251,111,0.32)", borderRadius:12, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap"}}>
                  <span style={{fontSize:22, lineHeight:1}}>{teamHit ? "🎉" : "🎁"}</span>
                  <div style={{display:"flex", flexDirection:"column", flex:1, minWidth:160}}>
                    <span style={{fontSize:10, fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", color: teamHit ? "#fbbf24" : "rgba(212,251,111,0.85)"}}>
                      {teamHit ? "Target hit — earn" : "Prize this month"}
                    </span>
                    <span style={{fontFamily:"gelica,serif", fontWeight:800, fontSize:18, color:B.rice, marginTop:2}}>{teamPrize}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reigning champ card — default visible (per JJ 2026-05-11). Admin can
            hide via reigning.visible = false. Two distinct sub-card blocks.
            Stays visible alongside the team goal card per JJ 2026-05-27. */}
        {period === "month" && reigning && reigning.visible !== false && reigning.total && (
          <div className="rolled-reigning-card">
            <div style={st.reignLbl}>
              <span style={{fontSize: 16}}>👑</span>
              Reigning champs · {reigning.total.month}
            </div>

            <div style={st.reignWinners}>
              <div style={st.reignItem}>
                <span style={st.reignName}>{reigning.total.name}</span>
                <span style={st.reignStat}>
                  <span style={st.reignCat}>Most Reviews</span>
                  <span style={st.reignNum}>{reigning.total.credits}</span>
                </span>
                {reigning.total.streak >= 2 && (
                  <span style={st.streak}>🔥 {reigning.total.streak}-month streak</span>
                )}
              </div>
              {reigning.rate && (
                <div style={st.reignItem}>
                  <span style={st.reignName}>{reigning.rate.name}</span>
                  <span style={st.reignStat}>
                    <span style={st.reignCat}>Best Rate</span>
                    <span style={st.reignNum}>{reigning.rate.perShift}</span>/{target} per shift
                  </span>
                  {reigning.rate.streak >= 2 && (
                    <span style={st.streak}>🔥 {reigning.rate.streak}-month streak</span>
                  )}
                </div>
              )}
            </div>

            {beatChampOn && reigning && reigning.total && (
              // Beat the Champ — challenge announcement only. Per-row
              // progress bars on the leaderboard below show each staffer's
              // own progress toward the target (locked 2026-05-10).
              <div style={st.beatChamp}>
                <div style={st.beatHead}>
                  <span style={{fontSize: 16}}>🎯</span>
                  <span style={st.beatTtl}>Beat the Champ</span>
                  <span style={st.beatCopy}>— first to <span style={st.reignNum}>{reigning.total.credits + 1}</span> wins</span>
                </div>
              </div>
            )}
          </div>
        )}

        {showCurrentLeaders && (
          <>
            <div style={st.sectionHead}>
              Current leaders <span style={st.sectionHeadSub}>· this month so far</span>
            </div>
            <div style={st.champs}>
              <div style={{...st.champ, ...(totalLeader.you ? st.champYou : {})}}>
                {totalLeader.you && <span style={st.youTag}>You</span>}
                <div style={st.champHead}>
                  <span style={{fontSize: 16}}>🥇</span>
                  <span style={st.champTtl}>Most Reviews</span>
                </div>
                <div style={st.champBody}>
                  <div style={st.champName}>{totalLeader.displayName || totalLeader.name}</div>
                  <div style={st.champCtx}>
                    {totalSecond && totalLeader.credits > totalSecond.credits ? (
                      <>leading by <span style={st.reignNum}>{totalLeader.credits - totalSecond.credits}</span></>
                    ) : (
                      <>tied at the top</>
                    )}
                  </div>
                </div>
                <div style={st.champStatRow}>
                  <span style={st.champBig}>{totalLeader.credits}</span>
                  <span style={st.champBigSub}>this month</span>
                </div>
                {!teamMode && indivMostPrize && (
                  <div style={st.prize}>
                    <span style={{fontSize: 14}}>🎁</span>
                    <span style={st.prizeLbl}>{prizeVerb}</span>
                    <span style={st.prizeName}>{indivMostPrize}</span>
                  </div>
                )}
              </div>

              <div style={{...st.champ, ...(rateLeader.you ? st.champYou : {})}}>
                {rateLeader.you && <span style={st.youTag}>You</span>}
                <div style={st.champHead}>
                  <span style={{fontSize: 16}}>⚡</span>
                  <span style={st.champTtl}>Best Rate</span>
                </div>
                <div style={st.champBody}>
                  <div style={st.champName}>{rateLeader.displayName || rateLeader.name}</div>
                  <div style={st.champCtx}>
                    over <span style={st.reignNum}>{rateLeader.shifts}</span> shifts this month
                  </div>
                </div>
                <div style={st.champStatRow}>
                  <span style={st.champBig}>{Number(rateLeader.perShift).toFixed(1)}</span>
                  <span style={st.champBigSub}>/{target} per shift</span>
                </div>
                {!teamMode && indivRatePrize && (
                  <div style={st.prize}>
                    <span style={{fontSize: 14}}>🎁</span>
                    <span style={st.prizeLbl}>{prizeVerb}</span>
                    <span style={st.prizeName}>{indivRatePrize}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Full leaderboard — collapsible. Header clearly button-styled.
            Stays visible alongside the team goal card per JJ 2026-05-27 —
            shows individual contribution to the team total. */}
        <button style={st.collapsibleHead} onClick={() => setLeaderboardOpen(v => !v)} aria-expanded={leaderboardOpen}>
          <span style={st.collapsibleTitle}>Leaderboard</span>
          {loading && <span style={{...st.collapsibleSummary, color: B.wasabi}}>· Refreshing…</span>}
          {blackout && <span style={st.collapsibleSummary}>· 🤫 Leader hidden</span>}
          <span style={st.collapsibleAction}>{leaderboardOpen ? "Hide ▾" : "Show ▸"}</span>
        </button>
        {leaderboardOpen && (
          <>
            <div style={st.sortBand}>
              <div style={st.sortTabs} role="tablist" aria-label="Sort">
                <button style={{...st.sortTab, ...(sort === "total" ? st.sortTabOn : {})}} onClick={() => setSort("total")} aria-pressed={sort==="total"}>
                  <span style={{fontSize: 14}}>🥇</span> Most Reviews
                </button>
                <button style={{...st.sortTab, ...(sort === "rate" ? st.sortTabOn : {})}} onClick={() => setSort("rate")} aria-pressed={sort==="rate"}>
                  <span style={{fontSize: 14}}>⚡</span> Best Rate
                </button>
              </div>
              <span style={st.sortSummary}>
                {sort === "total" ? "Ranked by total reviews" : "Ranked by review rate (target " + target + "/shift)"}
              </span>
            </div>

            <div style={st.list}>
              {loading && sorted.length === 0 && <div style={st.empty}>Loading leaderboard…</div>}
              {errored && sorted.length === 0 && <div style={st.empty}>Couldn't load reviews. Tap Back and try again.</div>}
              {sorted.length === 0 && !loading && !errored && (
                <div style={st.empty}>No 5★ reviews attributed {period === "month" ? "this month" : "all-time"} yet.</div>
              )}
              {sorted.map(renderRow)}
            </div>
          </>
        )}

        {/* Latest reviews — collapsible, capped at 10 most recent. */}
        <div style={st.feedSection}>
          <button style={st.collapsibleHead} onClick={() => setFeedOpen(v => !v)} aria-expanded={feedOpen}>
            <span style={st.collapsibleTitle}>Latest reviews</span>
            <span style={st.collapsibleAction}>{feedOpen ? "Hide ▾" : "Show ▸"}</span>
          </button>
          {feedOpen && (
            <>
              {loading && feedRows.length === 0 && <div style={st.empty}>Loading reviews…</div>}
              {!loading && feedRows.length === 0 && <div style={st.empty}>No 5★ reviews in this period yet.</div>}
              {feedDays.map(([dayKey, dayRows]) => (
                <div key={dayKey}>
                  <div style={st.feedDayHead}>{formatDayHead(dayKey)}</div>
                  {dayRows.map(renderFeedRow)}
                </div>
              ))}
            </>
          )}
        </div>

        <div style={st.howto}>
          <h3 style={st.howtoH}>How attribution works</h3>
          <ul style={st.howtoUl}>
            <li>Only <strong style={{color: B.wasabi}}>5★ reviews</strong> count.</li>
            <li><strong style={{color: B.wasabi}}>Named in review</strong> — review mentions a staff member who was on shift → that person gets the review.</li>
            <li><strong style={{color: B.wasabi}}>Solo shift</strong> — only one staff member was on shift in the visit window → they get the review.</li>
            <li><strong style={{color: B.wasabi}}>Multi-staff overlap</strong> — multiple people on shift, no name in text → everyone on shift gets the review.</li>
            <li><strong style={{color: B.wasabi}}>Review Rate</strong> = reviews ÷ shifts worked this period.</li>
            <li><strong style={{color: B.wasabi}}>Tied?</strong> If two people have the same total, the higher review rate ranks above. (And vice-versa for the Best Rate board.)</li>
          </ul>

          {/* B6 — Tips & tricks (2026-05-12). Practical advice for staff to
              climb the leaderboard fairly. Same howto card so it lives next to
              the attribution explainer — read what counts, then how to ask. */}
          <h3 style={{...st.howtoH, marginTop: 22}}>💡 Tips &amp; tricks — how to climb</h3>
          <ul style={st.howtoUl}>
            <li><strong style={{color: B.wasabi}}>Say your name when you serve.</strong> <em>"G'day, I'm Bluey, hope you enjoy!"</em> — makes you easy to spot if they shout you out.</li>
            <li><strong style={{color: B.wasabi}}>Wear your name tag</strong> — required part of uniform. If you don't have one, please alert management.</li>
            <li><strong style={{color: B.wasabi}}>Time the ask after a compliment or request</strong> is made by the customer.</li>
            <li><strong style={{color: B.wasabi}}>Point to the QR code sign.</strong> Fastest path — 20 seconds in Google and they're done.</li>
            <li><strong style={{color: B.wasabi}}>Solo shift?</strong> Every 5★ review lands on you, no sharing. Great chance to bank reviews.</li>
            <li><strong style={{color: B.wasabi}}>Ask once, never twice.</strong> Pressure backfires. A calm <em>"we love Google reviews if you've got 30 sec"</em> is plenty.</li>
          </ul>

          <div style={st.howtoSmall}>Disagree with how a review was attributed? Tap it in the live feed and hit "looks wrong?" — your manager takes a look.</div>
        </div>
      </div>

      {/* B4 v2 — Personal stats modal. PIN-gated; pulls handleGetStaffPersonalStats from @222.
          Header: name + both ranks (Most Reviews + Best Rate).
          Body: KPI grid · Climb-the-ranks gap-to-next · How you got the reviews (method mix) · vs last month. */}
      {(myStats || myStatsBusy || myStatsErr) && (
        <div onClick={e => { if (e.target === e.currentTarget) { setMyStats(null); setMyStatsErr(null); } }}
             style={{position:"fixed", inset:0, background:"rgba(0,42,32,0.75)", backdropFilter:"blur(6px)", display:"flex", alignItems:"flex-start", justifyContent:"center", zIndex:200, padding:"60px 18px 18px", overflow:"auto"}}>
          <div style={{background:B.kewpie, color:B.nori, borderRadius:18, maxWidth:580, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.4)", overflow:"hidden"}}>
            <div style={{background:B.nori, color:B.wasabi, padding:"18px 22px"}}>
              <div style={{display:"flex", alignItems:"flex-start", gap:14}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", opacity:0.65, marginBottom:4}}>Your stats · this month</div>
                  <div style={{fontFamily:"gelica,serif", fontSize:26, fontWeight:700, lineHeight:1.1}}>
                    {myStats ? myStats.me.firstName : (myStatsBusy ? "Loading…" : "—")}
                  </div>
                </div>
                <button onClick={() => { setMyStats(null); setMyStatsErr(null); }} aria-label="Close" style={{background:"transparent", color:B.wasabi, border:0, fontSize:24, padding:0, lineHeight:1, cursor:"pointer"}}>×</button>
              </div>
              {myStats && (
                <div style={{display:"flex", gap:8, marginTop:12, flexWrap:"wrap", alignItems:"center"}}>
                  {myStats.thisMonth.rankTotal > 0 ? (
                    <span style={{display:"inline-flex", alignItems:"center", gap:5, background: myStats.thisMonth.rankTotal === 1 ? B.wasabi : "rgba(212,251,111,0.16)", color: myStats.thisMonth.rankTotal === 1 ? B.nori : B.wasabi, padding:"4px 10px", borderRadius:999, fontSize:12, fontWeight:800, letterSpacing:"0.02em"}}>
                      🥇 #{myStats.thisMonth.rankTotal} Most Reviews
                    </span>
                  ) : (
                    <span style={{display:"inline-flex", alignItems:"center", gap:5, background:"rgba(255,255,255,0.08)", color:"rgba(212,251,111,0.55)", padding:"4px 10px", borderRadius:999, fontSize:12, fontWeight:700, letterSpacing:"0.02em"}}>
                      🥇 Not ranked yet
                    </span>
                  )}
                  {myStats.thisMonth.rankRate > 0 ? (
                    <span style={{display:"inline-flex", alignItems:"center", gap:5, background: myStats.thisMonth.rankRate === 1 ? B.wasabi : "rgba(212,251,111,0.16)", color: myStats.thisMonth.rankRate === 1 ? B.nori : B.wasabi, padding:"4px 10px", borderRadius:999, fontSize:12, fontWeight:800, letterSpacing:"0.02em"}}>
                      ⚡ #{myStats.thisMonth.rankRate} Best Rate
                    </span>
                  ) : null}
                  {myStats.thisMonth.totalRanked > 0 && (
                    <span style={{fontSize:11, color:"rgba(212,251,111,0.65)", fontWeight:600}}>
                      of {myStats.thisMonth.totalRanked} in {myStats.scope === "manchester" ? "Manchester" : (myStats.scope.charAt(0).toUpperCase() + myStats.scope.slice(1))}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div style={{padding:"20px 22px"}}>
              {myStatsBusy && <div style={{textAlign:"center", padding:24, color:"rgba(0,57,44,0.55)", fontWeight:600, fontSize:13}}>Pulling your numbers…</div>}
              {myStatsErr && <div style={{padding:"12px 14px", background:"rgba(200,90,90,0.12)", color:"#a8302d", borderRadius:10, fontSize:13, fontWeight:600}}>{myStatsErr}</div>}
              {myStats && (
                <>
                  {/* KPI grid */}
                  <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:18}}>
                    <div style={{background:"#fff", border:"1px solid rgba(0,57,44,0.08)", borderRadius:12, padding:"12px 14px"}}>
                      <div style={{fontFamily:"gelica,serif", fontSize:34, fontWeight:800, color:B.nori, lineHeight:1}}>{myStats.thisMonth.credits}</div>
                      <div style={{fontSize:10, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:"rgba(0,57,44,0.6)", marginTop:6}}>5★ reviews</div>
                    </div>
                    <div style={{background:"#fff", border:"1px solid rgba(0,57,44,0.08)", borderRadius:12, padding:"12px 14px"}}>
                      <div style={{fontFamily:"gelica,serif", fontSize:34, fontWeight:800, color:B.nori, lineHeight:1}}>
                        {Number(myStats.thisMonth.perShift).toFixed(1)}<span style={{fontSize:20, opacity:0.55, marginLeft:4}}>/{myStats.thisMonth.target}</span>
                      </div>
                      <div style={{fontSize:10, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:"rgba(0,57,44,0.6)", marginTop:6}}>Per-shift rate</div>
                    </div>
                    <div style={{background:"#fff", border:"1px solid rgba(0,57,44,0.08)", borderRadius:12, padding:"12px 14px"}}>
                      <div style={{fontFamily:"gelica,serif", fontSize:26, fontWeight:800, color:B.nori, lineHeight:1}}>{myStats.thisMonth.shifts}</div>
                      <div style={{fontSize:10, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:"rgba(0,57,44,0.6)", marginTop:6}}>Shifts worked</div>
                    </div>
                    <div style={{background: myStats.thisMonth.targetAttainmentPct >= 100 ? "#D4FB6F" : "#fff", border:"1px solid rgba(0,57,44,0.08)", borderRadius:12, padding:"12px 14px"}}>
                      <div style={{fontFamily:"gelica,serif", fontSize:26, fontWeight:800, color:B.nori, lineHeight:1}}>{myStats.thisMonth.targetAttainmentPct}%</div>
                      <div style={{fontSize:10, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:"rgba(0,57,44,0.6)", marginTop:6}}>Target hit</div>
                    </div>
                  </div>

                  {/* Climb the ranks — gap-to-next on both metrics */}
                  {(myStats.thisMonth.gapToNextTotal || myStats.thisMonth.gapToNextRate || (myStats.thisMonth.rankTotal === 1 && myStats.thisMonth.rankRate === 1 && myStats.thisMonth.credits > 0)) && (
                    <>
                      <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", color:"rgba(0,57,44,0.6)", marginBottom:10}}>Climb the ranks</div>
                      <div style={{display:"flex", flexDirection:"column", gap:8, marginBottom:18}}>
                        {myStats.thisMonth.gapToNextTotal && (
                          <div style={{padding:"10px 13px", background:"#fff", border:"1px solid rgba(0,57,44,0.08)", borderRadius:10, fontSize:13, display:"flex", alignItems:"center", gap:10}}>
                            <span style={{fontSize:18}}>🥇</span>
                            <span>
                              {myStats.thisMonth.gapToNextTotal.reviewsAhead === 0 ? (
                                <>You're <strong>tied</strong> with <strong>{myStats.thisMonth.gapToNextTotal.name}</strong> — next review tips you ahead.</>
                              ) : (
                                <><strong>{myStats.thisMonth.gapToNextTotal.reviewsAhead}</strong> more {myStats.thisMonth.gapToNextTotal.reviewsAhead === 1 ? "review" : "reviews"} and you overtake <strong>{myStats.thisMonth.gapToNextTotal.name}</strong>.</>
                              )}
                            </span>
                          </div>
                        )}
                        {myStats.thisMonth.gapToNextRate && (
                          <div style={{padding:"10px 13px", background:"#fff", border:"1px solid rgba(0,57,44,0.08)", borderRadius:10, fontSize:13, display:"flex", alignItems:"center", gap:10}}>
                            <span style={{fontSize:18}}>⚡</span>
                            <span>
                              {myStats.thisMonth.gapToNextRate.perShiftAhead === 0 ? (
                                <>Tied with <strong>{myStats.thisMonth.gapToNextRate.name}</strong> on per-shift rate.</>
                              ) : (
                                <><strong>+{Number(myStats.thisMonth.gapToNextRate.perShiftAhead).toFixed(2)}/shift</strong> and you overtake <strong>{myStats.thisMonth.gapToNextRate.name}</strong>.</>
                              )}
                            </span>
                          </div>
                        )}
                        {myStats.thisMonth.rankTotal === 1 && myStats.thisMonth.rankRate === 1 && myStats.thisMonth.credits > 0 && (
                          <div style={{padding:"10px 13px", background:B.wasabi, color:B.nori, borderRadius:10, fontSize:13, fontWeight:700, display:"flex", alignItems:"center", gap:10}}>
                            <span style={{fontSize:18}}>👑</span>
                            <span>You're crowning <strong>both</strong> leaderboards. Defend the throne! 🏆</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* How you got the reviews — method mix (info not in the public leaderboard) */}
                  {(() => {
                    const bm = myStats.thisMonth.byMethod || {};
                    const named = bm.CONFIRMED_NAME || 0;
                    const solo = bm.CONFIRMED_SHIFT_SOLO || 0;
                    const multi = bm.CONFIRMED_SHIFT_MULTI || 0;
                    const tot = named + solo + multi;
                    if (tot === 0) return null;
                    const pct = (n) => ((n/tot)*100).toFixed(0);
                    return (
                      <>
                        <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", color:"rgba(0,57,44,0.6)", marginBottom:10}}>How you got the reviews</div>
                        <div style={{background:"#fff", border:"1px solid rgba(0,57,44,0.08)", borderRadius:12, padding:"12px 14px", marginBottom:18}}>
                          <div style={{display:"flex", height:14, borderRadius:4, overflow:"hidden", marginBottom:10, border:"1px solid rgba(0,57,44,0.08)"}}>
                            <span style={{width: pct(named) + "%", background: B.nori}}></span>
                            <span style={{width: pct(solo) + "%", background: "#4ab27a"}}></span>
                            <span style={{width: pct(multi) + "%", background: "#9fd470"}}></span>
                          </div>
                          <div style={{display:"flex", gap:14, flexWrap:"wrap", fontSize:11.5, fontWeight:600, color:"rgba(0,57,44,0.7)"}}>
                            <span style={{display:"inline-flex", alignItems:"center", gap:5}}><span style={{display:"inline-block", width:9, height:9, borderRadius:2, background:B.nori}}></span><strong style={{color:B.nori}}>{named}</strong> named in review</span>
                            <span style={{display:"inline-flex", alignItems:"center", gap:5}}><span style={{display:"inline-block", width:9, height:9, borderRadius:2, background:"#4ab27a"}}></span><strong style={{color:B.nori}}>{solo}</strong> solo shift</span>
                            <span style={{display:"inline-flex", alignItems:"center", gap:5}}><span style={{display:"inline-block", width:9, height:9, borderRadius:2, background:"#9fd470"}}></span><strong style={{color:B.nori}}>{multi}</strong> multi-staff</span>
                          </div>
                          <div style={{fontSize:11, color:"rgba(0,57,44,0.55)", marginTop:10, fontStyle:"italic", lineHeight:1.5}}>
                            Named reviews land on you solo. Multi-staff splits across the whole shift — say your name when serving to push more into <strong>named</strong>.
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  {/* vs Last month */}
                  <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", color:"rgba(0,57,44,0.6)", marginBottom:10}}>vs Last month</div>
                  <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:14}}>
                    <div style={{flex:1, minWidth:170, padding:"10px 13px", background:"#fff", border:"1px solid rgba(0,57,44,0.08)", borderRadius:10, fontSize:13}}>
                      <span style={{fontFamily:"gelica,serif", fontSize:22, fontWeight:800, color: myStats.delta.credits >= 0 ? "#2e9a52" : "#c85a5a", marginRight:6}}>
                        {myStats.delta.credits >= 0 ? "+" : ""}{myStats.delta.credits}
                      </span>
                      reviews · was {myStats.lastMonth.credits}
                    </div>
                    <div style={{flex:1, minWidth:170, padding:"10px 13px", background:"#fff", border:"1px solid rgba(0,57,44,0.08)", borderRadius:10, fontSize:13}}>
                      <span style={{fontFamily:"gelica,serif", fontSize:22, fontWeight:800, color: myStats.delta.perShift >= 0 ? "#2e9a52" : "#c85a5a", marginRight:6}}>
                        {myStats.delta.perShift >= 0 ? "+" : ""}{Number(myStats.delta.perShift).toFixed(1)}
                      </span>
                      per-shift · was {Number(myStats.lastMonth.perShift).toFixed(1)}
                    </div>
                  </div>

                  <div style={{fontSize:11, color:"rgba(0,57,44,0.55)", fontStyle:"italic", lineHeight:1.5}}>
                    Tap × or outside to close. Want more? Read "How attribution works" + "Tips &amp; tricks" on the main view.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  // Auto-updater: surfaces a "New version" banner when a newer build is live.
  const __updateAvailable = useAutoUpdater();
  useVersionPillDOM(__updateAvailable);
  // Every open of the app starts at the PIN pad (session lock, JJ 28 Sep 2026). Only a
  // preview host (SESSION_RESTORE_OK) still picks a sign-in back up after a refresh.
  const [view,setView] = useState(()=>{if(!SESSION_RESTORE_OK)return "login";try{return sessionStorage.getItem("rolled.view")||"login"}catch(e){return "login"}});
  const [pin,setPin] = useState("");
  const [staff,setStaff] = useState(()=>{if(!SESSION_RESTORE_OK)return "";try{return sessionStorage.getItem("rolled.staff")||""}catch(e){return ""}});
  const [staffRole,setStaffRole] = useState(()=>{if(!SESSION_RESTORE_OK)return "";try{return sessionStorage.getItem("rolled.staffRole")||""}catch(e){return ""}});
  const [err,setErr] = useState("");
  // Why the PIN pad is showing, when the idle lock put it there. Cleared by the next digit.
  const [lockNote,setLockNote] = useState("");
  const [shake,setShake] = useState(false);
  // R12 — neutral "Checking…" state for the login numpad. A valid PIN that isn't
  // in the tablet's cached roster used to flash the red reject INSTANTLY and only
  // clear it once the background server rescue logged in. Now the local miss shows
  // this neutral state during the rescue window, and the red error is only painted
  // if the rescue genuinely rejects (or aborts/times out).
  const [checking,setChecking] = useState(false);
  // R12 — "Refresh staff list" affordance is refreshing the pinsForLogin roster.
  const [rosterRefreshing,setRosterRefreshing] = useState(false);
  const [flash,setFlash] = useState(false);
  const [now,setNow] = useState(new Date());
  // O11 — bumped on every login-numpad 4-digit attempt. A background server
  // pin-login rescue only applies if this still matches the attempt that fired
  // it, so a stale rescue can't log the tablet in after the user has moved on
  // (retyped, or already signed in with another PIN).
  const loginAttemptRef = useRef(0);
  // Site access: the session token /api/access handed back at sign-in, used by the
  // heartbeat. MEMORY ONLY — never written to any storage — and cleared by logout().
  // null = no session (the login check was unreachable): the heartbeat still runs,
  // sending session null, so lockdown / a blocked device / the shop-wifi rule still
  // reach a device that signed in while the check couldn't be made.
  const accessSessionRef = useRef(null);
  // Staff PIN roster — fetched live from the Admin Staff Directory.
  // Cached in localStorage on first success so offline tablets keep the full roster mid-shift.
  // Falls back to STAFF_PINS_FALLBACK (JJ + Adam only) as last resort.
  const [staffPins,setStaffPins] = useState(()=>{
    try{const c=localStorage.getItem("staffPinsCache");if(c){const p=JSON.parse(c);if(p&&Object.keys(p).length>0)return p}}catch(e){}
    return STAFF_PINS_FALLBACK;
  });
  const [staffByPin,setStaffByPin] = useState(()=>{
    try{const c=localStorage.getItem("staffByPinCache");if(c){const p=JSON.parse(c);if(p&&typeof p==="object")return p}}catch(e){}
    return STAFF_BY_PIN_FALLBACK;
  });
  // `permissionsMatrix` used to live here — the "which roles may use which
  // function" map from ?action=getrolepermissions. Removed 11 Aug 2026: it was
  // fetched, reshaped, stored in state AND cached to the tablet on every load,
  // and then never read by anything. It decided nothing; the real permission
  // check has always been server-side. Carrying it meant the read-flip would have
  // had to reproduce a map nobody consumes.
  // NOTE the fetch itself stays — `functionMetadata` comes from the same call and
  // IS used (tile labels/icons at render time). So the flip only has to serve the
  // metadata half.
  // Function metadata — admin-managed label/desc/icon overrides per function.
  // Applied at render time to the hardcoded TOOLS array so JJ can rename tiles
  // from the admin dashboard without touching this file. Empty strings mean
  // "inherit frontend default". Cached in localStorage so the first render on a
  // fresh tablet isn't stuck with raw defaults until the live fetch resolves.
  const [functionMetadata,setFunctionMetadata] = useState(()=>{
    try{const c=localStorage.getItem("functionMetadataCache");if(c){const p=JSON.parse(c);if(p&&typeof p==="object")return p}}catch(e){}
    return null;
  });
  // Tile-level overrides (keyed by tool.id) — admin dashboard Tile Editor
  // writes here. Reaches Coming Soon tiles that functionMetadata can't (no fn).
  const [tileOverrides,setTileOverrides] = useState(()=>{
    try{const c=localStorage.getItem("tileOverridesCache");if(c){const p=JSON.parse(c);if(p&&typeof p==="object")return p}}catch(e){}
    return null;
  });
  // Holds the roster refresher (fetchPinsForLogin) so a local-miss action PIN can
  // trigger a background roster reload — see submitPin's instant-reject path (B3).
  const pinsRefreshRef = useRef(null);
  useEffect(()=>{
    // 2026-05-23: pinsForLogin polled every 5 min so admin edits to Staff
    // Directory (new staff, initials overrides, role changes) propagate to
    // open tablets without a hard reload. Single-shot fetch on mount missed
    // mid-shift admin edits — Michaella's "MT" initials override sat in the
    // backend for hours while tablets still showed auto-derived "MI".
    const fetchPinsForLogin = () => fetch(BACKEND+"?action=pinsForLogin")
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(d&&d.pins&&Object.keys(d.pins).length>0){
          setStaffPins(d.pins);
          try{localStorage.setItem("staffPinsCache",JSON.stringify(d.pins))}catch(e){}
        }
        if(d&&d.staff&&typeof d.staff==="object"&&Object.keys(d.staff).length>0){
          setStaffByPin(d.staff);
          try{localStorage.setItem("staffByPinCache",JSON.stringify(d.staff))}catch(e){}
        }
      })
      .catch(()=>{/* keep current state — cache (from useState init) or fallback */});
    // Expose the refresher so a wrong/unknown action PIN can self-heal a stale roster
    // (a staffer added in the last few minutes) on the next tap — see submitPin (B3).
    pinsRefreshRef.current = fetchPinsForLogin;
    // Clear the dead permissions map off tablets that already cached it. Harmless
    // either way, but leaving stale data on a device is how a future reader picks
    // it up and believes it.
    try{localStorage.removeItem("permissionsMatrixCache")}catch(e){}
    fetchPinsForLogin();
    const pinsPollId = setInterval(fetchPinsForLogin, 5*60*1000);
    fetch(BACKEND+"?action=getrolepermissions")
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        // d.functionRoles is deliberately ignored — see the note above the
        // functionMetadata state. It was reshaped and cached and never read.
        if(d&&d.functionMetadata&&typeof d.functionMetadata==="object"){
          setFunctionMetadata(d.functionMetadata);
          try{localStorage.setItem("functionMetadataCache",JSON.stringify(d.functionMetadata))}catch(e){}
        }
      })
      .catch(()=>{/* keep cache */});
    // Tile overrides now come from Supabase (was ?action=tileOverrides on Apps Script).
    // Same public read, same resulting map — but the '*'-plus-this-site MERGE that the
    // old backend did server-side now happens client-side in fetchTileOverrides(). It is
    // a whole-row replace, not a per-field merge; see lib/tileOverrides.js (semantics
    // pinned by lib/tileOverrides.test.mjs; per-site parity with the live feed is checked
    // by supabase/_tiles_parity_check.mjs — run it, don't trust this comment).
    fetchTileOverrides(SITE.key)
      .then(m=>{
        if(m && typeof m==="object"){
          setTileOverrides(m);
          try{localStorage.setItem("tileOverridesCache",JSON.stringify(m))}catch(e){}
        }
      })
      .catch(()=>{/* keep cache */});
    return () => { clearInterval(pinsPollId); };
  },[]);
  // Stock catalog — fetched live from admin-managed Stock Catalog on mount.
  // Cached in localStorage so a fresh offline load still has the list.
  const [stockCatalog,setStockCatalog] = useState(()=>{
    try{const c=localStorage.getItem("stockCatalogCache");if(c){const a=JSON.parse(c);if(Array.isArray(a))return a}}catch(e){}
    return [];
  });
  // Which source served the last catalog read ('supabase' green / 'sheet' amber).
  // Shown only on a flipped tablet (mirrors the kitchen badge); null everywhere else.
  const [catalogSource,setCatalogSource] = useState(null);
  // Sites HELD off the Supabase catalogue read even when the gate/override is on.
  // Every FOH tablet has fetched the catalogue with site=tc hard-coded, so Meadowhall
  // currently shows TRAFFORD's list. Reading Supabase with the correct site=meadowhall
  // CORRECTS that (staging 12 Sep 2026: Meadowhall gains "Twisties", loses "Honey Soy
  // Sauce (Bottle)") — a real change to what staff see, so it must be a deliberate
  // daylight decision by Adam, never a silent side-effect of a read-flip. Remove a key
  // from this set to allow that site's ON path.
  const CATALOG_READ_SITE_HELD = new Set(["meadowhall"]);
  useEffect(()=>{
    // CATALOGUE READ FLIP (BJ) — gated OFF by default (stockCatalog.CATALOG_READ_HOSTS
    // is empty). On a flipped, non-held tablet the item list + photos come from Supabase
    // for THIS tablet's own site, falling back automatically to the same sheet feed on
    // any problem. Everywhere else this is byte-for-byte the old sheet fetch.
    const catalogReadOn = stockCatalogSrc.catalogReadEnabled() && !CATALOG_READ_SITE_HELD.has(SITE.key);
    if(catalogReadOn){
      // ON path — read this tablet's own site; the sheet-fallback URL carries the same
      // real site key so a fallback still shows the correct shop's list, never site=tc.
      stockCatalogSrc.fetchStockCatalog({ sheetUrl: BACKEND+"?action=stockCatalog&site="+encodeURIComponent(SITE.key), site: SITE.key })
        .then(d=>{
          // An empty or malformed answer never replaces a list we already have
          // (stockCatalog.shouldReplaceCatalog). `stockCatalog` here is the
          // mount-time value — i.e. exactly the localStorage copy — because this
          // effect runs once. On a refusal we keep that list and still set the
          // source, so the amber "Fallback" badge tells staff it isn't live.
          if(!stockCatalogSrc.shouldReplaceCatalog(stockCatalog,d)){
            // Warn on every refusal, not just the well-formed-empty one — an
            // unrecognised answer shape is a real signal too and was silently
            // dropped before (review of #372, finding 2).
            try{console.warn("[catalog] kept the cached list — the catalogue answer was "+(d&&Array.isArray(d.items)?"empty":"missing or malformed"),{source:(d&&d._source)||null,kept:stockCatalog.length})}catch(e){}
            if(d&&Array.isArray(d.items)){
              setCatalogSource(d._source||null);
            }
            return;
          }
          setStockCatalog(d.items);
          setCatalogSource(d._source||null);
          try{localStorage.setItem("stockCatalogCache",JSON.stringify(d.items))}catch(e){}
        })
        .catch(()=>{/* keep current state */});
      return;
    }
    // OFF path — the same bare Apps Script fetch as always (historical site=tc),
    // now with the same empty-answer guard as the ON path above.
    fetch(BACKEND+"?action=stockCatalog&site=tc")
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(!stockCatalogSrc.shouldReplaceCatalog(stockCatalog,d)){
          try{console.warn("[catalog] kept the cached list — the Google answer was "+(d&&Array.isArray(d.items)?"empty":"missing or malformed"),{kept:stockCatalog.length})}catch(e){}
          return;
        }
        setStockCatalog(d.items);
        try{localStorage.setItem("stockCatalogCache",JSON.stringify(d.items))}catch(e){}
      })
      .catch(()=>{/* keep current state */});
  },[]);
  // Allergen catalog + categories — admin-managed, fetched on mount with localStorage cache.
  const [allergenCatalog,setAllergenCatalog] = useState(()=>{
    try{const c=localStorage.getItem("allergenCatalogCache_"+SITE.key);if(c){const a=JSON.parse(c);if(Array.isArray(a))return a}}catch(e){}
    return [];
  });
  const [allergenCategories,setAllergenCategories] = useState(()=>{
    try{const c=localStorage.getItem("allergenCategoriesCache");if(c){const a=JSON.parse(c);if(Array.isArray(a))return a}}catch(e){}
    return [];
  });
  // Reads Supabase now (src/lib/allergens.js), not the Apps Script feeds. Same shape,
  // same localStorage cache. The readers THROW on an empty/failed read so the .catch
  // preserves the last good cache instead of blanking it — see the guard in allergens.js.
  useEffect(()=>{
    fetchAllergenCatalog(SITE.key)
      .then(items=>{
        setAllergenCatalog(items);
        try{localStorage.setItem("allergenCatalogCache_"+SITE.key,JSON.stringify(items))}catch(e){}
      })
      .catch(()=>{/* keep current state / cached */});
    fetchAllergenCategories()
      .then(cats=>{
        setAllergenCategories(cats);
        try{localStorage.setItem("allergenCategoriesCache",JSON.stringify(cats))}catch(e){}
      })
      .catch(()=>{/* keep current state / cached */});
  },[]);
  // FOH Checklist config — fetched on mount. Tile is hidden from staff
  // until admin flips visibleToStaff in the dashboard editor (off by
  // default until Bizimply + Google Reviews are wired).
  // The config cache is now SITE-SCOPED (was a single shared "checklistConfigCache"
  // key, so two shops on one device — or a device that changed site — could paint the
  // wrong shop's checklist for a beat). Keyed by site.key, exactly like the day cache
  // (checklistEventsCache::<site>) already is.
  const [checklistConfig,setChecklistConfig] = useState(()=>{
    try{const c=localStorage.getItem("checklistConfigCache::"+SITE.key);if(c){const p=JSON.parse(c);if(p&&typeof p==="object")return p}}catch(e){}
    return null;
  });
  // Which source served the config, and whether it passed the form-seed parity check
  // ({source:"supabase"|"sheet", parity:"clean"|"drift"|null}). Only surfaced on a
  // flipped shop; null/inert everywhere else.
  const [checklistCfgMeta,setChecklistCfgMeta] = useState(null);
  useEffect(()=>{
    // CHECKLIST READ FLIP (gated OFF by default — checklist.CHECKLIST_READ_HOSTS
    // is empty): on a flipped shop the sections, tasks and sweeps come from
    // Supabase ONLY when they match the live Sheet config (the form-seed drift
    // guard), falling straight back to this same sheet feed on any problem — drift,
    // an empty checklist, or a sheet it cannot reach to verify.
    // Everywhere else this is byte-for-byte the old sheet fetch.
    checklistSrc.fetchChecklistConfig({ sheetUrl: BACKEND+"?action=checklistconfig&site="+SITE.key, site: SITE.key })
      .then(r=>{
        setChecklistCfgMeta(r?{source:r._source||null,parity:r._parity||null,drift:r._drift||null}:null);
        const d = r && r.config;
        if(d&&!d.error){
          setChecklistConfig(d);
          try{localStorage.setItem("checklistConfigCache::"+SITE.key,JSON.stringify(d))}catch(e){}
        }
      })
      .catch(()=>{/* keep cache */});
  },[]);
  // Pre-warm the FOH 67 Checklist day cache the moment we know a staffer is
  // logged in (includes session-restore). Writes the same two localStorage
  // keys the Checklist tile and EOD % block already hydrate from, so
  // subsequent navigation into either paints instantly instead of waiting on
  // its own cold fetch. Fire-and-forget; the components still do their own
  // revalidating fetch, this just removes the cold-start blank.
  //
  // 20 Sep 2026 (login-time reads): this is the slowest thing the tablet fires
  // at login — the backend walks the whole tick log (12 s on its own on Trafford,
  // 24–83 s while the reviews banner's reads were running) — and it fired on
  // EVERY login, including the next staffer picking up a shared tablet a minute
  // later. Three guards now:
  //   1. only when the tile can actually be opened on this tablet (the config has
  //      sweeps and sections, and it is visible to staff or the viewer is an
  //      Admin) — a hidden tile never needs its cache warmed;
  //   2. not when the tablet's cached copy of today is younger than
  //      CHECKLIST_PREWARM_MAX_AGE_MS — the tile / EOD reads stamp that copy, so a
  //      login shortly after a sweep costs no read at all;
  //   3. shared with the tile and the EOD block (fetchChecklistDayShared): a tile
  //      opened while this is still running joins it instead of starting a
  //      second copy of the same slow read.
  // `checklistConfig` is in the deps on purpose: on a fresh tablet the config
  // lands a few seconds after login, and the pre-warm should fire then (once).
  useEffect(()=>{
    if(!staff)return;
    // Guard 1, computed the way the home grid computes the tile: the config rule
    // (visibleToStaff or an Admin viewer, AND something to tick) becomes the tile's
    // `on`, then the admin Tile Editor override and the routing rule are applied —
    // so a tile switched on by an override row still gets its pre-warm, and one
    // switched off by an override row never fires a read (review of #404).
    const __clTool = TOOLS.find(t=>t.id==="checklist");
    const __clVisible = checklistSrc.checklistTileShowable(checklistConfig,{isAdmin:rosterFree.isAdminRole(staffRole)});
    const __clEff = __clTool ? applyTileOverride(applyFunctionMeta({...__clTool, on: __clVisible}, functionMetadata), tileOverrides) : null;
    if(!__clEff || !toolAccessFor(__clEff).enabled)return;
    const today=todayKey();  // London day (was UTC) — near midnight BST the cache label must match the London-day content, or a flipped shop flashes yesterday's checklist as today's
    let cachedRaw=null; try{cachedRaw=localStorage.getItem("checklistEventsCache::"+SITE.key)}catch(e){}
    if(readFreshness.storedReadIsFresh(cachedRaw,{maxAgeMs:CHECKLIST_PREWARM_MAX_AGE_MS,today}))return;
    let cancelled=false;
    checklistSrc.fetchChecklistDayShared({ sheetUrl: BACKEND+"?action=checklistday&site="+SITE.key, site: SITE.key })
      .then(r=>{
        const d = r && r.day;
        if(cancelled||!d)return;
        if(Array.isArray(d.events)){
          try{localStorage.setItem("checklistEventsCache::"+SITE.key,JSON.stringify({date:today,events:d.events,ts:Date.now()}))}catch(e){}
        }
        const rak=Array.isArray(d.randomActs)?d.randomActs.length:0;
        const reviews=Array.isArray(d.manualReviews)?d.manualReviews.length:0;
        try{localStorage.setItem("checklistDailyCountsCache::"+SITE.key,JSON.stringify({date:today,rak,reviews}))}catch(e){}
      })
      .catch(()=>{});
    return()=>{cancelled=true};
  },[staff,checklistConfig,staffRole,functionMetadata,tileOverrides]);
  const [sellOutTimes,setSellOutTimes] = useState(appData.sellOutTimes);
  const [sellOutCycles,setSellOutCycles] = useState(appData.sellOutCycles);
  const [stockRequests,setStockRequests] = useState(appData.stockRequests);
  const [reportDirty,setReportDirty] = useState(false);
  const [backConfirm,setBackConfirm] = useState(false);
  const [stockPending,setStockPending] = useState(false); // true when stock requests have unsubmitted selections
  const [wasteDirty,setWasteDirty] = useState(false); // true when Ingredient Wastage has unsaved grams edits (Meadowhall)
  const [pendingNavDest,setPendingNavDest] = useState("home"); // where to go if user confirms "leave anyway"
  const [sideOpen,setSideOpen] = useState(()=>{try{const v=localStorage.getItem("rolled.sideOpen");return v===null?false:v==="1"}catch(e){return false}});
  // Club Card Scanner session — populated when the unified login PIN is ALSO accepted by the Railway backend
  const [ccToken,setCcToken] = useState(()=>{if(!SESSION_RESTORE_OK)return null;try{const r=sessionStorage.getItem("rolled.cc.session.v1");if(!r)return null;const p=JSON.parse(r);return (p&&p.token)||null}catch(e){return null}});
  const [ccSession,setCcSession] = useState(()=>{if(!SESSION_RESTORE_OK)return null;try{const r=sessionStorage.getItem("rolled.cc.session.v1");if(!r)return null;const p=JSON.parse(r);return (p&&p.session)||null}catch(e){return null}});
  // Scanner-only offline session (JJ, 13 Oct 2026: "let them back into the scanner
  // offline"). Set when someone signs in at the PIN pad with no connection at all: the app
  // shows only the Club Card scanner until the Wi-Fi's back, then asks for a PIN again so
  // the queue can send. { since, name|null, auth } — never a PIN. Cleared by logout().
  const [offlineSession,setOfflineSession] = useState(null);
  // Signed in, but the Club Card server didn't answer at sign-in (see ccSetTokenless): the full
  // app as usual, with the scanner in its offline mode until a sign-in gets a token.
  const [ccTokenless,setCcTokenlessState] = useState(false);
  const setCcTokenless = (on)=>{ ccSetTokenless(on); setCcTokenlessState(!!on); };

  // FOH Clock-In flow state. Replaces Bizimply Timestation as the kiosk
  // source-of-truth for who's on shift.
  // staffOnShift hydrates from localStorage so the banner paints something
  // sensible the moment home renders, then refreshes from getStaffOnShift
  // every 30s. clockFlow tracks the current modal flow:
  //   mode: null | "in" | "out"
  //   step: null | "pin" | "camera" | "picker" | "success"
  //   pin/photo/target: transient flow data
  //   busy/err: in-flight + error surface
  //   successName/successTime: rendered on the success scene
  const isTabletKiosk = isTabletKioskHost();
  const [staffOnShift,setStaffOnShift] = useState(()=>{
    try {
      const c = localStorage.getItem("staffOnShiftCache::"+CLOCK_IN_SITE_KEY);
      if (c) {
        const p = JSON.parse(c);
        if (p && Array.isArray(p.shifts)) return p.shifts;
      }
    } catch(e) {}
    return [];
  });
  const [clockFlow,setClockFlow] = useState({
    mode:null, step:null, pin:"", photo:null, target:null,
    busy:false, err:"", successName:"", successTime:""
  });
  // Holds the success-auto-close setTimeout id so a manual ✕ tap (or a
  // re-opened flow before the timer fires) can clear it. Without this, the
  // 2.5s timer would keep firing and stomp a flow the staffer just opened.
  const clockCloseTimerRef = useRef(null);
  // Per-action prompt-to-clock-in (2026-04-29). When a staffer enters their
  // PIN for any per-action gate but isn't currently on shift, we surface a
  // "Clock in first?" prompt instead of running the action. State carries
  // the staffer's name so the prompt can address them by name; null = closed.
  const [notClockedInPrompt, setNotClockedInPrompt] = useState(null);

  // Preview hosts only: save the sign-in so a refresh doesn't log out. A shop host
  // saves nothing — every page load is a fresh PIN (session lock, JJ 28 Sep 2026). Never
  // the scanner-only offline session: a refresh there must land back on the PIN pad, not
  // restore the full app with no token.
  useEffect(()=>{if(!SESSION_RESTORE_OK||offlineSession)return;try{sessionStorage.setItem("rolled.view",view);sessionStorage.setItem("rolled.staff",staff);sessionStorage.setItem("rolled.staffRole",staffRole||"")}catch(e){}},[view,staff,staffRole,offlineSession]);
  // If we restored a logged-in state but staff is empty (corrupt), fall back to login
  useEffect(()=>{if(view!=="login"&&!staff){setView("login")}},[]);

  // Responsive orientation
  const isPortrait = useOrientation();

  // PIN modal — declared before effects that reference it
  const [pinModal,setPinModal] = useState(false);
  const [pmVal,setPmVal] = useState("");
  const [pmErr,setPmErr] = useState("");
  // R12 — neutral "Checking…" state for the per-action PIN modal, mirroring the
  // login numpad: a PIN missing from the cached roster shows this during the
  // background server rescue instead of an instant red "Invalid PIN".
  const [pmChecking,setPmChecking] = useState(false);
  const [pmLoad,setPmLoad] = useState(false);
  const pmCb = useRef(null);
  const pmCancelCb = useRef(null);
  // Offline PIN (JJ, 13 Oct 2026). pmOfflineOk: this prompt's job can run with no
  // connection (only the Club Card scanner's offline jobs ask for it — stamps queue, the
  // offline reward picker). With no connection the PIN always gets through and is checked
  // later by the server (lib/offlinePin.js).
  const pmOfflineOk = useRef(false);

  // Lock body scroll when any modal is open
  useEffect(()=>{
    if(pinModal||backConfirm){document.body.classList.add("modal-open")}
    else{document.body.classList.remove("modal-open")}
    return()=>document.body.classList.remove("modal-open");
  },[pinModal,backConfirm]);

  // Mobile hardware back button: navigate within the app (tool → home),
  // don't close the whole tab. Wires history.pushState/popstate so the
  // Android/iOS back gesture goes home instead of exiting. Pending changes
  // still route through BackConfirmModal so unsubmitted work isn't lost.
  const navStateRef = useRef({view, reportDirty, stockPending, backConfirm});
  navStateRef.current = {view, reportDirty, stockPending, backConfirm};
  // The Club Card scanner keeps its OWN internal screen stack (home/clubscan/lookup/…) and
  // pushes nothing to history, so hardware-back inside it would otherwise skip straight to the
  // site-app home. It publishes a back handler here; onPop consults it for view==="scanner" and
  // only leaves the scanner (setView("home")) once the scanner reports it's at its own home.
  const scannerBackRef = useRef(null);
  useEffect(()=>{
    try{
      const v0 = navStateRef.current.view;
      if(v0==="login"||v0==="home"){
        window.history.replaceState({rolledNav:v0},"");
      } else {
        window.history.replaceState({rolledNav:"home"},"");
        window.history.pushState({rolledNav:v0},"");
      }
    }catch(e){}
  },[]);
  useEffect(()=>{
    try{
      const cur = window.history.state;
      if(view==="login"||view==="home"){
        if(!cur||cur.rolledNav!==view){
          window.history.replaceState({rolledNav:view},"");
        }
      } else if(!cur||cur.rolledNav!==view){
        const prevWasTool = cur && cur.rolledNav && cur.rolledNav!=="home" && cur.rolledNav!=="login";
        if(prevWasTool){
          window.history.replaceState({rolledNav:view},"");
        } else {
          window.history.pushState({rolledNav:view},"");
        }
      }
    }catch(e){}
  },[view]);
  useEffect(()=>{
    const onPop = ()=>{
      try{
        const {view:v,reportDirty:rd,stockPending:sp,backConfirm:bc} = navStateRef.current;
        if(bc){
          setBackConfirm(false);
          if(v!=="login"&&v!=="home"){
            window.history.pushState({rolledNav:v},"");
          }
          return;
        }
        if(v==="login"||v==="home") return;
        const pending = (v==="report"&&rd)||(v==="stock"&&sp);
        if(pending){
          window.history.pushState({rolledNav:v},"");
          setPendingNavDest("home");
          setBackConfirm(true);
        } else if(v==="scanner" && scannerBackRef.current){
          // Let the scanner unwind its own screens first. If it consumed the back (was on an
          // inner screen), stay in the scanner and restore the history entry so the NEXT back
          // pops again. Only when it's already at its own home do we leave for the site-app home.
          const consumed = scannerBackRef.current();
          if(consumed){
            window.history.pushState({rolledNav:"scanner"},"");
          } else {
            setView("home");
          }
        } else {
          setView("home");
        }
      }catch(err){}
    };
    window.addEventListener("popstate",onPop);
    return ()=>window.removeEventListener("popstate",onPop);
  },[]);

  // Sync state back to appData + localStorage so it survives logout, refresh, tab close mid-day
  useEffect(()=>{appData.sellOutTimes=sellOutTimes;persistAppData()},[sellOutTimes]);
  useEffect(()=>{appData.sellOutCycles=sellOutCycles;persistAppData()},[sellOutCycles]);
  useEffect(()=>{appData.stockRequests=stockRequests;persistAppData()},[stockRequests]);

  // Day-rollover watchdog. Kiosk tablets stay running across midnight, so
  // React state would otherwise hold yesterday's sell-out marks, EOD waste
  // counters, and the stock-request list into a new day — staff arriving
  // for opening had to manually undo phantom sell-outs (Sophie @ Trafford
  // hit this 2026-05-15; cleanup flagged 16 historical orphan undo rows).
  // Polls every 60s and listens for `visibilitychange` so wake-from-sleep
  // catches a rollover that happened while the tablet was asleep. On
  // rollover: wipe all day-scoped state, clear localStorage, bump dayKey
  // so child components with internal state (EOD waste counters etc)
  // remount cleanly via key={dayKey}.
  const [dayKey,setDayKey] = useState(()=>todayKey());
  useEffect(()=>{
    const check = ()=>{
      const cur = todayKey();
      if (cur === dayKey) return;
      setSellOutTimes({});
      setSellOutCycles({});
      setStockRequests([]);
      setStaffOnShift([]);
      appData.sellOutTimes = {};
      appData.sellOutCycles = {};
      appData.stockRequests = [];
      appData.reportSubmitted = false;
      const w = {}; SKU_LIST.forEach(s => w[s] = 0);
      appData.waste = w;
      appData.tgtgSold = null;
      appData.tgtgNC = null;
      // Wipe day-scoped localStorage caches outside the main LS_KEY blob —
      // otherwise yesterday's on-shift roster (gates clock-in actions) and
      // checklist tick counts (shown on home / EOD) carry over.
      try { localStorage.removeItem(LS_KEY); } catch(e) {}
      try { localStorage.removeItem("staffOnShiftCache::"+CLOCK_IN_SITE_KEY); } catch(e) {}
      try { localStorage.removeItem("checklistDailyCountsCache::"+SITE.key); } catch(e) {}
      try { localStorage.removeItem("checklistEventsCache::"+SITE.key); } catch(e) {}
      // The reviews banner's cached copy carries a "today" count; since it is
      // no longer re-fetched on every re-mount (REVIEWS_BANNER_MIN_REFRESH_MS),
      // drop it at rollover so the new day never paints yesterday's count.
      try { if (SITE.reviews && SITE.reviews.banner) localStorage.removeItem("reviewsBannerCache::"+SITE.reviews.banner); } catch(e) {}
      setDayKey(cur);
    };
    check();
    const id = setInterval(check, 60000);
    const onVis = ()=>{ if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVis);
    return ()=>{ clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  },[dayKey]);
  // Persist sidebar preference across sessions
  useEffect(()=>{try{localStorage.setItem("rolled.sideOpen",sideOpen?"1":"0")}catch(e){}},[sideOpen]);

  useEffect(()=>{const t=setInterval(()=>setNow(new Date()),30000);return()=>clearInterval(t)},[]);

  const digit = useCallback(d=>{
    if(pin.length>=4)return;   // a PIN is already being checked — ignore extra taps (⌫ cancels the check)
    setErr("");setChecking(false);setLockNote("");
    if(pin.length<4){const next=pin+d;setPin(next);
      if(next.length===4){const __attempt=++loginAttemptRef.current;setTimeout(async ()=>{
        // A1 — who is this? Two ways of answering, and only one of them needs the
        // whole shop's PIN list to have been downloaded to the tablet.
        //   gate OFF (everywhere today): match against the downloaded roster, byte
        //     for byte what it has always done.
        //   gate ON: ask the server about this ONE PIN. The roster is never
        //     consulted for the decision. Everything after the branch is unchanged
        //     — the Command Centre deactivation check, the CC token for the
        //     scanner, the login event, presence — because those are JJ's seam and
        //     this is not the change that touches them.
        // Gate ON at both shops since 1 Oct 2026 (JJ): the Command Centre's list is
        // the ONE list. A definite "no" from the server (wrong / ambiguous PIN) is
        // final — the Club Card rescue below must not let the Sheet's copy of the
        // list overrule it. When the server gave no verdict on this PIN (offline,
        // timed out, server error, this tablet locked out) the rescue may still run.
        // No internet at all still means no sign-in except the break-glass owners.
        let __srvName = "", __srvRole = "", __srvRefused = false, __srvLocked = false, __srvNoVerdict = false;
        if (pinAuth.serverPinVerifyEnabled()) {
          let __srv = null;
          setChecking(true);   // the server takes ~0.5–1 s — show "Checking…", not a frozen pad
          try { __srv = await pinAuth.loginWithPin(next, { siteKey: SITE.key, timeoutMs: pinAuth.LOGIN_TIMEOUT_MS }); } catch (e) { __srv = null; }
          if (loginAttemptRef.current !== __attempt) return; // user retyped — drop this stale answer
          if (__srv && __srv.ok) { __srvName = __srv.staffName || ""; __srvRole = __srv.role || ""; }
          else {
            __srvRefused = pinAuth.serverRefusedPin(__srv);
            __srvLocked = !!(__srv && __srv.reason === "locked");
            __srvNoVerdict = !__srvRefused;
            // A definite "no" to this PIN: take it out of this tablet's offline book too.
            if (__srvRefused) offlinePin.forget(next);
          }
        }
        const __who  = pinAuth.serverPinVerifyEnabled() ? __srvName : staffPins[next];
        const __role = pinAuth.serverPinVerifyEnabled() ? __srvRole : ((staffByPin[next]&&staffByPin[next].role)||"");
        // Site access (JJ, 29 Sep 2026): the LAST check before a sign-in completes, run by
        // both accept paths below only once the PIN has already been accepted (roster/server
        // + the Command Centre deactivation check, or the pin-login rescue). Only called when
        // SITE_ACCESS_ON. Resolves true = carry on and sign in exactly as before (allowed, OR
        // the check was unreachable/slow/odd — fail open), false = stop here: either a newer
        // attempt owns the UI (stale — touch nothing), or the server said "block", in which
        // case nobody is signed in and its message shows in the PIN pad's error slot. The
        // session token from an allowed reply is kept in a ref (memory only) for the heartbeat.
        const __accessAllows = async ()=>{
          let __acc = null;
          try { __acc = await siteAccess.accessCheck({event:"login", site:SITE.key, pin:next}); } catch (e) { __acc = null; }
          if(loginAttemptRef.current!==__attempt) return false;   // user retyped / moved on — drop this stale check
          try {
            if(siteAccess.loginVerdict(__acc)==="block"){
              accessSessionRef.current=null;
              setChecking(false);setFlash(false);setStaff("");setStaffRole("");
              setErr(siteAccess.staffMessage(__acc));setShake(true);setTimeout(()=>{setShake(false);setPin("")},500);
              return false;
            }
            accessSessionRef.current=siteAccess.sessionFrom(__acc);
          } catch (e) { accessSessionRef.current=null; }   // anything unexpected: fail open, sign in as before
          return true;
        };
        if(__who){  /* gate off: validated locally against fetched directory · gate on: validated by the server */
          // 2026-05-04: capture validated PIN to sessionStorage on
          // localhost / LAN hosts so per-action requestPin auto-fires
          // without making JJ retype his PIN for every test action.
          // Production hosts skip this — modal behaves as before.
          try {
            const h = window.location.hostname || "";
            const isPreview = h === "localhost" || h === "127.0.0.1" || h.startsWith("192.168.") || h.startsWith("10.") || h.startsWith("172.");
            if (isPreview) sessionStorage.setItem("rolled.preview.pin", next);
          } catch(e) {}
          setChecking(false);   // clear the gate-on "Checking…" so a later error (deactivated / site access) can show
          setStaff(__who);
          setStaffRole(__role);
          setFlash(true);
          // F1/SC-STAFF-2: the PIN is in the cached Bizimply roster, but the Command
          // Centre Users list is the source of truth — a staffer deactivated there must
          // NOT log in. Verify with the server before completing the sign-in. Block ONLY
          // on an explicit 403 'deactivated'; fail OPEN (local grant) on any network
          // error / timeout / other status so a blip at the till never locks staff out.
          // The pin-login response also carries the CC token for the Scanner tile.
          (async()=>{
            const ctrl=new AbortController();const to=setTimeout(()=>{try{ctrl.abort()}catch(e){}},1500);
            let d=null, deactivated=false, __ccStatus=0, __ccNoAnswer=false;
            try{
              const r=await fetch(CC_API+"/staff/pin-login",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({pin:next,site_id:SITE.id}),signal:ctrl.signal});
              __ccStatus=r.status;
              if(r.status===403){deactivated=true;}
              else if(r.ok){d=await r.json().catch(()=>null);}
            }catch(e){__ccNoAnswer=true;/* network/timeout → fall open to the local grant */}
            clearTimeout(to);
            if(loginAttemptRef.current!==__attempt)return;   // user retyped / moved on — drop this stale check
            if(deactivated){
              offlinePin.forget(next);   // switched off in the Command Centre: out of the offline book too
              setFlash(false);setStaff("");setStaffRole("");
              setErr("This PIN no longer has access");setShake(true);setTimeout(()=>{setShake(false);setPin("")},500);
              return;
            }
            // Site access: this device / network / time allowed? (fails open — see __accessAllows)
            if(SITE_ACCESS_ON && !(await __accessAllows())) return;
            // Signed in on the Club Card server's own yes (gate on): this PIN now goes in the
            // tablet's offline book (stretched, never the PIN itself) — JJ, 13 Oct 2026. Only
            // on that yes: never on a break-glass match or the login check alone (review).
            if(pinAuth.serverPinVerifyEnabled() && d && d.staff && d.staff.name) offlinePin.remember(next, {name:d.staff.name, role:(d.staff.role||"")});
            // Active (server token) OR server unreachable (offline fail-open): complete login.
            try{const __di=getDeviceInfo();fetch(BACKEND,{method:"POST",mode:"no-cors",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"loginEvent",site:SITE.key,pin:next,staffName:(d&&d.staff&&d.staff.name)||__who,device:__di.device,ua:__di.ua})})}catch(e){}
            presence.recordTap({ siteKey: SITE.key, pin: next, kind: "login", source: "login" });
            if(d&&d.access_token&&d.staff){const _ccs={staffId:d.staff.id,staffName:d.staff.name,staffRole:d.staff.role,siteId:SITE.id,siteName:SITE.name};ccSetNetDown(false);setCcToken(d.access_token);setCcSession(_ccs);ccAuth.refresh=d.refresh_token||null;if(SESSION_RESTORE_OK){try{sessionStorage.setItem("rolled.cc.session.v1",JSON.stringify({token:d.access_token,refresh:d.refresh_token||null,session:_ccs}))}catch(e){}}}
            // Signed in, but the Club Card server didn't answer (it's down, or no Wi-Fi on an
            // owner's break-glass sign-in): the scanner runs its offline mode instead of asking for
            // a sign-in it can't get (review, 13 Oct 2026). Never on a real answer (a 4xx).
            else if(pinAuth.serverPinVerifyEnabled() && (__ccNoAnswer || __ccStatus===502 || __ccStatus===503 || __ccStatus===504) && !(await ccServerReachable(3000))){
              if(loginAttemptRef.current!==__attempt)return;
              setCcToken(null);
              setCcSession({staffId:null, staffName:__who, staffRole:__role, siteId:SITE.id, siteName:SITE.name});
              setCcTokenless(true);
            }
            setTimeout(()=>{setView("home");setPin("");setFlash(false)},700);
          })();
        }
        else if(SERVER_PIN_LOGIN_FALLTHROUGH && !__srvRefused){   // gate on: never after a definite server "no" (one list)
          // O11 — local miss + flag ON (SC-STAFF-1): the PIN may belong to a
          // staffer added in Command Centre — present in the backend `staff`
          // table but not in the cached Bizimply roster. This fires the server
          // login as a BACKGROUND rescue. If it recognises a genuine CC staffer we
          // establish the session and go home; otherwise it rejects. Aborted after
          // 1.2s so a hung API can't leave the rescue dangling.
          // R12 (off-list flash fix): a VALID PIN not in the cached roster used to
          // flash the red 'PIN not recognised' + shake INSTANTLY here, then the
          // rescue cleared it and logged in — so every such login blinked red
          // first. We now show a NEUTRAL 'Checking…' state during the rescue window
          // and only paint the red error inside the genuine-reject branch (incl.
          // abort/timeout → d=null). Pure UX ordering; no auth decision changes.
          // SC-STAFF-2 (2a): also force a fresh pinsForLogin pull on the miss so a
          // just-added staffer's roster row lands without waiting on the 5-min poll
          // (mirrors submitPin's instant-reject path).
          setChecking(true);
          if(pinsRefreshRef.current)pinsRefreshRef.current();
          (async()=>{
            const ctrl=new AbortController();const to=setTimeout(()=>{try{ctrl.abort()}catch(e){}},1200);
            let d=null, __ccStatus=0;
            try{
              const r=await fetch(CC_API+"/staff/pin-login",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({pin:next,site_id:SITE.id}),signal:ctrl.signal});
              __ccStatus=r.status;
              d=r.ok?await r.json():null;
            }catch(e){d=null;}
            clearTimeout(to);
            if(loginAttemptRef.current!==__attempt)return;          // user retyped / already signed in — newer attempt owns the UI, drop this stale rescue
            if(!d||!d.access_token||!d.staff){                      // genuine reject (or abort/timeout → d=null): now paint the error
              // Neither server gave a verdict on this PIN (no 4xx answer) and the Club Card server
              // doesn't answer anything at all: no connection, so let them into the scanner only
              // (JJ, 13 Oct 2026). A reachable server = no pass.
              if(__srvNoVerdict && !(__ccStatus>=400&&__ccStatus<500) && !__srvLocked && pinAuth.serverPinVerifyEnabled() && !(await ccServerReachable(3000))){
                if(loginAttemptRef.current!==__attempt)return;
                if(await enterOfflineScanner(next, __attempt)) return;
              }
              if(loginAttemptRef.current!==__attempt)return;
              // Gate on and neither server gave a verdict on this PIN (no 4xx answer) → it's the connection.
              setChecking(false);setErr((__srvNoVerdict&&!(__ccStatus>=400&&__ccStatus<500))?"Can't connect — check the internet and try again":"PIN not recognised");setShake(true);setTimeout(()=>{setShake(false);setPin("")},500);
              return;
            }
            // Site access: this device / network / time allowed? "Checking…" stays up while it
            // runs; fails open — see __accessAllows.
            if(SITE_ACCESS_ON && !(await __accessAllows())) return;
            // The Club Card server said yes and the sign-in is going through: keep this PIN in
            // the offline book (gate on only — gate off, the roster already works offline).
            if(pinAuth.serverPinVerifyEnabled()) offlinePin.remember(next, {name:d.staff.name, role:(d.staff.role||"")});
            // Preview-host shortcut parity (harmless on prod hosts).
            try{const h=window.location.hostname||"";const isPreview=h==="localhost"||h==="127.0.0.1"||h.startsWith("192.168.")||h.startsWith("10.")||h.startsWith("172.");if(isPreview)sessionStorage.setItem("rolled.preview.pin",next)}catch(e){}
            setChecking(false);setErr("");setStaff(d.staff.name);
            setStaffRole(d.staff.role||"");
            setFlash(true);
            try{const __di=getDeviceInfo();fetch(BACKEND,{method:"POST",mode:"no-cors",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"loginEvent",site:SITE.key,pin:next,staffName:d.staff.name,device:__di.device,ua:__di.ua})})}catch(e){}
            presence.recordTap({ siteKey: SITE.key, pin: next, kind: "login", source: "login" });
            const _ccs={staffId:d.staff.id,staffName:d.staff.name,staffRole:d.staff.role,siteId:SITE.id,siteName:SITE.name};
            ccSetNetDown(false);setCcToken(d.access_token);setCcSession(_ccs);ccAuth.refresh=d.refresh_token||null;
            if(SESSION_RESTORE_OK){try{sessionStorage.setItem("rolled.cc.session.v1",JSON.stringify({token:d.access_token,refresh:d.refresh_token||null,session:_ccs}))}catch(e){}}
            // SC-STAFF-1: seed the local roster from the server login so this
            // staffer clears per-action PIN gates for the rest of the session
            // without waiting on the 5-min roster poll (mirrors submitPin's inject).
            setStaffPins(prev=>{const nx={...prev,[next]:d.staff.name};try{localStorage.setItem("staffPinsCache",JSON.stringify(nx))}catch(e){}return nx;});
            setStaffByPin(prev=>{const nx={...prev,[next]:{role:(d.staff.role||"")}};try{localStorage.setItem("staffByPinCache",JSON.stringify(nx))}catch(e){}return nx;});
            setTimeout(()=>{setView("home");setPin("");setFlash(false)},1000);
          })();
        }
        else{
          // Flag OFF: no server rescue, so this stays an INSTANT reject. R12 (2a):
          // still force a fresh pinsForLogin pull so a just-added staffer's roster
          // row can land for the next attempt without waiting on the 5-min poll.
          if(pinsRefreshRef.current)pinsRefreshRef.current();
          // No verdict and the Club Card server doesn't answer at all: scanner-only (as above).
          if(__srvNoVerdict && !__srvLocked && pinAuth.serverPinVerifyEnabled() && !(await ccServerReachable(3000))){
            if(loginAttemptRef.current!==__attempt)return;
            if(await enterOfflineScanner(next, __attempt)) return;
          }
          if(loginAttemptRef.current!==__attempt)return;
          setChecking(false);
          setErr(__srvLocked?"Too many wrong PINs — wait 10 minutes":__srvNoVerdict?"Can't connect — check the internet and try again":"PIN not recognised");setShake(true);setTimeout(()=>{setShake(false);setPin("")},500);
        }
      },60)}
    }
  },[pin]);
  // ⌫ also cancels a check still in flight: bump the attempt so its late answer is
  // dropped (it can't sign in, or shake away digits being retyped). Safe: once a PIN
  // is accepted the full-screen greeting covers the pad, so ⌫ can't be tapped then.
  const del = useCallback(()=>{loginAttemptRef.current++;setPin(p=>p.slice(0,-1));setErr("");setChecking(false)},[]);
  // The pad first, then the offline session: this app renders without React's batching, so
  // outside a tap every set re-renders at once — ending the scanner-only session first would
  // flash the full scanner up, with no token, before the pad.
  const logout = ()=>{ccSetScannerOnly(false);accessSessionRef.current=null;setView("login");setOfflineSession(null);setCcTokenless(false);setStaff("");setStaffRole("");setCcToken(null);setCcSession(null);ccAuth.refresh=null;ccAuth.epoch++;try{sessionStorage.removeItem("rolled.view");sessionStorage.removeItem("rolled.staff");sessionStorage.removeItem("rolled.staffRole");sessionStorage.removeItem("rolled.cc.session.v1")}catch(e){}};
  // Back into the scanner with no connection (JJ, 13 Oct 2026: "let them back into the
  // scanner offline"). Only when the sign-in check got no answer AND the Club Card server
  // doesn't answer either. Any PIN gets in; the tablet's book says who it is if it knows
  // the PIN. The unlock is queued (PIN encrypted for the server) and sent when someone signs
  // in online again; the Command Centre lists it under Offline PINs. No scanner token: the
  // scanner runs its offline mode only (stamps queue, rewards use the offline picker).
  const enterOfflineScanner = async (pinTyped, attempt)=>{
    ccSetNetDown(true);
    const hit = await offlinePin.lookup(pinTyped);
    const pinEnc = await offlinePin.encryptPin(pinTyped);
    if (loginAttemptRef.current!==attempt) return false;   // retyped meanwhile — drop it
    const auth = hit ? offlinePin.AUTH_PIN_OFFLINE : offlinePin.AUTH_PIN_UNCHECKED;
    try { await ccQueuePinUse({event_key:ccMakeIdemKey(), kind:"unlock", used_at:new Date().toISOString(), tablet_auth:auth, tablet_name:hit?hit.name:null, pin_enc:pinEnc, site_id:SITE.id}); }
    catch(e){ try{console.error("[offline-unlock] couldn't save the unlock",e)}catch(_){} }
    accessSessionRef.current=null;
    ccSetScannerOnly(true);   // nothing asks the server until someone signs in again
    setOfflineSession({since:Date.now(), name:hit?hit.name:null, auth});
    setStaff(hit ? hit.name : "Offline PIN");
    setStaffRole(hit ? (hit.role||"") : "");
    setCcToken(null);
    setCcSession({staffId:null, staffName:hit?hit.name:null, staffRole:hit?(hit.role||""):"", siteId:SITE.id, siteName:SITE.name});
    setChecking(false); setErr(""); setPin("");
    setView("scanner");
    return true;
  };

  // Wire the token-refresh bridge: let the module-level refresher push a new
  // access token into React state, and bounce to the login screen if the
  // refresh token itself has died. Hydrate the refresh token on mount so a
  // reloaded (session-restored) tab can still refresh.
  useEffect(()=>{
    ccAuth.onToken=(t)=>setCcToken(t);
    ccAuth.onExpired=()=>{try{logout()}catch(e){}};
    ccLoadRefresh();
    return ()=>{ccAuth.onToken=null;ccAuth.onExpired=null;};
  },[]);

  // requestPin(onOk, onCancel?) — opens the PIN modal. onOk is called with the staff name on success.
  // onCancel (optional) is called if the user dismisses the modal without submitting — useful for callers
  // like the scanner that need to unwind scan state when the staff member bails out.
  // 2026-05-04: localhost / LAN preview shortcut. After login captured the
  // PIN, every per-action prompt skips the modal and auto-calls cb. Prod
  // hosts never set the sessionStorage key so behaviour is unchanged.
  // opts.offline (JJ, 13 Oct 2026): this job can run with no connection, so when the server
  // genuinely can't be reached the PIN gets through anyway. onOk then gets a third argument
  // {auth, pinEnc}: auth = offlinePin.AUTH_PIN_OFFLINE (this tablet's book knew the PIN —
  // the name argument is that person) or AUTH_PIN_UNCHECKED (it didn't — the name is null);
  // pinEnc = the PIN encrypted for the server to check when the job syncs. Every other
  // caller is unchanged: no connection = "Can't connect".
  const requestPin = (cb,cancelCb,opts)=>{
    pmOfflineOk.current = false;
    try {
      const h = window.location.hostname || "";
      const isPreview = h === "localhost" || h === "127.0.0.1" || h.startsWith("192.168.") || h.startsWith("10.") || h.startsWith("172.");
      // Row E: this dev-only shortcut reads the roster (staffPins[captured]). Never
      // fires on a real kiosk host (isPreview is false there), and now also skips
      // when server PIN verify is on, so a `?serverpin=on` soak on a LAN preview
      // exercises the real server path with no roster read. OFF path unchanged.
      if (isPreview && !pinAuth.serverPinVerifyEnabled()) {
        const captured = sessionStorage.getItem("rolled.preview.pin");
        if (captured && staffPins && staffPins[captured]) {
          if (cb) cb(staffPins[captured], captured);
          return;
        }
      }
    } catch(e) {}
    pmOfflineOk.current = !!(opts && opts.offline);
    pmCb.current=cb;pmCancelCb.current=cancelCb||null;setPmVal("");setPmErr("");setPmChecking(false);setPinModal(true);
  };
  const cancelPin = ()=>{const c=pmCancelCb.current;setPinModal(false);setPmChecking(false);pmCb.current=null;pmCancelCb.current=null;pmOfflineOk.current=false;if(c)c()};
  // Idle lock (JJ, 28 Sep 2026): no taps for IDLE_LOCK_MS_HERE → back to the PIN pad, as
  // if "Log out" had been tapped. Anything half-open is shut first so none of it pops back
  // up in front of the next person to sign in. An open PIN prompt is closed WITHOUT running
  // its cancel callback: every prompt belongs to a screen that unmounts with the lock, and
  // the Club Card scanners' cancel restarts the camera, which would then keep running
  // behind the PIN pad because its screen is gone before the stream starts (review of the
  // session-lock PR). The leave-anyway and clock-in prompts close too, and the unsaved-work
  // flags are cleared because their screens unmount (each sets its flag again on reopen).
  // End of Day counts are not lost — they live on the tablet (appData).
  // lockToPad(note) is that whole lock; the site-access sign-out (below) uses it too, so a
  // server "signout" closes everything the idle lock closes. `note` shows on the PIN pad.
  const lockToPad = (note)=>{
    pmCb.current=null;pmCancelCb.current=null;pmOfflineOk.current=false;setPinModal(false);setPmChecking(false);
    setBackConfirm(false);setNotClockedInPrompt(null);
    setReportDirty(false);setStockPending(false);setWasteDirty(false);
    logout();
    setLockNote(note);
  };
  const lockForIdle = ()=>lockToPad("Locked after "+(IDLE_LOCK_MS_HERE>=60000?Math.round(IDLE_LOCK_MS_HERE/60000)+" min":Math.round(IDLE_LOCK_MS_HERE/1000)+" s")+" with no taps — enter your PIN");
  useIdleLock({active: view!=="login" && !!staff, idleMs: IDLE_LOCK_MS_HERE, onLock: lockForIdle});
  // Scanner-only offline session, or the full app with no scanner token (ccTokenless): watch for
  // the Club Card server coming back (a small GET of its /health every 15 s, and on the browser's
  // 'online' event). Answering for 10 s or more means it's back (one blip on a flapping signal
  // isn't enough): a banner offers Sign in. In the scanner-only session the tablet also locks for a
  // PIN by itself once nobody's mid-job (the scanner on its home screen, no PIN pad open) — never
  // mid-job, which would take a queued reward's Square code off the screen before it's applied —
  // or once the banner has been up 3 minutes AND nobody has touched the screen for a minute (a scan
  // screen left open between customers is never "home", and queued rewards aren't burned on the
  // server until they send; the minute's quiet keeps a just-shown Square code on screen). The full
  // app is never locked for it: there the banner shows on every screen. A normal sign-in gets a
  // scanner token, and the queue then sends on its own.
  const WIFI_BACK_NOTE = "Wi-Fi's back — enter your PIN to carry on. Anything queued sends once you're signed in.";
  const CC_BACK_NOTE = "The Club Card server's back — enter your PIN so the scanner can send what's queued.";
  const [offlineWifiBack,setOfflineWifiBack] = useState(false);
  const offlineIdleRef = useRef(true);   // kept by ClubCardScanner: on its home screen, nothing open
  const pinModalOpenRef = useRef(false);
  pinModalOpenRef.current = pinModal;
  const signInAgain = ()=>{ ccSetNetDown(false); lockToPad(offlineSession ? WIFI_BACK_NOTE : CC_BACK_NOTE); };
  useEffect(()=>{
    setOfflineWifiBack(false);
    if(!offlineSession && !ccTokenless) return;
    let stopped=false, busy=false, goodSince=0, backSince=0, lastTapAt=Date.now();
    const onTap=()=>{ lastTapAt=Date.now(); };
    const check=async()=>{
      if(stopped||busy) return;
      busy=true;
      const up=await ccServerReachable(4000);
      busy=false;
      if(stopped) return;
      if(!up){ goodSince=0; backSince=0; setOfflineWifiBack(false); return; }
      if(!goodSince) goodSince=Date.now();
      const back = Date.now()-goodSince >= 10000;
      setOfflineWifiBack(back);
      if(!back || !offlineSession || pinModalOpenRef.current) return;   // the full app: just the banner
      if(!backSince) backSince=Date.now();
      if(offlineIdleRef.current || (Date.now()-backSince >= 180000 && Date.now()-lastTapAt >= 60000)){ ccSetNetDown(false); lockToPad(WIFI_BACK_NOTE); }
    };
    const iv=setInterval(check,15000);
    const on=()=>{ check(); };
    window.addEventListener("online",on);
    window.addEventListener("pointerdown",onTap,true);
    return ()=>{ stopped=true; clearInterval(iv); window.removeEventListener("online",on); window.removeEventListener("pointerdown",onTap,true); };
  },[offlineSession,ccTokenless]);
  // A scanner token again (sign-in, or a silent refresh): send whatever the tablet queued while
  // it had none — offline unlocks, stamps and redemptions — whichever screen is open, so the
  // "Wi-Fi's back" sign-in really does send everything. All three are single-flight and keyed,
  // so this never doubles up with the scanner's own drains. Quiet: no toasts from here.
  useEffect(()=>{
    if(!ccToken) return;
    setCcTokenless(false);
    ccSyncPinUses(ccToken).catch(()=>{});
    ccSyncQueue(ccToken,null,null,true).catch(()=>{});
    ccSyncRedeems(ccToken).catch(()=>{});
  },[ccToken]);
  // Site access heartbeat (JJ, 29 Sep 2026; SITE_ACCESS_SPEC.md §4). While someone is
  // signed in, every HEARTBEAT_MS and whenever the app comes back into view, the session
  // token from sign-in goes to /api/access. Only an explicit "signout"/"block" reply ends
  // the session (a PIN switched off in the Command Centre, "Sign everyone out now", the
  // lock-down switch); it signs out exactly like the idle lock and shows the server's
  // message where the idle-lock note shows. Unreachable/slow/odd → nothing happens.
  // Site access off → no heartbeat. No session (the login check was unreachable at
  // sign-in) → the heartbeat STILL runs, with session null (review, 29 Sep 2026): the
  // server never signs a device out for lacking a session, but lockdown, a blocked
  // device and the shop-wifi rule still apply — otherwise a phone that signed in with
  // the check unreachable (e.g. in airplane mode) would never hear any of them.
  // Keyed on signed-in-or-not, NOT on `view`, so moving between screens doesn't restart
  // the 60 s timer. A reply for a session that has since ended is dropped.
  const accessSignOutRef = useRef(null);
  accessSignOutRef.current = (msg)=>lockToPad(msg);
  const __accessSignedIn = view!=="login" && !!staff;
  useEffect(()=>{
    if(!SITE_ACCESS_ON || !__accessSignedIn) return;
    let stopped=false, inFlight=false;
    const beat = async ()=>{
      if(stopped || inFlight) return;
      const session = accessSessionRef.current;   // may be null: sent as session null, never skipped
      inFlight=true;
      let reply=null;
      try{ reply = await siteAccess.accessCheck({event:"heartbeat", site:SITE.key, session}); }catch(e){ reply=null; }
      inFlight=false;
      if(stopped || accessSessionRef.current!==session) return;
      if(siteAccess.heartbeatVerdict(reply)==="signout"){
        try{ accessSignOutRef.current(siteAccess.staffMessage(reply)); }catch(e){ try{console.error("[site-access] sign-out failed",e)}catch(_){} }
      }
    };
    const iv = setInterval(beat, siteAccess.HEARTBEAT_MS);
    const onShow = ()=>{ if(!document.hidden) beat(); };
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("pageshow", onShow);
    return ()=>{ stopped=true; clearInterval(iv); document.removeEventListener("visibilitychange", onShow); window.removeEventListener("pageshow", onShow); };
  },[__accessSignedIn]);
  // Site access GPS: asked for every time the PIN pad shows (at most once per 2 min),
  // then on sign-in and every GPS_REFRESH_MS while signed in. Never waited on — each
  // check sends the fix already held, and only if it is under 10 minutes old (a fix
  // from hours ago would pin a sign-in at home on the shop). Kept in memory only.
  // The browser's location permission is watched from the first render (Permissions
  // API + its change event); with how the last GPS ask ended, it goes with every
  // check as `location` so the CC can see "said no" vs "timed out" (JJ, 29 Sep 2026).
  useEffect(()=>{ if(SITE_ACCESS_ON) siteAccess.watchLocationPermission(); },[]);
  useEffect(()=>{ if(SITE_ACCESS_ON && view==="login") siteAccess.askGpsOnPad(); },[view]);
  useEffect(()=>{
    if(!SITE_ACCESS_ON || !__accessSignedIn) return;
    siteAccess.requestGpsFix();
    const iv = setInterval(()=>siteAccess.requestGpsFix(), siteAccess.GPS_REFRESH_MS);
    return ()=>clearInterval(iv);
  },[__accessSignedIn]);
  // Auto-submit when the 4th digit lands — no Submit button needed. valOverride is used by the keypad
  // so we don't hit a state-update race between the 4th tap and the submit dispatch.
  const submitPin = async(valOverride)=>{
    const p=(valOverride!==undefined?valOverride:pmVal).trim();
    if(p.length!==4){setPmErr("Enter 4 digits");return}
    // Optimistic close: if the PIN is in the local staff directory (loaded at
    // mount via pinsForLogin + cached in localStorage), trust it and dismiss
    // the modal immediately — matches the login numpad feel. The action's own
    // server POST still carries the PIN for audit + server-side re-check,
    // same as before. Only fall back to the validatePin roundtrip when the
    // PIN isn't in the cached roster (e.g. a staffer added mid-session).
    // A1 leak-close: when server PIN verify is on, never DECIDE from the
    // downloaded roster — force a fall-through to the single-PIN server check
    // below (which already exists as the fallback). Off everywhere until a shop
    // is flipped, so this is inert.
    const localName = pinAuth.serverPinVerifyEnabled() ? null : (staffPins && staffPins[p]);
    if (localName) {
      // Clock-in gate (2026-04-29). Cache-based pre-check is unreliable —
      // staffOnShift can lag the actor's actual shift state by up to 30s
      // (poller cadence) and the optimistic-cache fix only helps when this
      // tablet's React app drove the clock-in/out itself (not the case if
      // the staffer clocked in/out from another device, or if the page is
      // an older build serving stale cache). Authoritative live check:
      // ask the backend directly. Adds ~0.5-1s latency on every per-action
      // PIN entry, but is the only way to be bulletproof. Many existing
      // per-action POSTs use mode:"no-cors" so they can't surface a
      // backend NOT_CLOCKED_IN response — this is the one place where the
      // gate has visibility into the live state.
      if (CLOCK_IN_ENABLED) {
        setPmLoad(true);
        try {
          const live = await fetch(BACKEND, {
            method:"POST",
            headers:{"Content-Type":"text/plain;charset=utf-8"},
            body: JSON.stringify({action:"getStaffOnShift", site:CLOCK_IN_SITE_KEY})
          });
          const liveData = await live.json();
          const liveShifts = (liveData && Array.isArray(liveData.shifts)) ? liveData.shifts : [];
          // Refresh the local cache too so subsequent gates use truth.
          setStaffOnShift(liveShifts);
          if (!staffIsOnShift(localName, liveShifts)) {
            setPinModal(false); setPmVal(""); setPmErr(""); setPmLoad(false);
            pmCb.current = null; pmCancelCb.current = null;
            setNotClockedInPrompt({ name: localName });
            return;
          }
        } catch(e) {
          // Live check failed (network blip). Fall back to cache; backend
          // backstops with NOT_CLOCKED_IN on the per-action POST itself.
          if (!staffIsOnShift(localName, staffOnShift)) {
            setPinModal(false); setPmVal(""); setPmErr(""); setPmLoad(false);
            pmCb.current = null; pmCancelCb.current = null;
            setNotClockedInPrompt({ name: localName });
            return;
          }
        }
        setPmLoad(false);
      }
      setPinModal(false);
      setPmVal("");
      setPmErr("");
      const ok=pmCb.current;pmCb.current=null;pmCancelCb.current=null;
      if(ok)ok(localName, p);
      // S22 presence: somebody just tapped in at this shop. Fired AFTER the modal
      // has closed and the action has run, deliberately not awaited, so it adds
      // zero delay to the tap and cannot fail it. Write-only; nothing reads it.
      presence.recordTap({ siteKey: SITE.key, pin: p, kind: "action", source: "foh-action" });
      return;
    }
    // O11 — local-first rejection (E2E round 2 / B3, hardened round 8). The staff
    // roster (staffPins) is loaded at mount, cached, and re-polled every 5 min. A
    // PIN not in it is either wrong OR belongs to a staffer added in Command Centre
    // → Add Staff (backend `staff` table, accepted by POST /staff/pin-login, but not
    // in the cached Bizimply roster). This USED to BLOCK the gate on a no-timeout
    // /staff/pin-login round-trip BEFORE showing the error, so every genuinely-wrong
    // PIN sat behind a spinner for 1-3s. Now: reject INSTANTLY (matches the login
    // numpad + Clock-In feel), then fire /staff/pin-login as a BACKGROUND rescue.
    // Only if it returns a genuine CC staffer do we auto-clear the error and run the
    // pending action. Security is unchanged: the action's own server POST still
    // carries the PIN and re-validates server-side.
    // R12 (off-list flash fix, modal half): a PIN missing from the cached roster
    // used to paint the red 'Invalid PIN' INSTANTLY, then the background rescue
    // cleared it and ran the action — so a valid-but-uncached PIN blinked red
    // first. With the flag ON we now show a NEUTRAL 'Checking…' during the rescue
    // window and only paint 'Invalid PIN' inside the genuine-reject branch (incl.
    // abort/timeout → d=null). Flag OFF stays an INSTANT reject (no rescue).
    setPmVal("");
    setPmLoad(false);
    // Gate on: the roster never decides here, so don't pull the whole list from
    // Apps Script on every action PIN (the 5-min poll still keeps it fresh).
    if (pinsRefreshRef.current && !pinAuth.serverPinVerifyEnabled()) pinsRefreshRef.current();
    if (SERVER_PIN_LOGIN_FALLTHROUGH || pinAuth.serverPinVerifyEnabled()) {
      setPmChecking(true);
      const rescuePin = p;
      const __gated = pinAuth.serverPinVerifyEnabled();
      // Offline PIN (JJ, 13 Oct 2026) — only for a job that can run with no connection
      // (requestPin's opts.offline), and only with the server check on (gate off = the
      // roster still decides, which already works offline).
      const __offlineOk = __gated && pmOfflineOk.current;
      const cbAtStart = pmCb.current;
      // With no connection the PIN gets through (JJ, 13 Oct 2026: "we can just do pin, but
      // track the offline pins"). This tablet's book says who it is if it knows the PIN
      // (AUTH_PIN_OFFLINE); otherwise it's let through unchecked (AUTH_PIN_UNCHECKED, name
      // null). Either way the PIN goes with the job encrypted for the server, which checks
      // it when the job syncs, and the Command Centre lists every one. The connection is
      // marked down, so the scanner takes its offline path at once (stamps queue, rewards
      // use the offline picker) instead of waiting out another try.
      const acceptOffline = async ()=>{
        ccSetNetDown(true);
        const hit = await offlinePin.lookup(rescuePin);
        const pinEnc = await offlinePin.encryptPin(rescuePin);
        if (!cbAtStart || pmCb.current!==cbAtStart) { setPmChecking(false); return; }   // closed, or another PIN took over
        setPmChecking(false); setPmVal(""); setPmErr(""); setPinModal(false);
        const ok=pmCb.current;pmCb.current=null;pmCancelCb.current=null;pmOfflineOk.current=false;
        if(ok)ok(hit?hit.name:null, rescuePin, {auth: hit?offlinePin.AUTH_PIN_OFFLINE:offlinePin.AUTH_PIN_UNCHECKED, pinEnc});
      };
      (async()=>{
        // The till has already seen the server fail to answer (ccNetDown — set by a real
        // failed request, not by navigator.onLine, which WebView tablets misreport): don't
        // make staff wait out a timeout first.
        // The scanner-only offline session: until someone signs in again, nothing here asks the
        // server (ccScannerOnly). An offline job's PIN goes straight through; anything else
        // needs the internet.
        if (ccScannerOnly()) {
          if (__offlineOk) { await acceptOffline(); return; }
          setPmChecking(false); setPmErr("Needs the internet — sign in again once the Wi-Fi's back"); return;
        }
        if (__offlineOk && ccNetDown()) { await acceptOffline(); return; }
        const ctrl = new AbortController();
        // Gate on: this is the ONLY check for every action PIN, not a rare rescue, so
        // give a slow mobile link 4 s ("Checking…" shows meanwhile) before giving up.
        let __timedOut = false;
        const to = setTimeout(()=>{__timedOut=true;try{ctrl.abort()}catch(e){}},__gated?4000:1200);
        let d = null, __ccStatus = 0, __body = null, __netErr = false;
        try {
          const r = await fetch(CC_API+"/staff/pin-login",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({pin:rescuePin,site_id:SITE.id}),signal:ctrl.signal});
          __ccStatus = r.status;
          __body = await r.json().catch(()=>null);
          d = r.ok ? __body : null;
        } catch(e) { d = null; __netErr = !__timedOut; }
        clearTimeout(to);
        // Any real answer (not a 5xx) means the server is there: clear an old "down" mark so
        // the scanner stops queueing (review, 13 Oct 2026).
        if (__ccStatus && __ccStatus < 500) ccSetNetDown(false);
        if (!d || !d.access_token || !d.staff) {
          // A definite "no" to this PIN from OUR server (its own JSON — not a proxy or
          // captive-portal page): wrong, or switched off in the Command Centre. Out of the
          // book too.
          if (__gated && (__ccStatus===401 || __ccStatus===403) && __body && typeof __body==="object" && ("error" in __body || "ok" in __body || "reason" in __body)) offlinePin.forget(rescuePin);
          if (__offlineOk) {
            // Truly no connection: the request failed outright, or the gateway says the
            // server is down (502/503/504 — a 500 is a bug, not an outage).
            const __down = __netErr || __ccStatus===502 || __ccStatus===503 || __ccStatus===504;
            // Our own 4 s cut-off: the connection, or a slow genuine "no"? (An unknown PIN
            // makes the server look staff up live, which can take a while.) Ask the server
            // for anything at all: if it answers, it's reachable and this PIN gets no pass.
            if (__down || (__timedOut && !(await ccServerReachable(3000)))) { await acceptOffline(); return; }
            if (__timedOut) { if (pmCb.current===cbAtStart) { setPmChecking(false); setPmErr("Slow connection — try again"); } return; }
          }
          // Gate on and no 4xx answer = the connection, not the PIN — say so instead of "Invalid PIN".
          setPmChecking(false); setPmErr(__gated && !(__ccStatus>=400&&__ccStatus<500) ? "Can't connect — try again" : "Invalid PIN"); return;   // genuine reject (or abort/timeout) — now paint the error
        }
        if (!cbAtStart || pmCb.current!==cbAtStart) { setPmChecking(false); return; }   // modal cancelled/closed, or another PIN already resolved it — a late yes never runs the NEXT prompt's job
        const srvName = d.staff.name;
        // The server said yes on this tablet: keep it in the offline book (stretched, never
        // the PIN itself) so this person's PIN still works here when the Wi-Fi drops.
        if (__gated) offlinePin.remember(rescuePin, {name: srvName, role: (d.staff.role||"")});
        // Clock-in gate — same authoritative live check as the local path,
        // keyed on the server-returned name.
        if (CLOCK_IN_ENABLED) {
          let liveShifts = staffOnShift;
          try {
            const live = await fetch(BACKEND, {
              method:"POST",
              headers:{"Content-Type":"text/plain;charset=utf-8"},
              body: JSON.stringify({action:"getStaffOnShift", site:CLOCK_IN_SITE_KEY})
            });
            const liveData = await live.json();
            liveShifts = (liveData && Array.isArray(liveData.shifts)) ? liveData.shifts : [];
            setStaffOnShift(liveShifts);
          } catch(e) { /* network blip → fall back to cached staffOnShift */ }
          if (!cbAtStart || pmCb.current!==cbAtStart) { setPmChecking(false); return; }
          if (!staffIsOnShift(srvName, liveShifts)) {
            setPmChecking(false); setPinModal(false); setPmVal(""); setPmErr("");
            pmCb.current = null; pmCancelCb.current = null;
            setNotClockedInPrompt({ name: srvName });
            return;
          }
        }
        // Seed the local roster so later taps this session skip the round-trip.
        setStaffPins(prev=>{const nx={...prev,[rescuePin]:srvName};try{localStorage.setItem("staffPinsCache",JSON.stringify(nx))}catch(e){}return nx;});
        setStaffByPin(prev=>{const nx={...prev,[rescuePin]:{role:(d.staff.role||"")}};try{localStorage.setItem("staffByPinCache",JSON.stringify(nx))}catch(e){}return nx;});
        setPmChecking(false); setPinModal(false); setPmVal(""); setPmErr("");
        const ok=pmCb.current;pmCb.current=null;pmCancelCb.current=null;
        if(ok)ok(srvName, rescuePin);
        presence.recordTap({ siteKey: SITE.key, pin: rescuePin, kind: "action", source: "foh-action" });
      })();
    } else {
      // Flag OFF: no rescue path, so this is an INSTANT reject.
      setPmErr("Invalid PIN");
    }
  };
  // Keypad handlers for the PIN modal — mirrors the login numpad flow
  const pmDigit = useCallback((d)=>{
    setPmErr("");setPmChecking(false);
    setPmVal(v=>{
      if(v.length>=4||pmLoad)return v;
      const next=v+d;
      if(next.length===4){setTimeout(()=>submitPin(next),60)}
      return next;
    });
  },[pmLoad]);
  const pmDel = useCallback(()=>{setPmErr("");setPmChecking(false);setPmVal(v=>v.slice(0,-1))},[]);

  // Clock-In: 30s poller for the live roster. Runs only on tablet kiosks
  // (phones + desktops never see the banner so don't waste a fetch). Caches
  // the result to localStorage so a fresh page load paints the banner from
  // the cache before the first network response lands.
  useEffect(()=>{
    if (!CLOCK_IN_ENABLED || !isTabletKiosk) return;
    let cancelled = false;
    const fetchOnShift = ()=>{
      fetch(BACKEND, {
        method:"POST",
        headers:{"Content-Type":"text/plain;charset=utf-8"},
        body: JSON.stringify({action:"getStaffOnShift", site:CLOCK_IN_SITE_KEY})
      })
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if (cancelled || !d || d.status !== "ok") return;
        const shifts = Array.isArray(d.shifts) ? d.shifts : [];
        setStaffOnShift(shifts);
        try { localStorage.setItem("staffOnShiftCache::"+CLOCK_IN_SITE_KEY, JSON.stringify({shifts: shifts, ts: Date.now()})); } catch(e) {}
      })
      .catch(()=>{ /* keep cached state, will retry next tick */ });
    };
    fetchOnShift();
    const t = setInterval(fetchOnShift, 30000);
    return ()=>{ cancelled = true; clearInterval(t); };
  }, [isTabletKiosk]);

  // Refresh the roster cache imperatively after a successful clock-in/out so
  // the banner reflects the change without waiting up to 30s for the next
  // poll. Fire-and-forget — failure here just means the next poll catches up.
  const refreshOnShift = useCallback(()=>{
    fetch(BACKEND, {
      method:"POST",
      headers:{"Content-Type":"text/plain;charset=utf-8"},
      body: JSON.stringify({action:"getStaffOnShift", site:CLOCK_IN_SITE_KEY})
    })
    .then(r=>r.ok?r.json():null)
    .then(d=>{
      if (!d || d.status !== "ok") return;
      const shifts = Array.isArray(d.shifts) ? d.shifts : [];
      setStaffOnShift(shifts);
      try { localStorage.setItem("staffOnShiftCache::"+CLOCK_IN_SITE_KEY, JSON.stringify({shifts: shifts, ts: Date.now()})); } catch(e) {}
    })
    .catch(()=>{});
  }, []);

  // Clock-In flow control: open + close the scrim. Reset ALL transient flow
  // state on each open so a previous error/success doesn't leak across.
  const openClockIn = useCallback(()=>{
    setClockFlow({mode:"in", step:"pin", pin:"", photo:null, target:null, busy:false, err:"", successName:"", successTime:""});
  }, []);
  const openClockOut = useCallback(()=>{
    setClockFlow({mode:"out", step:"picker", pin:"", photo:null, target:null, busy:false, err:"", successName:"", successTime:""});
  }, []);
  const closeClock = useCallback(()=>{
    if (clockCloseTimerRef.current) {
      clearTimeout(clockCloseTimerRef.current);
      clockCloseTimerRef.current = null;
    }
    setClockFlow({mode:null, step:null, pin:"", photo:null, target:null, busy:false, err:"", successName:"", successTime:""});
  }, []);

  // Clock-IN POST. OPTIMISTIC UI: as soon as PIN+photo are captured we
  // immediately swap to the success scene with the locally-known name and
  // current time. The fetch fires in the background. The user perceives
  // capture → success as instant rather than waiting on the ~1-2s Apps
  // Script roundtrip (sheet reads + Bizimply mirror are synchronous server-
  // side). On the rare POST failure we revert to the camera scene with an
  // error so the user can retry — the open-shift state is verified by the
  // 30s roster poll regardless.
  const submitClockIn = useCallback(async (pin, photoBase64, verifiedName)=>{
    // A1 (row A): on a server-PIN-verify host the name came from the PIN scrim's
    // verify (threaded in as verifiedName), never the downloaded roster. OFF path
    // is byte-identical — reads staffPins exactly as before.
    const localName = pinAuth.serverPinVerifyEnabled() ? (verifiedName || null) : (staffPins && staffPins[pin]);
    if (!localName) {
      setClockFlow(f=>({...f, busy:false, err:"Wrong PIN", step:"pin", pin:""}));
      return;
    }
    const t = new Date().toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"});
    // Optimistic success — flip the scene immediately so the user isn't
    // stuck on the camera frame waiting on the network.
    setClockFlow(f=>({...f, step:"success", busy:false, err:"", successName: localName, successTime: t}));
    // Optimistically add to the on-shift roster so any per-action gate fires
    // correctly the moment the user lands back on home. Without this,
    // refreshOnShift only catches up after the fetch resolves (~1-2s) and
    // a fast staffer could squeeze actions through during the cache lag.
    // Backend backstops via NOT_CLOCKED_IN.
    setStaffOnShift(s=>{
      const list = Array.isArray(s) ? s : [];
      if (list.some(x => x && String(x.name||"").toLowerCase() === String(localName).toLowerCase())) return list;
      return list.concat([{name: localName, role: "", siteKey: CLOCK_IN_SITE_KEY, clockedInAt: new Date().toISOString()}]);
    });
    const clientUuid = uuid4();
    try {
      const r = await fetch(BACKEND, {
        method:"POST",
        headers:{"Content-Type":"text/plain;charset=utf-8"},
        body: JSON.stringify({
          action:"staffShiftStart",
          site: CLOCK_IN_SITE_KEY,
          staffPin: pin,
          photoBase64: photoBase64 || "",
          uuid: clientUuid
        })
      });
      if (!r.ok) throw new Error("HTTP "+r.status);
      const d = await r.json();
      if (!d || d.status !== "ok") throw new Error((d && d.message) || "Clock-in failed");
      // S22: mirror the clock-in into Supabase. Inert at every site today
      // (CLOCK_IN_ENABLED is false everywhere) — this just means the data has a
      // home ready for the day it's switched on. Fire-and-forget.
      presence.recordClockIn({ siteKey: SITE.key, pin });
      // Sync the server-confirmed name in case it differs from the local
      // cache (e.g. mid-session staff directory change). Only if still on
      // the success scene — user may have already tapped Done.
      setClockFlow(f=>(f.step === "success" ? {...f, successName: d.staffName || f.successName} : f));
      refreshOnShift();
    } catch (e) {
      // Revert ONLY if still on the success scene. If the user already
      // tapped Done the scrim is closed and we don't want to reopen it.
      // The 30s roster poll will reveal the actual state on next tick.
      setClockFlow(f=>{
        if (f.mode !== "in" || f.step !== "success") return f;
        return {...f, step:"camera", err: (e && e.message) || "Connection error — try again", successName:"", successTime:""};
      });
    }
  }, [staffPins, refreshOnShift]);

  // Clock-OUT POST. Same OPTIMISTIC UI pattern as submitClockIn — we
  // flip to the success scene immediately and let the fetch resolve in
  // the background, so the user isn't stuck on the camera frame for
  // ~1-2s while Apps Script + Bizimply mirror complete. On rare POST
  // failure we revert to camera with an error so the user can retry.
  const submitClockOut = useCallback(async (pin, targetName, photoBase64, verifiedName)=>{
    // A1 (row B): name from the verify (threaded in) on a gated host, else the
    // roster. OFF path byte-identical.
    const localName = pinAuth.serverPinVerifyEnabled() ? (verifiedName || null) : (staffPins && staffPins[pin]);
    // PIN privacy backstop (memory pin_privacy.md). BOTH validation paths
    // surface the same opaque "Wrong PIN" message. Primary check is
    // upstream in ClockFlowScrimRenderer.onPinComplete; this backstops
    // any caller that bypasses the PIN scrim.
    if (rosterFree.clockOutRejected(localName, targetName)) {
      setClockFlow(f=>({...f, busy:false, err:rosterFree.WRONG_PIN_MESSAGE, pin:""}));
      return;
    }
    const t = new Date().toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"});
    // Optimistic success — flip the scene immediately.
    setClockFlow(f=>({...f, step:"success", busy:false, err:"", successName: localName, successTime: t}));
    // Optimistically remove from on-shift roster so the per-action gate
    // fires correctly the moment they land back on home, without waiting
    // on refreshOnShift (~1-2s lag). Backend backstops via NOT_CLOCKED_IN.
    setStaffOnShift(s=>{
      const list = Array.isArray(s) ? s : [];
      return list.filter(x => x && String(x.name||"").toLowerCase() !== String(localName).toLowerCase());
    });
    try {
      const r = await fetch(BACKEND, {
        method:"POST",
        headers:{"Content-Type":"text/plain;charset=utf-8"},
        body: JSON.stringify({action:"staffShiftEnd", site:CLOCK_IN_SITE_KEY, staffPin: pin, photoBase64: photoBase64 || ""})
      });
      if (!r.ok) throw new Error("HTTP "+r.status);
      const d = await r.json();
      if (!d || d.status !== "ok") throw new Error((d && d.message) || "Clock-out failed");
      // S22: mirror the clock-out. Same story — inert until clock-in is on.
      presence.recordClockOut({ siteKey: SITE.key, pin });
      setClockFlow(f=>(f.step === "success" ? {...f, successName: d.staffName || f.successName} : f));
      refreshOnShift();
    } catch (e) {
      // Revert ONLY if still on the success scene. If user already tapped
      // Done the scrim is closed; the 30s roster poll will reveal truth.
      setClockFlow(f=>{
        if (f.mode !== "out" || f.step !== "success") return f;
        return {...f, step:"camera", err: (e && e.message) || "Connection error — try again", successName:"", successTime:""};
      });
    }
  }, [staffPins, refreshOnShift]);

  const day=now.toLocaleDateString("en-GB",{weekday:"long"});
  const date=now.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
  const time=now.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
  const greetWord=now.getHours()<12?"Good morning":now.getHours()<17?"Good afternoon":"Good evening";

  const PinModal = pinModal ? (
    <div style={S.overlay}><div style={{...S.modal,display:"flex",flexDirection:"column",alignItems:"center",padding:isPortrait?24:16,maxWidth:isPortrait?380:340}}>
      <div style={{...S.modalTitle,textAlign:"center",fontSize:isPortrait?"1.1rem":"0.95rem"}}>Staff PIN</div>
      <div style={{...S.modalDesc,textAlign:"center",marginBottom:isPortrait?18:8,fontSize:isPortrait?"0.8rem":"0.72rem"}}>{(pmOfflineOk.current&&ccIsOffline())?"No connection — enter your PIN as usual":"Enter your 4-digit PIN to confirm"}</div>
      <div style={{...S.dots,marginBottom:isPortrait?10:6,gap:isPortrait?20:14}}>
        {[0,1,2,3].map(i=>(<div key={i} style={{...S.dot,...(i<pmVal.length?S.dotFilled:S.dotEmpty),width:isPortrait?20:16,height:isPortrait?20:16}}/>))}
      </div>
      <div style={{color:pmChecking?B.kewpie:B.coral,opacity:pmChecking?0.6:1,fontSize:"0.78rem",fontWeight:600,height:18,marginBottom:isPortrait?6:2,textAlign:"center"}}>{pmChecking?"Checking…":(pmErr||" ")}</div>
      <div style={{...S.pad,gap:isPortrait?10:6,maxWidth:isPortrait?280:220}}>
        {[1,2,3,4,5,6,7,8,9,null,0,"del"].map((k,i)=>{
          if(k===null) return (<div key={i}/>);
          return (<button key={i} style={{...S.key,aspectRatio:isPortrait?"1.35":"1.8",fontSize:isPortrait?"1.6rem":"1.2rem",borderRadius:isPortrait?14:10}} onClick={()=>k==="del"?pmDel():pmDigit(String(k))} disabled={pmLoad}>{k==="del"?"⌫":k}</button>);
        })}
      </div>
      <button style={{...S.modalCancel,marginTop:isPortrait?18:8,width:"100%",maxWidth:isPortrait?280:220,padding:isPortrait?12:8,fontSize:"0.8rem"}} onClick={cancelPin} disabled={pmLoad}>{pmLoad?"Checking...":"Cancel"}</button>
    </div></div>
  ) : null;

  // Per-action prompt-to-clock-in modal. Renders only when a staffer
  // enters their PIN for an action while not currently on shift. CTA opens
  // the clock-in scrim; the staffer retries the action after clocking in.
  const NotClockedInPromptModal = notClockedInPrompt ? (
    <NotClockedInPrompt
      staffName={notClockedInPrompt.name}
      onClockIn={()=>{ setNotClockedInPrompt(null); openClockIn(); }}
      onDismiss={()=>setNotClockedInPrompt(null)}
    />
  ) : null;

  // Clock-In flow scrim — extracted into a separate function component below
  // (ClockFlowScrimRenderer). Mounted only while clockFlow.mode is set so the
  // camera hook (useClockInCamera) starts/stops cleanly with the modal.
  const ClockFlowScrim = (CLOCK_IN_ENABLED && clockFlow.mode) ? (
    <ClockFlowScrimRenderer
      clockFlow={clockFlow}
      setClockFlow={setClockFlow}
      submitClockIn={submitClockIn}
      submitClockOut={submitClockOut}
      closeClock={closeClock}
      staffOnShift={staffOnShift}
      staffPins={staffPins}
    />
  ) : null;

  // ==== LOGIN ====
  if(view==="login") return (
    <div style={{...S.loginRoot,height:"100dvh",overflow:"hidden"}}>
      <div style={S.strip}><div style={S.stripDot}/><span style={S.stripTxt}>{SITE.name}</span></div>
      <div style={{...S.loginBody,flex:1,justifyContent:"center",paddingTop:40}}>
        <RolledLogo height={isPortrait?52:36} />
        <div style={{fontSize:"0.82rem",fontWeight:600,color:B.kewpie,opacity:0.4,marginTop:8,marginBottom:isPortrait?"2rem":"1rem",letterSpacing:"0.06em"}}>Site App - {SITE.name}</div>
        <div style={{...S.dots,...(shake?{animation:"shake 0.4s ease"}:{})}}>
          {[0,1,2,3].map(i=>(<div key={i} style={{...S.dot,...(i<pin.length?S.dotFilled:S.dotEmpty)}}/>))}
        </div>
        {/* The error/note slot grows (was a fixed 20 px) so a site-access message — a full
            sentence from the server — wraps under the dots instead of running over the keypad.
            One short line renders exactly as before. */}
        {checking?<div style={S.pinHint}>Checking…</div>:err?<div style={{...S.pinErr,...S.pinMsgGrow}}>{err}</div>:<div style={{...S.pinHint,...S.pinMsgGrow}}>{lockNote||"Enter your staff PIN"}</div>}
        <div style={{...S.pad,gap:isPortrait?10:6,maxWidth:isPortrait?280:260}}>
          {[1,2,3,4,5,6,7,8,9,null,0,"del"].map((k,i)=>{
            if(k===null) return (<div key={i}/>);
            return (<button key={i} style={{...S.key,aspectRatio:isPortrait?"1.35":"1.6",fontSize:isPortrait?"1.6rem":"1.3rem"}} onClick={()=>k==="del"?del():digit(String(k))}>{k==="del"?"⌫":k}</button>);
          })}
        </div>
        {/* R12 (SC-STAFF-2): just-added staff needn't wait on the 5-min roster poll —
            this forces a fresh pinsForLogin pull on demand. Subtle/secondary. */}
        <button
          onClick={()=>{ if(pinsRefreshRef.current&&!rosterRefreshing){ setRosterRefreshing(true); Promise.resolve(pinsRefreshRef.current()).finally(()=>setTimeout(()=>setRosterRefreshing(false),700)); } }}
          disabled={rosterRefreshing}
          style={{marginTop:isPortrait?18:12,background:"none",border:"none",color:B.kewpie,opacity:rosterRefreshing?0.65:0.4,fontSize:"0.72rem",fontWeight:600,letterSpacing:"0.03em",cursor:rosterRefreshing?"default":"pointer",textDecoration:"underline",textUnderlineOffset:3,padding:6}}>
          {rosterRefreshing?"Refreshing staff list…":"Just added? Refresh staff list"}
        </button>
      </div>
      <div style={{color:B.kewpie,opacity:0.25,fontSize:"0.75rem",textAlign:"center",paddingBottom:isPortrait?20:12,paddingTop:8,flexShrink:0}}>{date} · {time}</div>
      {flash && <div style={S.flash}><div style={S.greet}>G'day, {staff}</div></div>}
    </div>
  );

  // ==== SCANNER-ONLY, NO CONNECTION ==== (JJ, 13 Oct 2026: "let them back into the scanner
  // offline"). Just the Club Card scanner in its offline mode, a plain "no connection" bar and
  // Sign out — no tools, no sidebar, no way into the rest of the app until the Wi-Fi's back.
  if(offlineSession) return (
    <div style={S.shellRoot}>
      <div style={{...S.shellBar,position:"sticky",top:0,zIndex:100,background:B.nori}}>
        <span style={{fontSize:"0.78rem",fontWeight:800,color:B.coral,letterSpacing:"0.02em"}}>📴 No connection — scanner only</span>
        <span style={{fontFamily:"gelica,serif",fontSize:"1rem",fontWeight:700,color:B.wasabi}}>💳 Club Card Scanner</span>
        <button style={S.shellBack} onClick={()=>lockToPad("Signed out")}>Sign out</button>
      </div>
      {offlineWifiBack && <div style={{margin:"10px 16px 0",padding:"10px 12px",borderRadius:12,background:B.wasabi,color:B.nori,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
        <span style={{fontSize:"0.85rem",fontWeight:700,lineHeight:1.35}}>📶 Wi-Fi's back. Finish this customer, then sign in so the queue sends.</span>
        <button style={{fontFamily:"inherit",fontSize:"0.85rem",fontWeight:800,padding:"9px 16px",borderRadius:999,border:"none",background:B.nori,color:B.kewpie,cursor:"pointer"}} onClick={signInAgain}>Sign in</button>
      </div>}
      <div style={{padding:"10px 16px 0",fontSize:"0.78rem",lineHeight:1.45,color:B.kewpie,opacity:0.82,textAlign:"center"}}>
        {offlineSession.name ? <>Signed in as <b>{offlineSession.name}</b>. </> : <>This tablet doesn't know that PIN yet — it's checked once the Wi-Fi's back. </>}
        Stamps and rewards queue on the tablet. When the Wi-Fi's back you'll be asked for your PIN again so they can send.
      </div>
      <div style={{paddingTop:12}}>
        <ClubCardScanner session={ccSession} token={null} requestPin={requestPin} backRef={scannerBackRef} onSignIn={()=>lockToPad("Signed out")} offline idleRef={offlineIdleRef}/>
      </div>
      {PinModal}
    </div>
  );

  // Signed in with no scanner token while the Club Card server was down (ccTokenless), and it's
  // answering again: on every screen of the full app, not just the scanner, so whoever's on the
  // till sees it and the queue (offline rewards too) sends soon.
  const ccBackBanner = (ccTokenless && !ccToken && offlineWifiBack) ? (
    <div style={{margin:"0 16px 12px",padding:"10px 12px",borderRadius:12,background:B.wasabi,color:B.nori,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
      <span style={{fontSize:"0.85rem",fontWeight:700,lineHeight:1.35}}>📶 The Club Card server's back. Sign in again so the scanner can send what's queued.</span>
      <button style={{fontFamily:"inherit",fontSize:"0.85rem",fontWeight:800,padding:"9px 16px",borderRadius:999,border:"none",background:B.nori,color:B.kewpie,cursor:"pointer"}} onClick={signInAgain}>Sign in</button>
    </div>
  ) : null;

  // ==== HOME ====
  if(view==="home") return (
    <div style={S.homeRoot}>
      <div style={S.topBar}>
        <div style={S.topL}><RolledLogo height={24}/><span style={S.hSite}>{SITE.name}</span></div>
        <div style={S.topR}><div style={S.chip}><div style={S.chipAv}>{staff[0]}</div><span style={S.chipName}>{staff}</span></div><button style={S.logoutBtn} onClick={logout}>Log out</button></div>
      </div>
      {ccBackBanner}
      <div style={S.greetBox}><div style={S.greetHi}>{greetWord}, <span style={{color:B.wasabi}}>{staff}</span></div><div style={S.greetD}>{day} · {date} · {time}</div></div>
      {CLOCK_IN_ENABLED && isTabletKiosk && (
        <ClockInRosterBanner
          staffOnShift={staffOnShift}
          onClockIn={openClockIn}
          onClockOut={openClockOut}
        />
      )}
      <KioskBindPanel site={SITE} isAdmin={rosterFree.isAdminRole(staffRole)}/>{/* CB: renders null unless KIOSK_BIND_HOSTS lists this host (it is empty) */}
      {SITE.reviews && (
        <ReviewsBanner
          siteKey={SITE.reviews.banner}
          siteName={SITE.reviews.bannerName}
          onOpen={()=>setView("reviews")}
        />
      )}
      <ClubCardStats token={ccToken}/>
      {(()=>{
        const accFor = (t)=>toolAccessFor(t);
        // Override the FOH 67 Checklist tile's `on` based on the per-site
        // visibleToStaff flag fetched from the admin dashboard. Off by
        // default so staff don't see the tile until JJ flips it on. Admin
        // role bypasses the gate so JJ can always preview hidden tiles.
        const isAdminViewer = staffRole && staffRole.split(",").map(s=>s.trim()).indexOf("Admin") !== -1;
        const checklistVisible = !!(checklistConfig && checklistConfig.visibleToStaff) || isAdminViewer;
        const effectiveTools = TOOLS.map(t => applyTileOverride(applyFunctionMeta(t.id === "checklist" ? {...t, on: checklistVisible} : t, functionMetadata), tileOverrides));
        // Hidden tiles are dropped entirely — they don't even show up in
        // COMING SOON. Remaining tiles split into enabled (live) vs soon.
        const visibleTools = effectiveTools.filter(t => accFor(t).state !== "hidden");
        const avail = visibleTools.filter(t=>accFor(t).enabled);
        const soon = visibleTools.filter(t=>!accFor(t).enabled);
        const gridCols = isPortrait?"repeat(2,1fr)":"repeat(3,1fr)";
        return (<>
          <div className="rolled-grid" style={{...S.grid,gridTemplateColumns:gridCols}}>
            {avail.map((t,i)=>(
              <button key={t.id} className="rolled-card" style={{...S.card,...(isPortrait?{aspectRatio:"auto",padding:"1.2rem"}:{}),animationDelay:i*40+"ms"}} onClick={()=>setView(t.id)}>
                <div style={S.cTop}><div style={S.cIcon}>{t.icon}</div></div>
                <div style={S.cLabel}>{t.label}</div><div style={S.cDesc}>{t.desc}</div>
              </button>
            ))}
          </div>
          {soon.length>0 && (<>
            <div style={{display:"flex",alignItems:"center",gap:10,margin:"28px 4px 14px"}}>
              <div style={{flex:1,height:1,background:"rgba(212,251,111,0.12)"}}/>
              <span style={{fontFamily:"Manrope,sans-serif",fontSize:"0.65rem",fontWeight:700,color:B.kewpie,opacity:0.4,letterSpacing:"0.2em",textTransform:"uppercase"}}>Coming Soon</span>
              <div style={{flex:1,height:1,background:"rgba(212,251,111,0.12)"}}/>
            </div>
            <div className="rolled-grid" style={{...S.grid,gridTemplateColumns:gridCols}}>
              {soon.map((t,i)=>{
                const acc = accFor(t);
                return (
                  <button key={t.id} className="rolled-card" style={{...S.card,...(isPortrait?{aspectRatio:"auto",padding:"1.2rem"}:{}),...S.cardOff,animationDelay:(avail.length+i)*40+"ms"}} onClick={()=>{}} disabled>
                    <div style={S.cTop}><div style={S.cIcon}>{t.icon}</div>{acc.badge&&<span style={S.cSoon}>{acc.badge}</span>}</div>
                    <div style={S.cLabel}>{t.label}</div><div style={S.cDesc}>{t.desc}</div>
                  </button>
                );
              })}
            </div>
          </>)}
        </>);
      })()}
      <div style={S.hFoot}>Rolled Site App v0.5.5 · {SITE.name}</div>{PinModal}{ClockFlowScrim}{NotClockedInPromptModal}
    </div>
  );

  // ==== REVIEWS LEADERBOARD ====
  // Drill-down from the home reviews banner. Self-contained view —
  // doesn't go through the Tool Shell because it's not a "tool" the
  // user navigates to from the tile grid. Trafford is a Manchester
  // tablet, so the leaderboard exposes the 3-way site toggle
  // (Trafford / Arndale / Manchester combined).
  if (view === "reviews") {
    return (
      <div>
        <ReviewsTile
          defaultSiteKey={(SITE.reviews || {}).tileDefault}
          allowedSites={(SITE.reviews || {}).allowed}
          siteName={SITE.name}
          region={(SITE.reviews || {}).region}
          requestPin={requestPin}
          onBack={()=>setView("home")}
        />
        {PinModal}{ClockFlowScrim}{NotClockedInPromptModal}
      </div>
    );
  }

  // ==== TOOL SHELL with sidebar ====
  const __isAdminViewer = staffRole && staffRole.split(",").map(s=>s.trim()).indexOf("Admin") !== -1;
  const __checklistVisible = !!(checklistConfig && checklistConfig.visibleToStaff) || __isAdminViewer;
  const activeTools = TOOLS
    .map(t => applyTileOverride(applyFunctionMeta(t.id === "checklist" ? {...t, on: __checklistVisible} : t, functionMetadata), tileOverrides))
    .filter(t => toolAccessFor(t).state !== "hidden")
    .filter(t=>toolAccessFor(t).enabled);
  // A Square code up on the Club Card scanner counts too: once staff leave, it can't be shown again.
  const scannerCodeUp = ()=>view==="scanner"&&ccCodeHolds>0;
  const hasPending = ()=>(view==="report"&&reportDirty)||(view==="stock"&&stockPending)||(view==="ingredientwaste"&&wasteDirty)||scannerCodeUp();
  const goBack = ()=>{if(hasPending()){setPendingNavDest("home");setBackConfirm(true);return}setView("home")};
  const forceBack = ()=>{const dest=pendingNavDest;setBackConfirm(false);setReportDirty(false);setStockPending(false);setWasteDirty(false);setView(dest)};
  const navTo = (id)=>{if(id===view)return;if(hasPending()){setPendingNavDest(id);setBackConfirm(true);return}setView(id)};

  const pendingMsg = view==="ingredientwaste"&&wasteDirty
    ? "You have unsaved wastage figures. They haven't been saved yet."
    : view==="stock"&&stockPending
    ? "You have unsubmitted stock request selections. They haven't been sent yet."
    : view==="scanner"
    ? "It's already been redeemed, so it won't come back once you leave. Tap it in Square first, then Done — Next Customer."
    : "Your end of day report hasn't been submitted yet. Please submit it before leaving.";
  const BackConfirmModal = backConfirm ? (
    <div style={S.overlay}><div style={S.modal}>
      <div style={S.modalTitle}>{view==="ingredientwaste"?"Unsaved Wastage":view==="stock"?"Unsubmitted Selections":view==="scanner"?"Square code still on screen":"Unsubmitted Report"}</div>
      <div style={{fontSize:"0.85rem",color:B.kewpie,opacity:0.7,lineHeight:1.5,marginBottom:16}}>{pendingMsg}</div>
      <div style={S.modalBtns}><button style={S.modalCancel} onClick={forceBack}>Leave anyway</button><button style={S.modalOk} onClick={()=>setBackConfirm(false)}>{view==="scanner"?"Stay on the code":"Go back"}</button></div>
    </div></div>
  ) : null;

  const toolContent = {
    // key={dayKey} on day-scoped views forces a clean remount when the
    // day-rollover watchdog bumps dayKey, so internal form state (EOD waste
    // counters, TGTG entries, stock-request selections) starts fresh.
    report: <EndOfDayReport key={dayKey} site={SITE} backend={BACKEND} requestPin={requestPin} sellOutTimes={sellOutTimes} setDirty={setReportDirty} appData={appData} stockRequests={stockRequests} checklistConfig={checklistConfig}/>,
    sellout: <RollSellOut key={dayKey} site={SITE} backend={BACKEND} requestPin={requestPin} sellOutTimes={sellOutTimes} setSellOutTimes={setSellOutTimes} sellOutCycles={sellOutCycles} setSellOutCycles={setSellOutCycles}/>,
    forecast: <MeadowhallForecast backend={SITE.forecastBackend} requestPin={requestPin}/>,
    production: <ProductionForecast key={dayKey} site={SITE} backend={BACKEND} requestPin={requestPin} staffByPin={staffByPin}/>,
    inbound: <InboundDeliveries key={dayKey} site={SITE} backend={BACKEND} requestPin={requestPin} stockCatalog={stockCatalog}/>,
    ingredientwaste: <IngredientWastage key={dayKey} site={SITE} requestPin={requestPin} setDirty={setWasteDirty}/>,
    incident: <IncidentReport site={SITE} requestPin={requestPin}/>,
    stock: <StockRequests key={dayKey} site={SITE} backend={BACKEND} requestPin={requestPin} stockRequests={stockRequests} setStockRequests={setStockRequests} setStockPending={setStockPending} stockCatalog={stockCatalog} catalogSource={catalogSource}/>,
    deliveryOrders: <DeliveryOrders site={SITE} backend={BACKEND} requestPin={requestPin}/>,
    scanner: <ClubCardScanner session={ccSession} token={ccToken} requestPin={requestPin} backRef={scannerBackRef} onSignIn={logout} offline={ccTokenless && !ccToken}/>,
    checklist: <FOHChecklist site={SITE} backend={BACKEND} requestPin={requestPin} checklistConfig={checklistConfig} configSource={checklistCfgMeta&&checklistCfgMeta.source} configParity={checklistCfgMeta&&checklistCfgMeta.parity}/>,
    compliance: SITE.compliance === "full"
      ? <ComplianceModuleFull site={SITE} backend={BACKEND} requestPin={requestPin} staffByPin={staffByPin}/>
      : <ComplianceModule site={SITE} backend={BACKEND} requestPin={requestPin} staffByPin={staffByPin}/>,
    insights: <PreviousWeekInsights site={SITE} backend={BACKEND} availableSites={[{key:"tc",name:"Trafford"}]}/>,
    allergens: <AllergenInfo site={SITE} allergenCatalog={allergenCatalog} allergenCategories={allergenCategories}/>,
    allergensQR: <AllergensQRView/>,
    contacts: <EmergencyContacts site={SITE} backend={BACKEND}/>,
  };
  const currentTool = (t => t ? applyTileOverride(applyFunctionMeta(t, functionMetadata), tileOverrides) : t)(TOOLS.find(t=>t.id===view));

  return (
    <div style={S.shellRoot}>
      <div style={{...S.shellBar,position:"sticky",top:0,zIndex:100,background:B.nori}}>
        <button style={S.shellBack} onClick={goBack}>← Back</button>
        <span style={{fontFamily:"gelica,serif",fontSize:"1rem",fontWeight:700,color:B.wasabi}}>{currentTool&&currentTool.icon} {currentTool?currentTool.label:""}</span>
        <span style={S.shellSiteFull}>{SITE.name}</span>
      </div>
      <div style={{display:"flex",minHeight:"calc(100vh - 52px)"}}>
        {/* Collapsible sidebar — sticky, stays visible when scrolling */}
        <div style={{...S.sidebar,...(sideOpen?{}:S.sidebarCollapsed),position:"sticky",top:52,alignSelf:"flex-start",height:"calc(100vh - 52px)",overflowY:"auto"}}>
          <button style={S.sideToggle} onClick={()=>setSideOpen(o=>!o)} title={sideOpen?"Collapse menu":"Expand menu"} aria-label={sideOpen?"Collapse menu":"Expand menu"}>
            <span style={{display:"inline-block",transition:"transform 0.2s ease",transform:sideOpen?"rotate(0deg)":"rotate(180deg)"}}>◀</span>
          </button>
          {activeTools.map(t=>(
            <button key={t.id} style={{...S.sideItem,...(t.id===view?S.sideItemActive:{}),...(sideOpen?{}:S.sideItemCollapsed)}} onClick={()=>navTo(t.id)} title={sideOpen?"":t.label}>
              <span style={{fontSize:"1.05rem",marginRight:sideOpen?10:0,flexShrink:0}}>{t.icon}</span>
              {sideOpen&&<span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.label}</span>}
            </button>
          ))}
        </div>
        {/* Content — no overflowY here, page scrolls naturally so position:fixed modals always centre on viewport */}
        <div style={{flex:1,paddingTop:16,transition:"margin-left 0.2s ease"}}>
          {ccBackBanner}
          {toolContent[view] || <Placeholder label={currentTool?currentTool.label:""} icon={currentTool?currentTool.icon:"?"} msg="Coming soon."/>}
        </div>
      </div>
      {PinModal}{BackConfirmModal}{ClockFlowScrim}{NotClockedInPromptModal}
    </div>
  );
}

// ===========================================
// ROLL SELL OUT TIMES — reactive cycles (Undo + Restock), per-action PIN gates
// ===========================================
// Data model:
//   sellOutTimes: {sku: "HH:MM" | undefined} — "is this roll currently out?" snapshot, used by EOD discrepancy check + backend compat
//   sellOutCycles: {sku: [{soldOut:"HH:MM", restock:"HH:MM"|null}, ...]} — full history of sell-out → restock cycles for the day
// A SKU is currently OUT iff its last cycle exists AND last.restock is null.
// Every write action (Mark Sold Out, Restock, Undo) requires a PIN re-entry for audit.
// 15-minute window during which a Mark Sold Out can be undone on the kiosk.
// Past this, the Undo button is hidden — staff must escalate to JJ for any
// real correction. Stops end-of-shift "I changed my mind" undos and the
// stale-state ghost undos that bit us 2026-05-15.
const SELL_OUT_UNDO_WINDOW_MS = 15 * 60 * 1000;

// ============================================================
// DRUM-ROLL TIME PICKER (lifted from CmpTimePicker, dark-theme inline styles)
// ============================================================
// Native <input type="time"> on Android renders an unreadable circular
// clock face. This drum-roll picker is identical in mechanics to the
// compliance CmpTimePicker but with hardcoded brand styles so it works
// inside the dark sell-out confirm modal (compliance's CSS is scoped to
// .cmp-root which has a Kewpie background — wrong palette for here).
// Output is HH:MM 24h so downstream code is unchanged.
const SO_TIME_ITEM_H = 44;
const SO_TIME_VISIBLE = 5;
const SO_TIME_PAD = ((SO_TIME_VISIBLE - 1) / 2) * SO_TIME_ITEM_H;
const SO_TIME_CHEV_H = 26;

function soParseHHMM(v){
  if(!v) return {hour:9, minute:0, period:"AM"};
  const [h,m] = v.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12; if (hour12 === 0) hour12 = 12;
  return {hour: hour12, minute: m||0, period};
}
function soFormatHHMM({hour, minute, period}){
  let h24 = hour % 12;
  if (period === "PM") h24 += 12;
  return String(h24).padStart(2,"0") + ":" + String(minute).padStart(2,"0");
}

function SOTimeWheel({items, selectedIndex, onChange, render}){
  const ITEM_H = SO_TIME_ITEM_H;
  const VISIBLE = SO_TIME_VISIBLE;
  const PAD = SO_TIME_PAD;
  const ref = useRef(null);
  const settleTimer = useRef(null);
  const programmatic = useRef(false);
  const lastWheelAt = useRef(0);
  const [liveIdx, setLiveIdx] = useState(selectedIndex);
  useEffect(() => {
    if (!ref.current) return;
    const target = selectedIndex * ITEM_H;
    if (Math.abs(ref.current.scrollTop - target) > 1){
      programmatic.current = true;
      ref.current.scrollTop = target;
      setLiveIdx(selectedIndex);
      setTimeout(() => { programmatic.current = false; }, 60);
    }
  }, [selectedIndex]);
  const onScroll = () => {
    if (!ref.current) return;
    const liveRaw = ref.current.scrollTop / ITEM_H;
    const liveClamped = Math.max(0, Math.min(items.length - 1, Math.round(liveRaw)));
    if (liveClamped !== liveIdx) setLiveIdx(liveClamped);
    if (programmatic.current) return;
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      if (!ref.current) return;
      const idx = Math.round(ref.current.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      const targetTop = clamped * ITEM_H;
      if (Math.abs(ref.current.scrollTop - targetTop) > 0.5){
        programmatic.current = true;
        ref.current.scrollTo({top: targetTop, behavior:"smooth"});
        setTimeout(() => { programmatic.current = false; }, 240);
      }
      if (clamped !== selectedIndex) onChange(clamped);
    }, 110);
  };
  const onWheel = (e) => {
    e.preventDefault();
    const now = Date.now();
    if (now - lastWheelAt.current < 90) return;
    lastWheelAt.current = now;
    const dir = e.deltaY > 0 ? 1 : -1;
    onItemClick(Math.max(0, Math.min(items.length - 1, liveIdx + dir)));
  };
  const targetIdxRef = useRef(selectedIndex);
  useEffect(() => { targetIdxRef.current = selectedIndex; }, [selectedIndex]);
  const onItemClick = (i) => {
    if (!ref.current) return;
    targetIdxRef.current = i;
    programmatic.current = true;
    ref.current.scrollTo({top: i * ITEM_H, behavior:"smooth"});
    setLiveIdx(i);
    setTimeout(() => { programmatic.current = false; }, 240);
    if (i !== selectedIndex) onChange(i);
  };
  const step = (dir) => {
    const next = Math.max(0, Math.min(items.length - 1, targetIdxRef.current + dir));
    onItemClick(next);
  };
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",position:"relative"}}>
      <button type="button" tabIndex={-1} onClick={() => step(-1)} style={{height:SO_TIME_CHEV_H,padding:"0 14px",border:"none",background:"transparent",color:B.wasabi,fontSize:14,cursor:"pointer",fontFamily:"inherit",fontWeight:800,lineHeight:1,display:"flex",alignItems:"center"}}>▲</button>
      <div ref={ref}
        onScroll={onScroll}
        onWheel={onWheel}
        style={{
          height: VISIBLE * ITEM_H,
          overflowY:"scroll",
          width: 70,
          textAlign:"center",
          fontSize:22,
          fontWeight:700,
          color: B.kewpie,
          fontFamily:"inherit",
          msOverflowStyle:"none",
          scrollbarWidth:"none",
          touchAction:"pan-y",
          WebkitOverflowScrolling:"touch",
          position:"relative",
          overscrollBehavior:"contain"
        }}>
        <div style={{height: PAD}} />
        {items.map((it, i) => {
          const dist = Math.abs(i - liveIdx);
          return (
            <div key={i} onClick={() => onItemClick(i)} style={{
              height: ITEM_H,
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
              opacity: dist === 0 ? 1 : dist === 1 ? 0.55 : dist === 2 ? 0.28 : 0.15,
              transform: `scale(${dist === 0 ? 1 : 0.86})`,
              transition:"opacity 0.16s, transform 0.16s",
              cursor:"pointer",
              userSelect:"none"
            }}>{render ? render(it) : it}</div>
          );
        })}
        <div style={{height: PAD}} />
      </div>
      <button type="button" tabIndex={-1} onClick={() => step(1)} style={{height:SO_TIME_CHEV_H,padding:"0 14px",border:"none",background:"transparent",color:B.wasabi,fontSize:14,cursor:"pointer",fontFamily:"inherit",fontWeight:800,lineHeight:1,display:"flex",alignItems:"center"}}>▼</button>
    </div>
  );
}

function SOTimePicker({value, onChange}){
  const {hour, minute, period} = soParseHHMM(value);
  // 11↔12 boundary auto-flips AM/PM (crosses noon/midnight) — same rule as
  // the compliance picker so it feels identical.
  const setH = (idx) => {
    const newHour = idx + 1;
    let newPeriod = period;
    if (hour === 11 && newHour === 12) newPeriod = period === "AM" ? "PM" : "AM";
    else if (hour === 12 && newHour === 11) newPeriod = period === "AM" ? "PM" : "AM";
    onChange(soFormatHHMM({hour: newHour, minute, period: newPeriod}));
  };
  const setM = (idx) => onChange(soFormatHHMM({hour, minute: idx, period}));
  const setP = (idx) => onChange(soFormatHHMM({hour, minute, period: idx === 0 ? "AM" : "PM"}));
  return (
    <div>
      <div style={{textAlign:"center",fontFamily:"monospace",fontWeight:800,fontSize:32,color:B.kewpie,letterSpacing:"0.05em",margin:"6px 0 12px"}}>
        {hour}:{String(minute).padStart(2,"0")} <span style={{color:B.wasabi}}>{period}</span>
      </div>
      <div style={{position:"relative",padding:"4px 0 12px"}}>
        <div style={{
          position:"absolute",
          top: 4 + SO_TIME_CHEV_H + SO_TIME_PAD,
          left: 0, right: 0,
          height: SO_TIME_ITEM_H,
          background:"rgba(212,251,111,0.10)",
          borderTop:"1px solid rgba(212,251,111,0.45)",
          borderBottom:"1px solid rgba(212,251,111,0.45)",
          borderRadius:6,
          pointerEvents:"none",
          zIndex:0
        }} />
        <div style={{
          display:"grid",
          gridTemplateColumns:"1fr auto 1fr 1fr",
          alignItems:"stretch",
          justifyItems:"center",
          gap:14,
          position:"relative",
          zIndex:1
        }}>
          <SOTimeWheel items={Array.from({length:12},(_,i)=>i+1)} selectedIndex={hour-1} onChange={setH} />
          <div style={{
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            fontSize:30,
            fontWeight:800,
            color: B.wasabi,
            height: SO_TIME_CHEV_H * 2 + SO_TIME_VISIBLE * SO_TIME_ITEM_H,
            lineHeight:1
          }}>:</div>
          <SOTimeWheel items={Array.from({length:60},(_,i)=>i)} selectedIndex={minute} onChange={setM} render={n => String(n).padStart(2,"0")} />
          <SOTimeWheel items={["AM","PM"]} selectedIndex={period === "AM" ? 0 : 1} onChange={setP} />
        </div>
      </div>
    </div>
  );
}

function RollSellOut({site,backend,requestPin,sellOutTimes,setSellOutTimes,sellOutCycles,setSellOutCycles}){
  const [confirm,setConfirm]=useState(null); // {sku, mode: "markOut" | "restock"}
  const [undoConfirm,setUndoConfirm]=useState(null); // {sku, soldOut}
  const [manualTime,setManualTime]=useState("");
  const [sending,setSending]=useState({});
  const [readSource,setReadSource]=useState(null); // soak: which source drove this read ('supabase'|'sheet')
  // Tick every 30s so the Undo button auto-hides at the 15-min cutoff without
  // requiring user interaction. Only used as a re-render trigger.
  const [nowTick,setNowTick]=useState(Date.now());
  useEffect(()=>{const id=setInterval(()=>setNowTick(Date.now()),30000);return()=>clearInterval(id);},[]);
  const allSkus=SKU_LIST;

  // Lock body scroll when confirm/undo modals are open (uses a counter approach to avoid fighting with App's effect)
  useEffect(()=>{
    if(confirm||undoConfirm){document.body.classList.add("modal-open")}
    else{document.body.classList.remove("modal-open")}
    return()=>document.body.classList.remove("modal-open");
  },[confirm,undoConfirm]);

  // Server-authoritative hydrate. Sell-out marks live on the backend (Sell
  // Out Times + SellOutEvents sheets); fetch the reconstructed state on
  // mount + when the tab becomes visible again. Means any device opening
  // this screen sees the live state across the whole site — previously
  // local-state-only, Sophie's marks at Trafford were invisible from JJ's
  // home tablet (2026-05-15).
  useEffect(()=>{
    let cancelled=false;
    const load=()=>{
      // Read-flip (off by default): read today's sold-out state from Supabase
      // when selloutReadEnabled(), else the unchanged Google sheet feed. Both
      // resolve to the SAME {sellOutTimes, sellOutCycles} shape.
      const src=selloutSync.selloutReadEnabled()
        ? selloutSync.fetchSellOutToday(site.key,{sheetUrl:backend+"?action=sellOutToday&site="+site.key})
        : fetch(backend+"?action=sellOutToday&site="+site.key).then(r=>r.ok?r.json():null);
      src
        .then(d=>{
          if(cancelled||!d)return;
          if(d.sellOutTimes)setSellOutTimes(d.sellOutTimes);
          if(d.sellOutCycles)setSellOutCycles(d.sellOutCycles);
          if(d._source)setReadSource(d._source); // soak indicator: 'supabase' | 'sheet'
        })
        .catch(()=>{});
    };
    load();
    const onVis=()=>{if(!document.hidden)load();};
    document.addEventListener("visibilitychange",onVis);
    return()=>{cancelled=true;document.removeEventListener("visibilitychange",onVis);};
  },[site.key,backend]);

  const lastCycle=(sku)=>{const c=sellOutCycles[sku];return c&&c.length>0?c[c.length-1]:null};
  const isOut=(sku)=>{const lc=lastCycle(sku);return !!lc&&!lc.restock};
  const soldOutCount=allSkus.filter(sku=>isOut(sku)).length;

  const now=()=>{const n=new Date();return n.getHours().toString().padStart(2,"0")+":"+n.getMinutes().toString().padStart(2,"0")};
  const fmtDateBE=()=>{const td=new Date();return String(td.getDate()).padStart(2,"0")+"/"+String(td.getMonth()+1).padStart(2,"0")+"/"+td.getFullYear()};

  const startMark=(sku)=>{setConfirm({sku,mode:"markOut"});setManualTime(now())};
  const startRestock=(sku)=>{setConfirm({sku,mode:"restock"});setManualTime(now())};
  const startUndo=(sku)=>{const lc=lastCycle(sku);if(!lc)return;setUndoConfirm({sku,soldOut:lc.soldOut})};

  const confirmAction=()=>{
    if(!confirm)return;
    const{sku,mode}=confirm;const t=manualTime;setConfirm(null);
    // PIN-gate the write
    requestPin(async(staffName,staffPin)=>{
      setSending(s=>({...s,[sku]:true}));
      const fmtDate=fmtDateBE();
      // Snapshot for rollback if the confirmed write fails (Phase 1 write-hardening).
      const priorCycle=sellOutCycles[sku];const priorTime=sellOutTimes[sku];
      const revert=(msg,label)=>{
        setSellOutCycles(c=>{const n={...c};if(priorCycle===undefined)delete n[sku];else n[sku]=priorCycle;return n;});
        setSellOutTimes(x=>{const n={...x};if(priorTime===undefined)delete n[sku];else n[sku]=priorTime;return n;});
        notifyWriteFailed(sku+" "+label+" didn't save — "+msg);
      };
      if(mode==="markOut"){
        // loggedAt stamp powers the 15-min Undo window — Undo button hides
        // once the cycle is older than SELL_OUT_UNDO_WINDOW_MS (defined above).
        setSellOutCycles(c=>{const cur=c[sku]||[];return {...c,[sku]:[...cur,{soldOut:t,restock:null,loggedAt:new Date().toISOString()}]}});
        setSellOutTimes(x=>({...x,[sku]:t}));
        // Confirmed write: waits for the backend and rolls the screen back if it didn't land.
        const {ok}=await confirmedWrite({backend,action:"sellOutTimes",staffName,staffPin,payload:{site:site.key,date:fmtDate,times:JSON.stringify({[sku]:t})},onRollback:(msg)=>revert(msg,"sell-out"),
          // markOut appends a cycle and is NOT idempotent, so never false-rollback
          // (a re-tap would double-count demand). Re-poll to absorb the brief
          // append→visible lag (a single clean-but-stale read must not conclude
          // "didn't save"); keep the mark if the feed is unreadable.
          verify: () => confirmByReRead(backend+"?action=sellOutToday&site="+site.key, d=>skuSoldOut(d, sku), {onUnreadable:true})});
        // Audit-trail event to SellOutEvents — best-effort, only after the primary write confirmed.
        if(ok){
          confirmedWrite({backend,action:"sellOutRestock",staffName,staffPin,payload:{site:site.key,date:fmtDate,sku,restockTime:t,event:"sold_out"}});
          // Supabase shadow (dual-run) — one event row; the legacy mirror above is
          // deliberately NOT shadowed (the idem key would collapse it anyway).
          selloutSync.recordSoldOut({siteKey:site.key,sku,time:t,staffPin}); // enqueues durably; best-effort, never throws
        }
      }else{
        // restock: close the current open cycle
        setSellOutCycles(c=>{const cur=c[sku]||[];if(cur.length===0)return c;const upd=cur.slice();upd[upd.length-1]={...upd[upd.length-1],restock:t};return {...c,[sku]:upd}});
        setSellOutTimes(x=>{const n={...x};delete n[sku];return n});
        const {ok:rsOk}=await confirmedWrite({backend,action:"sellOutRestock",staffName,staffPin,payload:{site:site.key,date:fmtDate,sku,restockTime:t},onRollback:(msg)=>revert(msg,"restock"),
          // restock is idempotent (the backend no-ops a second restock), so a
          // real failure is safe to surface: goal is "no longer out". Re-poll to
          // absorb append→visible lag.
          verify: () => confirmByReRead(backend+"?action=sellOutToday&site="+site.key, d=>!skuSoldOut(d, sku))});
        if(rsOk){selloutSync.recordRestock({siteKey:site.key,sku,time:t,staffPin});} // enqueues durably; best-effort, never throws
      }
      setSending(s=>({...s,[sku]:false}));
    });
  };

  const confirmUndo=()=>{
    if(!undoConfirm)return;
    const{sku,soldOut}=undoConfirm;setUndoConfirm(null);
    // PIN-gate + log the undo attempt to backend (audit trail — prevents hiding sell-outs silently)
    requestPin(async(staffName,staffPin)=>{
      setSending(s=>({...s,[sku]:true}));
      const fmtDate=fmtDateBE();
      const undoTime=now();
      // Snapshot for rollback if the confirmed write fails (Phase 1 write-hardening).
      const priorCycle=sellOutCycles[sku];const priorTime=sellOutTimes[sku];
      // Remove the last cycle functionally — read inside the updater to avoid stale closure
      setSellOutCycles(c=>{
        const cur=c[sku]||[];
        if(cur.length===0)return c;
        const arr=cur.slice(0,-1);
        if(arr.length===0){const n={...c};delete n[sku];return n}
        return {...c,[sku]:arr};
      });
      // Use a ref-like approach: read the UPDATED cycles (post-setSellOutCycles) by computing from the undo itself
      // The previous cycle is the one before the one we just removed
      setSellOutTimes(x=>{
        const n={...x};
        // After removing the last cycle, check if there's a previous open (unrestock'd) cycle
        // We know we just removed the last one, so look at second-to-last from the ORIGINAL array
        const origCycles=sellOutCycles[sku]||[];
        const remaining=origCycles.slice(0,-1);
        const lastRemaining=remaining.length>0?remaining[remaining.length-1]:null;
        if(lastRemaining&&!lastRemaining.restock){n[sku]=lastRemaining.soldOut}else{delete n[sku]}
        return n;
      });
      // Fresh backend baseline BEFORE the undo: local cycle state can be stale on
      // a shared multi-tablet kiosk, so confirm a real DECREASE from what the
      // backend shows now, not from a local count. Unreadable baseline -> keep.
      const undoBaseline = await (async()=>{ const d = await fetchJsonWithRetry(backend+"?action=sellOutToday&site="+site.key); return d ? sellOutCycleCount(d, sku) : null; })();
      const {ok:unOk}=await confirmedWrite({backend,action:"sellOutUndo",staffName,staffPin,payload:{site:site.key,date:fmtDate,sku,originalSoldOutTime:soldOut,undoTime},onRollback:(msg)=>{
        setSellOutCycles(c=>{const n={...c};if(priorCycle===undefined)delete n[sku];else n[sku]=priorCycle;return n;});
        setSellOutTimes(x=>{const n={...x};if(priorTime===undefined)delete n[sku];else n[sku]=priorTime;return n;});
        notifyWriteFailed(sku+" undo didn't save — "+msg);
      },
        // undo pops the LAST cycle and is NOT idempotent (a second undo would pop
        // an unrelated cycle), so NEVER false-rollback. A landed undo shows a
        // STRICT DECREASE from the pre-undo baseline; re-poll absorbs the lag.
        verify: async () => { if(undoBaseline==null||undoBaseline<=0) return true; return confirmByReRead(backend+"?action=sellOutToday&site="+site.key, d=>sellOutCycleCount(d, sku) < undoBaseline, {onUnreadable:true}); }});
      // Supabase shadow (dual-run) — pairs with the sold-out via the original time.
      if(unOk){selloutSync.recordSellOutUndo({siteKey:site.key,sku,originalTime:soldOut,undoTime,staffPin});} // enqueues durably; best-effort, never throws
      setSending(s=>({...s,[sku]:false}));
    });
  };

  return (
    <div style={S.content}>
      <div style={S.sectionCard}><div style={S.secLabel}>Roll Sell Out Times</div><div style={S.secDesc}>Tap Mark Sold Out when a roll runs out. If more arrives later, tap Restock. Each action is PIN-logged.</div></div>
      {/* Soak indicator — only on a flipped kiosk (selloutReadEnabled). At-a-glance
          confirmation of which source drove this read: green = live Supabase,
          amber = fell back to the Google sheet (Supabase read errored). */}
      {selloutSync.selloutReadEnabled()&&readSource&&<div style={{fontSize:"0.68rem",fontWeight:600,marginBottom:10,display:"flex",alignItems:"center",gap:6,color:readSource==="supabase"?B.eucalyptus:B.coral}}><span style={{fontSize:"0.9rem",lineHeight:1}}>●</span>{readSource==="supabase"?"Live — sell-outs connected":"⚠ Fallback — sell-outs from the backup copy"}</div>}
      <div style={{...S.infoBanner,background:"rgba(255,122,92,0.08)",borderColor:"rgba(255,122,92,0.25)",marginBottom:12}}><span style={{color:B.coral,fontWeight:700}}>Only mark sold out if the roll ran out BEFORE TGTG.</span> <span style={{color:B.kewpie,opacity:0.75}}>Rolls used in TGTG bags don't count — that's planned surplus, not demand.</span></div>
      {soldOutCount>0&&<div style={{...S.infoBanner,background:"rgba(212,251,111,0.08)",borderColor:"rgba(212,251,111,0.2)",marginBottom:12}}><span style={{color:B.wasabi,fontWeight:700}}>{soldOutCount} roll{soldOutCount>1?"s":""} currently sold out</span></div>}
      {allSkus.map(sku=>{
        const out=isOut(sku);const last=lastCycle(sku);const isSending=sending[sku];
        const history=sellOutCycles[sku]||[];
        return (<div key={sku} style={{...S.soRow,...(out?{background:"rgba(255,122,92,0.08)",borderColor:"rgba(255,122,92,0.2)"}:{})}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={S.itemName}>{sku}</div>
            {out&&<div style={{fontSize:"0.72rem",color:B.coral,fontWeight:600,marginTop:2}}>Sold out at {last.soldOut}{history.length>1&&" · cycle "+history.length}</div>}
            {!out&&last&&last.restock&&<div style={{fontSize:"0.72rem",color:B.eucalyptus,fontWeight:600,marginTop:2}}>Restocked at {last.restock}{history.length>1&&" · "+history.length+" cycles"}</div>}
          </div>
          {out?(
            <div style={{display:"flex",gap:6,flexShrink:0}}>
              {last && last.loggedAt && (nowTick - new Date(last.loggedAt).getTime() < SELL_OUT_UNDO_WINDOW_MS) &&
                <button style={S.undoBtn} onClick={()=>startUndo(sku)} disabled={isSending}>Undo</button>}
              <button style={S.restockBtn} onClick={()=>startRestock(sku)} disabled={isSending}>{isSending?"...":"Restock"}</button>
            </div>
          ):(<button style={S.markBtn} onClick={()=>startMark(sku)} disabled={isSending}>{isSending?"...":"Mark Sold Out"}</button>)}
        </div>);
      })}
      {confirm&&(<div style={S.overlay}><div style={S.modal}>
        <div style={S.modalTitle}>{confirm.mode==="markOut"?"Confirm Sell Out":"Confirm Restock"}</div>
        <div style={{fontSize:"0.9rem",color:B.kewpie,marginBottom:16}}>
          {confirm.mode==="markOut"?"Log ":"Restock "}<strong style={{color:B.wasabi}}>{confirm.sku}</strong>{confirm.mode==="markOut"?" as sold out at:":" at:"}
        </div>
        <SOTimePicker value={manualTime} onChange={setManualTime}/>
        <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.4,marginBottom:16,textAlign:"center"}}>Time auto-filled to now. Scroll the wheels to change.</div>
        {confirm.mode==="markOut"&&<div style={{fontSize:"0.78rem",color:B.coral,fontWeight:600,lineHeight:1.4,padding:"10px 12px",background:"rgba(255,122,92,0.08)",border:"1px solid rgba(255,122,92,0.25)",borderRadius:8,marginBottom:16}}>Only confirm if this roll ran out BEFORE TGTG. Rolls used in TGTG bags don't count as sold out.</div>}
        <div style={S.modalBtns}><button style={S.modalCancel} onClick={()=>setConfirm(null)}>Cancel</button><button style={S.modalOk} onClick={confirmAction}>Confirm</button></div>
      </div></div>)}
      {undoConfirm&&(<div style={S.overlay}><div style={S.modal}>
        <div style={S.modalTitle}>Undo Sell Out?</div>
        <div style={{fontSize:"0.88rem",color:B.kewpie,marginBottom:16,lineHeight:1.5}}>
          This will remove the <strong style={{color:B.wasabi}}>{undoConfirm.sku}</strong> sell-out logged at <strong style={{color:B.wasabi}}>{undoConfirm.soldOut}</strong>. The undo attempt is logged with your PIN for audit.
        </div>
        <div style={S.modalBtns}><button style={S.modalCancel} onClick={()=>setUndoConfirm(null)}>Cancel</button><button style={S.modalOk} onClick={confirmUndo}>Confirm Undo</button></div>
      </div></div>)}
    </div>
  );
}

// ===========================================
// END OF DAY REPORT
// ===========================================
// Read-only FOH 67 Checklist completion percentage at the bottom of the EOD
// Report. Just the overall % — JJ doesn't want a full breakdown here.
// Hides itself on sites without checklist config (Meadowhall).
//
// Loads optimistically from localStorage cache keyed by site+date, then
// revalidates in the background. User sees the % immediately (no loading
// spinner) — the fetch only matters if ticks landed since the cache was last
// written. Cache is also populated by the FOH 67 Checklist tile's loadDay
// so opening EOD right after the tile is instant.
function ChecklistSummaryEOD({site,backend,checklistConfig}){
  const cfg = checklistConfig || {};
  const tiers = cfg.tiers || [];
  const sections = cfg.sections || [];
  const hasChecklist = tiers.length > 0 && sections.length > 0;
  const cacheKey = "checklistEventsCache::" + site.key;
  const countsCacheKey = "checklistDailyCountsCache::" + site.key;
  const todayStr = todayKey();  // London day (was UTC) — the checklist % cache must key on the same London day as the authoritative checklist_day read

  const [events,setEvents] = useState(()=>{
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.date === todayStr && Array.isArray(parsed.events)) return parsed.events;
    } catch(e){}
    return null;
  });

  // RAK + Google review counts for today. Separate cache key from the events
  // cache because the FOH Checklist tile's loadDay writes the events cache on
  // a 15s poll without these counts; collapsing them into the same JSON would
  // cause the tile to silently clobber the EOD block's counts.
  const [counts,setCounts] = useState(()=>{
    try {
      const raw = localStorage.getItem(countsCacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.date === todayStr) return { rak: Number(parsed.rak)||0, reviews: Number(parsed.reviews)||0 };
    } catch(e){}
    return null;
  });

  useEffect(()=>{
    if (!hasChecklist) return;
    let cancelled = false;
    // Shared with the tile and the login pre-warm — joins a day read already in
    // flight instead of starting another copy of the slow Sheet walk.
    checklistSrc.fetchChecklistDayShared({ sheetUrl: backend + "?action=checklistday&site=" + site.key, site: site.key })
      .then(r => {
        const d = r && r.day;
        if (cancelled || !d || !Array.isArray(d.events)) return;
        setEvents(d.events);
        const rak = Array.isArray(d.randomActs) ? d.randomActs.length : 0;
        const reviews = Array.isArray(d.manualReviews) ? d.manualReviews.length : 0;
        setCounts({rak, reviews});
        try { localStorage.setItem(cacheKey, JSON.stringify({date: todayStr, events: d.events, ts: Date.now()})); } catch(e){}
        try { localStorage.setItem(countsCacheKey, JSON.stringify({date: todayStr, rak, reviews})); } catch(e){}
      })
      .catch(()=>{});
    return ()=>{ cancelled = true; };
  },[site.key,backend,hasChecklist,cacheKey,countsCacheKey,todayStr]);

  if (!hasChecklist) return null;

  let overallPct = null;
  if (events !== null) {
    const tickMap = {};
    events.forEach(e=>{
      if (e.action === "undo") return;
      if (!tickMap[e.tierOrder]) tickMap[e.tierOrder] = {};
      if (!tickMap[e.tierOrder][e.section]) tickMap[e.tierOrder][e.section] = {};
      tickMap[e.tierOrder][e.section][e.task] = e;
    });
    const totalTasks = sections.reduce((n,s)=>n + s.tasks.length, 0);
    const overall = tiers.reduce((n,t)=>n + sections.reduce((m,s)=>m + s.tasks.filter(task=>tickMap[t.order]&&tickMap[t.order][s.name]&&tickMap[t.order][s.name][task.name]).length, 0), 0);
    const overallTotal = totalTasks * tiers.length;
    overallPct = overallTotal ? Math.round((overall/overallTotal)*100) : 0;
  }

  // Contributors — unique staff names from today's non-undo ticks.
  // Accountability signal: admin/manager glances at the EOD and sees exactly
  // who was on the tool. Drops optimistic/anon entries ("you", empty string).
  const contributors = [];
  if (events) {
    const seen = {};
    events.forEach(e=>{
      if (e.action === "undo") return;
      const name = String(e.staff || "").trim();
      if (!name || name === "you" || seen[name]) return;
      seen[name] = true;
      contributors.push(name);
    });
  }

  // Three-band traffic-light indicator. Wasabi (brand green) for high,
  // Sunshine (brand yellow) for medium, Coral for low. Ring colour follows
  // the same bands for consistency with the badge.
  let status = null;
  if (overallPct !== null) {
    if (overallPct >= 75)        status = { label: "High",   color: B.wasabi,   bg: "rgba(212,251,111,0.12)" };
    else if (overallPct >= 50)   status = { label: "Medium", color: B.sunshine, bg: "rgba(241,197,68,0.14)" };
    else                         status = { label: "Low",    color: B.coral,    bg: "rgba(255,122,92,0.12)" };
  }

  const ringCircumference = 226; // matches FOH Checklist header ring (r=36)
  const ringPct = overallPct === null ? 0 : overallPct;
  const ringColor = overallPct === null ? "rgba(212,251,111,0.4)" : (overallPct >= 75 ? B.wasabi : overallPct >= 50 ? B.sunshine : B.coral);

  return (
    <>
      <div style={S.secTitle}>FOH 67 Checklist <span style={{marginLeft:6,color:B.kewpie,opacity:0.3,fontWeight:400,textTransform:"none",letterSpacing:0}}>(read-only)</span></div>
      <div style={{...S.unclearedBox,padding:"14px 16px"}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{width:80,height:80,position:"relative",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width="80" height="80" viewBox="0 0 80 80" style={{transform:"rotate(-90deg)"}}>
              <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(212,251,111,0.15)" strokeWidth="9"/>
              <circle cx="40" cy="40" r="36" fill="none" stroke={ringColor} strokeWidth="9" strokeLinecap="round"
                strokeDasharray={ringCircumference} strokeDashoffset={ringCircumference - (ringPct/100)*ringCircumference}
                style={{transition:"stroke-dashoffset 0.4s ease"}}/>
            </svg>
            <div style={{position:"absolute",fontSize:"1.05rem",fontWeight:800,color:ringColor,fontFamily:"gelica,serif"}}>{overallPct === null ? "—" : overallPct + "%"}</div>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.55,textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:600,marginBottom:4}}>Today's completion</div>
            {status && (
              <div style={{display:"inline-block",padding:"3px 10px",borderRadius:999,background:status.bg,color:status.color,fontSize:"0.72rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>{status.label}</div>
            )}
          </div>
        </div>
        {contributors.length > 0 && (
          <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid rgba(212,251,111,0.06)"}}>
            <div style={{fontSize:"0.68rem",color:B.kewpie,opacity:0.5,textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:600,marginBottom:5}}>Contributors ({contributors.length})</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
              {contributors.map(n=>(
                <span key={n} style={{padding:"3px 8px",background:"rgba(212,251,111,0.06)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:999,fontSize:"0.72rem",color:B.kewpie,opacity:0.85}}>{n}</span>
              ))}
            </div>
          </div>
        )}
        {events !== null && contributors.length === 0 && (
          <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid rgba(212,251,111,0.06)",fontSize:"0.72rem",color:B.kewpie,opacity:0.4,fontStyle:"italic"}}>No contributors yet today</div>
        )}
        {counts && (
          <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid rgba(212,251,111,0.06)",display:"flex",gap:8,flexWrap:"wrap"}}>
            {[
              {key:"rak",     icon:"💛", label:"Random Acts",  count:counts.rak},
              {key:"reviews", icon:"⭐", label:"5★ Reviews",    count:counts.reviews}
            ].map(p=>{
              const pass = p.count > 0;
              const col = pass ? B.wasabi : B.coral;
              const bg  = pass ? "rgba(212,251,111,0.12)" : "rgba(255,122,92,0.10)";
              const bd  = pass ? "rgba(212,251,111,0.25)" : "rgba(255,122,92,0.25)";
              return (
                <div key={p.key} style={{flex:"1 1 140px",minWidth:0,padding:"8px 12px",borderRadius:10,background:bg,border:"1px solid "+bd,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:"1.2rem",flexShrink:0}}>{p.icon}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"0.6rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:col,opacity:0.85,marginBottom:2}}>{p.label}</div>
                    <div style={{fontFamily:"gelica,serif",fontSize:"1.25rem",fontWeight:700,color:col,lineHeight:1}}>{p.count}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function EndOfDayReport({site,backend,requestPin,sellOutTimes,setDirty,appData:ad,stockRequests,checklistConfig}){
  const [waste,setWaste]=useState(ad.waste);
  const [tgtgSold,setTgtgSold]=useState(ad.tgtgSold);
  const [tgtgNC,setTgtgNC]=useState(ad.tgtgNC);
  const [status,setStatus]=useState(ad.reportSubmitted?"done":null);
  const [statusMsg,setStatusMsg]=useState("");
  const [undoing,setUndoing]=useState(false);

  // ===========================================
  // EOD TIME GATE — hybrid: hard-lock / warn / free / admin override
  // ===========================================
  // Fetched from `?action=eodtimegate` (cached in localStorage) + polled every
  // 60s so admin edits + emergency unlock propagate without a tablet reload.
  // State derives from close time for today's day-of-week + earlyHours offset;
  // admin override (today-scoped) bypasses hard-lock for the whole day.
  const [timeGate,setTimeGate] = useState(()=>{
    try{const c=localStorage.getItem("eodTimeGateCache");if(c){const p=JSON.parse(c);if(p&&p.sites)return p}}catch(e){}
    return null;
  });
  useEffect(()=>{
    let cancelled=false;
    const fetchGate = async ()=>{
      try {
        const r = await fetch(backend+"?action=eodtimegate");
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled||!d||!d.sites) return;
        setTimeGate(d);
        try{localStorage.setItem("eodTimeGateCache",JSON.stringify(d))}catch(e){}
      } catch(e){}
    };
    fetchGate();
    const id = setInterval(fetchGate, 60000);
    return ()=>{cancelled=true;clearInterval(id);};
  },[backend]);
  // Trigger a re-render every 30s so the clock-dependent gateState refreshes
  // across window/warn/free boundaries without the user having to interact.
  const [,setNowTick] = useState(0);
  useEffect(()=>{
    const id = setInterval(()=>setNowTick(t=>t+1), 30000);
    return ()=>clearInterval(id);
  },[]);
  const _dayOrder = ["mon","tue","wed","thu","fri","sat","sun"];
  const _fmtMin = m => String(Math.floor(m/60)).padStart(2,"0")+":"+String(m%60).padStart(2,"0");
  const gateState = (()=>{
    if (!timeGate || !timeGate.sites || !timeGate.sites[site.key]) return { name:"free" };
    const cfg = timeGate.sites[site.key];
    const todayIso = new Date().toISOString().slice(0,10);
    const overrideActive = !!(timeGate.override && timeGate.override[site.key] && timeGate.override[site.key].date === todayIso);
    if (overrideActive) return { name:"override" };
    const now = new Date();
    const todayKey = _dayOrder[(now.getDay()+6)%7];
    const closeStr = cfg.close && cfg.close[todayKey];
    if (!closeStr || !/^\d{1,2}:\d{2}$/.test(closeStr)) return { name:"free" };
    const [ch,cm] = closeStr.split(":").map(Number);
    const closeMin = ch*60+cm;
    const warnMin = Math.max(0, closeMin - Math.round((Number(cfg.earlyHours)||2)*60));
    const nowMin = now.getHours()*60+now.getMinutes();
    if (nowMin < warnMin) return { name:"locked", closeStr, warnStartStr:_fmtMin(warnMin), earlyHours: Number(cfg.earlyHours)||2 };
    if (nowMin < closeMin) return { name:"warn",   closeStr, warnStartStr:_fmtMin(warnMin) };
    return { name:"free", closeStr };
  })();
  const isGateLocked   = gateState.name === "locked";
  const isGateWarn     = gateState.name === "warn";
  const isGateOverride = gateState.name === "override";
  const [gateConfirmOpen,setGateConfirmOpen]=useState(false);
  // Real server-side reversal. POSTs undoDailyReport, awaits a success
  // response, and only then clears local + serverEod state. Previously
  // this button was client-state-only — see memory rule
  // `undo_must_be_real.md` for why that's never acceptable.
  const undoSubmission = () => requestPin(async(staffName,staffPin)=>{
    if (undoing) return;
    setUndoing(true);
    setStatusMsg("Undoing submission...");
    try {
      // Lane CD: on a shop that has been switched off Google (EMPTY list today, so
      // this branch is dead on every tablet), the Undo is a real reversal through the
      // 1480 door — it flips today's report to 'undone' and the database stamps who
      // and when. "There was nothing to take back" is said out loud: on a dropped shop
      // there is no sheet left to fall back on.
      if(eodWriteDrop.eodWriteDropEnabled()){
        const _undoRes=await eodWriteDrop.undoEodThroughDoor({siteKey:site.key,staffPin});
        if(_undoRes.ok){
          setStatus(null);
          setServerEod(null);
          ad.reportSubmitted = false;
          persistAppData();
          setStatusMsg("Submission undone by "+staffName+" — you can now re-edit and resubmit");
        } else {
          setStatusMsg(eodWriteDrop.eodUndoFailureMessage(_undoRes));
        }
        return;
      }
      const today = new Date().toLocaleDateString("en-GB");
      // Simple-request POST (no Content-Type) avoids CORS preflight — Apps
      // Script doPost reads e.postData.contents regardless of content type.
      const r = await fetch(backend, {
        method: "POST",
        redirect: "follow",
        body: JSON.stringify({action:"undoDailyReport",site:site.key,date:today,staffName,staffPin})
      });
      const result = await r.json().catch(()=>null);
      if (result && result.status === "success") {
        setStatus(null);
        setServerEod(null);
        ad.reportSubmitted = false;
        persistAppData();
        setStatusMsg("Submission undone by "+staffName+" — you can now re-edit and resubmit");
        // Supabase shadow (dual-run) — only after the old backend confirmed the undo.
        try{selloutSync.recordEodUndo({siteKey:site.key,staffPin});}catch(_){}
      } else {
        setStatusMsg("Undo failed: "+((result && result.message) || "server did not confirm"));
      }
    } catch (e) {
      setStatusMsg("Undo failed — check connection and try again");
    } finally {
      setUndoing(false);
    }
  });
  // Backend-authoritative "already submitted" state — prevents the silent
  // double-submission scenario where a local tablet's reportSubmitted flag
  // was stale and staff thought they'd submitted but hadn't (the 2026-04-22
  // 14:00 timestamp mystery). Nothing here depends on local state.
  const [serverEod,setServerEod]=useState(null);
  useEffect(()=>{
    let cancelled=false;
    // Read-flip (off by default): read the End of Day report from Supabase when
    // eodReadEnabled(), else the unchanged sheet. The Supabase reader falls
    // back to the SAME sheet call when Supabase has no active report today, and
    // the EOD-undo self-heal keeps a stale close from re-closing a corrected day.
    // Its OWN gate since 11 Aug — this used to ride on selloutReadEnabled(), which
    // meant Meadowhall's EOD could not flip until its till capture rate was fixed.
    const eodSrc=selloutSync.eodReadEnabled()
      ? selloutSync.fetchEodToday(site.key,{sheetUrl:backend+"?action=eodToday&site="+site.key})
      : fetch(backend+"?action=eodToday&site="+site.key).then(r=>r.ok?r.json():null);
    eodSrc
      .then(d=>{
        if(cancelled||!d)return;
        setServerEod(d);
        if(d.submitted){
          setStatus("done");
          ad.reportSubmitted=true;persistAppData();
          const t=d.timestamp?new Date(d.timestamp).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}):"";
          setStatusMsg("Submitted by "+(d.staffName||"staff")+(t?" at "+t:""));
          // Hydrate the form with what was actually submitted so staff can
          // see the values at a glance. wasteByItem is a JSON string of
          // {SKU: qty}; fall back to parsing the human-readable summary if
          // legacy rows predate the WasteByItem column.
          let parsedWaste=null;
          try { parsedWaste=d.wasteByItem?JSON.parse(d.wasteByItem):null; } catch(e) { parsedWaste=null; }
          if(!parsedWaste||typeof parsedWaste!=="object"){
            parsedWaste={};
            String(d.waste||"").split(",").forEach(seg=>{
              const m=seg.trim().match(/^(\d+)\s+(.+)$/);
              if(m) parsedWaste[m[2].trim()]=Number(m[1]);
            });
          }
          const hydrated={};
          SKU_LIST.forEach(sku=>{ hydrated[sku]=Number(savedCount(parsedWaste,sku))||0; });
          setWaste(hydrated);
          setTgtgSold(Number(d.tgtgSold)||0);
          setTgtgNC(Number(d.tgtgNotCollected)||0);
        }
      })
      .catch(()=>{});
    return ()=>{cancelled=true;};
  },[site.key,backend]);

  // Persist to appData + localStorage
  useEffect(()=>{ad.waste=waste;ad.tgtgSold=tgtgSold;ad.tgtgNC=tgtgNC;persistAppData()},[waste,tgtgSold,tgtgNC]);

  const totalWaste=Object.values(waste).reduce((a,b)=>a+b,0);
  const tgtgReady = tgtgSold != null && tgtgNC != null;
  const isDirty=status!=="done"&&(totalWaste>0||(tgtgSold!=null&&tgtgSold>0)||(tgtgNC!=null&&tgtgNC>0));
  useEffect(()=>{setDirty(isDirty)},[isDirty,setDirty]);

  const adj=(sku,d)=>{ setWaste(w=>({...w,[sku]:Math.max(0,w[sku]+d)})); };
  // Null = not yet entered. First + bumps to 1, first − sets to 0 (so staff
  // can type "zero" explicitly by tapping the − once).
  const adjTgtgSold=d=>{ setTgtgSold(v=>v==null?(d>0?1:0):Math.max(0,v+d)); };
  const adjTgtgNC  =d=>{ setTgtgNC  (v=>v==null?(d>0?1:0):Math.max(0,v+d)); };
  const discrepancies=SKU_LIST.filter(sku=>waste[sku]>0&&sellOutTimes[sku]);

  // Guarded by the time gate. onSubmitClick (below) routes to either this
  // directly (free state), the confirmation modal (warn / override), or
  // no-ops (locked). The isGateLocked bail here is defence-in-depth so a
  // stale rendering can't sneak a submission through mid-window flip.
  const submit=()=>{ if (isGateLocked) return;
    if (!tgtgReady) { setStatus("error"); setStatusMsg("Set TGTG Bags Sold and Not Collected before submitting (tap − once for zero)."); return; }
    requestPin(async(staffName,staffPin)=>{
    setStatus("loading");const we=[];for(const k in waste)if(waste[k]>0)we.push(waste[k]+" "+k);
    // Lane BE Wave 3: when the till reads Supabase, FREEZE the open stock requests
    // into column 12 (OpenStockRequestsSnapshot) from the live Supabase read, so the
    // EOD snapshot stays honest once the Sheet stock write is dropped (today the kiosk
    // sends nothing, so column 12 is blank). Sent ONLY when the read genuinely came
    // from Supabase — never a stale-Sheet fallback, never a fabricated empty list
    // (null omits the field, leaving today's blank). Best-effort: never blocks the EOD.
    let openStockRequestsSnapshot=null;
    try{
      if(stockReader.stockReadEnabled()){
        // ccOverlay:false — the snapshot uses none of the CC-ordered overlay marks, and the
        // overlay's cold path is a blocking 5-20s fetch to the slow Apps Script getStockRequests
        // feed; keeping it off the EOD submit path (this reader is on stockReadEnabled(), already
        // ON estate-wide, so it runs the moment this deploys). See the review note on PR #324.
        const _snapRes=await stockReader.fetchStockRequests(site.key,{sheetUrl:backend+"?action=getStockRequests&site="+site.key,ccOverlay:false});
        if(_snapRes && String(_snapRes._source||"").startsWith("supabase")) openStockRequestsSnapshot=openStockSnapshotFromFeed(_snapRes.requests);
      }
    }catch(e){ openStockRequestsSnapshot=null; }
    // Lane CD: on a shop that has been switched off Google (EOD_WRITE_DROP_HOSTS —
    // EMPTY today, so this branch is dead on every tablet), the report goes STRAIGHT
    // into Supabase through the 1480 door and is AWAITED. No Apps Script POST, and no
    // quiet fallback to it: a failure is shown in plain words and the form is kept so
    // the staff member can tap Submit again. The open-stock-requests snapshot rides
    // into the Supabase row here instead of into the sheet's column 12.
    if(eodWriteDrop.eodWriteDropEnabled()){
      const _dropRes=await eodWriteDrop.submitEodThroughDoor({siteKey:site.key,staffPin,
        wasteSummary:we.join(", ")||"None",wasteByItem:waste,tgtgSold,tgtgNotCollected:tgtgNC,
        gateState:gateState.name,stockSnapshot:openStockRequestsSnapshot});
      if(_dropRes.ok){setStatus("done");ad.reportSubmitted=true;persistAppData();
        setStatusMsg("Report logged at "+new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})+" by "+staffName+(_dropRes.duplicate?" (today's report was already in)":""));}
      else{setStatus("error");setStatusMsg(eodWriteDrop.eodSubmitFailureMessage(_dropRes));}
      return;
    }
    // verify: on an uncertain reply (flaky /exec HTML-200), re-read eodToday and
    // confirm today's report actually landed rather than trusting a non-JSON 200.
    // The POST stays single-shot so a re-submit can't create a duplicate EOD row.
    const {ok,errMsg}=await confirmedWrite({backend,action:"dailyReport",staffName,staffPin,payload:{site:site.key,date:new Date().toLocaleDateString("en-GB"),waste:we.join(", ")||"None",tgtgSold,tgtgNotCollected:tgtgNC,wasteByItem:JSON.stringify(waste),...(openStockRequestsSnapshot!==null?{openStockRequestsSnapshot}:{})},
      // eodSubmitted is "a report exists for today" (not this exact submission) —
      // fine: the on-mount eodToday read makes a second tablet see it's already in,
      // and the backend dedupes per (site,date), so a re-submit is safe.
      verify: () => confirmByReRead(backend+"?action=eodToday&site="+site.key, eodSubmitted)});
    if(ok){setStatus("done");ad.reportSubmitted=true;persistAppData();setStatusMsg("Report logged at "+new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})+" by "+staffName);
      // Supabase shadow (dual-run) — carries the gate state the old payload never had.
      try{selloutSync.recordEodSubmit({siteKey:site.key,staffPin,wasteSummary:we.join(", ")||"None",wasteByItem:waste,tgtgSold,tgtgNotCollected:tgtgNC,gateState:gateState.name});}catch(_){}
    }
    else{setStatus("error");setStatusMsg("Didn't save — "+errMsg);}
  })};

  return (
    <div style={S.content}>
      <div style={S.sectionCard}><div style={S.secLabel}>End of Day Report</div><div style={S.secDesc}>Log wastage and TGTG. Stock requests are captured separately in the Stock Requests tool.</div></div>
      {serverEod&&serverEod.submitted&&(
        <div style={{background:"rgba(111,175,143,0.15)",border:"1.5px solid "+B.eucalyptus,borderRadius:14,padding:"14px 18px",marginBottom:14,display:"flex",alignItems:"center",gap:14}}>
          <div style={{fontSize:28,lineHeight:1}}>✓</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:"gelica,serif",fontSize:"1.05rem",fontWeight:700,color:B.eucalyptus,lineHeight:1.2}}>End of Day submitted</div>
            <div style={{fontSize:"0.82rem",color:B.kewpie,opacity:0.85,marginTop:4}}>by <strong>{serverEod.staffName||"staff"}</strong>{serverEod.timestamp?" at "+new Date(serverEod.timestamp).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}):""} · {serverEod.date||""}</div>
            <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.5,marginTop:3}}>If you need to correct it, scroll down and tap Undo.</div>
          </div>
        </div>
      )}
      {discrepancies.length>0&&<div style={{...S.infoBanner,background:"rgba(255,122,92,0.1)",borderColor:"rgba(255,122,92,0.25)"}}><span style={{color:B.coral,fontWeight:700}}>⚠ Discrepancy: </span><span style={{color:B.coral}}>Waste logged for {discrepancies.join(", ")} - but {discrepancies.length>1?"these":"this"} sold out.</span></div>}
      {isGateLocked && (
        <div style={{background:"rgba(255,122,92,0.1)",border:"1px dashed rgba(255,122,92,0.3)",borderRadius:10,padding:"14px 16px",marginBottom:12,color:"#FFB39E",fontSize:"0.82rem",lineHeight:1.55,display:"flex",gap:12,alignItems:"flex-start"}}>
          <div style={{fontSize:"1.1rem"}}>🔒</div>
          <div><strong style={{color:"#FFB39E"}}>Submit unlocks at {gateState.warnStartStr}</strong><br/>Today's close is {gateState.closeStr} — early-submit window opens {gateState.earlyHours}h before close. Keep logging wastage as broken rolls happen — submit will unlock at {gateState.warnStartStr}. If you've genuinely sold out early, ping an admin to emergency-unlock.</div>
        </div>
      )}
      {isGateOverride && (
        <div style={{background:"rgba(255,122,92,0.1)",border:"1px solid rgba(255,122,92,0.35)",borderRadius:10,padding:"12px 14px",marginBottom:12,color:"#FFB39E",fontSize:"0.82rem",lineHeight:1.5,display:"flex",gap:10,alignItems:"center"}}>
          <div style={{fontSize:"1.05rem"}}>🔓</div>
          <div><strong style={{color:"#FFB39E"}}>Admin override active</strong> — EOD submit is unlocked for today. Submissions are logged as override.</div>
        </div>
      )}
      {isGateWarn && (
        <div style={{background:"rgba(241,197,68,0.1)",border:"1px solid rgba(241,197,68,0.3)",borderRadius:10,padding:"12px 14px",marginBottom:12,color:B.sunshine,fontSize:"0.82rem",lineHeight:1.5,display:"flex",gap:10,alignItems:"flex-start"}}>
          <div style={{fontSize:"1.05rem"}}>⚠</div>
          <div><strong style={{color:B.sunshine}}>Early-submit window</strong> — today's close is {gateState.closeStr}. Submit will ask for confirmation.</div>
        </div>
      )}
      <div style={{opacity:status==="done"?0.6:1,pointerEvents:status==="done"?"none":"auto"}}>
      <div style={S.secTitle}>Wastage (portions){totalWaste>0&&<span style={{color:B.coral,marginLeft:8,fontSize:"0.85rem"}}>{totalWaste} total</span>}</div>
      <div style={{fontSize:"0.75rem",color:B.kewpie,opacity:0.6,marginTop:-2,marginBottom:10,lineHeight:1.4,fontWeight:500}}>Broken rolls — just mark them as regular wastage for that Roll.</div>
      {SKU_LIST.map(sku=>{const soTime=sellOutTimes[sku];const disc=waste[sku]>0&&soTime;
        return (<div key={sku} style={{...S.row,...(disc?{borderColor:"rgba(255,122,92,0.3)",background:"rgba(255,122,92,0.06)"}:{})}}>
          <div style={{flex:1}}><span style={S.itemName}>{sku}</span>{soTime&&<div style={{fontSize:"0.7rem",color:B.eucalyptus,fontWeight:600,marginTop:2}}>Sold out at {soTime}</div>}</div>
          <div style={S.ctrls}><button style={S.cBtn}onClick={()=>adj(sku,-1)}>−</button><span style={{...S.cVal,...(waste[sku]>0?{color:disc?B.coral:B.wasabi,opacity:1}:{})}}>{waste[sku]}</span><button style={S.cBtn}onClick={()=>adj(sku,1)}>+</button></div>
        </div>)})}
      <div style={S.secTitle}>TGTG {!tgtgReady && <span style={{marginLeft:6,color:B.sunshine,fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.04em"}}>required · tap − for zero</span>}</div>
      <div style={{display:"flex",gap:10,marginBottom:12}}>
        <div style={{...S.row,flex:1,justifyContent:"space-between",...(tgtgSold==null?{borderColor:"rgba(241,197,68,0.45)"}:{})}}>
          <span style={S.itemName}>Bags Sold</span>
          <div style={S.ctrls}>
            <button style={S.cBtn}onClick={()=>adjTgtgSold(-1)}>−</button>
            <span style={{...S.cVal,...(tgtgSold!=null&&tgtgSold>0?{color:B.wasabi,opacity:1}:{}),...(tgtgSold==null?{color:B.sunshine,opacity:0.8}:{})}}>{tgtgSold==null?"—":tgtgSold}</span>
            <button style={S.cBtn}onClick={()=>adjTgtgSold(1)}>+</button>
          </div>
        </div>
        <div style={{...S.row,flex:1,justifyContent:"space-between",...(tgtgNC==null?{borderColor:"rgba(241,197,68,0.45)"}:{})}}>
          <span style={S.itemName}>Not Collected</span>
          <div style={S.ctrls}>
            <button style={S.cBtn}onClick={()=>adjTgtgNC(-1)}>−</button>
            <span style={{...S.cVal,...(tgtgNC!=null&&tgtgNC>0?{color:B.coral,opacity:1}:{}),...(tgtgNC==null?{color:B.sunshine,opacity:0.8}:{})}}>{tgtgNC==null?"—":tgtgNC}</span>
            <button style={S.cBtn}onClick={()=>adjTgtgNC(1)}>+</button>
          </div>
        </div>
      </div>
      </div>

      <ChecklistSummaryEOD site={site} backend={backend} checklistConfig={checklistConfig}/>

      {/* 2026-05-06: read-only stock-requests recap. Non-editable summary
          of the items currently on the running list — purely for at-a-
          glance visibility while filling the EOD. Doesn't gate submit. */}
      {stockRequests && stockRequests.length > 0 && (
        <>
          <div style={S.secTitle}>Stock requests on the running list <span style={{marginLeft:6,color:B.kewpie,opacity:0.45,fontSize:"0.7rem",fontWeight:500}}>({stockRequests.length} {stockRequests.length===1?"item":"items"})</span></div>
          <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.55,marginTop:-2,marginBottom:8,lineHeight:1.4}}>{site.selfReceivesStock ? "Read-only — items still to collect from the storeroom. Clears when marked collected." : "Read-only — items waiting for kitchen delivery. Auto-clears when delivered."}</div>
          <div style={{display:"flex",flexDirection:"column",gap:5,padding:"10px 12px",background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:10,marginBottom:14}}>
            {[...stockRequests].sort(byOldestRequested).map(r => (
              <div key={"eod-stock-"+(r.id||r.item)} style={{display:"flex",alignItems:"center",gap:10,fontSize:"0.78rem",padding:"4px 0"}}>
                <span style={{color:B.kewpie,fontWeight:600,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.item}</span>
                {r.custom && <span style={{color:B.sunshine,fontSize:"0.62rem",fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",flexShrink:0}}>extra</span>}
                <span style={{color:B.kewpie,opacity:0.45,fontSize:"0.68rem",flexShrink:0}}>{r.dateStr || ""}{r.time?" · "+r.time:""}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <button style={{...S.submitBtn,...(status==="done"?{opacity:0.5}:{}),...(isGateLocked||(!tgtgReady&&status!=="done")?{background:"rgba(254,250,240,0.08)",color:"rgba(254,250,240,0.35)",border:"1px dashed rgba(254,250,240,0.15)",cursor:"not-allowed"}:{}),...(tgtgReady&&isGateWarn?{background:B.sunshine,color:B.nori}:{}),...(tgtgReady&&isGateOverride?{background:B.coral,color:B.nori}:{}),marginTop:16}} onClick={()=>{ if (isGateLocked) return; if (!tgtgReady) { setStatus("error"); setStatusMsg("Set TGTG Bags Sold and Not Collected before submitting (tap − once for zero)."); return; } if (isGateWarn||isGateOverride) { setGateConfirmOpen(true); return; } submit(); }} disabled={isGateLocked||status==="loading"||status==="done"||(!tgtgReady&&status!=="done")}>{status==="loading"?"Submitting...":status==="done"?"Submitted ✓":isGateLocked?("Locked until "+gateState.warnStartStr):!tgtgReady?"Set TGTG values to submit":isGateOverride?"Submit Report (override)":"Submit Report"}</button>
      {statusMsg&&<div style={{textAlign:"center",fontSize:"0.8rem",marginTop:8,color:status==="error"?B.coral:B.wasabi}}>{statusMsg}</div>}
      {status==="done"&&(
        <button style={{...S.modalCancel,marginTop:8,width:"100%",padding:10,fontSize:"0.8rem",textAlign:"center",display:"block",opacity:undoing?0.5:1,cursor:undoing?"wait":"pointer"}} disabled={undoing} onClick={undoSubmission}>{undoing?"Undoing...":"Undo Submission (re-edit report)"}</button>
      )}
      {gateConfirmOpen && (
        <div style={S.overlay} onClick={()=>setGateConfirmOpen(false)}>
          <div style={S.modal} onClick={e=>e.stopPropagation()}>
            <div style={S.modalTitle}>{isGateOverride?"Override in effect — submit now?":"Are you sure this is end of day?"}</div>
            <div style={{fontSize:"0.88rem",color:B.kewpie,opacity:0.85,marginBottom:12,lineHeight:1.55}}>
              {isGateOverride
                ? <>{site.name} has been remotely unlocked by an admin. You're about to submit today's EOD with:</>
                : <>It's currently <strong style={{color:B.wasabi}}>{new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</strong> — before today's expected close at <strong style={{color:B.wasabi}}>{gateState.closeStr}</strong>. You'll submit with:</>}
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:14}}>
              <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(254,250,240,0.06)",border:"1px solid rgba(254,250,240,0.1)",fontSize:"0.72rem",color:B.kewpie,opacity:totalWaste>0?1:0.55}}>Waste: {totalWaste}</span>
              <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(254,250,240,0.06)",border:"1px solid rgba(254,250,240,0.1)",fontSize:"0.72rem",color:B.kewpie,opacity:tgtgSold!=null&&tgtgSold>0?1:0.55}}>TGTG sold: {tgtgSold==null?"—":tgtgSold}</span>
              <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(254,250,240,0.06)",border:"1px solid rgba(254,250,240,0.1)",fontSize:"0.72rem",color:B.kewpie,opacity:tgtgNC!=null&&tgtgNC>0?1:0.55}}>Not collected: {tgtgNC==null?"—":tgtgNC}</span>
            </div>
            <div style={{fontSize:"0.7rem",color:B.kewpie,opacity:0.5,marginBottom:14,letterSpacing:"0.02em"}}>Submit attempts are logged with PIN, site, time + context.</div>
            <div style={S.modalBtns}>
              <button style={S.modalCancel} onClick={()=>setGateConfirmOpen(false)}>Not yet</button>
              <button style={{...S.modalOk,...(isGateOverride?{background:B.coral,color:B.nori}:{})}} onClick={()=>{ setGateConfirmOpen(false); submit(); }}>{isGateOverride?"Yes, close out":"Yes, submit"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================
// CATALOG THUMBNAIL — 2026-05-04 (next session)
// ===========================================
// Catalogue thumb + tap-to-enlarge lightbox for the StockRequests UI.
// Mirrors the pattern in Meadowhall and the Manchester kitchen so staff
// see the same affordance everywhere. Sizes match the rest of the FOH
// thumbnails (44px Needed list, 48px search/category rows).
function CatalogThumb({item, imageUrl, size, onZoom, marginRight, showPlaceholder, category}){
  const px = size || 48;
  // React-owned broken flag — direct innerHTML mutation in onError corrupts
  // reconciliation, broken state then never recovers on re-render.
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [imageUrl]);
  const wrap = {width:px,height:px,borderRadius:8,background:"rgba(254,250,240,0.06)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",marginRight:marginRight||0};
  if (!imageUrl || broken) {
    if (!showPlaceholder) return null;
    return <div style={wrap}><span style={{fontSize:Math.round(px*0.42),opacity:0.4}}>📦</span></div>;
  }
  const open = (e)=>{ if(e){e.stopPropagation(); e.preventDefault();} onZoom && onZoom({item, imageUrl, category}); };
  // 2026-05-04: Google Drive image URLs (lh3.googleusercontent.com).
  // Single shared `=s256` so all thumb sizes hit one cached URL per item.
  const crispSrc = (() => {
    if (!imageUrl) return imageUrl;
    if (imageUrl.indexOf('lh3.googleusercontent.com') !== -1 && imageUrl.indexOf('=') === -1) {
      return imageUrl + '=s400';
    }
    return imageUrl;
  })();
  return (
    <div role="button" tabIndex={0} aria-label={"View "+(item||"item")+" image"}
      onClick={open}
      onKeyDown={e=>{ if(e.key==='Enter'||e.key===' '){ open(e); } }}
      style={{...wrap,cursor:"zoom-in"}}>
      <img src={crispSrc} alt={item||""} draggable={false}
        style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
        onError={()=>setBroken(true)}/>
    </div>
  );
}

function CatalogThumbLightbox({item, imageUrl, category, onClose}){
  if (!imageUrl) return null;
  const lbSrc = (imageUrl.indexOf('lh3.googleusercontent.com') !== -1 && imageUrl.indexOf('=') === -1)
    ? imageUrl + '=s400' : imageUrl;
  const headerLabel = category && category.trim() ? category : "Catalogue item";
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,28,22,0.88)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:24,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()}
        style={{maxWidth:240,width:"100%",maxHeight:"calc(100dvh - 48px)",background:"#fff",borderRadius:14,overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 22px 48px rgba(0,0,0,0.5)"}}>
        <div style={{padding:"14px 18px",background:B.nori,display:"flex",alignItems:"center",justifyContent:"space-between",gap:14,borderBottom:"1px solid rgba(0,57,44,0.15)"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:"#C8EAD8"}}>📦 {headerLabel}</div>
            <div style={{fontFamily:"gelica,serif",fontSize:18,fontWeight:700,color:B.wasabi,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item||""}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{width:32,height:32,borderRadius:"50%",background:"rgba(0,0,0,0.3)",color:B.wasabi,border:"none",cursor:"pointer",fontSize:16,fontWeight:700,lineHeight:1}}>×</button>
        </div>
        <div style={{flex:1,minHeight:0,background:"#0a0a0a",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <img src={lbSrc} alt={item||""} draggable={false}
            style={{maxWidth:"100%",maxHeight:240,display:"block"}}
            onError={ev=>{ev.target.style.display="none";ev.target.parentNode.innerHTML='<div style="color:#fff;padding:40px;text-align:center;font-size:13px;opacity:0.7">Image could not load.</div>';}}/>
        </div>
      </div>
    </div>
  );
}

// ===========================================
// PREVIOUS WEEK INSIGHTS — read-only EOD view (Phase A)
// ===========================================
// Pulls each day's submitted EOD report from backend `?action=eodinsights`.
// Shows yesterday by default; a 7-day picker strip lets staff flick through
// the past week. Tile is hidden by default — admin flips it live via the
// CC Tile Editor (state override) when ready to roll out.
//
// Severity thresholds are hardcoded in Phase A and per-site (see THRESH_).
// Phase B will move these onto a Site Targets sheet edited from admin.
function PreviousWeekInsights({site, backend, availableSites}){
  const ymdLocal = (d) => d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
  const today = useMemo(()=>{ const d=new Date(); d.setHours(0,0,0,0); return d; },[]);
  const daysAgo = useCallback((n) => { const d = new Date(today); d.setDate(d.getDate()-n); return d; }, [today]);
  const PREV_7 = useMemo(()=>[1,2,3,4,5,6,7].map(daysAgo), [daysAgo]);

  // Site toggle — when availableSites has 2+ entries, a pill segment switcher
  // appears between the day banner and the date strip. Default is the home
  // site (`site` prop). Single-site frontends pass nothing and skip the toggle.
  const sitesList = (availableSites && availableSites.length) ? availableSites : [site];
  const showSiteToggle = sitesList.length > 1;
  const [currentSiteKey, setCurrentSiteKey] = useState(()=>site.key);
  const currentSite = sitesList.find(s => s.key === currentSiteKey) || sitesList[0];

  const [selectedDate, setSelectedDate] = useState(()=>ymdLocal(daysAgo(1)));
  // Cache of {[siteKey]: {data, error}} for the SELECTED date. Sites are
  // fetched in parallel on date change so toggling between Trafford/Arndale
  // is instant — no loading spinner just to switch sites once data lands.
  const [dataBySite, setDataBySite] = useState({});

  // Phase A — hardcoded per-site thresholds. Phase B replaces these with
  // an admin-managed Site Targets fetch on mount.
  const THRESH = (function(){
    const k = String(currentSite.key||'').toLowerCase();
    if (k==='arndale')    return {tgtgRange:[8,14],  wasteWarn:20, wasteBad:30, soldEarly:"15:30", soldMid:"17:30"};
    if (k==='meadowhall') return {tgtgRange:[10,18], wasteWarn:15, wasteBad:25, soldEarly:"15:00", soldMid:"17:00"};
    if (k==='kitchen')    return {tgtgRange:[10,20], wasteWarn:15, wasteBad:25, soldEarly:"15:00", soldMid:"17:00"};
    return                       {tgtgRange:[15,25], wasteWarn:15, wasteBad:25, soldEarly:"15:00", soldMid:"17:00"}; // tc
  })();

  // Stable string of site keys for dep array — re-creating availableSites on
  // each render would otherwise re-fire the effect on every parent re-render.
  const sitesListKey = sitesList.map(s=>s.key).join(",");

  useEffect(()=>{
    let cancelled = false;
    // Reset cache for the new date so stale data from a previous date doesn't
    // flash before the parallel fetch lands.
    setDataBySite({});
    Promise.all(sitesList.map(s => {
      // B8 read-flip, OFF everywhere: read the day from Supabase when
      // eodInsightsReadEnabled(), else the unchanged backend call. The Supabase
      // reader falls back to this SAME call on any failure, so the worst case is
      // exactly today's behaviour.
      const sheetUrl = backend + "?action=eodinsights&site=" + encodeURIComponent(s.key) + "&date=" + encodeURIComponent(selectedDate);
      const src = eodInsights.eodInsightsReadEnabled()
        ? eodInsights.fetchEodInsightsDay(s.key, selectedDate, { sheetUrl })
        : fetch(sheetUrl).then(r => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); });
      return src
        .then(d => ({key: s.key, data: d || null, error: null}))
        .catch(e => ({key: s.key, data: null, error: e.message || "Failed to load"}));
    })).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(r => { map[r.key] = {data: r.data, error: r.error}; });
      setDataBySite(map);
    });
    return () => { cancelled = true; };
  }, [selectedDate, backend, sitesListKey]);

  const slot = dataBySite[currentSite.key];
  const loading = !slot;
  const data = slot ? slot.data : null;
  const error = slot ? slot.error : null;

  const fmtLong = (d) => d.toLocaleDateString("en-GB", {weekday:"long", day:"numeric", month:"long"});
  const fmtDow  = (d) => d.toLocaleDateString("en-GB", {weekday:"short"});
  const fmtMon  = (d) => d.toLocaleDateString("en-GB", {month:"short"});
  const isYesterday = selectedDate === ymdLocal(daysAgo(1));

  const timeSev = (time) => {
    if (!time) return "low";
    const [h,m] = time.split(":").map(Number);
    const mins = h*60 + (m||0);
    const [eh,em] = THRESH.soldEarly.split(":").map(Number);
    const [mh,mm] = THRESH.soldMid.split(":").map(Number);
    if (mins < eh*60+em) return "early";
    if (mins < mh*60+mm) return "mid";
    return "late";
  };
  const qtySev = (qty) => qty >= 5 ? "high" : qty >= 3 ? "med" : "low";
  const sevColor = (sev) => sev==="high"||sev==="early"||sev==="bad" ? B.coral : sev==="med"||sev==="mid"||sev==="warn" ? B.sunshine : B.wasabi;
  const tgtgSev = (bags) => bags >= THRESH.tgtgRange[0] && bags <= THRESH.tgtgRange[1] ? "good" : bags < THRESH.tgtgRange[0] ? "warn" : "bad";

  const wrap = {maxWidth:760, margin:"0 auto", padding:"16px 18px 32px"};
  const card = {background:"rgba(212,251,111,0.04)", border:"1px solid rgba(212,251,111,0.1)", borderRadius:14, padding:"14px 16px", marginBottom:14};
  const labelStyle = {fontSize:"0.7rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.45, marginBottom:6};
  const numStyle = {fontFamily:"Manrope,sans-serif", fontWeight:800, fontSize:"1.95rem", lineHeight:1.05, color:B.kewpie, letterSpacing:"-0.01em"};

  if (loading) return (
    <div style={wrap}>
      <div style={{...card, textAlign:"center", padding:"40px 20px"}}>
        <div style={{width:28, height:28, border:"3px solid rgba(212,251,111,0.15)", borderTopColor:B.wasabi, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto"}}/>
        <div style={{fontSize:"0.8rem", color:B.kewpie, opacity:0.5, marginTop:12}}>Loading...</div>
      </div>
    </div>
  );

  const r = data && (data.totalWaste !== null || data.tgtgSold !== null || (data.soldOut && data.soldOut.length) || (data.wastage && data.wastage.length)) ? data : null;
  const total = r && r.wastage ? r.wastage.reduce((s,row)=>s+(row[1]||0),0) : 0;
  const wastePct = r && r.forecastTotal ? Math.round(((r.totalWaste||0)/r.forecastTotal)*100) + "%" : null;

  return (
    <div style={wrap}>
      {/* Day banner */}
      <div style={{marginBottom:14}}>
        <div style={{fontFamily:"gelica,serif", fontSize:"1.5rem", fontWeight:700, color:B.kewpie, letterSpacing:"-0.01em", lineHeight:1.15}}>
          {fmtLong(new Date(selectedDate + "T00:00:00"))}
          {isYesterday && <span style={{display:"inline-block", marginLeft:10, fontFamily:"Manrope,sans-serif", fontSize:"0.65rem", fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", padding:"3px 8px", borderRadius:5, background:"rgba(212,251,111,0.1)", color:B.wasabi, border:"1px solid rgba(212,251,111,0.2)", verticalAlign:"middle"}}>Yesterday</span>}
        </div>
        {r && r.submittedBy && <div style={{fontSize:"0.75rem", color:B.kewpie, opacity:0.5, marginTop:3}}>Submitted by {r.submittedBy[0]} ({r.submittedBy[1]}) at {r.submittedBy[2]} · {currentSite.name}</div>}
      </div>

      {/* Site toggle (only shown when 2+ sites configured) */}
      {showSiteToggle && (
        <div style={{display:"inline-flex", border:"1px solid rgba(212,251,111,0.15)", borderRadius:10, padding:4, marginBottom:14, gap:3, background:"rgba(254,250,240,0.04)"}}>
          {sitesList.map(s => {
            const active = s.key === currentSite.key;
            return (
              <button key={s.key} onClick={()=>setCurrentSiteKey(s.key)}
                style={{border:"none", background: active ? B.wasabi : "none", cursor:"pointer", fontFamily:"Manrope,sans-serif", fontSize:"0.82rem", fontWeight: active ? 700 : 600, color: active ? B.nori : B.kewpie, padding:"8px 18px", borderRadius:7, transition:"all 0.15s", minWidth:96, opacity: active ? 1 : 0.55}}>
                {s.name}
              </button>
            );
          })}
        </div>
      )}

      {/* 7-day picker strip */}
      <div style={{display:"flex", gap:5, overflowX:"auto", padding:4, background:"rgba(254,250,240,0.04)", border:"1px solid rgba(212,251,111,0.1)", borderRadius:10, marginBottom:16, WebkitOverflowScrolling:"touch"}}>
        {PREV_7.map(d => {
          const k = ymdLocal(d);
          const active = k === selectedDate;
          return (
            <button key={k} onClick={()=>setSelectedDate(k)} style={{flex:"0 0 auto", border:"1px solid transparent", background: active ? B.wasabi : "none", cursor:"pointer", fontFamily:"Manrope,sans-serif", padding:"8px 12px", borderRadius:7, color: active ? B.nori : B.kewpie, textAlign:"center", minWidth:62, transition:"background 0.15s"}}>
              <span style={{display:"block", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", opacity: active ? 0.7 : 0.5, color: active ? B.nori : B.kewpie}}>{fmtDow(d)}</span>
              <span style={{display:"block", fontFamily:"Manrope,sans-serif", fontSize:"1.05rem", fontWeight:700, lineHeight:1.1, marginTop:2, color: active ? B.nori : B.kewpie, fontVariantNumeric:"tabular-nums"}}>{d.getDate()}</span>
              <span style={{display:"block", fontSize:"0.6rem", fontWeight:600, opacity: active ? 0.7 : 0.5, textTransform:"uppercase", letterSpacing:"0.04em", marginTop:1, color: active ? B.nori : B.kewpie}}>{fmtMon(d)}</span>
            </button>
          );
        })}
      </div>

      {error && <div style={{...card, color:B.coral, fontSize:"0.85rem"}}>Couldn't load: {error}</div>}

      {!r && !error && (
        <div style={{...card, textAlign:"center", padding:"36px 20px", fontStyle:"italic", color:B.kewpie, opacity:0.45, fontSize:"0.85rem"}}>
          No EOD report submitted for this day.
        </div>
      )}

      {r && (
        <>
          {/* Hero stats */}
          <div style={{display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:8, marginBottom:14}}>
            <div style={card}>
              <div style={labelStyle}>Total Wasted</div>
              <div style={{...numStyle, color: sevColor((r.totalWaste||0) >= THRESH.wasteBad ? "bad" : (r.totalWaste||0) >= THRESH.wasteWarn ? "warn" : "good")}}>{r.totalWaste||0}</div>
              {wastePct && <div style={{fontSize:"0.72rem", color:B.kewpie, opacity:0.5, marginTop:6, lineHeight:1.4}}><strong style={{color:B.kewpie, fontWeight:700}}>{wastePct}</strong> of the day's {r.forecastTotal} prep</div>}
            </div>
            <div style={card}>
              <div style={labelStyle}>TGTG Bags Sold</div>
              <div style={{...numStyle, color: sevColor(tgtgSev(r.tgtgSold||0))}}>{r.tgtgSold||0}</div>
              <div style={{fontSize:"0.72rem", color:B.kewpie, opacity:0.5, marginTop:6, lineHeight:1.4}}>
                <strong style={{color:B.kewpie, fontWeight:700}}>{(r.tgtgSold||0)*3} rolls</strong> equivalent (1 bag = 3 rolls)
                <span style={{display:"block", marginTop:2, fontSize:"0.68rem", fontStyle:"italic"}}>(optimal range = {THRESH.tgtgRange[0]} to {THRESH.tgtgRange[1]})</span>
              </div>
            </div>
            <div style={card}>
              <div style={labelStyle}>Rolls Sold Out</div>
              <div style={{...numStyle, color: sevColor((r.soldOutCount||0) >= 6 ? "bad" : (r.soldOutCount||0) >= 3 ? "warn" : "good")}}>{r.soldOutCount||0}</div>
              <div style={{fontSize:"0.72rem", color:B.kewpie, opacity:0.5, marginTop:6, lineHeight:1.4}}><strong style={{color:B.kewpie, fontWeight:700}}>{r.soldOutCount||0}</strong> of 14 SKUs ran out before close</div>
            </div>
          </div>

          {/* Sell Out Times panel */}
          <div style={card}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"0 0 10px", marginBottom:10, borderBottom:"1px solid rgba(212,251,111,0.1)"}}>
              <div>
                <div style={{fontFamily:"gelica,serif", fontSize:"1rem", fontWeight:700, color:B.kewpie}}>Sell Out Times</div>
                <div style={{fontSize:"0.7rem", color:B.kewpie, opacity:0.45, marginTop:2}}>Earliest first — make more today</div>
              </div>
              <span style={{fontSize:"0.72rem", fontWeight:700, color:B.kewpie, opacity:0.6, whiteSpace:"nowrap"}}>{r.soldOut.length} roll{r.soldOut.length===1?"":"s"}</span>
            </div>
            {r.soldOut.length === 0
              ? <div style={{textAlign:"center", padding:"18px 14px", color:B.kewpie, opacity:0.45, fontSize:"0.78rem", fontStyle:"italic"}}>Nothing sold out.</div>
              : <table style={{width:"100%", borderCollapse:"collapse", fontFamily:"Manrope,sans-serif"}}>
                  <thead><tr>
                    <th style={{textAlign:"left", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Roll</th>
                    <th style={{textAlign:"right", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Sold out</th>
                  </tr></thead>
                  <tbody>{r.soldOut.map(([sku,t],i)=>(
                    <tr key={sku+i}>
                      <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontSize:"0.85rem", fontWeight:600, color:B.kewpie}}>{sku}</td>
                      <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontFamily:"Manrope,sans-serif", fontWeight:700, fontSize:"0.95rem", textAlign:"right", color:sevColor(timeSev(t)), fontVariantNumeric:"tabular-nums"}}>{t}</td>
                    </tr>
                  ))}</tbody>
                </table>
            }
          </div>

          {/* Wastage panel */}
          <div style={card}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"0 0 10px", marginBottom:10, borderBottom:"1px solid rgba(212,251,111,0.1)"}}>
              <div>
                <div style={{fontFamily:"gelica,serif", fontSize:"1rem", fontWeight:700, color:B.kewpie}}>Wastage</div>
                <div style={{fontSize:"0.7rem", color:B.kewpie, opacity:0.45, marginTop:2}}>Highest first — prep less today</div>
              </div>
              <span style={{fontSize:"0.72rem", fontWeight:700, color:B.kewpie, opacity:0.6, whiteSpace:"nowrap"}}>{total} portion{total===1?"":"s"}</span>
            </div>
            {(!r.wastage || r.wastage.length === 0)
              ? <div style={{textAlign:"center", padding:"18px 14px", color:B.kewpie, opacity:0.45, fontSize:"0.78rem", fontStyle:"italic"}}>Zero wastage — clean sweep.</div>
              : <table style={{width:"100%", borderCollapse:"collapse", fontFamily:"Manrope,sans-serif"}}>
                  <thead><tr>
                    <th style={{textAlign:"left", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Roll</th>
                    <th style={{textAlign:"right", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Wasted</th>
                    <th style={{textAlign:"right", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Share</th>
                  </tr></thead>
                  <tbody>{r.wastage.map(([sku,q],i)=>{
                    const sev = qtySev(q);
                    const share = total ? Math.round((q/total)*100) + "%" : "—";
                    return (
                      <tr key={sku+i}>
                        <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontSize:"0.85rem", fontWeight:600, color:B.kewpie}}>{sku}</td>
                        <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontFamily:"Manrope,sans-serif", fontWeight:700, fontSize:"0.95rem", textAlign:"right", color:sevColor(sev), fontVariantNumeric:"tabular-nums"}}>{q}</td>
                        <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontSize:"0.72rem", textAlign:"right", color:B.kewpie, opacity:0.5, fontWeight:500, fontVariantNumeric:"tabular-nums"}}>{share}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
            }
          </div>
        </>
      )}
    </div>
  );
}


// ===========================================
// STOCK REQUESTS — batch operations, single PIN per batch
// ===========================================
// Items stay on the list until FOH marks them Received.
// Requesting: select multiple items from categories → floating "Request N items" button → single PIN → all added
// Receiving: tap "Mark Received" → single PIN → checkboxes appear → pick items → confirm

// Sort comparator for the FOH "Needed" running list — oldest-requested at the
// top so items that have been on the list longest surface first. Used in both
// the StockRequests tile and the EOD-report read-only recap.
function byOldestRequested(a, b){
  const __t = (r)=>{
    if(!r) return Infinity;
    const ts = r.requestedFullTs;
    if(!ts) return Infinity;
    const t = Date.parse(ts);
    return isNaN(t) ? Infinity : t;
  };
  return __t(a) - __t(b);
}

function StockRequests({site,backend,requestPin,stockRequests,setStockRequests,setStockPending,stockCatalog,catalogSource}){
  // Build the category → [itemName] map used by the category/search rendering.
  // Retains the shape the existing render code expects; source of truth is now
  // the admin-managed catalog instead of the hardcoded STOCK_ITEMS constant.
  const STOCK_ITEMS_MAP = useMemo(()=>{
    const map = {};
    (stockCatalog||[]).forEach(i=>{
      if(!i||!i.name||!i.category)return;
      if(!map[i.category]) map[i.category]=[];
      map[i.category].push(i.name);
    });
    return map;
  },[stockCatalog]);
  const [extraText,setExtraText]=useState("");
  // 2026-05-03: when staff try to add a custom extra that already maps to a
  // catalogue item, block + show this modal pointing them to the SKU. Fuzzy
  // match (token-jaccard with naive stemming, threshold 0.7) — full-name or
  // codeName substring is an instant hit. Surfaces the matched catalogue
  // entry so staff can jump to the proper tile.
  const [extraBlock,setExtraBlock]=useState(null);
  // Multiple categories can stay open at once — staff often pick items from
  // several sections in one go, so accordion-style one-at-a-time adds friction.
  const [expandedCats,setExpandedCats]=useState(()=>new Set());
  function toggleCat(cat){
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }
  const [search,setSearch]=useState("");
  // Batch request: items selected before PIN
  const [pendingBatch,setPendingBatch]=useState(new Set());
  // Tap-to-enlarge for catalogue thumbnails — Needed mini-list, search row,
  // category row.
  const [thumbZoom,setThumbZoom]=useState(null);
  // imageUrl + category lookups against the admin-managed catalogue. Mirrors
  // the pattern in Meadowhall/kitchen: name → image / category, return null
  // if missing.
  const imageFor = (itemName)=>{
    const m = (stockCatalog||[]).find(c=>c && c.name===itemName);
    return m && m.imageUrl ? m.imageUrl : null;
  };
  const categoryOf = (itemName)=>{
    const m = (stockCatalog||[]).find(c=>c && c.name===itemName);
    return m && m.category ? m.category : "";
  };

  // Notify parent when there are unsubmitted selections (for navigation guard)
  useEffect(()=>{if(setStockPending)setStockPending(pendingBatch.size>0)},[pendingBatch.size]);

  // 2026-05-05 — cross-site storeroom flags. The kitchen storeroom is
  // shared between TC and Arndale; if Arndale's runner reports an item
  // out of stock (or running low), TC staff need to know too because the
  // next request from TC for the same item will hit the same empty
  // shelf. Fetches every 60s and seeds from localStorage so the banner
  // renders instantly on page open.
  // 2026-09-19 (kitchen → Trafford merge): scoped to THIS site, and OFF on a
  // self-collect site. The banner exists to surface a REMOTE storeroom's flags
  // to a kiosk it supplies; on a self-collect site (Meadowhall, and Trafford
  // since the kitchen moved in on 24 Sep 2026) the flagged rows already sit on
  // the Needed list with their own OUT OF STOCK / ORDERED badges (isVisible rule
  // below), so a banner would show every flagged item twice. Was the literal
  // "tc" for every site, which had Meadowhall's till polling Trafford's flags.
  const STOREROOM_CACHE_KEY = "rolled."+site.key+".storeroomFlags";
  // Arndale removed 2026-08-17 (site closed 16/08/2026). Was ["tc","arndale"]:
  // the storeroom used to be shared, so an Arndale out-of-stock flag surfaced on
  // the Trafford tills. One site per storeroom now; none on a self-collect site.
  const STOREROOM_SITES = site.selfReceivesStock ? [] : [site.key];
  const [storeroomFlags, setStoreroomFlags] = useState(()=>{
    try { const c = localStorage.getItem(STOREROOM_CACHE_KEY); if(c){ const p = JSON.parse(c); if(p && Array.isArray(p.items)) return p; } } catch(e) {}
    return {items: [], ts: 0};
  });
  useEffect(()=>{
    let cancelled = false;
    const loadStoreroom = ()=>{
      // READ FLIP (gated OFF by default — stockReader.STOCK_READ_HOSTS): on a
      // flipped till the storeroom banner reads Supabase (CC overlay + sheet
      // fallback inside fetchStockRequests); otherwise the old sheet fetch,
      // unchanged. Catalog filtering happens below via __isItemActive, same as
      // today, so the reader is called with no server-side name filter here.
      Promise.all(STOREROOM_SITES.map(s => {
        const sheetUrl = backend+"?action=getStockRequests&site="+s;
        return (stockReader.stockReadEnabled()
          ? stockReader.fetchStockRequests(s, { sheetUrl })
          : fetch(sheetUrl).then(r=>r.ok?r.json():{requests:[]}))
          .then(d=>((d&&d.requests)||[]).filter(x=>x && (x.flag==="out_of_stock"||x.flag==="running_low")).map(x=>({item:x.item, flag:x.flag, ordered: !!x.ordered, _site:s})))
          .catch(()=>[]);
      })).then(arrays => {
        if(cancelled) return;
        // Dedup by item name; OOS supersedes Running Low for display.
        // Ordered=true if ANY site's row has it (kitchen ordered = item handled
        // for both sites).
        const byItem = {};
        arrays.flat().forEach(r => {
          if(!byItem[r.item]) byItem[r.item] = {item:r.item, flag:r.flag, ordered:!!r.ordered, sites:[r._site]};
          else {
            const cur = byItem[r.item];
            if(cur.sites.indexOf(r._site)===-1) cur.sites.push(r._site);
            if(r.flag === "out_of_stock") cur.flag = "out_of_stock";
            if(r.ordered) cur.ordered = true;
          }
        });
        const items = Object.values(byItem);
        const next = {items, ts: Date.now()};
        setStoreroomFlags(next);
        try { localStorage.setItem(STOREROOM_CACHE_KEY, JSON.stringify(next)); } catch(e) {}
      }).catch(()=>{});
    };
    loadStoreroom();
    const t = setInterval(loadStoreroom, 60000);
    return ()=>{ cancelled = true; clearInterval(t); };
  },[backend]);
  // 2026-05-19: hide deactivated catalog items from the storeroom banner so a
  // lingering OOS row for a discontinued SKU doesn't keep nagging staff. The
  // public stockCatalog already excludes inactive items, so an item missing
  // from the catalog set is either a custom request (keep visible) or a
  // deactivated catalog item (hide). The row carries `flag` set by the BOH
  // side but not `custom` in the storeroom feed shape — we look it up against
  // the active catalog instead. Items present = active; items missing = drop.
  const __activeSet = new Set((stockCatalog||[]).filter(c=>c&&c.name&&c.status!=="inactive").map(c=>c.name.toLowerCase()));
  const __isItemActive = (name)=> !name || __activeSet.size === 0 || __activeSet.has(String(name).toLowerCase());
  // 2026-05-22: ordered items move OUT of the OOS/Low banners and INTO a
  // separate green "On order" banner. Kitchen has placed the supplier order
  // so the OOS state is handled — leaving it under the red OOS badge made
  // FOH re-raise requests for items already on the way.
  const storeroomOos = (storeroomFlags.items||[]).filter(x => x.flag === "out_of_stock" && !x.ordered && __isItemActive(x.item));
  const storeroomLow = (storeroomFlags.items||[]).filter(x => x.flag === "running_low" && !x.ordered && __isItemActive(x.item));
  const storeroomOrdered = (storeroomFlags.items||[]).filter(x => x.ordered && __isItemActive(x.item));
  // Tap-to-expand storeroom banners — collapsed shows item names inline, expanded shows photo rows.
  const [storeroomOosExpanded, setStoreroomOosExpanded] = useState(false);
  const [storeroomLowExpanded, setStoreroomLowExpanded] = useState(false);
  const [storeroomOrderedExpanded, setStoreroomOrderedExpanded] = useState(false);

  // 2026-07-30: first-load sync gate. JJ opened Stock Requests on a freshly-
  // woken tablet, tapped Napkins before the open-requests list had come back
  // from the backend, and raised a duplicate of a request a teammate had made
  // earlier that day. The list DOES self-hydrate (reconcileOpen below), but the
  // window between opening the screen and the first successful fetch (cold Apps
  // Script can take 3-8s, longer or never on patchy in-mall wifi) is exactly
  // when an item still looks un-requested. This state machine tracks that first
  // fetch — "loading" until the first reconcile lands, then "synced"; "stale"
  // if the first fetch fails or hangs (>9s). While "loading" the catalogue
  // Select buttons are disabled so staff can't request an item before we know
  // what's already on the list. "stale" RE-ENABLES selecting (a broken fetch
  // must never hard-block the till) but shows a warning to check with the
  // kitchen first. A later 60s tick that succeeds always flips back to "synced".
  const [syncState, setSyncState] = useState("loading"); // "loading" | "synced" | "stale"

  // 2026-05-15: stale-while-revalidate cache for the "Needed for site"
  // running list. JJ flagged a 3-8s blank-list-on-mount delay tonight
  // while Apps Script getStockRequests does a cold-start cross-sheet
  // scan. Cache lives outside the main LS_KEY blob because stock
  // requests legitimately span days (request Mon, deliver Tue) — the
  // day-rollover migration shouldn't wipe it. Hydrate on first mount
  // when state is empty; persist on every change so next mount is
  // instant. The reconcileOpen poll below still does the source-of-
  // truth sync — pruning delivered items, hydrating new backend rows.
  const RUNNING_LIST_CACHE_KEY = "rolled.stockReqRunning::" + site.key;
  useEffect(()=>{
    if (stockRequests.length > 0) return; // never clobber local optimistic writes
    try {
      const raw = localStorage.getItem(RUNNING_LIST_CACHE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed.list) || parsed.list.length === 0) return;
      setStockRequests(parsed.list);
    } catch(e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]); // once on mount
  useEffect(()=>{
    try {
      localStorage.setItem(RUNNING_LIST_CACHE_KEY, JSON.stringify({
        ts: Date.now(),
        list: stockRequests
      }));
    } catch(e) {}
  },[stockRequests]);

  // 2026-05-06: Mark Received was dropped — Manchester now uses the
  // Meadowhall housed-model `delivered = closed` rule. The kitchen's
  // photo-confirmed delivered event closes the row server-side; this
  // poll syncs FOH state so delivered items auto-drop from the Needed
  // list. Optimistic adds within the last 30s are preserved so a recent
  // submit doesn't briefly disappear before the backend writes.
  useEffect(()=>{
    let cancelled = false;
    const reconcileOpen = ()=>{
      // READ FLIP (gated): same branch as the storeroom banner. The reader's
      // null return (Supabase AND sheet both failed) throws here so .catch
      // skips the filter pass — preserving the 2026-05-06 rule that a failed
      // read must never wipe the local list.
      const sheetUrl = backend+"?action=getStockRequests&site="+site.key;
      (stockReader.stockReadEnabled()
        ? stockReader.fetchStockRequests(site.key, { sheetUrl })
          .then(d=>{ if(!d) throw new Error("stock read unavailable"); return d; })
        : fetch(sheetUrl).then(r=>{
          // Throw on non-2xx so .catch skips the filter pass entirely. Without
          // this, a 5xx returned an empty {requests:[]} which made the
          // reconcile WIPE every local item — devastating on patchy in-mall
          // wifi. Caught in code review 2026-05-06.
          if(!r.ok) throw new Error("HTTP "+r.status);
          return r.json();
        }))
        .then(d=>{
          if(cancelled) return;
          const openItemNames = new Set();
          const openBackendRows = [];
          (d.requests||[]).forEach(r => {
            // Kitchen still owes TC stock if:
            //   - status=requested (open, no flag)
            //   - flag=running_low + uncollected (storeroom warned but not delivered)
            //   - flag=out_of_stock + ordered=true (kitchen ordered from supplier;
            //     FOH need visibility that it's on the way) — 2026-05-22
            // Kitchen does NOT owe stock if:
            //   - flag=out_of_stock + not ordered (kitchen has nothing, no order placed)
            //   - closed (delivered/collected — job done)
            //   - flag=running_low + closed (delivered, just storeroom warning)
            // 2026-05-06.
            const isClosed = r.closed === true;
            const isOos = r.flag === "out_of_stock";
            const isOrdered = r.ordered === true;
            // Self-collect sites (Meadowhall) have no kitchen storeroom banner to
            // catch an out-of-stock row, so an OOS-not-collected item must STAY on
            // the Needed list (flagged) for the next runner — the whole point of
            // the Out of Stock action. Kitchen-served sites (tc/arndale) keep the
            // original rule: an OOS-not-ordered row is hidden here and shown in the
            // shared storeroom banner instead.
            const isVisible = site.selfReceivesStock
              ? !isClosed
              : (!isClosed && (!isOos || isOrdered));
            if (isVisible && r.item) {
              openItemNames.add(r.item);
              openBackendRows.push(r);
            }
          });
          // 2026-08-03: drop "just collected" tombstones whose item is no longer
          // OPEN on the backend — our received write has landed (item closed), so a
          // later genuine re-request of the same item must not stay suppressed.
          for (const __k of Object.keys(receivedMarks.current)) {
            if (!openItemNames.has(__k)) delete receivedMarks.current[__k];
          }
          // 2026-05-06: two-way sync. Prune local rows the kitchen has finished
          // with AND hydrate local with backend-open rows that aren't already
          // in local state. Without hydration, OOS→Replenished was a one-way
          // trip — the row was pruned at OOS-time and never re-appeared on
          // the Needed list once the kitchen replenished.
          const cutoffTs = Date.now() - 30 * 1000;
          setStockRequests(prev => {
            const pruned = prev.filter(local => {
              if (openItemNames.has(local.item)) return true;
              const ts = local.requestedFullTs ? new Date(local.requestedFullTs).getTime() : 0;
              if (ts > cutoffTs) return true;
              return false;
            });
            const localItemNames = new Set(pruned.map(p => p.item));
            const hydrated = pruned.slice();
            openBackendRows.forEach(r => {
              if (!localItemNames.has(r.item)) {
                // 2026-08-03: skip an item just optimistically collected here — the
                // received write may not have landed yet, so the backend still
                // reports it OPEN; re-adding would flicker the row back onto Needed.
                // Tombstone is cleared once the backend closes it (above) or on a
                // failed write; LOCAL_MARK_TTL_MS is the backstop.
                const __jm = receivedMarks.current[r.item];
                if (__jm && localMarkIsFresh({_ts: __jm})) return;
                hydrated.push({
                  id: r.id || r.item,
                  item: r.item,
                  time: r.time || "",
                  dateStr: r.dateStr || "",
                  custom: !!r.custom,
                  status: r.status || "requested",
                  requestedBy: r.requestedBy || r.staffName || "",
                  requestedFullTs: r.requestedFullTs || "",
                  // 2026-05-22: surface kitchen-ordered state on FOH so staff
                  // see the awaiting-delivery row without needing to ask.
                  flag: r.flag || "",
                  ordered: !!r.ordered,
                  refImageUrl: r.refImageUrl || "",
                  supplierRef: r.supplierRef || "",
                  eta: r.eta || "",
                  adminNote: r.adminNote || ""
                });
              } else {
                // Item already in local state — refresh from the backend row.
                // 2026-07-30 FIX: previously this refreshed ONLY the ordered /
                // flag fields and left time / dateStr / status / requestedBy /
                // requestedFullTs FROZEN at whatever this tablet first saw. So if
                // a tablet raised a duplicate on an item another till had already
                // requested, its own optimistic entry (requestedBy=this staffer,
                // now-time) stuck forever while the kitchen read the backend live
                // and showed the ORIGINAL requester — the two screens disagreed
                // (JJ 30 Jul: napkins showed "JJ 16:43" on FOH, "Esther 15:22" on
                // the kitchen). The backend keeps the original requester (a
                // re-request of an open item is recorded as a bump, not a new
                // row), so pulling those fields from the backend makes FOH match
                // the kitchen and keeps the age honest. Backend value || local
                // fallback so a transient empty backend field never wipes a good
                // local one. NB: `id` is deliberately NOT refreshed — the local
                // optimistic id is what submitBatch's failed-write cleanup filters
                // on, so overwriting it with the backend row index would strand a
                // failed in-flight write. This restores the 2026-06-21 fix that
                // lived in the old per-site index.html and was dropped in the
                // move to the unified src/foh.jsx.
                for (let i = 0; i < hydrated.length; i++) {
                  if (hydrated[i].item === r.item) {
                    hydrated[i] = {
                      ...hydrated[i],
                      time: r.time || hydrated[i].time || "",
                      dateStr: r.dateStr || hydrated[i].dateStr || "",
                      status: r.status || hydrated[i].status || "requested",
                      requestedBy: (r.requestedBy || r.staffName) || hydrated[i].requestedBy || "",
                      requestedFullTs: r.requestedFullTs || hydrated[i].requestedFullTs || "",
                      flag: r.flag || hydrated[i].flag || "",
                      ordered: !!r.ordered,
                      refImageUrl: r.refImageUrl || hydrated[i].refImageUrl || "",
                      supplierRef: r.supplierRef || hydrated[i].supplierRef || "",
                      eta: r.eta || hydrated[i].eta || "",
                      adminNote: r.adminNote || hydrated[i].adminNote || ""
                    };
                    break;
                  }
                }
              }
            });
            return hydrated;
          });
          // First successful fetch — lift the sync gate (see syncState above).
          if(!cancelled) setSyncState("synced");
        })
        .catch(()=>{
          // network/5xx — leave the list alone, retry next tick. Only downgrade
          // the gate if we've NEVER had a good sync; once "synced", a transient
          // blip shouldn't yank the till into a warning state.
          if(!cancelled) setSyncState(prev => prev==="synced" ? "synced" : "stale");
        });
    };
    reconcileOpen();
    const t = setInterval(reconcileOpen, 60000);
    // Safety valve: if the very first fetch neither resolves nor rejects within
    // 9s (hung socket on bad wifi), stop blocking and let staff request with a
    // warning rather than staring at a disabled list.
    const staleTimer = setTimeout(()=>{ if(!cancelled) setSyncState(prev => prev==="loading" ? "stale" : prev); }, 9000);
    return ()=>{ cancelled = true; clearInterval(t); clearTimeout(staleTimer); };
  },[backend, site.key]);
  // Meadowhall self-collect: the request row whose Collect modal is open.
  // null = closed. `levelStep` = the modal has moved on to "How much is left?"
  // after ✓ Collected; it resets whenever a different row (or none) is open.
  const [actionItem,setActionItem]=useState(null);
  const [levelStep,setLevelStep]=useState(false);
  useEffect(()=>{ setLevelStep(false); },[actionItem]);
  // Catalogue items that keep a sealed backup pack in the store room (ticked per
  // item in the Command Centre). Opening that pack is the running-low moment.
  // Read from Supabase on mount + every 5 min, cached so the last good list
  // paints instantly and a failed read never un-ticks an item mid-shift.
  const [backupPackNames,setBackupPackNames]=useState(()=>{
    try{ const c=JSON.parse(localStorage.getItem("stockBackupPackCache")||"null"); return Array.isArray(c)?c:[]; }catch(e){ return []; }
  });
  useEffect(()=>{
    let live=true;
    const load=()=>stockCatalogSrc.fetchBackupPackItems().then(list=>{
      if(!live||!list) return;
      setBackupPackNames(list);
      try{ localStorage.setItem("stockBackupPackCache",JSON.stringify(list)); }catch(e){}
    });
    load();
    const t=setInterval(load,5*60*1000);
    return ()=>{ live=false; clearInterval(t); };
  },[]);
  const backupPacks=useMemo(()=>stockCatalogSrc.backupPackSet(backupPackNames),[backupPackNames]);
  const isBackupPackItem = (itemName)=>!!itemName && backupPacks.has(String(itemName).trim().toLowerCase());
  // 2026-08-03: "just collected" tombstones (item name -> ts). A collect
  // optimistically drops the row, but the received write takes 2-5s and the 60s
  // reconcileOpen poll can still see the item OPEN on the backend in that window
  // and re-hydrate it — flickering the row back onto "Needed" (staff then re-tap,
  // thinking it didn't save). A fresh tombstone makes the hydrate skip it until
  // the backend confirms it closed (then it's dropped); cleared on a failed write
  // so a lost save reappears. Mirrors the kitchen's local-mark TTL.
  const receivedMarks = useRef({});

  const nowStr=()=>new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
  const dateStr=()=>new Date().toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
  const fmtDateBE=()=>new Date().toLocaleDateString("en-GB");

  const requestedItems=new Set(stockRequests.map(r=>r.item));
  const isRequested=(item)=>requestedItems.has(item);

  // Toggle an item in the pending batch selection
  const togglePending=(item)=>{
    setPendingBatch(prev=>{const n=new Set(prev);if(n.has(item))n.delete(item);else n.add(item);return n});
  };

  // Phase 2 strangler dual-run: mirror each stock write into Supabase alongside
  // the Apps Script write. Best-effort and wrapped so it can NEVER block or break
  // the live till. `ops` runs once the ~15-min Supabase pass is in hand.

  // Submit the entire pending batch with one PIN
  const submitBatch=()=>{
    if(pendingBatch.size===0)return;
    const items=[...pendingBatch];
    requestPin(async(staffName,staffPin)=>{
      const now=nowStr();const ds=dateStr();const fd=fmtDateBE();
      const newEntries=items.map(item=>({id:Date.now()+"-"+Math.random().toString(36).slice(2,7),item,time:now,dateStr:ds,custom:false,status:"requested",requestedBy:staffName,requestedFullTs:new Date().toISOString()}));
      setStockRequests(r=>[...r,...newEntries]);
      setPendingBatch(new Set());
      // Write-drop gate (STOCK_WRITE_DROP_HOSTS — ships EMPTY): ON -> the awaited
      // Supabase landing IS the record and the Sheet POST is skipped; OFF -> today's
      // shadow + confirmedWrite, byte-for-byte untouched. On the ON path the awaited
      // per-entry landing replaces the Sheet re-read verify (re-reading the Sheet
      // after a write-drop would never see the Supabase-only row).
      const dropOn=stockSync.stockWriteDropEnabled();
      if(!dropOn) items.forEach(item=>stockSync.recordRequest({siteKey:site.key,item,staffPin}));
      // Confirmed writes — remove any that didn't save and tell staff.
      const results=await Promise.all(newEntries.map(entry=>
        dropOn
          ? stockSync.stockWriteAuthoritative({kind:"request",args:{siteKey:site.key,item:entry.item,type:"standard",staffPin}}).then(res=>({entry,ok:res.ok}))
          : confirmedWrite({backend,action:"stockRequest",staffName,staffPin,payload:{site:site.key,date:fd,item:entry.item,type:"standard"},
              verify: () => confirmByReRead(backend+"?action=getStockRequests&site="+site.key, d=>stockHasOpenRequest(d, entry.item))}).then(res=>({entry,ok:res.ok}))
      ));
      const failedIds=new Set(results.filter(x=>!x.ok).map(x=>x.entry.id));
      if(failedIds.size){
        setStockRequests(r=>r.filter(x=>!failedIds.has(x.id)));
        notifyWriteFailed(failedIds.size+" stock request"+(failedIds.size>1?"s":"")+" didn't save — try again");
      }
    });
  };

  // 2026-05-03: catalogue match for the extra-text path. Staff can't add a
  // custom request for an item that already has a SKU — they get pushed to
  // the catalogue tile instead so the kitchen + admin see the right item.
  // Strategy:
  //   1) Full catalogue name (or codeName) appears as substring in typed text
  //      → instant match.
  //   2) Token overlap with naive stemming (drop trailing s on 4+ char words).
  //      Score = catalogue tokens present in typed text / total catalogue
  //      tokens. Pick the highest-scoring item ≥ 0.7. Ties: longest catalogue
  //      name wins (so "Soy Sauce Stickers" beats "Soy Sauce Fish" for "soy
  //      sauce allergy stickers").
  // 2026-05-06: loose multi-candidate match. "stickers" surfaces every sticker
  // SKU; "matcha" surfaces Matcha Cans + Matcha Lids; "macha" (single-letter
  // typo) catches Matcha-* via subsequence match.
  function findCatalogMatches_(typed, max){
    max = max || 8;
    const norm = s => String(s||"").toLowerCase().replace(/[^a-z0-9 ]+/g," ").replace(/\s+/g," ").trim();
    const stem = w => (w && w.length>3 && w.endsWith("s")) ? w.slice(0,-1) : w;
    const tokens = s => norm(s).split(" ").filter(Boolean).map(stem);
    const isSubseq = (needle, haystack) => {
      if (!needle || !haystack) return false;
      let j = 0;
      for (let i = 0; i < haystack.length && j < needle.length; i++) {
        if (haystack[i] === needle[j]) j++;
      }
      return j === needle.length;
    };
    // 2026-05-24 (JJ): common-word stoplist so distinctive catalog tokens
    // (soy, miso, wasabi, ginger, mayo, koala, etc.) carry weight even when
    // staff write verbose entries like "two boxes of soy if we have enough
    // pls, very urgent!!!!". Without this list, "soy" alone in a long
    // sentence drowns in low typedCoverage and slips past the threshold.
    const STOP_WORDS = new Set([
      "the","a","an","of","for","in","on","at","by","with","to","from","and","or","is","are","be","not","this","that","i","we","you","they","it","my","our",
      "please","pls","need","needs","want","wanted","have","had","get","got","also","again","just","very","really","still","more","some","any","all","none","each","extra","spare","plenty",
      "urgent","asap","quick","quickly","soon","today","tomorrow","now","when","can","could","would","should","might",
      "one","two","three","four","five","six","seven","eight","nine","ten","few","couple",
      "box","boxes","bag","bags","packet","packets","pack","packs","piece","pieces","bottle","bottles","can","cans","tub","tubs","tray","trays","unit","units","carton","cartons","case","cases",
      "if","enough","ok","okay","sure","thanks","thx","ta","cheers",
      "pl"  // stemmed form of pls
    ]);
    const isDistinctive = (w) => !!w && w.length >= 3 && !STOP_WORDS.has(w);
    const t = norm(typed);
    if(!t) return [];
    const tTokens = tokens(typed);
    const tTokenSet = new Set(tTokens);
    const items = (stockCatalog||[]).filter(c=>c && c.name && c.status!=="inactive");
    const scored = [];
    for(const c of items){
      const cName = norm(c.name);
      const cTokens = tokens(c.name);
      const cTokenSet = new Set(cTokens);
      if(!cTokens.length) continue;
      let cHits = 0; for(const w of cTokens){ if(tTokenSet.has(w)) cHits++; }
      let tHits = 0; for(const w of tTokens){ if(cTokenSet.has(w)) tHits++; }
      const catCoverage = cHits / cTokens.length;
      const typedCoverage = tTokens.length ? tHits / tTokens.length : 0;
      const fullSubMatch = cName && t.indexOf(cName) !== -1;
      const reverseSubMatch = cName && cName.indexOf(t) !== -1;
      const subseqMatch = !reverseSubMatch && t.length >= 3 && isSubseq(t, cName);
      const codeName = norm(c.codeName);
      const codeMatch = codeName && codeName.length >= 3 && (t.indexOf(codeName) !== -1 || codeName.indexOf(t) !== -1);
      // Distinctive catalog tokens that appear in the typed text. Each one
      // adds 1.5 to the score — enough that ANY distinctive hit clears the
      // 1.0 threshold even with otherwise-low coverage. Stops verbose
      // entries like "two boxes of soy" from slipping past Soy Sauce Fish.
      let distinctiveHits = 0;
      for (const w of cTokens) { if (isDistinctive(w) && tTokenSet.has(w)) distinctiveHits++; }
      let score = typedCoverage * 2 + catCoverage;
      if (fullSubMatch) score += 3;
      if (reverseSubMatch) score += 1.5;
      if (subseqMatch) score += 1;
      if (codeMatch) score += 1;
      if (distinctiveHits) score += 1.5 * distinctiveHits;
      // 2026-05-19 — only surface strong matches. Below 1.0 is "one shared word" noise
      // that triggers unhelpful "did you mean" prompts. The custom-block needs to feel
      // intentional: when the modal opens it should be because we genuinely think the
      // staff member meant a SKU, not because their sentence happened to share a token.
      if (score < 1.0) continue;
      scored.push({ item: c, score, typedCoverage, nameLen: (c.name||"").length });
    }
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.typedCoverage !== a.typedCoverage) return b.typedCoverage - a.typedCoverage;
      return a.nameLen - b.nameLen;
    });
    return scored.slice(0, max).map(x => x.item);
  }

  // Add a custom "extra" item with PIN
  const sendExtra=()=>{
    const t=extraText.trim();if(!t)return;
    // Hold custom requests during the first-load sync gate too — same reason as
    // the catalogue Select buttons: isRequested() below reads the local list,
    // which hasn't hydrated a teammate's not-yet-synced request yet, so a novel
    // custom item could slip past the dedup. The button is also disabled while
    // loading; this is the belt-and-braces guard.
    if(syncState==="loading")return;
    if(isRequested(t))return;
    const matches = findCatalogMatches_(t);
    if(matches && matches.length){
      setExtraBlock({typed:t, items:matches});
      return;
    }
    requestPin(async(staffName,staffPin)=>{
      const entry={id:Date.now()+"-"+Math.random().toString(36).slice(2,7),item:t,time:nowStr(),dateStr:dateStr(),custom:true,status:"requested",requestedBy:staffName,requestedFullTs:new Date().toISOString()};
      setStockRequests(r=>[...r,entry]);
      setExtraText("");
      // Write-drop gate (ships EMPTY). ON -> awaited Supabase landing is the record,
      // Sheet POST skipped; OFF -> today's shadow + confirmedWrite, unchanged.
      const dropOn=stockSync.stockWriteDropEnabled();
      if(dropOn){
        const res=await stockSync.stockWriteAuthoritative({kind:"request",args:{siteKey:site.key,item:t,type:"custom",staffPin}});
        if(!res.ok){
          setStockRequests(r=>r.filter(x=>x.id!==entry.id));
          setExtraText(t);
          notifyWriteFailed("'"+t+"' didn't save — "+res.errMsg);
        }
      } else {
        stockSync.recordRequest({siteKey:site.key,item:t,type:"custom",staffPin});
        await confirmedWrite({backend,action:"stockRequest",staffName,staffPin,payload:{site:site.key,date:fmtDateBE(),item:t,type:"custom"},onRollback:(msg)=>{
          setStockRequests(r=>r.filter(x=>x.id!==entry.id));
          setExtraText(t);
          notifyWriteFailed("'"+t+"' didn't save — "+msg);
        },
          // add is state-idempotent (a re-request just bumps), so a real failure is
          // safe to surface: goal is an OPEN request for this item.
          verify: () => confirmByReRead(backend+"?action=getStockRequests&site="+site.key, d=>stockHasOpenRequest(d, t))});
      }
    });
  };

  // 5-min Undo window — staff who don't read English well were tapping Undo
  // when they meant Mark Received. After 5 min the Undo button hides on every
  // surface (running list + search/category rows), forcing the correct path.
  const UNDO_WINDOW_MS = 5*60*1000;
  const [nowMs,setNowMs] = useState(Date.now());
  useEffect(()=>{const id=setInterval(()=>setNowMs(Date.now()),30000);return ()=>clearInterval(id)},[]);
  const reqTs = (r)=>{
    if(r&&r.requestedFullTs){const t=Date.parse(r.requestedFullTs);if(!isNaN(t))return t}
    if(r&&typeof r.id==="string"){const m=r.id.match(/^(\d{10,16})/);if(m)return parseInt(m[1],10)}
    return null;
  };
  const canUndo = (r)=>{const t=reqTs(r);if(t==null)return false;return (nowMs-t)<UNDO_WINDOW_MS};

  const undoRequest=(id,item)=>{
    requestPin(async(staffName,staffPin)=>{
      const removed=stockRequests.find(x=>x.id===id);
      setStockRequests(r=>r.filter(x=>x.id!==id));
      // Write-drop gate (ships EMPTY). ON -> awaited Supabase landing is the record;
      // OFF -> today's shadow + confirmedWrite, unchanged.
      const dropOn=stockSync.stockWriteDropEnabled();
      if(dropOn){
        const res=await stockSync.stockWriteAuthoritative({kind:"undo",args:{siteKey:site.key,item,staffPin}});
        if(!res.ok){
          if(removed)setStockRequests(r=>[...r,removed]);
          notifyWriteFailed(item+" undo didn't save — "+res.errMsg);
        }
      } else {
        stockSync.recordUndo({siteKey:site.key,item,staffPin});
        await confirmedWrite({backend,action:"stockRequestUndo",staffName,staffPin,payload:{site:site.key,date:fmtDateBE(),item},onRollback:(msg)=>{
          if(removed)setStockRequests(r=>[...r,removed]);
          notifyWriteFailed(item+" undo didn't save — "+msg);
        },
          // undo is idempotent (last-event-wins). goal: this item has no OPEN request.
          verify: () => confirmByReRead(backend+"?action=getStockRequests&site="+site.key, d=>!stockHasOpenRequest(d, item))});
      }
    });
  };

  // ---- Meadowhall self-collect: three-action Collect flow ----
  // The unified-app merge dropped the full FOH Collect flow — only a bare
  // "✓ Collected" survived. Restored per JJ's Build Hub "Stock Requests —
  // Housed System (Meadowhall template)". The Collect modal offers three
  // per-row actions, each its own PIN-gated confirmedWrite:
  //   ✓ Collected    → stockDelivered                     (closes the request)
  //   ⚠ Running Low  → stockDelivered THEN stockRunningLow (closes + reorders)
  //   ✗ Out of Stock → stockOutOfStock                     (does NOT close)
  // JJ, 11 Oct 2026: Running Low was an optional extra button, so a runner
  // could take the last pack and tap plain Collected (Soy Sauce Stickers went
  // straight to Out of Stock at Trafford that way). Collected now asks "How
  // much is left?" before the PIN: Plenty left → collectOne; Running low /
  // That was the last one → runningLowOne. Items with a sealed backup pack
  // (Command Centre tick → stock_backup_pack_feed, migration 1910) word it as
  // "Opened the backup pack".
  // Collected + Running Low drop the row and tombstone it (so the 60s poll
  // can't flicker it back). Out of Stock keeps the row on the list flagged so
  // the next runner sees it — the flag is what fires the (server-side) reorder
  // email. Meadowhall has no kitchen storeroom banner, so an OOS row must live
  // on the Needed list itself (see the gated isVisible rule in reconcileOpen).
  //
  // Every one of the three ALSO shadows to Supabase via stockSync.recordKitchen
  // — the same generic recorder the kitchen tile uses (boh-kitchen.jsx), whose
  // KITCHEN_EVENT map already speaks stockDelivered / stockRunningLow /
  // stockOutOfStock. Meadowhall is a DUAL_RUN host, so without this every
  // collect is a "dropped shadow" the 30-minute stock self-heal has to repair —
  // which trips its abnormal-repair-volume alarm (reconcile_stock.mjs, >8 per
  // sweep) with a message no one can act on. recordKitchen's idem key is the
  // exact key the healer mirrors, so a shadow and a heal collapse onto one row.
  const _stockReqUrl = backend+"?action=getStockRequests&site="+site.key;

  // ✓ Collected — brought back to the kiosk. Closes the request.
  const collectOne = (r) => {
    // The PIN keypad (S.overlay, z 500) sits UNDER this dialog (z 600), so the
    // dialog must be gone before the prompt opens or the taps land on its buttons
    // (found on the Trafford launch test, 20 Sep 2026). `r` carries the row.
    setActionItem(null);
    requestPin(async(staffName,staffPin)=>{
      const fd=fmtDateBE();
      setActionItem(null);
      receivedMarks.current[r.item]=Date.now();            // tombstone so the poll doesn't flicker it back
      setStockRequests(rs=>rs.filter(x=>x.id!==r.id));
      // Write-drop gate (ships EMPTY). ON -> awaited Supabase landing is the record;
      // OFF -> today's shadow + confirmedWrite, unchanged.
      const dropOn=stockSync.stockWriteDropEnabled();
      if(dropOn){
        const res=await stockSync.stockWriteAuthoritative({kind:"kitchen",args:{siteKey:site.key,item:r.item,action:"stockDelivered",staffPin}});
        if(!res.ok){
          delete receivedMarks.current[r.item];
          setStockRequests(rs=>rs.some(x=>x.item===r.item)?rs:[...rs,r]);
          notifyWriteFailed(r.item+" collect didn't save — "+res.errMsg);
        }
      } else {
        stockSync.recordKitchen({siteKey:site.key,item:r.item,action:"stockDelivered",staffPin});
        await confirmedWrite({backend,action:"stockDelivered",staffName,staffPin,payload:{site:site.key,date:fd,item:r.item},onRollback:(msg)=>{
          delete receivedMarks.current[r.item];              // save failed — let it reappear
          setStockRequests(rs=>rs.some(x=>x.item===r.item)?rs:[...rs,r]);   // dedupe re-add by item
          notifyWriteFailed(r.item+" collect didn't save — "+msg);
        },
          // stockDelivered closes the row; goal: no OPEN request for it (a closed
          // row lingers in the feed today as closed:true — hence "no open row").
          verify: () => confirmByReRead(_stockReqUrl, d=>!stockHasOpenRequest(d, r.item))});
      }
    });
  };

  // ⚠ Running Low — it WAS collected (closes the request) but the store room is
  // nearly empty, so also flag running-low to trigger the reorder. Two events:
  // stockDelivered (close) then stockRunningLow (flag).
  const runningLowOne = (r) => {
    setActionItem(null); // see collectOne
    requestPin(async(staffName,staffPin)=>{
      const fd=fmtDateBE();
      setActionItem(null);
      receivedMarks.current[r.item]=Date.now();            // tombstone — the row closes
      setStockRequests(rs=>rs.filter(x=>x.id!==r.id));
      // Write-drop gate (ships EMPTY). ON -> awaited Supabase landing is the record
      // for BOTH the close and the flag; OFF -> today's two shadow+confirmedWrite
      // pairs, unchanged. `ok` is now shared across both branches (was `const`).
      const dropOn=stockSync.stockWriteDropEnabled();
      let ok;
      if(dropOn){
        const res=await stockSync.stockWriteAuthoritative({kind:"kitchen",args:{siteKey:site.key,item:r.item,action:"stockDelivered",staffPin}});
        ok=res.ok;
        if(!ok){
          delete receivedMarks.current[r.item];
          setStockRequests(rs=>rs.some(x=>x.item===r.item)?rs:[...rs,r]);
          notifyWriteFailed(r.item+" collect didn't save — "+res.errMsg);
        }
      } else {
        stockSync.recordKitchen({siteKey:site.key,item:r.item,action:"stockDelivered",staffPin});
        ({ok}=await confirmedWrite({backend,action:"stockDelivered",staffName,staffPin,payload:{site:site.key,date:fd,item:r.item},onRollback:(msg)=>{
          delete receivedMarks.current[r.item];
          setStockRequests(rs=>rs.some(x=>x.item===r.item)?rs:[...rs,r]);
          notifyWriteFailed(r.item+" collect didn't save — "+msg);
        },
          verify: () => confirmByReRead(_stockReqUrl, d=>!stockHasOpenRequest(d, r.item))}));
      }
      if(!ok) return;                                       // don't flag-low a collect that never landed
      // The row is now closed — nothing to roll back. A failed flag just means
      // the reorder email may not fire, so surface it but leave the row gone.
      if(dropOn){
        const res2=await stockSync.stockWriteAuthoritative({kind:"kitchen",args:{siteKey:site.key,item:r.item,action:"stockRunningLow",staffPin}});
        try{ notificationsSync.recordReorderSignal({siteKey:site.key,item:r.item,action:"stockRunningLow",pass:res2&&res2.pass}); }catch(_){}
        if(!res2.ok) notifyWriteFailed(r.item+" running-low flag didn't save — "+res2.errMsg);
      } else {
        stockSync.recordKitchen({siteKey:site.key,item:r.item,action:"stockRunningLow",staffPin})
          .then(res=>{ try{ notificationsSync.recordReorderSignal({siteKey:site.key,item:r.item,action:"stockRunningLow",pass:res&&res.pass}); }catch(_){} })
          .catch(()=>{});
        await confirmedWrite({backend,action:"stockRunningLow",staffName,staffPin,payload:{site:site.key,date:fd,item:r.item},onRollback:(msg)=>{
          notifyWriteFailed(r.item+" running-low flag didn't save — "+msg);
        }});
      }
    });
  };

  // ✗ Out of Stock — the store room was empty, so this canNOT close the request.
  // The row STAYS on the Needed list, flagged, so the next runner tries again;
  // stockOutOfStock fires the (server-side) reorder email. No tombstone — the
  // row is meant to persist.
  const outOfStockOne = (r) => {
    setActionItem(null); // see collectOne
    requestPin(async(staffName,staffPin)=>{
      const fd=fmtDateBE();
      const prevFlag=r.flag||"";
      setActionItem(null);
      setStockRequests(rs=>rs.map(x=>x.id===r.id?{...x,flag:"out_of_stock"}:x));   // optimistic pill, row stays
      // Write-drop gate (ships EMPTY). ON -> awaited Supabase landing is the record;
      // OFF -> today's shadow + confirmedWrite, unchanged. Either path raises the
      // reorder alert (best-effort, queues only) — #262 intent preserved across #318.
      const dropOn=stockSync.stockWriteDropEnabled();
      if(dropOn){
        const res=await stockSync.stockWriteAuthoritative({kind:"kitchen",args:{siteKey:site.key,item:r.item,action:"stockOutOfStock",staffPin}});
        try{ notificationsSync.recordReorderSignal({siteKey:site.key,item:r.item,action:"stockOutOfStock",pass:res&&res.pass}); }catch(_){}
        if(!res.ok){
          setStockRequests(rs=>rs.map(x=>x.id===r.id?{...x,flag:prevFlag}:x));
          notifyWriteFailed(r.item+" out-of-stock flag didn't save — "+res.errMsg);
        }
      } else {
        stockSync.recordKitchen({siteKey:site.key,item:r.item,action:"stockOutOfStock",staffPin})
          .then(res=>{ try{ notificationsSync.recordReorderSignal({siteKey:site.key,item:r.item,action:"stockOutOfStock",pass:res&&res.pass}); }catch(_){} })
          .catch(()=>{});
        await confirmedWrite({backend,action:"stockOutOfStock",staffName,staffPin,payload:{site:site.key,date:fd,item:r.item},onRollback:(msg)=>{
          setStockRequests(rs=>rs.map(x=>x.id===r.id?{...x,flag:prevFlag}:x));        // revert the flag
          notifyWriteFailed(r.item+" out-of-stock flag didn't save — "+msg);
        }});
      }
    });
  };

  const cats=Object.keys(STOCK_ITEMS_MAP);
  const reqCount=stockRequests.length;
  const pendingCount=pendingBatch.size;
  // Gate the catalogue Select buttons until the first sync lands, so nobody
  // requests an item before the tablet knows what a teammate already asked for.
  const selectDisabled = syncState==="loading";
  // Third badge state (Lane CP): catalogBadge() turns "none" (neither source
  // answered) into its own red warning, instead of the old two-way ternary
  // mis-reading it as "Fallback — item list from Google Sheet" (which implies
  // Google DID answer). Same lookup foh.jsx and boh-kitchen.jsx both use, so the
  // words always match.
  const catalogBadgeInfo = stockCatalogSrc.catalogBadge(catalogSource);

  return (
    <div style={S.content}>
      <div style={S.sectionCard}><div style={S.secLabel}>Stock Requests</div><div style={S.secDesc}>Select items you need, then tap the request button below. Items stay on the list until marked received.</div></div>
      {/* Catalogue source badge — shows only on a flipped tablet (mirrors the kitchen).
          On the OFF path catalogSource is null so nothing renders. */}
      {stockCatalogSrc.catalogReadEnabled()&&catalogBadgeInfo&&<div style={{fontSize:"0.68rem",fontWeight:600,marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",gap:6,color:catalogBadgeInfo.tone}}><span style={{fontSize:"0.9rem",lineHeight:1}}>●</span>{catalogBadgeInfo.text}</div>}

      {/* 2026-07-30: first-load sync status. While we're still fetching the
          current list, tell staff and hold the Select buttons; if the fetch
          fails, warn but let them carry on. Nothing shows once synced. */}
      {syncState==="loading" && (
        <div style={{...S.infoBanner,background:"rgba(212,251,111,0.06)",borderColor:"rgba(212,251,111,0.2)",marginBottom:12,padding:"10px 14px",display:"flex",alignItems:"center",gap:8}}>
          <span style={{display:"inline-block",width:14,height:14,border:"2px solid rgba(212,251,111,0.35)",borderTopColor:B.wasabi,borderRadius:"50%",animation:"spin 0.7s linear infinite",flexShrink:0}}/>
          <span style={{color:B.kewpie,fontWeight:600,fontSize:"0.8rem",opacity:0.85}}>Checking the latest requests… so you don't double up on something a teammate just asked for.</span>
        </div>
      )}
      {syncState==="stale" && (
        <div style={{...S.infoBanner,background:"rgba(241,197,68,0.12)",borderColor:"rgba(241,197,68,0.3)",marginBottom:12,padding:"10px 14px",display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:"1.05rem",flexShrink:0}}>⚠️</span>
          <span style={{color:B.sunshine,fontWeight:700,fontSize:"0.8rem"}}>Couldn't reach the server to refresh — the list below may be out of date. Check it before re-requesting an item.</span>
        </div>
      )}

      {/* Storeroom status banner — items currently flagged out of stock,
          running low, or already on order at this site's storeroom. Tap to
          expand and see per-item rows with thumbnails. */}
      {(storeroomOos.length>0 || storeroomLow.length>0 || storeroomOrdered.length>0) && (
        <div style={{marginBottom:12}}>
          {storeroomOrdered.length>0 && (
            <div style={{borderRadius:10,background:"rgba(111,175,143,0.14)",border:"1px solid rgba(111,175,143,0.4)",marginBottom:8,overflow:"hidden"}}>
              <button type="button" onClick={()=>setStoreroomOrderedExpanded(v=>!v)} style={{width:"100%",padding:"10px 14px",background:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:8,fontFamily:"inherit",textAlign:"left"}}>
                <span style={{fontSize:"1rem",lineHeight:1.2,flexShrink:0}}>📦</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:"#C8EAD8",fontWeight:800,fontSize:"0.72rem",letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:4}}>On order · awaiting delivery · {storeroomOrdered.length}</div>
                  {!storeroomOrderedExpanded && <div style={{color:B.kewpie,fontSize:"0.78rem",fontWeight:600,lineHeight:1.45}}>{storeroomOrdered.map(x=>x.item).join(" · ")}</div>}
                </div>
                <span style={{color:"#C8EAD8",fontSize:"0.7rem",fontWeight:700,flexShrink:0,opacity:0.7}}>{storeroomOrderedExpanded?"Hide":"Show items"}</span>
              </button>
              {storeroomOrderedExpanded && (
                <div style={{padding:"4px 12px 12px",display:"flex",flexDirection:"column",gap:6}}>
                  {storeroomOrdered.map(x => (
                    <div key={"ord-"+x.item} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 8px",background:"rgba(111,175,143,0.1)",borderRadius:8}}>
                      <CatalogThumb item={x.item} imageUrl={imageFor(x.item)} category={categoryOf(x.item)} size={40} onZoom={setThumbZoom} showPlaceholder/>
                      <span style={{flex:1,minWidth:0,color:B.kewpie,fontSize:"0.8rem",fontWeight:600}}>{x.item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {storeroomOos.length>0 && (
            <div style={{borderRadius:10,background:"rgba(255,122,92,0.12)",border:"1px solid rgba(255,122,92,0.32)",marginBottom:8,overflow:"hidden"}}>
              <button type="button" onClick={()=>setStoreroomOosExpanded(v=>!v)} style={{width:"100%",padding:"10px 14px",background:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:8,fontFamily:"inherit",textAlign:"left"}}>
                <span style={{fontSize:"1rem",lineHeight:1.2,flexShrink:0}}>❗</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:"#FF9B83",fontWeight:800,fontSize:"0.72rem",letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:4}}>Storeroom out of stock · {storeroomOos.length}</div>
                  {!storeroomOosExpanded && <div style={{color:B.kewpie,fontSize:"0.78rem",fontWeight:600,lineHeight:1.45}}>{storeroomOos.map(x=>x.item).join(" · ")}</div>}
                </div>
                <span style={{color:"#FF9B83",fontSize:"0.7rem",fontWeight:700,flexShrink:0,opacity:0.7}}>{storeroomOosExpanded?"Hide":"Show items"}</span>
              </button>
              {storeroomOosExpanded && (
                <div style={{padding:"4px 12px 12px",display:"flex",flexDirection:"column",gap:6}}>
                  {storeroomOos.map(x => (
                    <div key={"oos-"+x.item} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 8px",background:"rgba(255,122,92,0.08)",borderRadius:8}}>
                      <CatalogThumb item={x.item} imageUrl={imageFor(x.item)} category={categoryOf(x.item)} size={40} onZoom={setThumbZoom} showPlaceholder/>
                      <span style={{flex:1,minWidth:0,color:B.kewpie,fontSize:"0.8rem",fontWeight:600}}>{x.item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {storeroomLow.length>0 && (
            <div style={{borderRadius:10,background:"rgba(241,197,68,0.1)",border:"1px solid rgba(241,197,68,0.28)",overflow:"hidden"}}>
              <button type="button" onClick={()=>setStoreroomLowExpanded(v=>!v)} style={{width:"100%",padding:"10px 14px",background:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:8,fontFamily:"inherit",textAlign:"left"}}>
                <span style={{fontSize:"1rem",lineHeight:1.2,flexShrink:0}}>🪫</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:"#F1C544",fontWeight:800,fontSize:"0.72rem",letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:4}}>Storeroom running low · {storeroomLow.length}</div>
                  {!storeroomLowExpanded && <div style={{color:B.kewpie,fontSize:"0.78rem",fontWeight:600,lineHeight:1.45}}>{storeroomLow.map(x=>x.item).join(" · ")}</div>}
                </div>
                <span style={{color:"#F1C544",fontSize:"0.7rem",fontWeight:700,flexShrink:0,opacity:0.7}}>{storeroomLowExpanded?"Hide":"Show items"}</span>
              </button>
              {storeroomLowExpanded && (
                <div style={{padding:"4px 12px 12px",display:"flex",flexDirection:"column",gap:6}}>
                  {storeroomLow.map(x => (
                    <div key={"low-"+x.item} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 8px",background:"rgba(241,197,68,0.08)",borderRadius:8}}>
                      <CatalogThumb item={x.item} imageUrl={imageFor(x.item)} category={categoryOf(x.item)} size={40} onZoom={setThumbZoom} showPlaceholder/>
                      <span style={{flex:1,minWidth:0,color:B.kewpie,fontSize:"0.8rem",fontWeight:600}}>{x.item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Visual indicator when items selected but not yet submitted */}
      {pendingCount>0&&(
        <div style={{...S.infoBanner,background:"rgba(241,197,68,0.12)",borderColor:"rgba(241,197,68,0.3)",marginBottom:12,padding:"10px 14px",display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:"1.1rem"}}>⚠️</span>
          <span style={{color:B.sunshine,fontWeight:700,fontSize:"0.82rem"}}>{pendingCount} item{pendingCount>1?"s":""} selected — scroll down and tap <strong>"Request {pendingCount} Item{pendingCount>1?"s":""}"</strong> to submit</span>
        </div>
      )}

      {/* Running list — what's been requested (and not yet received) */}
      {reqCount>0&&(
        <div style={{...S.infoBanner,background:"rgba(212,251,111,0.06)",borderColor:"rgba(212,251,111,0.2)",marginBottom:12,padding:"12px 14px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <span style={{color:B.wasabi,fontWeight:700,fontSize:"0.85rem"}}>Needed for {site.name} · {reqCount} item{reqCount>1?"s":""}</span>
            {/* Self-collect sites (no kitchen to fire the closing 'delivered'
                event) get a per-row Collect… action that opens the three-action
                modal (Collected / Running Low / Out of Stock); kitchen-served
                sites keep the auto-clears-on-delivery model unchanged. */}
            {site.selfReceivesStock ? (
              <span style={{color:B.kewpie,opacity:0.45,fontSize:"0.7rem",fontStyle:"italic"}}>tap Collect on an item below</span>
            ) : (
              <span style={{color:B.kewpie,opacity:0.45,fontSize:"0.7rem",fontStyle:"italic"}}>auto-clears when delivered</span>
            )}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {[...stockRequests].sort((a,b)=>{
              // Ordered rows demoted to bottom; oldest-first within each bucket.
              const ao = a.ordered ? 1 : 0;
              const bo = b.ordered ? 1 : 0;
              if (ao !== bo) return ao - bo;
              return byOldestRequested(a,b);
            }).map(r=>{
              const rImg = imageFor(r.item);
              const ordered = !!r.ordered;
              const __flagged = r.flag === "out_of_stock" || r.flag === "running_low";
              const __ts = r.requestedFullTs ? Date.parse(r.requestedFullTs) : NaN;
              const daysOpen = isNaN(__ts) ? 0 : Math.floor((Date.now() - __ts) / 86400000);
              const showDaysOpen = !__flagged && daysOpen >= 1;
              return (
              <div key={r.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,fontSize:"0.78rem",padding:"6px 0",borderTop:"1px solid rgba(212,251,111,0.06)",opacity:ordered?0.72:1}}>
                <CatalogThumb item={r.item} imageUrl={rImg} category={categoryOf(r.item)} size={44} onZoom={setThumbZoom}/>
                <div style={{flex:1,minWidth:0}}>
                  <span style={{color:B.kewpie,fontWeight:600}}>{r.item}</span>
                  {r.custom&&<span style={{color:B.sunshine,marginLeft:6,fontSize:"0.68rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>extra</span>}
                  {showDaysOpen&&<span style={{marginLeft:6,background:"rgba(244,164,96,0.18)",color:"#F4A460",padding:"1px 7px",borderRadius:5,fontSize:"0.62rem",fontWeight:700,letterSpacing:"0.04em"}}>⏳ {daysOpen} {daysOpen===1?"DAY":"DAYS"} OPEN</span>}
                  {ordered&&<span style={{marginLeft:6,background:"rgba(111,175,143,0.32)",color:"#C8EAD8",padding:"1px 7px",borderRadius:5,fontSize:"0.62rem",fontWeight:700,letterSpacing:"0.04em"}}>📦 ORDERED · AWAITING DELIVERY</span>}
                  {/* Self-collect only: the store room was empty last time, so this
                      request is still owed — flagged for the next runner. */}
                  {site.selfReceivesStock&&r.flag==="out_of_stock"&&<span style={{marginLeft:6,background:"rgba(255,122,92,0.2)",color:"#FF9B83",padding:"1px 7px",borderRadius:5,fontSize:"0.62rem",fontWeight:700,letterSpacing:"0.04em"}}>⚠ OUT OF STOCK</span>}
                  <div style={{color:B.kewpie,opacity:0.4,fontSize:"0.68rem",marginTop:2}}>{r.dateStr} · {r.time}{r.requestedBy?" · "+r.requestedBy:""}</div>
                </div>
                {(!ordered || site.selfReceivesStock) && (
                  <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
                    {/* 5-min request Undo (retract a mistaken request) stays; the
                        self-collect Collect… button opens the three-action modal. */}
                    {!ordered && canUndo(r)&&(<button style={{...S.undoBtn,padding:"5px 10px",fontSize:"0.7rem"}} onClick={()=>undoRequest(r.id,r.item)}>Undo</button>)}
                    {site.selfReceivesStock&&(<button style={{...S.receivedBtn,padding:"5px 12px",fontSize:"0.72rem"}} onClick={()=>setActionItem(r)}>Collect…</button>)}
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
      )}

      {/* Search */}
      <input style={{...S.input,marginBottom:10,paddingLeft:36,backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23D4FB6F' opacity='0.4' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242.156a5 5 0 1 1 0-10 5 5 0 0 1 0 10z'/%3E%3C/svg%3E\")",backgroundRepeat:"no-repeat",backgroundPosition:"12px center",backgroundSize:"14px"}} placeholder="Search items..." value={search} onChange={e=>setSearch(e.target.value)}/>

      {/* Category list / search results */}
      {search.trim()?(
        // Search mode — flat list of matching items across all categories
        (()=>{
          const q=search.trim().toLowerCase();
          const matches=[];
          cats.forEach(cat=>{STOCK_ITEMS_MAP[cat].forEach(item=>{if(item.toLowerCase().includes(q))matches.push(item)})});
          return matches.length>0?matches.map(item=>{
            const req=isRequested(item);const pending=pendingBatch.has(item);
            const itImg=imageFor(item);
            return (<div key={item} style={{...S.soRow,...(req?{background:"rgba(212,251,111,0.08)",borderColor:"rgba(212,251,111,0.15)"}:pending?{background:"rgba(212,251,111,0.06)",borderColor:"rgba(212,251,111,0.2)"}:{})}}>
              <CatalogThumb item={item} imageUrl={itImg} category={categoryOf(item)} size={48} marginRight={10} onZoom={setThumbZoom}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={S.itemName}>{item}</div>
                {req&&<div style={{fontSize:"0.7rem",color:B.eucalyptus,fontWeight:600,marginTop:2}}>On the running list</div>}
                {pending&&!req&&<div style={{fontSize:"0.7rem",color:B.wasabi,fontWeight:600,marginTop:2}}>Selected</div>}
              </div>
              {req?((()=>{const r=stockRequests.find(r=>r.item===item);return r&&canUndo(r)?<button style={S.undoBtn} onClick={()=>undoRequest(r.id,r.item)}>Undo</button>:null})()):pending?(<button style={{...S.undoBtn,borderColor:"rgba(212,251,111,0.3)",color:B.wasabi}} onClick={()=>togglePending(item)}>Deselect</button>):(<button style={{...S.markBtn,...(selectDisabled?{opacity:0.4,cursor:"default"}:{})}} disabled={selectDisabled} onClick={()=>togglePending(item)}>Select</button>)}
            </div>);
          }):(<div style={{textAlign:"center",color:B.kewpie,opacity:0.35,fontSize:"0.82rem",padding:"20px 0"}}>No items matching "{search.trim()}"</div>);
        })()
      ):(
        // Normal category mode
        cats.map(cat=>{
        const items=STOCK_ITEMS_MAP[cat];const isOpen=expandedCats.has(cat);
        return (<div key={cat} style={{marginBottom:4}}>
          <button style={S.catHeader} onClick={()=>toggleCat(cat)}>
            <span>{cat}</span><span style={{opacity:0.4}}>{isOpen?"▾":"▸"}</span>
          </button>
          {isOpen&&items.map(item=>{
            const req=isRequested(item);
            const pending=pendingBatch.has(item);
            const itImg=imageFor(item);
            return (<div key={item} style={{...S.soRow,...(req?{background:"rgba(212,251,111,0.08)",borderColor:"rgba(212,251,111,0.15)"}:pending?{background:"rgba(212,251,111,0.06)",borderColor:"rgba(212,251,111,0.2)"}:{})}}>
              <CatalogThumb item={item} imageUrl={itImg} category={categoryOf(item)} size={48} marginRight={10} onZoom={setThumbZoom}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={S.itemName}>{item}</div>
                {req&&<div style={{fontSize:"0.7rem",color:B.eucalyptus,fontWeight:600,marginTop:2}}>On the running list</div>}
                {pending&&!req&&<div style={{fontSize:"0.7rem",color:B.wasabi,fontWeight:600,marginTop:2}}>Selected</div>}
              </div>
              {req?(
                (()=>{const r=stockRequests.find(r=>r.item===item);return r&&canUndo(r)?<button style={S.undoBtn} onClick={()=>undoRequest(r.id,r.item)}>Undo</button>:null})()
              ):pending?(
                <button style={{...S.undoBtn,borderColor:"rgba(212,251,111,0.3)",color:B.wasabi}} onClick={()=>togglePending(item)}>Deselect</button>
              ):(
                <button style={{...S.markBtn,...(selectDisabled?{opacity:0.4,cursor:"default"}:{})}} disabled={selectDisabled} onClick={()=>togglePending(item)}>Select</button>
              )}
            </div>)
          })}
        </div>)
      })
      )}

      <div style={{...S.secTitle,marginTop:16}}>Request Something Else</div>
      <div style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.6,marginBottom:6,lineHeight:1.4}}>Only for items <strong>not</strong> in the catalogue above. If it's listed, use that tile instead so the storeroom + admin see the right SKU.</div>
      <textarea style={S.textarea} placeholder="e.g. Replacement door key, branded pen for visit..." value={extraText} onChange={e=>setExtraText(e.target.value.slice(0,500))} maxLength={500}/>
      {extraText.trim()&&<button style={{...S.submitBtn,marginTop:8,...(selectDisabled?{opacity:0.4,cursor:"default"}:{})}} disabled={selectDisabled} onClick={sendExtra}>{selectDisabled?"Checking latest requests…":"Add to Running List"}</button>}

      {/* 2026-05-06: multi-candidate "Did you mean..." block. Loose matcher
          surfaces up to 8 plausible catalogue items. */}
      {extraBlock && (
        <div onClick={()=>setExtraBlock(null)} style={{position:"fixed",inset:0,background:"rgba(0,57,44,0.78)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{maxWidth:480,width:"100%",maxHeight:"calc(100vh - 32px)",overflowY:"auto",background:B.nori,border:"1px solid rgba(212,251,111,0.25)",borderRadius:14,padding:"22px 22px 18px",color:B.kewpie,boxShadow:"0 18px 44px rgba(0,0,0,0.45)"}}>
            <div style={{fontSize:"0.7rem",fontWeight:800,color:B.sunshine,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>⚠ Did you mean…</div>
            <div style={{fontFamily:"gelica,serif",fontSize:"1.25rem",lineHeight:1.25,marginBottom:6,color:B.kewpie}}>Pick the right item</div>
            <div style={{fontSize:"0.82rem",lineHeight:1.5,opacity:0.7,marginBottom:14}}>You typed <em>"{extraBlock.typed}"</em>. These look similar — tap one to use the proper catalogue tile so the storeroom + admin see the right SKU, image and quantity. If none fit, tap <strong>Edit my text</strong> and rewrite your note so it doesn't mention these items.</div>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
              {(extraBlock.items||[]).map(it=>(
                <button key={it.name} style={{textAlign:"left",padding:"12px 14px",borderRadius:10,background:"rgba(212,251,111,0.05)",color:B.kewpie,border:"1px solid rgba(212,251,111,0.18)",fontFamily:"inherit",cursor:"pointer",display:"flex",alignItems:"center",gap:10}} onClick={()=>{
                  setExpandedCats(prev=>{const next=new Set(prev);next.add(it.category);return next;});
                  setSearch(it.name);
                  setExtraText("");
                  setExtraBlock(null);
                }}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:"0.92rem",color:B.wasabi,whiteSpace:"normal",overflowWrap:"anywhere"}}>{it.name}</div>
                    <div style={{fontSize:"0.7rem",opacity:0.55,marginTop:2}}>{it.category||"Catalogue"}</div>
                  </div>
                  <span style={{fontSize:"0.7rem",fontWeight:700,opacity:0.6}}>Open ›</span>
                </button>
              ))}
            </div>
            {/* 2026-05-19 — the "None of these — add as custom" bypass has been
                removed. Once the matcher finds a strong catalogue hit, staff must
                either tap the matching SKU above or rewrite their note. Custom
                submissions are only for genuinely novel items the catalogue
                doesn't cover (e.g. one-off paper supplies). */}
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button style={{flex:"1 1 110px",padding:"12px 16px",borderRadius:10,background:"transparent",color:B.kewpie,border:"1px solid rgba(254,250,240,0.25)",fontFamily:"inherit",fontWeight:700,fontSize:"0.88rem",cursor:"pointer"}} onClick={()=>setExtraBlock(null)}>Edit my text</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch request submit button — inline, always visible when items selected */}
      {pendingCount>0&&(
        <div style={{position:"sticky",bottom:16,zIndex:80,marginTop:16,animation:"fadeUp 0.2s ease both"}}>
          <button style={{...S.submitBtn,padding:"14px 24px",borderRadius:14,boxShadow:"0 4px 24px rgba(0,0,0,0.4)",fontSize:"0.95rem",width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8}} onClick={submitBatch}>
            <span>📋 Request {pendingCount} Item{pendingCount>1?"s":""}</span>
          </button>
        </div>
      )}

      {/* Meadowhall self-collect — Collect modal. Step 1: Collected or Out of
          Stock. Collected moves to step 2, "How much is left?", which picks the
          write (see collectOne / runningLowOne / outOfStockOne), so a runner
          can't close a request without saying how much is left. Collected
          closes the request; Out of Stock keeps it on the list flagged for the
          next runner. Gated on selfReceivesStock so the modal never appears for
          kitchen-served sites. */}
      {site.selfReceivesStock && actionItem && (()=>{
        const img = imageFor(actionItem.item);
        const backupPack = isBackupPackItem(actionItem.item);
        const choiceBtn = (bg,border,color)=>({width:"100%",textAlign:"left",padding:"12px 14px",borderRadius:12,cursor:"pointer",background:bg,border:"1px solid "+border,color,fontFamily:"inherit"});
        const choiceTitle = {fontWeight:800,fontSize:"0.92rem",letterSpacing:"0.02em"};
        const choiceSub = {fontSize:"0.72rem",opacity:0.75,marginTop:3,lineHeight:1.4};
        return (
          <div role="dialog" aria-modal="true" onClick={()=>setActionItem(null)} style={{position:"fixed",inset:0,background:"rgba(0,28,22,0.72)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:600,padding:20,backdropFilter:"blur(2px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:440,maxHeight:"calc(100vh - 40px)",overflowY:"auto",background:B.nori,borderRadius:20,padding:"22px 22px 24px",color:B.kewpie,boxShadow:"0 12px 48px rgba(0,0,0,0.5)",animation:"fadeUp 0.22s ease both"}}>
              {/* Item header */}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                <CatalogThumb item={actionItem.item} imageUrl={img} category={categoryOf(actionItem.item)} size={64} onZoom={setThumbZoom}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"gelica,serif",fontSize:"1.2rem",fontWeight:700,letterSpacing:"-0.01em"}}>{actionItem.item}</div>
                  <div style={{fontSize:"0.75rem",opacity:0.55,marginTop:2}}>Raised {actionItem.time||"earlier"}{actionItem.requestedBy?" · "+actionItem.requestedBy:""}</div>
                </div>
              </div>

              {/* Current-flag banner */}
              {actionItem.flag==="out_of_stock"&&(
                <div style={{background:"rgba(255,122,92,0.14)",border:"1px solid rgba(255,122,92,0.32)",color:"#FF9B83",padding:"10px 12px",borderRadius:10,fontSize:"0.78rem",marginBottom:14,fontWeight:700}}>⚠ Currently flagged Out of Stock</div>
              )}

              {backupPack&&(
                <div style={{background:"rgba(241,197,68,0.10)",border:"1px solid rgba(241,197,68,0.28)",color:B.sunshine,padding:"10px 12px",borderRadius:10,fontSize:"0.78rem",marginBottom:14,lineHeight:1.45}}>📦 Keep a sealed backup pack of this in the store room. If you open it, it's running low.</div>
              )}

              {!levelStep ? (<>
              <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:B.kewpie,opacity:0.5,marginBottom:8}}>What happened?</div>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
                {/* ✓ Collected — asks how much is left before anything is saved */}
                <button onClick={()=>setLevelStep(true)} style={choiceBtn("rgba(212,251,111,0.15)","rgba(212,251,111,0.45)",B.wasabi)}>
                  <div style={choiceTitle}>✓ Collected</div>
                  <div style={choiceSub}>Brought back to the kiosk — next, say how much is left.</div>
                </button>
                {/* ✗ Out of Stock — keeps the request on the list, flagged */}
                <button onClick={()=>outOfStockOne(actionItem)} style={choiceBtn("rgba(255,122,92,0.12)","rgba(255,122,92,0.3)",B.coral)}>
                  <div style={choiceTitle}>✗ Out of Stock</div>
                  <div style={choiceSub}>Store room was empty — keeps it on the list with a red flag and reorders it.</div>
                </button>
              </div>
              </>) : (<>
              <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:B.kewpie,opacity:0.5,marginBottom:8}}>How much is left in the store room?</div>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
                {/* Plenty left — plain collect, closes the request */}
                <button onClick={()=>collectOne(actionItem)} style={choiceBtn("rgba(212,251,111,0.15)","rgba(212,251,111,0.45)",B.wasabi)}>
                  <div style={choiceTitle}>{backupPack?"✓ Plenty — backup pack still sealed":"✓ Plenty left"}</div>
                  <div style={choiceSub}>Closes the request.</div>
                </button>
                {/* Running low — collected AND flags running-low to reorder */}
                <button onClick={()=>runningLowOne(actionItem)} style={choiceBtn("rgba(241,197,68,0.12)","rgba(241,197,68,0.3)",B.sunshine)}>
                  <div style={choiceTitle}>{backupPack?"⚠ Opened the backup pack":"⚠ Running low"}</div>
                  <div style={choiceSub}>Closes the request and flags it to reorder.</div>
                </button>
                {/* That was the last one — same write as running low */}
                <button onClick={()=>runningLowOne(actionItem)} style={choiceBtn("rgba(255,122,92,0.12)","rgba(255,122,92,0.3)",B.coral)}>
                  <div style={choiceTitle}>⚠ That was the last one</div>
                  <div style={choiceSub}>None left in the store room — closes the request and flags it to reorder.</div>
                </button>
              </div>
              <button onClick={()=>setLevelStep(false)} style={{width:"100%",padding:"10px 16px",marginBottom:8,borderRadius:12,border:"none",background:"transparent",color:B.kewpie,fontFamily:"inherit",fontWeight:700,fontSize:"0.8rem",cursor:"pointer",opacity:0.6}}>← Back</button>
              </>)}

              <button onClick={()=>setActionItem(null)} style={{width:"100%",padding:"14px 16px",borderRadius:12,border:"1px solid rgba(254,250,240,0.12)",background:"transparent",color:B.kewpie,fontFamily:"inherit",fontWeight:700,fontSize:"0.88rem",cursor:"pointer",opacity:0.75}}>Cancel</button>
            </div>
          </div>
        );
      })()}

      {thumbZoom && <CatalogThumbLightbox item={thumbZoom.item} imageUrl={thumbZoom.imageUrl} category={thumbZoom.category} onClose={()=>setThumbZoom(null)}/>}
    </div>
  );
}

// ===========================================
// DELIVERY ORDERS — outbound handoff photo proof
// ===========================================
// 2026-05-12: Photo + order # proof tile, fired BEFORE the staff hands a
// delivery order to the courier. Picture must show the full order + the
// platform ticket. Lands on the admin Delivery Order Proof tile for
// dispute lookup if a customer claims a missing item.
//
// Single screen — no tabs. Currently Uber Eats only, but the platform
// picker is there so Deliveroo / Just Eat slot in without a frontend
// change.
// Preview/staging hosts get a small set of demo orders injected when the
// backend returns nothing, so JJ can see populated state without having
// to log a real handoff. Prod (*.eatrolled.com) skips this entirely.
const __IS_PREVIEW_HOST = (typeof window !== "undefined") &&
  !((window.location.hostname || "").endsWith(".eatrolled.com"));

function buildDemoOrders(siteKey){
  const now = Date.now();
  return [
    {ts: now - 35*60*1000,  orderNo: "6FA3E", platform: "Uber Eats", staffName: "Sharon",  photoUrl: "https://picsum.photos/seed/uber-6fa3e/320/320", note: "", site: siteKey, _demo: true},
    {ts: now - 95*60*1000,  orderNo: "8XK22", platform: "Uber Eats", staffName: "Iraj",    photoUrl: "https://picsum.photos/seed/uber-8xk22/320/320", note: "Bag handle was loose — taped", site: siteKey, _demo: true},
    {ts: now - 180*60*1000, orderNo: "2HJ91", platform: "Uber Eats", staffName: "Charles", photoUrl: "https://picsum.photos/seed/uber-2hj91/320/320", note: "", site: siteKey, _demo: true}
  ];
}

// ── Delivery-order guidance: cache + seed ────────────────────────────────────
// The ✅ Right / ❌ Wrong examples come from one Apps Script GET that is measurably
// slow (8.8s cold, 13s+ when it races the other two GETs this tile fires on mount)
// and sometimes hands back an HTML interstitial at HTTP 200. Until this fix the
// whole block was behind `guidance &&` with `guidance` seeded null and the fetch
// swallowed on `.catch(()=>{})`, so staff photographed and submitted long before
// the examples arrived — "the examples never show".
//
// The guidance is GLOBAL, not per-site (the endpoint takes no site param — see
// deliveryConfigReader.js), so one cache key serves every site.
const GUIDANCE_CACHE_KEY = "rolled.deliveryGuidance";
// Seed of last resort for a tablet with a cold cache, so the first staffer of the
// day sees the REAL examples instead of two grey boxes. NOT a source of truth:
// every successful read overwrites it and writes through to the cache above. If
// JJ swaps the images in the Command Centre (Delivery Order Proof → Guidance
// Images) this goes stale for the first few seconds of a cold load only — update
// it here if it is ever meaningfully wrong.
const GUIDANCE_SEED = {
  right: {imageUrl:"https://lh3.googleusercontent.com/d/1Jw2x9e6kieNIgmxaPNZgR_-rStOncfoS", caption:"All rolls, dips and drinks clearly visible and order ticket # in frame"},
  wrong: {imageUrl:"https://lh3.googleusercontent.com/d/1aka2DJIyj9ukpIggCqTTkeBtzPyWFNu7", caption:"Closed box, obscured items or unclear order ticket"},
};

function DeliveryOrders({backend,requestPin,site}){
  const [orderNo,setOrderNo] = useState("");
  // Admin-managed platforms. Fetched on mount via getdeliveryplatforms.
  // While loading, fall back to a sensible single-Uber-Eats default so
  // the tile is functional even on a cold cache.
  const [platforms,setPlatforms] = useState([{name:"Uber Eats", example:"6FA3E", active:true}]);
  const [platform,setPlatform] = useState("Uber Eats");
  const activePlatforms = platforms.filter(p => p.active);
  const currentPlatformObj = activePlatforms.find(p => p.name === platform) || activePlatforms[0] || {name:"Uber Eats", example:"6FA3E"};
  const orderPlaceholder = currentPlatformObj.example ? "e.g. " + currentPlatformObj.example : "Enter order #";

  // 2026-05-14: multi-photo support — up to 6 photos per handoff. Each
  // entry is a self-contained snapshot with its own EXIF metadata
  // resolved from EXIF DateTimeOriginal → Samsung filename pattern
  // (YYYYMMDD_HHMMSS) → file.lastModified. Source = 'camera' (fresh
  // capture) / 'library' (camera roll) / 'unknown'. Submit uploads
  // them sequentially and writes ONE sheet row carrying all URLs.
  const MAX_PHOTOS = 6;
  const [photos,setPhotos] = useState([]); // {dataUrl, mime, filename, capturedAt, gpsLat, gpsLng, exifPresent, source}
  const [note,setNote] = useState("");
  const [submitting,setSubmitting] = useState(false);
  const [error,setError] = useState("");
  // Today's orders for THIS site — backed by getdeliveryorderstoday.
  // Persists across staff swaps on the same tablet AND surfaces orders
  // logged from any other tablet at this site. Optimistic prepend on
  // submit; backend refresh reconciles right after.
  const [todaysOrders,setTodaysOrders] = useState(null);
  const [todaysErr,setTodaysErr] = useState(null);
  const [lightbox,setLightbox] = useState(null);

  // 2026-05-14 dedup guards. Staff occasionally tap Confirm twice on slow
  // peak-hour wifi (the photo upload can take 3-8s) — that produced 3x rows
  // on the admin Delivery Order Proof tile on 12 + 13 May. Two layers:
  //   (a) submitInFlight ref locks the second tap synchronously BEFORE the
  //       PIN modal can open again — the previous `submitting` state only
  //       flipped after the PIN callback ran, leaving an open race window.
  //   (b) clientToken is sent on the write and dedupes server-side, so
  //       genuine browser/network retries also collapse to one sheet row.
  //       Token regenerates after each successful submit so the next order
  //       gets a fresh idempotency window.
  const submitInFlight = useRef(false);
  const makeClientToken = () => {
    try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch(e) {}
    return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2,10) + Math.random().toString(36).slice(2,6);
  };
  const [clientToken,setClientToken] = useState(makeClientToken);

  // 2026-05-14: admin-managed ✅ right / ❌ wrong guidance examples,
  // fetched from getdeliveryorderguidance on mount. Two slots, global
  // config. Renders above the photo capture area as an impossible-to-
  // miss visual reference.
  // Never null: last good answer for this tablet, else the seed. The examples are
  // on screen from the first paint and the slow fetch only ever REPLACES them.
  const [guidance,setGuidance] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(GUIDANCE_CACHE_KEY) || "null");
      if (c && c.right && c.wrong) return c;
    } catch(e) {}
    return GUIDANCE_SEED;
  });
  const [guidanceLightbox,setGuidanceLightbox] = useState(null);
  useEffect(()=>{
    let dead = false;
    // Lane BL: the guidance can read Supabase (delivery_order_guidance_feed) behind
    // DELIVERY_CONFIG_READ_HOSTS. The OFF path (gate empty) runs the exact original fetch
    // below; the reader falls back to this same sheetFetch on any drift/error.
    //
    // Read as TEXT then JSON.parse, not r.json(): under load the /exec hands back a
    // Google HTML error page with HTTP 200, and r.json() throws on it — the same
    // silent-failure class as the EOD/sell-out drops.
    //
    // 25s, not 10s: this endpoint's own worst case is the ~13s raced figure above, so
    // a 10s bound would abort the ordinary slow-but-live read on every cold mount and
    // burn all three tries for nothing. The abort is only here to stop a genuinely
    // HUNG request holding the retries. Nothing on screen waits for this — the panel
    // already renders from cache/seed — so a long bound costs the staffer nothing.
    // No parameter: deliveryConfigReader calls the injected sheetFetch() with no
    // arguments, so an `ms` argument would have to carry a default or it would abort
    // at 0ms on the Lane BL path.
    const sheetFetch = () => {
      const ac = new AbortController();
      const t = setTimeout(()=>ac.abort(), 25000);
      return fetch(backend + "?action=getdeliveryorderguidance", {redirect:"follow", credentials:"omit", signal:ac.signal})
        .then(r => r.ok ? r.text() : null)
        .then(txt => { if (!txt) return null; try { return JSON.parse(txt); } catch(e) { return null; } })
        .finally(()=>clearTimeout(t));
    };
    const apply = d => {
      if (!d || !d.guidance || !d.guidance.right || !d.guidance.wrong) return false;
      // A blank pair from the AUTHORITATIVE Supabase feed is the un-imported empty
      // state (migration 1220 created the views; the importer has never run), not an
      // answer — keep what we have and retry. A blank pair from the SHEET is someone
      // deliberately pulling an example in the Command Centre, so it has to apply and
      // be cached — otherwise the seed puts the withdrawn photo straight back on the
      // next mount and no admin control can ever take it down.
      if (!d.guidance.right.imageUrl && !d.guidance.wrong.imageUrl && d._parity === "authoritative") return false;
      // Cache first, and regardless of `dead`: a localStorage write is safe after
      // unmount, and it is what makes the NEXT visit open on the real config instead
      // of the seed. Staff routinely leave this tile before a 9-13s read lands, so
      // gating the cache on `dead` would keep the tablet cold forever. Only the React
      // state update is gated.
      try { localStorage.setItem(GUIDANCE_CACHE_KEY, JSON.stringify(d.guidance)); } catch(e) {}
      if (!dead) setGuidance(d.guidance);
      return true;
    };
    const once = () => deliveryConfigReader.deliveryConfigReadEnabled()
      ? deliveryConfigReader.readDeliveryGuidance({ sheetFetch })
      : sheetFetch();
    (async () => {
      for (let i = 0; i < 3 && !dead; i++) {
        try { if (apply(await once())) return; } catch(e) {}
        if (i < 2) await new Promise(r => setTimeout(r, 2000 * (i + 1)));
      }
    })();
    return () => { dead = true; };
  },[]);

  // Order-list lightbox carousel index — for the multi-photo "Today's
  // orders" pop-up. Resets to 0 whenever the lightbox opens on a new row.
  const [lightboxIdx,setLightboxIdx] = useState(0);

  // Fetch platforms once on mount. Default Uber Eats stays in place if
  // the call fails — staff can still log handoffs without the admin
  // config landing.
  useEffect(()=>{
    // Lane BL: platforms can read Supabase (delivery_platforms_feed) behind
    // DELIVERY_CONFIG_READ_HOSTS. The OFF path (gate empty) runs the exact original fetch
    // below; the reader falls back to this same sheetFetch on any drift/error.
    const sheetFetch = () => fetch(backend + "?action=getdeliveryplatforms", {redirect:"follow", credentials:"omit"})
      .then(r => r.ok ? r.json() : null);
    const apply = d => {
      if (!d || !Array.isArray(d.platforms) || d.platforms.length === 0) return;
      setPlatforms(d.platforms);
      const firstActive = d.platforms.find(p => p.active);
      if (firstActive) setPlatform(firstActive.name);
    };
    if (deliveryConfigReader.deliveryConfigReadEnabled()) {
      deliveryConfigReader.readDeliveryPlatforms({ sheetFetch }).then(apply).catch(()=>{});
    } else {
      sheetFetch().then(apply).catch(()=>{});
    }
  },[]);

  const loadToday = () => {
    // Lane BM — on a write-drop host Google is no longer being filled, so the strip reads the
    // per-shop, today-only Supabase feed (delivery_orders_today, migration 1240) instead of
    // getdeliveryorderstoday. Feed rows carry no photo link (the pictures sit in the private
    // bucket behind the Command Centre signer); the rows THIS till logged keep their local
    // thumbnails across the refresh. OFF (every host today) = the original fetch below, untouched.
    if (deliveryWriteDrop.deliveryWriteDropEnabled()) {
      deliveryWriteDrop.readTodayOrders(site.key).then(res => {
        if (!res || !res.ok) { setTodaysErr((res && res.error) || "Could not load today's orders"); return; }
        setTodaysOrders(prev => deliveryWriteDrop.mergeLocalThumbnails(res.rows, prev));
        setTodaysErr(null);
      });
      return;
    }
    fetch(backend + "?action=getdeliveryorderstoday&site=" + encodeURIComponent(site.key), {redirect:"follow", credentials:"omit"})
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) { setTodaysErr("Could not load today's orders"); return; }
        if (d.status === "error") { setTodaysErr(d.error || "Could not load today's orders"); return; }
        const orders = d.orders || [];
        if (orders.length === 0 && __IS_PREVIEW_HOST) {
          setTodaysOrders(buildDemoOrders(site.key));
        } else {
          setTodaysOrders(orders);
        }
        setTodaysErr(null);
      })
      .catch(e => {
        if (__IS_PREVIEW_HOST) {
          setTodaysOrders(buildDemoOrders(site.key));
          setTodaysErr(null);
        } else {
          setTodaysErr(e.message || "Could not load today's orders");
        }
      });
  };
  useEffect(()=>{ loadToday(); },[]);

  // 2026-05-14: handleFile now APPENDS to the photos array instead of
  // replacing a single state slot. Each photo carries its own EXIF
  // metadata. Source = 'camera' | 'library' from which input fired the
  // change event — more reliable than the previous time-gap heuristic
  // (Samsung's library picker resets file.lastModified to now on copy).
  const handleFile = async (file, source) => {
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) { setError("Max " + MAX_PHOTOS + " photos per order"); return; }
    if (file.size > 6 * 1024 * 1024) { setError("Photo too large — keep it under 6MB"); return; }
    setError("");
    const mime = file.type || "image/jpeg";
    let resolvedTs = null;
    let hasExifEvidence = false;
    let extractedLat = null, extractedLng = null;
    try {
      if (window.exifr && typeof window.exifr.parse === "function") {
        const meta = await window.exifr.parse(file, { tiff: true, exif: true, gps: true, ifd0: true });
        if (meta) {
          const dt = meta.DateTimeOriginal || meta.CreateDate;
          if (dt) { resolvedTs = new Date(dt).getTime(); hasExifEvidence = true; }
          if (typeof meta.latitude === "number") { extractedLat = meta.latitude; hasExifEvidence = true; }
          if (typeof meta.longitude === "number") extractedLng = meta.longitude;
        }
      }
    } catch (e) { /* non-fatal */ }
    if (!resolvedTs && file.name) {
      const m = file.name.match(/(\d{4})(\d{2})(\d{2})[_-]?(\d{2})(\d{2})(\d{2})/);
      if (m) {
        const ts = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6])).getTime();
        if (!isNaN(ts) && ts > 0) resolvedTs = ts;
      }
    }
    if (!resolvedTs && file.lastModified) resolvedTs = file.lastModified;
    const reader = new FileReader();
    reader.onload = () => {
      const newPhoto = {
        dataUrl: String(reader.result || ""),
        mime: mime,
        filename: file.name || "order.jpg",
        capturedAt: resolvedTs ? new Date(resolvedTs).toISOString() : null,
        gpsLat: extractedLat,
        gpsLng: extractedLng,
        exifPresent: hasExifEvidence,
        source: (source === "camera" || source === "library") ? source : "unknown"
      };
      setPhotos(prev => [...prev, newPhoto]);
    };
    reader.onerror = () => setError("Could not read file");
    reader.readAsDataURL(file);
  };
  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const reset = () => {
    setOrderNo("");
    setPhotos([]);
    setNote(""); setSubmitting(false); setError("");
    submitInFlight.current = false;
    setClientToken(makeClientToken());
  };

  const canSubmit = !!orderNo.trim() && photos.length > 0 && !submitting;

  // 2026-05-14: multi-photo submit — upload each photo sequentially,
  // collect URLs in order, then POST deliveryOrderLogged with the array.
  // First photo's EXIF represents the row (existing single-EXIF columns
  // stay; per-photo EXIF parity is a future enhancement). Both endpoints
  // PIN-gated server-side. submitInFlight + clientToken stop duplicates.
  const submit = () => {
    if (!canSubmit || submitInFlight.current) return;
    submitInFlight.current = true;
    setSubmitting(true);
    // Strip a leading '#' (or any non-alphanumeric prefix) so order #s
    // like "#B0113" don't render as "##B0113" on the admin tile.
    const cleanOrderNo = orderNo.trim().replace(/^[^A-Za-z0-9]+/,"").slice(0,40);
    const releaseLock = () => { submitInFlight.current = false; setSubmitting(false); };
    requestPin(async (staffName, staffPin) => {
      setError("");
      try {
        // Lane BM — write-drop ON for this host (DELIVERY_WRITE_DROP_HOSTS or ?deliverywritedrop=on):
        // Google is NOT written. Both Apps Script POSTs below are skipped and the Supabase door
        // (record_courier_handoff, 0600) is AWAITED — it IS the record. A failure blocks "saved"
        // and shows a retry message; nothing is swallowed. The staff PIN goes to the door only
        // (to mint the pass), exactly as it does on the shadow path today. The row prepended here
        // carries the LOCAL photo thumbnails so the staff member still sees what they logged.
        // OFF (every host today) = the original code below, untouched.
        if (deliveryWriteDrop.deliveryWriteDropEnabled()) {
          // requireAllPhotos: this write IS the record, so a hand-off whose photos did not
          // ALL reach the bucket is refused BEFORE the row is written (no row = a clean retry
          // with the same keys; the form is kept, nothing is reset). Google's own path has
          // always failed the whole submit on a bad upload — a drop host keeps that rule.
          const dropRes = await deliveryOrdersSync.recordCourierHandoff({
            siteKey: site.key, orderNo: cleanOrderNo, platform, staffPin,
            clientToken, photos, legacyUrls: [], note, requireAllPhotos: true,
          });
          if (!dropRes || !dropRes.ok || dropRes.skipped) {
            setError(deliveryWriteDrop.handoffFailureMessage(dropRes));
            releaseLock();
            return;
          }
          const dropPrimary = photos[0];
          setTodaysOrders(prev => [{
            ts: Date.now(),
            orderNo: cleanOrderNo,
            platform: platform,
            staffName: staffName,
            photoUrl: dropPrimary.dataUrl,
            photoUrls: photos.map(p => p.dataUrl),
            photoCount: photos.length,
            note: note,
            site: site.key,
            capturedAt: dropPrimary.capturedAt,
            gpsLat: dropPrimary.gpsLat,
            gpsLng: dropPrimary.gpsLng,
            exifPresent: dropPrimary.exifPresent,
            source: dropPrimary.source
          }, ...(prev || [])]);
          reset();
          loadToday();
          return;
        }
        const uploadedUrls = [];
        for (let i = 0; i < photos.length; i++) {
          const photo = photos[i];
          const b64 = String(photo.dataUrl || "").split(",")[1] || "";
          const uploadRes = await fetch(backend, {
            method:"POST",
            headers:{"Content-Type":"text/plain;charset=utf-8"},
            body: JSON.stringify({
              action: "uploadDeliveryOrderPhoto",
              site: site.key,
              orderNo: cleanOrderNo,
              staffName, staffPin,
              base64: b64,
              mimeType: photo.mime,
              filename: photo.filename
            })
          });
          const uploadData = await uploadRes.json().catch(()=>null);
          if (!uploadData || !uploadData.ok || !uploadData.url) {
            setError((uploadData && uploadData.error) || ("Photo " + (i+1) + " upload failed — try again"));
            releaseLock();
            return;
          }
          uploadedUrls.push(uploadData.url);
        }
        // First photo's EXIF represents the row's single-photo metadata
        // columns. Keeps existing dispute-evidence schema unchanged.
        const primary = photos[0];
        const writeRes = await fetch(backend, {
          method:"POST",
          headers:{"Content-Type":"text/plain;charset=utf-8"},
          body: JSON.stringify({
            action: "deliveryOrderLogged",
            site: site.key,
            date: new Date().toLocaleDateString("en-GB"),
            orderNo: cleanOrderNo,
            platform: platform,
            staffName, staffPin,
            photoUrls: uploadedUrls,
            note: note,
            capturedAt: primary.capturedAt,
            gpsLat: primary.gpsLat,
            gpsLng: primary.gpsLng,
            exifPresent: primary.exifPresent,
            source: primary.source,
            clientToken: clientToken
          })
        });
        const writeData = await writeRes.json().catch(()=>null);
        if (!writeData || !(writeData.status === "success" || writeData.status === "ok")) {
          setError((writeData && writeData.error) || "Save failed — try again");
          releaseLock();
          return;
        }
        const newRow = {
          ts: Date.now(),
          orderNo: cleanOrderNo,
          platform: platform,
          staffName: staffName,
          photoUrl: uploadedUrls[0],
          photoUrls: uploadedUrls,
          note: note,
          site: site.key,
          capturedAt: primary.capturedAt,
          gpsLat: primary.gpsLat,
          gpsLng: primary.gpsLng,
          exifPresent: primary.exifPresent,
          source: primary.source
        };
        setTodaysOrders(prev => [newRow, ...(prev || [])]);
        // Dual-write the hand-off proof into Supabase — AFTER the sheet write has
        // already succeeded, and after the staff member has been told it saved, so
        // it can't slow the hand-off down. Photos go to the private bucket, the
        // Drive links ride along on the row so the evidence is never lost, and the
        // till's own clientToken keys the row so the same order appears exactly
        // once (the 12-14 May triplicate bug lived in this path). Best-effort: it
        // swallows its own errors and can never throw in here.
        // One-line kill: DELIVERY_ORDERS_SYNC_ENABLED in lib/deliveryOrdersSync.js.
        try {
          deliveryOrdersSync.recordCourierHandoff({
            siteKey: site.key, orderNo: cleanOrderNo, platform, staffPin,
            clientToken, photos, legacyUrls: uploadedUrls, note,
          });
        } catch (_) {}
        reset();
        loadToday();
      } catch (e) {
        setError(e.message || "Network error — try again");
        releaseLock();
      }
    }, releaseLock);
  };

  const fmtTs = (ms) => {
    try {
      const d = new Date(ms);
      return d.toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"});
    } catch (e) { return ""; }
  };

  // First photo's EXIF stands in as a representative dispute-evidence
  // signal for the row's existing single-EXIF columns. Per-photo
  // chips on every thumbnail would just be visual noise.
  const primary = photos[0] || null;
  // Carousel data for the order-list lightbox. Accepts both new rows
  // (photoUrls[]) and any legacy rows still serving a single photoUrl.
  const lightboxPhotos = lightbox ? (Array.isArray(lightbox.photoUrls) && lightbox.photoUrls.length ? lightbox.photoUrls : (lightbox.photoUrl ? [lightbox.photoUrl] : [])) : [];
  const openLightbox = (row) => { setLightboxIdx(0); setLightbox(row); };
  const lightboxNext = () => { if (lightboxPhotos.length > 1) setLightboxIdx(i => (i + 1) % lightboxPhotos.length); };
  const lightboxPrev = () => { if (lightboxPhotos.length > 1) setLightboxIdx(i => (i - 1 + lightboxPhotos.length) % lightboxPhotos.length); };

  return (
    <div style={S.content}>
      <div style={S.sectionCard}>
        <div style={S.secLabel}>🛵 Delivery Orders</div>
        <div style={S.secDesc}>Snap proof BEFORE handing over — every photo must show the <strong>full order contents + the order ticket</strong>. If the order's already gone, pick from your camera roll. Saves you in any dispute.</div>
      </div>

      {/* 2026-05-14: Impossible-to-miss reminder banner. Wasabi-on-Nori,
          bold, single-line — sits before the form so staff can't blow
          past it. Font scales down on narrow viewports to keep the line. */}
      <div style={{marginTop:14,padding:"14px 16px",borderRadius:14,background:B.wasabi,color:B.nori,fontWeight:800,fontSize:"clamp(0.78rem, 2.4vw, 0.98rem)",lineHeight:1.2,letterSpacing:"0.01em",border:"3px solid "+B.wasabi,boxShadow:"0 3px 12px rgba(212,251,111,0.35)",textAlign:"center",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
        📸 Please ensure all items and the order number are clearly visible
      </div>

      {/* 2026-05-14: ✅ Right / ❌ Wrong examples. Admin uploads via the
          Command Centre Delivery Order Proof tile → Guidance Images.
          Falls back to a placeholder if not yet configured. */}
      {guidance && (
        <div style={{marginTop:12,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <button type="button" onClick={()=>guidance.right.imageUrl && setGuidanceLightbox({label:"✅ Right",imageUrl:guidance.right.imageUrl,caption:guidance.right.caption,colorFg:"#7BC97E",colorBg:"rgba(123,201,126,0.12)"})} style={{padding:8,borderRadius:12,border:"2px solid rgba(123,201,126,0.55)",background:"rgba(123,201,126,0.08)",cursor:guidance.right.imageUrl?"pointer":"default",textAlign:"left",fontFamily:"inherit"}}>
            <div style={{fontSize:11,fontWeight:800,color:"#7BC97E",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:6}}>✅ Right</div>
            {guidance.right.imageUrl ? (
              <img src={guidance.right.imageUrl} alt="Right example" style={{width:"100%",aspectRatio:"1/1",objectFit:"cover",borderRadius:8,display:"block"}}/>
            ) : (
              <div style={{width:"100%",aspectRatio:"1/1",borderRadius:8,background:"rgba(123,201,126,0.18)",display:"flex",alignItems:"center",justifyContent:"center",color:"#7BC97E",fontSize:11,fontWeight:600}}>Example coming soon</div>
            )}
            {guidance.right.caption && <div style={{marginTop:6,fontSize:11,color:B.kewpie,opacity:0.75,lineHeight:1.3}}>{guidance.right.caption}</div>}
          </button>
          <button type="button" onClick={()=>guidance.wrong.imageUrl && setGuidanceLightbox({label:"❌ Wrong",imageUrl:guidance.wrong.imageUrl,caption:guidance.wrong.caption,colorFg:"#FF9B83",colorBg:"rgba(255,122,92,0.12)"})} style={{padding:8,borderRadius:12,border:"2px solid rgba(255,122,92,0.55)",background:"rgba(255,122,92,0.08)",cursor:guidance.wrong.imageUrl?"pointer":"default",textAlign:"left",fontFamily:"inherit"}}>
            <div style={{fontSize:11,fontWeight:800,color:"#FF9B83",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:6}}>❌ Wrong</div>
            {guidance.wrong.imageUrl ? (
              <img src={guidance.wrong.imageUrl} alt="Wrong example" style={{width:"100%",aspectRatio:"1/1",objectFit:"cover",borderRadius:8,display:"block"}}/>
            ) : (
              <div style={{width:"100%",aspectRatio:"1/1",borderRadius:8,background:"rgba(255,122,92,0.18)",display:"flex",alignItems:"center",justifyContent:"center",color:"#FF9B83",fontSize:11,fontWeight:600}}>Example coming soon</div>
            )}
            {guidance.wrong.caption && <div style={{marginTop:6,fontSize:11,color:B.kewpie,opacity:0.75,lineHeight:1.3}}>{guidance.wrong.caption}</div>}
          </button>
        </div>
      )}

      {/* Order # */}
      <div style={{marginTop:14}}>
        <label style={{display:"block",fontSize:"0.78rem",fontWeight:700,color:B.wasabi,letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:6}}>Order #</label>
        <input
          type="text"
          value={orderNo}
          onChange={e=>setOrderNo(e.target.value.toUpperCase())}
          placeholder={orderPlaceholder}
          maxLength={40}
          style={{...S.input,letterSpacing:"0.08em",fontWeight:700,fontSize:"1.05rem"}}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
        />
      </div>

      {/* Platform — driven by admin config. Static badge if one active
          platform; pill picker if multiple. */}
      <div style={{marginTop:14}}>
        <label style={{display:"block",fontSize:"0.78rem",fontWeight:700,color:B.wasabi,letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:6}}>Platform</label>
        {activePlatforms.length <= 1 ? (
          <div style={{display:"inline-flex",alignItems:"center",padding:"10px 16px",borderRadius:999,background:B.wasabi,color:B.nori,fontWeight:800,fontSize:"0.9rem",letterSpacing:"0.02em"}}>{currentPlatformObj.name}</div>
        ) : (
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {activePlatforms.map(p => (
              <button key={p.name} type="button" onClick={()=>setPlatform(p.name)} style={{
                padding:"10px 14px", borderRadius:999, border:"1px solid "+(platform===p.name?B.wasabi:"rgba(212,251,111,0.2)"),
                background: platform===p.name ? B.wasabi : "transparent",
                color: platform===p.name ? B.nori : B.kewpie,
                fontFamily:"inherit", fontWeight:700, fontSize:"0.88rem", cursor:"pointer"
              }}>{p.name}</button>
            ))}
          </div>
        )}
      </div>

      {/* 2026-05-14: Multi-photo grid (max 6). One photo is enough if it
          shows everything clearly; the "+ Add photo" tile makes adding
          more an option without making it feel mandatory. */}
      <div style={{marginTop:14}}>
        <label style={{display:"block",fontSize:"0.78rem",fontWeight:700,color:B.wasabi,letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:6}}>
          Photos
          {photos.length === 0 && <span style={{color:"#FF9B83",marginLeft:6,fontWeight:600,textTransform:"none",letterSpacing:0}}>required</span>}
        </label>
        {photos.length === 0 ? (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <label style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px 16px",border:"2px dashed rgba(212,251,111,0.3)",borderRadius:14,cursor:"pointer",background:"rgba(212,251,111,0.04)"}}>
              <span style={{fontSize:44}}>📷</span>
              <div style={{fontWeight:800,fontSize:"0.95rem",color:B.wasabi,marginTop:6}}>Take photo</div>
              <div style={{fontSize:"0.75rem",color:B.kewpie,opacity:0.55,marginTop:4}}>Show the whole order AND the ticket in one shot — one's enough if it's clear</div>
              <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>handleFile(e.target.files && e.target.files[0], "camera")}/>
            </label>
            <label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"10px 14px",borderRadius:10,cursor:"pointer",color:B.kewpie,opacity:0.7,fontSize:"0.82rem",fontWeight:600}}>
              <span style={{fontSize:18}}>🖼️</span>
              <span>Or upload from camera roll</span>
              <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleFile(e.target.files && e.target.files[0], "library")}/>
            </label>
          </div>
        ) : (
          <>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:8}}>
              {photos.map((p, idx) => (
                <div key={idx} style={{position:"relative",aspectRatio:"1/1",borderRadius:10,overflow:"hidden",border:"1px solid rgba(212,251,111,0.25)",background:"#0a1e1a"}}>
                  <img src={p.dataUrl} alt={"Photo " + (idx+1)} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                  <span style={{position:"absolute",top:4,left:4,background:"rgba(0,57,44,0.88)",color:B.wasabi,fontSize:10,fontWeight:800,padding:"2px 6px",borderRadius:999}}>{idx+1}</span>
                  <button type="button" onClick={()=>removePhoto(idx)} aria-label={"Remove photo " + (idx+1)} style={{position:"absolute",top:4,right:4,width:26,height:26,borderRadius:"50%",background:"rgba(216,68,60,0.95)",color:"#fff",border:"none",fontSize:15,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>×</button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label style={{aspectRatio:"1/1",borderRadius:10,border:"2px dashed rgba(212,251,111,0.45)",background:"rgba(212,251,111,0.06)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:B.wasabi,cursor:"pointer",fontSize:11,fontWeight:700,gap:4}}>
                  <span style={{fontSize:24,lineHeight:1}}>📷</span>
                  <span style={{textAlign:"center",padding:"0 6px"}}>+ Add photo</span>
                  <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>handleFile(e.target.files && e.target.files[0], "camera")}/>
                </label>
              )}
            </div>
            {photos.length < MAX_PHOTOS && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10,gap:8,flexWrap:"wrap"}}>
                <div style={{fontSize:11,color:B.kewpie,opacity:0.55,fontStyle:"italic"}}>One's enough if everything's clear. Tap + to add more if anything's hidden — up to {MAX_PHOTOS}.</div>
                <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 10px",borderRadius:8,cursor:"pointer",color:B.kewpie,opacity:0.6,fontSize:"0.75rem",fontWeight:600}}>
                  <span>🖼️</span><span>From camera roll</span>
                  <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleFile(e.target.files && e.target.files[0], "library")}/>
                </label>
              </div>
            )}
            {/* Representative EXIF chips for the FIRST photo (the row's
                single-EXIF columns reflect this photo's metadata). */}
            {primary && (primary.source !== "unknown" || primary.capturedAt || primary.exifPresent === false) && (
              <div style={{marginTop:10,display:"flex",gap:6,flexWrap:"wrap"}}>
                {primary.source === "camera" && (
                  <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.7)",color:B.wasabi,fontSize:"0.72rem",fontWeight:700}}>📷 Taken in app · photo 1</span>
                )}
                {primary.source === "library" && (
                  <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.7)",color:"#FFD68A",fontSize:"0.72rem",fontWeight:700}}>🖼️ From camera roll · photo 1</span>
                )}
                {primary.capturedAt && (
                  <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.7)",color:B.wasabi,fontSize:"0.72rem",fontWeight:700}}>🕒 {new Date(primary.capturedAt).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</span>
                )}
                {(primary.gpsLat !== null && primary.gpsLng !== null) && (
                  <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.7)",color:B.wasabi,fontSize:"0.72rem",fontWeight:700}}>📍 Location ✓</span>
                )}
                {primary.source === "unknown" && !primary.capturedAt && (
                  <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.7)",color:"#FFD68A",fontSize:"0.72rem",fontWeight:700}}>⚠ No metadata on photo 1</span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Note */}
      <div style={{marginTop:14}}>
        <label style={{display:"block",fontSize:"0.78rem",fontWeight:700,color:B.wasabi,letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:6}}>Note <span style={{color:B.kewpie,opacity:0.5,marginLeft:4,fontWeight:500,textTransform:"none",letterSpacing:0}}>(optional)</span></label>
        <textarea
          value={note}
          onChange={e=>setNote(e.target.value.slice(0,500))}
          placeholder="Anything the courier or admin should know about this order"
          rows={2}
          style={{...S.input,resize:"vertical",fontFamily:"inherit",minHeight:60}}
        />
        <div style={{textAlign:"right",fontSize:"0.7rem",color:B.kewpie,opacity:0.4,marginTop:2}}>{note.length}/500</div>
      </div>

      {error && (
        <div style={{marginTop:12,padding:"10px 12px",borderRadius:10,background:"rgba(255,122,92,0.18)",color:"#FF9B83",fontSize:13,fontWeight:600}}>{error}</div>
      )}
      {/* Submit */}
      <div style={{marginTop:16}}>
        <button type="button" onClick={submit} disabled={!canSubmit} style={{
          ...S.submitBtn,
          width:"100%", padding:"16px 20px", borderRadius:14, fontSize:"1rem",
          opacity: canSubmit ? 1 : 0.4, cursor: canSubmit ? "pointer" : "not-allowed"
        }}>
          {submitting ? ("Saving " + (photos.length > 1 ? photos.length + " photos…" : "…")) : "✓ Confirm"}
        </button>
      </div>

      {/* Today's orders — backed by getdeliveryorderstoday. Persists
          across staff swaps on the same tablet AND surfaces orders
          logged from any other tablet at this site today. */}
      <div style={{...S.sectionCard,marginTop:18}}>
        <div style={S.secLabel}>Today's orders {todaysOrders ? <span style={{marginLeft:6,color:B.kewpie,opacity:0.45,fontSize:"0.7rem",fontWeight:500}}>({todaysOrders.length})</span> : null}</div>
        {todaysOrders === null && !todaysErr && (
          <div style={{padding:"16px 0",textAlign:"center",color:B.kewpie,opacity:0.5,fontSize:"0.82rem"}}>Loading…</div>
        )}
        {todaysErr && (
          <div style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,122,92,0.18)",color:"#FF9B83",fontSize:13,fontWeight:600,marginTop:6}}>{todaysErr}</div>
        )}
        {todaysOrders && todaysOrders.length === 0 && (
          <div style={{padding:"16px 12px",textAlign:"center",color:B.kewpie,opacity:0.45,fontSize:"0.82rem",fontStyle:"italic"}}>No handoffs logged today yet.</div>
        )}
        {todaysOrders && todaysOrders.length > 0 && (
          <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:8}}>
            {todaysOrders.map((r,i) => {
              const rowPhotoCount = (Array.isArray(r.photoUrls) && r.photoUrls.length) ? r.photoUrls.length : (r.photoUrl ? 1 : 0);
              // Lane BM: a row from the Supabase today feed (write-drop hosts only) carries a photo
              // COUNT but no link — the pictures sit in the private bucket behind the Command Centre
              // signer. Show a "photo saved" chip instead of a broken <img>, and keep the lightbox
              // shut. Sheet rows never carry photoCount, so this is false on every OFF host.
              const rowPhotoLocked = rowPhotoCount === 0 && Number(r.photoCount) > 0;
              const rowPhotoShown = rowPhotoLocked ? Number(r.photoCount) : rowPhotoCount;
              return (
                <button key={i} type="button" onClick={rowPhotoLocked ? undefined : ()=>openLightbox(r)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:10,background:"rgba(254,250,240,0.04)",border:"1px solid rgba(212,251,111,0.08)",cursor:rowPhotoLocked ? "default" : "pointer",textAlign:"left",fontFamily:"inherit"}}>
                  <div style={{position:"relative",width:48,height:48,flex:"0 0 auto"}}>
                    {rowPhotoLocked ? (
                      <div title="Photo saved — view it in the Command Centre" style={{width:48,height:48,borderRadius:8,background:"rgba(212,251,111,0.12)",border:"1px solid rgba(212,251,111,0.28)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2}}>
                        <span style={{fontSize:15,lineHeight:1}}>🔒</span>
                        <span style={{fontSize:7.5,fontWeight:800,color:B.wasabi,letterSpacing:"0.04em",textTransform:"uppercase",lineHeight:1}}>photo saved</span>
                      </div>
                    ) : (
                      <img src={r.photoUrl || (r.photoUrls && r.photoUrls[0])} alt="" style={{width:48,height:48,objectFit:"cover",borderRadius:8,background:"rgba(0,0,0,0.2)"}} onError={e=>{e.target.style.opacity=0.2}}/>
                    )}
                    {rowPhotoShown > 1 && (
                      <span style={{position:"absolute",bottom:-3,right:-3,background:B.wasabi,color:B.nori,fontSize:9,fontWeight:800,padding:"1px 5px",borderRadius:999,border:"2px solid "+B.nori}}>+{rowPhotoShown - 1}</span>
                    )}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <span style={{color:B.wasabi,fontWeight:700,fontSize:"0.92rem",letterSpacing:"0.04em"}}>#{(r.orderNo||"").replace(/^[^A-Za-z0-9]+/,"")}</span>
                      <span style={{fontSize:"0.68rem",fontWeight:700,padding:"1px 7px",borderRadius:999,background:"rgba(212,251,111,0.18)",color:B.wasabi,letterSpacing:"0.04em"}}>{r.platform}</span>
                    </div>
                    <div style={{color:B.kewpie,opacity:0.55,fontSize:"0.72rem",marginTop:2}}>{fmtTs(r.ts)}{r.staffName ? " · " + r.staffName : ""}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Order-list lightbox — supports multi-photo carousel (prev/next +
          thumb dots). Tapping outside the inner card closes; the inner
          card stops the event. */}
      {lightbox && (
        <div style={{...S.overlay,zIndex:700}} onClick={()=>setLightbox(null)}>
          <div style={{maxWidth:"94vw",maxHeight:"86vh",display:"flex",flexDirection:"column",gap:8}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <div>
                <div style={{color:B.wasabi,fontWeight:800,fontSize:"1.1rem",letterSpacing:"0.05em"}}>#{(lightbox.orderNo||"").replace(/^[^A-Za-z0-9]+/,"")}</div>
                <div style={{color:B.kewpie,opacity:0.7,fontSize:"0.78rem"}}>{lightbox.platform}{lightbox.staffName ? " · " + lightbox.staffName : ""} · {fmtTs(lightbox.ts)}{lightboxPhotos.length > 1 ? " · photo " + (lightboxIdx+1) + "/" + lightboxPhotos.length : ""}</div>
              </div>
              <button type="button" onClick={()=>setLightbox(null)} style={{padding:"8px 14px",borderRadius:8,background:"transparent",color:B.kewpie,border:"1px solid rgba(254,250,240,0.3)",fontFamily:"inherit",fontWeight:700,cursor:"pointer"}}>Close</button>
            </div>
            <div style={{position:"relative"}}>
              {lightboxPhotos.length > 0 ? (
                <img src={lightboxPhotos[lightboxIdx]} alt="Order proof" style={{maxWidth:"100%",maxHeight:"68vh",objectFit:"contain",borderRadius:12,background:"#000",display:"block",margin:"0 auto"}}/>
              ) : (
                <div style={{padding:60,textAlign:"center",color:B.kewpie,opacity:0.4}}>No photo on this row.</div>
              )}
              {lightboxPhotos.length > 1 && (
                <>
                  <button type="button" onClick={lightboxPrev} aria-label="Previous photo" style={{position:"absolute",top:"50%",left:10,transform:"translateY(-50%)",width:44,height:44,borderRadius:"50%",background:"rgba(0,57,44,0.75)",color:B.wasabi,border:"1px solid "+B.wasabi,fontFamily:"inherit",fontSize:22,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>‹</button>
                  <button type="button" onClick={lightboxNext} aria-label="Next photo" style={{position:"absolute",top:"50%",right:10,transform:"translateY(-50%)",width:44,height:44,borderRadius:"50%",background:"rgba(0,57,44,0.75)",color:B.wasabi,border:"1px solid "+B.wasabi,fontFamily:"inherit",fontSize:22,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>›</button>
                </>
              )}
            </div>
            {lightboxPhotos.length > 1 && (
              <div style={{display:"flex",gap:6,justifyContent:"center",flexWrap:"wrap"}}>
                {lightboxPhotos.map((u, i) => (
                  <button key={i} type="button" onClick={()=>setLightboxIdx(i)} style={{padding:0,width:48,height:48,borderRadius:8,overflow:"hidden",border:"2px solid "+(i===lightboxIdx?B.wasabi:"rgba(254,250,240,0.18)"),background:"rgba(0,0,0,0.2)",cursor:"pointer"}}>
                    <img src={u} alt={"Thumb "+(i+1)} style={{width:"100%",height:"100%",objectFit:"cover",display:"block",opacity:i===lightboxIdx?1:0.7}}/>
                  </button>
                ))}
              </div>
            )}
            {lightbox.note && <div style={{padding:"8px 12px",borderRadius:8,background:"rgba(254,250,240,0.06)",color:B.kewpie,fontSize:"0.85rem"}}>{lightbox.note}</div>}
          </div>
        </div>
      )}

      {/* Guidance examples lightbox — full-size view of the ✅ / ❌
          reference photo when a staffer taps either example tile. */}
      {guidanceLightbox && (
        <div style={{...S.overlay,zIndex:700}} onClick={()=>setGuidanceLightbox(null)}>
          <div style={{maxWidth:"94vw",maxHeight:"86vh",display:"flex",flexDirection:"column",gap:8}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <div style={{color:guidanceLightbox.colorFg,fontWeight:800,fontSize:"1.1rem",letterSpacing:"0.04em"}}>{guidanceLightbox.label}</div>
              <button type="button" onClick={()=>setGuidanceLightbox(null)} style={{padding:"8px 14px",borderRadius:8,background:"transparent",color:B.kewpie,border:"1px solid rgba(254,250,240,0.3)",fontFamily:"inherit",fontWeight:700,cursor:"pointer"}}>Close</button>
            </div>
            <img src={guidanceLightbox.imageUrl} alt={guidanceLightbox.label} style={{maxWidth:"100%",maxHeight:"68vh",objectFit:"contain",borderRadius:12,background:"#000"}}/>
            {guidanceLightbox.caption && <div style={{padding:"10px 14px",borderRadius:10,background:guidanceLightbox.colorBg,color:guidanceLightbox.colorFg,fontSize:"0.92rem",fontWeight:600}}>{guidanceLightbox.caption}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================
// CLUB CARD SCANNER — full port from standalone scanner.html
// ===========================================
// Ported from scanner.html into the unified app as native JSX. Uses:
// - html5-qrcode (loaded from CDN in the <head>) for QR scanning
// - Railway Postgres backend for stamp/lookup/migrate API calls
// - IndexedDB for offline scan queue
// - The unified app's existing PIN entry — Railway auth happens in parallel
//   during login and hands a token + staff record down via props (no second login)
// First-party API domain (Cloudflare-fronted) — same host the customer app uses.
// Using api.eatrolled.com instead of the raw *.up.railway.app name so flaky
// staff hotspots / carrier Private-DNS can't fail to resolve or block the till.
// The railway.app domain stays live as a fallback if we ever need to revert.
const CC_API = "https://api.eatrolled.com/api/v1";
const CC_DB_NAME = "rolled-scanner";
const CC_QUEUE_STORE = "scan-queue";
// Offline REDEMPTIONS (rewards + special cards) queue separately from stamps — they
// sync to a reconcile path (not a plain stamp) and carry the staff-picked reward/card
// + its cached Square code. DB bumped to v2 to add this store.
const CC_REDEEM_STORE = "redeem-queue";
// Scanner unlocks made with no connection (JJ, 13 Oct 2026: "let them back into the scanner
// offline") — sent to /scanner/offline-pin-uses once someone signs in online. Stamps and
// redemptions carry their own offline-PIN details in their queues. Its OWN database, so
// "rolled-scanner" stays at version 2: a build rolled back onto this tablet opens version 2,
// and on a newer database that fails outright (VersionError), taking the offline stamp and
// redemption queues down with it.
const CC_PINUSE_DB = "rolled-scanner-pinuses";
const CC_PINUSE_STORE = "pin-uses";

function ccOpenDB(){
  return new Promise((resolve,reject)=>{
    const req=indexedDB.open(CC_DB_NAME,2);
    req.onupgradeneeded=(e)=>{const db=e.target.result;if(!db.objectStoreNames.contains(CC_QUEUE_STORE))db.createObjectStore(CC_QUEUE_STORE,{keyPath:"id",autoIncrement:true});if(!db.objectStoreNames.contains(CC_REDEEM_STORE))db.createObjectStore(CC_REDEEM_STORE,{keyPath:"id",autoIncrement:true})};
    req.onsuccess=()=>resolve(req.result);
    req.onerror=()=>reject(req.error);
  });
}
function ccOpenPinUseDB(){
  return new Promise((resolve,reject)=>{
    const req=indexedDB.open(CC_PINUSE_DB,1);
    req.onupgradeneeded=(e)=>{const db=e.target.result;if(!db.objectStoreNames.contains(CC_PINUSE_STORE))db.createObjectStore(CC_PINUSE_STORE,{keyPath:"event_key"})};
    req.onsuccess=()=>resolve(req.result);
    req.onerror=()=>reject(req.error);
  });
}
async function ccEnqueue(data){
  // Belt-and-braces per-card dedupe: every queued row is unsynced (sync removes on
  // success), so a queued row with the same loyalty_id means this card is already
  // waiting — a second offline scan of it must not become a second stamp on sync.
  if(data&&data.loyalty_id){const existing=await ccGetQueued();if(existing.some(q=>q.loyalty_id===data.loyalty_id))return;}
  const db=await ccOpenDB();const tx=db.transaction(CC_QUEUE_STORE,"readwrite");tx.objectStore(CC_QUEUE_STORE).add({...data,queued_at:new Date().toISOString()});return new Promise((res,rej)=>{tx.oncomplete=res;tx.onerror=()=>rej(tx.error)})}
async function ccGetQueued(){const db=await ccOpenDB();const tx=db.transaction(CC_QUEUE_STORE,"readonly");const s=tx.objectStore(CC_QUEUE_STORE);return new Promise((res,rej)=>{const r=s.getAll();r.onsuccess=()=>res(r.result);r.onerror=()=>rej(r.error)})}
async function ccRemoveFromQueue(id){const db=await ccOpenDB();const tx=db.transaction(CC_QUEUE_STORE,"readwrite");tx.objectStore(CC_QUEUE_STORE).delete(id);return new Promise((res,rej)=>{tx.oncomplete=res;tx.onerror=()=>rej(tx.error)})}
// Persist a bumped retry count on a queued scan (dead-letter after N attempts).
async function ccBumpAttempts(item){const db=await ccOpenDB();const tx=db.transaction(CC_QUEUE_STORE,"readwrite");tx.objectStore(CC_QUEUE_STORE).put({...item,attempts:(item.attempts||0)+1});return new Promise((res,rej)=>{tx.oncomplete=res;tx.onerror=()=>rej(tx.error)})}
// ── Offline REDEMPTION queue (rewards + special cards) ───────────────────────
// One row per PHYSICAL redemption (each gets its own idempotency_key). We do NOT dedupe
// per (customer+kind+ref): the backend is authoritative — a repeat of a capped card lands
// a conflict, a repeat reward finds nothing to claim (conflict_none), and an UNCAPPED card
// (Mates Rates / Staff) is legitimately redeemable again in the same offline window, which
// a client-side dedupe would wrongly block. Accidental same-screen double-taps are already
// stopped by the picker's own busy/picked guard. Returns true once persisted.
async function ccEnqueueRedeem(data){
  const db=await ccOpenDB();const tx=db.transaction(CC_REDEEM_STORE,"readwrite");tx.objectStore(CC_REDEEM_STORE).add({...data,queued_at:new Date().toISOString()});return new Promise((res,rej)=>{tx.oncomplete=()=>res(true);tx.onerror=()=>rej(tx.error)})}
async function ccGetQueuedRedeems(){const db=await ccOpenDB();const tx=db.transaction(CC_REDEEM_STORE,"readonly");const s=tx.objectStore(CC_REDEEM_STORE);return new Promise((res,rej)=>{const r=s.getAll();r.onsuccess=()=>res(r.result);r.onerror=()=>rej(r.error)})}
async function ccRemoveRedeem(id){const db=await ccOpenDB();const tx=db.transaction(CC_REDEEM_STORE,"readwrite");tx.objectStore(CC_REDEEM_STORE).delete(id);return new Promise((res,rej)=>{tx.oncomplete=res;tx.onerror=()=>rej(tx.error)})}
async function ccBumpRedeemAttempts(item){const db=await ccOpenDB();const tx=db.transaction(CC_REDEEM_STORE,"readwrite");tx.objectStore(CC_REDEEM_STORE).put({...item,attempts:(item.attempts||0)+1});return new Promise((res,rej)=>{tx.oncomplete=res;tx.onerror=()=>rej(tx.error)})}
// ── Offline scanner unlocks (offline PINs, JJ 13 Oct 2026) ───────────────────
// { event_key, kind:"unlock", used_at, tablet_auth, tablet_name, pin_enc, site_id } — the PIN
// only ever encrypted for the server (lib/offlinePin.js encryptPin).
async function ccQueuePinUse(data){const db=await ccOpenPinUseDB();const tx=db.transaction(CC_PINUSE_STORE,"readwrite");tx.objectStore(CC_PINUSE_STORE).put(data);return new Promise((res,rej)=>{tx.oncomplete=()=>res(true);tx.onerror=()=>rej(tx.error)})}
async function ccGetPinUses(){const db=await ccOpenPinUseDB();const tx=db.transaction(CC_PINUSE_STORE,"readonly");const s=tx.objectStore(CC_PINUSE_STORE);return new Promise((res,rej)=>{const r=s.getAll();r.onsuccess=()=>res(r.result);r.onerror=()=>rej(r.error)})}
async function ccRemovePinUse(key){const db=await ccOpenPinUseDB();const tx=db.transaction(CC_PINUSE_STORE,"readwrite");tx.objectStore(CC_PINUSE_STORE).delete(key);return new Promise((res,rej)=>{tx.oncomplete=res;tx.onerror=()=>rej(tx.error)})}
// Send them in batches of up to 50 once there's a scanner token again. The server answers
// per event; only a "failed" (its database errored) stays for the next try. Single-flight.
let _ccPinUseSync=null;
function ccSyncPinUses(token){
  if(!token) return Promise.resolve();
  if(!_ccPinUseSync) _ccPinUseSync=(async()=>{
    try{
      const all=await ccGetPinUses();
      for(let i=0;i<all.length;i+=50){
        const batch=all.slice(i,i+50);
        // Slow by nature (the server may look a new starter up in the staff directory): the
        // long timeout, so a slow answer can't flip an online till into offline mode.
        const r=await ccApiCall("/scanner/offline-pin-uses",token,{method:"POST",body:JSON.stringify({uses:batch}),timeoutMs:CC_SLOW_TIMEOUT_MS});
        const results=(r&&Array.isArray(r.results))?r.results:[];
        for(const x of results){ if(x&&x.event_key&&x.status!=="failed") await ccRemovePinUse(x.event_key); }
      }
    }catch(e){ console.error("[PIN-USES] sync failed",e); }
    finally{ _ccPinUseSync=null; }
  })();
  return _ccPinUseSync;
}
// A stable key per PHYSICAL scan, reused on every re-send (offline enqueue + the
// lost-response re-enqueue + each sync retry) so the backend applies it exactly
// once — the offline-queue double-stamp guard (backend migration 074).
function ccMakeIdemKey(){try{return crypto.randomUUID()}catch(e){return "scan-"+Date.now()+"-"+Math.random().toString(36).slice(2,10)}}
// Drop a queued scan after this many failed sync attempts (poison-item dead-letter).
const CC_SYNC_MAX_ATTEMPTS = 5;

// ── Staff token auto-refresh ─────────────────────────────────────────────
// pin-login hands the scanner a 12h access token AND a long-lived refresh
// token. We keep the refresh token and, when a call comes back 401 "Token
// expired", silently mint a fresh access token and replay the call once — so
// staff never get bounced to the PIN pad mid-shift. Mirrors the customer
// app's tryRefresh. The app shell registers ccAuth.onToken (push the new token
// into React state) + ccAuth.onExpired (refresh token itself is dead → drop to
// the login screen). Single-flight so a burst of 401s only refreshes once.
// `epoch` goes up on every sign-out (Log out or the idle lock): a refresh that was
// in flight when the person signed out is dropped when it lands, so the previous
// person's token can never reach the next session, and a late 401 can never sign
// the NEXT person out (session lock, JJ 28 Sep 2026).
const ccAuth = { refresh: null, onToken: null, onExpired: null, epoch: 0 };
let _ccRefreshing = null, _ccRefreshingEpoch = -1;
function ccLoadRefresh(){
  try{const r=sessionStorage.getItem("rolled.cc.session.v1");if(r){const p=JSON.parse(r);if(p&&p.refresh){ccAuth.refresh=p.refresh}}}catch(e){}
  return ccAuth.refresh;
}
async function ccTryRefresh(){
  const ep = ccAuth.epoch;
  if(_ccRefreshing && _ccRefreshingEpoch===ep) return _ccRefreshing;
  const rt = ccAuth.refresh || ccLoadRefresh();
  if(!rt) return null;
  _ccRefreshingEpoch = ep;
  _ccRefreshing = (async()=>{
    try{
      const res=await fetch(CC_API+"/staff/refresh",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({refresh_token:rt}),signal:ccTimeoutSignal()});
      if(ccAuth.epoch!==ep) return null; // signed out while this was in flight — the answer belongs to the previous person
      if(res.status===401){
        ccAuth.refresh=null;
        if(SESSION_RESTORE_OK){try{const r=sessionStorage.getItem("rolled.cc.session.v1");if(r){const p=JSON.parse(r);delete p.refresh;sessionStorage.setItem("rolled.cc.session.v1",JSON.stringify(p))}}catch(e){}}
        if(ccAuth.onExpired) ccAuth.onExpired();
        return null;
      }
      if(!res.ok) return null;
      const data=await res.json();
      const t=data&&data.access_token;
      if(!t || ccAuth.epoch!==ep) return null;
      if(SESSION_RESTORE_OK){try{const r=sessionStorage.getItem("rolled.cc.session.v1");const p=r?JSON.parse(r):{};p.token=t;sessionStorage.setItem("rolled.cc.session.v1",JSON.stringify(p))}catch(e){}}
      if(ccAuth.onToken) ccAuth.onToken(t);
      return t;
    }catch(e){ return null; }
    finally{ _ccRefreshing=null; }
  })();
  return _ccRefreshing;
}

// ── Slow connection = offline (JJ, 29 Sep 2026) ───────────────────────────────
// navigator.onLine only says the tablet has a network, not that the Club Card
// server answers: mall wifi can be joined yet dead, and the till used to freeze on
// a request that never came back. Every ccApiCall is now bounded; one that gets no
// answer in time throws an isNetwork error and marks the connection DOWN, and the
// next call that gets any answer marks it back up. Scanner decisions read
// ccIsOffline() (no network OR marked down), so on a slow connection the till
// switches to its offline mode (stamps queue, redemptions use the offline picker)
// after about 8 seconds instead of freezing.
const CC_API_TIMEOUT_MS = 8000;
// The few calls that are slow by nature get longer: the Square-backed day summary
// on the FOH home, and every photo upload (paper-card migrations, keychain, FS4L).
const CC_SLOW_TIMEOUT_MS = 30000;
let _ccNetDown = false;
const _ccNetSubs = new Set();
function ccNetDown(){ return _ccNetDown; }
// The scanner-only offline session (signed in at the PIN pad with no connection, so there's no
// scanner token): until someone signs in again, every Club Card call counts as no connection.
// Otherwise any answer, even the 401 a token-less call gets, would clear the down mark and send
// the scanner down its online paths with no token (review, 13 Oct 2026).
let _ccScannerOnly = false;
function ccScannerOnly(){ return _ccScannerOnly; }
function ccSetScannerOnly(on){ _ccScannerOnly=!!on; if(_ccScannerOnly) ccSetNetDown(true); ccSyncPullToRefresh(); }
// Signed in to the full app while the Club Card server didn't answer (it's down, or there's no
// Wi-Fi on an owner's break-glass sign-in), so there's no scanner token: the scanner runs its
// offline mode, and a token-less Club Card call counts as no connection, until a sign-in gets a
// token (review, 13 Oct 2026). Unlike the scanner-only session, other PIN prompts still ask the server.
let _ccTokenless = false;
function ccSetTokenless(on){ _ccTokenless=!!on; if(_ccTokenless) ccSetNetDown(true); ccSyncPullToRefresh(); }
function ccIsOffline(){ return (typeof navigator!=="undefined" && !navigator.onLine) || _ccNetDown || _ccScannerOnly || _ccTokenless; }
function ccSetNetDown(down){
  down=!!down;
  if(down===_ccNetDown) return;
  _ccNetDown=down;
  _ccNetSubs.forEach(f=>{ try{ f(down); }catch(e){} });
  ccSyncPullToRefresh();
}
// Pull-to-refresh is off while the till is offline (JJ, 14 Oct 2026: an accidental pull-down
// with no internet, on the Samsung tablet's Chrome). Offline a reload can't fetch the app at
// all, and on a shop host a reload signs everyone out, so the till would sit on a browser
// error until the Wi-Fi came back. Online it's as before. overscroll-behavior on the page
// root is how Chrome on Android switches its pull-to-refresh off.
function ccSyncPullToRefresh(){
  try {
    const v = ccIsOffline() ? "none" : "";
    document.documentElement.style.overscrollBehaviorY = v;
    if (document.body) document.body.style.overscrollBehaviorY = v;
  } catch(e) {}
}
if (typeof window !== "undefined") {
  try { window.addEventListener("online", ccSyncPullToRefresh); window.addEventListener("offline", ccSyncPullToRefresh); } catch(e) {}
}
// Is the Club Card server answering? One small GET of /health, read properly (the API's CORS
// lets the tills read it): only a 2xx counts. In a Railway outage the edge still answers, with
// a 502, and that must read as down (review, 13 Oct 2026). Used before letting a PIN through
// offline, to tell "the server is slow" from "no connection" (a slow genuine "no" to a wrong PIN
// must never be mistaken for an outage), and by the scanner-only offline session to notice the
// Wi-Fi coming back. Resolves true / false; never throws.
async function ccServerReachable(ms){
  const ctrl=new AbortController();
  const t=setTimeout(()=>{try{ctrl.abort()}catch(e){}},ms||3000);
  try{ const r=await fetch(CC_API.replace(/\/api\/v1$/,"")+"/health?t="+Date.now(),{cache:"no-store",signal:ctrl.signal}); return !!(r&&r.ok); }
  catch(e){ return false; }
  finally{ clearTimeout(t); }
}
// Re-renders a component when the flag flips (the Offline pill).
function useCcNetDown(){
  const [down,setDown]=useState(_ccNetDown);
  useEffect(()=>{ setDown(_ccNetDown); _ccNetSubs.add(setDown); return ()=>{ _ccNetSubs.delete(setDown); }; },[]);
  return down;
}
// No answer: timed out, or failed below HTTP ("Failed to fetch" on Chrome, "Load
// failed" on Safari, "NetworkError…" on Firefox, or an abort). Worded like the
// till's other connection errors, because some screens show it as it is.
function ccNetworkError(cause,timedOut){
  ccSetNetDown(true);
  const err=new Error("Connection error — check signal and retry.");
  err.isNetwork=true;
  err.timedOut=!!timedOut;
  err.cause=cause;
  return err;
}
// ccTryRefresh's fetch gets the same bound, so a hung refresh fails (null, its
// existing failure outcome) instead of stalling every caller waiting behind its
// single-flight. Aborting a request that already finished does nothing.
function ccTimeoutSignal(){
  const c=new AbortController();
  setTimeout(()=>{ try{ c.abort(); }catch(e){} },CC_API_TIMEOUT_MS);
  return c.signal;
}

async function ccApiCall(endpoint,token,options={},_retried){
  // The scanner-only offline session never asks the server (see ccScannerOnly), and with no
  // scanner token after a sign-in the server didn't answer, a token-less call can't succeed
  // either (see ccSetTokenless).
  if(_ccScannerOnly || (_ccTokenless && !token)) throw ccNetworkError(new Error("no scanner token — offline"),false);
  const headers={"Content-Type":"application/json"};
  if(token)headers["Authorization"]="Bearer "+token;
  // options.timeoutMs (default CC_API_TIMEOUT_MS) and options.signal bound the whole
  // call, reply body included; everything else goes to fetch. A timed-out request is
  // aborted, so a late reply can never land on a screen staff have moved on from.
  const {timeoutMs,signal,...init}=options;
  const bound=pinAuth.boundedSignal(signal||null,timeoutMs>0?timeoutMs:CC_API_TIMEOUT_MS);
  let res,text;
  try{
    res=await fetch(CC_API+endpoint,{...init,signal:bound.signal,headers:{...headers,...(init.headers||{})}});
    // Read as text first so non-JSON error bodies (502/504 gateway HTML, Cloudflare interstitials)
    // surface a clean HTTP status message instead of a confusing JSON parse error
    text=await res.text();
  }catch(e){
    throw ccNetworkError(e,bound.timedOut());
  }finally{
    bound.release();
  }
  // It answered, so the connection works. A 5xx is the server failing, not the
  // connection: it neither sets nor clears the flag, and errors exactly as before.
  if(res.status<500)ccSetNetDown(false);
  if(res.status===401 && !_retried){
    const fresh=await ccTryRefresh();
    if(fresh) return ccApiCall(endpoint,fresh,options,true);
  }
  let data=null;
  try{data=text?JSON.parse(text):null}catch(e){data=null}
  if(!res.ok){
    const msg=(data&&data.error)?data.error:("HTTP "+res.status+(res.statusText?" "+res.statusText:""));
    // Carry the backend's typed failure detail through so the scanner can word
    // it for staff (see ccStaffError) instead of one generic red toast.
    const err=new Error(msg);
    err.httpStatus=res.status;
    if(data){err.cardStatus=data.status;err.reason=data.reason;err.lastClaimedAt=data.last_claimed_at;err.lastClaimedSite=data.last_claimed_site;err.actualCardType=data.actual_card_type;err.actualDisplayName=data.actual_display_name;err.data=data;}
    throw err;
  }
  return data;
}

// Drain the offline REDEMPTION queue → POST /scanner/redeem-sync (reconcile). A row is
// cleared once reconciled — whether it 'applied' or came back a 'conflict' (already
// claimed) — because the backend has now recorded it either way. A redemption is NEVER
// dead-lettered/deleted the way a stamp is: the discount was already applied at the till,
// so dropping an unsynced redeem would leave a capped card burned nowhere server-side and
// silently re-redeemable. On a network/5xx error we keep the row and keep retrying (any
// terminal 2xx — applied or conflict — clears it); after enough failures we just warn.
//
// Module-level (not a screen closure) and driven from the always-mounted scanner SHELL on
// reconnect + mount, so it fires regardless of which sub-screen is open. The redeem queue
// is FILLED from the redeem screen but must DRAIN even when staff never open the stamp
// scanner — otherwise a reconnected till could leave a money event unreconciled (and a
// capped card re-redeemable at other tills) until someone happened to open that screen.
// Single-flight: the shell's mount / 'online' / 15 s drains share one run, so a
// slow connection (each POST can wait out the ccApiCall timeout) never stacks runs
// or sends the same row twice at once.
let _ccRedeemSync=null;
function ccSyncRedeems(token, showToast){
  if(!token) return Promise.resolve();   // the scanner-only offline session has none — rows wait
  if(!_ccRedeemSync) _ccRedeemSync=ccSyncRedeemsOnce(token,showToast).finally(()=>{ _ccRedeemSync=null; });
  return _ccRedeemSync;
}
async function ccSyncRedeemsOnce(token, showToast){
  try{
    const queued=await ccGetQueuedRedeems();
    if(!queued||queued.length===0)return;
    let conflicts=0,stuck=0,applied=0;
    for(const r of queued){
      try{
        const res=await ccApiCall("/scanner/redeem-sync",token,{method:"POST",body:JSON.stringify({loyalty_id:r.loyalty_id,kind:r.kind,ref:r.ref,discount_name:r.discount_name||null,site_id:r.site_id,staff_name:r.staff||null,idempotency_key:r.idempotency_key,queued_at:r.queued_at,...(r.staff_auth?{staff_auth:r.staff_auth,staff_pin_enc:r.staff_pin_enc||undefined}:{})})});
        if(res&&res.outcome&&res.outcome!=="applied")conflicts++;else applied++;
        await ccRemoveRedeem(r.id);
      }catch(err){
        console.error("[REDEEM-SYNC]",err);
        // Keep the row and retry — do NOT delete. Bump attempts for visibility/backoff.
        await ccBumpRedeemAttempts(r);
        if((r.attempts||0)+1>=CC_SYNC_MAX_ATTEMPTS)stuck++;
      }
    }
    if(typeof showToast==="function"){
      if(applied>0&&conflicts===0&&stuck===0)showToast("Offline redemption"+(applied>1?"s":"")+" synced","success");
      else if(conflicts>0)showToast(conflicts+" offline redemption(s) flagged for review — synced","info",4000);
      if(stuck>0)showToast(stuck+" redemption(s) still trying to sync — keep the tablet online; tell a manager if it sticks","info",5000);
    }
  }catch(err){console.error("[REDEEM-SYNC] failed",err)}
}

// Drain the offline STAMP queue → POST /scanner/scan. Lived inside the stamp
// scanner until 29 Sep 2026; module-level now so the always-mounted scanner shell
// can also run it on a timer (a till on bad wifi never fires 'online', so queued
// stamps used to wait for someone to reopen the scanner). Single-flight like
// ccSyncRedeems. A row is dead-lettered only when the server definitively refuses
// it (a 4xx such as a deleted customer) CC_SYNC_MAX_ATTEMPTS times; no answer, a
// 5xx, 401 (sign-in trouble, not the stamp), 408 or 429 keep it without counting,
// so bad wifi can no longer delete genuine stamps; the server's own "already stamped" refusal (anti-farm 429 or near-duplicate) drops the row at once. No answer also ends the run —
// the rest would only wait out the same timeout. quiet (the timer): no
// "Syncing…" / "will retry" toasts every 20 s; synced and dropped still show.
let _ccScanSync=null;
function ccSyncQueue(token,showToast,setQueueCount,quiet){
  if(!token) return Promise.resolve();   // the scanner-only offline session has none — rows wait
  if(!_ccScanSync) _ccScanSync=ccSyncQueueOnce(token,showToast,setQueueCount,quiet).finally(()=>{ _ccScanSync=null; });
  return _ccScanSync;
}
async function ccSyncQueueOnce(token,showToast,setQueueCount,quiet){
  const toast=typeof showToast==="function"?showToast:()=>{};
  try{
    const queued=await ccGetQueued();
    if(queued.length===0)return;
    if(!quiet)toast("Syncing "+queued.length+" queued scan"+(queued.length>1?"s":"")+"...","info");
    let dropped=0;
    for(const scan of queued){
      try{
        // idempotency_key is carried from when the scan was queued — a re-send the
        // server already applied dedupes cleanly (no double stamp); a genuine
        // offline scan applies now.
        // queued_offline:true — every scan drained from this queue was captured while the
        // till had no answer from the server; the Command Centre badges it "Offline"
        // (stamps.queued_offline, from the never-merged #386). staff_auth + staff_pin_enc:
        // the PIN couldn't be checked at the time, so the server checks it now (offline PINs).
        await ccApiCall("/scanner/scan",token,{method:"POST",body:JSON.stringify({loyalty_id:scan.loyalty_id,site_id:scan.site_id,channel:scan.channel||"unknown",staff_name:scan.staff||null,idempotency_key:scan.idempotency_key||undefined,queued_offline:true,queued_at:scan.queued_at||undefined,...(scan.staff_auth?{staff_auth:scan.staff_auth,staff_pin_enc:scan.staff_pin_enc||undefined}:{})})});
        await ccRemoveFromQueue(scan.id);
      }catch(err){
        console.error("[SYNC]",err);
        const st=err&&err.httpStatus;
        // The server's own "no" to THIS stamp: the anti-farm ladder (429 with our JSON
        // body, "Already stamped — ask them to pop back a little later") or the 60 s
        // near-duplicate guard. The visit already has its stamp, so drop the row now and
        // quietly. Keeping it would retry until the gap passed and turn a double scan
        // into a second stamp. (A bare 429 with no JSON body is the rate limiter: kept.)
        if((st===429&&err.data&&err.data.error)||(err&&err.reason==="duplicate_scan")){ await ccRemoveFromQueue(scan.id); continue; }
        if(!st||st>=500||st===401||st===408||st===429){ if(err&&err.isNetwork)break; continue; }
        // Dead-letter a poison item (e.g. a deleted customer) after N attempts so
        // it stops blocking the queue + re-toasting on every sync.
        if((scan.attempts||0)+1>=CC_SYNC_MAX_ATTEMPTS){await ccRemoveFromQueue(scan.id);dropped++;}
        else{await ccBumpAttempts(scan);}
      }
    }
    const remaining=await ccGetQueued();
    if(typeof setQueueCount==="function")setQueueCount(remaining.length);
    if(dropped>0)toast(dropped+" scan(s) couldn't sync and were dropped — check with a manager","error");
    else if(remaining.length===0)toast("All scans synced!","success");
    else if(!quiet)toast(remaining.length+" scan(s) failed to sync — will retry","info");
  }catch(err){console.error("[SYNC] queue sync failed",err)}
}

// Turn a failed ccApiCall into a short, specific staff message. The backend
// tags special-card redemption blocks with a typed `status` + (for cap hits)
// when/where it was last claimed; everything else falls back to the server
// message. Keep these terse — staff read them at a glance at the counter.
function ccFmtClaim(iso,site){
  let t="";
  try{
    const d=new Date(iso);
    let h=d.getHours();const m=d.getMinutes();
    const ap=h<12?"am":"pm";h=h%12;if(h===0)h=12;
    t=h+":"+(m<10?"0"+m:m)+ap;
  }catch(e){}
  if(t&&site)return t+", "+site;
  return t||site||"";
}
// "Already stamped at 9:13am with Mates Rates Card scan": the words the customer's own code shows
// while a stamp can't go on yet (JJ, 12 Oct 2026). Never says when the next one can.
function ccStampBlockLine(b){
  const t=b&&b.last_stamp_at?ccFmtClaim(b.last_stamp_at,""):"";
  return "Already stamped"+(t?" at "+t:"")+(b&&b.stamped_with?" with "+b.stamped_with+" scan":"");
}

function ccStaffError(err){
  if(!err)return "Something went wrong";
  // Turned down by the stamp cooldown or the 60-second double-scan guard: the same line as above.
  if(err.data&&err.data.stamp_block)return ccStampBlockLine(err.data.stamp_block);
  const st=err.cardStatus;
  const when=err.lastClaimedAt?ccFmtClaim(err.lastClaimedAt,err.lastClaimedSite):"";
  switch(st){
    case "cap_daily":    return "Already claimed today"+(when?" — "+when:"");
    case "cap_weekly":   return "Weekly limit reached"+(when?" — last "+when:"");
    case "cap_monthly":  return "Monthly limit reached"+(when?" — last "+when:"");
    case "cap_lifetime": return "Already used"+(when?" — "+when:"");
    case "expired":      return "Card expired";
    case "pending_activation": return err.reason||"Not active yet";
    case "eligibility_failed": return err.reason||"Not valid today";
    case "no_discount":  return "Reward not set up — grab a manager";
    case "type_inactive":return "Card paused";
    case "revoked":      return "Card cancelled";
    case "card_mismatch":return err.reason||"Wrong card for this scanner";
    case "wrong_site":   return err.reason||"This card doesn't work at this centre";
    case "locked_birthday": return err.reason||"Not their birthday window yet";
    case "awaiting_reactivation": return err.reason||"No reward to redeem right now";
    case "rotating_protected": return "Scan the customer's live QR for this card";
    // Anti-screenshot rotating-QR rejections — kept GENERIC on purpose (JJ): naming the
    // exact reason (expired / already-scanned / not-recognised) tips off how the guard
    // works and helps people game it. Point staff to the secure fallback (Customer Lookup).
    // ...except a code carried over from the wrong scanner: it was read moments ago and has
    // simply moved on while staff tapped across and keyed their PIN. Nothing was applied.
    case "stale_code":   if(ccCarriedCode){ ccCarriedCode=null; return "That code's moved on — ask them to show their QR again"; }
                         return "QR Invalid - Do Not Apply Discount or Reward";
    case "already_used": return "QR Invalid - Do Not Apply Discount or Reward";
    case "invalid_code": return "QR Invalid - Do Not Apply Discount or Reward";
  }
  // Prefer a human-readable backend reason (it has a space) over a bare machine code
  // or the generic "Redemption not allowed"; otherwise fall back to the top message.
  if(err.reason && /\s/.test(err.reason)) return err.reason;
  return err.message||"Something went wrong";
}

// === Phase 5 — Loyalty integration: recruiter signup QR ===
// The legacy special-card redemption panel (in-app reward picker + POS-flag
// modal + their mocks) was removed — special-card redemptions now run through
// the live "scan the customer's Redeem QR" path (redemption_intent →
// /scanner/scan). What remains here is the recruiter signup QR. CC_PHASE5_ENABLED
// hides it on prod hosts; localhost preview renders it against
// ccMockMintRecruiterToken for QA. Flip CC_PHASE5_ENABLED true once the
// recruiter-QR backend is wired in prod. Full spec: Notion → Site Apps Build
// Hub → "Phase 5 — Loyalty Integration".
const CC_PHASE5_ENABLED = typeof window!=="undefined" && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const CC_PHASE5_MOCKS = CC_PHASE5_ENABLED;

// The customer side of every "scan this" QR the till shows (sign-up, Centre Worker, Day 1,
// Mates keychain, paper-card migration). JJ, 12 Oct 2026: "friendlier and more illustrative
// of what scanning it does". Built to the certify course mockup (chapters 9 and 11): what
// they get, what happens when they scan, the QR, three steps, the fine print. The staff
// lines around it (title, Issued by, Done — next customer) stay each screen's own.
function CCFriendlyQr({headline,sub,value,alt,steps,fine,fineWarn}){
  return (
    <div className="cc-fq">
      <div className="cc-fq-h">{headline}</div>
      {sub?<div className="cc-fq-sub">{sub}</div>:null}
      <div className="cc-fq-qr"><LocalQr value={value} size={ccFriendlyQrSize()} alt={alt}/></div>
      <ol className="cc-fq-steps">{steps.map((t,i)=><li key={i}><b>{i+1}</b>{t}</li>)}</ol>
      {fine?<div className={"cc-fq-fine"+(fineWarn?" warn":"")}>{fine}</div>:null}
    </div>
  );
}
// Big enough to scan from across the counter (250px on a tablet), smaller on a short phone
// so more of the card fits; the box scrolls on the very smallest screens.
function ccFriendlyQrSize(){
  const w=(typeof window!=="undefined"&&window.innerWidth)||1024;
  const h=(typeof window!=="undefined"&&window.innerHeight)||768;
  return Math.round(Math.max(150,Math.min(250,w-130,h-480)));
}
// "24 hours", "7 days", "15 min": how long a freshly minted link lasts, worked out from the
// expires_at the server sent with it (ISO string or epoch ms), so the fine print says what
// the server actually set. null when there's nothing usable to go on.
function ccLinkLife(expiresAt){
  if(expiresAt==null||expiresAt==="") return null;
  const t=typeof expiresAt==="number"?expiresAt:Date.parse(expiresAt);
  if(!isFinite(t)) return null;
  const mins=Math.round((t-Date.now())/60000);
  if(mins<=0) return null;
  if(mins<60) return mins+" min";
  const hours=Math.round(mins/60);
  if(hours<48) return hours+(hours===1?" hour":" hours");
  const days=Math.round(hours/24);
  return days+(days===1?" day":" days");
}
// What a full Club Card gives on the starting tier ("2 Free Rolls"), from the tier rewards the
// scanner home cached. "" until the tablet has fetched them once.
function ccFullCardReward(){
  const t=ccCachedTierRewards().find(x=>x&&(x.name==="standard"||x.name==="none"));
  return (t&&String(t.reward_description||"").trim())||"";
}
// What a card gives, for a QR headline: "10% off" from the card's discount value in the
// Command Centre, else its reward line — read from the card types the scanner home cached.
function ccOfferFor(keys,fallback){
  const t=ccCachedCardTypes().find(x=>x&&keys.indexOf(x.key)>-1);
  if(!t) return fallback;
  const v=String(t.discount_value||"").trim();
  if(/^\d+(\.\d+)?%$/.test(v)) return v+" off";
  return String(t.reward_description||v||"").trim()||fallback;
}

async function ccMockMintRecruiterToken(){
  await new Promise(r=>setTimeout(r,200));
  return {claim_token:"mock_"+Math.random().toString(36).slice(2,12),expires_at:new Date(Date.now()+15*60000).toISOString()};
}

// Recruiter QR — staff taps "Sign up a customer" → PIN gate → mint token →
// show QR for the customer to scan. Token auto-refreshes every 60s; backend
// attributes the signup to the staffer who minted the token.
// onFinished (optional): called when staff tap Done on the QR itself, not on a PIN cancel or
// a failed mint (the Free Sushi for Life flow ticks its sign-up step with it); doneLabel
// words that button for a flow that carries on afterwards.
function CCSignupQR({session,token,requestPin,onClose,showToast,onFinished,doneLabel}){
  const [stage,setStage]=useState("pin"); // pin → loading → show
  const [staffName,setStaffName]=useState("");
  const [claimToken,setClaimToken]=useState(null);
  // Also capture the acting PIN so the backend can attribute by staff id (stable),
  // not just by a free-text name that can mis-match.
  const [staffPin,setStaffPin]=useState("");

  // Stage 1 — PIN gate (required so the backend knows whom to attribute)
  useEffect(()=>{
    if(stage!=="pin") return;
    requestPin((name,pin)=>{
      setStaffName(name);
      setStaffPin(pin);
      setStage("loading");
    },()=>{
      onClose();
    });
  },[]);

  // A background refresh failed — keep the code on screen rather than closing
  // (JJ, 11 Oct 2026: only Done closes a QR) and say so. A code lasts 15 min from
  // its mint, so once the last good one is ~14 min old the line says it has
  // expired. Cleared by the next successful refresh. (0 = no failure.)
  const [refreshFailedAt,setRefreshFailedAt]=useState(0);
  const shownRef=useRef(false);
  const lastOkRef=useRef(0);
  // How long one code lasts, from the server reply (ccLinkLife), for the fine print.
  const [codeLife,setCodeLife]=useState(null);

  // Stage 2+ — mint on entry, refresh every 60s while visible
  useEffect(()=>{
    if(!staffName) return;
    let cancelled=false;
    const mint=async()=>{
      try{
        const r=CC_PHASE5_MOCKS
          ? await ccMockMintRecruiterToken()
          : await ccApiCall("/staff/recruiter-qr/mint",token,{method:"POST",body:JSON.stringify({staff_name:staffName, staff_pin:staffPin})});
        if(cancelled) return;
        setClaimToken(r.claim_token);
        setCodeLife(ccLinkLife(r.expires_at));
        setRefreshFailedAt(0);
        lastOkRef.current=Date.now();
        shownRef.current=true;
        setStage("show");
      }catch(e){
        if(cancelled) return;
        if(shownRef.current){ setRefreshFailedAt(Date.now()); return; }   // keep the QR up
        showToast("Couldn't generate QR — "+(e.message||"try again"),"error");
        onClose();                                                   // nothing to show yet
      }
    };
    mint();
    const timer=setInterval(mint,60000);
    return ()=>{cancelled=true;clearInterval(timer)};
  },[staffName]);

  if(stage==="pin") return null; // unified PIN modal is rendered elsewhere
  if(stage==="loading"||!claimToken){
    return (
      <div className="cc-modal" style={{zIndex:360}}>
        <div className="cc-modal-box" style={{maxWidth:360,textAlign:"center",padding:40}}>
          <div className="cc-spinner" style={{margin:"0 auto 16px"}}/>
          <div style={{color:"var(--cc-kewpie-dim)",fontSize:13}}>Generating signup QR…</div>
        </div>
      </div>
    );
  }

  const qrUrl=CC_CLAIM_HOST+"/signup?claim="+encodeURIComponent(claimToken);
  const parts=(staffName||"").trim().split(/\s+/);
  const displayName=parts.length>1
    ? parts[0]+" "+parts[parts.length-1].charAt(0)+"."
    : (parts[0]||"Staff");

  // Only "Done — next customer" closes this (JJ, 11 Oct 2026) — no tap-outside close.
  return (
    <div className="cc-modal" style={{zIndex:360}}>
      <div className="cc-modal-box cc-fq-box">
        <CCQrHold/><div className="cc-modal-title">Sign up a customer{CC_PHASE5_MOCKS&&<span className="cc-mock-badge">Mock</span>}</div>
        <div className="cc-signup-staff">Signing up as: <b>{displayName}</b></div>
        <CCFriendlyQr
          headline="Scan for your Rolled Rewards Club Card"
          sub={<>Point your phone camera here. Join and your <b>Club Card</b> lands in the app — a stamp every visit, and {ccFullCardReward()||"a reward"} when it's full.</>}
          value={qrUrl} alt="Signup QR"
          steps={["Scan","Join or log in","Card in your app"]}
          fineWarn={!!refreshFailedAt}
          fine={!refreshFailedAt
            ? "QR refreshes every 60s"+(codeLife?" · each code valid "+codeLife:"")
            : (refreshFailedAt - lastOkRef.current >= 14*60*1000
              ? "This code has expired — tap Done and start again"
              : "Couldn't refresh the code — retrying…")}/>
        <button className="cc-btn cc-btn-secondary" onClick={()=>{if(onFinished)onFinished();onClose();}} style={{width:"100%",marginTop:16}}>{doneLabel||"Done — next customer"}</button>
      </div>
    </div>
  );
}

// Issue Centre Card — staff taps "Issue Centre Card" → PIN gate → mint a Centre
// Worker claim link tagged with THIS kiosk's centre → show the QR for the worker
// to scan. Revised mechanic (JJ, 15 Jul): the centre comes from WHICH kiosk
// issued the QR — no dropdown. The worker signs up giving only name + which
// store; the card locks to this centre and the signup credits the issuing staff.
async function ccMockMintCentreLink(session){
  await new Promise(r=>setTimeout(r,200));
  return {claim_token:"mock_centre_"+Math.random().toString(36).slice(2,12),centre_name:(session&&(session.siteName||session.centreName))||"This Centre",expires_at:new Date(Date.now()+24*3600000).toISOString()};
}

function CCIssueCentreCard({session,token,requestPin,onClose,showToast}){
  const [stage,setStage]=useState("pin"); // pin → loading → show
  const [staffName,setStaffName]=useState("");
  const [claimToken,setClaimToken]=useState(null);
  const [centreName,setCentreName]=useState("");
  const [linkLife,setLinkLife]=useState(null); // from the server reply (ccLinkLife)
  // Also capture the acting PIN so the backend can credit by staff id (stable),
  // not just by a free-text name that can mis-match.
  const [staffPin,setStaffPin]=useState("");

  // Stage 1 — PIN gate (required so the backend credits the issuing staffer)
  useEffect(()=>{
    if(stage!=="pin") return;
    requestPin((name,pin)=>{setStaffName(name);setStaffPin(pin);setStage("loading")},()=>{onClose()});
  },[]);

  // Stage 2 — mint the centre link on entry. Unlike the recruiter QR (a 15-min token
  // that rotates every 60s), a centre link lives 24 hours (rolled-club-card #418), so mint once.
  useEffect(()=>{
    if(!staffName) return;
    let cancelled=false;
    (async()=>{
      try{
        const r=CC_PHASE5_MOCKS
          ? await ccMockMintCentreLink(session)
          : await ccApiCall("/staff/centre-card/mint",token,{method:"POST",body:JSON.stringify({staff_name:staffName, staff_pin:staffPin})});
        if(cancelled) return;
        setClaimToken(r.claim_token);
        setCentreName(r.centre_name||"");
        setLinkLife(ccLinkLife(r.expires_at));
        setStage("show");
      }catch(e){
        if(cancelled) return;
        showToast((e&&e.message)||"Couldn't generate the Centre Worker QR — try again","error");
        onClose();
      }
    })();
    return ()=>{cancelled=true};
  },[staffName]);

  if(stage==="pin") return null; // unified PIN modal is rendered elsewhere
  if(stage==="loading"||!claimToken){
    return (
      <div className="cc-modal" style={{zIndex:360}}>
        <div className="cc-modal-box" style={{maxWidth:360,textAlign:"center",padding:40}}>
          <div className="cc-spinner" style={{margin:"0 auto 16px"}}/>
          <div style={{color:"var(--cc-kewpie-dim)",fontSize:13}}>Generating Centre Worker QR…</div>
        </div>
      </div>
    );
  }

  // Lands on the customer app's claim gate: the card (fixed to this centre)
  // shows as the hero, then signup asks only name + which store.
  const qrUrl=CC_CLAIM_HOST+"/claim?token="+encodeURIComponent(claimToken)+"&source=centre-worker";
  const parts=(staffName||"").trim().split(/\s+/);
  const displayName=parts.length>1
    ? parts[0]+" "+parts[parts.length-1].charAt(0)+"."
    : (parts[0]||"Staff");

  // Only "Done — next customer" closes this (JJ, 11 Oct 2026) — no tap-outside close.
  return (
    <div className="cc-modal" style={{zIndex:360}}>
      <div className="cc-modal-box cc-fq-box">
        <CCQrHold/><div className="cc-modal-title">Issue Centre Card{CC_PHASE5_MOCKS&&<span className="cc-mock-badge">Mock</span>}</div>
        <div className="cc-signup-staff">{centreName?<span>Centre: <b>{centreName}</b> · </span>:null}Issued by: <b>{displayName}</b></div>
        <CCFriendlyQr
          headline={"Scan for "+ccOfferFor(["centre","centre_worker"],"10% off")+(centreName?" at "+centreName:"")}
          sub={<>Point your phone camera here. Join Rolled Rewards and your <b>Centre Worker card</b> lands in the app — show it before you pay, every visit.</>}
          value={qrUrl} alt="Centre Worker signup QR"
          steps={["Scan","Join or log in","Card in your app"]}
          fine={"Locks to "+(centreName||"this centre")+(linkLife?" · link valid "+linkLife:"")}/>
        <button className="cc-btn cc-btn-secondary" onClick={onClose} style={{width:"100%",marginTop:16}}>Done — next customer</button>
      </div>
    </div>
  );
}

// The customer app's canonical host. club.eatrolled.com still answers but
// 301-redirects here — checked 23 Sep 2026, `curl -I
// https://club.eatrolled.com/claim` → 301 → rewards.eatrolled.com — and a
// redirect on mall wifi is an extra round trip between a customer's phone and
// the card we just promised them. Point the QR straight at the real host.
const CC_CLAIM_HOST = "https://rewards.eatrolled.com";
// Claim URL for a freshly minted link. Prefers the backend's own claim_url
// when it sends one (that is built from FRONTEND_URL, so it follows a host
// move with no scanner deploy) but re-points the legacy club host so the
// phone never takes the 301.
function ccClaimUrl(r,source){
  // Only an ABSOLUTE https URL: a relative one would encode into a QR that a
  // phone camera cannot open, so fall through and build the link ourselves.
  const raw=(r&&r.claim_url)||"";
  if(/^https?:\/\//i.test(raw)) return raw.replace(/^https?:\/\/club\.eatrolled\.com/i,CC_CLAIM_HOST);
  return CC_CLAIM_HOST+"/claim?token="+encodeURIComponent((r&&r.claim_token)||"")+"&source="+encodeURIComponent(source);
}

// Issue Day 1 Card — staff pick the Day 1 face in Issue Special Card (Trafford
// only) → PIN gate → mint a FRESH Day 1 claim link → show the QR for the
// customer to scan. Because the staffer typed their PIN, the backend records
// WHO minted the link (resolveMintingStaff, PIN-first) and writes a
// launch_card_qr_minted audit event, so the Command Centre can show this card
// as staff-issued, by whom, at which site. Clone of CCIssueCentreCard.
//
// It used to hang off a "launch event" row that JJ stopped using in July, so
// the whole flow was unreachable. It now asks for nothing but a claim token:
// the card's name, dates and offer all live in the Command Centre card config,
// and a link that quietly stopped carrying an event name must not blank the
// screen a staffer is standing in front of.
async function ccMockMintLaunchLink(){
  await new Promise(r=>setTimeout(r,200));
  return {claim_token:"mock_launch_"+Math.random().toString(36).slice(2,12),expires_at:new Date(Date.now()+3600000).toISOString()};
}

function CCIssueLaunchCard({session,token,requestPin,onClose,showToast}){
  const [stage,setStage]=useState("pin"); // pin → loading → show
  const [staffName,setStaffName]=useState("");
  const [claimUrl,setClaimUrl]=useState("");
  const [linkLife,setLinkLife]=useState(null); // from the server reply (ccLinkLife)
  // Also capture the acting PIN so the backend credits the issuing staffer by
  // stable staff id (PIN-first), not just a free-text name that can mis-match —
  // same as CCSignupQR / CCIssueCentreCard do post-#131. This PIN is the whole
  // reason the Command Centre can say who handed the card out.
  const [staffPin,setStaffPin]=useState("");

  useEffect(()=>{
    if(stage!=="pin") return;
    requestPin((name,pin)=>{setStaffName(name);setStaffPin(pin);setStage("loading")},()=>{onClose()});
  },[]);

  // Mint on entry. A fresh link every time the tile is tapped, so two customers
  // never share a token and each mint carries its own staffer + site.
  useEffect(()=>{
    if(!staffName) return;
    let cancelled=false;
    (async()=>{
      try{
        const r=CC_PHASE5_MOCKS
          ? await ccMockMintLaunchLink()
          : await ccApiCall("/staff/launch-card/mint",token,{method:"POST",body:JSON.stringify({staff_name:staffName, staff_pin:staffPin})});
        if(cancelled) return;
        if(!r||(!r.claim_token&&!r.claim_url)) throw new Error("Couldn't generate the Day 1 QR — try again");
        setClaimUrl(ccClaimUrl(r,"launch-day"));
        setLinkLife(ccLinkLife(r.expires_at));
        setStage("show");
      }catch(e){
        if(cancelled) return;
        showToast((e&&e.message)||"Couldn't generate the Day 1 QR — try again","error");
        onClose();
      }
    })();
    return ()=>{cancelled=true};
  },[staffName]);

  if(stage==="pin") return null;
  if(stage==="loading"||!claimUrl){
    return (
      <div className="cc-modal" style={{zIndex:360}}>
        <div className="cc-modal-box" style={{maxWidth:360,textAlign:"center",padding:40}}>
          <div className="cc-spinner" style={{margin:"0 auto 16px"}}/>
          <div style={{color:"var(--cc-kewpie-dim)",fontSize:13}}>Generating Day 1 QR…</div>
        </div>
      </div>
    );
  }

  const parts=(staffName||"").trim().split(/\s+/);
  const displayName=parts.length>1
    ? parts[0]+" "+parts[parts.length-1].charAt(0)+"."
    : (parts[0]||"Staff");

  // Only "Done — next customer" closes this (JJ, 11 Oct 2026) — no tap-outside close.
  return (
    <div className="cc-modal" style={{zIndex:360}}>
      <div className="cc-modal-box cc-fq-box">
        <CCQrHold/><div className="cc-modal-title">Issue Day 1 Card{CC_PHASE5_MOCKS&&<span className="cc-mock-badge">Mock</span>}</div>
        <div className="cc-signup-staff">Issued by: <b>{displayName}</b></div>
        <CCFriendlyQr
          headline={"Scan for "+ccOfferFor(["launch"],"your Day 1 card")}
          sub={<>Point your phone camera here. Join Rolled Rewards and your <b>Day 1 card</b> lands in the app — show it before you pay.</>}
          value={claimUrl} alt="Day 1 signup QR"
          steps={["Scan","Join or log in","Card in your app"]}
          fine={"One card per customer · usable on the day only"+(linkLife?" · link valid "+linkLife:"")}/>
        <button className="cc-btn cc-btn-secondary" onClick={onClose} style={{width:"100%",marginTop:16}}>Done — next customer</button>
      </div>
    </div>
  );
}

// Issue Staff Card — onto an EXISTING account by Roll ID (no signup). ROLL-
// lookup → PIN gate → POST /scanner/staff-card/request → pending approval. A
// manager in the Command Centre confirms the person is staff before it goes live.
function CCIssueStaffCard({session,token,requestPin,onDone,showToast}){
  // Staff only type the code after the fixed "ROLL-" prefix; we hold the suffix.
  const [idSuffix,setIdSuffix]=useState("");
  const [customer,setCustomer]=useState(null);
  const [note,setNote]=useState("");
  const [loading,setLoading]=useState(false);
  const [sending,setSending]=useState(false);
  const [done,setDone]=useState(false);
  const [alreadyHolds,setAlreadyHolds]=useState(false);
  const lookup=async()=>{
    const id=("ROLL-"+idSuffix).trim().toUpperCase();
    if(!idSuffix)return;
    setLoading(true);
    try{const r=token==="mock-preview-token"?await ccMockLookup(id):await ccApiCall("/scanner/lookup/"+id,token);setCustomer(r)}
    catch(err){showToast(ccStaffError(err),"error");setCustomer(null)}
    finally{setLoading(false)}
  };
  const send=()=>{
    if(!customer)return;
    requestPin(async(staffName)=>{
      setSending(true);
      try{
        const r=token==="mock-preview-token"
          ? {success:true,request_id:"mock_staffreq_"+Math.random().toString(36).slice(2,10),status:"pending",already_holds:false}
          : await ccApiCall("/scanner/staff-card/request",token,{method:"POST",body:JSON.stringify({loyalty_id:customer.loyalty_id,site_id:session.siteId,note:note.trim()||undefined,staff_name:staffName})});
        setAlreadyHolds(!!(r&&r.already_holds));
        setDone(true);
      }catch(err){showToast(ccStaffError(err),"error")}
      finally{setSending(false)}
    });
  };
  if(done){
    return (
      <div className="cc-screen cc-result">
        <div className="cc-fs4l-sent">
          <div className="cc-fs4l-sent-ic"><CCCheck color="#4ade80"/></div>
          <div className="cc-fs4l-sent-h">{alreadyHolds?"Already has a Staff Card":"Sent for approval"}</div>
          <div className="cc-fs4l-sent-sub">{alreadyHolds?((customer.first_name||"They")+" already holds a Staff Card — nothing more to do."):((customer.first_name||"Their")+"'s request is in the manager approval queue — it'll go live on their account once approved.")}</div>
        </div>
        <button className="cc-done-btn" onClick={onDone}>Done — Next Customer</button>
      </div>
    );
  }
  return (
    <div className="cc-screen cc-migrate">
      <div className="cc-flat-head">
        <button className="cc-back-btn" onClick={onDone}>‹ Back</button>
        <div className="cc-flat-title">Staff Card</div>
        <div className="cc-special-head-site">{session.siteName}</div>
      </div>
      <div className="cc-migrate-body">
        <div className="cc-callout">
          <div className="cc-callout-txt">Staff Cards go onto an existing Club account by Roll ID. This goes to a manager for approval — they'll confirm you're staff.</div>
        </div>
        <div className="cc-mstep">
          <div className="cc-mstep-lab"><span className="cc-mstep-n">1</span> Find the account</div>
          <div className="cc-lookup-search">
            <label className="cc-input" style={{flex:1,display:"flex",alignItems:"center",gap:2,padding:"0 16px",cursor:"text"}}>
              <span style={{color:"var(--cc-kewpie-dim)",fontWeight:700,userSelect:"none"}}>ROLL-</span>
              <input style={{flex:1,minWidth:0,padding:"14px 0",background:"transparent",border:"none",outline:"none",color:"var(--cc-kewpie)",fontSize:16,fontFamily:"inherit",letterSpacing:"1px"}} placeholder="A1B2C3" value={idSuffix} onChange={e=>setIdSuffix(e.target.value.toUpperCase().replace(/^ROLL-?/,"").replace(/[^A-Z0-9]/g,"").slice(0,6))} onKeyDown={e=>e.key==="Enter"&&idSuffix&&lookup()} inputMode="text" autoFocus/>
            </label>
            <button className="cc-btn cc-btn-primary" onClick={()=>idSuffix&&lookup()} disabled={loading||!idSuffix}>{loading?"…":"Look up"}</button>
          </div>
          {customer&&<div className="cc-found">✓ {customer.first_name} · {customer.loyalty_id}</div>}
        </div>
        <div className={"cc-mstep"+(!customer?" cc-mstep-off":"")}>
          <div className="cc-mstep-lab"><span className="cc-mstep-n">2</span> Anything to add?</div>
          <input className="cc-input" placeholder="Note for the manager (optional)" value={note} onChange={e=>setNote(e.target.value)} maxLength={500}/>
        </div>
        <button className="cc-fs4l-btn" onClick={send} disabled={!customer||sending}>{sending?"Sending…":"Send for approval"}</button>
        <div className="cc-migrate-note">A manager must confirm you're staff</div>
      </div>
    </div>
  );
}

// Issue Rolled Fam (Friends & Family, 50% off) — onto an EXISTING account by
// Roll ID (no signup). ROLL- lookup → PIN gate → POST /scanner/family-card/request
// → a pending approval in the Command Centre. Clone of CCIssueStaffCard (this
// replaced the old claim-link mint, which threw a validation toast).
function CCIssueFamRoll({session,token,requestPin,onDone,showToast}){
  // Staff only type the code after the fixed "ROLL-" prefix; we hold the suffix.
  const [idSuffix,setIdSuffix]=useState("");
  const [customer,setCustomer]=useState(null);
  const [note,setNote]=useState("");
  const [loading,setLoading]=useState(false);
  const [sending,setSending]=useState(false);
  const [done,setDone]=useState(false);
  const [alreadyHolds,setAlreadyHolds]=useState(false);
  const lookup=async()=>{
    const id=("ROLL-"+idSuffix).trim().toUpperCase();
    if(!idSuffix)return;
    setLoading(true);
    try{const r=token==="mock-preview-token"?await ccMockLookup(id):await ccApiCall("/scanner/lookup/"+id,token);setCustomer(r)}
    catch(err){showToast(ccStaffError(err),"error");setCustomer(null)}
    finally{setLoading(false)}
  };
  const send=()=>{
    // "Who is it?" is required for Rolled Fam — a manager approving the card needs to
    // know the relationship, not just a first name. Guard here as well as disabling
    // the button, so it can never fire empty.
    if(!customer||!note.trim())return;
    requestPin(async(staffName)=>{
      setSending(true);
      try{
        const r=token==="mock-preview-token"
          ? {success:true,request_id:"mock_famreq_"+Math.random().toString(36).slice(2,10),status:"pending",already_holds:false}
          : await ccApiCall("/scanner/family-card/request",token,{method:"POST",body:JSON.stringify({loyalty_id:customer.loyalty_id,site_id:session.siteId,note:note.trim(),staff_name:staffName})});
        setAlreadyHolds(!!(r&&r.already_holds));
        setDone(true);
      }catch(err){showToast(ccStaffError(err),"error")}
      finally{setSending(false)}
    });
  };
  if(done){
    return (
      <div className="cc-screen cc-result">
        <div className="cc-fs4l-sent">
          <div className="cc-fs4l-sent-ic"><CCCheck color="#4ade80"/></div>
          <div className="cc-fs4l-sent-h">{alreadyHolds?"Already has a Rolled Fam card":"Sent for approval"}</div>
          <div className="cc-fs4l-sent-sub">{alreadyHolds?((customer.first_name||"They")+" already holds a Rolled Fam card — nothing more to do."):((customer.first_name||"Their")+"'s request is in the manager approval queue — it'll go live on their account once approved.")}</div>
        </div>
        <button className="cc-done-btn" onClick={onDone}>Done — Next Customer</button>
      </div>
    );
  }
  return (
    <div className="cc-screen cc-migrate">
      <div className="cc-flat-head">
        <button className="cc-back-btn" onClick={onDone}>‹ Back</button>
        <div className="cc-flat-title">Rolled Fam</div>
        <div className="cc-special-head-site">{session.siteName}</div>
      </div>
      <div className="cc-migrate-body">
        <div className="cc-callout">
          <div className="cc-callout-txt">Rolled Fam cards go onto an existing Club account by Roll ID. This goes to a manager for approval.</div>
        </div>
        <div className="cc-mstep">
          <div className="cc-mstep-lab"><span className="cc-mstep-n">1</span> Find the account</div>
          <div className="cc-lookup-search">
            <label className="cc-input" style={{flex:1,display:"flex",alignItems:"center",gap:2,padding:"0 16px",cursor:"text"}}>
              <span style={{color:"var(--cc-kewpie-dim)",fontWeight:700,userSelect:"none"}}>ROLL-</span>
              <input style={{flex:1,minWidth:0,padding:"14px 0",background:"transparent",border:"none",outline:"none",color:"var(--cc-kewpie)",fontSize:16,fontFamily:"inherit",letterSpacing:"1px"}} placeholder="A1B2C3" value={idSuffix} onChange={e=>setIdSuffix(e.target.value.toUpperCase().replace(/^ROLL-?/,"").replace(/[^A-Z0-9]/g,"").slice(0,6))} onKeyDown={e=>e.key==="Enter"&&idSuffix&&lookup()} inputMode="text" autoFocus/>
            </label>
            <button className="cc-btn cc-btn-primary" onClick={()=>idSuffix&&lookup()} disabled={loading||!idSuffix}>{loading?"…":"Look up"}</button>
          </div>
          {customer&&<div className="cc-found">✓ {customer.first_name} · {customer.loyalty_id}</div>}
        </div>
        <div className={"cc-mstep"+(!customer?" cc-mstep-off":"")}>
          <div className="cc-mstep-lab"><span className="cc-mstep-n">2</span> Who is it?</div>
          <input className="cc-input" placeholder="Their name / how they know us (required)" value={note} onChange={e=>setNote(e.target.value)} maxLength={500}/>
        </div>
        <button className="cc-fs4l-btn" onClick={send} disabled={!customer||!note.trim()||sending}>{sending?"Sending…":"Send for approval"}</button>
        <div className="cc-migrate-note">A manager must approve this</div>
      </div>
    </div>
  );
}

// Issue Special Card — the picker. Staff tap "Issue Special Card" on the home
// toolbar → this full-screen page shows the five issuable card FACES (like the
// special-cards redeem grid). The grid stays mounted as the base; tapping a face
// opens that card's sub-flow OVER it via an internal `mode`. Centre's PIN pad
// (the parent global modal) + its QR modal overlay the grid directly; the four
// full-screen flows (FSFL / Mates / Rolled Fam / Staff) render in a fixed overlay
// above it. Back or cancel in ANY sub-flow returns to the grid (setMode(null));
// only the grid's own top "‹ Back" leaves the picker for the scanner home.
// Day 1 is a Trafford card: the Square button "Day 1 Trafford Relaunch" only
// exists at that till, so a Day 1 card issued at Arndale or Mcr Kitchen could
// not be redeemed. Same site-aware shape as CLOCK_IN_ENABLED — the gate travels
// with the source rather than relying on which shop this bundle was built for.
// OFF since 28 Sep 2026 (JJ): the Trafford Day 1 giveaway is over and the Day 1 card
// is admin-issue only, so the tile only led to "manual-issue only" errors. The Day 1
// flow below stays in place: a future relaunch turns it back on here.
const DAY1_CARD_ENABLED = false;

function CCIssueSpecialCard({session,token,requestPin,onClose,showToast}){
  const [mode,setMode]=useState(null); // null=grid | centre | day1 | fsfl | mates | family | staff
  const OVL={position:"fixed",inset:0,height:"100vh",zIndex:355,background:"var(--cc-nori)"};
  const backToGrid=()=>setMode(null); // every sub-flow's close / Done / PIN-cancel lands here
  // Grid order locked by JJ: Centre Worker · FSFL · Mates Rates · Rolled Fam · Staff Card.
  // typeKey feeds CCFace (production card key → the bespoke mockup face).
  // Day 1 slots in behind Centre Worker: both are "scan this QR and you are in"
  // signup flows that end in a QR modal over the grid, where the four below are
  // full-screen flows onto an account that already exists. JJ's five keep their
  // order. Trafford only — see DAY1_CARD_ENABLED.
  const tiles=[
    {mode:"centre",typeKey:"centre",         label:"Centre Worker"},
    ...(DAY1_CARD_ENABLED?[{mode:"day1", typeKey:"launch", label:"Day 1"}]:[]),
    {mode:"fsfl",  typeKey:"fs4l",           label:"FSFL"},
    {mode:"mates", typeKey:"mates_rates",    label:"Mates Rates"},
    {mode:"family",typeKey:"family_friends", label:"Rolled Fam"},
    {mode:"staff", typeKey:"staff",          label:"Staff Card"},
  ];
  return (
    <div className="cc-root" style={OVL}>
      <div className="cc-screen cc-redeem" style={{"--accent":"#d4fb6f","--accent-bg":"rgba(212,251,111,0.16)","--accent-line":"rgba(212,251,111,0.5)"}}>
        <div className="cc-flat-head">
          <button className="cc-back-btn" onClick={onClose}>‹ Back</button>
          <div className="cc-flat-title">Issue Special Card</div>
          <div className="cc-special-head-site">{session.siteName}</div>
        </div>
        <div className="cc-redeem-body">
          <div className="cc-before-tag">Pick a card to issue</div>
          <div className="sc-cardgrid">
            {tiles.map(t=>(
              <button key={t.mode} className="sc-grid-card" onClick={()=>setMode(t.mode)}>
                <div className="cardwrap"><CCFace typeKey={t.typeKey}/></div>
                <div style={{marginTop:7,textAlign:"center",fontSize:12.5,fontWeight:700,letterSpacing:"0.2px",color:"var(--cc-kewpie)"}}>{t.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Centre — PIN pad (parent global modal, z-index 500) + QR (.cc-modal scrim);
          both overlay the grid still rendered behind. Cancel / Done → grid. */}
      {mode==="centre"&&<CCIssueCentreCard session={session} token={token} requestPin={requestPin} showToast={showToast} onClose={backToGrid}/>}
      {/* Day 1 — same shape as Centre: PIN pad, then a QR modal over the grid. */}
      {mode==="day1"&&<CCIssueLaunchCard session={session} token={token} requestPin={requestPin} showToast={showToast} onClose={backToGrid}/>}
      {/* Full-screen drill-downs render over the grid; each has its own "‹ Back"
          wired to onDone → grid. "Done — Next Customer" also lands on the grid. */}
      {mode==="fsfl"&&<div className="cc-root" style={OVL}><CCFs4lMigration session={session} token={token} requestPin={requestPin} onDone={backToGrid} showToast={showToast}/></div>}
      {mode==="mates"&&<div className="cc-root" style={OVL}><CCKeychain session={session} token={token} requestPin={requestPin} onDone={backToGrid} showToast={showToast}/></div>}
      {mode==="family"&&<div className="cc-root" style={OVL}><CCIssueFamRoll session={session} token={token} requestPin={requestPin} onDone={backToGrid} showToast={showToast}/></div>}
      {mode==="staff"&&<div className="cc-root" style={OVL}><CCIssueStaffCard session={session} token={token} requestPin={requestPin} onDone={backToGrid} showToast={showToast}/></div>}
    </div>
  );
}

// === end Phase 5 loyalty integration helpers ===

// ============================================================================
// SCANNER UI REBUILD (design locked 25 Jun) — real soy-fish stamps, themed
// home, special tiles auto-populated from the catalogue. The fish asset +
// per-card colours are ported from the customer app (rolled-frontend) so the
// scanner reads like the cards customers carry.
// ============================================================================

// — Real soy-fish (the brand stamp), ported from rolled-frontend's SoyFishMini
//   (the "fewer, larger scales" set). One viewBox, drawn filled or empty.
const CC_FISH_VIEWBOX = "0 0 823.78 481.7";
const CC_FISH_EYE = { cx: 629.28, cy: 221.23, r: 34.3 };
const CC_FISH_BODY = "M1.48,361.63l40.2-100.4c2-5,2-10.5,0-15.4L1.58,146.73c-1.6-3.9-2-8.1-1-12.1,4.8-20.7,28.8-32.2,48.4-20.9l125.35,72.39c19.47-34.17,53.19-63.84,96.49-85.97-1.01-5.63-3.47-28.92,19.37-50.62C316.48,24.63,369.88-.97,422.58.03c50.16.95,95.79,57.52,100.07,62.98,96.49,13.93,175.55,57.91,212.71,116.05.27-.01.55-.02.83-.02h65.4c12.26,0,22.2,9.94,22.2,22.2v105.6c0,12.26-9.94,22.2-22.2,22.2h-65.4c-.28,0-.55-.01-.83-.02-44.51,69.63-149.13,118.96-271.82,120.67-37.11,25.13-63.59,35.6-89.66,30.95-21.85-3.96-20.11-30.01-18.03-42.21-83.81-19.39-150.5-62.2-181.42-116.3l-125.55,72.51c-19.6,11.3-43.6-.2-48.4-20.9-.9-4-.5-8.2,1-12.1ZM629.28,255.53c18.94,0,34.3-15.36,34.3-34.3s-15.36-34.3-34.3-34.3-34.3,15.36-34.3,34.3,15.36,34.3,34.3,34.3ZM207.14,303.22c32.45,69.67,132.02,120.41,249.83,120.41,35.29,0,68.93-4.56,99.64-12.81-14.3-25.86-41.5-84.15-39.64-162.99l-.1.1c1.67-72.12,26.77-125.02,41.16-150.31-31.08-8.49-65.23-13.19-101.06-13.19-117.92,0-217.56,50.82-249.92,120.6-7.2,15.52-11.08,31.96-11.08,49s3.91,33.62,11.17,49.19Z";
const CC_MINI_DETAIL = "M516.98,247.83l-.1.1c1.67-72.12,26.77-125.02,41.16-150.31-31.08-8.49-65.23-13.19-101.06-13.19-117.92,0-217.56,50.82-249.92,120.6-7.2,15.52-11.08,31.96-11.08,49s3.91,33.62,11.17,49.2c32.45,69.67,132.02,120.41,249.83,120.41,35.29,0,68.93-4.56,99.64-12.81-14.3-25.86-41.5-84.16-39.64-162.99Z";
const CC_MINI_SCALES = ["M443.49,310.33c5.52,5.22,5.98,13.81.92,19.48-.31.31-2.15,3.07-2.15,7.98s1.84,7.67,2.15,7.98c5.06,5.68,4.6,14.27-.92,19.48-5.68,5.22-14.42,4.91-19.64-.77-1.69-1.69-9.51-11.05-9.51-26.69s7.98-25.01,9.51-26.69c5.22-5.68,13.96-5.98,19.64-.77Z","M373.38,310.33c5.52,5.22,5.98,13.81.92,19.48-.31.31-2.15,3.07-2.15,7.98s1.84,7.67,2.15,7.98c5.06,5.68,4.6,14.27-.92,19.48-5.68,5.22-14.42,4.91-19.64-.77-1.69-1.69-9.51-11.05-9.51-26.69s7.98-25.01,9.51-26.69c5.22-5.68,13.96-5.98,19.64-.77Z","M408.51,226.57c5.52,5.22,5.98,13.81.92,19.48-.03.03-.09.11-.15.2v-.04.04c-.53.74-1.99,3.39-1.99,7.78,0,4.91,1.84,7.67,2.15,7.98,5.06,5.68,4.6,14.27-.92,19.48-5.68,5.22-14.42,4.91-19.64-.77-1.69-1.69-9.51-11.05-9.51-26.69h0c0-15.65,7.98-25.01,9.51-26.69,5.22-5.68,13.96-5.98,19.64-.77ZM409.28,262.01v-.15.15Z","M338.25,226.57c5.52,5.22,5.98,13.81.92,19.48-.31.31-2.15,3.07-2.15,7.98s1.84,7.67,2.15,7.98c5.06,5.68,4.6,14.27-.92,19.48-5.68,5.22-14.42,4.91-19.64-.77-1.69-1.69-9.51-11.05-9.51-26.69h0c0-15.65,7.98-25.01,9.51-26.69,5.22-5.68,13.96-5.98,19.64-.77Z","M443.49,142.81c5.52,5.22,5.98,13.81.92,19.48-.31.31-2.15,3.07-2.15,7.98s1.84,7.67,2.15,7.98c5.06,5.68,4.6,14.27-.92,19.48-5.68,5.22-14.42,4.91-19.64-.77-1.69-1.69-9.51-11.05-9.51-26.69h0c0-15.65,7.98-25.01,9.51-26.69,5.22-5.68,13.96-5.98,19.64-.77Z","M373.38,142.81c5.52,5.22,5.98,13.81.92,19.48-.03.03-.09.11-.15.2v-.04.04c-.53.74-1.99,3.39-1.99,7.78,0,4.91,1.84,7.67,2.15,7.98,5.06,5.68,4.6,14.27-.92,19.48-5.68,5.22-14.42,4.91-19.64-.77-1.69-1.69-9.51-11.05-9.51-26.69h0c0-15.65,7.98-25.01,9.51-26.69,5.22-5.68,13.96-5.98,19.64-.77ZM374.15,178.25v-.15.15Z","M478.54,226.57c5.52,5.22,5.98,13.81.92,19.48-.31.31-2.15,3.07-2.15,7.98s1.84,7.67,2.15,7.98c5.06,5.68,4.6,14.27-.92,19.48-5.68,5.22-14.42,4.91-19.64-.77-1.69-1.69-9.51-11.05-9.51-26.69h0c0-15.65,7.98-25.01,9.51-26.69,5.22-5.68,13.96-5.98,19.64-.77Z"];

function CCSoyFish({filled,accent,className,style}){
  const bodyFill = filled ? (accent||"#d4fb6f") : "rgba(254,250,240,0.10)";
  const detailFill = filled ? "#00392c" : "transparent";
  return (
    <svg className={"cc-soyfish"+(className?" "+className:"")} style={style} viewBox={CC_FISH_VIEWBOX} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {filled && <circle cx={CC_FISH_EYE.cx} cy={CC_FISH_EYE.cy} r={CC_FISH_EYE.r} fill={detailFill}/>}
      {filled && <path d={CC_MINI_DETAIL} fill={detailFill}/>}
      <path d={CC_FISH_BODY} fill={bodyFill}/>
      {filled && <g>{CC_MINI_SCALES.map((d,i)=><path key={i} d={d} fill={bodyFill}/>)}</g>}
    </svg>
  );
}

// — Per-card-type visual system (colour + icon + locked label). The catalogue
//   has no colour/icon column, so the scanner maps both by key (same accents as
//   the customer card faces). A brand-new card type still appears on the home
//   (default wasabi) with zero code change; add it here to brand it.
const CC_SPECIAL_ACCENTS = {birthday:"#ff9166",fs4l:"#ecca5e",mates_rates:"#9ec61f",influencer:"#c89bff",launch:"#8fd8bf",family_friends:"#ffb84d",staff:"#9ec3f0",founders:"#d8b45a"};
const ccAccent = (k)=>CC_SPECIAL_ACCENTS[k]||"#d4fb6f";
const CC_SPECIAL_ICONS = {fs4l:"🍣",birthday:"🎂",mates_rates:"🤝",family_friends:"🦘",influencer:"👑",staff:"🐟",launch:"🚀",founders:"👑"};
const ccCardIcon = (k)=>CC_SPECIAL_ICONS[k]||"🎟️";
const CC_SPECIAL_LABELS = {fs4l:"Free Sushi for Life",birthday:"Birthday",mates_rates:"Mates Rates",family_friends:"Rolled Fam",influencer:"Rolled Legend",staff:"Staff Card",launch:"Day 1",founders:"Founders"};
const ccCardLabel = (t)=>(t&&(CC_SPECIAL_LABELS[t.key]||t.display_name||t.key))||"";
// Special-card config, Phase 6: each till placement reads its OWN field from the server
// when the reply carries it (the card type's scanner columns, rolled-club-card migrations
// 145 + 153). A key that is there is used as it is: blank means that placement's own
// built-in (the scanner's label, or the key), never another field. A key that is missing
// (an older backend, or a card-type list cached on this tablet before the switch) runs
// exactly the old code. Rolled Legends never carries the redemption copy: its till words
// are the sent reward's (reward_label / reward_description), as before.
const ccOwn=(o,k)=>!!o&&typeof o==="object"&&Object.prototype.hasOwnProperty.call(o,k);
// The grey line under the Square button in Customer Lookup's "Redeemed" pop-up.
const ccLookupRewardLine=(rd)=>ccOwn(rd,"lookup_redeem_reward_line")?rd.lookup_redeem_reward_line:rd.reward_label;
// Student Night — its own sky-blue type (JJ, 18 Sep 2026). Branded here so the
// scanner face + Customer Lookup icon show sky blue + "Student Night" instead of
// the default wasabi / ticket. Assigned rather than inlined into the maps above to
// keep this edit off the emoji-laden literals. The generic CCFace (no bespoke
// CC_FACE_HTML entry) then paints the accent→nori gradient + "Student Night" hero,
// which matches the approved scanner mockup. Icon is staff wayfinding only.
CC_SPECIAL_ACCENTS.student_night = "#1c4a86"; // dusk (matches app .sf-sn face)
CC_SPECIAL_ICONS.student_night = "🎓"; // graduation cap
CC_SPECIAL_LABELS.student_night = "Student Night";
// Tile subtext = what the customer gets. Locked brand copy overrides the
// catalogue reward_description for cards where the wording is fixed (Legend has
// no standing discount — it's the offer model, "Invite only offers").
const CC_SPECIAL_SUBTEXT = {influencer:"Invite only offers"};
const ccCardReward = (t)=>(t&&(CC_SPECIAL_SUBTEXT[t.key]||t.reward_description))||"";
// O7 — the mini card-face pill wants the DISCOUNT line ("50% off"), not the
// reward line. discount_value is served by GET /scanner/special-card-types.
const ccCardDisc = (t)=> t && t.discount_value ? t.discount_value : "";

// hex → rgba (kiosk-safe; avoids color-mix() which old tablet browsers lack).
function ccHexToRgb(hex){const h=(hex||"").replace("#","");const f=h.length===3?h.split("").map(c=>c+c).join(""):h;const n=parseInt(f,16);return [(n>>16)&255,(n>>8)&255,n&255];}
function ccRgba(hex,a){const [r,g,b]=ccHexToRgb(hex);return "rgba("+r+","+g+","+b+","+a+")";}

// — Staff-readable card-types feed for the home special tiles. Mock on
//   localhost preview; the real endpoint (rolled-club-card #62) on prod.
async function ccMockSpecialCardTypes(){
  await new Promise(r=>setTimeout(r,180));
  return {types:[
    {key:"birthday",display_name:"Birthday",reward_description:"Two free rolls, a drink & a dip",discount_name:"LOYALTY BDAY",eligibility_check:"birthday_day",daily_cap:1},
    {key:"fs4l",display_name:"Free Sushi for Life",reward_description:"2 free rolls",discount_name:"FREE SUSHI FOR LIFE",daily_cap:1},
    {key:"mates_rates",display_name:"Mates Rates",reward_description:"10% off the bill",discount_name:"Mates Rates"},
    {key:"family_friends",display_name:"Rolled Fam",reward_description:"50% off the bill",discount_name:"Friends"},
    {key:"influencer",display_name:"Rolled Legends Card",reward_description:"Invite only offers",discount_name:null},
    {key:"staff",display_name:"Staff Card",reward_description:"50% off the bill",discount_name:"Rolled Staff"},
    {key:"free_roll",display_name:"Free Roll",reward_description:"1 free roll",discount_name:"FREE ROLL"},
  ]};
}
// Cache the CC-configured card-type code set so an OFFLINE redemption can still
// show the correct Square code (the offline picker reads ccCachedCardTypes()).
const CC_CARDTYPES_CACHE = "rolled.cc.cardtypes.v1";
async function ccFetchSpecialCardTypes(token){
  if(CC_PHASE5_MOCKS) return ccMockSpecialCardTypes();
  try{
    const r = await ccApiCall("/scanner/special-card-types",token);
    try{ if(r&&Array.isArray(r.types)) localStorage.setItem(CC_CARDTYPES_CACHE, JSON.stringify(r.types)); }catch(e){}
    return r;
  }catch(err){
    // Offline / fetch failed → fall back to the cached set so the deck + offline
    // redeem picker still work. Empty if this tablet has never fetched online.
    try{ const c=localStorage.getItem(CC_CARDTYPES_CACHE); if(c) return {types:JSON.parse(c), _cached:true}; }catch(e){}
    throw err;
  }
}
// The cached CC code set (card_type → discount_name/value/reward_description), for
// the offline redeem picker. [] if never cached online.
function ccCachedCardTypes(){
  try{ const c=localStorage.getItem(CC_CARDTYPES_CACHE); return c?JSON.parse(c):[]; }catch(e){ return []; }
}
// Tier-rewards config (Club Card per-tier rewards) — cached the same way so the
// offline redeem picker can show every tier reward + its correct Square code.
const CC_TIERREWARDS_CACHE = "rolled.cc.tierrewards.v1";
async function ccFetchTierRewards(token){
  try{
    const r = await ccApiCall("/scanner/tier-rewards",token);
    try{ if(r&&Array.isArray(r.tiers)) localStorage.setItem(CC_TIERREWARDS_CACHE, JSON.stringify(r.tiers)); }catch(e){}
    return r;
  }catch(err){
    try{ const c=localStorage.getItem(CC_TIERREWARDS_CACHE); if(c) return {tiers:JSON.parse(c), _cached:true}; }catch(e){}
    throw err;
  }
}
function ccCachedTierRewards(){
  try{ const c=localStorage.getItem(CC_TIERREWARDS_CACHE); return c?JSON.parse(c):[]; }catch(e){ return []; }
}
// Local-preview mock customer (lookup + migrate) — only ever returned for the
// sentinel preview token, so it can't reach a real scanner.
async function ccMockLookup(id){
  await new Promise(r=>setTimeout(r,160));
  return {loyalty_id:id||"ROLL-7F3K9Q",first_name:"Liam",customer_name:"Liam",loyalty_tier:"gold",stamp_count:6,stamps_required:8,total_visits:23,completed_cards:2,total_cards:2,special_cards:[
    {card_type:"birthday",status:"pending_activation",activates_at:"2026-07-14",display_name:"Birthday"},
    {card_type:"fs4l",status:"available",display_name:"Free Sushi for Life"},
    {card_type:"mates_rates",status:"available",display_name:"Mates Rates"},
    {card_type:"family_friends",status:"cap_daily",used_today:1,display_name:"Rolled Fam"},
    {card_type:"influencer",status:"available",last_redeemed_at:null,display_name:"Rolled Legend"},
    {card_type:"free_roll",status:"available",display_name:"Free Roll"},
  ]};
}

// ============================================================
// SCANNER REDESIGN — special-card faces + before/after split home.
// Built to the locked mockup (artifact 20098945, 17 Jul): a BEFORE/AFTER split
// home, an animated special-card deck + full Silver Club Card, the camera↔£
// order diagram, and a redeem drill-down (full Club Card reward first, then the
// active special cards → each card's scanner). Faces render per card TYPE (no
// customer data at scan time). Sub-scanners (CCScannerScreen, CCUniversalRedeemScan) are
// reused unchanged; only home + the redeem drill-down are new.
// ============================================================
const CC_ROO_PATHS = ["M183.65,256.44c1.35,1.41.9,3.55-.67,6.01-38.94,50.24-71.98,115.18-138.28,131.85-10.19,2.41-37.48,9.64-44.35.99-3.71-9.76,22.09-20.77,28.94-25.68,4.62-2.86,9.26-5.89,13.83-8.97,33.71-22.45,52.48-49.36,57.25-89,.56-3,1.54-5.63,3.28-7.54,11.43-10.51,66.39-15.13,79.87-7.8l.12.13Z","M205.56,205.85c17.93,2.96,35.82,7.94,51.89,16.51,11.52,6.63-5.62,8.57-10.66,9.96-11.94,2.89-23.59,7.53-35.17,11.49-2.63.88-5.37,1.55-8.12,1.66-3.22.17-6.54-.44-9.72-1.16-27.98-6.82-57.13-4.89-84.97,2.12-6.95,1.17-5.36-5.51-3.87-9.75,3.8-9.68,10.09-29.08,20.44-32.76,10.8-2.79,22.32-2.33,33.43-2.63,15.63.02,31.18,1.7,46.46,4.51l.29.05Z","M334.73,222.54c.46,2.8-13.13,2.47-25.77,2.25-9.6-.17-18.19.22-22.25-.76-4.01-1-7.71-3.32-11.24-5.55-32.7-20.97-72.65-28.37-111.3-29.89-7.17-.39-15.81-.08-22.55.28-3.74.12-6.15-.06-6.34-1.71-.17-1.31,1.02-3.42,2.8-5.76,2.69-3.51,6.74-7.76,10.05-11.43,6.51-7.13,14.11-16.23,21.19-17.66,7.88-2.09,18.04-.15,26.42.78,51.95,5.34,107.06,26.62,138.94,69.3l.03.16Z","M375.65,212.28c2.85,5.83,12.81,21.85,8.71,25.83-2.58,2.5-9.18-1.84-12.85-3.68-3.7-1.84-6.98-3.16-11.02-5.7-2.37-1.49-5.26-3.2-7.15-5.54-37-49.64-96.56-77.61-157.51-83.33-2.77-.34-8.44-.67-9.07-2.88-.42-3.01,7.47-6.56,10.68-7.93,78.17-32.33,140.85,14,178.06,82.97l.14.26Z","M341.99,264.98c-.93-17.49-20.22-20.96-36.39-20.27-25.36.83-49.41,6.81-72.72,15.31l-.25.09c-21.31,6.42-40.93,29.1-50.92,50.07-46.08,103.46,166.13,26.91,160.27-45.2ZM290.71,292.86c-12.1,10.27-28.97,17.72-44.16,23.43-10.7,4.08-34.99,12.43-24.21-8.93,4.17-8.9,12.84-21.04,24.56-27.3,13.45-7.09,31.17-8.02,45.63-6.14,3.41.46,8.71,1.76,9.19,3.56l.04.1c.94,4.2-3.91,9.13-11.05,15.29Z","M323.15,58.36c1.61,2.57,2,2.84,2.29,4.62,2.07,12.79-3.56,20.44-1.21,34.08,2.99,17.36,8.75,40.05,26.07,49.59,11.64,6.42,16.43-11.49,22.35-16.08,8.09-6.27,20.87.7,28.9,1.94,15.19,2.35,44.61,10.22,53.42-3.48,13.77-21.4-6.76-25.03-19.78-35.09-15.4-9.33-26-22.07-40.24-32.73-8.56-6.41-23.29-3.86-31.39-11.07-8.1-7.21-7.63-21.29-12.63-29.54-4.29-7.09-11.05-12.67-16.71-15.86-7.07-3.97-14.74-7.07-20.35-2.39-11.24,9.38-3.88,25.39-1.04,36,2.34,8.76,6.41,13.75,10.33,20.01Z"];
// Face HTML (inner of .sf) transcribed VERBATIM from mockup 20098945; {UID} is
// swapped per render so pattern ids never collide. cc-prefixed symbol ids.
const CC_FACE_HTML = {
  freeroll: "<div class=\"sf-lay sf-nori\"></div><div class=\"sf-lay sf-aurora\"></div><div class=\"sf-lay sf-glints\"><span style=\"top:24%;left:70%;animation-delay:0s\"></span><span style=\"top:58%;left:84%;animation-delay:1.4s\"></span><span style=\"top:74%;left:60%;animation-delay:2.6s\"></span><span style=\"top:16%;left:52%;animation-delay:2s\"></span></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.2\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><g transform=\"translate(196,101) rotate(-45) scale(0.205) translate(-411.89,-240.85)\" fill=\"#e6c06a\"><use href=\"#cc-fishbody\"/><use href=\"#cc-miniscales\"/></g></svg><div class=\"sf-lay sf-spot\"></div><div class=\"sf-content\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-pill\" style=\"color:#d8b45a;border-color:#d8b45a\">Free Sushi Roll</span></div><div style=\"flex:1\"></div><div class=\"sf-kick\">Welcome to the club</div><div class=\"sf-hero\">Free<br/>Roll</div></div>",
  centre: "<div class=\"sf-lay sf-graphite\"></div><div class=\"sf-lay sf-sheen-lite\"></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.16\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><defs><pattern id=\"p-centre{UID}\" width=\"150\" height=\"78\" patternUnits=\"userSpaceOnUse\" patternTransform=\"rotate(-6)\"><g fill=\"#ffffff\" transform=\"translate(6,12) scale(0.072)\"><use href=\"#cc-fishbody\"/><use href=\"#cc-miniscales\"/></g><g fill=\"#ffffff\" transform=\"translate(144,12) scale(-0.072,0.072)\"><use href=\"#cc-fishbody\"/><use href=\"#cc-miniscales\"/></g></pattern></defs><rect width=\"300\" height=\"207\" fill=\"url(#p-centre{UID})\"/></svg><div class=\"sf-content sf-content--dark\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-pill\">10% off</span></div><div style=\"flex:1\"></div><div class=\"sf-kick\">Centre worker</div><div class=\"sf-hero\">{SITE}</div></div>",
  founders: "<div class=\"sf-lay sf-nori\"></div><div class=\"sf-lay sf-aurora\"></div><div class=\"sf-lay sf-glints\"><span style=\"top:24%;left:70%;animation-delay:0s\"></span><span style=\"top:58%;left:84%;animation-delay:1.4s\"></span><span style=\"top:74%;left:60%;animation-delay:2.6s\"></span><span style=\"top:16%;left:52%;animation-delay:2s\"></span></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.17\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><use href=\"#cc-roo\" transform=\"translate(150,44) scale(0.30)\" fill=\"#e6c06a\"/></svg><div class=\"sf-lay sf-spot\"></div><div class=\"sf-content\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-pill\">100% off</span></div><div style=\"flex:1\"></div><div class=\"sf-hero\">Founders<br/>Card</div></div>",
  fs4l: "<div class=\"sf-lay sf-gold\"></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.15\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><defs><pattern id=\"p-fs4l{UID}\" width=\"84\" height=\"74\" patternUnits=\"userSpaceOnUse\"><use href=\"#cc-roo\" fill=\"#ffffff\" transform=\"translate(12,11) scale(0.131)\"/></pattern></defs><rect width=\"300\" height=\"207\" fill=\"url(#p-fs4l{UID})\"/></svg><div class=\"sf-lay sf-sheen\"></div><div class=\"sf-lay sf-spot\"></div><div class=\"sf-content\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-serial\">No. 5 of 5</span></div><div class=\"sf-mid\"><div class=\"sf-fs\">Free Sushi</div><div class=\"sf-fl\">FOR LIFE</div></div></div>",
  mates: "<div class=\"sf-lay sf-lime\"></div><div class=\"sf-lay sf-sheen-lite\"></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.13\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><defs><pattern id=\"p-mates{UID}\" width=\"150\" height=\"78\" patternUnits=\"userSpaceOnUse\" patternTransform=\"rotate(-6)\"><g fill=\"#ffffff\" transform=\"translate(6,12) scale(0.072)\"><use href=\"#cc-fishbody\"/><use href=\"#cc-miniscales\"/></g><g fill=\"#ffffff\" transform=\"translate(144,12) scale(-0.072,0.072)\"><use href=\"#cc-fishbody\"/><use href=\"#cc-miniscales\"/></g></pattern></defs><rect width=\"300\" height=\"207\" fill=\"url(#p-mates{UID})\"/></svg><div class=\"sf-content\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-pill\">10% off</span></div><div style=\"flex:1\"></div><div class=\"sf-hero\">Mates<br/>Rates</div></div>",
  staff: "<div class=\"sf-lay sf-midnight\"></div><div class=\"sf-lay sf-sheen-lite\"></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.28\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><g transform=\"translate(31,25) scale(0.29) rotate(-26 411.89 240.85)\"><use href=\"#cc-fishbody\" fill=\"#bcd4f2\"/><use href=\"#cc-miniscales\" fill=\"#5e83b8\"/><circle cx=\"629.28\" cy=\"221.23\" r=\"34.3\" fill=\"#16306b\"/></g></svg><div class=\"sf-content sf-content--dark\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-pill\" style=\"text-transform:uppercase\">50% off</span></div><div style=\"flex:1\"></div><div class=\"sf-hero\">Staff<br/>Card</div></div>",
  fam: "<div class=\"sf-lay sf-coral\"></div><div class=\"sf-lay sf-sheen-lite\"></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.14\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><defs><pattern id=\"p-fam{UID}\" width=\"70\" height=\"60\" patternUnits=\"userSpaceOnUse\" patternTransform=\"rotate(-8)\"><use href=\"#cc-roo\" fill=\"#ffffff\" transform=\"translate(2,4) scale(0.072)\"/><use href=\"#cc-roo\" fill=\"#ffffff\" transform=\"translate(43,30) scale(0.040)\"/></pattern></defs><rect width=\"300\" height=\"207\" fill=\"url(#p-fam{UID})\"/></svg><div class=\"sf-content\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-pill\">50% off</span></div><div style=\"flex:1\"></div><div class=\"sf-kick\">Family &amp; friends</div><div class=\"sf-hero\">Rolled<br/>Fam</div></div>",
  legend: "<div class=\"sf-lay sf-violet\"></div><div class=\"sf-lay sf-aurora\"></div><div class=\"sf-lay sf-glints\"><span style=\"top:24%;left:70%;animation-delay:0s\"></span><span style=\"top:58%;left:84%;animation-delay:1.4s\"></span><span style=\"top:74%;left:60%;animation-delay:2.6s\"></span><span style=\"top:16%;left:52%;animation-delay:2s\"></span></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.16\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><use href=\"#cc-roo\" transform=\"translate(150,44) scale(0.30)\" fill=\"#c89bff\"/></svg><div class=\"sf-content sf-content--dark\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-pill\">Member</span></div><div style=\"flex:1\"></div><div class=\"sf-kick\" style=\"color:#c89bff;opacity:0.9\">Invite only</div><div class=\"sf-hero\">Rolled<br/>Legend</div></div>",
  birthday: "<div class=\"sf-lay sf-pearl\"></div><div class=\"sf-lay sf-holo\"></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.72;-webkit-mask-image:linear-gradient(to left,#000 22%,transparent 82%);mask-image:linear-gradient(to left,#000 22%,transparent 82%)\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><defs><pattern id=\"p-bday{UID}\" width=\"100\" height=\"62\" patternUnits=\"userSpaceOnUse\" patternTransform=\"rotate(-8)\"><g transform=\"translate(8,6) scale(0.092)\"><use href=\"#cc-fishbody\" fill=\"#ffffff\"/><use href=\"#cc-miniscales\" fill=\"#ffffff\"/></g></pattern></defs><rect width=\"300\" height=\"207\" fill=\"url(#p-bday{UID})\"/></svg><div class=\"sf-lay sf-spot\"></div><div class=\"sf-content\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-pill\">1st Dec</span></div><div style=\"flex:1\"></div><div class=\"sf-hero\">Birthday<br/>Card</div></div>",
  day1: "<div class=\"sf-lay sf-day1-base\"></div><div class=\"sf-lay sf-aurora\"></div><div class=\"sf-lay sf-spot\"></div><svg class=\"sf-lay sf-mono\" style=\"--sf-base:0.17\" viewBox=\"0 0 300 207\" preserveAspectRatio=\"xMidYMid slice\"><use href=\"#cc-roo\" transform=\"translate(150,44) scale(0.30)\" fill=\"#e8c068\"/></svg><div class=\"sf-content sf-content--dark\"><div class=\"sf-top\"><span class=\"sf-logo\"><svg viewBox=\"0 0 462 400\" aria-hidden=\"true\"><use href=\"#cc-roo\"/></svg><span class=\"sf-wordmark\">rolled</span></span><span class=\"sf-pill\" style=\"text-transform:uppercase\">2 free rolls</span></div><div style=\"flex:1\"></div><div class=\"sf-kick\">Day 1 only</div><div class=\"sf-hero\">Day 1<br/>Card</div></div>",
};
// Which mockup face each production card-type key wears. Unknown keys fall back
// to a generic accent face (CCFace), so a brand-new card type still renders.
const CC_KEY_TO_FACE = {staff:"staff",influencer:"legend",birthday:"birthday",mates_rates:"mates",family_friends:"fam",fs4l:"fs4l",founders:"founders",centre:"centre",centre_worker:"centre",free_roll:"freeroll",launch:"day1"};

// Shared SVG symbols (roo mark + soy fish) the faces reference via <use>.
// Rendered once inside the scanner root so refs always resolve.
function CCScannerDefs(){
  return (
    <svg width="0" height="0" style={{position:"absolute"}} aria-hidden="true">
      <defs>
        <g id="cc-roo">{CC_ROO_PATHS.map((d,i)=><path key={i} d={d}/>)}</g>
        <path id="cc-fishbody" d={CC_FISH_BODY}/>
        <g id="cc-miniscales">{CC_MINI_SCALES.map((d,i)=><path key={i} d={d}/>)}</g>
      </defs>
    </svg>
  );
}

// A special-card face for one card TYPE. Bespoke design when the key is known;
// otherwise a brand-safe generic (accent gradient + label + discount pill).
let __ccFaceSeq=0;
function CCFace({typeKey,label,disc}){
  const idRef=useRef(null);
  if(idRef.current===null) idRef.current="ccf"+(++__ccFaceSeq);
  const face=CC_KEY_TO_FACE[typeKey];
  if(face){
    const html=CC_FACE_HTML[face].replace(/\{UID\}/g,idRef.current).replace(/\{SITE\}/g,(SITE&&SITE.name)||"Rolled");
    return <div className={"sf"+((face==="founders"||face==="freeroll")?" sf-founders":"")+(face==="day1"?" sf-day1":"")} dangerouslySetInnerHTML={{__html:html}}/>;
  }
  const accent=ccAccent(typeKey);
  return (
    <div className="sf">
      <div className="sf-lay" style={{background:"linear-gradient(125deg,"+accent+" 0%,#00392c 100%)"}}/>
      <div className="sf-lay sf-sheen-lite"/>
      <div className="sf-content sf-content--dark">
        <div className="sf-top">
          <span className="sf-logo"><svg viewBox="0 0 462 400" aria-hidden="true"><use href="#cc-roo"/></svg><span className="sf-wordmark">rolled</span></span>
          {disc?<span className="sf-pill">{disc}</span>:null}
        </div>
        <div style={{flex:1}}/>
        <div className="sf-hero">{label||typeKey}</div>
      </div>
    </div>
  );
}

// Full Club Card face (tier header + 8 soy-fish stamps). Silver by default; used
// for the "reward" (all 8 filled) and the AFTER "in progress" preview.
function CCFullClubCard({tier="silver",filled=8,total=8,title="Rolled Club Card",name}){
  const cap=(tier||"silver").charAt(0).toUpperCase()+(tier||"silver").slice(1);
  const stamps=[]; for(let i=0;i<total;i++) stamps.push(i<filled);
  return (
    <div className="club-card">
      <div className={"card-header card-header--"+tier}>
        <div className={"card-header-scrim"+(tier==="diamond"?" card-header-scrim--diamond":"")}/>
        <div className="card-header-content">
          <div>
            <span className="card-header-title">{title}</span>
            {name?<span className="card-header-name">{name}</span>:null}
          </div>
          <span className={"card-tier-badge card-tier-badge--"+tier}>{cap}</span>
        </div>
      </div>
      <div className="card-body">
        <div className="pool-fx">
          <div className="pool-patch" style={{width:78,height:78,left:"8%",top:"20%",animationDuration:"7s"}}/>
          <div className="pool-patch" style={{width:62,height:62,left:"62%",top:"44%",animationDuration:"9s",animationDelay:"2s"}}/>
        </div>
        <div className="stamp-grid">
          {stamps.map((f,i)=><div key={i} className="stamp-slot"><CCSoyFish filled={f}/></div>)}
        </div>
      </div>
    </div>
  );
}

// camera↔£ order chips — BEFORE = camera→£ (scan then pay), AFTER = £→camera.
function CCOrderDiagram({side}){
  const cam=<svg viewBox="0 0 24 24"><path d="M4 7.5h2.8L8 5.5h8L17.2 7.5H20a1 1 0 0 1 1 1v9.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8.5a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.4"/></svg>;
  const arrow=<span className="oarrow"><svg viewBox="0 0 24 16"><path d="M2 8h18M15 3l6 5-6 5"/></svg></span>;
  const camChip=<div className={"oi"+(side==="before"?" oi--active":"")}>{cam}</div>;
  const poundChip=<div className={"oi"+(side==="after"?" oi--active":"")}><span className="pound">£</span></div>;
  return <div className="sc-order">{side==="before"?<>{camChip}{arrow}{poundChip}</>:<>{poundChip}{arrow}{camChip}</>}</div>;
}
function CCScanIcon(){ return <svg viewBox="0 0 24 24"><path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm10 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-4 4h2v2h-2v-2zm2 2h2v2h-2v-2zm2-2h2v2h-2v-2z"/></svg>; }

// Mini club card for the home row tiles. "reward" = all 8 filled; each fish
// wiggles on its own timing, then the grid flips to a "Reward Ready!" state.
// "stamp" = fills 0→3 one at a time (bounce-pop), holds, then resets — the
// after-payment stamping cue. Silver header is textless (see .sc-rowtile-viz CSS).
function CCMiniClubCard({variant}){
  const header=(
    <div className="card-header card-header--silver">
      <div className="card-header-scrim"/>
      <div className="card-header-content"><div><span className="card-header-title">Rolled Club Card</span><span className="card-header-name">Silver</span></div><span className="card-tier-badge card-tier-badge--silver">Silver</span></div>
    </div>
  );
  const pools=(<div className="pool-fx"><div className="pool-patch" style={{width:60,height:60,left:"8%",top:"20%",animationDuration:"7s"}}/><div className="pool-patch" style={{width:48,height:48,left:"62%",top:"44%",animationDuration:"9s",animationDelay:"2s"}}/></div>);
  if(variant==="reward"){
    const st=[0,0.5,0.2,0.7,0.35,0.9,0.15,0.6];
    return (
      <div className="club-card">{header}
        <div className="card-body">{pools}
          <div className="stamp-grid rw-stamps">{st.map((d,i)=><div key={i} className="stamp-slot"><CCSoyFish filled className="rw-fish" style={{animationDelay:(-d)+"s"}}/></div>)}</div>
          <div className="rw-ready"><span>Reward<br/>Ready!</span></div>
        </div>
      </div>
    );
  }
  const sp=["sp1","sp2","sp3"];
  return (
    <div className="club-card">{header}
      <div className="card-body">{pools}
        <div className="stamp-grid">
          {sp.map((c,i)=><div key={i} className="stamp-slot stamp-anim"><CCSoyFish className="s-empty"/><CCSoyFish filled className={"s-pop "+c}/></div>)}
          {[0,1,2,3,4].map(i=><div key={"e"+i} className="stamp-slot"><CCSoyFish/></div>)}
        </div>
      </div>
    </div>
  );
}

// Section-head camera↔£ chips — BEFORE = camera→£ (camera lit), AFTER = £→camera (£ lit).
function CCSecOrder({side}){
  const cam=<svg viewBox="0 0 24 24"><path d="M4 7.5h2.8L8 5.5h8L17.2 7.5H20a1 1 0 0 1 1 1v9.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8.5a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.4"/></svg>;
  const arrow=<span className="oarrow"><svg viewBox="0 0 24 16"><path d="M2 8h18M15 3l6 5-6 5"/></svg></span>;
  const camChip=<div className={"oi"+(side==="before"?" oi--amber":"")}>{cam}</div>;
  const poundChip=<div className={"oi"+(side==="after"?" oi--wasabi":"")}><span className="pound">£</span></div>;
  return <div className="sc-sec-order">{side==="before"?<>{camChip}{arrow}{poundChip}</>:<>{poundChip}{arrow}{camChip}</>}</div>;
}

// — HOME (mockup 20098945 v3): utility toolbar up top (Lookup / Sign Up / Issue
//   Centre / Migrate) + divider, then two stacked sections. AFTER payment → Add a
//   Stamp (stamp scanner). BEFORE payment → Club Card Reward (its own reward
//   scanner) and Special Cards (the drawer). Each card tile carries a live mini card.
// An online-only toolbar button while the till is offline: greyed, and it doesn't open.
const CC_UTIL_OFF = {opacity:0.38, cursor:"not-allowed"};
export function CCHomeScreen({session,token,isOnline,queueCount,onReward,onRedeem,onClubScan,onLookup,onSignup,onIssueSpecial,onMigrate}){
  const [types,setTypes]=useState(null); // active special-card types (for the deck)
  useEffect(()=>{
    let cancelled=false;
    ccFetchSpecialCardTypes(token)
      .then(r=>{if(!cancelled) setTypes((r&&r.types)||[])})
      .catch(()=>{if(!cancelled) setTypes([])});
    // Warm the tier-rewards cache for the offline redeem picker (fire-and-forget).
    ccFetchTierRewards(token).catch(()=>{});
    return ()=>{cancelled=true};
  },[token]);

  // Decorative Special Cards deck — Staff first, Founders + the legacy Day 1
  // (launch) card excluded (JJ 23 Jul) + Free Roll (JJ 14 Sep); Mates always in.
  const deckKeys=(()=>{
    const active=(types&&types.length)?types.map(t=>t.key):["staff","influencer","birthday","mates_rates"];
    const shown=active.filter(k=>k!=="founders"&&k!=="launch"&&k!=="free_roll");
    const ordered=["staff",...shown.filter(k=>k!=="staff")];
    if(!ordered.includes("mates_rates")) ordered.push("mates_rates");
    return ordered.slice(0,4);
  })();

  return (
    <div className="cc-screen cc-home3">
      {(!isOnline||queueCount>0)&&(
        <div className="cc-home-bar">
          <div className="cc-home-status">
            {!isOnline&&<span className="cc-offline"><span className="cc-offline-dot"/> Offline</span>}
            {queueCount>0&&<span className="cc-queue">⏳ {queueCount} queued</span>}
          </div>
        </div>
      )}

      {/* Utility toolbar — left→right order (JJ 12 Aug): Migrate · Issue Special ·
          Customer Lookup · Customer Sign Up. Day 1 used to have a sixth button here,
          shown only while a "launch event" row was live — a model JJ dropped in July,
          so the button never once appeared. Day 1 is a card, and it is now issued from
          the card grid behind Issue Special Card like every other card. */}
      {/* Offline (JJ, 14 Oct 2026): these four all need the server, so they don't open
          screens that can't work. Stamps and rewards keep working offline below. */}
      <div className="sc-utils">
        <button className="sc-util" onClick={onMigrate} disabled={!isOnline} style={isOnline?undefined:CC_UTIL_OFF}><span className="sc-util-ic"><svg viewBox="0 0 24 24"><path d="M4 8h12M12 4l4 4-4 4"/><path d="M20 16H8M12 20l-4-4 4-4"/></svg></span><span className="sc-util-lbl">Migrate<br/>Paper Card</span></button>
        <button className="sc-util" onClick={onIssueSpecial} disabled={!isOnline} style={isOnline?undefined:CC_UTIL_OFF}><span className="sc-util-ic"><svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 10h18M7 15h5"/></svg></span><span className="sc-util-lbl">Issue<br/>Special Card</span></button>
        <button className="sc-util" onClick={onLookup} disabled={!isOnline} style={isOnline?undefined:CC_UTIL_OFF}><span className="sc-util-ic"><svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg></span><span className="sc-util-lbl">Customer<br/>Lookup</span></button>
        <button className="sc-util" onClick={onSignup} disabled={!isOnline} style={isOnline?undefined:CC_UTIL_OFF}><span className="sc-util-ic"><svg viewBox="0 0 24 24"><circle cx="9" cy="8" r="3.6"/><path d="M3.5 20c0-3.6 2.6-5.6 5.5-5.6"/><path d="M17 10v6M14 13h6"/></svg></span><span className="sc-util-lbl">Customer<br/>Sign Up</span></button>
      </div>
      {!isOnline&&<div style={{fontSize:12,fontWeight:700,color:"var(--cc-kewpie)",opacity:0.75,textAlign:"center",margin:"-2px 12px 8px",lineHeight:1.4}}>No connection: these four need one. Stamps and rewards still work.</div>}
      <div className="sc-utils-hr"/>

      <div className="sc-sections">
        {/* AFTER payment (top) */}
        <div className="sc-section sc-section--after">
          <div className="sc-sec-head"><span className="sc-sec-eyebrow after">After payment</span><CCSecOrder side="after"/></div>
          <div className="sc-sec-sub"><b>After</b> customers have made a food purchase, give them a stamp!</div>
          <button className="sc-rowtile after" onClick={onClubScan}>
            <div className="sc-rowtile-viz"><CCMiniClubCard variant="stamp"/></div>
            <div className="sc-rowtile-body"><div className="sc-rowtile-title">Add a Stamp</div><div className="sc-rowtile-sub">Once they&rsquo;ve paid &mdash; scan their Club Card to add a stamp.</div></div>
          </button>
        </div>

        <div className="sc-hr"/>

        {/* BEFORE payment (bottom) */}
        <div className="sc-section sc-section--before">
          <div className="sc-sec-head"><span className="sc-sec-eyebrow before">Before payment</span><CCSecOrder side="before"/></div>
          <div className="sc-sec-sub">Discounts and Rewards are redeemed <b>before</b> payment. Scan their QR code first.</div>
          <button className="sc-rowtile before" onClick={onReward}>
            <div className="sc-rowtile-viz sc-rowtile-viz--reward"><CCMiniClubCard variant="reward"/></div>
            <div className="sc-rowtile-body"><div className="sc-rowtile-title">Club Card Reward</div><div className="sc-rowtile-sub">A fully-stamped Club Card. Unlock the reward &amp; apply it in Square.</div></div>
          </button>
          <button className="sc-rowtile before" onClick={onRedeem}>
            <div className="sc-rowtile-viz"><div className="sc-deck">{deckKeys.map((k,i)=><div key={i} className="dcard"><CCFace typeKey={k}/></div>)}</div></div>
            <div className="sc-rowtile-body"><div className="sc-rowtile-title">Special Cards</div><div className="sc-rowtile-sub">Staff, Legend, Birthday, Mates &amp; more &mdash; scan any of them to redeem.</div></div>
          </button>
        </div>
      </div>
      <a className="sc-guide" href="https://regular-xylophone-a52.notion.site/Club-Card-Scanner-Staff-Guide-3b5ffaac23678115a114ea7cc0c22443" target="_blank" rel="noopener noreferrer">
        <span className="sc-guide-ic">📖</span>
        <span className="sc-guide-body">
          <span className="sc-guide-t">Staff Guide <svg className="sc-guide-ext" viewBox="0 0 24 24"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg></span>
          <span className="sc-guide-s">How to use the scanner &mdash; opens in a new tab</span>
        </span>
      </a>
    </div>
  );
}

// The wrong-scanner panel. JJ, 12 Oct 2026: "everytime the wrong scanner is picked (like when
// you pick the club card reward scanner and try to add a stamp) it should give an option to
// take you to the right scanner". Nothing has been sent for the scan; each button carries the
// same scan to the scanner it names (onGo), so the customer needn't show their QR again.
const CC_GO_ICON={
  stamp:<svg viewBox="0 0 24 24"><path d="M4 7.5h2.8L8 5.5h8L17.2 7.5H20a1 1 0 0 1 1 1v9.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8.5a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.2"/></svg>,
  reward:<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/></svg>,
  special:<svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 10h18M7 15h5"/></svg>,
};
const CC_GO_LABEL={stamp:"Want to give a stamp? Tap here",reward:"Want to redeem a reward? Tap here",special:"Want to redeem a Special Card? Tap here"};
function CCWrongQrPanel({title,text,go,onGo,onRetry}){
  return (
    <div className="cc-wrongqr">
      <div className="cc-wrongqr-badge"><svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M8 12h8"/></svg></div>
      <div className="cc-wrongqr-h">{title}</div>
      {text?<div className="cc-wrongqr-p">{text}</div>:null}
      {(go||[]).map(k=><button key={k} className="cc-wrongqr-cta" onClick={()=>onGo(k)}>{CC_GO_ICON[k]} {CC_GO_LABEL[k]}</button>)}
      <button className="cc-wrongqr-retry" onClick={onRetry}>Scan another card</button>
    </div>
  );
}

// html5-qrcode's start() resolves before the video is playing: isScanning only turns true
// about half a second later, and stop() can't be called before then. So a carried scan waits
// for it (up to 3 s, e.g. no camera) before it runs, and the scan path can stop the camera.
function ccWhenScanning(getScanner,isGone,run){
  const t0=Date.now();
  const tick=()=>{
    if(isGone()) return;
    const sc=getScanner();
    if((sc&&sc.isScanning)||Date.now()-t0>3000){ run(); return; }
    setTimeout(tick,100);
  };
  tick();
}

// initialQr: {text} — a scan handed over from another scanner's "Tap here" (see onWrongQr),
// run here as if this camera had read it, so the customer doesn't show their QR twice.
function CCScannerScreen({session,token,requestPin,onScanResult,onBack,showToast,variant="stamp",onWrongQr,initialQr}){
  const [scanning,setScanning]=useState(false);
  const [isOnline,setIsOnline]=useState(navigator.onLine);
  const netDown=useCcNetDown(); // no answer from the server = offline too (ccIsOffline)
  const [queueCount,setQueueCount]=useState(0);
  // {kind,text}: this QR belongs in another scanner — kind "reward" (a full card on Add a
  // Stamp), "stamp" (an unfinished card on Club Card Reward) or "special" (a Special Card QR).
  const [wrongQr,setWrongQr]=useState(null);
  const [checking,setChecking]=useState(false); // reading card state between scan and PIN
  const [offlinePick,setOfflinePick]=useState(null); // {loyaltyId,staffName} → offline redeem picker
  const html5QrRef=useRef(null);
  const processingRef=useRef(false);
  const onQrSuccessRef=useRef(null); // the inverted-QR side reader calls the latest handler
  const runCarryRef=useRef(null); // runs a scan carried over from another scanner (initialQr)

  useEffect(()=>{
    // Stamp queue drains here; the REDEEM queue drains from the always-mounted shell
    // (ccSyncRedeems) so it reconciles on reconnect no matter which screen is open.
    const online=()=>{setIsOnline(true);syncQueue()};
    const offline=()=>setIsOnline(false);
    window.addEventListener("online",online);
    window.addEventListener("offline",offline);
    return ()=>{window.removeEventListener("online",online);window.removeEventListener("offline",offline)};
  },[]);

  useEffect(()=>{
    ccGetQueued().then(q=>setQueueCount(q.length)).catch(()=>{});
    const interval=setInterval(()=>{ccGetQueued().then(q=>setQueueCount(q.length)).catch(()=>{})},5000);
    return ()=>clearInterval(interval);
  },[]);

  // The drain itself is module-level (ccSyncQueue) so the scanner shell can also run
  // it on a timer; this screen still drains on 'online' and when it opens.
  const syncQueue=()=>ccSyncQueue(token,showToast,setQueueCount);

  // Sync-on-mount: drain any queued scans as soon as the scanner opens. The window
  // 'online' listener only fires on an offline→online transition, so scans queued in
  // a previous session would otherwise sit in IndexedDB until the next flip. (Redeems
  // drain from the shell — see ClubCardScanner — so they reconcile on reconnect even
  // when this screen isn't mounted.)
  useEffect(()=>{ if(!ccIsOffline()){ syncQueue(); } /* eslint-disable-next-line react-hooks/exhaustive-deps */ },[]);

  useEffect(()=>{
    let scanner=null;
    // Side reader for a QR the customer's phone has flipped to cream-on-dark (dark mode),
    // which html5-qrcode can't read. Same handler, only while the camera is scanning.
    const stopInverted=startInvertedQrFallback({readerId:"cc-qr-reader",isScanning:()=>!!(html5QrRef.current&&html5QrRef.current.isScanning),onDecode:(t)=>{if(onQrSuccessRef.current)onQrSuccessRef.current(t)}});
    const start=async()=>{
      try{
        if(typeof Html5Qrcode==="undefined"){showToast("QR library not loaded","error");return}
        scanner=new Html5Qrcode("cc-qr-reader");
        html5QrRef.current=scanner;
        await scanner.start({facingMode:"environment"},{fps:10,qrbox:{width:250,height:250},aspectRatio:1.0},onQrSuccess,()=>{});
        setScanning(true);
      }catch(err){console.error("[QR] camera failed",err);showToast("Camera access denied or unavailable","error")}
    };
    // A carried scan (initialQr) runs once the camera is really scanning, so the scan path can
    // stop it as it does for its own scans (else it reads a stray QR behind a panel).
    let gone=false;
    start().then(()=>{ if(!gone&&initialQr&&initialQr.text) ccWhenScanning(()=>html5QrRef.current,()=>gone,()=>{ if(runCarryRef.current) runCarryRef.current(); }); });
    return ()=>{gone=true;stopInverted();if(scanner&&scanner.isScanning){scanner.stop().catch(()=>{})}};
  },[]);

  const onQrSuccess=async(decodedText)=>{
    if(processingRef.current)return;
    processingRef.current=true;
    // QR payload formats:
    //   Wave 2 regular stamp: "ROLL-XXX" or "ROLL-XXX|<channel>"
    //   Phase 7d redemption:  "ROLL-XXX|<channel>|redeem|<card_id>"
    //   Reward-claim (b2/b3): "ROLL-XXX|<channel>|reward"
    // The reward-ready QR (rolled-frontend #148) carries a distinct "|reward"
    // suffix so claiming a completed card is its OWN scan — never a normal stamp
    // that silently auto-redeems the pending reward (JJ, 17 Jul). When present we
    // send reward_claim:true and the backend runs the explicit claim path
    // (redeem → fresh card → first stamp), then returns reward_redeemed so the
    // result routes to the reward-claimed screen.
    const parts=decodedText.trim().split("|");
    const rawId=parts[0];
    const rawChannel=parts[1];
    const loyaltyId=(rawId||"").toUpperCase();
    const channel=((rawChannel||"").toLowerCase().trim())||"unknown";
    // ── Kiosk "Sign Up to Keep": faux card QR ("FAUX|<token>") ───────────────
    // A signed-out visitor's faux card shows this. Staff scan it to genuinely
    // "give" the theatrical stamp — the backend records ONE faux stamp against the
    // token (never a real stamp on a real card; it firms up only if they sign up to
    // keep it, else it lapses). Handled BEFORE the ROLL validation + card lookup:
    // a faux token isn't a loyalty id and there's no card to look up. PIN-gated like
    // every other scanner action; who scanned it is audited server-side.
    if((parts[0]||"").trim().toUpperCase()==="FAUX"){
      const fauxToken=(parts[1]||"").trim();
      if(!/^[A-Za-z0-9_-]{10,64}$/.test(fauxToken)){
        showToast("Invalid faux card QR","error");
        setTimeout(()=>{processingRef.current=false;restartScanner()},1800);
        return;
      }
      if(html5QrRef.current&&html5QrRef.current.isScanning){await html5QrRef.current.stop().catch(()=>{})}
      if(ccIsOffline()){
        showToast("Offline — the faux stamp needs a connection. Try again when reconnected.","error");
        setTimeout(()=>{processingRef.current=false;restartScanner()},2500);
        return;
      }
      requestPin(async(staffName)=>{
        try{
          const r=await ccApiCall("/scanner/faux-stamp",token,{method:"POST",body:JSON.stringify({token:fauxToken,site_id:session.siteId,staff_name:staffName})});
          showToast((r&&r.already_scanned)?"Already collected — they're on 3 stamps":"Faux stamp added — they'll keep it when they join!","success");
          setTimeout(()=>{processingRef.current=false;restartScanner()},2200);
        }catch(err){
          showToast(ccStaffError(err),"error");
          setTimeout(()=>{processingRef.current=false;restartScanner()},2200);
        }
      },()=>{processingRef.current=false;restartScanner()});
      return;
    }
    let redemptionCardId=null;
    // A special-card redeem QR is either the legacy "…|redeem|<card_id>" or the new
    // rotating "…|rcode|<code>" (anti-screenshot). Detect BOTH so neither is ever
    // mistaken for a plain stamp scan below.
    if(parts[2]==="redeem" && parts[3] && /^[0-9a-f-]{36}$/i.test(parts[3])){
      redemptionCardId=parts[3];
    }else if(parts[2]==="rcode" && parts[3] && /^[A-Za-z0-9_-]{16,64}$/.test(parts[3])){
      redemptionCardId="rotating"; // marker: it's a special-card redeem, wrong scanner
    }
    // NOTE: the "|reward" suffix is deliberately NOT read any more. It only ever told us
    // which screen the customer happened to be on, which is not the same question as
    // "is there a reward to claim?" — see the guard below.
    if(!loyaltyId.match(/^ROLL-[A-Z0-9]{6}$/)){showToast("Invalid QR code","error");setTimeout(()=>{processingRef.current=false;restartScanner()},1500);return}
    // Special-card redemption belongs to the special-card scanner (staff open it from that
    // card's own tile), NOT the Club stamp scanner. A special-card redeem QR is
    // "…|redeem|<card_id>" — reject it here so a special card can never be mistaken for a
    // Club Card and wrongly earn a stamp (JJ, 17 Jul: "special cards have their own scanner").
    // A Special Card QR belongs on Special Cards. Nothing about it is sent from here (a live
    // Special Card code is single-use, so this scanner must never spend it); the panel below
    // offers the one-tap hop and hands Special Cards this same scan (JJ, 12 Oct 2026).
    if(redemptionCardId){
      if(html5QrRef.current&&html5QrRef.current.isScanning){await html5QrRef.current.stop().catch(()=>{})}
      setWrongQr({kind:"special",text:decodedText});
      processingRef.current=false;
      return;
    }
    if(html5QrRef.current&&html5QrRef.current.isScanning){await html5QrRef.current.stop().catch(()=>{})}
    // ── Which scanner is right? ASK THE CARD, NOT THE QR. ────────────────────────
    // This guard used to key off the "|reward" suffix. That was wrong, because a QR
    // carrying "|reward" is the EXCEPTION, not the rule — a customer with a claimable
    // reward very often presents a PLAIN "ROLL-XXX|<channel>" QR:
    //   • Wallet passes are ALWAYS plain. Apple/Google barcodes are a fixed
    //     "<loyalty_id>|wallet-apple|google" baked per customer — they carry no card
    //     state and can never say "|reward". Wallet users could NEVER pass the old gate.
    //   • The app only arms "|reward" after the customer taps Claim, and that arming
    //     resets whenever they switch tabs or reload.
    //   • During the 8/8 celebration (and for a beat after the 8th stamp) the app is
    //     still showing its plain collect QR.
    // So the old gate rejected genuine reward claims in the reward scanner, AND let a
    // plain scan through the stamp scanner where the backend silently auto-redeems the
    // pending reward — staff ring up a stamp, the customer's free rolls quietly vanish.
    // The card's real state is the only reliable signal, so read it: lookup is a
    // read-only GET on the scanner's own token (no PIN needed), so it is safe to run
    // before the PIN gate — staff shouldn't key a PIN only to be told "wrong scanner".
    const wantsClaim=(variant==="reward");
    if(ccIsOffline()){
      // Offline: we can't read card state — but a reward CAN still be redeemed. Hand off to
      // the offline picker (all Club Card tier rewards + special cards): staff tap what the
      // customer's redeeming, it shows the Square code and QUEUES the redemption to reconcile
      // on reconnect (the backend burns the actual pending reward and flags a mismatch if the
      // picked tier differs — never a silent over-give). A STAMP still queues exactly as
      // before, so fall through to the queue path untouched.
      if(wantsClaim){
        requestPin((staffName,_pin,meta)=>{ processingRef.current=false; setOfflinePick({loyaltyId, staffName, staffAuth:(meta&&meta.auth)||null, staffPinEnc:(meta&&meta.pinEnc)||null}); },()=>{processingRef.current=false;restartScanner()},{offline:true});
        return;
      }
    }else{
      setChecking(true);
      let pending=null;
      let block=null; // the card can't take a stamp yet (cooldown / double scan)
      // This check blocks the screen, so it must always end. Mall wifi can leave a
      // request hanging indefinitely; without a bound, staff would be stuck watching a
      // spinner with a customer waiting. Six seconds, then treat it as unreachable.
      const preAbort=new AbortController();
      const preTimer=setTimeout(()=>preAbort.abort(),6000);
      try{
        const pre=await ccApiCall("/scanner/lookup/"+loyaltyId,token,{signal:preAbort.signal});
        pending=!!(pre&&pre.has_pending_reward);
        block=(pre&&pre.stamp_block)||null;
      }catch(err){
        clearTimeout(preTimer);
        if(err&&err.httpStatus===404){
          setChecking(false);
          showToast("No Club Card found for that QR","error");
          setTimeout(()=>{processingRef.current=false;restartScanner()},2000);
          return;
        }
        // Couldn't reach the server (mall wifi drops mid-scan). A claim that got no
        // answer at all is offline: same hand-off to the offline picker as the offline
        // branch above; any other failure stops with a retry. A stamp carries on into
        // the queue path below, i.e. exactly what this scanner did before this guard
        // existed. pending stays null = "unknown", so we never hand off on a guess.
        if(wantsClaim){
          setChecking(false);
          if(err&&err.isNetwork){ requestPin((staffName,_pin,meta)=>{ processingRef.current=false; setOfflinePick({loyaltyId, staffName, staffAuth:(meta&&meta.auth)||null, staffPinEnc:(meta&&meta.pinEnc)||null}); },()=>{processingRef.current=false;restartScanner()},{offline:true}); return; }
          showToast("Couldn't check the card — try again in a moment.","error");
          setTimeout(()=>{processingRef.current=false;restartScanner()},2200);
          return;
        }
      }
      clearTimeout(preTimer);
      setChecking(false);
      if(pending!==null&&wantsClaim!==pending){
        // Reward scanner + nothing to claim, or stamp scanner + a reward sat waiting.
        // Either way: do nothing, and offer the one-tap hop to the right scanner, which
        // picks up this same scan.
        setWrongQr({kind:wantsClaim?"stamp":"reward",text:decodedText});
        processingRef.current=false;
        return;
      }
      // Already stamped: say so now, before anyone keys a PIN (JJ, 12 Oct 2026). Nothing is sent and
      // the camera restarts as after any refusal. If the check couldn't be made (offline, timed out)
      // the scan carries on as before and the server turns it down with the same line.
      if(!wantsClaim&&block){
        showToast(ccStampBlockLine(block),"error");
        setTimeout(()=>{processingRef.current=false;restartScanner()},2000);
        return;
      }
    }
    // PIN-gate every stamp award — staff re-enters PIN before the commit. {offline:true}:
    // with no connection the PIN is checked on this tablet (or a name typed) so the stamp
    // can still queue; staffAuth then says so, and the Command Centre flags it.
    requestPin(async(staffName,staffPin,meta)=>{
      const staffAuth=(meta&&meta.auth)||null;
      const staffPinEnc=(meta&&meta.pinEnc)||null;   // the PIN encrypted for the server (offline PINs only)
      // One stable key per physical scan — reused on every re-send below (offline
      // enqueue, the lost-response re-enqueue, and each sync retry) so the backend
      // applies this scan exactly once. Fixes the offline-queue double-stamp.
      const idem=ccMakeIdemKey();
      try{
        if(ccIsOffline()){
          // Redemption-intent AND reward-claim scans can't be queued offline —
          // both need live server-side state (a redemption's caps/eligibility/
          // cooldowns; a reward-claim's pending-reward row + the atomic
          // redeem→fresh-card→stamp). A reward-claim queued as a plain stamp would
          // also lose the reward_claim intent. Surface a clear error to retry online.
          // A reward claim that went offline AFTER the card check (the PIN check got no
          // answer) goes to the offline picker, exactly like one that was offline before
          // it (review, 13 Oct 2026) — never a dead end with the customer waiting.
          if(wantsClaim){processingRef.current=false;setOfflinePick({loyaltyId,staffName,staffAuth,staffPinEnc});return}
          if(redemptionCardId){
            showToast("Offline — Special Card redemption needs a live connection. Try again when reconnected.","error");
            setTimeout(()=>{processingRef.current=false;restartScanner()},2500);
            return;
          }
          // Per-card offline dedupe: two offline scans of the SAME card must not
          // become two queued rows (= two stamps on sync). Every queued row is
          // unsynced, so a matching loyalty_id means this card is already waiting.
          const pending = await ccGetQueued(); if (pending.some(q => q.loyalty_id === loyaltyId)) { showToast("This card's already queued — it'll stamp once we're back online","info"); setTimeout(()=>{processingRef.current=false;restartScanner()},2000); return; }
          await ccEnqueue({loyalty_id:loyaltyId,site_id:session.siteId,channel,staff:staffName,staff_auth:staffAuth,staff_pin_enc:staffPinEnc,idempotency_key:idem});
          setQueueCount(p=>p+1);showToast("Offline — scan queued for sync","info");
          setTimeout(()=>{processingRef.current=false;restartScanner()},2000);
          return;
        }
        // Club scanner: a normal scan stamps only and never forwards a redeem token
        // (special cards have their own before-payment scanner). On the reward
        // scanner we send reward_claim:true, which tells the backend to run the
        // explicit claim path (redeem → fresh card → stamp) instead of a normal
        // stamp. We only get here on that scanner once the guard above has confirmed
        // a reward really is pending — so this no longer depends on the QR's shape,
        // and a wallet pass claims exactly like the app's armed QR does.
        const result=await ccApiCall("/scanner/scan",token,{method:"POST",body:JSON.stringify({loyalty_id:loyaltyId,site_id:session.siteId,channel,staff_name:staffName,idempotency_key:idem,...(wantsClaim?{reward_claim:true}:{}),...(staffAuth?{staff_auth:staffAuth,staff_pin_enc:staffPinEnc}:{})})});
        // The scan has landed. The follow-up lookup only adds the visit stats, so if it
        // fails the result still shows — it must never hide a claimed reward's code or
        // send a landed claim to the offline picker below.
        const customer=await ccApiCall("/scanner/lookup/"+loyaltyId,token).catch(()=>null);
        onScanResult({...result,customer});
      }catch(err){
        // No answer to a claim (timed out / no network): same hand-off to the offline
        // picker as the offline branch before the PIN.
        if(wantsClaim&&err&&err.isNetwork){processingRef.current=false;setOfflinePick({loyaltyId,staffName,staffAuth,staffPinEnc});return}
        // No answer to a stamp: queue it under the SAME idempotency key the request
        // carried, so if it did reach the server the replay dedupes (no double stamp).
        if(!redemptionCardId&&!wantsClaim&&(!navigator.onLine||(err&&err.isNetwork))){
          // Same per-card dedupe as the offline branch: don't queue a second row
          // for a card that's already waiting (would double-stamp on sync).
          const pending = await ccGetQueued(); if (pending.some(q => q.loyalty_id === loyaltyId)) { showToast("This card's already queued — it'll stamp once we're back online","info"); setTimeout(()=>{processingRef.current=false;restartScanner()},2000); return; }
          await ccEnqueue({loyalty_id:loyaltyId,site_id:session.siteId,channel,staff:staffName,staff_auth:staffAuth,staff_pin_enc:staffPinEnc,idempotency_key:idem});
          setQueueCount(p=>p+1);showToast("Connection lost — scan queued","info");
        }else{showToast(ccStaffError(err),"error")}
        setTimeout(()=>{processingRef.current=false;restartScanner()},2000);
      }
    },()=>{
      // PIN cancelled — restart the scanner without awarding
      processingRef.current=false;
      restartScanner();
    },{offline:true});
  };

  onQrSuccessRef.current=onQrSuccess;

  // The carried scan (initialQr), run once by the camera effect above once the camera is up.
  // If the camera has already read a QR by then (the customer's still holding it up), that
  // scan stands and the carried one is dropped.
  const carriedRef=useRef(false);
  runCarryRef.current=()=>{
    if(carriedRef.current||!initialQr||!initialQr.text) return;
    carriedRef.current=true;
    if(processingRef.current) return;
    if(onQrSuccessRef.current) onQrSuccessRef.current(initialQr.text);
  };

  const restartScanner=async()=>{
    try{if(html5QrRef.current&&!html5QrRef.current.isScanning){await html5QrRef.current.start({facingMode:"environment"},{fps:10,qrbox:{width:250,height:250},aspectRatio:1.0},onQrSuccess,()=>{})}}catch(err){console.error("[QR] restart failed",err)}
  };

  // Restart the camera AFTER the offline picker closes and #cc-qr-reader is back in the DOM.
  // Doing it synchronously in the picker's onDone runs BEFORE React remounts the reader div,
  // so html5-qrcode's start() throws on the missing element and the camera stays dead until
  // staff press Back and reopen. A post-commit effect guarantees the div exists first.
  const wasPickingRef=useRef(false);
  useEffect(()=>{
    if(offlinePick){ wasPickingRef.current=true; }
    else if(wasPickingRef.current){ wasPickingRef.current=false; restartScanner(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[offlinePick]);

  const dismissWrong=()=>{setWrongQr(null);processingRef.current=false;restartScanner()};

  // Offline reward redemption: staff scanned a completed card while offline → the picker
  // (same one the special-card scanner uses) shows all rewards, queues the pick, shows the
  // Square code. Mirrors CCUniversalRedeemScan's offline handoff.
  if(offlinePick){
    return <CCOfflineRedeemPicker loyaltyId={offlinePick.loyaltyId} staffName={offlinePick.staffName} staffAuth={offlinePick.staffAuth} staffPinEnc={offlinePick.staffPinEnc} session={session} onDone={()=>{setOfflinePick(null);processingRef.current=false;/* camera restarts via the offlinePick effect, after the reader div remounts */}}/>;
  }

  const reward=variant==="reward";
  const accent=reward?"#fbbf24":"#d4fb6f";
  return (
    <div className="cc-screen cc-clubscan" style={{"--accent":accent,"--accent-bg":ccRgba(accent,0.16),"--accent-line":ccRgba(accent,0.5)}}>
      <div className="cc-flat-head">
        <button className="cc-back-btn" onClick={onBack}>‹ Back</button>
        <div className="cc-flat-title">{reward?"Redeeming: Club Card reward":"Add a stamp to a Club Card"}</div>
        <div className="cc-special-head-site">{session.siteName}</div>
      </div>
      <div className="cc-special-body">
        <div className={reward?"cc-before-tag":"cc-after-tag"}>{reward?"Scan before payment":"Scan after payment"}</div>
        <div className="cc-special-viewfinder"><div id="cc-qr-reader" style={{width:"100%",height:"100%"}}/></div>
        <div className="cc-special-coach">{reward?<>Scan their <b>reward</b> QR</>:<>Scan their <b>Club Card</b> QR</>}</div>
        <div className="cc-special-help">{scanning?(reward?"Their reward shows once scanned — apply it in Square before payment.":"Point the camera at the customer's QR to add a stamp."):"Starting camera…"}</div>
        {(!isOnline||netDown||queueCount>0)&&<div className="cc-clubscan-status">{(!isOnline||netDown)&&<span className="cc-offline"><span className="cc-offline-dot"/> Offline</span>}{queueCount>0&&<span className="cc-queue">⏳ {queueCount} queued</span>}</div>}
      </div>
      {checking&&!wrongQr&&(
        <div className="cc-checking"><div className="cc-checking-spin"/><div className="cc-checking-t">Checking their card…</div></div>
      )}
      {wrongQr&&(wrongQr.kind==="special"
        ? <CCWrongQrPanel title="That's a Special Card"
            text={reward?"Nothing's been claimed. Special Cards are redeemed on the Special Cards scanner.":"No stamp added. Special Cards are redeemed before they pay, on the Special Cards scanner — if they've already paid, it can't go on this sale."}
            go={["special"]} onGo={(k)=>onWrongQr&&onWrongQr(k,wrongQr.text)} onRetry={dismissWrong}/>
        : <CCWrongQrPanel title={reward?"No reward ready on this card":"This card's full — there's a reward to claim"}
            text={reward?"Nothing's been claimed. They haven't finished a card yet — give them a stamp instead?":"No stamp added. Claim their reward first, before they pay — a fresh card starts straight after."}
            go={[reward?"stamp":"reward"]} onGo={(k)=>onWrongQr&&onWrongQr(k,wrongQr.text)} onRetry={dismissWrong}/>)}
    </div>
  );
}

function CCCheck({color}){
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12.5 L10 17.5 L19 7" stroke={color||"#00392c"} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// Result stamp grid — real soy-fish; the newest stamp pops.
// noNew: no stamp landed (the server skipped it), so none of them pops as new.
function CCResultFishGrid({count,total,noNew}){
  return (
    <div className="cc-rgrid">
      {Array.from({length:total},(_,i)=>{
        const filled=i<count;const isNew=!noNew&&i===count-1;
        return <div key={i} className={"cc-rslot"+(filled?" filled":"")+(isNew?" new":"")}><CCSoyFish filled={filled}/></div>;
      })}
    </div>
  );
}

// Apply-to icons. Stroke-only, same language as CCCheck — they sit inside a pill
// whose colour already carries the meaning, so they stay monochrome.
function CCBasketIcon(){
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 9h16l-1.4 9.2a2 2 0 0 1-2 1.8H7.4a2 2 0 0 1-2-1.8L4 9Z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round"/>
      <path d="M8.5 9 12 3.6 15.5 9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 13v3M14 13v3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/>
    </svg>
  );
}
// A hand roll, end-on: nori wrap + the fill showing at the top.
function CCRollIcon(){
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.9"/>
      <circle cx="12" cy="12" r="4.1" stroke="currentColor" strokeWidth="1.9"/>
      <path d="M12 3.6v4.3M12 16.1v4.3M3.6 12h4.3M16.1 12h4.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

// === Special-card redemption result — JJ's Step 1 spec (15 Jul 2026) ==========
// Staff glance -> see the exact Square button -> tap it. So the discount code is the
// hero and everything else is support: apply-to cue, then the reward as a subtitle,
// then a small who-it-was confirm. Deliberately spare — nothing competing with the code.
//
// WHY THIS TAKES PRIORITY OVER card_complete / reward_redeemed — a fix, not a preference.
// The backend spreads awardStamp's result into the redemption response
// (scanner.js: `...(stampResult || {})`), so a redemption can arrive carrying stamp flags
// too. CCStampResult used to check those FIRST and return early, and neither of those
// screens renders the Square code — so staff never saw the discount at all.
//
// BUT: "redemption wins" must NEVER mean "the stamp outcome is dropped". Both can be real
// money at the till, and they are NOT the same thing:
//
//   reward_redeemed  the backend ALREADY burnt a pending reward — irreversibly
//                    (stamps.js: `UPDATE rewards SET redeemed_at = NOW()`). It returns
//                    reward_redeemed:true, card_complete:FALSE. Staff must hand the free
//                    rolls over NOW, in Square, ON TOP of the special discount. Miss it and
//                    the customer pays for sushi they had already earned and the reward row
//                    is gone — unrecoverable. So its callout renders ALONGSIDE the code.
//   card_complete    the card just filled. Nothing to apply today; the reward lands next
//                    visit. Informational, so it stays a one-liner.
//
// (These are mutually exclusive in the backend — the auto-redeem branch hard-codes
// card_complete:false — but they are rendered independently rather than relying on that.)
function CCRedemptionResult({result,onDone}){
  const r = result.redemption || {};
  const scope = r.discount_scope;                    // "basket" | "item" | null
  const missing = !r.discount_name;
  // Plain-English reward for the always-on "What they get" block. reward_label is the
  // Legends sent-reward headline; reward_description is the special-card config's reward line
  // (both forwarded by rolled-club-card #224). Staff see the Square CODE above, this tells
  // them what the customer actually walks away with. Not gated on discount_scope.
  // Phase 6: the card type's own "What they get" (scanner_what_they_get) when the server
  // sends it; never sent for a Legends card, whose sent reward still fills this box.
  const whatGet = ccOwn(r,"scanner_what_they_get") ? r.scanner_what_they_get : (r.reward_label || r.reward_description);
  // No "also apply the reward" co-fire block any more: a special-card scan can no longer
  // redeem a completed-card reward (backend bug-stop, rolled-club-card #144), so reward_redeemed
  // never arrives here. The reward is claimed on its own scan. This screen is the special
  // discount only.
  const cardComplete = !!result.card_complete;
  const tierUp = result.tier_changed ? result.loyalty_tier : null;
  const stampSkipped = !!result.stamp_skipped;
  // JJ, 11 Oct 2026: say so when the scan ALSO put a Club Card stamp on. The backend
  // spreads a stamp result into the reply only when a stamp actually landed (awardStamp's
  // full count, or the next-card count while a completed-card reward is waiting —
  // scanner.js), so a numeric stamp_count with no stamp_skipped means one went on. Cards
  // that earn no stamp (FSFL, student night) send neither, so no line shows.
  const stampAdded = !stampSkipped && !result.reward_redeemed && typeof result.stamp_count === "number";
  const stampOf = typeof result.stamps_required === "number" ? result.stamp_count+"/"+result.stamps_required : null;
  return (
    <div className="cc-screen cc-result cc-redeem-result">
      <CCCodeHold/>
      {r.card_type && <div className="cc-redeem-face"><div className="cardwrap"><CCFace typeKey={r.card_type} label={ccOwn(r,"scanner_name")?(r.scanner_name||""):(r.display_name||r.card_type)} disc={ccOwn(r,"scanner_face_pill")?(r.scanner_face_pill||""):(r.discount_name||"")}/></div></div>}
      <div className={"cc-redeem-hero" + (missing ? " cc-redeem-hero-missing" : "")}>
        <div className="cc-redeem-eyebrow">Discount code to click in Square</div>
        <CCSquareButton name={r.discount_name} value={r.discount_value} missing={missing}/>
        <CCWhatGet text={whatGet}/>
        <CCApplyTo scope={scope} items={r.reward_description}/>
      </div>
      {tierUp && <div className={"cc-tierup cc-tierup--"+tierUp}>★ Levelled up — {tierUp}</div>}
      {stampAdded && (
        <div className="cc-stamp-added">
          {result.reward_pending
            ? "✓ +1 stamp added — saved on their next card, as they've got a reward waiting"
            : "✓ +1 stamp added to their Club Card"+(stampOf?" — now "+stampOf:"")}
        </div>
      )}
      {stampSkipped && <div className="cc-skip-note">Stamp skipped — already stamped this visit</div>}
      {cardComplete && (
        <div className="cc-redeem-alsonote">Card complete too — they claim their reward on their next visit.</div>
      )}
      <button className="cc-done-btn" onClick={onDone}>Done — Next Customer</button>
    </div>
  );
}

// "What they get" — the reward in PLAIN ENGLISH, shown for EVERY reward type. Staff used to
// see only the Square till-code (e.g. "MATES RATES") and not what the customer actually
// receives. Additive: the Square code + Apply-to still render; this sits alongside them and
// is NOT gated on discount_scope. Renders nothing when there's no reward text. (JJ, 9 Aug —
// reward_label/reward_description forwarded by rolled-club-card #224.)
function CCWhatGet({text}){
  const t = (text == null ? "" : String(text)).trim();
  if(!t) return null;
  return (
    <div className="cc-whatget">
      <div className="cc-whatget-lab">What they get</div>
      <div className="cc-whatget-txt">{t}</div>
    </div>
  );
}

export function CCStampResult({result,onDone}){
  // Redemption FIRST — see CCRedemptionResult's header. A redemption can arrive
  // with card_complete/reward_redeemed set too, and those screens never render the
  // Square code, so checking them first silently hid the discount from staff.
  if(result.redemption)return <CCRedemptionResult result={result} onDone={onDone}/>;
  if(result.reward_redeemed)return <CCRewardRedeemed result={result} onDone={onDone}/>;
  if(result.card_complete)return <CCCardComplete result={result} onDone={onDone}/>;
  const {customer_name,stamps_required,customer}=result;
  const tier=(customer&&customer.loyalty_tier)||"none";
  // Discount-always-applies (#62): a special redeem may skip the co-fired stamp
  // on cooldown, so stamp_count can be absent — fall back to the looked-up card.
  const stampSkipped=!!result.stamp_skipped;
  const stamp_count=result.stamp_count!=null?result.stamp_count:((customer&&customer.stamp_count)||0);
  const total=stamps_required||(customer&&customer.stamps_required)||8;
  const remaining=Math.max(0,total-stamp_count);
  // No redemption branch here any more — a redemption never reaches this component
  // (CCStampResult routes it to CCRedemptionResult above), so this is the plain
  // stamp result only.
  return (
    <div className="cc-screen cc-result">
      <div className="cc-result-card">
        {/* A stamp the server skipped is just that: no discount went on here (round-1 audit). */}
        <div className={"cc-stamp-pill"+(stampSkipped?" muted":"")}>{stampSkipped?"No stamp added":<><CCCheck/> Stamp added</>}</div>
        {stampSkipped&&<div className="cc-skip-note">Stamp skipped — already stamped this visit</div>}
        <div className="cc-name">{customer_name}</div>
        {tier!=="none"&&<div className={"cc-tier cc-tier-"+tier}>{tier}</div>}
        <CCResultFishGrid count={stamp_count} total={total} noNew={stampSkipped}/>
        <div className="cc-count">{stamp_count}<span> / {total}</span></div>
        {remaining>0&&<div className="cc-nudge"><b>{remaining}</b> more stamps = {result.tier_reward||"2 free rolls"}</div>}
        {customer&&(
          <div className="cc-stats">
            <div className="cc-stat"><div className="cc-stat-val">{customer.total_visits}</div><div className="cc-stat-lab">Visits</div></div>
            <div className="cc-stat"><div className="cc-stat-val">{customer.completed_cards}</div><div className="cc-stat-lab">Cards</div></div>
            <div className="cc-stat"><div className="cc-stat-val">{(customer.special_cards&&customer.special_cards.length)||0}</div><div className="cc-stat-lab">Special</div></div>
          </div>
        )}
      </div>
      <button className="cc-done-btn" onClick={onDone}>Done — Next Customer</button>
    </div>
  );
}

function CCCardComplete({result,onDone}){
  const total=result.stamps_required||8;
  const customer=result.customer;
  const tier=(customer&&customer.loyalty_tier)||"none";
  return (
    <div className="cc-screen cc-result" style={{background:"linear-gradient(175deg,#002a20 0%,#00392c 60%,#1a3a20 100%)"}}>
      <div className="cc-result-card cc-result-celebrate">
        <div className="cc-stamp-pill"><CCCheck/> Card complete</div>
        <div className="cc-name">{result.customer_name}</div>
        {tier!=="none"&&<div className={"cc-tier cc-tier-"+tier}>{tier}</div>}
        <CCResultFishGrid count={total} total={total}/>
        {/* E2E round-7: the load-bearing "do nothing in Square today" is now the HERO, and
            the reward is demoted to a calm ghost chip (was a solid wasabi block that looked
            like the action screens where staff DO click a Square button). */}
        <div style={{fontFamily:"gelica,serif",fontSize:23,fontWeight:800,color:"var(--cc-wasabi)",lineHeight:1.18,margin:"8px 0 2px"}}>Next Visit = They Claim Reward</div>
        <div className="cc-reward-callout cc-reward-callout-green" style={{marginTop:8}}>
          <div className="cc-reward-callout-lab">Their reward next visit</div>
          <div className="cc-reward-callout-mid">{result.tier_reward||"2 free rolls"}</div>
        </div>
        <div className="cc-reward-sub" style={{marginTop:10}}>Don't apply anything in Square today.</div>
      </div>
      <button className="cc-done-btn" onClick={onDone}>Done — Next Customer</button>
    </div>
  );
}

function CCRewardRedeemed({result,onDone}){
  // Same hierarchy as the special-card redemption screen (CCRedemptionResult): the code staff
  // must click is the HERO at the top — and for Diamond the A/B choice comes FIRST so staff act
  // on it immediately — with the "free sushi" celebration demoted to a support line. This is a
  // till tool: staff need the button/choice, not a headline (JJ, 16 Jul).
  const tierUp=result.tier_changed?result.loyalty_tier:null;
  // Diamond rewards make staff pick A or B before there's a code to apply. Hold back
  // "Done — Next Customer" until that pick is made so nobody closes the screen before
  // applying the discount. Non-Diamond (single-code) rewards show Done immediately.
  const opts=result.reward&&result.reward.reward_options;
  const needsPick=Array.isArray(opts)&&opts.length>0;
  const [picked,setPicked]=useState(false);
  return (
    <div className="cc-screen cc-result cc-redeem-result" style={{background:"linear-gradient(175deg,#002a20 0%,#00392c 60%,#1a3a20 100%)"}}>
      <CCCodeHold/>
      <div className="cc-redeem-hero cc-rwhero">
        <CCRewardApply reward={result.reward} tierReward={result.tier_reward} hero onPicked={setPicked}/>
      </div>
      {tierUp&&<div className={"cc-tierup cc-tierup--"+tierUp}>★ Levelled up — {tierUp}</div>}
      <div className="cc-reward-sub">Card complete — free sushi earned. New card started · {result.stamp_count} / {result.stamps_required}</div>
      {(!needsPick||picked)&&<button className="cc-done-btn" onClick={onDone}>Done — Next Customer</button>}
    </div>
  );
}

// ── 8/8 completion reward: the tier's Square button + Diamond's A/B staff pick ──────
// The completed-card reward has its OWN per-tier Square code (loyalty #131 / migrations
// 049+056): Standard→Platinum each map to one button; Diamond is a STAFF CHOICE of A or B
// decided at the counter (register: "staff picks A or B, the scanner shows the matching
// code — no customer-side choice"). Used in both reward moments: the pure completion
// redemption (CCRewardRedeemed) and the special-card co-fire (CCRedemptionResult's "also"
// block). Before this, the pure path showed a generic "8th visit · 2 free rolls" with no
// button name at all, and Diamond's A/B were read-only lines in the co-fire path only.
//
// discount_name / label are nullable in the backend schema (validators.js) and the CC
// Discounts register now lets admins edit them, so a half-configured option is possible —
// never render a blank code; say "grab a manager" like the missing-hero path.
const CODE_MISSING = "No code set — grab a manager";
function ccScopeSuffix(scope){ return scope === "basket" ? " · whole basket" : scope === "item" ? " · each item" : ""; }
function ccOptLabel(o){ return o.label || o.discount_name || ("Option " + o.key); }

// Faithful replica of the actual Square discount button — a grey tile + tag icon, the code
// name left-aligned, the discount value on the right, on a white row (JJ, 16 Jul: make it look
// like the real Square button so staff make the mental link at a glance). The value renders
// when the backend sends discount_value; the row is unchanged without it.
function CCSquareButton({name, value, missing}){
  return (
    <div className={"cc-sqbtn" + (missing ? " cc-sqbtn-missing" : "")}>
      <span className="cc-sqbtn-tile" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none"><path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .53.21 1.04.59 1.42l9 9c.39.39.9.58 1.41.58s1.02-.19 1.41-.58l7-7c.39-.39.58-.9.58-1.41s-.2-1.03-.58-1.42z" fill="#ffffff"/><circle cx="6.5" cy="6.5" r="1.6" fill="#6e6e6e"/></svg>
      </span>
      <span className="cc-sqbtn-name">{missing ? "No discount set — grab a manager" : name}</span>
      {value && !missing ? <span className="cc-sqbtn-value">{value}</span> : null}
    </div>
  );
}

// "Apply discount to" — the basket-vs-item cue staff get wrong, made explicit: a pill for
// Whole basket vs Each item. The reward itself is shown once, in "What they get"; we used to
// also split it into dot points here, which read as a mess and only ever fired for item-scope
// cards (so birthday was the only card that bulleted — inconsistent). Dropped (JJ).
function CCApplyTo({scope}){
  if(scope !== "basket" && scope !== "item") return null;
  return (
    <div className="cc-applyto">
      <div className="cc-applyto-lab">Apply discount to</div>
      <div className="cc-applyto-pill">{scope === "basket" ? <><CCBasketIcon/><span>Whole basket</span></> : <><CCRollIcon/><span>Each item</span></>}</div>
    </div>
  );
}

// Interactive picker — used where the reward is the ONLY thing to apply (pure completion).
// Staff taps A or B → the chosen Square button becomes the hero, reversible via "Pick again".
function CCDiamondPicker({options, value, onPicked}){
  const [pick, setPick] = useState(null);
  // Tell the parent whether a choice is locked in — the "Done — Next Customer"
  // button stays hidden until staff commit to A or B (and hides again on "Pick again").
  const choose = (k) => { setPick(k); if(onPicked) onPicked(true); };
  const pickAgain = () => { setPick(null); if(onPicked) onPicked(false); };
  const chosen = pick != null ? options.find(o => o.key === pick) : null;
  if(chosen){
    return (
      <div className="cc-rwpick">
        <div className="cc-rwpick-chosen">
          <div className="cc-rwpick-chosen-eyebrow">Tap this in Square</div>
          <CCSquareButton name={chosen.discount_name} value={value} missing={!chosen.discount_name}/>
          <CCWhatGet text={chosen.reward_description || chosen.label || chosen.discount_name}/>
          <CCApplyTo scope={chosen.discount_scope} items={ccOptLabel(chosen)}/>
        </div>
        <button className="cc-rwpick-change" onClick={pickAgain}>Wrong one? Pick again</button>
      </div>
    );
  }
  return (
    <div className="cc-rwpick">
      <div className="cc-rwpick-q">Which reward did they choose?</div>
      <div className="cc-rwpick-opts">
        {options.map(o => (
          <button key={o.key} className="cc-rwpick-btn" onClick={() => choose(o.key)}>
            <span className="cc-rwpick-btn-tag">Choice {o.key}</span>
            <span className="cc-rwpick-btn-lab">{ccOptLabel(o)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
// Read-only variant for the co-fire screen: a special-card code is already the visible hero
// above, so the reward's codes must NOT hide behind a tap — a rushed staffer could apply the
// special discount and miss the earned free rolls. Both codes stay on screen.
function CCDiamondCodes({options, value}){
  return (
    <div className="cc-rwpick">
      <div className="cc-rwpick-q">Staff pick one — apply the matching code</div>
      {options.map(o => (
        <div key={o.key} className="cc-rwapply-single">
          <CCSquareButton name={o.discount_name} value={value} missing={!o.discount_name}/>
          <CCApplyTo scope={o.discount_scope} items={ccOptLabel(o)}/>
        </div>
      ))}
    </div>
  );
}
// hero = the reward is the top-of-screen HERO (pure completion): the single code renders in the
// big special-card code style; the Diamond picker is scaled up by the .cc-rwhero ancestor CSS.
// Non-hero = the compact secondary render inside the co-fire "also apply" callout.
function CCRewardApply({reward, tierReward, interactive = true, hero = false, onPicked}){
  const rw = reward || {};
  // Plain-English tier/completed-card reward for the "What they get" block. tier_reward is
  // the completed-card headline; reward_description is the config line (loyalty forwards both).
  const whatGet = tierReward || rw.reward_description;
  const options = Array.isArray(rw.reward_options) && rw.reward_options.length ? rw.reward_options : null;
  if(options){
    // Diamond A/B: interactive pick when it's the only thing on screen; read-only both-codes
    // in the co-fire screen so the reward is never hidden behind a tap. onPicked lets the
    // completion screen gate its "Done" button on the staff making the A/B choice.
    return interactive
      ? <CCDiamondPicker options={options} value={rw.discount_value} onPicked={onPicked}/>
      : <CCDiamondCodes options={options} value={rw.discount_value}/>;
  }
  // Single tier code. Fall back to the generic line only when no button is configured yet.
  if(rw.discount_name){
    const applyTo = <CCApplyTo scope={rw.discount_scope} items={rw.reward_description || tierReward}/>;
    if(hero){
      return (
        <>
          <div className="cc-redeem-eyebrow">Apply in Square</div>
          <CCSquareButton name={rw.discount_name} value={rw.discount_value}/>
          <CCWhatGet text={whatGet}/>
          {applyTo}
        </>
      );
    }
    return (
      <div className="cc-rwapply-single">
        <CCSquareButton name={rw.discount_name} value={rw.discount_value}/>
        <CCWhatGet text={whatGet}/>
        {applyTo}
      </div>
    );
  }
  if(hero){
    return (
      <>
        <div className="cc-redeem-eyebrow">Reward earned</div>
        <div className="cc-redeem-code cc-redeem-code-sm">{"8th visit · " + (tierReward || "2 free rolls")}</div>
      </>
    );
  }
  return <div className="cc-reward-callout-mid">{"8th visit · " + (tierReward || "2 free rolls")}</div>;
}

// Universal special-card redeem scanner — ONE scanner for every special card.
// Staff scan any special-card redeem QR (static "…|redeem|<card_id>" or rotating
// "…|rcode|<code>") and it redeems straight through, landing on the normal
// redemption result — which now shows the card's FACE on top (see
// CCRedemptionResult). Replaces the old "Which special card?" chooser +
// per-type scanner; the redeem itself runs through POST /scanner/scan,
// unchanged. No pre-pick, no confirm step — the QR/server already knows the card.
// Offline redeem picker — offline the scanner can't resolve which reward/card a QR
// is for, so (per JJ) staff pick it: shows all Club Card tier rewards + all special
// cards from the CACHED CC config, staff tap the right one, it shows the correct
// Square code and QUEUES the redemption to reconcile on sync. Optimistic: staff apply
// the discount now; the backend burns it (or flags a conflict to the CC) on reconnect.
// The offline picker's result screen: the Square code for what staff picked, shown once the
// redemption is saved to the tablet's offline queue (CCOfflineRedeemPicker only renders it
// after that save). Its own component so the card builder's till preview (tillPreview.jsx)
// can draw it without saving anything.
export function CCOfflineRedeemResult({picked,onDone}){
  const o=picked.obj;
  return (
    <div className="cc-screen cc-result cc-redeem-result">
      <CCCodeHold/>
      {picked.kind==="special" && o.key && <div className="cc-redeem-face"><div className="cardwrap"><CCFace typeKey={o.key} label={ccOwn(o,"scanner_name")?(o.scanner_name||""):(o.display_name||o.key)} disc={ccOwn(o,"scanner_face_pill")?(o.scanner_face_pill||""):(o.discount_name||"")}/></div></div>}
      <div className="cc-redeem-hero">
        <div className="cc-redeem-eyebrow">Discount code to click in Square</div>
        {picked.kind==="reward"
          ? <CCRewardApply reward={o}/>
          : <><CCSquareButton name={o.discount_name} value={o.discount_value} missing={!o.discount_name}/><CCWhatGet text={ccOwn(o,"offline_what_they_get")?o.offline_what_they_get:o.reward_description}/></>}
      </div>
      <div className="cc-offline-queued">⏳ Offline — redemption queued. It'll sync on its own when Wi-Fi's back.</div>
      <button className="cc-done-btn" onClick={()=>onDone()}>Done — Next Customer</button>
    </div>
  );
}

// staffAuth / staffPinEnc: set when the server couldn't check the PIN at the time
// (offlinePin.AUTH_* + the PIN encrypted for it) — they go with the queued redemption so the
// server can check it on sync and the Command Centre can list it.
function CCOfflineRedeemPicker({loyaltyId, staffName, staffAuth, staffPinEnc, session, onDone}){
  const tiers = ccCachedTierRewards();
  const cards = ccCachedCardTypes();
  const [picked,setPicked]=useState(null);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState(null);
  const noConfig = (!tiers||!tiers.length) && (!cards||!cards.length);
  const cap = (s)=>String(s||"").replace(/\b\w/g,c=>c.toUpperCase());

  // Only show the Square code + "queued" screen AFTER the redemption is actually saved to
  // the offline queue. Showing it optimistically is unsafe: if the tablet's storage write
  // throws, staff would apply a discount that never syncs and is never reconciled. So we
  // await the save first, and only reveal the code on success. The picked/busy guard stops
  // an accidental same-screen double-tap.
  const choose = async(kind, ref, obj)=>{
    if(picked||busy) return;
    setBusy(true); setErr(null);
    const idem = ccMakeIdemKey();
    try{
      await ccEnqueueRedeem({loyalty_id:loyaltyId, kind, ref, discount_name:obj.discount_name||null, site_id:session.siteId, staff:staffName||null, staff_auth:staffAuth||null, staff_pin_enc:staffPinEnc||null, idempotency_key:idem});
      setPicked({kind, ref, obj}); // saved → now it's safe to show the code
    }catch(e){
      // Queue write failed → nothing will sync. Don't show the code; make staff retry.
      setErr("Couldn't save this on the tablet — don't apply the discount. Tap it again to retry.");
      setBusy(false);
    }
  };

  if(picked) return <CCOfflineRedeemResult picked={picked} onDone={onDone}/>;

  // Club Card reward tiles — show the REAL full card face per tier (with the tier
  // strip) so staff see exactly what the customer sees (1:1). A tier with A/B options
  // (Diamond) becomes ONE tile PER option, so staff tap the exact reward directly and
  // the result shows that option's Square code (no second A/B step).
  const tierTiles = [];
  (tiers||[]).forEach((t)=>{
    const tierKey = (t.name==="none"?"standard":String(t.name||"standard").toLowerCase());
    const opts = Array.isArray(t.reward_options) && t.reward_options.length ? t.reward_options : null;
    if(opts){
      opts.forEach((o)=>tierTiles.push({
        tierKey, name:t.name,
        label: cap(t.name)+" · "+(o.label || o.reward_description || o.discount_name || ""),
        desc: o.reward_description || o.label || "",
        // single-option reward object → CCRewardApply shows just this code, not the picker
        obj: {...t, discount_name:o.discount_name, discount_scope:o.discount_scope, reward_description:o.reward_description||o.label, reward_options:null},
      }));
    } else {
      tierTiles.push({ tierKey, name:t.name, label: cap(t.name), desc: t.reward_description||t.discount_name||"", obj:t });
    }
  });

  return (
    <div className="cc-screen cc-offline-pick">
      <span className="cc-offline-pill"><span className="cc-offline-pdot"/>Offline — pick the reward</span>
      <div className="cc-offline-pick-sub">Can't check the card while offline — tap the card they're redeeming. It shows the Square code and queues for sync.</div>
      {noConfig && <div className="cc-error-box">No cached reward list on this tablet yet.<span>Open the scanner once on Wi-Fi so the codes download, then offline redeem works.</span></div>}
      {err && <div className="cc-error-box">{err}</div>}
      {tierTiles.length>0 && (<>
        <div className="cc-pick-grouplab">Club Card rewards</div>
        <div className="cc-pick-cardgrid">{tierTiles.map((tt,i)=>(
          <button key={"t-"+i} className="cc-pick-cardtile" onClick={()=>choose("reward",tt.name,tt.obj)}>
            <div className="cc-pick-clubface"><CCFullClubCard tier={tt.tierKey} filled={8} total={8}/></div>
            <div className="cc-pick-cardname">{tt.label}</div>
            <div className="cc-pick-carddesc">{tt.desc}</div>
          </button>
        ))}</div>
      </>)}
      {cards&&cards.length>0 && (<>
        <div className="cc-pick-grouplab cc-pick-grouplab--gap">Special Cards</div>
        <div className="cc-pick-cardgrid">{cards.map((c)=>(
          <button key={"c-"+c.key} className="cc-pick-cardtile" onClick={()=>choose("special",c.key,c)}>
            <div className="cc-pick-specialface"><div className="cardwrap"><CCFace typeKey={c.key} label={ccOwn(c,"scanner_name")?(c.scanner_name||""):(c.display_name||c.key)} disc={ccOwn(c,"scanner_face_pill")?(c.scanner_face_pill||""):(c.discount_name||"")}/></div></div>
            <div className="cc-pick-cardname">{ccOwn(c,"scanner_name")?(c.scanner_name||c.key):(c.display_name||c.key)}</div>
            <div className="cc-pick-carddesc">{ccOwn(c,"offline_picker_subline")?(c.offline_picker_subline||""):(c.reward_description||c.discount_name||"")}</div>
          </button>
        ))}</div>
      </>)}
      <button className="cc-btn cc-btn-secondary" onClick={()=>onDone()} style={{marginTop:16,width:"100%"}}>Cancel</button>
    </div>
  );
}

// A live Special Card code carried over from the wrong scanner (CCUniversalRedeemScan's
// initialQr), until that screen closes or its camera reads something else (ccStaffError).
let ccCarriedCode=null;

// onWrongQr(kind,text) / initialQr {text}: the wrong-scanner hand-over, as on CCScannerScreen.
export function CCUniversalRedeemScan({session,token,requestPin,onScanResult,onBack,showToast,onWrongQr,initialQr}){
  const [scanning,setScanning]=useState(false);
  // {text,checking,pending}: a Club Card QR was scanned here. pending = their card has a
  // reward waiting (true/false), null when it couldn't be checked.
  const [wrongQr,setWrongQr]=useState(null);
  const [offlinePick,setOfflinePick]=useState(null); // {loyaltyId,staffName} → offline redeem picker
  const qrRef=useRef(null);
  const processingRef=useRef(false);
  const onQrRef=useRef(null); // the inverted-QR side reader calls the latest handler
  const WASABI="#d4fb6f";

  const restart=async()=>{try{if(qrRef.current&&!qrRef.current.isScanning){await qrRef.current.start({facingMode:"environment"},{fps:10,qrbox:{width:250,height:250},aspectRatio:1.0},onQr,()=>{})}}catch(e){}};

  // A Club Card QR on Special Cards: nothing is redeemed. Say so and offer the hop to where
  // it belongs — Club Card Reward when a reward's waiting, else Add a Stamp, both when the
  // card can't be checked. The check is the stamp scanner's own read-only lookup (no PIN).
  const clubCardHere=async(text,loyaltyId)=>{
    if(qrRef.current&&qrRef.current.isScanning){await qrRef.current.stop().catch(()=>{})}
    setWrongQr({text,checking:true,pending:null});
    let pending=null;
    if(!ccIsOffline()){
      const ab=new AbortController();
      const t=setTimeout(()=>ab.abort(),6000);
      try{
        const pre=await ccApiCall("/scanner/lookup/"+loyaltyId,token,{signal:ab.signal});
        pending=!!(pre&&pre.has_pending_reward);
      }catch(err){
        if(err&&err.httpStatus===404){
          clearTimeout(t);setWrongQr(null);showToast("No Club Card found for that QR","error");
          setTimeout(()=>{processingRef.current=false;restart()},2000);
          return;
        }
      }
      clearTimeout(t);
    }
    setWrongQr({text,checking:false,pending});
    processingRef.current=false;
  };
  const dismissWrong=()=>{setWrongQr(null);processingRef.current=false;restart()};

  const onQr=async(decodedText)=>{
    if(processingRef.current)return;
    processingRef.current=true;
    if(decodedText!==ccCarriedCode) ccCarriedCode=null; // a fresh camera scan, not the hand-over
    const parts=decodedText.trim().split("|");
    const loyaltyId=(parts[0]||"").toUpperCase();
    const channel=((parts[1]||"").toLowerCase().trim())||"unknown";
    // Accept BOTH redeem shapes: legacy static "…|redeem|<card_id>" and rotating
    // single-use "…|rcode|<code>" (the anti-screenshot default; the code carries the
    // card identity server-side). No expected_card_type — this scanner takes any card.
    let cardId=null,code=null;
    if(parts[2]==="redeem"&&parts[3]&&/^[0-9a-f-]{36}$/i.test(parts[3])) cardId=parts[3];
    else if(parts[2]==="rcode"&&parts[3]&&/^[A-Za-z0-9_-]{16,64}$/.test(parts[3])) code=parts[3];
    if(!loyaltyId.match(/^ROLL-[A-Z0-9]{6}$/)){showToast("Invalid QR code","error");setTimeout(()=>{processingRef.current=false;restart()},1500);return}
    if(!cardId&&!code){clubCardHere(decodedText,loyaltyId);return}
    if(qrRef.current&&qrRef.current.isScanning){await qrRef.current.stop().catch(()=>{})}
    requestPin(async(staffName,_pin,meta)=>{
      const staffAuth=(meta&&meta.auth)||null;   // set when the server couldn't check the PIN at the time (no connection)
      const staffPinEnc=(meta&&meta.pinEnc)||null;
      try{
        if(ccIsOffline()){processingRef.current=false;setOfflinePick({loyaltyId,staffName,staffAuth,staffPinEnc});return}
        const intent=code?{code}:{card_id:cardId};
        const body={loyalty_id:loyaltyId,site_id:session.siteId,channel,staff_name:staffName,redemption_intent:intent,...(staffAuth?{staff_auth:staffAuth,staff_pin_enc:staffPinEnc}:{})};
        const result=await ccApiCall("/scanner/scan",token,{method:"POST",body:JSON.stringify(body)});
        // Landed: a failed follow-up lookup must never hide the Square code, or send a
        // redemption that went through to the offline picker for a second go.
        const customer=await ccApiCall("/scanner/lookup/"+loyaltyId,token).catch(()=>null);
        onScanResult({...result,customer});
      }catch(err){
        // No answer (timed out / no network): same hand-off to the offline picker as
        // the offline check above.
        if(err&&err.isNetwork){processingRef.current=false;setOfflinePick({loyaltyId,staffName,staffAuth,staffPinEnc});return}
        if(err.data && err.data.stamp_skipped && (err.data.redemption || err.data.discount_name)){
          const customer=await ccApiCall("/scanner/lookup/"+loyaltyId,token).catch(()=>null);
          onScanResult({...err.data,customer});
        }else{
          showToast(ccStaffError(err),"error");
          setTimeout(()=>{processingRef.current=false;restart()},2200);
        }
      }
    },()=>{processingRef.current=false;restart()},{offline:true});
  };

  onQrRef.current=onQr;

  // The carried scan (initialQr), run once by the camera effect below once the camera is up.
  // A live Special Card code only lasts ~15-20 s, so it may have moved on by the time staff
  // have tapped across and keyed their PIN: ccCarriedCode makes that refusal say "show it
  // again", not "QR Invalid".
  const carriedRef=useRef(false);
  const runCarryRef=useRef(null);
  runCarryRef.current=()=>{
    if(carriedRef.current||!initialQr||!initialQr.text) return;
    carriedRef.current=true;
    if(processingRef.current) return; // the camera read a QR first: that scan stands
    // Only a live rotating code can simply have moved on; a static one stays "QR Invalid".
    ccCarriedCode=(initialQr.text.split("|")[2]==="rcode")?initialQr.text:null;
    if(onQrRef.current) onQrRef.current(initialQr.text);
  };

  useEffect(()=>{
    let scanner=null;
    let gone=false;
    // Side reader for a QR flipped to cream-on-dark (see the stamp scanner above).
    const stopInverted=startInvertedQrFallback({readerId:"cc-uni-reader",isScanning:()=>!!(qrRef.current&&qrRef.current.isScanning),onDecode:(t)=>{if(onQrRef.current)onQrRef.current(t)}});
    (async()=>{
      try{
        if(typeof Html5Qrcode==="undefined"){showToast("QR library not loaded","error");return}
        scanner=new Html5Qrcode("cc-uni-reader");
        qrRef.current=scanner;
        await scanner.start({facingMode:"environment"},{fps:10,qrbox:{width:250,height:250},aspectRatio:1.0},onQr,()=>{});
        setScanning(true);
      }catch(err){showToast("Camera access denied or unavailable","error")}
    })().then(()=>{ if(!gone&&initialQr&&initialQr.text) ccWhenScanning(()=>qrRef.current,()=>gone,()=>{ if(runCarryRef.current) runCarryRef.current(); }); }); // the carried scan, once the camera is really scanning
    return ()=>{gone=true;ccCarriedCode=null;stopInverted();if(scanner&&scanner.isScanning){scanner.stop().catch(()=>{})}};
  },[]);

  // Restart the camera AFTER the offline picker closes and #cc-uni-reader is back in the
  // DOM. A synchronous restart in onDone runs before React remounts the reader div, so
  // start() throws and the camera stays dead until Back+reopen. A post-commit effect fixes it.
  const wasPickingRef=useRef(false);
  useEffect(()=>{
    if(offlinePick){ wasPickingRef.current=true; }
    else if(wasPickingRef.current){ wasPickingRef.current=false; restart(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[offlinePick]);

  if(offlinePick){
    return <CCOfflineRedeemPicker loyaltyId={offlinePick.loyaltyId} staffName={offlinePick.staffName} staffAuth={offlinePick.staffAuth} staffPinEnc={offlinePick.staffPinEnc} session={session} onDone={()=>{setOfflinePick(null);processingRef.current=false;/* camera restarts via the offlinePick effect, after the reader div remounts */}}/>;
  }

  return (
    <div className="cc-screen cc-special-scan" style={{"--accent":WASABI,"--accent-bg":ccRgba(WASABI,0.16),"--accent-line":ccRgba(WASABI,0.5)}}>
      <div className="cc-special-head">
        <button className="cc-back-btn" onClick={onBack}>‹ Back</button>
        <div className="cc-flat-title">Redeem a Special Card</div>
        <div className="cc-special-head-site">{session.siteName}</div>
      </div>
      <div className="cc-special-body">
        <div className="cc-before-tag">Scan before payment</div>
        <div className="cc-special-viewfinder"><div id="cc-uni-reader" style={{width:"100%",height:"100%"}}/></div>
        <div className="cc-special-coach">Scan <b>any</b> Special Card</div>
        {!scanning&&<div className="cc-special-help">Starting camera…</div>}
      </div>
      {wrongQr&&(wrongQr.checking
        ? <div className="cc-checking"><div className="cc-checking-spin"/><div className="cc-checking-t">Checking their card…</div></div>
        : <CCWrongQrPanel title={wrongQr.pending?"That's their Club Card — and a reward's ready":"That's a Club Card, not a Special Card"}
            text={wrongQr.pending?"Nothing's been redeemed. Claim it on Club Card Reward, before they pay.":(wrongQr.pending===false?"Nothing's been redeemed. If they've already paid, give them their stamp.":"Nothing's been redeemed. The card can't be checked right now — pick where it goes.")}
            go={wrongQr.pending?["reward"]:(wrongQr.pending===false?["stamp"]:["reward","stamp"])}
            onGo={(k)=>onWrongQr&&onWrongQr(k,wrongQr.text)} onRetry={dismissWrong}/>)}
    </div>
  );
}

// "14 Nov" (or "14 Jan 2027" when it isn't this year) for a date the server sends as UTC
// midnight, such as a Birthday Card's unlocks_at: read in UTC so a tablet set to another
// timezone can't show the day before.
function ccUtcDay(iso){
  if(!iso) return "";
  try{
    const d=new Date(iso);
    if(isNaN(d.getTime())) return "";
    const sameYear=d.getUTCFullYear()===new Date().getUTCFullYear();
    return d.toLocaleDateString("en-GB",Object.assign({day:"numeric",month:"short",timeZone:"UTC"},sameYear?{}:{year:"numeric"}));
  }catch(e){return ""}
}
// Status pill for a special card in lookup. Green = usable, grey = used,
// blue = upcoming (with date), amber = not configured, red = expired. Legend is
// binary (offer model). Defensive about the field names the lookup returns.
function ccCardPill(c){
  const key=c.card_type||c.key;
  const fmt=(iso)=>{try{return new Date(iso).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}catch(e){return ""}};
  if(c.status==="revoked") return {label:"Cancelled",cls:"grey"};
  // Legend is binary (offer model): used if it's ever been redeemed.
  if(key==="influencer") return c.last_redeemed_at?{label:"Used",cls:"grey"}:{label:"Not used",cls:"green"};
  const date=c.activates_at||c.unlocks_at||c.available_at;
  switch(c.status){
    case "available": return {label:"Available",cls:"green"};
    case "cap_daily": case "used_today": return {label:"Used today",cls:"grey"};
    case "cap_weekly": return {label:"Weekly limit",cls:"grey"};
    case "cap_monthly": return {label:"Monthly limit",cls:"grey"};
    case "cap_lifetime": return {label:"Used",cls:"grey"};
    case "pending_activation": return {label:date?("From "+fmt(date)):"Upcoming",cls:"blue"};
    case "eligibility_failed": return {label:"Upcoming",cls:"blue"};
    // A Birthday Card outside its week (or a new member's, before their first visit): say
    // when it opens, not the raw status (round-1 audit, agreed 13 Oct 2026).
    case "locked_birthday": return c.first_visit_required
      ? {label:"Unlocks after their first visit",cls:"blue"}
      : {label:ccUtcDay(c.unlocks_at)?("Unlocks on "+ccUtcDay(c.unlocks_at)):"Unlocks on their birthday",cls:"blue"};
    case "expired": return {label:"Expired",cls:"red"};
    case "no_discount": return {label:"Not set up",cls:"amber"};
    case "type_inactive": return {label:"Paused",cls:"grey"};
    default: return c.available?{label:"Available",cls:"green"}:{label:c.status||"—",cls:"grey"};
  }
}

// "Claimed 12 Aug, 2:14pm" for a used/locked special card in Lookup, so staff can see
// WHEN the reward was last taken (e.g. "your birthday reward was already claimed on the
// 12th"). Only shown for a card that ISN'T currently available — an available card has
// nothing past to report.
function ccWhenRedeemed(c){
  if(!c||!c.last_redeemed_at||c.status==="available") return "";
  try{
    const d=new Date(c.last_redeemed_at);
    const day=d.toLocaleDateString("en-GB",{day:"numeric",month:"short"});
    let h=d.getHours();const m=d.getMinutes();const ap=h<12?"am":"pm";h=h%12;if(h===0)h=12;
    return "Claimed "+day+", "+h+":"+(m<10?"0"+m:m)+ap;
  }catch(e){return ""}
}

// Customer Lookup — search by ID; shows status, special-card pills, and the
// two staff actions: Add a stamp + Comp a stamp (PIN + reason, parent-handled).
export function CCLookup({session,token,requestPin,onCompStamp,onMigrate,onBack,showToast}){
  // Staff only type the code after the fixed "ROLL-" prefix; we hold the suffix.
  const [idSuffix,setIdSuffix]=useState("");
  const [loading,setLoading]=useState(false);
  const [cust,setCust]=useState(null);
  // Redeeming an UNLOCKED special card straight from Lookup — the phone-free fallback for
  // when a customer's armed QR can't reach us at the till. `redeeming` = the card_type in
  // flight (button spinner); `redeemDone` = the result to show staff (the Square button).
  const [redeeming,setRedeeming]=useState(null);
  const [redeemDone,setRedeemDone]=useState(null);
  // Staff type the code after the fixed "ROLL-" prefix; search takes the full
  // "ROLL-"+suffix (an explicit id is still accepted for programmatic calls).
  const search=async(explicitId)=>{
    const v=(typeof explicitId==="string"?explicitId:("ROLL-"+idSuffix)).trim().toUpperCase();
    if(!v)return;
    setLoading(true);setCust(null);
    try{const c=token==="mock-preview-token"?await ccMockLookup(v):await ccApiCall("/scanner/lookup/"+v,token);setCust(c)}
    catch(err){showToast(ccStaffError(err),"error")}
    finally{setLoading(false)}
  };
  // Redeem an available special card via the staff endpoint. The backend runs the SAME
  // eligibility as a real scan, so this only ever lands for a genuinely unlocked card and
  // it returns the discount name for staff to tap in Square. A locked card would 409 with
  // a specific reason (ccStaffError).
  // PIN first, like every other till action (round-1 audit, agreed 13 Oct 2026). The
  // staffer's name and PIN go with the redeem so it's put down to them, not the tablet.
  const redeemCard=(c)=>{
    const ct=c.card_type||c.key;
    requestPin(async(staffName,staffPin)=>{
      setRedeeming(ct);
      try{
        const r=token==="mock-preview-token"
          ? {ok:true,card_type:ct,display_name:c.display_name||ccCardLabel({key:ct}),discount_name:"DEMO DISCOUNT",discount_value:c.discount_value||null,reward_label:c.reward_description||null}
          : await ccApiCall("/staff/customers/"+cust.id+"/redeem-special",token,{method:"POST",body:JSON.stringify({card_type:ct,benefit_applied:"Redeemed via Customer Lookup",staff_name:staffName,...(/^\d{4,6}$/.test(staffPin||"")?{staff_pin:staffPin}:{})})});
        setRedeemDone(r);
      }catch(err){showToast(ccStaffError(err),"error")}
      finally{setRedeeming(null)}
    },()=>{});
  };
  // Dismiss the result + re-pull the lookup so the card flips to its used/locked state
  // (and now shows a "Claimed …" line).
  const closeRedeemDone=()=>{const id=cust&&cust.loyalty_id;setRedeemDone(null);if(id)search(id);};
  const total=(cust&&cust.stamps_required)||8;
  const cards=(cust&&cust.special_cards)||[];
  return (
    <div className="cc-screen cc-lookup">
      <div className="cc-flat-head">
        <button className="cc-back-btn" onClick={onBack}>‹ Back</button>
        <div className="cc-flat-title">Customer Lookup</div>
        <div className="cc-special-head-site">{session.siteName}</div>
      </div>
      <div className="cc-lookup-body">
        <div className="cc-lookup-search">
          <label className="cc-input" style={{flex:1,display:"flex",alignItems:"center",gap:2,padding:"0 16px",cursor:"text"}}>
            <span style={{color:"var(--cc-kewpie-dim)",fontWeight:700,userSelect:"none"}}>ROLL-</span>
            <input style={{flex:1,minWidth:0,padding:"14px 0",background:"transparent",border:"none",outline:"none",color:"var(--cc-kewpie)",fontSize:16,fontFamily:"inherit",letterSpacing:"1px"}} placeholder="A1B2C3" value={idSuffix} onChange={e=>setIdSuffix(e.target.value.toUpperCase().replace(/^ROLL-?/,"").replace(/[^A-Z0-9]/g,"").slice(0,6))} onKeyDown={e=>e.key==="Enter"&&idSuffix&&search("ROLL-"+idSuffix)} inputMode="text" autoFocus/>
          </label>
          <button className="cc-btn cc-btn-primary" onClick={()=>idSuffix&&search("ROLL-"+idSuffix)} disabled={loading||!idSuffix}>{loading?"…":"Look up"}</button>
        </div>
        {cust&&(
          <div className="cc-lookup-result">
            <div className="cc-lookup-top">
              <div><div className="cc-name">{cust.first_name||cust.customer_name}</div><div className="cc-lookup-id">{cust.loyalty_id}</div>{
                // One line per migrated paper card — up to two cards can be migrated,
                // often on different days, so they're never collapsed. Falls back to the
                // legacy single `migration` field if an older backend didn't send the list.
                (cust.migrations&&cust.migrations.length?cust.migrations:(cust.migration?[cust.migration]:[])).map((m,i)=>(
                  <div key={i} className="cc-mig-line">{"📇"} Migrated paper card{m.date?" · "+new Date(m.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}):""}{m.stamp_count!=null?" · "+m.stamp_count+" stamp"+(m.stamp_count===1?"":"s"):""}{m.staff_name?" · by "+m.staff_name:""}</div>
                ))
              }</div>
              {cust.loyalty_tier&&cust.loyalty_tier!=="none"&&<div className={"cc-tier cc-tier-"+cust.loyalty_tier}>{cust.loyalty_tier}</div>}
            </div>
            <div className="cc-lookup-prog">
              <div className="cc-lookup-fish">{Array.from({length:total},(_,i)=><span key={i}><CCSoyFish filled={i<cust.stamp_count}/></span>)}</div>
              <div className="cc-count" style={{fontSize:20}}>{cust.stamp_count}<span> / {total}</span></div>
            </div>
            <div className="cc-stats">
              <div className="cc-stat"><div className="cc-stat-val">{cust.total_visits}</div><div className="cc-stat-lab">Visits</div></div>
              <div className="cc-stat"><div className="cc-stat-val">{cust.completed_cards||cust.total_cards||0}</div><div className="cc-stat-lab">Cards</div></div>
              <div className="cc-stat"><div className="cc-stat-val">{cards.length}</div><div className="cc-stat-lab">Special</div></div>
            </div>
            {cards.length>0&&(
              <div className="cc-lookup-cards">
                <div className="cc-lookup-cards-lab">Special Cards</div>
                {cards.map((c,i)=>{const p=ccCardPill(c);const k=c.card_type||c.key;const when=ccWhenRedeemed(c);return (
                  <div key={i} className="cc-lookup-cardrow">
                    <span className="cc-lookup-cardicon" style={{background:ccAccent(k)}}>{ccCardIcon(k)}</span>
                    <span className="cc-lookup-cardname" style={{flex:1,minWidth:0}}>
                      {ccOwn(c,"lookup_list_name")?(c.lookup_list_name||ccCardLabel({key:k})):ccCardLabel({key:k,display_name:c.display_name})}
                      {when?<span style={{display:"block",fontSize:11,fontWeight:500,color:"var(--cc-kewpie-dim,#9a9a8e)",marginTop:2}}>{when}</span>:null}
                    </span>
                    {c.status==="available" && c.lookup_redeemable
                      ? <button onClick={()=>redeemCard(c)} disabled={redeeming===k} style={{border:"none",borderRadius:999,padding:"7px 18px",fontFamily:"inherit",fontSize:13,fontWeight:800,letterSpacing:"0.3px",cursor:redeeming===k?"default":"pointer",background:"#d4fb6f",color:"#00392c",opacity:redeeming===k?0.6:1}}>{redeeming===k?"…":"Redeem"}</button>
                      : <span className={"cc-pill cc-pill-"+p.cls}>{p.label}</span>}
                  </div>
                );})}
              </div>
            )}
            {/* Add-a-stamp lives on the scanner (real QR scan). Lookup is for the
                two staff-side exceptions: migrate paper stamps, and goodwill comps. */}
            <div className="cc-lookup-actions" style={{flexDirection:"column"}}>
              <button className="cc-act cc-act-migrate" onClick={()=>{
                // A member may migrate up to two paper cards. Gate on how many are left
                // (migrations_remaining); fall back to the old boolean for a stale backend.
                const rem=cust.migrations_remaining!=null?cust.migrations_remaining:(cust.migrated?0:2);
                return rem<=0?showToast("Both paper cards already migrated","info"):onMigrate(cust);
              }}>
                <span className="cc-act-ic"><svg viewBox="0 0 24 24" fill="none" stroke="#9ec3f0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h12M12 4l4 4-4 4"/><path d="M20 16H8M12 20l-4-4 4-4"/></svg></span>
                <span className="cc-act-lab">Migrate paper stamps</span>
                <span className="cc-act-cap">{cust.migrations_remaining!=null&&cust.migrations_remaining<2?(cust.migrations_remaining===0?"Both cards already migrated":"1 more card can be migrated"):"Old paper card → this account"}</span>
              </button>
              <button className="cc-act cc-act-comp" onClick={()=>onCompStamp(cust)}>
                <span className="cc-act-ic"><svg viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v8H4v-8M2 8h20v4H2zM12 8v12M12 8C9.8 8 8 6.5 8 5s1.5-2 2.5-1.3S12 6 12 8zM12 8c2.2 0 4-1.5 4-3s-1.5-2-2.5-1.3S12 6 12 8z"/></svg></span>
                <span className="cc-act-lab">Comp a stamp</span>
                <span className="cc-act-cap">Goodwill — needs a reason</span>
              </button>
            </div>
          </div>
        )}
        {/* Only Done closes this: the card is already redeemed, so a stray tap outside
            used to lose the Square code for good (round-1 audit, 13 Oct 2026). */}
        {redeemDone&&(
          <div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,20,15,0.74)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
            <CCCodeHold/>
            <div style={{background:"#00392c",border:"1px solid rgba(212,251,111,0.32)",borderRadius:18,padding:"22px 20px",maxWidth:360,width:"100%",textAlign:"center",boxShadow:"0 18px 50px rgba(0,0,0,0.5)"}}>
              <div style={{fontSize:12,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#d4fb6f",marginBottom:4}}>Redeemed · click in Square</div>
              <div style={{fontSize:18,fontWeight:800,color:"var(--cc-kewpie,#fefaf0)",marginBottom:14}}>{ccOwn(redeemDone,"scanner_name")?(redeemDone.scanner_name||ccCardLabel({key:redeemDone.card_type})):(redeemDone.display_name||ccCardLabel({key:redeemDone.card_type}))}</div>
              <CCSquareButton name={redeemDone.discount_name} value={redeemDone.discount_value} missing={!redeemDone.discount_name}/>
              {ccLookupRewardLine(redeemDone)?<div style={{marginTop:12,fontSize:14,color:"var(--cc-kewpie-dim,#c7c9b8)"}}>{ccLookupRewardLine(redeemDone)}</div>:null}
              {/* JJ, 11 Oct 2026: the redeem also stamped their Club Card (staff.js sends
                  stamp_awarded:true only when one actually went on). */}
              {redeemDone.stamp_awarded===true&&<div className="cc-stamp-added" style={{marginTop:12,maxWidth:"none"}}>✓ +1 stamp added to their Club Card</div>}
              <button className="cc-btn cc-btn-primary" style={{marginTop:18,width:"100%"}} onClick={closeRedeemDone}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// Downscale a captured photo to a small JPEG data URL before upload — proof
// shots don't need full res, and this keeps us well under the API body limit.
// Falls back to the raw file read if canvas/decoding isn't available. Returns a
// full "data:image/jpeg;base64,…" URL (what the keychain/mint endpoint wants).
function ccDownscaleImage(file,maxDim=900,quality=0.72){
  return new Promise((resolve,reject)=>{
    const reader=new FileReader();
    reader.onerror=()=>reject(new Error("read failed"));
    reader.onload=()=>{
      const img=new Image();
      img.onerror=()=>reject(new Error("decode failed"));
      img.onload=()=>{
        let w=img.width,h=img.height;
        if(w>maxDim||h>maxDim){const s=maxDim/Math.max(w,h);w=Math.round(w*s);h=Math.round(h*s);}
        const c=document.createElement("canvas");c.width=w;c.height=h;
        c.getContext("2d").drawImage(img,0,0,w,h);
        resolve(c.toDataURL("image/jpeg",quality));
      };
      img.src=reader.result;
    };
    reader.readAsDataURL(file);
  });
}
// Read a file → downscaled data URL → setter (raw-read fallback if it fails).
function ccCapturePhoto(file,setter){
  if(!file)return;
  ccDownscaleImage(file).then(setter).catch(()=>{const r=new FileReader();r.onload=()=>setter(r.result);r.readAsDataURL(file)});
}
// The paper-card migration proof photo: always the downscaled JPEG, no raw-file
// fallback — a multi-MB original would only bounce off the server with a cryptic
// error, so staff are asked to take it again instead.
function ccCaptureMigrationPhoto(file,setter,showToast){
  if(!file)return;
  ccDownscaleImage(file).then(setter).catch(()=>showToast("That photo didn't load — take it again","error"));
}

export function CCMigration({session,token,requestPin,onDone,showToast}){
  const [photo,setPhoto]=useState(null);
  const [count,setCount]=useState(0);
  const [minting,setMinting]=useState(false);
  const [claimToken,setClaimToken]=useState(null);
  const [linkLife,setLinkLife]=useState(null); // from the server reply (ccLinkLife)
  const fileRef=useRef(null);
  const onPhoto=(e)=>ccCaptureMigrationPhoto(e.target.files&&e.target.files[0],setPhoto,showToast);

  // Rebuilt migration (JJ 23 Jul): NO customer lookup. Staff tap the paper stamp
  // count + PIN → mint a single-use claim link carrying those stamps → show its QR.
  // The customer scans it and signs up / logs in later to KEEP the stamps; the
  // minting staffer is credited. Up to two paper cards per account, enforced at claim.
  // Proof photo (JJ, 29 Sep 2026): staff photograph the paper card FIRST — no photo,
  // no QR. It rides the link and shows beside the migration in the Command Centre.
  const handleMint=()=>{
    if(count===0||!photo)return;
    requestPin(async(staffName,staffPin)=>{
      setMinting(true);
      try{
        const r=token==="mock-preview-token"
          ? {claim_token:"mock_mig_"+Math.random().toString(36).slice(2,12),expires_at:new Date(Date.now()+7*24*3600000).toISOString()}
          : await ccApiCall("/scanner/migrate/mint",token,{method:"POST",timeoutMs:CC_SLOW_TIMEOUT_MS,body:JSON.stringify({stamp_count:count,site_id:session.siteId,staff_name:staffName,staff_pin:staffPin,photo:photo})});
        setClaimToken(r.claim_token);
        setLinkLife(ccLinkLife(r.expires_at));
      }catch(err){showToast(ccStaffError(err),"error")}
      finally{setMinting(false)}
    });
  };
  const finish=()=>{setClaimToken(null);setPhoto(null);onDone();};
  const qrUrl=claimToken?(CC_CLAIM_HOST+"/claim?token="+encodeURIComponent(claimToken)+"&source=paper-migration"):"";

  return (
    <div className="cc-screen cc-migrate">
      <div className="cc-flat-head">
        <button className="cc-back-btn" onClick={onDone}>‹ Back</button>
        <div className="cc-flat-title">Migrate paper card</div>
        <div className="cc-special-head-site">{session.siteName}</div>
      </div>
      <div className="cc-migrate-body">
        <div className="cc-mstep">
          <div className="cc-mstep-lab"><span className="cc-mstep-n">1</span> Photo of the paper card {!photo&&<span className="cc-req">required</span>}</div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} style={{display:"none"}}/>
          {photo
            ? <div className="cc-photo-preview"><img src={photo} alt="Paper card"/><button className="cc-photo-retake" disabled={minting} onClick={()=>fileRef.current&&fileRef.current.click()}>Retake</button></div>
            : <button className="cc-photo-btn" disabled={minting} onClick={()=>fileRef.current&&fileRef.current.click()}>📷 Take photo</button>}
        </div>
        <div className={"cc-mstep"+(!photo?" cc-mstep-off":"")}>
          <div className="cc-mstep-lab"><span className="cc-mstep-n">2</span> Tap the stamps on their paper card</div>
          <div className="cc-tapfish" style={{gridTemplateColumns:"repeat(4,1fr)",margin:"14px 0 8px"}}>
            {Array.from({length:8},(_,i)=>i).map(i=>{
              if(i===7){
                // 8th slot locked — a migration tops out at 7 (the 8th completes the card).
                return <button key="locked" className="cc-tapfish-slot" tabIndex={-1} style={{cursor:"default",opacity:0.35,borderStyle:"dashed"}}>{"🔒"}</button>;
              }
              const n=i+1;
              const filled=n<=count;
              return <button key={n+"-"+filled} className={"cc-tapfish-slot"+(filled?" filled":"")} onClick={()=>setCount(n===count?n-1:n)}><CCSoyFish filled={filled}/></button>;
            })}
          </div>
          <div className="cc-tapfish-count">{count} of 7</div>
        </div>
        <button className="cc-migrate-btn" onClick={handleMint} disabled={!photo||count===0||minting}>{minting?"…":"Show migration QR"}</button>
        <div className="cc-migrate-note">{photo?"Up to two paper cards per account.":"Take the photo first — no photo, no migration."}</div>
      </div>

      {/* Only "Done — next customer" closes the QR (JJ, 11 Oct 2026). A stray tap
          outside it used to clear the code mid-scan. */}
      {claimToken&&(
        <div className="cc-modal" style={{zIndex:360}}>
          <div className="cc-modal-box cc-fq-box">
            <CCQrHold/><div className="cc-modal-title">Migration QR</div>
            <CCFriendlyQr
              headline={<>Don't lose your stamps.<br/>Join to keep them now.</>}
              sub={<>Scan with your phone camera. Your <b>{count} paper stamp{count!==1?"s":""}</b> move{count!==1?"":"s"} onto a Rolled Rewards Club Card.</>}
              value={qrUrl} alt="Paper card migration QR"
              steps={["Scan","Join or log in","Stamps added"]}
              fine={"Up to two paper cards per account"+(linkLife?" · link valid "+linkLife:"")}/>
            <button className="cc-btn cc-btn-secondary" onClick={finish} style={{width:"100%",marginTop:16}}>Done — next customer</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Mates keychain — the customer shows their physical Mates keychain; staff snap
// a quick photo of it and mint a single-use claim link (same claim-link rails as
// the Centre card / paper migration), shown as a QR. The customer scans it to
// claim their Mates Rates card on their own account — no counter lookup needed.
// The minting staffer is credited; the backend blocks a second keychain claim so
// a keychain can't stack cards. Photo → mint → claim-QR is the only flow (E2E r7
// removed the old lookup-and-attach fallback).
function CCKeychain({session,token,requestPin,onDone,showToast}){
  // Photo → claim-QR flow (the only flow).
  const [photo,setPhoto]=useState(null);
  const [minting,setMinting]=useState(false);
  const [claim,setClaim]=useState(null); // {claim_url, display_name}
  const fileRef=useRef(null);
  const onPhoto=(e)=>ccCapturePhoto(e.target.files&&e.target.files[0],setPhoto);

  const mint=()=>{
    if(!photo)return;
    // PIN gate captures BOTH the name and the acting PIN so the backend credits
    // the issuing staffer by stable id, not just a free-text name.
    requestPin(async(staffName,staffPin)=>{
      setMinting(true);
      try{
        const r=token==="mock-preview-token"
          ? {claim_token:"mock_keychain_"+Math.random().toString(36).slice(2,12),claim_url:CC_CLAIM_HOST+"/claim?token=mock_keychain&source=mates-keychain",card_type:"mates_rates",display_name:"Mates Rates",has_photo:true,expires_at:Date.now()+7*24*3600*1000}
          : await ccApiCall("/scanner/keychain/mint",token,{method:"POST",timeoutMs:CC_SLOW_TIMEOUT_MS,body:JSON.stringify({site_id:session.siteId,photo:photo,staff_pin:staffPin,staff_name:staffName})});
        // Prefer the backend's claim_url; fall back to building it from the token
        // exactly like the other claim-link flows do.
        const url=ccClaimUrl(r,"mates-keychain");
        setClaim({claim_url:url,display_name:r.display_name||"Mates Rates",life:ccLinkLife(r.expires_at)});
      }catch(err){showToast(ccStaffError(err),"error")}
      finally{setMinting(false)}
    });
  };
  const finish=()=>{setClaim(null);setPhoto(null);onDone();};

  // Primary view — photo of the keychain → claim-link QR.
  return (
    <div className="cc-screen cc-migrate">
      <div className="cc-flat-head">
        <button className="cc-back-btn" onClick={onDone}>‹ Back</button>
        <div className="cc-flat-title">Mates keychain</div>
        <div className="cc-special-head-site">{session.siteName}</div>
      </div>
      <div className="cc-migrate-body">
        <div className="cc-migrate-intro">Customer showed their Mates keychain? Snap a photo of it, then hand them the QR to claim their Mates Rates card.</div>
        <div className="cc-mstep">
          <div className="cc-mstep-lab"><span className="cc-mstep-n">1</span> Photo of the keychain <span className="cc-req">required</span></div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} style={{display:"none"}}/>
          {photo
            ? <div className="cc-photo-preview"><img src={photo} alt="Mates keychain"/><button className="cc-photo-retake" onClick={()=>fileRef.current&&fileRef.current.click()}>Retake</button></div>
            : <button className="cc-photo-btn" onClick={()=>fileRef.current&&fileRef.current.click()}>📷 Take photo</button>}
        </div>
        <div className={"cc-mstep"+(!photo?" cc-mstep-off":"")}>
          <div className="cc-mstep-lab"><span className="cc-mstep-n">2</span> What they'll get</div>
          <div className="cc-get-chip">
            <div className="cc-get-chip-th"/>
            <div><div className="cc-get-chip-t">Mates Rates</div><div className="cc-get-chip-s">10% off, every visit · no expiry</div></div>
          </div>
        </div>
        <button className="cc-migrate-btn" onClick={mint} disabled={!photo||minting}>{minting?"…":"Create claim QR"}</button>
        <div className="cc-migrate-note">One per account · the customer scans to claim.</div>
      </div>

      {/* Only "Done — next customer" closes the QR (JJ, 11 Oct 2026). */}
      {claim&&(
        <div className="cc-modal" style={{zIndex:360}}>
          <div className="cc-modal-box cc-fq-box">
            <CCQrHold/><div className="cc-modal-title">Mates keychain QR</div>
            <CCFriendlyQr
              headline={"Scan for "+ccOfferFor(["mates_rates"],"10% off")+", every visit"}
              sub={<>Point your phone camera here. Join or log in and your <b>{claim.display_name}</b> card lands in the app — show it before you pay.</>}
              value={claim.claim_url} alt="Mates keychain claim QR"
              steps={["Scan","Join or log in","Card in your app"]}
              fine={"One per account · single-use link"+(claim.life?" · valid "+claim.life:"")}/>
            <button className="cc-btn cc-btn-secondary" onClick={finish} style={{width:"100%",marginTop:16}}>Done — next customer</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Free Sushi for Life — approval-gated migration of the physical gold card to the
// digital fs4l card. Staff take a pic of the card; it goes to the Command Centre queue,
// and staff KEEP the card for a manager to collect.
// The order (JJ, 12 Oct 2026: "the first step is actually a QR code to sign them up, once
// they are signed up they can move to step 2 which is giving their ROLL ID"): 1) the
// sign-up QR, which members skip; 2) their Roll ID; 3) a pic of the gold card; 4) Send for
// approval. The sign-up QR is the till's own Customer Sign Up QR, so the staffer is
// credited for the sign-up as usual.
function CCFs4lMigration({session,token,requestPin,onDone,showToast}){
  const [joined,setJoined]=useState(null); // null = step 1 open | "signed_up" | "member"
  const [showSignup,setShowSignup]=useState(false);
  // Staff only type the code after the fixed "ROLL-" prefix; we hold the suffix.
  const [idSuffix,setIdSuffix]=useState("");
  const [customer,setCustomer]=useState(null);
  const [note,setNote]=useState("");
  const [photo,setPhoto]=useState(null); // required proof shot (base64 data URL)
  const [loading,setLoading]=useState(false);
  const [sending,setSending]=useState(false);
  const [done,setDone]=useState(false);
  const fileRef=useRef(null);
  const onPhoto=(e)=>ccCapturePhoto(e.target.files&&e.target.files[0],setPhoto);
  const lookup=async()=>{
    const id=("ROLL-"+idSuffix).trim().toUpperCase();
    if(!idSuffix)return;
    setLoading(true);
    try{const r=token==="mock-preview-token"?await ccMockLookup(id):await ccApiCall("/scanner/lookup/"+id,token);setCustomer(r)}
    catch(err){showToast(ccStaffError(err),"error");setCustomer(null)}
    finally{setLoading(false)}
  };
  const send=()=>{
    if(!customer||!photo)return;
    requestPin(async(staffName)=>{
      setSending(true);
      try{
        if(token!=="mock-preview-token"){
          await ccApiCall("/scanner/fs4l-migration",token,{method:"POST",timeoutMs:CC_SLOW_TIMEOUT_MS,body:JSON.stringify({loyalty_id:customer.loyalty_id,note:note.trim()||undefined,site_id:session.siteId,photo:photo,staff_name:staffName})});
        }
        setDone(true);
      }catch(err){showToast(ccStaffError(err),"error")}
      finally{setSending(false)}
    });
  };
  if(done){
    return (
      <div className="cc-screen cc-result">
        <div className="cc-fs4l-sent">
          <div className="cc-fs4l-sent-ic"><CCCheck color="#4ade80"/></div>
          <div className="cc-fs4l-sent-h">Sent for approval</div>
          <div className="cc-callout">
            <div className="cc-callout-lead">Keep the physical card on site</div>
            <div className="cc-callout-txt">A manager needs to collect it from you — once collected, the migration is approved. Don't hand it back or bin it.</div>
          </div>
          <div className="cc-fs4l-sent-sub">{customer.first_name}'s card is in the manager's queue — it'll go live on their account once approved.</div>
        </div>
        <button className="cc-done-btn" onClick={onDone}>Done — Next Customer</button>
      </div>
    );
  }
  const step1Done=joined!==null;
  return (
    <div className="cc-screen cc-migrate">
      <div className="cc-flat-head">
        <button className="cc-back-btn" onClick={onDone}>‹ Back</button>
        <div className="cc-flat-title">Free Sushi for Life Card</div>
        <div className="cc-special-head-site">{session.siteName}</div>
      </div>
      <div className="cc-migrate-body">
        <div className="cc-callout">
          <div className="cc-callout-txt">Honour the gold card once today: <b>Physical Free Sushi For Life (2 ROLLS ONLY)</b> on 2 rolls. Keep the card for a manager, then move it across below.</div>
        </div>
        <div className="cc-mstep">
          <div className="cc-mstep-lab"><span className="cc-mstep-n">1</span> Sign them up</div>
          {!step1Done
            ? <>
                <div className="cc-migrate-intro">Not on Rolled Rewards yet? They scan the sign-up QR and join first — the card moves onto their account.</div>
                <button className="cc-photo-btn" onClick={()=>setShowSignup(true)}>Show the sign-up QR</button>
                <button className="cc-photo-retake" style={{alignSelf:"center"}} onClick={()=>setJoined("member")}>Already a member? Skip this step</button>
              </>
            : <div className="cc-found">✓ {joined==="member"?"Already a member":"Signed up"}{" "}<button className="cc-photo-retake" style={{marginLeft:8,padding:"10px 14px"}} onClick={()=>setShowSignup(true)}>{joined==="member"?"Show the sign-up QR":"Show the QR again"}</button></div>}
        </div>
        <div className={"cc-mstep"+(!step1Done?" cc-mstep-off":"")}>
          <div className="cc-mstep-lab"><span className="cc-mstep-n">2</span> Their Roll ID</div>
          <div className="cc-lookup-search">
            <label className="cc-input" style={{flex:1,display:"flex",alignItems:"center",gap:2,padding:"0 16px",cursor:"text"}}>
              <span style={{color:"var(--cc-kewpie-dim)",fontWeight:700,userSelect:"none"}}>ROLL-</span>
              <input style={{flex:1,minWidth:0,padding:"14px 0",background:"transparent",border:"none",outline:"none",color:"var(--cc-kewpie)",fontSize:16,fontFamily:"inherit",letterSpacing:"1px"}} placeholder="A1B2C3" value={idSuffix} onChange={e=>setIdSuffix(e.target.value.toUpperCase().replace(/^ROLL-?/,"").replace(/[^A-Z0-9]/g,"").slice(0,6))} onKeyDown={e=>e.key==="Enter"&&idSuffix&&lookup()} inputMode="text" disabled={!step1Done}/>
            </label>
            <button className="cc-btn cc-btn-primary" onClick={()=>idSuffix&&lookup()} disabled={!step1Done||loading||!idSuffix}>{loading?"…":"Look up"}</button>
          </div>
          {customer
            ? <div className="cc-found">✓ {customer.first_name} · {customer.loyalty_id}</div>
            : <div className="cc-migrate-note" style={{textAlign:"left"}}>It's in their app, under their QR code — or Profile → Details.</div>}
        </div>
        <div className={"cc-mstep"+(!customer?" cc-mstep-off":"")}>
          <div className="cc-mstep-lab"><span className="cc-mstep-n">3</span> Take a pic of the gold card {!photo&&<span className="cc-req">required</span>}</div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} style={{display:"none"}}/>
          {photo
            ? <div className="cc-photo-preview"><img src={photo} alt="Free Sushi for Life card"/><button className="cc-photo-retake" onClick={()=>fileRef.current&&fileRef.current.click()}>Retake</button></div>
            : <button className="cc-photo-btn" onClick={()=>fileRef.current&&fileRef.current.click()}>📷 Take a pic</button>}
        </div>
        <div className={"cc-mstep"+(!customer||!photo?" cc-mstep-off":"")}>
          <div className="cc-mstep-lab"><span className="cc-mstep-n">4</span> Send for approval</div>
          <input className="cc-input" placeholder="Card no. / note for the manager (optional)" value={note} onChange={e=>setNote(e.target.value)} maxLength={500}/>
        </div>
        <button className="cc-fs4l-btn" onClick={send} disabled={!customer||!photo||sending}>{sending?"Sending…":"Send for approval"}</button>
        <div className="cc-migrate-note">The gold card stays on site — a manager collects it and approves this</div>
      </div>
      {/* Step 1's QR: the till's own Customer Sign Up QR (PIN, then the QR; only Done closes
          it). Done ticks step 1 — the Roll ID look-up in step 2 is what proves they joined. */}
      {showSignup&&<CCSignupQR session={session} token={token} requestPin={requestPin} showToast={showToast} onClose={()=>setShowSignup(false)} onFinished={()=>setJoined(j=>j||"signed_up")} doneLabel="They've joined — next step"/>}
    </div>
  );
}

// Comp a stamp — staff give ONE free stamp from lookup (missed stamp, goodwill,
// promo). PIN + a reason, logged + admin-visible; the backend bypasses the
// anti-farming cooldown (manual goodwill, not a purchase). A comp is always a
// SINGLE stamp: the modal shows the customer's CURRENT card as a read-only
// preview with the one comped stamp highlighted (multi-stamp top-ups are the
// separate paper-migration flow, not this). If that stamp completes the card the
// reward is pending on the customer's next visit (never auto-redeemed).
// The paper-card reason ("Existing Physical Stamps", config: migration_reason) is a
// MIGRATION, not a comp — and a migration needs a photo of the paper card (JJ, 29 Sep
// 2026) — so picking it hands staff over to the migration modal (onMigrate) instead.
const CC_COMP_MIGRATION_REASON="Existing Physical Stamps";
function CCCompModal({customer,token,session,requestPin,onDone,onClose,onMigrate,showToast}){
  const total=(customer&&customer.stamps_required)||8;
  const current=(customer&&customer.stamp_count)||0;
  // A comp fills one slot on the current card. If it's already full (pending
  // reward) or there's none, the comp opens a fresh card, so it lands as stamp 1.
  const landsFresh=current>=total;
  const base=landsFresh?0:current;   // slots at or below base = the customer's existing stamps
  const [chip,setChip]=useState(null);
  const [custom,setCustom]=useState("");
  const [busy,setBusy]=useState(false);
  const CHIP_OTHER="Something else…";
  const DEFAULT=["Missed stamp","Service recovery","Goodwill","Promo"];
  // Comp reasons come from config (admin-editable). Fetch once when the modal
  // opens; fall back to DEFAULT on error or while loading so there's no empty flash.
  const [reasons,setReasons]=useState(null);
  const [migReason,setMigReason]=useState(CC_COMP_MIGRATION_REASON);
  useEffect(()=>{
    let x=false;
    (token==="mock-preview-token"?Promise.resolve({reasons:[...DEFAULT,CC_COMP_MIGRATION_REASON],migration_reason:CC_COMP_MIGRATION_REASON}):ccApiCall("/scanner/comp-reasons",token))
      .then(r=>{if(!x){setReasons((r&&r.reasons&&r.reasons.length)?r.reasons:DEFAULT);if(r&&r.migration_reason)setMigReason(r.migration_reason)}})
      .catch(()=>{if(!x)setReasons(DEFAULT)});
    return ()=>{x=true};
  },[token]);
  const chips=[...(reasons||DEFAULT),CHIP_OTHER];
  const reason=chip===CHIP_OTHER?custom:(chip||"");
  // Chip or typed — the backend matches the reason text either way.
  const isMigration=!!reason.trim()&&reason.trim().toLowerCase()===String(migReason).trim().toLowerCase();
  const after=base+1;                // a comp is always a single stamp
  const completes=after>=total;
  const canSubmit=!!reason.trim()&&!busy;
  const submit=()=>{
    if(!canSubmit)return;
    if(isMigration){onMigrate(customer);return;}
    requestPin(async(staffName)=>{
      setBusy(true);
      try{
        const result=await ccApiCall("/scanner/comp-stamp",token,{method:"POST",body:JSON.stringify({loyalty_id:customer.loyalty_id,reason:reason.trim(),count:1,site_id:session.siteId,staff_name:staffName})});
        const cust=await ccApiCall("/scanner/lookup/"+customer.loyalty_id,token);
        // Map the comp response onto the shared stamp-result screen: a full-card
        // comp shows the "Card complete → reward next visit" celebration.
        onDone({...result,customer_name:(cust&&cust.first_name)||customer.first_name,card_complete:!!result.card_completed,customer:cust});
      }catch(err){showToast(ccStaffError(err),"error");setBusy(false)}
    },()=>{});
  };
  const chipStyle=(sel)=>({padding:"8px 12px",borderRadius:999,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:sel?700:500,background:sel?"var(--cc-wasabi)":"rgba(254,250,240,0.08)",color:sel?"#00392c":"var(--cc-kewpie)"});
  return (
    <div className="cc-modal" onClick={e=>{if(e.target===e.currentTarget&&!busy)onClose()}}>
      <div className="cc-modal-box" style={{maxWidth:420}}>
        <div className="cc-modal-title">Comp stamp</div>
        <div className="cc-comp-sub">Give {customer.first_name||"this customer"} a free stamp — a missed stamp, goodwill or a promo. Logged for the manager.</div>
        {landsFresh&&<div style={{fontSize:13,color:"var(--cc-kewpie-dim)",marginTop:12}}>Their card's full — comping starts a fresh card.</div>}
        <div className="cc-tapfish" style={{gridTemplateColumns:"repeat("+total+",1fr)",margin:"14px 0 8px"}}>
          {Array.from({length:total},(_,i)=>i+1).map(n=>{
            const isExisting=n<=base;        // the customer's current stamps — context only
            const isComp=!isMigration&&n===base+1; // the single stamp this comp adds — highlighted (none for a migration hand-off)
            const filled=isExisting||isComp;
            // Read-only preview: nothing is tappable, a comp is always exactly one stamp.
            return (
              <button key={n} className={"cc-tapfish-slot"+(filled?" filled":"")} tabIndex={-1} style={{cursor:"default",opacity:isExisting?0.55:1}}>
                <CCSoyFish filled={filled}/>
              </button>
            );
          })}
        </div>
        <div className="cc-tapfish-count" style={{marginBottom:12}}>
          {isMigration
            ? "Stamps from a paper card are a migration, not a comp — it needs a photo of the card."
            : `Comp 1 stamp → ${after} / ${total}`+(completes?" · card completes, reward on their next visit":"")}
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:12}}>
          {chips.map(c=>(
            <button key={c} style={chipStyle(chip===c)} disabled={busy} onClick={()=>{setChip(c);if(c!==CHIP_OTHER)setCustom("")}}>{c}</button>
          ))}
        </div>
        {chip===CHIP_OTHER&&<input className="cc-input" placeholder="What's the reason?" value={custom} onChange={e=>setCustom(e.target.value)} autoFocus disabled={busy} style={{marginBottom:12}}/>}
        <button className="cc-btn cc-btn-primary" onClick={submit} disabled={!canSubmit} style={{width:"100%",marginBottom:8,padding:14,fontWeight:700}}>{busy?"…":(isMigration?"Migrate paper stamps →":"Comp 1 stamp")}</button>
        <button className="cc-btn cc-btn-secondary" onClick={onClose} disabled={busy} style={{width:"100%"}}>Cancel</button>
      </div>
    </div>
  );
}

// Migrate paper stamps onto an EXISTING account (Customer Lookup → Migrate). Staff tap
// how many stamps were on the paper card (1–7), PIN, submit → recorded as a real
// migration (attribution + per-account cap of two on the backend). Card-shaped 2-row
// grid (8 slots, 7 fillable, 8th locked) to mirror the 8-stamp card. Needs a photo of
// the paper card first (JJ, 29 Sep 2026) — it's saved with the migration and shows in
// the Command Centre.
function CCMigrateModal({customer,token,session,requestPin,onDone,onClose,showToast}){
  const [photo,setPhoto]=useState(null);
  const [count,setCount]=useState(0);
  const [busy,setBusy]=useState(false);
  const fileRef=useRef(null);
  const onPhoto=(e)=>ccCaptureMigrationPhoto(e.target.files&&e.target.files[0],setPhoto,showToast);
  const canSubmit=!!photo&&count>=1&&!busy;
  const submit=()=>{
    if(!canSubmit)return;
    requestPin(async(staffName)=>{
      setBusy(true);
      try{
        if(token==="mock-preview-token") await new Promise(r=>setTimeout(r,300));
        else await ccApiCall("/scanner/migrate-existing",token,{method:"POST",timeoutMs:CC_SLOW_TIMEOUT_MS,body:JSON.stringify({loyalty_id:customer.loyalty_id,count,site_id:session.siteId,staff_name:staffName,photo:photo})});
        showToast(count+" paper stamp"+(count!==1?"s":"")+" migrated onto "+(customer.first_name||"their")+"'s card","success");
        onDone(true);
      }catch(err){showToast(ccStaffError(err),"error");setBusy(false)}
    },()=>{});
  };
  return (
    <div className="cc-modal" onClick={e=>{if(e.target===e.currentTarget&&!busy)onClose()}}>
      <div className="cc-modal-box" style={{maxWidth:420,maxHeight:"100%",overflowY:"auto"}}>
        <div className="cc-modal-title">Migrate paper stamps</div>
        <div className="cc-comp-sub">Snap {customer.first_name?customer.first_name+"'s":"their"} paper card, then tap the stamps that were on it — laid out like the card.</div>
        <div className="cc-mstep" style={{marginTop:14}}>
          <div className="cc-mstep-lab"><span className="cc-mstep-n">1</span> Photo of the paper card {!photo&&<span className="cc-req">required</span>}</div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} style={{display:"none"}}/>
          {photo
            ? <div className="cc-photo-preview"><img src={photo} alt="Paper card"/><button className="cc-photo-retake" disabled={busy} onClick={()=>fileRef.current&&fileRef.current.click()}>Retake</button></div>
            : <button className="cc-photo-btn" onClick={()=>fileRef.current&&fileRef.current.click()}>📷 Take photo</button>}
        </div>
        <div className={"cc-mstep"+(!photo?" cc-mstep-off":"")} style={{marginTop:14,gap:0}}>
          <div className="cc-mstep-lab"><span className="cc-mstep-n">2</span> Tap the stamps on the card</div>
          <div className="cc-tapfish" style={{gridTemplateColumns:"repeat(4,1fr)",margin:"14px 0 8px"}}>
            {Array.from({length:8},(_,i)=>i).map(i=>{
              if(i===7){
                return <button key="locked" className="cc-tapfish-slot" tabIndex={-1} style={{cursor:"default",opacity:0.35,borderStyle:"dashed"}}>{"🔒"}</button>;
              }
              const filled=i<count;
              return <button key={i+"-"+filled} className={"cc-tapfish-slot"+(filled?" filled":"")} disabled={busy} onClick={()=>setCount(count===i+1?i:i+1)}><CCSoyFish filled={filled}/></button>;
            })}
          </div>
          <div className="cc-tapfish-count" style={{marginBottom:8}}>
            {count===0?"Tap the stamps that were on their paper card":("Migrate "+count+" stamp"+(count!==1?"s":""))}
          </div>
        </div>
        <div style={{fontSize:12,color:"var(--cc-kewpie-dim)",marginBottom:12}}>{customer.migrations_remaining===1?"One paper card is already migrated — this is their second (and final one).":"A customer can migrate up to two paper cards."}</div>
        <button className="cc-btn cc-btn-primary" onClick={submit} disabled={!canSubmit} style={{width:"100%",marginBottom:8,padding:14,fontWeight:700}}>{busy?"…":(count===0?"Migrate stamps":("Migrate "+count+" stamp"+(count!==1?"s":"")))}</button>
        <button className="cc-btn cc-btn-secondary" onClick={onClose} disabled={busy} style={{width:"100%"}}>Cancel</button>
      </div>
    </div>
  );
}

// offline: the scanner-only offline session (signed in at the PIN pad with no connection —
// no scanner token). Every screen runs its offline path: stamps queue, rewards and Special
// Cards use the offline picker; nothing tries to refresh a token or drain a queue.
export function ClubCardScanner({session,token,requestPin,backRef,onSignIn,offline,idleRef}){
  const [screen,setScreen]=useState("home");
  const [scanResult,setScanResult]=useState(null);
  const [showSignup,setShowSignup]=useState(false);
  const [showIssueSpecial,setShowIssueSpecial]=useState(false);
  const [compCustomer,setCompCustomer]=useState(null);
  const [migrateCustomer,setMigrateCustomer]=useState(null);
  // idleRef (the scanner-only offline session): true while nothing's mid-job: the scanner home,
  // no result, no sheet open. Only then may "Wi-Fi's back" lock the tablet by itself.
  useEffect(()=>{ if(idleRef) idleRef.current = screen==="home" && !scanResult && !showSignup && !showIssueSpecial && !compCustomer && !migrateCustomer; });
  const [isOnline,setIsOnline]=useState(typeof navigator!=="undefined"?navigator.onLine:true);
  const netDown=useCcNetDown(); // no answer from the server = offline too (home Offline pill)
  const [queueCount,setQueueCount]=useState(0);
  const [toast,setToast]=useState(null);
  const toastTimerRef=useRef(null);

  const showToast=useCallback((msg,type="info",dur=3000)=>{
    if(toastTimerRef.current)clearTimeout(toastTimerRef.current);
    setToast({msg,type});
    toastTimerRef.current=setTimeout(()=>setToast(null),dur);
  },[]);
  useEffect(()=>()=>{if(toastTimerRef.current)clearTimeout(toastTimerRef.current)},[]);

  // Home header online + queue indicators. Some kiosk tablets never fire 'online', and offline
  // now greys four toolbar buttons (JJ, 14 Oct 2026), so a stale "offline" heals by itself:
  // the 5 s tick re-reads navigator.onLine, and while that still says offline, a small /health
  // read every 20 s settles it (the server answering means we're online).
  useEffect(()=>{
    const on=()=>setIsOnline(true),off=()=>setIsOnline(false);
    window.addEventListener("online",on);window.addEventListener("offline",off);
    let ticks=0;
    const poll=()=>{
      ccGetQueued().then(q=>setQueueCount(q.length)).catch(()=>{});
      if(typeof navigator==="undefined") return;
      if(navigator.onLine){ setIsOnline(true); return; }
      if((ticks++)%4===0) ccServerReachable(3000).then(ok=>{ if(ok) setIsOnline(true); }).catch(()=>{});
    };
    poll();const t=setInterval(poll,5000);
    return ()=>{window.removeEventListener("online",on);window.removeEventListener("offline",off);clearInterval(t)};
  },[]);

  // Local-preview escape hatch — mock session+token on localhost so the UI
  // renders for QA. Production hosts still enforce real auth.
  const isLocalPreview = typeof window!=="undefined" && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  const effSession = session || (isLocalPreview ? {siteId:"a1000000-0000-4000-8000-000000000001",siteName:"TRAFFORD CENTRE",staffName:"Preview User",staffPin:"0000"} : null);
  const effToken = offline ? null : (token || (isLocalPreview ? "mock-preview-token" : null));

  // Self-heal: with no live token the scanner makes no API calls, so the on-401
  // token refresh never fires and it would sit dead until a human reloads. When a
  // session is present but the token's gone, re-mint one from the refresh token on
  // mount, when the network returns, and on a short interval. ccTryRefresh() pushes
  // any new token into the shell (ccAuth.onToken -> setCcToken), re-rendering us with
  // a working token. If the refresh token is also dead it triggers onExpired (-> the
  // shell's logout -> PIN pad), so this degrades to a real sign-in and never loops.
  useEffect(()=>{
    if(!effSession || effToken || offline) return;
    let stopped=false;
    const attempt=()=>{ if(stopped) return; if(typeof navigator!=="undefined" && !navigator.onLine) return; ccTryRefresh().catch(()=>{}); };
    attempt();
    const onOnline=()=>attempt();
    if(typeof window!=="undefined") window.addEventListener("online",onOnline);
    const iv=setInterval(attempt,5000);
    return ()=>{ stopped=true; if(typeof window!=="undefined") window.removeEventListener("online",onOnline); clearInterval(iv); };
  },[effSession,effToken,offline]);

  // Drain the offline REDEMPTION queue from the ALWAYS-MOUNTED shell, so a reconnect
  // reconciles queued money events no matter which sub-screen is open (the redeem screen
  // fills the queue; without this it would only drain when staff later opened the stamp
  // scanner). DON'T gate on navigator.onLine — kiosk/WebView tablets misreport it and may
  // never fire 'online', and ccSyncRedeems already fails safe offline (its POSTs just
  // fail and the rows are kept), so an unconditional attempt is safe. Retry on an interval
  // to cover a stuck-offline tablet whose network is actually up. Toasts only on mount + the
  // 'online' event (real reconnect moments); interval drains are SILENT so a genuinely
  // offline till never spams "still syncing".
  useEffect(()=>{
    if(!effToken) return;
    ccSyncRedeems(effToken,showToast).catch(()=>{});
    const onOnline=()=>{ ccSyncRedeems(effToken,showToast).catch(()=>{}); };
    if(typeof window!=="undefined") window.addEventListener("online",onOnline);
    const iv=setInterval(()=>{ ccSyncRedeems(effToken).catch(()=>{}); },15000);
    return ()=>{ if(typeof window!=="undefined") window.removeEventListener("online",onOnline); clearInterval(iv); };
  },[effToken,showToast]);

  // Queued STAMPS drain from here too, every 20 s while any are waiting, so a till on
  // bad wifi (which never fires 'online') catches up without anyone reopening the
  // stamp scanner. Quiet: only "All scans synced!" or a dropped-scan warning shows.
  // With nothing queued but the connection marked down, the tick re-checks it with
  // one small read instead (tier rewards, which also refreshes the offline picker's
  // list), so the till comes out of offline mode by itself once the server answers.
  useEffect(()=>{
    if(!effToken) return;
    const tick=()=>{ ccSyncPinUses(effToken).catch(()=>{}); ccGetQueued().then(q=>{ if(q.length) return ccSyncQueue(effToken,showToast,setQueueCount,true); if(ccNetDown()) return ccFetchTierRewards(effToken); }).catch(()=>{}); };
    const iv=setInterval(tick,20000);
    if(typeof window!=="undefined") window.addEventListener("online",tick);
    return ()=>{ clearInterval(iv); if(typeof window!=="undefined") window.removeEventListener("online",tick); };
  },[effToken,showToast]);

  // The wrong-scanner hand-over: the scan the customer showed, carried to the scanner the
  // panel's button names, which runs it straight away (its initialQr).
  const [carry,setCarry]=useState(null); // {screen,text}
  const goCarry=(target,text)=>{setCarry({screen:target,text});setScreen(target)};
  const carryFor=(sc)=>(carry&&carry.screen===sc)?carry:null;
  const goHome=()=>{setScanResult(null);setCarry(null);setScreen("home")};
  // Publish a back handler to the Site App so hardware-back unwinds the scanner's own
  // screens before leaving for the site-app home. Returns true when it consumed the back
  // (was on an inner screen → go to the scanner home), false when already at home.
  const screenRef=useRef(screen);
  screenRef.current=screen;
  useEffect(()=>{
    if(!backRef) return;
    backRef.current=()=>{
      if(ccQrHolds>0) return true;          // a customer QR is up — only its Done button closes it
      if(ccCodeHolds>0) return true;        // a Square code is up — same: only Done closes it
      if(screenRef.current!=="home"){ goHome(); return true; }
      return false;
    };
    return ()=>{ if(backRef) backRef.current=null; };
  },[backRef]);
  const onScanResult=(r)=>{setScanResult(r);setScreen("result")};
  const onAddStampFromLookup=(cust)=>{
    requestPin(async(staffName)=>{
      try{
        const result=await ccApiCall("/scanner/scan",effToken,{method:"POST",body:JSON.stringify({loyalty_id:cust.loyalty_id,site_id:effSession.siteId,staff_name:staffName})});
        const customer=await ccApiCall("/scanner/lookup/"+cust.loyalty_id,effToken);
        onScanResult({...result,customer});
      }catch(err){showToast(ccStaffError(err),"error")}
    },()=>{});
  };

  if(!effSession||(!effToken&&!offline)){
    if(effSession){
      // Session present, token gone — the self-heal effect above is re-minting it.
      // State only what's true; "Retry now" kicks a manual refresh attempt.
      return (<div className="cc-root"><div className="cc-offline-screen"><span className="cc-offline-pill"><span className={isOnline?"cc-offline-spin":"cc-offline-pdot"}/>{isOnline?"Reconnecting":"No connection"}</span><div className="cc-offline-h">{isOnline?"Reconnecting…":"No internet connection"}</div><div className="cc-offline-sub">{isOnline?"Trying to get the scanner back online.":"The scanner keeps trying to reconnect. Check the tablet is on the venue Wi-Fi."}</div><button className="cc-offline-btn cc-offline-btn--ghost" onClick={()=>{ccTryRefresh().catch(()=>{})}}>Retry now</button><div className="cc-offline-help">{(effSession.siteName||"This till")+" · still stuck after a minute? Restart the tablet (hold the power button)."}</div></div></div>);
    }
    // No session at all — route to the REAL sign-in (the shell's login/PIN pad),
    // never a bare page reload (that was the loop bug). Reload only as a last resort.
    return (<div className="cc-root"><div className="cc-offline-screen"><span className="cc-offline-pill"><span className="cc-offline-pdot"/>Signed out</span><div className="cc-offline-h">Sign in to use the scanner</div><div className="cc-offline-sub">{"Tap below, then sign in with your PIN."}</div><button className="cc-offline-btn" onClick={()=>{ if(typeof onSignIn==="function") onSignIn(); else window.location.reload(); }}>Sign in</button><div className="cc-offline-help">If the PIN won't go through, check the tablet's Wi-Fi.</div></div></div>);
  }

  return (
    <div className="cc-root">
      <CCScannerDefs/>
      {screen==="home"&&<CCHomeScreen session={effSession} token={effToken} isOnline={isOnline&&!netDown} queueCount={queueCount} onReward={()=>setScreen("rewardscan")} onRedeem={()=>setScreen("redeem")} onClubScan={()=>setScreen("clubscan")} onLookup={()=>setScreen("lookup")} onSignup={()=>setShowSignup(true)} onIssueSpecial={()=>setShowIssueSpecial(true)} onMigrate={()=>setScreen("migration")}/>}
      {screen==="redeem"&&<CCUniversalRedeemScan session={effSession} token={effToken} requestPin={requestPin} onScanResult={onScanResult} onBack={goHome} showToast={showToast} initialQr={carryFor("redeem")} onWrongQr={(k,text)=>goCarry(k==="reward"?"rewardscan":"clubscan",text)}/>}
      {screen==="clubscan"&&<CCScannerScreen session={effSession} token={effToken} requestPin={requestPin} onScanResult={onScanResult} onBack={goHome} showToast={showToast} initialQr={carryFor("clubscan")} onWrongQr={(k,text)=>goCarry(k==="special"?"redeem":"rewardscan",text)}/>}
      {screen==="rewardscan"&&<CCScannerScreen variant="reward" session={effSession} token={effToken} requestPin={requestPin} onScanResult={onScanResult} onBack={goHome} showToast={showToast} initialQr={carryFor("rewardscan")} onWrongQr={(k,text)=>goCarry(k==="special"?"redeem":"clubscan",text)}/>}
      {screen==="lookup"&&<CCLookup session={effSession} token={effToken} requestPin={requestPin} onMigrate={setMigrateCustomer} onCompStamp={setCompCustomer} onBack={goHome} showToast={showToast}/>}
      {screen==="result"&&<CCStampResult result={scanResult} onDone={goHome}/>}
      {screen==="migration"&&<CCMigration session={effSession} token={effToken} requestPin={requestPin} onDone={goHome} showToast={showToast}/>}
      {showSignup&&<CCSignupQR session={effSession} token={effToken} requestPin={requestPin} showToast={showToast} onClose={()=>setShowSignup(false)}/>}
      {showIssueSpecial&&<CCIssueSpecialCard session={effSession} token={effToken} requestPin={requestPin} showToast={showToast} onClose={()=>setShowIssueSpecial(false)}/>}
      {compCustomer&&<CCCompModal customer={compCustomer} token={effToken} session={effSession} requestPin={requestPin} onDone={(r)=>{setCompCustomer(null);if(r)onScanResult(r)}} onClose={()=>setCompCustomer(null)} onMigrate={(c)=>{
        // "Existing Physical Stamps" picked in Comp → the migration modal (photo + count),
        // with the same two-cards-per-account gate as Lookup's Migrate button.
        const rem=c.migrations_remaining!=null?c.migrations_remaining:(c.migrated?0:2);
        setCompCustomer(null);
        if(rem<=0){showToast("Both paper cards already migrated","info");return;}
        setMigrateCustomer(c);
      }} showToast={showToast}/>}
      {migrateCustomer&&<CCMigrateModal customer={migrateCustomer} token={effToken} session={effSession} requestPin={requestPin} onDone={()=>setMigrateCustomer(null)} onClose={()=>setMigrateCustomer(null)} showToast={showToast}/>}
      {toast&&<div className={"cc-toast "+toast.type}>{toast.msg}</div>}
    </div>
  );
}

// ===========================================
// PLACEHOLDER
// ===========================================
// ===========================================
// FOH 67 CHECKLIST (Phase C, 2026-04-22)
// ===========================================
// Daily checklist tile. 4 sweeps per shift (Alpha → Sigma). Each task ticks
// 4 times across the day, sequential lock per task. Squad effort — any staff
// PIN can tick any slot. Random Act of Kindness is a once-per-day standalone.
function FOHChecklist({site,backend,requestPin,checklistConfig,configSource,configParity}){
  // Hydrate events from localStorage cache so the tile (and the EOD Report
  // % block that reads the same cache) renders with last-known state instantly.
  // The 15s poller + post-tick reload keep it honest; the cache is just the
  // optimistic first paint. Keyed by site+date so tomorrow's blank slate
  // won't reuse yesterday's ticks.
  const __cacheKey = "checklistEventsCache::" + site.key;
  const __todayStr = todayKey();  // London day (was UTC) — the checklist events cache must key on the same London day as the authoritative read (matches the cache-write below)
  const [events,setEvents] = useState(()=>{
    try {
      const raw = localStorage.getItem(__cacheKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (parsed && parsed.date === __todayStr && Array.isArray(parsed.events)) return parsed.events;
    } catch(e){}
    return [];
  });
  const [randomActs,setRandomActs] = useState([]);
  const [loading,setLoading] = useState(true);
  const [rakOpen,setRakOpen] = useState(false);
  const [rakReason,setRakReason] = useState("");
  const [reviewOpen,setReviewOpen] = useState(false);
  const [reviewMention,setReviewMention] = useState("");
  const [reviewNotes,setReviewNotes] = useState("");
  const [reviewSubmitting,setReviewSubmitting] = useState(false);
  const [victory,setVictory] = useState(null); // tier object that just graduated
  const [submitting,setSubmitting] = useState({}); // {key: true} per slot
  // Which source served the last day read ('supabase' green / 'sheet' amber).
  // Only shown on a flipped shop — everywhere else it is always the sheet.
  const [daySource,setDaySource] = useState(null);

  const cfg = checklistConfig || {};
  const tiers = cfg.tiers || [];
  const sections = cfg.sections || [];

  // Merges server response with any still-pending optimistic events. Without
  // this merge, the 15s auto-refresh would clobber an optimistic tick whose
  // POST hadn't yet landed, making the slot flicker grey then green again.
  // Shared with the login pre-warm and the EOD block — one in-flight day read per
  // shop (lib/checklist.js fetchChecklistDayShared). `{fresh:true}` straight after
  // a write, so a read that began BEFORE the tick can't be taken as its confirmation.
  const loadDay = useCallback(async (opts)=>{
    try {
      const res = await checklistSrc.fetchChecklistDayShared({ sheetUrl: backend + "?action=checklistday&site=" + site.key, site: site.key, fresh: !!(opts && opts.fresh) });
      const d = (res && res.day) || {};
      setDaySource((res && res._source) || null);
      if (Array.isArray(d.events)) {
        setEvents(prev => {
          const optimistic = prev.filter(e => e.__optimistic);
          const now = Date.now();
          const stillPending = optimistic.filter(oe => {
            const sinceMs = new Date(oe.ts).getTime();
            // Confirmed by the server (a landed row for this slot at/after the
            // tap)? Drop the optimistic dupe — the real row now carries it.
            if (checklistSrc.slotIsPresent(d.events, { tierOrder: oe.tierOrder, section: oe.section, task: oe.task }, sinceMs)) return false;
            // This is a CLEAN read (loadDay only reaches here with a real events
            // array) and it lacks the tick. Keep it briefly so a still-in-flight
            // write doesn't flicker grey→green; but after the grace window a
            // still-absent optimistic tick is a phantom that never saved — drop
            // it so a green tick can't keep lying (the Meadowhall 2–3 Aug bug).
            return (now - sinceMs) < CHECKLIST_OPTIMISTIC_GRACE_MS;
          });
          return [...d.events, ...stillPending];
        });
        // Write non-optimistic events to the shared cache so the EOD % block
        // reads today's state without its own cold-start fetch. Strip the
        // __optimistic marker — only server-confirmed events go in the cache.
        try { localStorage.setItem(__cacheKey, JSON.stringify({date: __todayStr, events: d.events, ts: Date.now()})); } catch(e){}
      }
      if (Array.isArray(d.randomActs)) {
        setRandomActs(prev => {
          const opt = prev.filter(a => a.__optimistic);
          const stillPending = opt.filter(oa => !d.randomActs.some(sa => sa.reason === oa.reason));
          return [...d.randomActs, ...stillPending];
        });
      }
      setLoading(false);
    } catch(e) { setLoading(false); }
  },[site.key,backend]);
  useEffect(()=>{ loadDay(); },[loadDay]);
  // Re-poll CHECKLIST_POLL_MS AFTER the previous read settles, not on a fixed
  // timer regardless: the read takes 12–80 s on a busy backend, so a fixed 15 s
  // interval stacked two to five copies of it at once, and every other backend
  // call queued behind them (20 Sep 2026).
  useEffect(()=>{
    let stopped = false, timer = null;
    const tick = async ()=>{
      if (stopped) return;
      try { await loadDay(); } catch(e) {}
      if (!stopped) timer = setTimeout(tick, CHECKLIST_POLL_MS);
    };
    timer = setTimeout(tick, CHECKLIST_POLL_MS);
    return ()=>{ stopped = true; if (timer) clearTimeout(timer); };
  },[loadDay]);

  // Build a lookup: tickMap[tierOrder][section][task] = event (latest non-undo)
  const tickMap = {};
  events.forEach(e=>{
    if (e.action === "undo") return;
    if (!tickMap[e.tierOrder]) tickMap[e.tierOrder] = {};
    if (!tickMap[e.tierOrder][e.section]) tickMap[e.tierOrder][e.section] = {};
    tickMap[e.tierOrder][e.section][e.task] = e;
  });

  // Tier progress + which tier is next available per task (sequential lock).
  const totalTasks = sections.reduce((n,s)=>n + s.tasks.length, 0);
  const tierStats = tiers.map(t=>{
    const ticked = sections.reduce((n,s)=>n + s.tasks.filter(task=>tickMap[t.order]&&tickMap[t.order][s.name]&&tickMap[t.order][s.name][task.name]).length, 0);
    return { ...t, ticked, total: totalTasks, done: ticked === totalTasks && totalTasks > 0 };
  });
  const overall = tierStats.reduce((n,t)=>n + t.ticked, 0);
  const overallTotal = totalTasks * tiers.length;
  const overallPct = overallTotal ? Math.round((overall/overallTotal)*100) : 0;

  // Watch for tier graduation — fire victory animation once per graduate.
  const lastGraduatedRef = React.useRef(0);
  useEffect(()=>{
    const graduated = tierStats.filter(t=>t.done).length;
    if (graduated > lastGraduatedRef.current && lastGraduatedRef.current !== 0) {
      const newest = tierStats.filter(t=>t.done).sort((a,b)=>a.order-b.order)[graduated-1];
      if (newest) setVictory(newest);
    }
    lastGraduatedRef.current = graduated;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[overall]);

  function fmtTime(iso){
    if (!iso) return "";
    try { const d = new Date(iso); return d.toTimeString().slice(0,5); } catch(_) { return ""; }
  }
  // Resolves a display initials string for a staffer by display name.
  //
  // Row G (Lane BF): a checklist slot shows the initials of whoever ticked it —
  // an ARBITRARY staffer, which a single-PIN verify can never answer. So on a
  // Supabase-checklist host the initials ride ON THE ROW (migration 1100,
  // `staffInitials`) and we resolve them without ever reading the downloaded
  // roster — that is what lets a server-PIN-verify tablet render checklist
  // initials with no cache read. On a Sheet-read host (and the OFF default) there
  // is no row initials, so it keeps scanning the `staffByPinCache` override
  // (populated by `?action=pinsForLogin`) exactly as before.
  function initialsOf(name, rowInitials){
    if (!name) return "??";
    // Supabase-checklist host: initials came down on the row — no roster read.
    if (checklistSrc.checklistReadEnabled()) return rosterFree.preferInitials(rowInitials, name);
    // Sheet-read host / OFF default: unchanged — scan the roster cache for the
    // admin's initials override (Michaella vs Mia), then fall back to name-derive.
    try {
      const raw = localStorage.getItem("staffByPinCache");
      if (raw) {
        const map = JSON.parse(raw);
        if (map && typeof map === "object") {
          for (const pin in map) {
            const rec = map[pin];
            if (rec && rec.name === name && rec.initials) {
              return String(rec.initials).toUpperCase();
            }
          }
        }
      }
    } catch(e) {}
    return rosterFree.deriveInitials(name);
  }

  // Optimistic update — slot turns green the instant PIN is verified, then the
  // save is CONFIRMED against the backend before the green is allowed to stay.
  // History: the original fire-and-forget never read the response (6 Jul "the
  // checklist wiped itself"); confirmedWrite then closed the 403/network hole.
  // But it still trusted a NON-JSON 200 — and the Apps Script /exec, under load,
  // returns exactly that: an HTML error/quota page with HTTP 200. So a flaky
  // /exec silently dropped the tick while the screen stayed green AND no presence
  // tap was written — which is why Meadowhall's reviews on 2–3 Aug attributed to
  // nobody (TEAM_POOL_OVERLAP). Now: on any UNCERTAIN reply confirmedWrite calls
  // `verify`, which re-reads the day (retry-until-JSON) and checks THIS slot
  // actually landed. Either the tick is really there (keep the green) or it isn't
  // (roll back immediately with a visible "didn't save" toast). The POST stays
  // single-shot, so the non-idempotent append can never double-write.
  function tickSlot(tier, section, task){
    const key = tier.order + "·" + section.name + "·" + task.name;
    if (submitting[key]) return;
    requestPin(async (staffName, staffPin)=>{
      const optimistic = {
        ts: new Date().toISOString(),
        tierOrder: tier.order, tierName: tier.name,
        section: section.name, task: task.name,
        staffPrefix: String(staffPin||"").substring(0,2)+"**",
        staff: staffName || "you", role: "", action: "tick", notes: "",
        __optimistic: true
      };
      setEvents(prev => [...prev, optimistic]);
      setSubmitting(s=>({...s,[key]:true}));
      // ── WRITE-AUTHORITATIVE (Lane BD) ────────────────────────────────────────
      // On a host in CHECKLIST_WRITE_HOSTS whose READ is flipped, Supabase is the
      // RECORD: await the 0440 tick door and DON'T post the sheet. Gated OFF (the
      // write set ships empty), so today every shop falls through to the
      // byte-identical else path below. The optimistic tick is already on screen; we
      // keep it on a confirmed write and roll it back with the same honest toast on a
      // real failure. On an UNCERTAIN door result ('error'/unknown) we re-read the
      // day (Supabase — the read is flipped on this host) three-valued, exactly like
      // the sheet path's verify, so a write that landed-then-errored isn't wrongly
      // rolled back (the single-shot door is idempotent on its idem key regardless).
      if (checklistSrc.checklistWriteAuthoritative(
            (typeof window !== "undefined" && window.location.hostname) || "",
            (typeof window !== "undefined" && window.location.search) || "",
            { readEnabled: checklistSrc.checklistReadEnabled() })) {
        const authRes = await checklistSync.recordChecklistTickAuthoritative({ siteKey: site.key, tier, section: section.name, task: task.name, staffPin });
        let ok = !!(authRes && authRes.ok);
        if (!ok && authRes && (authRes.status === "error" || authRes.status == null)) {
          const sinceMs = new Date(optimistic.ts).getTime();
          const slot = { tierOrder: tier.order, section: section.name, task: task.name };
          let sawCleanRead = false;
          for (let i = 0; i < 3; i++){
            let res = null;
            try { res = await checklistSrc.fetchChecklistDay({ sheetUrl: backend + "?action=checklistday&site=" + site.key, site: site.key }); } catch(e) { res = null; }
            if (res && res.day && Array.isArray(res.day.events)) {
              sawCleanRead = true;
              if (checklistSrc.slotIsPresent(res.day.events, slot, sinceMs)) { ok = true; break; }
            }
            if (i < 2) await new Promise(r => setTimeout(r, 500 * (i + 1)));
          }
          if (!ok && !sawCleanRead) ok = true;   // no clean read -> keep; the 15s poll reconciles
        }
        if (ok) { loadDay({fresh:true}); }
        else {
          setEvents(prev => prev.filter(ev => ev !== optimistic));
          notifyWriteFailed(task.name + " didn't save — " + ((authRes && authRes.errMsg) || "try again"));
        }
        setSubmitting(s=>{ const n={...s}; delete n[key]; return n; });
        return;
      }
      // Dual-run shadow into Supabase alongside the sheet write below. Best-effort,
      // never awaited, never throws; a bad PIN makes ensurePass fail so no shadow
      // row lands (mirrors the sheet, which also rejects a bad PIN). WRITE only —
      // the checklist read stays on the sheet (checklistSrc, gated off).
      try { checklistSync.recordChecklistTick({ siteKey: site.key, tier, section: section.name, task: task.name, staffPin }); } catch(_) {}
      await confirmedWrite({
        backend, action: "checklistTick", staffName, staffPin,
        payload: {
          site: site.key,
          tier: {order:tier.order, name:tier.name},
          section: section.name, task: task.name
        },
        // Read-back confirmation for an uncertain reply (flaky /exec HTML-200, or
        // a lost response). THREE-VALUED, so the very brownout this fixes can't
        // turn a tick that LANDED into a rolled-back one (which would re-tap and
        // double-write the non-idempotent backend):
        //   • a CLEAN day-read that contains this slot -> saved: keep the green.
        //   • a CLEAN day-read that lacks it           -> it genuinely didn't
        //        save: return false so the slot rolls back with a visible toast.
        //   • NO clean read at all (still browned out) -> UNKNOWN: return true to
        //        keep the tick rather than risk a false rollback; the 15s poll
        //        reconciles and drops it after the grace window if it never lands.
        // fetchChecklistDay already retries until it gets real JSON, and the few
        // attempts here also absorb the brief append→visible lag on a re-read.
        verify: async () => {
          const sinceMs = new Date(optimistic.ts).getTime();
          const slot = { tierOrder: tier.order, section: section.name, task: task.name };
          let sawCleanRead = false;
          for (let i = 0; i < 3; i++){
            let res = null;
            try { res = await checklistSrc.fetchChecklistDay({ sheetUrl: backend + "?action=checklistday&site=" + site.key, site: site.key }); } catch(e) { res = null; }
            if (res && res.day && Array.isArray(res.day.events)) {
              sawCleanRead = true;
              if (checklistSrc.slotIsPresent(res.day.events, slot, sinceMs)) return true;
            }
            if (i < 2) await new Promise(r => setTimeout(r, 500 * (i + 1)));
          }
          return sawCleanRead ? false : true;
        },
        onSuccess: () => { loadDay({fresh:true}); },
        onRollback: (msg) => {
          setEvents(prev => prev.filter(ev => ev !== optimistic));
          notifyWriteFailed(task.name + " didn't save — " + msg);
        },
      });
      setSubmitting(s=>{ const n={...s}; delete n[key]; return n; });
    });
  }

  function submitRak(){
    const reason = rakReason.trim();
    if (!reason) return;
    requestPin((staffName, staffPin)=>{
      const optimistic = {
        ts: new Date().toISOString(),
        staffPrefix: String(staffPin||"").substring(0,2)+"**",
        staff: staffName || "you", role: "", reason: reason,
        __optimistic: true
      };
      setRandomActs(prev => [optimistic, ...prev]);
      setRakOpen(false); setRakReason("");
      // ── WRITE-AUTHORITATIVE (Lane BD) ──────────────────────────────────────
      // Same gate as the tick: on a flipped WRITE host, await the RAK door and don't
      // post the sheet. Gated OFF today → the byte-identical else path below runs.
      if (checklistSrc.checklistWriteAuthoritative(
            (typeof window !== "undefined" && window.location.hostname) || "",
            (typeof window !== "undefined" && window.location.search) || "",
            { readEnabled: checklistSrc.checklistReadEnabled() })) {
        (async ()=>{
          const res = await checklistSync.recordChecklistRakAuthoritative({ siteKey: site.key, reason, staffPin });
          if (res && res.ok) { await loadDay({fresh:true}); }
          else {
            setRandomActs(prev => prev.filter(a => a !== optimistic));
            notifyWriteFailed("Random act didn't save — " + ((res && res.errMsg) || "try again"));
          }
        })();
        return;
      }
      // Dual-run shadow into Supabase alongside the sheet write. Best-effort, never
      // awaited, never throws (WRITE only — the read stays on the sheet).
      try { checklistSync.recordChecklistRak({ siteKey: site.key, reason, staffPin }); } catch(_) {}
      (async ()=>{
        try {
          await fetch(backend, {method:"POST", redirect:"follow", body:JSON.stringify({
            action:"randomActOfKindness", site:site.key, staffPin, reason
          })});
          await loadDay({fresh:true});
        } catch(e) {
          setRandomActs(prev => prev.filter(a => a !== optimistic));
        }
      })();
    });
  }

  const todayRak = randomActs[0]; // most recent, only one per day expected

  // Bridge UX until Google Business Profile API approval lands. Staff
  // self-reports a named review they received. Backed by ManualReviews
  // sheet tab (backend @68). Fire-and-forget with toast via alert fallback.
  function submitManualReview(){
    const mention = reviewMention.trim();
    if (!mention || reviewSubmitting) return;
    setReviewSubmitting(true);
    requestPin((staffName, staffPin)=>{
      // ── WRITE-AUTHORITATIVE (Lane BD Wave 2) ─────────────────────────────────
      // On a host flipped WRITE-authoritative, await the 1060 door and DON'T post the
      // sheet. The gate lives in Lane BD Wave 1 (#316); this PR is independent, so the
      // reference is guarded — if checklistWriteAuthoritative isn't on main yet it reads
      // as undefined and this whole branch is OFF (dual-write below runs, byte-identical
      // to today plus the additive shadow).
      const reviewWriteAuth = (typeof checklistSrc.checklistWriteAuthoritative === "function")
        && checklistSrc.checklistWriteAuthoritative(
             (typeof window !== "undefined" && window.location.hostname) || "",
             (typeof window !== "undefined" && window.location.search) || "",
             { readEnabled: checklistSrc.checklistReadEnabled() });
      if (reviewWriteAuth) {
        (async ()=>{
          const res = await checklistSync.recordChecklistManualReviewAuthoritative({ siteKey: site.key, customerMention: mention, notes: reviewNotes.trim(), staffPin });
          if (res && res.ok) { setReviewOpen(false); setReviewMention(""); setReviewNotes(""); }
          else { notifyWriteFailed("That review didn't save — " + ((res && res.errMsg) || "try again")); }
          setReviewSubmitting(false);
        })();
        return;
      }
      // Dual-run shadow into Supabase alongside the sheet write (additive from merge
      // day, exactly as the tick has since 19 Aug). Best-effort, never awaited, never
      // throws; the Sheet POST below is unchanged.
      try { checklistSync.recordChecklistManualReview({ siteKey: site.key, customerMention: mention, notes: reviewNotes.trim(), staffPin }); } catch(_) {}
      (async ()=>{
        try {
          await fetch(backend, {method:"POST", redirect:"follow", body:JSON.stringify({
            action:"manualReviewLog", site:site.key, staffPin,
            customerMention: mention, notes: reviewNotes.trim(), rating: 5
          })});
          setReviewOpen(false);
          setReviewMention("");
          setReviewNotes("");
        } catch(e) {
          // Leave modal open so staff can retry.
        } finally {
          setReviewSubmitting(false);
        }
      })();
    });
  }

  // Slot status for a (tier, section, task): "done" | "next" | "locked"
  // Sequential per-task: tier N is next if tier N-1 is done; first tier is always next.
  // Tier-wide sequential lock (2026-04-22): you can't tick a tier-N slot on
  // any task until every task across every section has been ticked at tier
  // N-1. Reflects how the 4-sweeps-a-day flow actually runs in service — a
  // sweep is a pass across the whole kiosk, not a per-task climb. Supersedes
  // the earlier per-task lock which let one staffer race ahead on a single
  // item while others were still mid-sweep.
  function slotStatus(tier, section, task){
    const event = tickMap[tier.order]&&tickMap[tier.order][section.name]&&tickMap[tier.order][section.name][task.name];
    if (event) return { state:"done", event };
    if (tier.order === 1) return { state:"next" };
    const prevTierFullyDone = sections.every(sec =>
      sec.tasks.every(t =>
        tickMap[tier.order-1] &&
        tickMap[tier.order-1][sec.name] &&
        tickMap[tier.order-1][sec.name][t.name]
      )
    );
    return { state: prevTierFullyDone ? "next" : "locked" };
  }

  if (loading && tiers.length === 0) {
    return <div style={{padding:40,textAlign:"center",color:B.kewpie,opacity:0.5}}>Loading checklist…</div>;
  }
  if (tiers.length === 0) {
    return <div style={{padding:40,textAlign:"center",color:B.kewpie,opacity:0.5}}>No checklist configured for this site yet.</div>;
  }

  const css = {
    wrap:{maxWidth:1100,margin:"0 auto",padding:"14px 14px 80px",fontFamily:"Manrope,sans-serif",color:B.kewpie},
    header:{background:"linear-gradient(135deg,"+B.nori+" 0%,#002a20 100%)",borderRadius:18,padding:"18px 22px",marginBottom:12,display:"grid",gridTemplateColumns:"auto 1fr auto auto",gap:18,alignItems:"center",position:"relative"},
    mascot:{fontSize:48,lineHeight:1,filter:"drop-shadow(0 3px 8px rgba(0,0,0,0.3))"},
    h1:{fontFamily:"gelica,serif",fontSize:22,lineHeight:1.15,letterSpacing:"-0.01em",color:B.kewpie},
    bolt:{color:B.wasabi},
    sub:{opacity:0.78,marginTop:4,fontSize:13},
    ringWrap:{width:80,height:80,position:"relative",display:"flex",alignItems:"center",justifyContent:"center"},
    rule:{background:B.kewpie,color:B.nori,borderRadius:12,padding:"10px 14px",fontSize:13,fontWeight:600,display:"flex",gap:8,alignItems:"center",marginBottom:12,borderLeft:"4px solid "+B.wasabi},
    squad:{background:B.kewpie,color:B.nori,borderRadius:14,padding:"12px 16px",marginBottom:12},
    squadHd:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:6},
    tierStrip:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8},
    tierMini:{background:B.kewpie,borderRadius:10,padding:"8px 6px",textAlign:"center",border:"2px solid transparent"},
    tierMiniDone:{borderColor:"#2e9a52",background:"#f0fdf4"},
    tierMiniActive:{borderColor:B.wasabi,background:"#fcffea",boxShadow:"0 4px 12px rgba(212,251,111,0.4)"},
    tierMiniLocked:{opacity:0.55},
    daily:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12},
    dailyCard:{background:B.kewpie,color:B.nori,borderRadius:14,padding:"12px 14px",border:"1.5px solid rgba(0,57,44,0.08)",display:"flex",flexDirection:"column",gap:6},
    rakCard:{borderColor:"#f7c948",background:"linear-gradient(135deg,#fffbeb 0%,"+B.kewpie+" 60%)"},
    revCard:{borderColor:B.wasabi,background:"linear-gradient(135deg,#fcffea 0%,"+B.kewpie+" 60%)"},
    secCard:{background:B.kewpie,color:B.nori,borderRadius:14,padding:"12px 14px",marginBottom:10,border:"1.5px solid rgba(0,57,44,0.08)"},
    secHead:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,paddingBottom:8,borderBottom:"1px solid rgba(0,57,44,0.08)"},
    taskRow:{display:"grid",gridTemplateColumns:"32px 1fr",columnGap:12,rowGap:6,padding:"10px 4px",borderBottom:"1px dashed rgba(0,57,44,0.08)",alignItems:"center"},
    slots:{gridColumn:"1 / -1",display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6},
    slot:{height:42,borderRadius:9,border:"1.5px solid rgba(0,57,44,0.15)",background:"#fff",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",fontFamily:"inherit",padding:"0 4px",color:B.nori,position:"relative"},
    slotTier:{fontSize:9,fontWeight:800,letterSpacing:"0.08em",color:"rgba(0,57,44,0.4)",textTransform:"uppercase",lineHeight:1,marginBottom:1},
    slotBody:{fontSize:13,fontWeight:800,lineHeight:1,display:"flex",gap:3,alignItems:"baseline"},
    slotTime:{fontSize:10,fontWeight:600,opacity:0.75},
    slotDone:{background:"#2e9a52",borderColor:"#2e9a52",color:"#fff"},
    slotNext:{borderColor:B.wasabi,background:"#fcffea",borderWidth:2,boxShadow:"0 0 0 0 rgba(212,251,111,0.65)"},
    slotLocked:{background:"rgba(0,57,44,0.08)",borderColor:"transparent",cursor:"not-allowed",color:"rgba(0,57,44,0.4)"}
  };

  return (
    <div style={css.wrap}>
      <div style={{...css.header,gridTemplateColumns:"auto 1fr auto"}}>
        <span style={css.mascot}>🐨</span>
        <div>
          <div style={css.h1}>FOH 67 <span style={css.bolt}>Labubu Skibidi</span> Checklist 🧠</div>
          <div style={css.sub}>{site.name} · Tap a slot → PIN → done</div>
        </div>
        <div style={css.ringWrap}>
          <svg width="80" height="80" viewBox="0 0 80 80" style={{transform:"rotate(-90deg)"}}>
            <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="9"/>
            <circle cx="40" cy="40" r="36" fill="none" stroke={B.wasabi} strokeWidth="9" strokeLinecap="round"
              strokeDasharray="226" strokeDashoffset={226 - (overallPct/100)*226}/>
          </svg>
          <div style={{position:"absolute",fontSize:18,fontWeight:800,color:B.wasabi}}>{overallPct}%</div>
        </div>
      </div>

      {/* Soak indicator — only on a flipped shop (checklist.CHECKLIST_READ_HOSTS
          is empty by default, so this never renders today). Green = the day is
          coming from Supabase; amber = it fell back to the Google Sheet. */}
      {checklistSrc.checklistReadEnabled()&&daySource&&<div style={{fontSize:"0.68rem",fontWeight:600,marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",gap:6,color:daySource==="supabase"?"#6FAF8F":"#FF7A5C"}}><span style={{fontSize:"0.9rem",lineHeight:1}}>●</span>{daySource==="supabase"?"Live — checklist connected":"⚠ Fallback — checklist from the backup copy"}</div>}
      {/* Config source + form-seed parity — only on a flipped shop. The form (its
          sections/tasks) comes from Supabase ONLY when it matches the live Sheet
          config; a drift falls the whole session back to the Sheet so a tick can
          never write a stale task name. Green = verified match; amber = on the
          Sheet (drift caught, or the Sheet couldn't be reached to verify). */}
      {checklistSrc.checklistReadEnabled()&&configParity&&<div style={{fontSize:"0.68rem",fontWeight:600,marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",gap:6,color:(configParity==="clean"||configParity==="authoritative")?"#6FAF8F":"#FF7A5C"}}><span style={{fontSize:"0.9rem",lineHeight:1}}>●</span>{configParity==="clean"?"Config live — matches the backup copy":configParity==="authoritative"?"Config live":configParity==="drift"?"⚠ Config from the backup copy — the live copy disagreed":"⚠ Config on the Sheet"}</div>}

      <div style={css.rule}>
        <span style={{fontSize:18}}>⏱️</span>
        <span><strong>Tick AS YOU GO.</strong> Times stamp live as soon as you tap — only tick once the task is actually done.</span>
      </div>

      <div style={css.squad}>
        <div style={css.squadHd}>
          <strong style={{fontFamily:"gelica,serif",fontSize:16}}>🏆 Squad Goals: The Quest to Sigma</strong>
          <span style={{fontSize:11,color:"rgba(0,57,44,0.6)"}}>Whole team contributes. No one tier hero.</span>
        </div>
        <div style={css.tierStrip}>
          {tierStats.map(t=>{
            const isActive = !t.done && (t.order === 1 || tierStats[t.order-2].done);
            return (
              <div key={t.order} style={{...css.tierMini, ...(t.done?css.tierMiniDone:isActive?css.tierMiniActive:css.tierMiniLocked)}}>
                <div style={{fontSize:24,lineHeight:1,marginBottom:3}}>{t.icon}</div>
                <div style={{fontFamily:"gelica,serif",fontSize:13,fontWeight:700}}>{t.name}</div>
                <div style={{fontSize:10,color:"rgba(0,57,44,0.6)",marginTop:2,fontWeight:600}}>{t.done?(t.ticked+"/"+t.total+" ✓"):isActive?(t.ticked+"/"+t.total):"Locked"}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={css.daily}>
        <div style={{...css.dailyCard, ...css.rakCard, ...(todayRak?{background:"#f0fdf4",borderColor:"#2e9a52"}:{})}}>
          <div style={{display:"flex",alignItems:"center",gap:8,fontFamily:"gelica,serif",fontSize:14,fontWeight:700}}><span style={{fontSize:18}}>💛</span> Random Act of Kindness</div>
          <div style={{fontSize:11,color:"rgba(0,57,44,0.6)"}}>Once a day per site · who + reason</div>
          {todayRak ? (
            <div style={{fontSize:13,fontWeight:600,color:B.nori}}>✓ {todayRak.staff} · {fmtTime(todayRak.ts)} — <em>"{todayRak.reason}"</em></div>
          ) : (
            <button onClick={()=>setRakOpen(true)} style={{background:B.nori,color:B.wasabi,border:0,padding:"7px 14px",borderRadius:100,fontFamily:"inherit",fontWeight:700,fontSize:12,cursor:"pointer",alignSelf:"flex-start"}}>+ Log a Random Act</button>
          )}
        </div>
        <div style={{...css.dailyCard, ...css.revCard}}>
          <div style={{display:"flex",alignItems:"center",gap:8,fontFamily:"gelica,serif",fontSize:14,fontWeight:700}}>
            <span style={{fontSize:18}}>⭐</span> 5★ Google Reviews
            <span style={{marginLeft:"auto",fontSize:9,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",background:"rgba(247,201,72,0.2)",color:"#7a5b00",padding:"2px 7px",borderRadius:999}}>Coming soon</span>
          </div>
          <div style={{fontSize:11,color:"rgba(0,57,44,0.6)"}}>Auto-leaderboard goes live when Google approves our API access (typically 7-10 working days).</div>
          <div style={{fontSize:11,color:"rgba(0,57,44,0.7)",fontStyle:"italic"}}>💡 Tell every customer your name. When a review names you, the system will credit you automatically once the API is live.</div>
          <button onClick={()=>setReviewOpen(true)} style={{background:B.nori,color:B.wasabi,border:0,padding:"7px 14px",borderRadius:100,fontFamily:"inherit",fontWeight:700,fontSize:12,cursor:"pointer",alignSelf:"flex-start"}}>+ Log a review I got</button>
        </div>
      </div>

      {sections.map(sec=>{
        const sectionTicks = sec.tasks.reduce((n,task)=>n + tiers.filter(t=>tickMap[t.order]&&tickMap[t.order][sec.name]&&tickMap[t.order][sec.name][task.name]).length, 0);
        const sectionTotal = sec.tasks.length * tiers.length;
        return (
          <div key={sec.order} style={css.secCard}>
            <div style={css.secHead}>
              <h3 style={{fontFamily:"gelica,serif",fontSize:16,fontWeight:700,color:B.nori,margin:0}}>{sec.icon} {sec.name}</h3>
              <span style={{fontSize:11,color:"rgba(0,57,44,0.6)",fontWeight:600}}>{sectionTicks} / {sectionTotal} ticks</span>
            </div>
            {sec.tasks.map(task=>(
              <div key={task.order} style={css.taskRow}>
                <div style={{fontSize:22,textAlign:"center"}}>{task.icon}</div>
                <div style={{fontSize:14,fontWeight:600,lineHeight:1.25,color:B.nori}}>{task.name}</div>
                <div style={css.slots}>
                  {tiers.map(t=>{
                    const status = slotStatus(t, sec, task);
                    const slotStyle = {...css.slot, ...(status.state==="done"?css.slotDone:status.state==="next"?css.slotNext:css.slotLocked)};
                    const e = status.event;
                    return (
                      <button key={t.order} disabled={status.state!=="next"} onClick={()=>tickSlot(t, sec, task)} style={slotStyle}>
                        <span style={{...css.slotTier, color: status.state==="done"?"rgba(255,255,255,0.75)":status.state==="next"?B.nori:"rgba(0,57,44,0.4)"}}>{t.name}</span>
                        {e ? <span style={css.slotBody}>{initialsOf(e.staff, e.staffInitials)}<span style={css.slotTime}>{fmtTime(e.ts)}</span></span> : <span style={css.slotBody}></span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {rakOpen && (
        <div onClick={()=>setRakOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,42,32,0.6)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",color:B.nori,borderRadius:20,padding:24,maxWidth:400,width:"100%"}}>
            <h3 style={{fontFamily:"gelica,serif",fontSize:18,marginBottom:6}}>💛 Random Act of Kindness</h3>
            <div style={{fontSize:13,color:"rgba(0,57,44,0.6)",marginBottom:14}}>What did you do for the customer? (one line)</div>
            <input autoFocus maxLength={150} value={rakReason} onChange={e=>setRakReason(e.target.value)} placeholder="e.g. Free roll for a kid in a Rolled cap" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid rgba(0,57,44,0.15)",fontFamily:"inherit",fontSize:14,marginBottom:14}}/>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={()=>{setRakOpen(false);setRakReason("");}} style={{background:"transparent",border:"1.5px solid rgba(0,57,44,0.15)",borderRadius:10,padding:"8px 14px",fontFamily:"inherit",fontWeight:600,fontSize:13,color:B.nori,cursor:"pointer"}}>Cancel</button>
              <button onClick={submitRak} disabled={!rakReason.trim()} style={{background:B.nori,color:B.wasabi,border:0,borderRadius:10,padding:"8px 16px",fontFamily:"inherit",fontWeight:700,fontSize:13,cursor:rakReason.trim()?"pointer":"not-allowed",opacity:rakReason.trim()?1:0.5}}>Log it</button>
            </div>
          </div>
        </div>
      )}

      {reviewOpen && (
        <div onClick={()=>!reviewSubmitting && setReviewOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,42,32,0.6)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",color:B.nori,borderRadius:20,padding:24,maxWidth:440,width:"100%"}}>
            <h3 style={{fontFamily:"gelica,serif",fontSize:18,marginBottom:4}}>⭐ Log a review you got</h3>
            <div style={{fontSize:12,color:"rgba(0,57,44,0.6)",marginBottom:14,lineHeight:1.4}}>Until the Google API is wired, this is how your recognition gets on the record. Log it here while the interaction's fresh.</div>
            <label style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",color:"rgba(0,57,44,0.6)"}}>Who left the review</label>
            <input autoFocus value={reviewMention} onChange={e=>setReviewMention(e.target.value)} placeholder="e.g. Sarah, a mum with two kids — Tuesday around 2pm" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid rgba(0,57,44,0.15)",fontFamily:"inherit",fontSize:14,marginTop:4,marginBottom:12}}/>
            <label style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",color:"rgba(0,57,44,0.6)"}}>Notes (optional)</label>
            <input value={reviewNotes} onChange={e=>setReviewNotes(e.target.value)} placeholder="What the review said, anything memorable…" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid rgba(0,57,44,0.15)",fontFamily:"inherit",fontSize:14,marginTop:4,marginBottom:14}}/>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button disabled={reviewSubmitting} onClick={()=>{setReviewOpen(false);setReviewMention("");setReviewNotes("");}} style={{background:"transparent",border:"1.5px solid rgba(0,57,44,0.15)",borderRadius:10,padding:"8px 14px",fontFamily:"inherit",fontWeight:600,fontSize:13,color:B.nori,cursor:reviewSubmitting?"not-allowed":"pointer",opacity:reviewSubmitting?0.6:1}}>Cancel</button>
              <button onClick={submitManualReview} disabled={!reviewMention.trim() || reviewSubmitting} style={{background:B.nori,color:B.wasabi,border:0,borderRadius:10,padding:"8px 16px",fontFamily:"inherit",fontWeight:700,fontSize:13,cursor:(reviewMention.trim()&&!reviewSubmitting)?"pointer":"not-allowed",opacity:(reviewMention.trim()&&!reviewSubmitting)?1:0.5}}>{reviewSubmitting?"Logging…":"Log review"}</button>
            </div>
          </div>
        </div>
      )}

      {victory && (
        <div onClick={()=>setVictory(null)} style={{position:"fixed",inset:0,background:"rgba(0,42,32,0.85)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:20}}>
          <div style={{textAlign:"center",color:B.kewpie,padding:30}}>
            <div style={{fontSize:96,marginBottom:12,animation:"viewIn 0.5s ease both"}}>{victory.icon}</div>
            <h2 style={{fontFamily:"gelica,serif",fontSize:36,color:B.wasabi,marginBottom:8,lineHeight:1.1,textTransform:"uppercase"}}>{victory.name} Unlocked</h2>
            <p style={{fontSize:16,opacity:0.85,marginBottom:24}}>The squad pushed through.</p>
            <button onClick={()=>setVictory(null)} style={{background:B.wasabi,color:B.nori,border:0,padding:"12px 28px",borderRadius:100,fontFamily:"inherit",fontWeight:800,fontSize:15,cursor:"pointer"}}>Onwards 🚀</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Placeholder({label,icon,msg}){
  return (<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"50vh",textAlign:"center",padding:40}}>
    <div style={{fontSize:"3rem",marginBottom:12}}>{icon}</div>
    <div style={{fontFamily:"gelica,serif",fontSize:"1.4rem",color:B.wasabi,marginBottom:6}}>{label}</div>
    <div style={{color:B.kewpie,opacity:0.4,fontSize:"0.85rem",maxWidth:300,lineHeight:1.5}}>{msg}</div>
  </div>);
}

// =====================================================================
// COMPLIANCE MODULE — lifted from _mockup-compliance/index.html
// =====================================================================
// Phase 1 (2026-04-29): foundation + Cleaning Schedule + Pest Daily +
// Corrective Actions. Historical-day picker, HACCP, Probe, Fish, Daily
// Temp Records, Emergency Fridge, Declaration, FSMS Acceptance defer to
// session 2/3.
//
// Stripped from the mockup:
//  - Force-Sunday toggle (mockup-only knob; window.__rolledForceSunday)
//  - "Command Centre" staff-facing copy (FOH never sees that phrase)
//  - Hardcoded MOCK_* tables; everything renders from backend reads
//  - HistoricalSignerBanner / dateSeed / pickFrom (no historical view yet)
//
// Wiring:
//  - Surface defs (cleaning tasks, pest areas) come from
//    getComplianceConfig with a 5-min localStorage cache.
//  - Every action POSTs to action=complianceLog with a `event:` discriminator.
//  - PIN gate reuses Trafford's `requestPin(cb)`. Backend re-validates
//    every PIN via canPerformAction.
//  - Sunday pill: amber 06:00–17:00, RED after 17:00 (per JJ 2026-04-29).
//  - testMode flag flows through every POST so QA agent runs land in
//    _TEST-suffixed sheets (configured via window.__cmpTestMode for
//    QA-only override; defaults to false in production).

// ---- Helpers ----
function cmpMondayOf(dateStrOrDate) {
  var d = (dateStrOrDate instanceof Date) ? new Date(dateStrOrDate) : new Date(dateStrOrDate);
  var dow = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}
function cmpFriendly(iso) {
  if (!iso) return "—";
  var d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {day: "2-digit", month: "short"});
}
function cmpInitials(name) {
  return String(name || "").trim().slice(0, 2).toUpperCase() || "??";
}
function cmpIsTestMode() {
  try {
    return (typeof window !== "undefined" && window.__cmpTestMode === true);
  } catch(_) { return false; }
}

// ---- Toast layer (scoped — does not collide with Trafford's globals) ----
var __cmpToastPush = null;
function cmpToast(msg, tone) {
  if (__cmpToastPush) __cmpToastPush({msg: msg, tone: tone || "", id: Date.now() + Math.random()});
}
function CmpToastLayer() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    __cmpToastPush = (t) => {
      setItems(p => [...p, t]);
      setTimeout(() => setItems(p => p.filter(x => x.id !== t.id)), 3200);
    };
    return () => { __cmpToastPush = null; };
  }, []);
  return (
    <div className="cmp-toast-layer">
      {items.map(t => (
        <div key={t.id} className={"cmp-toast " + (t.tone === "success" ? "cmp-success" : t.tone === "warn" ? "cmp-warn" : t.tone === "error" ? "cmp-error" : "")}>{t.msg}</div>
      ))}
    </div>
  );
}

// ---- complianceLog(event, payload) — single-helper backend writer ----
// Always includes site + staffPin from the caller. The backend resolves
// the staff name + role + home from the PIN via lookupDirectoryRow on
// every write — client-supplied staffName is never sent and never
// trusted. Returns a promise that resolves with the parsed JSON or
// throws on network/HTTP failure. Callers should toast their own
// success/error messages so users see status per action.
//
// Content-Type is text/plain so the browser treats this as a "simple"
// CORS request (no preflight). Apps Script web apps return
// Access-Control-Allow-Origin: * on simple requests but don't handle
// OPTIONS preflight, so application/json fetches fail with "Failed to
// fetch". Same trick the admin api() helper uses (line 248). The
// backend reads e.postData.contents regardless of Content-Type so the
// request body still parses as JSON.
function cmpComplianceLog(backend, site, event, payload, staffPin) {
  // Envelope keys (action / event / site / staffPin / testMode) MUST win
  // over payload — historical bug: pest-daily and corrective-raise both
  // pass `action` as the corrective text, which previously shadowed the
  // envelope action and caused the backend to reject with "Forbidden".
  // Order matters: payload first, envelope second so Object.assign
  // overwrites.
  var body = Object.assign({}, payload || {}, {
    action: "complianceLog",
    event: event,
    site: site,
    staffPin: staffPin,
    testMode: cmpIsTestMode()
  });
  return fetch(backend, {
    method: "POST",
    headers: {"Content-Type": "text/plain;charset=utf-8"},
    body: JSON.stringify(body)
  }).then(function(r) {
    if (!r.ok) throw new Error("HTTP " + r.status);
    return r.json();
  }).then(function(d) {
    if (d && d.status === "error") throw new Error(d.message || "Server error");
    return d;
  });
}

// ---- getComplianceConfig with 5-min localStorage cache ----
var CMP_CONFIG_TTL_MS = 5 * 60 * 1000;
function cmpFetchComplianceConfig(backend, site, opts) {
  var force = opts && opts.force;
  var cacheKey = "cmpComplianceConfig." + site;
  var nowMs = Date.now();
  if (!force) {
    try {
      var raw = localStorage.getItem(cacheKey);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && (nowMs - cached.ts) < CMP_CONFIG_TTL_MS) {
          return Promise.resolve(cached.payload);
        }
      }
    } catch(_) {}
  }
  // The existing Google-Sheet fetch, unchanged — the OFF path AND the config reader's
  // fallback on any Supabase error/drift. Keeps the 5-min cache write byte-for-byte.
  var sheetFetch = function() {
    return fetch(backend, {
      method: "POST",
      headers: {"Content-Type": "text/plain;charset=utf-8"},
      body: JSON.stringify({action: "getComplianceConfig", site: site, testMode: cmpIsTestMode()})
    }).then(function(r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    }).then(function(d) {
      if (d && d.status === "error") throw new Error(d.message || "Config fetch error");
      try { localStorage.setItem(cacheKey, JSON.stringify({ts: nowMs, payload: d})); } catch(_) {}
      return d;
    });
  };
  // Lane BK config read-flip (gated OFF by default — COMPLIANCE_CONFIG_READ_HOSTS empty).
  // With the gate off this returns exactly the sheet fetch above (OFF path byte-identical).
  // ON: serve Supabase ONLY when it resolves to the same lists as the live Sheet (the
  // drift guard inside readComplianceConfig); on drift/error -> the Sheet. The returned
  // config is cached under the same 5-min key so the resolvers/screens are unchanged.
  // Lane BV: on an AUTHORITATIVE host (COMPLIANCE_CONFIG_AUTHORITATIVE_HOSTS / ?compconfigauth=on,
  // empty today) there is NO Sheet fallback — the Sheet copy goes stale once the Command Centre
  // stops writing it — so a Supabase failure rejects like a failed Sheet fetch does today (the
  // screens' existing catch -> "Couldn't load …" toast). Guarded hosts keep the Sheet fallback.
  if (complianceReader.complianceConfigReadEnabled()) {
    var fallback = complianceReader.complianceConfigAuthoritativeEnabled()
      ? function() { return Promise.reject(new Error("Config unavailable (this tablet reads the live record only, no backup fallback)")); }
      : sheetFetch;
    return complianceReader.readComplianceConfig({ site: site, testMode: cmpIsTestMode(), sheetFetch: sheetFetch })
      .then(function(cfg) {
        if (!cfg) return fallback();
        try { localStorage.setItem(cacheKey, JSON.stringify({ts: nowMs, payload: cfg})); } catch(_) {}
        return cfg;
      })
      .catch(function() { return fallback(); });
  }
  return sheetFetch();
}

// Resolves the "items" array for a given (surfaceKey, siteKey) from a
// getComplianceConfig payload. Falls back to '*' (global default) if
// no per-site override exists.
function cmpResolveSurfaceItems(config, surfaceKey, siteKey) {
  if (!config || !config.surfaces || !config.surfaces[surfaceKey]) return [];
  var sf = config.surfaces[surfaceKey];
  // Prefer the backend's normalized site key — kitchen frontend sends
  // `site:"mcr"` but backend normalises that to "kitchen" and keys
  // pest-areas under `kitchen`. Falling back to siteKey covers callers
  // that already have a normalized key.
  var lookup = (config.site || siteKey);
  var perSite = sf.sites && (sf.sites[lookup] || sf.sites[siteKey] || sf.sites["*"]);
  if (!perSite) return [];
  return (perSite.items || []).filter(function(i) { return (i.status || "active") === "active"; });
}

// Derives the Sunday-pill tone per JJ 2026-04-29:
//   - any day except Sunday → null (no pill)
//   - Sunday before 17:00  → "warn" (amber)
//   - Sunday from 17:00+   → "bad"  (red)
// Sunday hours 00:00–05:59 also return "warn" — the kiosk isn't trading
// then, but if a tablet is left on overnight the pill stays visible
// (rather than going amber→null→amber). Acceptable since trading
// doesn't start until ~10:00 anyway.
function cmpSundayPillTone(now) {
  var d = now || new Date();
  if (d.getDay() !== 0) return null;
  return d.getHours() >= 17 ? "bad" : "warn";
}

// ---- Compliance PIN adapter ----
// Wraps Trafford's requestPin(cb) so mockup-style components can use
// the {prompt, desc, requiredRoles, onConfirm, onCancel} shape. Looks
// up role from the staffByPin cache for client-side requiredRoles
// pre-check; the backend re-checks via canPerformAction on every POST.
function makeCmpPinPrompt(requestPin, staffByPin) {
  return function cmpPinPrompt(opts) {
    requestPin(async function(staffName, staffPin) {
      // Row H: on a gated host do NOT read the roster cache at all — the server
      // verify below is the only source of role/home. OFF path byte-identical.
      var info = pinAuth.serverPinVerifyEnabled() ? null : (staffByPin && staffByPin[staffPin]);
      var role = info && info.role ? String(info.role) : "";
      var homeSite = info && info.homeSite ? info.homeSite : "";
      // A1: with server PIN verify on, the roster isn't downloaded, so the cache
      // has no role/home for this PIN — ask the server (verify-only) for them.
      if (pinAuth.serverPinVerifyEnabled()) {
        try { var srv = await pinAuth.verifyPin(staffPin); if (srv && srv.ok) { role = String(srv.role || role); homeSite = srv.homeSite || homeSite; } } catch (e) {}
      }
      var roles = role.split(",").map(function(s) { return s.trim(); }).filter(Boolean);
      // BOH inherits FOH (mirrors backend canPerformAction). Admin
      // never auto-inherits anything else — mockup behaviour.
      var effRoles = roles.slice();
      if (effRoles.indexOf("BOH") !== -1 && effRoles.indexOf("FOH") === -1) effRoles.push("FOH");
      if (opts.requiredRoles && opts.requiredRoles.length) {
        var ok = opts.requiredRoles.some(function(r) { return effRoles.indexOf(r) !== -1; });
        if (!ok) {
          cmpToast(staffName + " (" + (role || "no role") + ") not authorised — needs " + opts.requiredRoles.join(" or "), "error");
          if (opts.onCancel) opts.onCancel();
          return;
        }
      }
      var staff = {
        pin: staffPin,
        name: staffName,
        role: role,
        roles: roles,
        initials: cmpInitials(staffName),
        home: homeSite
      };
      opts.onConfirm(staff);
    }, opts.onCancel || null);
  };
}

// ---- CmpFormModal — generic field modal lifted from mockup ----
// Fields: [{key, label, type:"text|textarea|select", placeholder?, options?, required?, hint?}]
function CmpFormModal({title, desc, fields, initial, submitLabel, onSubmit, onCancel}) {
  const [values, setValues] = useState(() => initial || Object.fromEntries(fields.map(f => [f.key, f.default || ""])));
  const submit = () => {
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      if (f.required && !String(values[f.key] || "").trim()) {
        cmpToast(f.label + " is required", "error");
        return;
      }
    }
    onSubmit(values);
  };
  return (
    <div className="cmp-form-modal-scrim" onClick={onCancel}>
      <div className="cmp-form-modal" onClick={e => e.stopPropagation()}>
        <div className="cmp-form-head">
          <h3>{title}</h3>
          {desc && <div className="cmp-form-desc">{desc}</div>}
        </div>
        <div className="cmp-form-inner">
          {fields.map(f => (
            <div key={f.key} className="cmp-field-block">
              <label>{f.label}{f.required && <span style={{color:"#ff8888"}}> *</span>}</label>
              {f.type === "select" ? (
                <select value={values[f.key] || ""} onChange={e => setValues(v => Object.assign({}, v, {[f.key]: e.target.value}))}>
                  {(f.options || []).map(o => {
                    const opt = (typeof o === "object") ? o : {value: o, label: o};
                    return <option key={opt.value} value={opt.value}>{opt.label}</option>;
                  })}
                </select>
              ) : f.type === "textarea" ? (
                <textarea value={values[f.key] || ""} onChange={e => setValues(v => Object.assign({}, v, {[f.key]: e.target.value}))} placeholder={f.placeholder || ""} rows={3} />
              ) : (
                <input type={f.type || "text"} value={values[f.key] || ""} onChange={e => setValues(v => Object.assign({}, v, {[f.key]: e.target.value}))} placeholder={f.placeholder || ""} />
              )}
              {f.hint && <span className="cmp-hint">{f.hint}</span>}
            </div>
          ))}
        </div>
        <div className="cmp-form-foot">
          <button className="cmp-form-cancel" onClick={onCancel}>Cancel</button>
          <button className="cmp-form-submit" onClick={submit}>{submitLabel || "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ---- Compliance Home (tile grid) ----
// Tile counts come from getComplianceOverview if admin; otherwise just
// renders the form entries. Staff-facing — shows what's available and
// the current Sunday pill state.
function CmpHome({nav, sundayTone}) {
  return (
    <div className="cmp-wrap">
      <div className="cmp-status-strip">
        <span className="cmp-label">Today</span>
        <span className="cmp-chip cmp-info"><span className="cmp-dot" />Compliance · foundation build</span>
        {sundayTone && (
          <span className={"cmp-chip cmp-" + sundayTone}><span className="cmp-dot" />
            Weekly Sign-Off Due
          </span>
        )}
      </div>
      <h2 style={{fontSize:22,marginBottom:10,marginTop:4,color:"var(--cm-nori)"}}>Compliance</h2>
      <div className="cmp-tile-list">
        <button className="cmp-tile-row" onClick={() => nav("cleaning")}>
          <div className="cmp-tile-icon">🧽</div>
          <div className="cmp-tile-body">
            <div className="cmp-tile-title">Weekly Cleaning Schedule</div>
            <div className="cmp-tile-meta">Tasks × days · sign off on Sunday</div>
          </div>
          <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
        </button>
        <button className="cmp-tile-row" onClick={() => nav("pest")}>
          <div className="cmp-tile-icon">🔍</div>
          <div className="cmp-tile-body">
            <div className="cmp-tile-title">Pest Daily Check</div>
            <div className="cmp-tile-meta">Per-area · droppings, damage, smells, sightings</div>
          </div>
          <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
        </button>
        <button className="cmp-tile-row" onClick={() => nav("corrective")}>
          <div className="cmp-tile-icon">🧯</div>
          <div className="cmp-tile-body">
            <div className="cmp-tile-title">Corrective Actions</div>
            <div className="cmp-tile-meta">Raise + resolve · stream from all forms</div>
          </div>
          <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
        </button>
      </div>
      <div className="cmp-card" style={{marginTop:24}}>
        <h4>More forms — coming soon</h4>
        <div className="cmp-sub" style={{marginBottom:0}}>HACCP Daily, Probe Calibration, Fish Freezing, Daily Temp Records, Emergency Fridge, Declaration of Competency &amp; FSMS Acceptance, Pest Monday Deep Sweep ship in the next session.</div>
      </div>
    </div>
  );
}

// ---- Cleaning Schedule (matrix + weekly sign-off) ----
// PIN gate reused for every cell tap. Yes-state turns the cell green
// (state="done"); No-state turns it grey (state="skipped") but still
// records who attempted. Admin-only "remove sign-off" flow re-triggers
// PIN with requiredRoles:["Admin"].
function CmpCleaningSchedule({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [config, setConfig] = useState(null);
  const [matrix, setMatrix] = useState({});  // {taskIdx: {dayIdx: {state, init}}}
  const [weekSig, setWeekSig] = useState(null);
  const [pendingCell, setPendingCell] = useState(null);
  const [weekStart, setWeekStart] = useState(() => cmpMondayOf(new Date()));
  const todayIdx = (new Date().getDay() + 6) % 7;
  const sundayTone = cmpSundayPillTone();
  const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const dayDates = days.map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const weekRangeLabel = (function(){
    const a = dayDates[0].toLocaleDateString("en-GB",{day:"numeric",month:"short"});
    const b = dayDates[6].toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
    return a + " – " + b;
  })();

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(setConfig)
      .catch(e => cmpToast("Couldn't load cleaning tasks: " + e.message, "error"));
  }, [backend, site.key]);

  const tasks = useMemo(() => {
    if (!config) return [];
    return cmpResolveSurfaceItems(config, "cleaning-tasks", site.key);
  }, [config, site.key]);

  // matrix is local-only this session; backend writes happen but reads
  // for prior-day state defer to session 2 (needs getComplianceOverview
  // pivoted by week). For now the in-memory matrix represents this
  // session's actions; refreshing the page resets the visual state but
  // the backend rows remain. Documented limitation, called out in QA.
  const cellState = (taskIdx, dayIdx) => {
    if (matrix[taskIdx] && matrix[taskIdx][dayIdx]) return matrix[taskIdx][dayIdx];
    return {state: "empty"};
  };

  const writeCell = (taskIdx, dayIdx, state, staff) => {
    const taskName = tasks[taskIdx] && tasks[taskIdx].label;
    cmpComplianceLog(backend, site.key, "cleaning-cell", {
      weekStart: weekStart, taskIndex: taskIdx, dayIndex: dayIdx,
      taskName: taskName, state: state
    }, staff.pin).then(() => {
      setMatrix(m => Object.assign({}, m, {
        [taskIdx]: Object.assign({}, m[taskIdx] || {}, {
          [dayIdx]: {state: state, init: staff.initials}
        })
      }));
      cmpToast(state === "done" ? "Cell marked complete" : state === "skipped" ? "Cell marked not completed" : "Cell sign-off removed", state === "skipped" ? "warn" : "success");
    }).catch(e => cmpToast("Save failed: " + e.message, "error"));
  };

  const onCellClick = (taskIdx, dayIdx) => {
    const cur = cellState(taskIdx, dayIdx);
    if (cur.state === "done" || cur.state === "skipped") {
      // Admin-remove flow.
      pinPrompt({
        prompt: "Remove cleaning sign-off",
        requiredRoles: ["Admin"],
        onConfirm: (s) => writeCell(taskIdx, dayIdx, "empty", s)
      });
    } else {
      setPendingCell({taskIdx, dayIdx});
    }
  };

  const completeCell = (yes) => {
    const {taskIdx, dayIdx} = pendingCell;
    setPendingCell(null);
    const taskName = tasks[taskIdx] && tasks[taskIdx].label;
    pinPrompt({
      prompt: yes ? "PIN — Task Completed" : "PIN — Task Not Completed",
      desc: taskName + " · " + days[dayIdx],
      onConfirm: (s) => writeCell(taskIdx, dayIdx, yes ? "done" : "skipped", s),
      onCancel: () => setPendingCell({taskIdx, dayIdx})
    });
  };

  const weekSign = () => {
    pinPrompt({
      prompt: "Weekly Manager Sign Off",
      desc: "Confirms the week's cleaning has been reviewed. Any signed-in staff PIN works.",
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "cleaning-weekly-signoff", {
          weekStart: weekStart
        }, s.pin).then(() => {
          setWeekSig({init: s.initials, name: s.name, ts: new Date().toISOString()});
          cmpToast("Week signed off by " + s.name, "success");
        }).catch(e => cmpToast("Sign-off failed: " + e.message, "error"));
      }
    });
  };

  if (!config) return <div className="cmp-loading">Loading cleaning schedule…</div>;
  if (!tasks.length) return <div className="cmp-empty">No cleaning tasks configured for this site yet. Ask admin to add some.</div>;

  return (
    <div className="cmp-wrap">
      {sundayTone && !weekSig && (
        <div className={"cmp-card cmp-" + (sundayTone === "bad" ? "coral" : "amber")}>
          <h4 style={{color:"var(--cm-" + (sundayTone === "bad" ? "coral" : "amber") + ")",marginBottom:4}}>Sunday — Sign-Off Required</h4>
          <div style={{fontSize:13,color:"var(--cm-nori-70)"}}>Last day of this cleaning week. Any signed-in staff can sign the week off below before close.</div>
        </div>
      )}
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div style={{flex:1,minWidth:0}}>
            <h3>Weekly Cleaning Schedule</h3>
            <div className="cmp-sub">Week of <strong>{weekRangeLabel}</strong>. Tap a cell to mark the task Completed (green) or Not completed (grey). Both record initials. Sign the week off on Sunday.</div>
          </div>
          <span style={{fontSize:11,letterSpacing:"0.08em",textTransform:"uppercase",padding:"6px 12px",borderRadius:6,background:"var(--cm-wasabi-20)",color:"var(--cm-nori)",fontWeight:800,whiteSpace:"nowrap"}}>📅 {weekRangeLabel}</span>
        </div>
      </div>
      {weekSig && (
        <div className="cmp-card cmp-green">
          <h4 style={{color:"var(--cm-green)",marginBottom:4}}>✓ Weekly Sign Off Complete</h4>
          <div style={{fontSize:13,color:"var(--cm-nori-70)"}}>Signed by <strong>{weekSig.name}</strong> at <strong>{new Date(weekSig.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</strong>.</div>
        </div>
      )}
      <div className="cmp-card cmp-matrix-scroll" style={{padding:"4px 8px"}}>
        <table className="cmp-matrix">
          <thead><tr>
            <th className="cmp-task">Task</th>
            {days.map((d, i) => (
              <th key={d} style={i === todayIdx ? {color:"var(--cm-nori)"} : null}>
                <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.06em"}}>{d.toUpperCase()}</div>
                <div style={{fontSize:9,fontWeight:700,opacity:0.65,marginTop:2}}>{dayDates[i].toLocaleDateString("en-GB",{day:"numeric",month:"short"}).toUpperCase()}</div>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {tasks.map((t, i) => (
              <tr key={t.id || i}>
                <td className="cmp-task">
                  <div className="cmp-tname">{t.label}</div>
                </td>
                {days.map((_, d) => {
                  const cell = cellState(i, d);
                  const isToday = d === todayIdx;
                  const tooltip = cell.state === "done" ? "Done by " + cell.init : cell.state === "skipped" ? "Not completed (signed by " + cell.init + ")" : "Tap to initial";
                  return (
                    <td key={d}>
                      <button
                        className={"cmp-cell-btn " + (cell.state === "done" ? "cmp-done" : cell.state === "skipped" ? "cmp-skipped" : "") + (isToday ? " cmp-today" : "")}
                        onClick={() => onCellClick(i, d)}
                        title={tooltip}
                      >
                        {(cell.state === "done" || cell.state === "skipped") ? cell.init : "·"}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:12,alignItems:"center"}}>
        {!weekSig && <button className="cmp-btn cmp-btn-primary" onClick={weekSign}>Weekly Manager Sign Off (PIN required)</button>}
        <span style={{fontSize:12,color:"var(--cm-nori-60)"}}>Any signed-in staff PIN works. Sign off on Sunday. Unsigned weeks raise an admin alert.</span>
      </div>
      <div className="cmp-card" style={{background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",marginTop:12}}>
        <div style={{fontSize:13,lineHeight:1.5,color:"var(--cm-nori-70)"}}>
          <strong>Cell colours:</strong> 🟢 green = completed · ⚪ grey = not completed (initials still captured) · empty = not yet recorded.
        </div>
      </div>
      {pendingCell && (
        <div className="cmp-confirm-scrim" onClick={() => setPendingCell(null)}>
          <div className="cmp-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{color:"var(--cm-wasabi)",marginBottom:8}}>Was the task completed?</h3>
            <div style={{color:"rgba(254,250,240,0.75)",fontSize:13,marginBottom:20,lineHeight:1.4}}>
              {tasks[pendingCell.taskIdx].label} · {days[pendingCell.dayIdx]}
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
              <button onClick={() => completeCell(false)} style={{padding:"14px 22px",borderRadius:10,background:"#7a7a7a",color:"#fff",fontWeight:800,fontSize:14,border:"none",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>No, not completed</button>
              <button onClick={() => completeCell(true)} style={{padding:"14px 22px",borderRadius:10,background:"var(--cm-wasabi)",color:"var(--cm-nori)",fontWeight:800,fontSize:14,border:"none",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Yes, completed</button>
            </div>
            <div style={{fontSize:11,color:"rgba(254,250,240,0.55)",marginTop:12}}>Both capture your PIN. No still records who attempted it (cell turns grey, not green).</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Pest Daily Check (single-area form) ----
// Per-site areas come from getComplianceConfig surfaces["pest-areas"].
// Yes-evidence requires details + corrective action and auto-raises a
// CorrectiveActions row server-side.
function CmpPestDaily({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [config, setConfig] = useState(null);
  const [newRow, setNewRow] = useState({area: "", evidence: "No", details: "", action: ""});
  const [todayRows, setTodayRows] = useState([]);

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        setConfig(c);
        const areas = cmpResolveSurfaceItems(c, "pest-areas", site.key);
        if (areas.length && !newRow.area) {
          setNewRow(r => Object.assign({}, r, {area: areas[0].label}));
        }
      })
      .catch(e => cmpToast("Couldn't load pest areas: " + e.message, "error"));
  }, [backend, site.key]);

  const areas = useMemo(() => config ? cmpResolveSurfaceItems(config, "pest-areas", site.key) : [], [config, site.key]);

  const submit = () => {
    if (newRow.evidence === "Yes" && (!newRow.details.trim() || !newRow.action.trim())) {
      cmpToast("Yes-evidence requires details + corrective action", "error");
      return;
    }
    if (!newRow.area) {
      cmpToast("Pick an area", "error");
      return;
    }
    pinPrompt({
      prompt: "Sign off pest check",
      desc: "Your PIN stamps this check. Yes-rows raise a corrective action automatically.",
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "pest-daily", {
          area: newRow.area, evidence: newRow.evidence,
          details: newRow.details, action: newRow.action
        }, staff.pin).then((d) => {
          setTodayRows(r => [{
            ts: new Date().toISOString(), area: newRow.area, evidence: newRow.evidence,
            details: newRow.details, action: newRow.action, init: staff.initials,
            incidentId: d && d.incidentId ? d.incidentId : ""
          }, ...r]);
          setNewRow({area: (areas[0] && areas[0].label) || "", evidence: "No", details: "", action: ""});
          if (newRow.evidence === "Yes") {
            cmpToast("Yes-row submitted · corrective action raised", "warn");
          } else {
            cmpToast("Pest check logged", "success");
          }
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      }
    });
  };

  if (!config) return <div className="cmp-loading">Loading pest areas…</div>;
  if (!areas.length) return <div className="cmp-empty">No pest areas configured for this site yet. Ask admin to add some.</div>;

  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Daily Pest Visual Check</h3>
        <div className="cmp-sub">Check each area listed for droppings, damage, smells, sightings.</div>
        <div className="cmp-card" style={{background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",marginTop:12,marginBottom:0,padding:"12px 14px"}}>
          <h4 style={{fontSize:14,marginBottom:8}}>What to Look For</h4>
          <div style={{padding:"4px 0"}}><strong style={{color:"var(--cm-nori)",fontSize:13}}>Droppings:</strong> <span style={{fontSize:12,color:"var(--cm-nori-70)"}}>Small, dark pellets.</span></div>
          <div style={{padding:"4px 0",borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)",fontSize:13}}>Damage:</strong> <span style={{fontSize:12,color:"var(--cm-nori-70)"}}>Gnaw marks on packaging, cables, or structure.</span></div>
          <div style={{padding:"4px 0",borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)",fontSize:13}}>Smells:</strong> <span style={{fontSize:12,color:"var(--cm-nori-70)"}}>Unusual or ammonia-like odours.</span></div>
          <div style={{padding:"4px 0",borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)",fontSize:13}}>Sightings:</strong> <span style={{fontSize:12,color:"var(--cm-nori-70)"}}>Live or dead insects/rodents.</span></div>
          <div style={{padding:"4px 0",borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)",fontSize:13}}>Proofing:</strong> <span style={{fontSize:12,color:"var(--cm-nori-70)"}}>Any new holes or gaps in the walls/floors.</span></div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12,marginTop:12,alignItems:"start"}}>
          <div className="cmp-field">
            <label>Area Checked</label>
            <select value={newRow.area} onChange={e => setNewRow(r => Object.assign({}, r, {area: e.target.value}))} style={{height:42}}>
              {areas.map(a => <option key={a.id || a.label} value={a.label}>{a.label}</option>)}
            </select>
          </div>
          <div className="cmp-field">
            <label>Evidence of Pests?</label>
            <select value={newRow.evidence} onChange={e => setNewRow(r => Object.assign({}, r, {evidence: e.target.value}))} style={{height:42}}>
              <option>No</option><option>Yes</option>
            </select>
          </div>
        </div>
        {newRow.evidence === "Yes" && (
          <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:12}}>
            <div className="cmp-field cmp-warn">
              <label>Details (required)</label>
              <textarea value={newRow.details} onChange={e => setNewRow(r => Object.assign({}, r, {details: e.target.value}))} placeholder="Droppings? Damage? Smell? Location?" />
            </div>
            <div className="cmp-field cmp-warn">
              <label>Corrective action (required)</label>
              <textarea value={newRow.action} onChange={e => setNewRow(r => Object.assign({}, r, {action: e.target.value}))} placeholder="What did you do? Who else notified?" />
              <span className="cmp-hint" style={{color:"var(--cm-coral)",fontWeight:700}}>⚠ Yes-row raises a Corrective Action automatically</span>
            </div>
          </div>
        )}
        <div style={{marginTop:12}}>
          <button className="cmp-btn cmp-btn-primary" onClick={submit}>Submit Check (PIN required)</button>
        </div>
      </div>
      {todayRows.length > 0 && (
        <div className="cmp-card">
          <h3>This Session</h3>
          <table className="cmp-row-table">
            <thead><tr><th>Time</th><th>Area</th><th>Evidence</th><th>Details</th><th>Action</th><th>By</th></tr></thead>
            <tbody>
              {todayRows.map((r, i) => (
                <tr key={i} style={r.evidence === "Yes" ? {background:"var(--cm-coral-soft)"} : null}>
                  <td style={{fontWeight:700}}>{new Date(r.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</td>
                  <td>{r.area}</td>
                  <td><span className={"cmp-badge " + (r.evidence === "Yes" ? "cmp-bad" : "cmp-ok")}>{r.evidence}</span></td>
                  <td style={{fontSize:12}}>{r.details || <span style={{color:"var(--cm-nori-60)"}}>—</span>}</td>
                  <td style={{fontSize:12}}>{r.action || <span style={{color:"var(--cm-nori-60)"}}>—</span>}</td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{r.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Corrective Actions (raise + resolve) ----
// Session 1: only the raise + resolve flow. The "stream from all
// forms" view defers to Compliance Overview admin tile.
function CmpCorrectiveActions({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("open");
  const [formOpen, setFormOpen] = useState(false);
  const [resolveFor, setResolveFor] = useState(null);

  const submitRaise = (vals) => {
    setFormOpen(false);
    pinPrompt({
      prompt: "Sign off corrective action",
      desc: "Confirm with your PIN to log this concern.",
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "corrective-raise", {
          description: vals.description, action: vals.action
        }, s.pin).then(d => {
          setItems(arr => [{
            id: d && d.incidentId ? d.incidentId : "(pending)",
            source: "Ad hoc raise", site: site.key, ts: new Date().toISOString(),
            desc: vals.description, action: vals.action,
            flagged: s.name, status: "open"
          }, ...arr]);
          cmpToast("Corrective action raised", "warn");
        }).catch(e => cmpToast("Raise failed: " + e.message, "error"));
      },
      onCancel: () => setFormOpen(true)
    });
  };

  const submitResolve = (vals) => {
    const id = resolveFor;
    setResolveFor(null);
    pinPrompt({
      prompt: "Sign off resolution",
      desc: "Confirm with your PIN to close " + id + ".",
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "corrective-resolve", {
          incidentId: id, resolutionAction: vals.resolution
        }, s.pin).then(() => {
          setItems(arr => arr.map(x => x.id === id ? Object.assign({}, x, {
            status: "resolved", resolvedBy: s.name, resolvedAt: new Date().toISOString(),
            resolutionAction: vals.resolution
          }) : x));
          cmpToast("Action resolved by " + s.name, "success");
        }).catch(e => cmpToast("Resolve failed: " + e.message, "error"));
      },
      onCancel: () => setResolveFor(id)
    });
  };

  const filtered = items.filter(i => tab === "all" || i.status === tab);

  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div>
            <h3>Corrective Actions</h3>
            <div className="cmp-sub">Raise a concern, sign with your PIN. Resolved actions stay on file.</div>
          </div>
          <button className="cmp-btn cmp-btn-danger" onClick={() => setFormOpen(true)}>+ Raise concern</button>
        </div>
        <div className="cmp-tabs" style={{marginTop:12}}>
          {["open","resolved","all"].map(t => (
            <button key={t} className={tab === t ? "cmp-active" : ""} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)} ({items.filter(i => t === "all" || i.status === t).length})
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 && (
        <div className="cmp-empty">
          {tab === "open"     ? "No open actions in this session. Anything raised here will appear above."
           : tab === "resolved" ? "Nothing resolved yet in this session."
           : "Nothing logged yet in this session. Raise a concern above to start."}
        </div>
      )}
      {filtered.map(i => (
        <div key={i.id} className={"cmp-ca-row cmp-" + i.status}>
          <div className={"cmp-pill cmp-" + i.status}>{i.status === "open" ? "!" : "✓"}</div>
          <div className="cmp-body">
            <div className="cmp-desc">{i.desc}</div>
            <div className="cmp-meta">
              <strong style={{color:"var(--cm-nori)"}}>{i.id}</strong> · {i.source} · {cmpFriendly(i.ts)} · flagged by {i.flagged}
              {i.resolvedBy && <span> · <span style={{color:"var(--cm-green)"}}>resolved by {i.resolvedBy}</span></span>}
            </div>
            <div style={{fontSize:12,marginTop:8,lineHeight:1.45}}>{i.action}</div>
            {i.resolutionAction && (
              <div style={{fontSize:12,marginTop:8,lineHeight:1.45,padding:"8px 10px",background:"var(--cm-green-soft)",borderRadius:6,borderLeft:"3px solid var(--cm-green)"}}>
                <strong style={{color:"var(--cm-green)"}}>Resolution:</strong> {i.resolutionAction}
              </div>
            )}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
            <span className={"cmp-chip " + (i.status === "open" ? "cmp-bad" : "cmp-ok")}><span className="cmp-dot" />{i.status}</span>
            {i.status === "open" && <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => setResolveFor(i.id)}>Mark resolved</button>}
          </div>
        </div>
      ))}
      {formOpen && (
        <CmpFormModal
          title="Raise corrective action"
          desc="Describe the issue and the action you're taking. You'll confirm with your PIN on the next step."
          fields={[
            {key: "description", label: "Describe the issue", type: "textarea", placeholder: "What did you spot? Where? When?", required: true},
            {key: "action", label: "Action you're taking", type: "textarea", placeholder: "What have you done / will do to fix it?", required: true}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitRaise}
          onCancel={() => setFormOpen(false)}
        />
      )}
      {resolveFor && (
        <CmpFormModal
          title="Resolve corrective action"
          desc={"Closing " + resolveFor + ". Capture the corrective action taken before signing — required."}
          fields={[
            {key: "resolution", label: "Corrective action taken", type: "textarea", placeholder: "What was done to close this issue?", required: true, hint: "Logged on the row and on the audit trail with your PIN signature."}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitResolve}
          onCancel={() => setResolveFor(null)}
        />
      )}
    </div>
  );
}

// ---- ComplianceModule shell — handles internal nav between sub-pages ----
function ComplianceModule({site, backend, requestPin, staffByPin}) {
  const [view, setView] = useState("home");
  const sundayTone = cmpSundayPillTone();
  // Persist sub-page across refresh so the back-button journey feels right.
  useEffect(() => {
    try {
      const v = sessionStorage.getItem("cmpView");
      if (v && ["home","cleaning","pest","corrective"].indexOf(v) !== -1) setView(v);
    } catch(_) {}
  }, []);
  useEffect(() => {
    try { sessionStorage.setItem("cmpView", view); } catch(_) {}
    window.scrollTo({top: 0, left: 0, behavior: "auto"});
  }, [view]);

  let body;
  if (view === "cleaning")        body = <CmpCleaningSchedule backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "pest")       body = <CmpPestDaily       backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "corrective") body = <CmpCorrectiveActions backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else                            body = <CmpHome nav={setView} sundayTone={sundayTone} />;

  return (
    <div className="cmp-root">
      {view !== "home" && (
        <div className="cmp-wrap" style={{paddingTop:0,paddingBottom:0,marginBottom:8}}>
          <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => setView("home")}>← Compliance home</button>
        </div>
      )}
      {body}
      <CmpToastLayer />
    </div>
  );
}


// ===========================================
// INGREDIENT WASTAGE — Meadowhall ONLY (2026-08-23)
// ===========================================
// Daily In / Used / Wasted grams per protein (the "Fish Tracker"). Supabase is
// the SOURCE OF TRUTH (ingredient_wastage_daily, migration 0620) — so unlike the
// best-effort shadows, the save result is SURFACED, and the tile PREFILLS today's
// saved row on open so a partial re-save can never wipe the morning's figures
// (if that prefill read fails, Save stays disabled). Grams are entered on a
// numpad (the app's ±1 stepper is for roll counts, useless for 4,000g). Save is
// PIN-gated like every other write. Ingredient list is v1-hardcoded (Salmon +
// Tuna), admin-managed later — the same posture as the kitchen INGREDIENTS list.
const INGREDIENT_WASTE_ITEMS = [
  { name: "Salmon", icon: "🐟" },
  { name: "Tuna",   icon: "🐟" },
];
const IW_FIELD_LABEL = { in: "In", used: "Used", wasted: "Wasted" };

function IngredientWastage({ site, requestPin, setDirty }) {
  const siteKey = site.key;
  const [entries, setEntries]   = useState({});          // { Salmon:{in,used,wasted}, ... } grams
  const [loadState, setLoad]    = useState("loading");   // loading | ready | preview | error
  const [touched, setTouched]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [status, setStatus]     = useState(null);        // {type:'ok'|'error'|'preview', msg?}
  const [edit, setEdit]         = useState(null);        // {ingredient, field, val:string}
  const savingRef = useRef(false);
  const dirtyRef  = useRef(new Set());                    // "ingredient|field" cells edited this session (drives merge-on-save)

  useEffect(() => { setDirty(touched); }, [touched, setDirty]);

  const load = useCallback(async () => {
    setLoad("loading"); setStatus(null);
    const res = await ingredientWastage.readIngredientWastage({ siteKey });
    if (res && res.skipped) { setLoad("preview"); return; }
    if (!res || !res.ok)    { setLoad("error");   return; }
    setEntries(res.rows || {}); setLoad("ready");
  }, [siteKey]);

  useEffect(() => { let alive = true; (async () => { if (alive) await load(); })(); return () => { alive = false; }; }, [load]);

  const g   = (ing, f) => { const r = entries[ing]; return r ? r[f] : undefined; };
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-GB"));
  const canEdit = loadState === "ready" || loadState === "preview";
  const hasAny  = INGREDIENT_WASTE_ITEMS.some(it => { const r = entries[it.name]; return r && ((r.in || 0) + (r.used || 0) + (r.wasted || 0)) > 0; });
  const anyError = INGREDIENT_WASTE_ITEMS.some(it => ingredientWastage.ingredientWastageError(entries[it.name]));
  // EVERY ingredient has a row and all figures are 0 → a zero-waste day is fully
  // logged (distinct from a never-logged day, where entries is {}). `.every` (not
  // `.some`) so the "logged" banner never over-claims when only one fish is done.
  const zeroLoggedToday = loadState === "ready" && !hasAny && INGREDIENT_WASTE_ITEMS.every(it => entries[it.name] != null);
  // The fresh, nothing-logged, nothing-typed state — offer the one-tap zero-day
  // action here instead of a dead greyed Save (what read as "won't go through").
  const showZeroDay = canEdit && !hasAny && !touched && !zeroLoggedToday;

  const openEdit = (ingredient, field) => { if (!canEdit) return; const cur = g(ingredient, field); setEdit({ ingredient, field, val: cur == null ? "" : String(cur) }); };
  const pushDigit = (d) => setEdit(e => e && (e.val.length >= 6 ? e : { ...e, val: (e.val === "0" ? "" : e.val) + d }));
  const commitEdit = () => {
    setEdit(e => {
      if (!e) return null;
      const v = e.val === "" ? 0 : parseInt(e.val, 10);
      setEntries(prev => ({ ...prev, [e.ingredient]: { ...(prev[e.ingredient] || {}), [e.field]: v } }));
      dirtyRef.current.add(e.ingredient + "|" + e.field);
      setTouched(true);
      return null;
    });
  };

  // Apply a save/zero-day result: reflect the authoritative rows, then reset.
  const applyResult = (res) => {
    if (res && res.allLogged) {
      // A real log already existed for every ingredient (cross-device race) —
      // nothing of ours written. Reflect the truth from the returned rows and say
      // so, rather than a false "saved". No load() here — its setStatus(null) would
      // swallow this banner; the rows come back with the result instead.
      if (Array.isArray(res.data)) {
        setEntries(prev => {
          const next = { ...prev };
          res.data.forEach(r => { next[r.ingredient] = { in: r.in_g || 0, used: r.used_g || 0, wasted: r.wasted_g || 0 }; });
          return next;
        });
      }
      dirtyRef.current = new Set(); setTouched(false); setStatus({ type: "exists" });
      return;
    }
    if (res && res.skipped)   { setStatus({ type: "preview" }); return; }
    if (res && res.ok) {
      // Reflect the authoritative saved values (the merge may have pulled in
      // fresh figures for fields this session didn't touch), then reset.
      if (Array.isArray(res.data)) {
        setEntries(prev => {
          const next = { ...prev };
          res.data.forEach(r => { next[r.ingredient] = { in: r.in_g || 0, used: r.used_g || 0, wasted: r.wasted_g || 0 }; });
          return next;
        });
      }
      dirtyRef.current = new Set();
      setStatus({ type: "ok" }); setTouched(false);
      return;
    }
    setStatus({ type: "error", msg: (res && typeof res.error === "string") ? res.error : null });
  };

  // Shared PIN-gated runner for both a figures save and the one-tap zero day.
  const runSave = (call) => {
    requestPin(async (_staffName, staffPin) => {
      if (savingRef.current) return;
      savingRef.current = true; setSaving(true); setStatus(null);
      try { applyResult(await call(staffPin)); }
      finally { savingRef.current = false; setSaving(false); }
    });
  };

  const save = () => {
    if ((!hasAny && !touched) || saving || anyError || loadState === "loading" || loadState === "error") return;
    runSave((staffPin) => ingredientWastage.recordIngredientWastage({ siteKey, staffPin, entries, dirtyFields: [...dirtyRef.current] }));
  };

  // "Nothing wasted today" — one tap logs an explicit 0/0/0 for every ingredient
  // that has nothing real logged yet (the sync layer never overwrites a weighed row).
  const logZeroDay = () => {
    if (saving || !canEdit) return;
    runSave((staffPin) => ingredientWastage.recordZeroWastageDay({ siteKey, staffPin, ingredients: INGREDIENT_WASTE_ITEMS.map(i => i.name) }));
  };

  const metric = (it, field, waste, invalid) => {
    const val = g(it.name, field);
    const base = waste
      ? { bg: "rgba(255,122,92,0.05)", bd: "rgba(255,122,92,0.22)", lc: B.coral, vc: "#ffb39e" }
      : { bg: "rgba(254,250,240,0.03)", bd: "rgba(212,251,111,0.09)", lc: "rgba(254,250,240,0.4)", vc: B.kewpie };
    // A figure that breaks the In >= Used / In >= Wasted rule is flagged in Coral.
    if (invalid) { base.bg = "rgba(255,122,92,0.14)"; base.bd = B.coral; base.lc = B.coral; base.vc = B.coral; }
    return (
      <button onClick={() => openEdit(it.name, field)} disabled={!canEdit}
        style={{ textAlign: "left", background: base.bg, border: "1px solid " + base.bd, borderRadius: 11, padding: "10px 12px", cursor: canEdit ? "pointer" : "default", fontFamily: "Manrope,sans-serif" }}>
        <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: base.lc, marginBottom: 5 }}>{IW_FIELD_LABEL[field]}</div>
        <div style={{ fontFamily: "gelica,serif", fontSize: "1.3rem", fontWeight: 700, color: val == null ? "rgba(254,250,240,0.28)" : base.vc }}>
          {fmt(val)}<span style={{ fontFamily: "Manrope,sans-serif", fontSize: "0.7rem", opacity: 0.4, marginLeft: 3 }}>g</span>
        </div>
      </button>
    );
  };

  return (
    <div style={S.content}>
      <div style={S.sectionCard}>
        <div style={S.secLabel}>Ingredient Wastage</div>
        <div style={S.secDesc}>{site.name} · today's In, Used &amp; Wasted in grams. Tap any figure to edit.</div>
      </div>

      {loadState === "loading" && <div style={S.emptyState}>Loading today's figures…</div>}

      {loadState === "error" && (
        <div style={{ ...S.infoBanner, borderColor: "rgba(255,122,92,0.25)", background: "rgba(255,122,92,0.06)", color: B.coral, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <span>Couldn't load today's saved figures. Saving is disabled until this loads (so a save can't overwrite the day blindly).</span>
          <button style={{ ...S.markBtn, flexShrink: 0 }} onClick={load}>Retry</button>
        </div>
      )}

      {loadState === "preview" && (
        <div style={{ ...S.infoBanner, borderColor: "rgba(241,197,68,0.25)", background: "rgba(241,197,68,0.05)", color: B.sunshine }}>
          Preview mode — saving is off on this host. Open on the live {site.name} tablet (or add <b>?dualrun=on</b>) to record for real.
        </div>
      )}

      {canEdit && (<>
        <div style={{ ...S.infoBanner, borderColor: "rgba(212,251,111,0.15)", background: "rgba(212,251,111,0.04)", color: B.kewpie }}>
          <b style={{ color: B.wasabi }}>Weigh the bin, don't guess.</b> Only <b>Wasted</b> feeds the wastage report — In &amp; Used give it context and true yield.
        </div>

        {INGREDIENT_WASTE_ITEMS.map(it => {
          const err = ingredientWastage.ingredientWastageError(entries[it.name]);
          return (
            <div key={it.name} style={{ ...S.sectionCard, marginBottom: 12, ...(err ? { borderColor: "rgba(255,122,92,0.45)" } : {}) }}>
              <div style={{ fontFamily: "gelica,serif", fontSize: "1.1rem", fontWeight: 700, color: B.wasabi, display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
                <span style={{ fontSize: "1.2rem" }}>{it.icon}</span>{it.name}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                {metric(it, "in", false, false)}
                {metric(it, "used", false, err && err.field === "used")}
                {metric(it, "wasted", true, err && err.field === "wasted")}
              </div>
              {err && <div style={{ marginTop: 8, fontSize: "0.74rem", fontWeight: 700, color: B.coral, display: "flex", alignItems: "center", gap: 6 }}>⚠ {err.msg} ({fmt(g(it.name, "in"))}g in).</div>}
            </div>
          );
        })}

        {status && status.type === "ok" && !zeroLoggedToday && <div style={{ ...S.infoBanner, borderColor: "rgba(111,175,143,0.3)", background: "rgba(111,175,143,0.08)", color: B.eucalyptus }}>✓ Saved — synced to Command Centre.</div>}
        {status && status.type === "exists"  && <div style={{ ...S.infoBanner, borderColor: "rgba(241,197,68,0.25)", background: "rgba(241,197,68,0.05)", color: B.sunshine }}>Today's figures were already logged on another device — refreshed to show them.</div>}
        {status && status.type === "error" && status.msg === "invalid" && <div style={{ ...S.infoBanner, borderColor: "rgba(255,122,92,0.3)", background: "rgba(255,122,92,0.08)", color: B.coral }}>A figure came out higher than In — reopen the flagged ingredient and check. Nothing was saved.</div>}
        {status && status.type === "error" && status.msg !== "invalid" && <div style={{ ...S.infoBanner, borderColor: "rgba(255,122,92,0.3)", background: "rgba(255,122,92,0.08)", color: B.coral }}>Couldn't save{status.msg === "pin" ? " — PIN not recognised" : ""}. Nothing was recorded — check the connection and try again.</div>}
        {status && status.type === "preview" && <div style={{ ...S.infoBanner, borderColor: "rgba(241,197,68,0.25)", background: "rgba(241,197,68,0.05)", color: B.sunshine }}>Preview only — not saved. Use the live {site.name} tablet to record.</div>}

        {/* A zero-waste day is the North Star — it must be loggable in one tap, not
            blocked behind an all-zero guard. Offered only in the fresh, nothing-
            entered state; the moment a real figure is typed, the normal Save takes over. */}
        {showZeroDay ? (<>
          <button style={{ ...S.submitBtn, marginTop: 12, background: "transparent", color: B.eucalyptus, border: "1.5px solid rgba(111,175,143,0.55)", ...(saving ? { opacity: 0.5, cursor: "default" } : {}) }} onClick={logZeroDay} disabled={saving}>
            {saving ? "Saving…" : "✓ Log a zero-waste day"}
          </button>
          <div style={{ textAlign: "center", fontSize: "0.72rem", color: B.kewpie, opacity: 0.45, marginTop: 12, lineHeight: 1.5 }}>Checked the bins and there's nothing to report? Log the day as zero — or tap a figure above to enter real grams.</div>
        </>) : (zeroLoggedToday && !touched) ? (
          // A completed zero-waste day: show the standing confirmation only — no
          // greyed Save under a "done" message (that dead control was the very
          // thing that read as "won't go through"). Tapping a figure re-enables Save.
          <div style={{ ...S.infoBanner, marginTop: 12, borderColor: "rgba(111,175,143,0.3)", background: "rgba(111,175,143,0.08)", color: B.eucalyptus }}>✓ Zero waste logged for today. Tap a figure above if you need to add any waste.</div>
        ) : (
          <button style={{ ...S.submitBtn, marginTop: 12, ...(((!hasAny && !touched) || anyError) ? { opacity: 0.3, cursor: "default" } : {}) }} onClick={save} disabled={(!hasAny && !touched) || saving || anyError}>
            {saving ? "Saving…" : anyError ? "Fix the flagged figures to save" : "Save today's log"}
          </button>
        )}
        {/* Shared per-action note under the zero-day / Save action; hidden in the
            completed-zero state where there is no action to take. */}
        {!(zeroLoggedToday && !touched) && <div style={{ textAlign: "center", fontSize: "0.72rem", color: B.kewpie, opacity: 0.35, marginTop: 12, lineHeight: 1.5 }}>Per-action PIN required · saves who logged it, and when.</div>}
      </>)}

      {edit && (
        <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) setEdit(null); }}>
          <div style={{ ...S.modal, maxWidth: 360 }}>
            <div style={S.modalTitle}>{edit.ingredient} · {IW_FIELD_LABEL[edit.field]}</div>
            <div style={S.modalDesc}>Enter the weight in grams</div>
            <div style={{ background: "rgba(254,250,240,0.05)", border: "1px solid rgba(212,251,111,0.15)", borderRadius: 12, padding: 14, textAlign: "center", fontFamily: "gelica,serif", fontSize: "2rem", fontWeight: 700, color: B.kewpie, marginBottom: 12 }}>
              {edit.val === "" ? "0" : Number(edit.val).toLocaleString("en-GB")}<span style={{ fontFamily: "Manrope,sans-serif", fontSize: "1rem", opacity: 0.4, marginLeft: 4 }}>g</span>
            </div>
            <div style={S.pad}>
              {["1","2","3","4","5","6","7","8","9"].map(d => (<button key={d} style={S.key} onClick={() => pushDigit(d)}>{d}</button>))}
              <button style={{ ...S.key, background: "transparent", color: "rgba(212,251,111,0.55)", fontSize: "0.85rem" }} onClick={() => setEdit(e => e && { ...e, val: "" })}>Clear</button>
              <button style={S.key} onClick={() => pushDigit("0")}>0</button>
              <button style={{ ...S.key, background: "transparent", color: "rgba(212,251,111,0.55)", fontSize: "1.1rem" }} onClick={() => setEdit(e => e && { ...e, val: e.val.slice(0, -1) })}>⌫</button>
            </div>
            <div style={{ ...S.modalBtns, marginTop: 14 }}>
              <button style={S.modalCancel} onClick={() => setEdit(null)}>Cancel</button>
              <button style={S.modalOk} onClick={commitEdit}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ===========================================
// STYLES
// ===========================================
// INCIDENT REPORT — all trading sites (FOH + BOH), 2026-09-03
// ===========================================================
// File a dietary mishap, accident or issue: type -> severity -> what happened,
// plus optional photos, customer details and who else was on duty. Supabase is
// the SOURCE OF TRUTH (incidents table + record_incident door, migration 0640),
// so the result is SURFACED (a failed save is visible — this is an EHO record).
// Photos go to the shared private bucket under <site>/incidents/… (best-effort).
// Key contacts sit at the top, tap-to-call, from the same emergency_contacts
// feed the Contacts tile uses. Submit is PIN-gated like every other write.
//
// NOTE: severity 'urgent' is meant to raise an immediate manager alert. There is
// no live ops-notification sender yet (a separate dispatcher slice), so an urgent
// incident is captured + flagged for the Command Centre; nothing is emailed yet.
const INCIDENT_TYPES = [
  { key: "Dietary / Allergen",     icon: "🥜" },
  { key: "Customer incident",      icon: "🧑" },
  { key: "Health & Safety",        icon: "🩹" },
  { key: "Equipment / Facilities", icon: "🔧" },
  { key: "Theft / Security",       icon: "🔒" },
  { key: "Other",                  icon: "📋" },
];
const INCIDENT_MAX_PHOTOS = 4;

function IncidentReport({ site, requestPin }) {
  const [type, setType]             = useState(null);
  const [otherDetail, setOtherDetail] = useState("");      // free text shown when type === "Other"
  const [severity, setSeverity]     = useState(null);
  const [description, setDescription] = useState("");
  const [photos, setPhotos]         = useState([]);        // [{ dataUrl, mime }]
  const [custOpen, setCustOpen]     = useState(false);
  const [custName, setCustName]     = useState("");
  const [custContact, setCustContact] = useState("");
  const [custNote, setCustNote]     = useState("");
  const [onDuty, setOnDuty]         = useState([]);         // typed witness names
  const [addingName, setAddingName] = useState(false);
  const [nameDraft, setNameDraft]   = useState("");
  const [saving, setSaving]         = useState(false);
  const [status, setStatus]         = useState(null);       // {type:'ok'|'error'|'preview', msg?}
  const [contacts, setContacts]     = useState(null);
  const savingRef = useRef(false);
  const fileRef   = useRef(null);

  // Key contacts — reuse the Contacts tile's feed (emergency_contacts). Best-
  // effort: a load failure just hides the strip; 999 is always shown.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await fetchEmergencyContacts(site.key);
        if (alive) setContacts((rows || []).slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
      } catch (e) { if (alive) setContacts([]); }
    })();
    return () => { alive = false; };
  }, [site.key]);

  const err = incidents.incidentError({ type, severity, description });
  const needsOther = type === "Other" && !otherDetail.trim(); // "Other" must say what it is
  const canSubmit = !err && !needsOther && !saving;

  const onPickPhotos = (e) => {
    const files = Array.from((e.target && e.target.files) || []);
    e.target.value = ""; // let the same file be picked again after a remove
    files.slice(0, INCIDENT_MAX_PHOTOS - photos.length).forEach((f) => {
      const r = new FileReader();
      r.onload = () => setPhotos((prev) => (prev.length >= INCIDENT_MAX_PHOTOS ? prev : [...prev, { dataUrl: String(r.result || ""), mime: f.type || "image/jpeg" }]));
      r.readAsDataURL(f);
    });
  };
  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, x) => x !== i));

  const commitName = () => {
    const v = nameDraft.trim();
    if (v) setOnDuty((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setNameDraft(""); setAddingName(false);
  };

  const reset = () => {
    setType(null); setOtherDetail(""); setSeverity(null); setDescription(""); setPhotos([]);
    setCustOpen(false); setCustName(""); setCustContact(""); setCustNote("");
    setOnDuty([]); setAddingName(false); setNameDraft(""); setStatus(null);
  };

  const submit = () => {
    if (!canSubmit) return;
    requestPin(async (_staffName, staffPin) => {
      if (savingRef.current) return;
      savingRef.current = true; setSaving(true); setStatus(null);
      try {
        const clientToken = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : (String(Date.now()) + Math.random());
        // "Other" carries the staff's own words as the type (free-text column — no enum).
        const effectiveType = type === "Other" && otherDetail.trim() ? "Other — " + otherDetail.trim() : type;
        const res = await incidents.recordIncident({
          siteKey: site.key, staffPin, type: effectiveType, severity, description,
          customerName: custName, customerContact: custContact, customerNote: custNote,
          staffOnDuty: onDuty, photos: photos.map((p) => ({ dataUrl: p.dataUrl, mime: p.mime })),
          clientToken,
        });
        if (res && res.skipped)   setStatus({ type: "preview" });
        else if (res && res.ok)   setStatus({ type: "ok", urgent: severity === "urgent" });
        else                      setStatus({ type: "error", msg: (res && typeof res.error === "string") ? res.error : null });
        // An URGENT incident also raises a real-time page for the managers — fired
        // BESIDE the row write (which has already landed + been shown), best-effort,
        // never in front of it. Reuses the pass the row just minted (res.pass) so
        // the tap is ONE login, and — unlike the kitchen's reorder alert — it is
        // NOT dual-run gated: an urgent safety page must not be dropped on a
        // non-kiosk host. Queues the alert; the incident-dispatch job sends it.
        if (res && res.ok && severity === "urgent") {
          try {
            notificationsSync.recordIncidentSignal({
              siteKey: site.key, type: effectiveType, severity,
              summary: description.slice(0, 140), token: clientToken, pass: res.pass,
            });
          } catch (_) {}
        }
      } finally { savingRef.current = false; setSaving(false); }
    });
  };

  // ---- rendered pieces ----
  const chip = (t) => {
    const on = type === t.key;
    return (
      <button key={t.key} onClick={() => setType(t.key)} style={{
        display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-start", textAlign: "left",
        padding: "12px 13px", borderRadius: 13, cursor: "pointer", fontFamily: "Manrope,sans-serif", minHeight: 64,
        background: on ? "rgba(212,251,111,0.16)" : "rgba(254,250,240,0.03)",
        border: "1px solid " + (on ? B.wasabi : "rgba(212,251,111,0.22)"),
        boxShadow: on ? "inset 0 0 0 1.5px rgba(212,251,111,0.5)" : "none",
      }}>
        <span style={{ fontSize: "1.25rem", lineHeight: 1 }}>{t.icon}</span>
        <span style={{ fontSize: "0.8rem", fontWeight: 700, lineHeight: 1.15, color: on ? B.wasabi : B.kewpie }}>{t.key}</span>
      </button>
    );
  };

  const sevBtn = (val, label, desc, tone) => {
    const on = severity === val;
    const accent = tone === "urgent" ? "255,122,92" : "111,175,143";
    return (
      <button onClick={() => setSeverity(val)} style={{
        padding: "14px", borderRadius: 13, cursor: "pointer", textAlign: "left", fontFamily: "Manrope,sans-serif", minHeight: 64,
        background: on ? "rgba(" + accent + ",0.16)" : "rgba(254,250,240,0.03)",
        border: "1px solid " + (on ? "rgb(" + accent + ")" : "rgba(212,251,111,0.24)"),
        boxShadow: on ? "inset 0 0 0 1.5px rgba(" + accent + ",0.6)" : "none",
      }}>
        <div style={{ fontSize: "0.9rem", fontWeight: 800, color: on ? (tone === "urgent" ? "#ffb39e" : "#8fd6b3") : B.kewpie }}>{label}</div>
        <div style={{ fontSize: "0.68rem", opacity: 0.6, marginTop: 4, lineHeight: 1.35, color: B.kewpie }}>{desc}</div>
      </button>
    );
  };

  if (status && status.type === "ok") {
    return (
      <div style={S.content}>
        <div style={{ ...S.infoBanner, borderColor: "rgba(111,175,143,0.3)", background: "rgba(111,175,143,0.08)", color: B.eucalyptus, textAlign: "center", padding: "18px 16px" }}>
          ✓ Incident logged{status.urgent ? " and flagged urgent" : ""}.
          {status.urgent && <div style={{ fontSize: "0.72rem", opacity: 0.85, marginTop: 6, lineHeight: 1.4 }}>An urgent incident is flagged for the managers in the Command Centre.</div>}
        </div>
        <button style={S.submitBtn} onClick={reset}>File another incident</button>
      </div>
    );
  }

  return (
    <div style={S.content}>
      <div style={S.sectionCard}>
        <div style={S.secLabel}>Incident Report</div>
        <div style={S.secDesc}>{site.name} · log a dietary mishap, accident or issue. Stamped with your name, the time and the site.</div>
      </div>

      {/* Key contacts — prominent, tap-to-call */}
      <div style={{ ...S.infoBanner, borderColor: "rgba(255,122,92,0.3)", background: "rgba(255,122,92,0.09)", color: "#ffc4b3", fontWeight: 700 }}>
        🚨 In an emergency, always call <b style={{ color: B.rice }}>999</b> first — then log it here and notify management.
      </div>
      {contacts && contacts.length > 0 && (
        <div style={{ ...S.sectionCard, padding: "6px 4px", marginBottom: 16 }}>
          <div style={{ fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: B.wasabi, opacity: 0.7, padding: "6px 12px 4px" }}>Key contacts · tap to call</div>
          {contacts.map((c, i) => (
            <a key={c.contactId || i} href={"tel:" + (c.phone || "")} style={{ textDecoration: "none", color: "inherit", display: "flex", alignItems: "center", gap: 12, padding: "11px 12px", borderTop: i === 0 ? "none" : "1px solid rgba(212,251,111,0.08)" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "0.86rem", fontWeight: 700, color: B.kewpie, lineHeight: 1.2 }}>{c.name}</div>
                {c.role ? <div style={{ fontSize: "0.68rem", color: B.kewpie, opacity: 0.55 }}>{c.role}</div> : null}
              </div>
              <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.8rem", color: B.wasabi }}>{c.phone}</span>
              <span style={{ fontSize: "1rem" }}>📞</span>
            </a>
          ))}
        </div>
      )}

      {/* Type */}
      <div style={S.secTitle}>What kind of incident?<span style={{ color: B.coral, marginLeft: 4 }}>*</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 9, marginBottom: type === "Other" ? 12 : 16 }}>
        {INCIDENT_TYPES.map(chip)}
      </div>
      {type === "Other" && (
        <div style={{ marginBottom: 16 }}>
          <label style={S.fieldLabel}>Tell us what kind<span style={{ color: B.coral, marginLeft: 4 }}>*</span></label>
          <input autoFocus value={otherDetail} maxLength={80} onChange={(e) => setOtherDetail(e.target.value)}
            placeholder="e.g. spillage, near miss, supplier issue…" style={{ ...S.input }} />
        </div>
      )}

      {/* Severity */}
      <div style={S.secTitle}>How urgent is it?<span style={{ color: B.coral, marginLeft: 4 }}>*</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        {sevBtn("urgent", "🔴 Urgent", "Flags it for the managers to act on now.", "urgent")}
        {sevBtn("normal", "🟢 Non-urgent", "Logged for review.", "normal")}
      </div>

      {/* Description */}
      <div style={S.secTitle}>What happened?<span style={{ color: B.coral, marginLeft: 4 }}>*</span></div>
      <textarea style={{ ...S.textarea, minHeight: 110 }} value={description} onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe what happened, in your own words…" />
      <div style={{ fontSize: "0.72rem", color: B.kewpie, opacity: 0.5, margin: "8px 0 16px", lineHeight: 1.5 }}>
        For a dietary mishap, cover: what was <b style={{ color: B.wasabi }}>ordered</b> · the <b style={{ color: B.wasabi }}>allergen</b> · what was <b style={{ color: B.wasabi }}>served</b> · any <b style={{ color: B.wasabi }}>reaction / action taken</b>. This is a food-safety record and may be seen by the EHO.
      </div>

      {/* Photos */}
      <div style={S.secTitle}>Photos <span style={{ marginLeft: 6, fontSize: "0.58rem", fontWeight: 800, color: B.nori, background: B.wasabi, borderRadius: 999, padding: "2px 8px", opacity: 0.85 }}>RECOMMENDED</span></div>
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickPhotos} style={{ display: "none" }} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        {photos.map((p, i) => (
          <div key={i} style={{ width: 84, height: 84, borderRadius: 13, position: "relative", overflow: "hidden", border: "1px solid rgba(212,251,111,0.2)", backgroundImage: "url(" + p.dataUrl + ")", backgroundSize: "cover", backgroundPosition: "center" }}>
            <button onClick={() => removePhoto(i)} aria-label="Remove photo" style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", fontSize: "0.72rem", cursor: "pointer" }}>✕</button>
          </div>
        ))}
        {photos.length < INCIDENT_MAX_PHOTOS && (
          <button onClick={() => fileRef.current && fileRef.current.click()} style={{ width: 84, height: 84, borderRadius: 13, border: "1px dashed rgba(212,251,111,0.34)", background: "rgba(212,251,111,0.05)", color: B.wasabi, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, cursor: "pointer", fontFamily: "Manrope,sans-serif", fontSize: "0.64rem", fontWeight: 700 }}>
            <span style={{ fontSize: "1.4rem" }}>📷</span>Add photo
          </button>
        )}
      </div>
      <div style={{ fontSize: "0.72rem", color: B.kewpie, opacity: 0.5, marginBottom: 16, lineHeight: 1.5 }}>Snap the item, the label / allergen card, or the area.</div>

      {/* Customer (collapsible) */}
      <button onClick={() => setCustOpen((v) => !v)} style={{ width: "100%", padding: "13px 14px", background: "rgba(254,250,240,0.03)", border: "1px solid rgba(212,251,111,0.24)", borderRadius: 13, color: B.kewpie, fontFamily: "Manrope,sans-serif", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: custOpen ? 10 : 16 }}>
        <span>{custOpen ? "– Customer details" : "＋ Add customer details"}</span>
        <span style={{ fontSize: "0.62rem", fontWeight: 700, opacity: 0.55 }}>optional</span>
      </button>
      {custOpen && (
        <div style={{ marginBottom: 16 }}>
          <label style={S.fieldLabel}>Customer name</label>
          <input style={{ ...S.input, marginBottom: 10 }} value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="e.g. Alex Taylor" />
          <label style={S.fieldLabel}>Contact (phone or email)</label>
          <input style={{ ...S.input, marginBottom: 10 }} value={custContact} onChange={(e) => setCustContact(e.target.value)} placeholder="For follow-up only" />
          <label style={S.fieldLabel}>What they told you</label>
          <input style={{ ...S.input }} value={custNote} onChange={(e) => setCustNote(e.target.value)} placeholder="e.g. reported a nut reaction · wants a call back" />
          <div style={{ fontSize: "0.66rem", color: B.kewpie, opacity: 0.4, marginTop: 6, lineHeight: 1.4 }}>🔒 Only add this if a customer is involved and happy to be contacted. Used only for follow-up.</div>
        </div>
      )}

      {/* Staff on duty */}
      <div style={S.secTitle}>Staff on duty</div>
      <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ padding: "12px 16px", borderRadius: 999, border: "1px dashed rgba(212,251,111,0.26)", background: "rgba(254,250,240,0.03)", color: B.kewpie, fontSize: "0.82rem", fontWeight: 600 }}>👤 You <span style={{ opacity: 0.6 }}>· added on PIN</span></span>
        {onDuty.map((n, i) => (
          <button key={i} onClick={() => setOnDuty((prev) => prev.filter((_, x) => x !== i))} style={{ padding: "12px 16px", borderRadius: 999, border: "1px solid " + B.wasabi, background: "rgba(212,251,111,0.18)", color: B.wasabi, fontSize: "0.82rem", fontWeight: 800, cursor: "pointer", fontFamily: "Manrope,sans-serif" }}>{n} <span style={{ opacity: 0.7 }}>✕</span></button>
        ))}
        {addingName ? (
          <input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitName(); } else if (e.key === "Escape") { setNameDraft(""); setAddingName(false); } }}
            onBlur={commitName} placeholder="Type a name…"
            style={{ padding: "12px 16px", borderRadius: 999, border: "1px solid " + B.wasabi, background: "rgba(212,251,111,0.08)", color: B.kewpie, fontFamily: "Manrope,sans-serif", fontSize: "0.82rem", fontWeight: 600, outline: "none", width: 150 }} />
        ) : (
          <button onClick={() => setAddingName(true)} style={{ padding: "12px 16px", borderRadius: 999, border: "1px dashed rgba(212,251,111,0.26)", background: "rgba(254,250,240,0.03)", color: B.wasabi, fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", fontFamily: "Manrope,sans-serif", opacity: 0.85 }}>＋ Add name</button>
        )}
      </div>
      <div style={{ fontSize: "0.72rem", color: B.kewpie, opacity: 0.5, marginBottom: 16, lineHeight: 1.5 }}>You're added automatically. Add anyone else who was working / witnessed it.</div>

      {/* status + submit */}
      {status && status.type === "error"   && <div style={{ ...S.infoBanner, borderColor: "rgba(255,122,92,0.3)", background: "rgba(255,122,92,0.08)", color: B.coral }}>Couldn't file this{status.msg === "pin" ? " — PIN not recognised" : ""}. Nothing was recorded — check the connection and try again.</div>}
      {status && status.type === "preview" && <div style={{ ...S.infoBanner, borderColor: "rgba(241,197,68,0.25)", background: "rgba(241,197,68,0.05)", color: B.sunshine }}>Preview only — not saved. Use the live tablet (or add <b>?dualrun=on</b>) to record for real.</div>}

      <button onClick={submit} disabled={!canSubmit}
        style={{ ...S.submitBtn, ...(severity === "urgent" && canSubmit ? { background: B.coral, color: B.rice } : {}), ...(!canSubmit ? { opacity: 0.3, cursor: "default" } : {}) }}>
        {saving ? "Filing…" : severity === "urgent" ? "Submit — flag urgent" : "Submit incident report"}
      </button>
      {(needsOther || err) && <div style={{ textAlign: "center", fontSize: "0.72rem", color: "#ffb39e", opacity: 0.9, marginTop: 9, lineHeight: 1.4 }}>Still needed: {needsOther ? "what kind of incident it is" : err.field === "type" ? "a type" : err.field === "severity" ? "an urgency" : "what happened"}.</div>}
      <div style={{ textAlign: "center", fontSize: "0.72rem", color: B.kewpie, opacity: 0.35, marginTop: 12, lineHeight: 1.5 }}>Per-action PIN required · records who filed it, their role &amp; home site, and when.</div>
    </div>
  );
}

const S = {
  // Login
  loginRoot:{minHeight:"100vh",background:B.nori,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:"Manrope,sans-serif",position:"relative",animation:"viewIn 0.18s ease both"},
  strip:{position:"absolute",top:0,left:0,right:0,background:"rgba(0,0,0,0.2)",padding:"10px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:8},
  stripDot:{width:8,height:8,borderRadius:"50%",background:B.wasabi},stripTxt:{fontWeight:700,fontSize:"0.8rem",color:B.kewpie,letterSpacing:"0.08em",textTransform:"uppercase"},
  loginBody:{display:"flex",flexDirection:"column",alignItems:"center",width:"100%",maxWidth:340},
  dots:{display:"flex",gap:20,marginBottom:10},
  dot:{width:20,height:20,borderRadius:"50%",border:"2.5px solid rgba(212,251,111,0.3)",borderColor:"rgba(212,251,111,0.3)",background:"transparent",boxShadow:"none",transition:"all 0.15s ease"},
  dotFilled:{borderColor:B.wasabi,background:B.wasabi,boxShadow:"0 0 14px rgba(212,251,111,0.35)"},
  dotEmpty:{borderColor:"rgba(212,251,111,0.3)",background:"transparent",boxShadow:"none"},
  pinErr:{color:B.coral,fontSize:"0.82rem",fontWeight:600,height:20,marginBottom:8},pinHint:{color:B.kewpie,opacity:0.45,fontSize:"0.82rem",height:20,marginBottom:8},
  pinMsgGrow:{height:"auto",minHeight:20,textAlign:"center",lineHeight:1.35},
  pad:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,width:"100%",maxWidth:280},
  key:{aspectRatio:"1.35",borderRadius:14,border:"none",background:"rgba(212,251,111,0.1)",color:B.wasabi,fontSize:"1.6rem",fontFamily:"Manrope,sans-serif",fontWeight:700,cursor:"pointer",transition:"all 0.1s ease",display:"flex",alignItems:"center",justifyContent:"center"},
  loginFoot:{color:B.kewpie,opacity:0.25,fontSize:"0.75rem",textAlign:"center",paddingBottom:16},
  flash:{position:"fixed",top:0,left:0,right:0,bottom:0,width:"100%",height:"100%",background:B.nori,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:100},
  greet:{fontFamily:"gelica,serif",fontSize:"2.8rem",fontWeight:700,color:B.wasabi,animation:"greetIn 1s ease forwards",textAlign:"center",padding:"0 1rem",width:"100%",maxWidth:600},

  // Home
  homeRoot:{minHeight:"100vh",background:B.nori,fontFamily:"Manrope,sans-serif",padding:"0 2rem 1rem",display:"flex",flexDirection:"column",animation:"viewIn 0.2s ease both"},
  topBar:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:"1px solid rgba(212,251,111,0.1)"},
  topL:{display:"flex",alignItems:"center",gap:14},
  hSite:{background:"rgba(212,251,111,0.12)",border:"1px solid rgba(212,251,111,0.2)",color:B.wasabi,borderRadius:999,padding:"4px 14px",fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.04em",textTransform:"uppercase"},
  topR:{display:"flex",alignItems:"center",gap:12},chip:{display:"flex",alignItems:"center",gap:8,background:"rgba(254,250,240,0.07)",borderRadius:999,padding:"4px 14px 4px 4px"},
  chipAv:{width:28,height:28,borderRadius:"50%",background:B.wasabi,color:B.nori,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:"0.75rem"},
  chipName:{fontSize:"0.82rem",fontWeight:600,color:B.kewpie},
  logoutBtn:{background:"none",border:"1px solid rgba(254,250,240,0.15)",borderRadius:999,padding:"5px 14px",fontSize:"0.75rem",fontWeight:600,color:B.kewpie,opacity:0.5,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  greetBox:{padding:"2rem 0 1.5rem"},greetHi:{fontFamily:"gelica,serif",fontSize:"2rem",fontWeight:700,color:B.kewpie,lineHeight:1.2},greetD:{fontSize:"0.88rem",color:B.kewpie,opacity:0.35,marginTop:4},
  // Tile grid — tablet-first, 3 cols capped at 960px, 4:3 tiles with vertically centred content
  grid:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,width:"100%",maxWidth:960,margin:"0 auto",flex:"0 0 auto"},
  card:{background:"rgba(212,251,111,0.06)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:16,padding:"1.4rem 1.4rem 1.3rem",textAlign:"left",cursor:"pointer",transition:"all 0.12s ease",display:"flex",flexDirection:"column",justifyContent:"center",fontFamily:"Manrope,sans-serif",animation:"fadeUp 0.35s ease both",position:"relative",aspectRatio:"4/3"},
  cardOff:{opacity:0.25,cursor:"default"},cTop:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12},cIcon:{fontSize:"1.75rem"},
  cSoon:{background:"rgba(254,250,240,0.08)",borderRadius:999,padding:"2px 6px",fontSize:"0.6rem",fontWeight:700,color:B.kewpie,opacity:0.5,textTransform:"uppercase"},
  cLabel:{fontFamily:"gelica,serif",fontSize:"1rem",fontWeight:700,color:B.wasabi,marginBottom:4,lineHeight:1.2},cDesc:{fontSize:"0.72rem",color:B.kewpie,opacity:0.45,lineHeight:1.4},
  hFoot:{textAlign:"center",color:B.kewpie,opacity:0.15,fontSize:"0.7rem",marginTop:"auto",paddingTop:16},

  // Shell
  shellRoot:{minHeight:"100vh",background:B.nori,fontFamily:"Manrope,sans-serif"},
  shellBar:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 20px",borderBottom:"1px solid rgba(212,251,111,0.1)"},
  shellBack:{background:"none",border:"none",color:B.wasabi,fontSize:"0.85rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  shellSiteFull:{fontSize:"0.72rem",fontWeight:700,color:B.wasabi,opacity:0.5,letterSpacing:"0.04em",textTransform:"uppercase"},
  // Sidebar (collapsible)
  sidebar:{width:200,borderRight:"1px solid rgba(212,251,111,0.08)",padding:"12px 8px",flexShrink:0,transition:"width 0.22s ease, padding 0.22s ease",overflow:"hidden"},
  sidebarCollapsed:{width:56,padding:"12px 6px"},
  sideToggle:{display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:32,borderRadius:8,border:"none",background:"none",color:B.kewpie,opacity:0.35,fontSize:"0.7rem",cursor:"pointer",fontFamily:"Manrope,sans-serif",marginBottom:10,transition:"opacity 0.15s ease"},
  sideItem:{display:"flex",alignItems:"center",width:"100%",padding:"8px 10px",borderRadius:10,border:"none",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.78rem",fontWeight:500,cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left",marginBottom:2,transition:"all 0.15s ease",whiteSpace:"nowrap"},
  sideItemCollapsed:{padding:"10px 0",justifyContent:"center"},
  sideItemActive:{background:"rgba(212,251,111,0.1)",color:B.wasabi,opacity:1,fontWeight:700},

  // Shared module styles
  content:{padding:"0 20px 24px",maxWidth:640,width:"100%",margin:"0 auto"},
  sectionCard:{background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:14,padding:"14px 16px",marginBottom:16},
  secLabel:{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:B.wasabi,marginBottom:2},secDesc:{fontSize:"0.8rem",color:B.kewpie,opacity:0.5,lineHeight:1.5},
  secTitle:{fontSize:"0.7rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8,marginTop:8,display:"flex",alignItems:"center"},
  fieldLabel:{fontSize:"0.7rem",color:B.kewpie,opacity:0.4,marginBottom:3,display:"block"},
  input:{width:"100%",padding:"10px 12px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",outline:"none",boxSizing:"border-box"},
  textarea:{width:"100%",padding:"10px 12px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.85rem",outline:"none",resize:"none",minHeight:70,lineHeight:1.5,boxSizing:"border-box"},
  submitBtn:{width:"100%",padding:14,background:B.wasabi,color:B.nori,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",fontWeight:700,border:"none",borderRadius:14,cursor:"pointer",letterSpacing:"0.02em"},
  infoBanner:{padding:"10px 14px",borderRadius:10,border:"1px solid",marginBottom:12,fontSize:"0.78rem",lineHeight:1.5},

  // Rows
  row:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:10,marginBottom:4,transition:"all 0.15s ease"},
  soRow:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:10,marginBottom:4,transition:"all 0.15s ease"},
  itemName:{fontSize:"0.85rem",fontWeight:500,color:B.kewpie},
  ctrls:{display:"flex",alignItems:"center",gap:8},
  cBtn:{width:30,height:30,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.15)",background:"none",color:B.wasabi,fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Manrope,sans-serif"},
  cVal:{fontFamily:"Manrope,sans-serif",fontWeight:700,fontSize:"0.95rem",color:B.kewpie,opacity:0.4,minWidth:24,textAlign:"center"},
  markBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(212,251,111,0.25)",background:"rgba(212,251,111,0.08)",color:B.wasabi,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
  undoBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(255,122,92,0.2)",background:"rgba(255,122,92,0.06)",color:B.coral,fontSize:"0.78rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  restockBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(111,175,143,0.35)",background:"rgba(111,175,143,0.12)",color:B.eucalyptus,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
  receivedBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(111,175,143,0.35)",background:"rgba(111,175,143,0.12)",color:B.eucalyptus,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
  unclearedBox:{background:"rgba(212,251,111,0.03)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:12,padding:"10px 14px",marginBottom:8},

  // Stock categories
  catHeader:{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.08)",borderRadius:10,color:B.wasabi,fontSize:"0.82rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",marginBottom:2},

  // Modals — z-index must sit ABOVE the scanner's own .cc-modal (350) so the PIN modal
  // spawns on top of the scanner's Manual Lookup card, not behind it.
  overlay:{position:"fixed",top:0,left:0,width:"100vw",height:"100vh",background:"rgba(0,0,0,0.6)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:16,boxSizing:"border-box",overflowY:"auto"},
  modal:{background:B.nori,border:"1px solid rgba(212,251,111,0.2)",borderRadius:18,padding:20,maxWidth:380,width:"calc(100% - 32px)",maxHeight:"80vh",overflowY:"auto"},
  modalTitle:{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:B.wasabi,marginBottom:4},
  modalDesc:{fontSize:"0.8rem",color:B.kewpie,opacity:0.5,marginBottom:14},
  modalInput:{width:"100%",padding:"14px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.15)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"1.2rem",textAlign:"center",letterSpacing:"8px",outline:"none",marginBottom:8,boxSizing:"border-box"},
  modalErr:{fontSize:"0.78rem",color:B.coral,textAlign:"center",marginBottom:8},
  modalBtns:{display:"flex",gap:8},
  modalCancel:{flex:1,padding:12,borderRadius:10,border:"1px solid rgba(254,250,240,0.15)",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.85rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  modalOk:{flex:1,padding:12,borderRadius:10,border:"none",background:B.wasabi,color:B.nori,fontSize:"0.85rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"},

  // Loading
  loadingBox:{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 20px"},
  spinner:{width:32,height:32,border:"3px solid rgba(212,251,111,0.15)",borderTopColor:B.wasabi,borderRadius:"50%",animation:"spin 0.8s linear infinite"},
  emptyState:{textAlign:"center",padding:"60px 20px",fontSize:"0.85rem",color:B.kewpie,opacity:0.4},
};

// =====================================================================
// FOOD ALLERGENS — customer-facing FOH tile (2026-05-07)
// Renders the admin-managed Allergen Catalog with per-site overrides
// applied. Customer-friendly: branded header, search, dietary +
// allergen-free filters, item cards, full breakdown modal. No site picker
// (deployment IS the site).
// =====================================================================

const ALLERGENS_FSA14 = [
  {id:"celery",      label:"Celery",      icon:"🌿"},
  {id:"gluten",      label:"Gluten",      icon:"🌾", longLabel:"Cereals containing gluten"},
  {id:"crustaceans", label:"Crustaceans", icon:"🦐"},
  {id:"egg",         label:"Egg",         icon:"🥚"},
  {id:"fish",        label:"Fish",        icon:"🐟"},
  {id:"lupin",       label:"Lupin",       icon:"🌱"},
  {id:"milk",        label:"Milk",        icon:"🥛"},
  {id:"molluscs",    label:"Molluscs",    icon:"🦑"},
  {id:"mustard",     label:"Mustard",     icon:"🌭"},
  {id:"nuts",        label:"Nuts",        icon:"🌰", longLabel:"Tree nuts (almond, hazelnut, walnut, cashew, pecan, brazil, pistachio, macadamia)"},
  {id:"peanuts",     label:"Peanuts",     icon:"🥜"},
  {id:"sesame",      label:"Sesame",      icon:"⚪"},
  {id:"soya",        label:"Soya",        icon:"🫘"},
  {id:"sulphites",   label:"Sulphites",   icon:"🧪", longLabel:"Sulphur Dioxide & Sulphites"},
];
const ALLERGENS_BY_ID = ALLERGENS_FSA14.reduce((m,a)=>{m[a.id]=a;return m;},{});

function _allergenEffective(item, siteKey){
  const out = {...(item.allergens || {})};
  const ov = (item.siteOverrides || {})[siteKey] || {};
  for (const k of Object.keys(ov)) {
    const v = ov[k];
    if (v === "none" || v === "" || v == null) delete out[k];
    else out[k] = v;
  }
  return out;
}
const _allergenHasGluten = (it) => it.allergens && (it.allergens.gluten === "contains" || it.allergens.gluten === "may");
const _allergenWithStatus = (it, status) => Object.entries(it.allergens||{}).filter(([k,v]) => v === status).map(([k])=>k);

function AllergenItemThumbFOH({item, large}){
  if (item.imageUrl) return <img src={item.imageUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>;
  const initials = (item.name||"").split(/[ \-/&]+/).slice(0,2).map(s=>s[0]||"").join("").toUpperCase();
  const palette = [["#00392c","#0a5240"],["#006a53","#1d8068"],["#0a4f3e","#1f6b56"],["#085041","#27735c"]];
  const idx = (item.itemId || item.name || "").charCodeAt(0) % palette.length;
  const [a,b] = palette[idx];
  return (
    <div style={{width:"100%",height:"100%",background:`linear-gradient(135deg, ${a} 0%, ${b} 60%, #0d6e57 100%)`,display:"flex",alignItems:"center",justifyContent:"center",color:"#d4fb6f",fontFamily:"gelica, Georgia, serif",fontWeight:700,fontSize:large?42:18,letterSpacing:"-0.02em",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",inset:0,background:"radial-gradient(circle at 80% 20%, rgba(212,251,111,0.18), transparent 50%)"}}/>
      <div style={{position:"relative",zIndex:1}}>{initials || "·"}</div>
      <div style={{position:"absolute",bottom:6,right:8,fontSize:9,fontFamily:"Manrope",fontWeight:700,color:"rgba(212,251,111,0.6)",letterSpacing:"0.1em"}}>ROLLED</div>
    </div>
  );
}

// ===========================================
// EMERGENCY CONTACTS (2026-05-18)
// ===========================================
// Per-site phone book. Admin manages rows via Command Centre →
// "Emergency Contacts" config tile. Frontend fetches active rows for this
// SITE.key (Site === 'all' OR Site === site.key) on mount + caches in
// localStorage. Stale paint on subsequent mounts; background refresh
// every open. 5-min TTL on the cache is symbolic — the next mount
// refetches anyway, so this just bounds how out-of-date a long-running
// session can drift.
function EmergencyContacts({site, backend}){
  const cacheKey = "emergencyContactsCache::" + site.key;
  const [contacts, setContacts] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(()=>{
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached && Array.isArray(cached.contacts)) setContacts(cached.contacts);
      }
    } catch(e) {}
    let cancelled = false;
    (async ()=>{
      try {
        const rows = await fetchEmergencyContacts(site.key);
        if (cancelled) return;
        setContacts(rows);
        try { localStorage.setItem(cacheKey, JSON.stringify({contacts: rows, ts: Date.now()})); } catch(e) {}
      } catch(e) { if (!cancelled) setError(e.message || "Failed to load contacts"); }
    })();
    return ()=>{ cancelled = true; };
  },[site.key, backend, cacheKey]);

  const grouped = React.useMemo(()=>{
    if (!contacts) return null;
    const map = {};
    contacts.forEach(c => { (map[c.group] = map[c.group] || []).push(c); });
    Object.keys(map).forEach(g => map[g].sort((a,b)=>(a.sortOrder||0)-(b.sortOrder||0)));
    return Object.keys(map).map(g => ({group:g, contacts:map[g]}));
  },[contacts]);

  const NORI = "#00392c", WASABI = "#d4fb6f", KEWPIE = "#fefaf0", RICE = "#fff", LINE = "rgba(0,57,44,0.12)", DIM = "rgba(0,57,44,0.65)", CORAL = "#FF7A5C";
  const isPhone = typeof window !== "undefined" && window.innerWidth < 500;

  return (
    <div style={{minHeight:"100dvh",background:KEWPIE,color:NORI,paddingBottom:80}}>
      <div style={{background:`linear-gradient(180deg, ${NORI} 0%, #00251c 100%)`,color:KEWPIE,padding:isPhone?"24px 18px 28px":"36px 32px 32px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-40,width:200,height:200,borderRadius:"50%",background:"radial-gradient(circle, rgba(212,251,111,0.18), transparent 70%)"}}/>
        <div style={{position:"relative"}}>
          <div style={{fontFamily:"gelica, Georgia, serif",fontSize:isPhone?28:42,fontWeight:700,letterSpacing:"-0.02em",lineHeight:1.05,color:WASABI}}>Emergency Contacts</div>
          <div style={{marginTop:8,fontSize:isPhone?13:15,color:"rgba(254,250,240,0.78)",maxWidth:560,lineHeight:1.5}}>Key numbers for {site.name}. Tap any row to call.</div>
        </div>
      </div>

      <div style={{maxWidth:900,margin:"0 auto",padding:isPhone?"18px 14px 0":"24px 24px 0"}}>
        <div style={{padding:"12px 16px",borderRadius:12,background:"rgba(255,122,92,0.08)",border:`1px solid rgba(255,122,92,0.25)`,color:CORAL,fontSize:13,fontWeight:600,marginBottom:18,lineHeight:1.5}}>
          🚨 <strong>In an emergency, always call 999 first.</strong> Then notify management.
        </div>

        {!contacts && !error && (
          <div style={{padding:"40px 0",textAlign:"center",color:DIM,fontSize:14}}>Loading contacts…</div>
        )}
        {error && (
          <div style={{padding:"16px 18px",borderRadius:12,background:"rgba(168,48,45,0.08)",color:"#a8302d",border:"1px solid rgba(168,48,45,0.25)",fontSize:13,fontWeight:600}}>
            Couldn't load contacts. {error} — call <strong>999</strong> for emergencies.
          </div>
        )}
        {contacts && contacts.length === 0 && !error && (
          <div style={{padding:"32px 18px",textAlign:"center",borderRadius:14,background:RICE,border:`1px dashed ${LINE}`,color:DIM,fontSize:13,lineHeight:1.6}}>
            No contacts configured for this site yet. <br/>An admin can add them.
          </div>
        )}

        {grouped && grouped.map(group => (
          <div key={group.group} style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:1.2,textTransform:"uppercase",color:DIM,marginBottom:6,paddingLeft:4}}>{group.group}</div>
            <div style={{background:RICE,borderRadius:14,border:`1px solid ${LINE}`,overflow:"hidden"}}>
              {group.contacts.map((c, i) => (
                <a key={c.contactId} href={"tel:"+c.phone} style={{textDecoration:"none",color:"inherit",display:"block",borderTop: i===0?"none":`1px solid ${LINE}`}}>
                  <div style={{padding:"14px 18px",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:15,color:NORI,lineHeight:1.25}}>{c.name}</div>
                      {c.role && <div style={{fontSize:12,color:DIM,marginTop:2}}>{c.role}</div>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                      <span style={{fontFamily:"monospace",fontWeight:700,fontSize:14,color:NORI,letterSpacing:0.2}}>{c.phone}</span>
                      <span style={{fontSize:20}}>📞</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Food Allergens QR (2026-05-26) — renders a full-bleed screen with the
// eatrolled.com/menu QR code. The QR is inlined as an SVG path so it works
// fully offline on the tablet (no runtime network call). Generated via the
// `qrcode` npm package at error-correction level H so it stays scannable
// even with mild sticker wear / glare.
const ALLERGENS_QR_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 33 33" shape-rendering="crispEdges" style="display:block;width:100%;height:100%"><path fill="#FEFAF0" d="M0 0h33v33H0z"/><path stroke="#00392C" d="M0 0.5h7m1 0h2m2 0h4m5 0h4m1 0h7M0 1.5h1m5 0h1m3 0h2m2 0h5m2 0h3m2 0h1m5 0h1M0 2.5h1m1 0h3m1 0h1m2 0h4m3 0h3m3 0h3m1 0h1m1 0h3m1 0h1M0 3.5h1m1 0h3m1 0h1m1 0h1m2 0h2m2 0h2m2 0h1m2 0h1m1 0h1m1 0h1m1 0h3m1 0h1M0 4.5h1m1 0h3m1 0h1m2 0h2m2 0h1m5 0h2m1 0h1m1 0h1m1 0h1m1 0h3m1 0h1M0 5.5h1m5 0h1m2 0h1m1 0h2m1 0h1m1 0h1m1 0h1m2 0h4m1 0h1m5 0h1M0 6.5h7m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h7M9 7.5h1m2 0h2m1 0h1m1 0h1m2 0h4M2 8.5h1m1 0h3m1 0h1m2 0h3m1 0h3m3 0h1m2 0h2m3 0h1m2 0h1M0 9.5h5m2 0h1m1 0h1m1 0h1m4 0h3m1 0h1m3 0h1m1 0h1m3 0h3M1 10.5h2m1 0h4m3 0h1m3 0h2m2 0h3m4 0h1m1 0h1m2 0h2M1 11.5h1m1 0h2m5 0h2m1 0h1m3 0h1m1 0h2m2 0h4M2 12.5h1m2 0h3m1 0h3m1 0h3m2 0h2m3 0h4m1 0h1m2 0h2M1 13.5h1m1 0h2m4 0h1m3 0h2m1 0h1m1 0h1m5 0h3m1 0h1m1 0h3M1 14.5h1m2 0h1m1 0h3m1 0h2m1 0h1m1 0h2m2 0h3m2 0h2m5 0h2M0 15.5h1m2 0h3m1 0h1m1 0h2m3 0h4m2 0h5m3 0h1m1 0h3M1 16.5h2m3 0h1m1 0h1m1 0h1m1 0h2m1 0h1m1 0h2m1 0h4m2 0h1m3 0h1M2 17.5h2m3 0h5m4 0h1m1 0h1m1 0h2m3 0h2m2 0h3M3 18.5h1m2 0h3m2 0h1m1 0h1m1 0h6m5 0h2m2 0h1m1 0h1M1 19.5h2m4 0h1m3 0h1m1 0h5m1 0h4m5 0h1M0 20.5h2m2 0h3m3 0h2m3 0h1m2 0h1m1 0h2m2 0h1m1 0h2m1 0h1m1 0h2M1 21.5h1m1 0h1m5 0h3m2 0h4m1 0h2m2 0h2m4 0h1m1 0h2M0 22.5h1m1 0h1m3 0h2m1 0h1m2 0h3m1 0h2m3 0h1m1 0h2m1 0h1m1 0h3m1 0h1M1 23.5h5m2 0h1m1 0h2m1 0h1m2 0h1m1 0h2m1 0h1m2 0h2m5 0h1M0 24.5h1m1 0h2m1 0h3m6 0h1m1 0h2m2 0h2m1 0h7m1 0h2M8 25.5h3m1 0h1m3 0h3m2 0h4m3 0h2m1 0h2M0 26.5h7m2 0h1m6 0h1m2 0h2m1 0h3m1 0h1m1 0h2m1 0h2M0 27.5h1m5 0h1m1 0h4m2 0h2m1 0h1m2 0h3m1 0h1m3 0h1m3 0h1M0 28.5h1m1 0h3m1 0h1m1 0h1m2 0h1m3 0h2m1 0h1m1 0h1m1 0h1m1 0h5m2 0h1M0 29.5h1m1 0h3m1 0h1m2 0h2m1 0h2m4 0h3m1 0h1m1 0h1m3 0h2M0 30.5h1m1 0h3m1 0h1m1 0h3m3 0h1m3 0h1m1 0h2m1 0h1m1 0h1m1 0h4m1 0h1M0 31.5h1m5 0h1m2 0h2m3 0h2m2 0h1m1 0h1m2 0h2m1 0h2m1 0h1m1 0h1M0 32.5h7m5 0h3m1 0h1m3 0h1m2 0h3m1 0h6"/></svg>';

function AllergensQRView(){
  const NORI = "#00392C", WASABI = "#D4FB6F", KEWPIE = "#FEFAF0";
  return (
    <div style={{minHeight:"100dvh",background:NORI,color:KEWPIE,padding:"24px 2rem 2rem",display:"flex",flexDirection:"column",fontFamily:"Manrope,sans-serif"}}>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",textAlign:"center",padding:"20px 0"}}>
        <div style={{fontFamily:"Manrope,sans-serif",fontSize:"0.78rem",fontWeight:700,letterSpacing:"0.22em",color:WASABI,textTransform:"uppercase",opacity:0.7,marginBottom:14}}>📱 Scan with your phone</div>
        <div style={{fontFamily:"gelica,Georgia,serif",fontSize:"clamp(2.4rem,5vw,3.6rem)",fontWeight:700,color:WASABI,letterSpacing:"-0.02em",lineHeight:1.05,marginBottom:10,maxWidth:720}}>Our menu &amp; allergens</div>
        <div style={{fontSize:"clamp(0.95rem,1.4vw,1.05rem)",color:KEWPIE,opacity:0.7,maxWidth:480,lineHeight:1.5,marginBottom:32}}>Point your phone camera at the code below to see our full menu and allergen info</div>
        <div style={{background:KEWPIE,borderRadius:24,padding:24,boxShadow:"0 24px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(212,251,111,0.12)",width:"min(440px,68vmin)",height:"min(440px,68vmin)"}} dangerouslySetInnerHTML={{__html: ALLERGENS_QR_SVG}}/>
      </div>
    </div>
  );
}

function AllergenInfo({site, allergenCatalog, allergenCategories}){
  // 2026-05-22: multi-select allergen-free filter. filter.values holds an
  // array of allergen IDs; matched items must be free of ALL of them (AND).
  const [filter,setFilter] = useState({type:"all", value:null, values:[]});
  const [search,setSearch] = useState("");
  const [openId,setOpenId] = useState(null);
  const [allergenSheetOpen,setAllergenSheetOpen] = useState(false);
  const [vw,setVw] = useState(typeof window!=="undefined"?window.innerWidth:1024);
  useEffect(()=>{
    const h = ()=> setVw(window.innerWidth);
    window.addEventListener("resize",h);
    return ()=> window.removeEventListener("resize",h);
  },[]);
  // Phones get 1 col; everything tablet-width and above gets 3 cols (per JJ 2026-05-07).
  const isPhone = vw < 500;
  const isTablet = vw >= 500 && vw < 1100;

  const visibleItems = useMemo(()=>{
    return (allergenCatalog || []).map(i => ({...i, allergens: _allergenEffective(i, site.key)}));
  },[allergenCatalog, site.key]);

  const filtered = useMemo(()=>{
    const q = search.trim().toLowerCase();
    let out = visibleItems;
    if (filter.type === "flag") {
      if (filter.value === "gluten-free") out = out.filter(i => !_allergenHasGluten(i));
      else if (filter.value === "vegetarian") out = out.filter(i => !!i.veg);
      else if (filter.value === "vegan") out = out.filter(i => !!i.vegan);
    } else if (filter.type === "allergen-free") {
      const ids = filter.values || [];
      if (ids.length > 0) {
        out = out.filter(i => {
          const a = i.allergens || {};
          return ids.every(id => {
            const v = a[id];
            return v !== "contains" && v !== "may";
          });
        });
      }
    }
    if (q) out = out.filter(i => i.name.toLowerCase().includes(q));
    return out;
  },[visibleItems, filter, search]);

  const grouped = useMemo(()=>{
    const m = {};
    filtered.forEach(it => {
      const cid = it.categoryId || "other";
      if(!m[cid]) m[cid]=[]; m[cid].push(it);
    });
    Object.values(m).forEach(arr => arr.sort((a,b)=>a.name.localeCompare(b.name)));
    return (allergenCategories || []).filter(c => m[c.id] && m[c.id].length>0).map(c => ({category:c, items:m[c.id]}));
  },[filtered, allergenCategories]);

  const openItem = filtered.find(i => i.id === openId || i.itemId === openId)
    || (() => {
      const raw = (allergenCatalog || []).find(i => i.id === openId || i.itemId === openId);
      return raw ? {...raw, allergens: _allergenEffective(raw, site.key)} : null;
    })();

  const NORI = "#00392c", WASABI = "#d4fb6f", KEWPIE = "#fefaf0", RICE = "#fff", LINE = "rgba(0,57,44,0.12)", DIM = "rgba(0,57,44,0.65)";

  return (
    <div style={{minHeight:"100dvh",background:KEWPIE,color:NORI,paddingBottom:80}}>
      <div style={{background:`linear-gradient(180deg, ${NORI} 0%, #00251c 100%)`,color:KEWPIE,padding:isPhone?"24px 18px 32px":"40px 32px 40px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-40,width:200,height:200,borderRadius:"50%",background:"radial-gradient(circle, rgba(212,251,111,0.18), transparent 70%)"}}/>
        <div style={{position:"relative"}}>
          <div style={{fontFamily:"gelica, Georgia, serif",fontSize:isPhone?32:isTablet?42:48,fontWeight:700,letterSpacing:"-0.02em",lineHeight:1.05,color:WASABI}}>Allergen Info</div>
          <div style={{marginTop:8,fontSize:isPhone?14:16,color:"rgba(254,250,240,0.78)",maxWidth:560,lineHeight:1.5}}>Filter by what you can't eat or what you're after. Tap any item for the full breakdown.</div>
        </div>
      </div>

      <div style={{maxWidth:1200,margin:"0 auto",padding:isPhone?"18px 14px 0":"24px 24px 0"}}>
        <div style={{position:"relative",marginBottom:14}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search rolls, sauces…" style={{width:"100%",maxWidth:isPhone?"none":560,padding:"14px 18px 14px 46px",borderRadius:14,border:`1px solid ${LINE}`,background:RICE,fontSize:15,color:NORI,fontWeight:500,outline:"none",fontFamily:"inherit",display:"block"}}/>
          <div style={{position:"absolute",left:16,top:"50%",transform:"translateY(-50%)",fontSize:18,opacity:0.5}}>🔍</div>
        </div>

        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
          {[
            {key:"all", label:"All items", count:visibleItems.length},
            {key:"glutenFree", label:"No gluten", icon:"🌾"},
            {key:"vegetarian", label:"Vegetarian", icon:"🥗"},
            {key:"vegan", label:"Vegan", icon:"🌱"},
          ].map(f => {
            const active = (f.key==="all" && filter.type==="all")
              || (f.key==="glutenFree" && filter.type==="flag" && filter.value==="gluten-free")
              || (f.key==="vegetarian" && filter.type==="flag" && filter.value==="vegetarian")
              || (f.key==="vegan" && filter.type==="flag" && filter.value==="vegan");
            return (
              <button key={f.key} onClick={()=>{
                if (f.key==="all") setFilter({type:"all",value:null,values:[]});
                else if (f.key==="glutenFree") setFilter({type:"flag",value:"gluten-free",values:[]});
                else setFilter({type:"flag",value:f.key,values:[]});
              }} style={{padding:"13px 18px",borderRadius:999,border:`1px solid ${active?NORI:LINE}`,background:active?NORI:RICE,color:active?WASABI:NORI,fontSize:13,fontWeight:700,letterSpacing:"0.01em",cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:6,fontFamily:"inherit"}}>
                {f.icon && <span>{f.icon}</span>}{f.label}{typeof f.count==="number" && <span style={{opacity:0.55,fontWeight:600}}>· {f.count}</span>}
              </button>
            );
          })}
          {(() => {
            const selected = (filter.type==="allergen-free" ? (filter.values||[]) : []);
            const active = selected.length > 0;
            const labels = selected.map(id => ALLERGENS_BY_ID[id]?.label).filter(Boolean);
            const display = labels.length <= 3
              ? labels.join(", ")
              : labels.slice(0,2).join(", ") + " +" + (labels.length-2) + " more";
            return (
              <button onClick={()=>setAllergenSheetOpen(true)} style={{padding:"13px 18px",borderRadius:999,border:`1px solid ${active?NORI:LINE}`,background:active?NORI:RICE,color:active?WASABI:NORI,fontSize:13,fontWeight:700,letterSpacing:"0.01em",cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:6,fontFamily:"inherit"}}>
                {active
                  ? <>✓ No {display} <span style={{opacity:0.7}}>✕</span></>
                  : <>+ Filter by allergen</>}
              </button>
            );
          })()}
        </div>

        {(filter.type !== "all" || search || (filter.values||[]).length > 0) && (
          <div style={{marginBottom:14,fontSize:13,color:DIM}}>
            Showing <strong style={{color:NORI}}>{filtered.length}</strong> of {visibleItems.length} items
            <button onClick={()=>{setFilter({type:"all",value:null,values:[]});setSearch("")}} style={{marginLeft:10,background:"none",border:"none",color:NORI,fontWeight:700,cursor:"pointer",textDecoration:"underline",fontFamily:"inherit"}}>Clear all</button>
          </div>
        )}

        {visibleItems.length === 0 && !search ? (
          <div style={{padding:60,textAlign:"center",color:DIM,background:RICE,borderRadius:16,border:`1px solid ${LINE}`}}>
            <div style={{fontSize:32,marginBottom:8,opacity:0.4}}>🍱</div>
            <div style={{fontWeight:700,fontSize:16,color:NORI}}>Allergen info loading…</div>
            <div style={{marginTop:6,fontSize:13}}>If this persists, the site may not have any items configured yet.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{padding:60,textAlign:"center",color:DIM,background:RICE,borderRadius:16,border:`1px solid ${LINE}`}}>
            <div style={{fontSize:32,marginBottom:8,opacity:0.4}}>🍱</div>
            <div style={{fontWeight:700,fontSize:16,color:NORI}}>No items match those filters</div>
            <div style={{marginTop:6,fontSize:13}}>Try clearing the search or removing an allergen filter.</div>
          </div>
        ) : grouped.map(({category, items:its}) => (
          <div key={category.id} style={{marginBottom:32}}>
            <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:12}}>
              <div style={{fontFamily:"gelica, Georgia, serif",fontSize:22,fontWeight:700,color:NORI,letterSpacing:"-0.01em",display:"inline-flex",alignItems:"center",gap:8}}>
                {category.icon && <span style={{fontSize:22}}>{category.icon}</span>}{category.name}
              </div>
              <div style={{fontSize:12,color:DIM}}>{its.length} item{its.length===1?"":"s"}</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:isPhone?"1fr":"repeat(3, 1fr)",gap:14}}>
              {its.map(item => (
                <AllergenItemCard key={item.itemId} item={item} category={category} onTap={()=>setOpenId(item.itemId)} colors={{NORI,WASABI,KEWPIE,RICE,LINE,DIM}}/>
              ))}
            </div>
          </div>
        ))}

        <div style={{marginTop:48,padding:"20px 0 30px",borderTop:`1px solid ${LINE}`,fontSize:12,color:DIM,textAlign:"center",lineHeight:1.6}}>
          Cross-contamination is possible — items may contain trace allergens not listed.<br/>
          <a href="https://eatrolled.com/menu" target="_blank" rel="noopener noreferrer" style={{color:NORI,fontWeight:700,textDecoration:"none",borderBottom:`1px solid ${LINE}`}}>eatrolled.com/menu</a>
        </div>
      </div>

      {openItem && <AllergenItemDetail item={openItem} category={(allergenCategories||[]).find(c=>c.id===openItem.categoryId)} onClose={()=>setOpenId(null)} isPhone={isPhone} colors={{NORI,WASABI,KEWPIE,RICE,LINE,DIM}}/>}
      {allergenSheetOpen && <AllergenSheetFOH
        selected={filter.type==="allergen-free" ? (filter.values||[]) : []}
        onToggle={(id)=>{
          setFilter(prev => {
            const prevIds = prev.type==="allergen-free" ? (prev.values||[]) : [];
            const next = prevIds.includes(id) ? prevIds.filter(x=>x!==id) : [...prevIds, id];
            if (next.length === 0) return {type:"all", value:null, values:[]};
            return {type:"allergen-free", value:null, values:next};
          });
        }}
        onClear={()=>{setFilter({type:"all",value:null,values:[]});setAllergenSheetOpen(false);}}
        onClose={()=>setAllergenSheetOpen(false)}
        colors={{NORI,WASABI,KEWPIE,RICE,LINE,DIM}}/>}
    </div>
  );
}

function AllergenItemCard({item, category, onTap, colors}){
  const {NORI,WASABI,RICE,LINE,DIM} = colors;
  const containsList = _allergenWithStatus(item, "contains");
  const mayList = _allergenWithStatus(item, "may");
  return (
    <button onClick={onTap} style={{display:"block",textAlign:"left",width:"100%",padding:0,background:RICE,borderRadius:18,border:`1px solid ${LINE}`,cursor:"pointer",fontFamily:"inherit",overflow:"hidden",boxShadow:"0 1px 2px rgba(0,57,44,0.04)",transition:"transform 0.12s, box-shadow 0.12s"}}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,57,44,0.08)"}}
      onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="0 1px 2px rgba(0,57,44,0.04)"}}>
      <div style={{aspectRatio:"16/10",width:"100%",background:"rgba(0,57,44,0.08)"}}>
        <AllergenItemThumbFOH item={item} large/>
      </div>
      <div style={{padding:"14px 16px 16px",display:"flex",flexDirection:"column",height:"100%"}}>
        <div style={{fontWeight:800,fontSize:16,color:NORI,letterSpacing:"-0.01em",lineHeight:1.25,marginBottom:8,minHeight:"2.5em",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{item.name}</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10,minHeight:22}}>
          {item.vegan && <AllergenPill icon="🌱" label="Vegan" tone="ok"/>}
          {item.veg && !item.vegan && <AllergenPill icon="🥗" label="Veggie" tone="ok"/>}
          {!_allergenHasGluten(item) && <AllergenPill icon="🌾" label="No gluten" tone="muted"/>}
        </div>
        <div style={{fontSize:11,color:DIM,fontWeight:600,letterSpacing:"0.04em",textTransform:"uppercase",marginBottom:10}}>{category ? `${category.icon||""} ${category.name}`.trim() : ""}</div>
        {containsList.length === 0 && mayList.length === 0 ? (
          <div style={{fontSize:12,color:"#2f8d5f",fontWeight:700}}>No declared allergens</div>
        ) : (
          <div>
            {containsList.length > 0 && (
              <div style={{marginBottom:6}}>
                <div style={{fontSize:10,color:DIM,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4}}>Contains</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {containsList.map(id => <AllergenChipFOH key={id} id={id} status="contains"/>)}
                </div>
              </div>
            )}
            {mayList.length > 0 && (
              <div>
                <div style={{fontSize:10,color:DIM,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4}}>May contain</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {mayList.map(id => <AllergenChipFOH key={id} id={id} status="may"/>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function AllergenPill({icon, label, tone}){
  const c = tone === "ok" ? {bg:"rgba(47,141,95,0.14)",color:"#1f6646"} : {bg:"rgba(0,57,44,0.08)",color:"rgba(0,57,44,0.65)"};
  return <span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:800,background:c.bg,color:c.color,letterSpacing:"0.02em",whiteSpace:"nowrap"}}>{icon && <span style={{fontSize:10}}>{icon}</span>}{label}</span>;
}

function AllergenChipFOH({id, status}){
  const a = ALLERGENS_BY_ID[id]; if (!a) return null;
  const isContains = status === "contains";
  return <span title={a.longLabel||a.label} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 9px",borderRadius:8,fontSize:11,fontWeight:700,background:isContains?"#00392c":"#d4fb6f",color:isContains?"#fefaf0":"#00392c",border:isContains?"none":"1px solid #00392c",letterSpacing:"0.01em"}}><span style={{fontSize:11}}>{a.icon}</span>{a.label}</span>;
}

function AllergenItemDetail({item, category, onClose, isPhone, colors}){
  const {NORI,WASABI,KEWPIE,RICE,LINE,DIM} = colors;
  useEffect(()=>{
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return ()=>{ window.removeEventListener("keydown", onKey); document.body.style.overflow=""; };
  },[onClose]);
  const containsList = _allergenWithStatus(item, "contains");
  const mayList = _allergenWithStatus(item, "may");
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,30,22,0.7)",backdropFilter:"blur(4px)",display:"flex",alignItems:isPhone?"flex-end":"center",justifyContent:"center",padding:isPhone?0:24,zIndex:1000}}>
      <div onClick={e=>e.stopPropagation()} style={{background:RICE,color:NORI,borderRadius:isPhone?"24px 24px 0 0":24,maxWidth:560,width:"100%",maxHeight:isPhone?"92vh":"88vh",overflowY:"auto"}}>
        <div style={{position:"relative",aspectRatio:"16/9",background:"rgba(0,57,44,0.08)"}}>
          <AllergenItemThumbFOH item={item} large/>
          <button onClick={onClose} style={{position:"absolute",top:14,right:14,width:40,height:40,background:"rgba(0,30,22,0.7)",color:KEWPIE,border:"none",borderRadius:"50%",cursor:"pointer",fontSize:18,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}>✕</button>
        </div>
        <div style={{padding:"22px 22px 30px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
            {item.vegan && <AllergenPill icon="🌱" label="Vegan" tone="ok"/>}
            {item.veg && !item.vegan && <AllergenPill icon="🥗" label="Vegetarian" tone="ok"/>}
            {!_allergenHasGluten(item) && <AllergenPill icon="🌾" label="Made without gluten" tone="muted"/>}
          </div>
          <div style={{fontFamily:"gelica, Georgia, serif",fontSize:30,fontWeight:700,letterSpacing:"-0.02em",lineHeight:1.1,marginBottom:4}}>{item.name}</div>
          <div style={{fontSize:13,color:DIM,fontWeight:600,letterSpacing:"0.04em",textTransform:"uppercase",marginBottom:18}}>{category ? `${category.icon||""} ${category.name}`.trim() : ""}</div>

          {containsList.length > 0 && (
            <div style={{marginBottom:18,padding:14,background:NORI,color:KEWPIE,borderRadius:14}}>
              <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:WASABI,marginBottom:8}}>⚠️ Contains</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {containsList.map(id => {
                  const a = ALLERGENS_BY_ID[id];
                  return <span key={id} title={a.longLabel||a.label} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"6px 10px",borderRadius:8,background:WASABI,color:NORI,fontSize:13,fontWeight:700}}><span>{a.icon}</span>{a.label}</span>;
                })}
              </div>
              {containsList.some(id => ALLERGENS_BY_ID[id]?.longLabel) && (
                <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(212,251,111,0.25)",fontSize:11,color:"rgba(254,250,240,0.8)",lineHeight:1.5}}>
                  {containsList.filter(id => ALLERGENS_BY_ID[id]?.longLabel).map(id => {
                    const a = ALLERGENS_BY_ID[id];
                    return <div key={id}><strong style={{color:WASABI}}>{a.label}:</strong> {a.longLabel}</div>;
                  })}
                </div>
              )}
            </div>
          )}

          {mayList.length > 0 && (
            <div style={{marginBottom:18,padding:14,background:KEWPIE,border:`1px solid ${LINE}`,borderRadius:14}}>
              <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:DIM,marginBottom:8}}>May contain</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {mayList.map(id => {
                  const a = ALLERGENS_BY_ID[id];
                  return <span key={id} title={a.longLabel||a.label} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"6px 10px",borderRadius:8,background:RICE,color:NORI,border:`1px solid ${LINE}`,fontSize:13,fontWeight:700}}><span>{a.icon}</span>{a.label}</span>;
                })}
              </div>
              {mayList.some(id => ALLERGENS_BY_ID[id]?.longLabel) && (
                <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${LINE}`,fontSize:11,color:DIM,lineHeight:1.5}}>
                  {mayList.filter(id => ALLERGENS_BY_ID[id]?.longLabel).map(id => {
                    const a = ALLERGENS_BY_ID[id];
                    return <div key={id}><strong style={{color:NORI}}>{a.label}:</strong> {a.longLabel}</div>;
                  })}
                </div>
              )}
              <div style={{marginTop:8,fontSize:11,color:DIM}}>Made in a kitchen that handles these allergens — cross-contamination possible.</div>
            </div>
          )}

          <div style={{marginTop:6}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:DIM,marginBottom:10}}>Full breakdown — UK FSA 14</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2, 1fr)",gap:6}}>
              {ALLERGENS_FSA14.map(a => {
                const v = (item.allergens||{})[a.id];
                const status = v === "contains" ? "contains" : v === "may" ? "may" : "none";
                const styles = status === "contains"
                  ? {bg:NORI,color:WASABI,sym:"✗",symColor:WASABI}
                  : status === "may"
                  ? {bg:KEWPIE,color:NORI,sym:"⚠",symColor:"#c87a1f"}
                  : {bg:RICE,color:"rgba(0,57,44,0.6)",sym:"·",symColor:"rgba(0,57,44,0.45)"};
                return (
                  <div key={a.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",borderRadius:10,background:styles.bg,color:styles.color,border:status==="none"?`1px solid ${LINE}`:"none",fontSize:13}} title={a.longLabel||a.label}>
                    <span style={{display:"inline-flex",alignItems:"center",gap:6,fontWeight:status==="none"?500:700}}><span>{a.icon}</span>{a.label}</span>
                    <span style={{fontSize:14,fontWeight:800,color:styles.symColor}}>{styles.sym}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AllergenSheetFOH({selected, onToggle, onClear, onClose, colors}){
  const {NORI,WASABI,KEWPIE,RICE,LINE,DIM} = colors;
  const selectedSet = new Set(selected || []);
  useEffect(()=>{
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return ()=>{ window.removeEventListener("keydown", onKey); document.body.style.overflow=""; };
  },[onClose]);
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,30,22,0.7)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,zIndex:1000}}>
      <div onClick={e=>e.stopPropagation()} style={{background:RICE,color:NORI,borderRadius:24,maxWidth:520,width:"100%",maxHeight:"82vh",overflowY:"auto",padding:24}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{fontFamily:"gelica, Georgia, serif",fontSize:22,fontWeight:700,letterSpacing:"-0.01em"}}>Hide items containing…</div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,color:NORI,cursor:"pointer",padding:6,fontFamily:"inherit"}}>✕</button>
        </div>
        <div style={{fontSize:13,color:DIM,marginBottom:18}}>Tap any allergens you want to avoid — we'll only show items that contain <strong>none</strong> of them. Tap again to remove.</div>
        {/* Grid stretches all cells to the row's tallest sibling so rows align cleanly even when only some cards have a longLabel subtitle (per JJ 2026-05-07). Content centered vertically inside each button. */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(2, 1fr)",gap:8}}>
          {ALLERGENS_FSA14.map(a => {
            const on = selectedSet.has(a.id);
            return (
              <button key={a.id} onClick={()=>onToggle(a.id)} title={a.longLabel||a.label} style={{display:"flex",alignItems:"center",gap:10,padding:"14px 14px",borderRadius:12,background:on?NORI:KEWPIE,color:on?WASABI:NORI,border:`1px solid ${on?NORI:LINE}`,fontSize:14,fontWeight:700,fontFamily:"inherit",cursor:"pointer",textAlign:"left",minHeight:64}}>
                <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:22,height:22,borderRadius:6,background:on?WASABI:"transparent",color:on?NORI:DIM,border:on?"none":`1.5px solid ${LINE}`,fontSize:14,fontWeight:900,flexShrink:0}}>{on?"✓":""}</span>
                <span style={{fontSize:18,lineHeight:"20px"}}>{a.icon}</span>
                <span style={{flex:1,minWidth:0}}>
                  <div>{a.label}</div>
                  {a.longLabel && <div style={{fontSize:10,fontWeight:500,opacity:0.7,marginTop:2,lineHeight:1.35}}>{a.longLabel}</div>}
                </span>
              </button>
            );
          })}
        </div>
        {selected && selected.length > 0 && (
          <div style={{marginTop:18,display:"flex",gap:8}}>
            <button onClick={onClear} style={{flex:1,padding:14,borderRadius:12,background:KEWPIE,border:`1px solid ${LINE}`,color:NORI,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Clear all</button>
            <button onClick={onClose} style={{flex:1,padding:14,borderRadius:12,background:NORI,border:`1px solid ${NORI}`,color:WASABI,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Show results ({selected.length})</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Mounted by src/main.jsx (role-based). Exported instead of self-rendering.
export { App as FohApp };
// The till preview door (tillPreview.jsx) draws these real screens for the card builder.
export { CCOfflineRedeemPicker, CCScannerDefs, CC_CARDTYPES_CACHE, CC_TIERREWARDS_CACHE };